import React, { useState, useMemo, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './index.css';
import { jsPDF } from 'jspdf';
import * as XLSX from 'xlsx';
import { auth, provider, db, storage, functions } from './firebase';
import { httpsCallable } from 'firebase/functions';
import { ref as sRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { signInWithPopup, signOut, onAuthStateChanged, signInWithEmailAndPassword, signInWithCustomToken, getIdTokenResult } from 'firebase/auth';
import { doc, collection, getDocs, getDocsFromCache, getDoc, writeBatch, setDoc, deleteDoc, onSnapshot, query, orderBy, limit, where } from 'firebase/firestore';

const ADMIN_EMAIL = 'juan@amarmgmt.com';

// Users can have multiple login emails (u.emails array) or a legacy single u.email string
const getUserEmails = u => Array.isArray(u.emails) ? u.emails : (u.email ? [u.email] : []);
const userMatchesEmail = (u, email) => !!email && getUserEmails(u).some(e => e.toLowerCase() === email.toLowerCase());
const DB_KEYS = ['properties', 'tenants', 'violations', 'maintenance', 'tasks', 'users', 'ledger', 'vendors', 'logins', 'appSettings', 'financials_invoices', 'financials_income', 'financials_vendors'];

// Session-scoped property filter shared across tabs (Tenants, Violations, Maintenance,
// Logins). Each tab reads this on mount, so a selection persists when switching tabs.
// 'all' = no filter. Resets to 'all' on full page reload (refresh/close) and on sign-out.
let SHARED_PROP_FILTER = 'all';

// Rent Roll view state, kept for the session so it survives tab switches (cleared on
// full page reload). propId/month fall back to props on first load.
const RR_PERSIST = { propId: null, month: null, view: 'simple', sortCol: null, sortDir: 'desc', hiddenCols: new Set(), hiddenRows: new Set(), colFilters: {} };

// ── Audit log helpers ─────────────────────────────────────────────────────────
const AUDIT_COLLECTIONS = new Set(['tenants', 'violations', 'ledger', 'tasks', 'maintenance', 'financials_invoices', 'properties', 'vendors']);
const AUDIT_SKIP_FIELDS  = new Set(['updatedAt', 'syncedAt', 'lastSync', 'added']);

function formatAuditVal(v) {
  if (v === null || v === undefined) return '—';
  if (Array.isArray(v)) return `[${v.length} item${v.length !== 1 ? 's' : ''}]`;
  if (typeof v === 'object') return '[object]';
  const s = String(v);
  return s.length > 60 ? s.slice(0, 57) + '…' : s;
}

// "07/29/2026 9:31 AM" in New York time — the leading token of every audit line. Shared by
// the Audit Log tab and the per-task audit view so the two can't drift apart.
// Notes and documents are arrays of objects, so rendering them as "[11 items] → [11 items]"
// says nothing about what actually changed. Compare the two lists by content and report the
// real movement: one entry out and one in is an edit, otherwise it's additions/removals.
// Works on historical entries too, because auditDiff stores the raw arrays.
const auditItemText = x => x === null || x === undefined ? ''
  : typeof x !== 'object' ? String(x)
  : String(x.text ?? x.name ?? x.title ?? x.summary ?? JSON.stringify(x));

function describeListChange(before, after) {
  const b = (Array.isArray(before) ? before : []).map(auditItemText);
  const a = (Array.isArray(after) ? after : []).map(auditItemText);
  // Multiset difference, so a genuinely duplicated note isn't mistaken for unchanged.
  const remaining = [...b];
  const added = [];
  for (const t of a) {
    const i = remaining.indexOf(t);
    if (i >= 0) remaining.splice(i, 1); else added.push(t);
  }
  const removed = remaining;
  if (!added.length && !removed.length) return null;
  // One out, one in — an entry was edited in place, so show it as before → after.
  if (added.length === 1 && removed.length === 1) return [{ kind: 'edit', before: removed[0], after: added[0] }];
  return [
    ...removed.map(t => ({ kind: 'removed', before: t })),
    ...added.map(t => ({ kind: 'added', after: t })),
  ];
}

function auditWhenLabel(e) {
  if (!e || !e.timestamp) return e && e.date ? fmtDate(e.date) : '—';
  const d = new Date(e.timestamp);
  const tz = { timeZone: 'America/New_York' };
  const date = d.toLocaleDateString('en-US', { ...tz, month: '2-digit', day: '2-digit', year: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { ...tz, hour: 'numeric', minute: '2-digit' });
  return `${date} ${time}`;
}

function auditDiff(before, after) {
  const changes = [];
  const allKeys = new Set([...Object.keys(before), ...Object.keys(after)]);
  for (const k of allKeys) {
    if (AUDIT_SKIP_FIELDS.has(k)) continue;
    if (JSON.stringify(before[k]) !== JSON.stringify(after[k]))
      changes.push({ field: k, before: before[k], after: after[k] });
  }
  return changes;
}

function auditLabel(col, d, allData) {
  if (col === 'tenants')             return d.name || d.id;
  if (col === 'violations')          return `${d.type || ''} ${d.caseNum || ''}`.trim() || d.id;
  if (col === 'tasks')               return d.title || d.id;
  if (col === 'maintenance')         return d.issue || d.description || d.id;
  if (col === 'financials_invoices') return `${d.vendor || ''}${d.title ? ' — ' + d.title : ''}` || d.id;
  if (col === 'properties')          return d.name || d.id;
  if (col === 'vendors')             return d.name || d.id;
  if (col === 'ledger') {
    const t = (allData.tenants || []).find(x => x.id === d.tenantId);
    return `${t ? t.name : d.tenantId} — ${d.description || ''}`;
  }
  return d.id;
}

function auditSummary(action, col, d, changes) {
  const NAME = { tenants: 'Tenant', violations: 'Violation', ledger: 'Ledger', tasks: 'Task', maintenance: 'Maintenance', financials_invoices: 'Invoice', properties: 'Property', vendors: 'Vendor' };
  const n = NAME[col] || col;
  if (action === 'DELETE') return `${n} deleted`;
  if (action === 'CREATE') {
    if (col === 'tenants')             return `Tenant added: ${d.name || '?'}`;
    if (col === 'violations')          return `${d.type || 'Violation'} added${d.caseNum ? ': ' + d.caseNum : ''}`;
    if (col === 'ledger')              return `Charge added: $${d.amount || 0} — ${d.description || ''}`;
    if (col === 'tasks')               return `Task created: ${d.title || '?'}`;
    if (col === 'maintenance')         return `Maintenance added: ${d.issue || d.description || '?'}`;
    if (col === 'financials_invoices') return `Invoice added: ${d.vendor || '?'} — $${d.amount || 0}`;
    if (col === 'properties')          return `Property added: ${d.name || '?'}`;
    if (col === 'vendors')             return `Vendor added: ${d.name || '?'}`;
    return `${n} created`;
  }
  if (changes.length === 0) return `${n} updated`;
  const clip = s => s.length > 70 ? s.slice(0, 67) + '…' : s;
  const summarizeChange = c => {
    // Arrays get the real item-level story rather than a count of both sides.
    const list = (Array.isArray(c.before) || Array.isArray(c.after)) ? describeListChange(c.before, c.after) : null;
    if (list && list.length === 1) {
      const it = list[0];
      if (it.kind === 'edit')  return `${c.field} edited: "${clip(it.before)}" → "${clip(it.after)}"`;
      if (it.kind === 'added') return `${c.field} added: "${clip(it.after)}"`;
      return `${c.field} removed: "${clip(it.before)}"`;
    }
    if (list) {
      const added = list.filter(x => x.kind === 'added').length;
      const removed = list.filter(x => x.kind === 'removed').length;
      return `${c.field}: ${[added ? `${added} added` : '', removed ? `${removed} removed` : ''].filter(Boolean).join(', ')}`;
    }
    return `${c.field}: ${formatAuditVal(c.before)} → ${formatAuditVal(c.after)}`;
  };
  const parts = changes.slice(0, 2).map(summarizeChange);
  return `${n} updated: ${parts.join('; ')}${changes.length > 2 ? ` (+${changes.length - 2} more)` : ''}`;
}

// ── icons ──────────────────────────────────────────────────────────────────
function ic(path, size = 16, color = 'currentColor') {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
      <path d={path} />
    </svg>
  );
}
const I = {
  building: 'M6 22V4a2 2 0 012-2h8a2 2 0 012 2v18zM2 22h20M15 22v-4a1 1 0 00-1-1h-4a1 1 0 00-1 1v4',
  users: 'M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 7a4 4 0 100 8 4 4 0 000-8z',
  user: 'M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 7a4 4 0 100 8 4 4 0 000-8z',
  gavel: 'M14 13l6.5 6.5a2.121 2.121 0 01-3 3L11 16l-4 1 1-4L14 7l3 3-3 3z',
  wrench: 'M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z',
  receipt: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 000 4h6a2 2 0 000-4M9 5a2 2 0 010-4h6a2 2 0 010 4',
  plus: 'M12 5v14M5 12h14',
  x: 'M18 6L6 18M6 6l12 12',
  sun: 'M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42M12 5a7 7 0 100 14A7 7 0 0012 5z',
  moon: 'M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z',
  arrowLeft: 'M19 12H5M12 19l-7-7 7-7',
  dollar: 'M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6',
  clipboard: 'M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2M9 2h6a1 1 0 011 1v2a1 1 0 01-1 1H9a1 1 0 01-1-1V3a1 1 0 011-1z',
  folder: 'M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z',
  file: 'M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8zM14 2v6h6',
  pencil: 'M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z',
  chevR: 'M9 18l6-6-6-6',
  logout: 'M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9',
  tasks: 'M9 11l3 3L22 4M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11',
  shield: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z',
  truck: 'M1 3h15v13H1zM16 8h4l3 3v5h-7V8zM5.5 19a1.5 1.5 0 100-3 1.5 1.5 0 000 3zM18.5 19a1.5 1.5 0 100-3 1.5 1.5 0 000 3z',
  lock: 'M19 11H5a2 2 0 00-2 2v7a2 2 0 002 2h14a2 2 0 002-2v-7a2 2 0 00-2-2zM7 11V7a5 5 0 0110 0v4',
  chart: 'M18 20V10M12 20V4M6 20v-6',
  grid: 'M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h7v7h-7z',
  moreVert: 'M12 5a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z',
  eye: 'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8zM12 9a3 3 0 100 6 3 3 0 000-6z',
  eyeOff: 'M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24M1 1l22 22',
  link: 'M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71',
  mail: 'M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2zM22 6l-10 7L2 6',
  calendar:  'M3 4h18v18H3zM16 2v4M8 2v4M3 10h18M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01M16 18h.01',
  paperclip: 'M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48',
  download: 'M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3',
  edit: 'M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z',
  alert: 'M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0zM12 9v4M12 17h.01',
};

const SEED_LOGINS = [
  { id: 'login001', title: 'Gmail', link: '', propertyLabel: '', username: 'juan@amarmgmt.com', password: 'JGq2w3e4$$', notes: '' },
  { id: 'login002', title: 'Gmail', link: '', propertyLabel: 'Amar Marketing Enterprise 2 LLC', username: 'waltham.office9@gmail.com', password: 'Waheguru13*', notes: '' },
  { id: 'login003', title: 'Gmail', link: '', propertyLabel: 'Amar 8615 Rockaway LLC', username: 'Office.amar8615@gmail.com', password: 'Waheguru13*8615', notes: '' },
  { id: 'login004', title: 'Gmail', link: '', propertyLabel: 'Shalbro Construction Group', username: 'shalbro.jaskirat@gmail.com', password: '25Nov#1997', notes: '' },
  { id: 'login005', title: 'Gmail', link: '', propertyLabel: 'Shalbro Construction Group', username: 'shalbro.Hemant@gmail.com', password: 'Waheguru13!', notes: '' },
  { id: 'login006', title: 'Gmail', link: '', propertyLabel: 'Skyview Developers Group', username: 'skyview.office9@gmail.com', password: 'Waheguru13!', notes: '' },
  { id: 'login007', title: 'All Building - HPD Violations', link: 'https://a806-pros.nyc.gov/PROS/', propertyLabel: '', username: 'shalbro.properties@gmail.com', password: 'Shalbroprop#1313', notes: '' },
  { id: 'login008', title: 'Check HPD Violations', link: '', propertyLabel: '', username: 'HOLDING10008@YAHOO.COM', password: 'Waheguru1313!', notes: '' },
  { id: 'login009', title: '8615 DHCR', link: 'https://apps.hcr.ny.gov/ORAOwnerForms/Default.aspx', propertyLabel: 'Amar 8615 Rockaway LLC', username: 'msingh4', password: 'EEMkngJwx$zf#8a', notes: '' },
  { id: 'login010', title: '9525 DHCR', link: 'https://apps.hcr.ny.gov/ORAOwnerForms/Default.aspx', propertyLabel: 'Amar Marketing Enterprise 2 LLC', username: 'asingh5', password: 'EEMkngJwx$zf#8a', notes: '' },
  { id: 'login011', title: '8114 DHCR', link: 'https://apps.hcr.ny.gov/ORAOwnerForms/Default.aspx', propertyLabel: 'Amar 8114 101 Ave LLC', username: 'Amar8114llc@gmail.com', password: 'Waheguru1313!', notes: '' },
  { id: 'login012', title: 'Section 8 NYCHA', link: 'https://eapps.nycha.info/Owners/login.aspx', propertyLabel: 'Amar 8114 101 Ave LLC', username: 'Amar8114llc@gmail.com', password: 'Waheguru1313!', notes: '' },
  { id: 'login013', title: 'Section 8 NYCHA', link: 'https://eapps.nycha.info/Owners/login.aspx', propertyLabel: 'Amar 8615 Rockaway LLC', username: 'amar8615llc@gmail.com', password: 'Waheguru1313!', notes: '' },
  { id: 'login014', title: 'Section 8 NYCHA', link: 'https://eapps.nycha.info/Owners/login.aspx', propertyLabel: 'Amar Marketing Enterprise 2 LLC', username: 'Amar9525waltham@gmail.com', password: 'Waheguru@13', notes: '' },
  { id: 'login015', title: 'Section 8 NYCHA', link: 'https://eapps.nycha.info/Owners/login.aspx', propertyLabel: 'AMAR 81-11 101 AVE LLC (OLD 8107/09)', username: 'amar810709101ave@outlook.com', password: 'Waheguru13!', notes: '' },
  { id: 'login016', title: 'Section 8 HPD', link: 'https://eapps.nycha.info/Owners/login.aspx', propertyLabel: 'Amar Marketing Enterprise 2 LLC', username: 'Amar9525waltham@gmail.com', password: 'Waheguru1313!', notes: '' },
  { id: 'login017', title: 'DOB NOW', link: 'https://eapps.nycha.info/Owners/login.aspx', propertyLabel: 'Amar Marketing Enterprise 2 LLC', username: 'Amar9525waltham@gmail.com', password: 'Waheguru1313!', notes: '' },
  { id: 'login018', title: 'DOB NOW', link: 'https://eapps.nycha.info/Owners/login.aspx', propertyLabel: 'Amar 8615 Rockaway LLC', username: 'Office.amar8615@gmail.com', password: 'Waheguru13*', notes: '' },
  { id: 'login019', title: 'DOB NOW', link: 'https://www1.nyc.gov/account/login.htm?spName=www1.nyc.gov-account&samlContext=us1_8176884_3ff946c0-1565-4846-9fe7-e1590f1cfe09', propertyLabel: 'Skyview Developers Group', username: 'skyviewnyc9@gmail.com', password: 'Waheguru1313!', notes: '' },
  { id: 'login020', title: 'DOB NOW', link: 'https://www1.nyc.gov/account/login.htm?spName=www1.nyc.gov-account&samlContext=us1_8176884_3ff946c0-1565-4846-9fe7-e1590f1cfe09', propertyLabel: 'AMAR 81-11 101 AVE LLC (OLD 8107/09)', username: 'Amar8107.8109@outlook.com', password: 'Waheguru13!', notes: '' },
  { id: 'login021', title: 'DOB NOW', link: 'https://www1.nyc.gov/account/login.htm?spName=www1.nyc.gov-account&samlContext=us1_8176884_3ff946c0-1565-4846-9fe7-e1590f1cfe09', propertyLabel: '', username: 'waltham.office9@gmail.com', password: 'Waheguru13*', notes: '' },
  { id: 'login022', title: 'Ecertification HPD', link: 'http://www.nyc.gov/dobefiling', propertyLabel: 'All Properties', username: 'waltham.office9@gmail.com', password: 'Waheguru13*', notes: '' },
  { id: 'login023', title: 'Reside Portal', link: 'https://mretax.my.salesforce-sites.com/OwnersPortal/OwnersPortalLoginPage?refURL=http%3A%2F%2Fmretax.my.salesforce-sites.com%2FO', propertyLabel: 'Amar 8615 Rockaway LLC', username: '8615rockaway@gmail.com', password: 'Waheguru13!', notes: '' },
  { id: 'login024', title: 'DOF', link: 'https://www.nyc.gov/site/finance/property/property-tax-bills.page', propertyLabel: 'Amar Marketing Enterprise 2 LLC', username: 'waltham.office9@gmail.com', password: 'Waheguru13!', notes: '' },
  { id: 'login025', title: 'NationalGrid (Gas)', link: 'https://login.nationalgrid.com/login.nationalgridus.com/b2c_1a_nationalgrid_convert_merge_signin/oauth2/v2.0/authorize?client_id=36488660-e', propertyLabel: 'Amar Marketing Enterprise 2 LLC', username: 'Waltham.office9@gmail.com', password: 'Waheguru13*', notes: '' },
  { id: 'login026', title: 'Coned (Electric)', link: '', propertyLabel: 'Amar Marketing Enterprise 2 LLC', username: 'walthamtowernyc@gmail.com', password: 'Waheguru1313!', notes: '' },
  { id: 'login027', title: 'DEP (Water)', link: '', propertyLabel: 'Amar Marketing Enterprise 2 LLC', username: 'waltham.office9@gmail.com', password: 'Waheguru13!', notes: '' },
  { id: 'login028', title: 'Fire Response', link: '', propertyLabel: 'Amar Marketing Enterprise 2 LLC', username: 'Account # 766776', password: 'Response', notes: 'Call: System Offline Call Metrodial 5166818' },
  { id: 'login029', title: 'App (Rent Redi)', link: 'https://app.rentredi.com/', propertyLabel: 'Amar 8615 Rockaway LLC', username: 'Itsmanni@icloud.com', password: 'Waheguru1313!', notes: '' },
  { id: 'login030', title: 'App (Tenant Cloud)', link: 'http://home.tenantcloud.com/', propertyLabel: '', username: 'amarmarketingenterprise@gmail.com', password: 'Shalbro@tenant1', notes: '' },
  { id: 'login031', title: 'App (Smart Lease)', link: 'https://app.smartleas.com/login', propertyLabel: '', username: 'amar9525waltham@gmail.com', password: '112233', notes: '' },
  // Amar 8615 Rockaway LLC
  { id: 'login032', title: 'Gmail', link: '', propertyLabel: 'Amar 8615 Rockaway LLC', username: 'amar8615rockaway@gmail.com', password: 'ONLY OWNER', notes: '' },
  { id: 'login033', title: 'CCTV', link: '', propertyLabel: 'Amar 8615 Rockaway LLC', username: 'Office.amar8615@gmail.com', password: 'aman9511@', notes: '' },
  { id: 'login034', title: 'Coned (Electric)', link: 'https://www.coned.com/en/', propertyLabel: 'Amar 8615 Rockaway LLC', username: 'office.shalbro@gmail.com', password: 'ju3&FN-8jkevpK8', notes: '' },
  { id: 'login035', title: 'National Grid (Gas)', link: 'https://login.nationalgrid.com/login.nationalgridus.com/b2c_1a_nationalgrid_convert_merge_signin/oauth2/v2.0/authorize?client_id=36488660-e', propertyLabel: 'Amar 8615 Rockaway LLC', username: 'Pending', password: 'Pending', notes: '' },
  { id: 'login036', title: 'DEP (Water)', link: 'https://umaxazprodb2c.b2clogin.com/umaxazprodb2c.onmicrosoft.com/b2c_1a_signup_signin_nd/oauth2/v2.0/authorize?client_id=c334a93a-a', propertyLabel: 'Amar 8615 Rockaway LLC', username: 'shalbro.office@gmail.com', password: '2s5T_9HkEE2x69X', notes: '' },
  // Amar 8114 101 Ave LLC
  { id: 'login037', title: 'CCTV', link: '', propertyLabel: 'Amar 8114 101 Ave LLC', username: 'Pending', password: 'Pending', notes: '' },
  { id: 'login038', title: 'Coned (Electric)', link: 'https://www.coned.com/en/', propertyLabel: 'Amar 8114 101 Ave LLC', username: 'office.shalbro@gmail.com', password: 'ju3&FN-8jkevpK8', notes: '' },
  { id: 'login039', title: 'National Grid (Gas)', link: 'https://login.nationalgrid.com/login.nationalgridus.com/b2c_1a_nationalgrid_convert_merge_signin/oauth2/v2.0/authorize?client_id=36488660-e', propertyLabel: 'Amar 8114 101 Ave LLC', username: 'Pending', password: 'Pending', notes: '' },
  { id: 'login040', title: 'DEP (Water)', link: 'https://umaxazprodb2c.b2clogin.com/umaxazprodb2c.onmicrosoft.com/b2c_1a_signup_signin_nd/oauth2/v2.0/authorize?client_id=c334a93a-a', propertyLabel: 'Amar 8114 101 Ave LLC', username: 'Pending', password: 'Pending', notes: '' },
  // Silk Road Ventures Corp
  { id: 'login041', title: 'DOB NOW', link: 'https://www1.nyc.gov/account/login.htm?spName=www1.nyc.gov-account&samlContext=us1_8176884_3ff946c0-1565-4846-9fe7-e1590f1cfe09', propertyLabel: 'Silk Road Ventures Corp', username: 'silkroadenterprise9@gmail.com', password: 'Waheguru1313!', notes: '' },
  { id: 'login042', title: 'Swyft', link: 'https://www.swyftfilings.com/portal/', propertyLabel: 'Silk Road Ventures Corp', username: 'silkroadenterprise9@gmail.com', password: 'Waheguru1313!', notes: '' },
  { id: 'login043', title: 'Spectrum', link: 'https://www.spectrum.com/', propertyLabel: 'Silk Road Ventures Corp', username: 'silkroadenterprise9@gmail.com', password: 'Waheguru13!*', notes: '' },
  // Shalbro Construction Group
  { id: 'login044', title: 'DOB NOW', link: 'https://www1.nyc.gov/account/login.htm?spName=www1.nyc.gov-account&samlContext=us1_8176884_3ff946c0-1565-4846-9fe7-e1590f1cfe09', propertyLabel: 'Shalbro Construction Group', username: 'shalbroconstruction@gmail.com', password: 'Waheguru13!!', notes: '' },
  // 82-10 Rockaway LLC
  { id: 'login045', title: 'Coned (Electric)', link: 'https://www.coned.com/en/', propertyLabel: '82-10 Rockaway LLC', username: 'Pending', password: 'Pending', notes: '' },
  { id: 'login046', title: 'National Grid (Gas)', link: 'https://login.nationalgrid.com/login.nationalgridus.com/b2c_1a_nationalgrid_convert_merge_signin/oauth2/v2.0/authorize?client_id=36488660-e', propertyLabel: '82-10 Rockaway LLC', username: 'Pending', password: 'Pending', notes: '' },
  { id: 'login047', title: 'DEP (Water)', link: 'https://umaxazprodb2c.b2clogin.com/umaxazprodb2c.onmicrosoft.com/b2c_1a_signup_signin_nd/oauth2/v2.0/authorize?client_id=c334a93a-a', propertyLabel: '82-10 Rockaway LLC', username: 'Pending', password: 'Pending', notes: '' },
];

const initData = {
  properties: [
    { id: 'p1', name: 'Maple Court', address: '142 Maple Ave, Brooklyn, NY 11201', units: 12, owner: 'Amar Holdings LLC', ein: '', businessType: 'Multi Family Housing', bin: '', lot: '', block: '', insurance: [], compliance: [], unitsList: [] },
    { id: 'p2', name: 'Park View Apts', address: '88 Park Blvd, Queens, NY 11375', units: 8, owner: 'Amar Holdings LLC', ein: '', businessType: 'Multi Family Housing', bin: '', lot: '', block: '', insurance: [], compliance: [], unitsList: [] },
  ],
  tenants: [
    { id: 't1', propertyId: 'p1', unit: '1A', name: 'Maria Santos', email: 'm.santos@email.com', phone: '718-555-0101', status: 'Occupied', rent: 2100, program: 'CityFHEPS', tenantShare: 900, programShare: 1200, leaseStart: '2023-01-01', leaseEnd: '2025-12-31', unitType: 'Affordable 421a', bedrooms: 2, bathrooms: 1, hasBalcony: false, hasParking: false, hasWasherDryer: false, parkingFee: 0, washerDryerFee: 0, brokerName: 'ABC Brokers', paymentType: 'Zelle', dueDay: 1, voucherNumber: 'CFHEPS-2023-441', notes: 'Has a cat. Renewing lease in December.', documents: [], householdMembers: ['Carlos Santos', 'Ana Santos'] },
    { id: 't2', propertyId: 'p1', unit: '2B', name: 'James Okafor', email: 'j.okafor@email.com', phone: '718-555-0102', status: 'Occupied', rent: 1850, program: 'Cash', tenantShare: 1850, programShare: 0, leaseStart: '2024-03-01', leaseEnd: '2025-02-28', unitType: 'Market', bedrooms: 1, bathrooms: 1, hasBalcony: true, hasParking: false, hasWasherDryer: false, parkingFee: 0, washerDryerFee: 0, brokerName: 'Direct', paymentType: 'Check', dueDay: 1, voucherNumber: '', notes: '', documents: [], householdMembers: [] },
    { id: 't3', propertyId: 'p1', unit: '3C', name: 'Linda Chen', email: 'l.chen@email.com', phone: '718-555-0103', status: 'Occupied', rent: 2400, program: 'Section 8', tenantShare: 600, programShare: 1800, leaseStart: '2022-06-01', leaseEnd: '2024-05-31', unitType: 'Affordable 421a', bedrooms: 3, bathrooms: 2, hasBalcony: false, hasParking: true, hasWasherDryer: true, parkingFee: 150, washerDryerFee: 50, brokerName: 'Metro Realty', paymentType: 'Money Order', dueDay: 5, voucherNumber: 'S8-HPD-7721', notes: 'Lease expired — follow up on renewal.', documents: [], householdMembers: ['David Chen'] },
    { id: 't4', propertyId: 'p2', unit: '1A', name: 'Devon Williams', email: 'd.williams@email.com', phone: '917-555-0201', status: 'Occupied', rent: 1950, program: 'Cash', tenantShare: 1950, programShare: 0, leaseStart: '2024-09-01', leaseEnd: '2025-08-31', unitType: 'Market', bedrooms: 2, bathrooms: 1, hasBalcony: false, hasParking: false, hasWasherDryer: false, parkingFee: 0, washerDryerFee: 0, brokerName: 'Direct', paymentType: 'App', dueDay: 1, voucherNumber: '', notes: '', documents: [], householdMembers: [] },
    { id: 't5', propertyId: 'p1', unit: '4D', name: 'VACANT', email: '', phone: '', status: 'Vacant', rent: 2200, program: '', tenantShare: 0, programShare: 0, leaseStart: '', leaseEnd: '', unitType: 'Market', bedrooms: 2, bathrooms: 1, hasBalcony: false, hasParking: false, hasWasherDryer: false, parkingFee: 0, washerDryerFee: 0, brokerName: '', paymentType: '', dueDay: 1, voucherNumber: '', notes: '', documents: [], householdMembers: [] },
  ],
  violations: [
    { id: 'v1', propertyId: 'p1', unit: '2B', type: 'HPD', caseNum: 'HPD-2024-0441', issue: 'Ceiling leak in bathroom', issuedDate: '2024-10-15', followUp: '2025-05-01', status: 'On-Going', notes: 'Plumber scheduled' },
    { id: 'v2', propertyId: 'p1', unit: '3C', type: 'DOB', caseNum: 'DOB-2024-7712', issue: 'Fire escape inspection required', issuedDate: '2024-11-01', followUp: '2025-04-30', status: 'Open', notes: '' },
    { id: 'v3', propertyId: 'p2', unit: '1A', type: 'HPD', caseNum: 'HPD-2025-0112', issue: 'Window guard missing', issuedDate: '2025-01-20', followUp: '2025-04-15', status: 'Completed', notes: 'Guard installed 03/01/2025' },
  ],
  maintenance: [
    { id: 'm1', propertyId: 'p1', unit: '1A', title: 'HVAC Filter Replacement', description: 'Annual HVAC maintenance', status: 'closed', date: '2025-01-10', vendor: 'CoolAir HVAC' },
    { id: 'm2', propertyId: 'p1', unit: '2B', title: 'Ceiling Leak Repair', description: 'Water stain on bathroom ceiling', status: 'in-progress', date: '2025-03-05', vendor: "Mike's Plumbing" },
    { id: 'm3', propertyId: 'p2', unit: 'Common', title: 'Lobby Light Replacement', description: 'LED retrofit for entrance lobby', status: 'open', date: '2025-04-01', vendor: '' },
  ],
  tasks: [],
  users: [],
  logins: [],
  appSettings: [{ id: 'vault', vaultPassword: 'JGq2w3e4$$' }],
  ledger: [
    { id: 'l1', tenantId: 't1', date: '2025-04-01', description: 'April Rent - Tenant Share', amount: 900, status: 'Paid' },
    { id: 'l2', tenantId: 't1', date: '2025-04-01', description: 'April Rent - Program Share', amount: 1200, status: 'Paid' },
    { id: 'l3', tenantId: 't2', date: '2025-04-01', description: 'April Rent', amount: 1850, status: 'Paid' },
    { id: 'l4', tenantId: 't3', date: '2025-04-01', description: 'April Rent - Tenant Share', amount: 600, status: 'Unpaid' },
    { id: 'l5', tenantId: 't4', date: '2025-04-01', description: 'April Rent', amount: 1950, status: 'Paid' },
  ],
  vendors: [],
  financials_invoices: [],
  financials_income: [],
  financials_vendors: [],
};

// ── small reusable components ──────────────────────────────────────────────

// One line (or a few) describing a single audited field change. Arrays go through
// describeListChange so an edited note reads as its old text → its new text instead of
// "[11 items] → [11 items]". Used by both the Audit Log tab and a task's own history.
function AuditChange({ change }) {
  const label = { fontSize: 12, color: 'var(--text3)', lineHeight: 1.45 };
  const wrap = { whiteSpace: 'pre-wrap', wordBreak: 'break-word' };
  const list = (Array.isArray(change.before) || Array.isArray(change.after))
    ? describeListChange(change.before, change.after)
    : null;

  if (list) {
    return (
      <div style={{ ...label, paddingLeft: 14 }}>
        {list.map((it, i) => (
          <div key={i} style={{ marginBottom: 2 }}>
            <span style={{ color: 'var(--text2)' }}>{change.field}</span>
            {it.kind === 'edit' ? (
              <>
                {' edited:'}
                <div style={{ ...wrap, color: 'var(--red-txt)', paddingLeft: 12 }}>− {it.before}</div>
                <div style={{ ...wrap, color: 'var(--green-txt)', paddingLeft: 12 }}>+ {it.after}</div>
              </>
            ) : it.kind === 'added' ? (
              <>
                {' added:'}
                <div style={{ ...wrap, color: 'var(--green-txt)', paddingLeft: 12 }}>+ {it.after}</div>
              </>
            ) : (
              <>
                {' removed:'}
                <div style={{ ...wrap, color: 'var(--red-txt)', paddingLeft: 12 }}>− {it.before}</div>
              </>
            )}
          </div>
        ))}
      </div>
    );
  }
  return (
    <div style={{ ...label, paddingLeft: 14 }}>
      {change.field}: {formatAuditVal(change.before)} → {formatAuditVal(change.after)}
    </div>
  );
}

// Renders free text with any URL turned into a clickable link, so a pasted address is
// reachable without copy/paste. Text is passed through untouched when it holds no link,
// and a non-string value is returned as-is (callers pass `value || '—'` freely).
// stopPropagation matters: several of these sit inside rows that are themselves clickable.
function Linkify({ text }) {
  if (typeof text !== 'string' || !text) return text ?? null;
  const re = /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+)/gi;
  const parts = [];
  let last = 0, m;
  while ((m = re.exec(text)) !== null) {
    let url = m[0];
    // Trailing punctuation is almost always the sentence's, not the URL's.
    const tail = (url.match(/[.,;:!?)\]}'"]+$/) || [''])[0];
    if (tail) url = url.slice(0, url.length - tail.length);
    if (!url) continue;
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(
      <a key={m.index} href={/^www\./i.test(url) ? `https://${url}` : url}
        target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
        style={{ color: 'var(--accent)', wordBreak: 'break-all' }}>{url}</a>
    );
    last = m.index + url.length;
  }
  if (!parts.length) return text;
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
}

// A textarea that grows to fit whatever it holds, so editing long text shows the whole
// thing instead of a few lines behind an inner scrollbar. It still stops at maxHeight
// (default 70% of the viewport) and scrolls past that, otherwise a very long entry would
// push the Save button off-screen.
function AutoTextarea({ value, onChange, minHeight = 90, maxHeight, style, ...rest }) {
  const ref = useRef(null);
  const fit = () => {
    const el = ref.current;
    if (!el) return;
    const cap = maxHeight || Math.round(window.innerHeight * 0.7);
    el.style.height = 'auto'; // reset first, or it can only ever grow
    const needed = Math.max(el.scrollHeight, minHeight);
    el.style.height = `${Math.min(needed, cap)}px`;
    el.style.overflowY = needed > cap ? 'auto' : 'hidden';
  };
  // Re-fit on mount (so an existing description opens at full height) and on every edit.
  useEffect(fit, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <textarea ref={ref} value={value}
      onChange={e => { onChange(e); fit(); }}
      style={{ ...style, minHeight, overflowY: 'hidden' }} {...rest} />
  );
}
// `compact` shrinks the button so it stops setting the height of a row — the default
// 16px icon + 4px padding is 24px tall, which forces a ~32px row even when the text beside
// it is 13px. Used in dense single-line lists (task Notes, Documents).
function KebabMenu({ items, compact }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const wrapRef = useRef();
  const menuRef = useRef();
  const MENU_W = 150;

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = ev => {
      if (wrapRef.current?.contains(ev.target) || menuRef.current?.contains(ev.target)) return;
      setOpen(false);
    };
    const dismiss = () => setOpen(false);
    document.addEventListener('mousedown', onDocMouseDown);
    window.addEventListener('scroll', dismiss, true);
    window.addEventListener('resize', dismiss);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      window.removeEventListener('scroll', dismiss, true);
      window.removeEventListener('resize', dismiss);
    };
  }, [open]);

  const toggle = ev => {
    ev.stopPropagation();
    if (!open && wrapRef.current) {
      const r = wrapRef.current.getBoundingClientRect();
      const estH = items.length * 37 + 8;
      let top = r.bottom + 4;
      if (top + estH > window.innerHeight - 8) top = Math.max(8, r.top - estH - 4); // flip up near bottom
      setPos({ top, left: Math.max(8, r.right - MENU_W) });
    }
    setOpen(o => !o);
  };

  // Portal the menu out of any overflow/scroll container so it can't be clipped.
  const portalTarget = (typeof document !== 'undefined' && document.querySelector('.app')) || (typeof document !== 'undefined' ? document.body : null);

  return (
    <div className="kebab-wrap" ref={wrapRef} style={{ display: 'inline-block' }}>
      <button className={`kebab-btn${compact ? ' kebab-btn-compact' : ''}`} onClick={toggle}>
        <svg width={compact ? 14 : 16} height={compact ? 14 : 16} viewBox="0 0 24 24" fill="currentColor" stroke="none">
          <circle cx={12} cy={5} r={1.5} /><circle cx={12} cy={12} r={1.5} /><circle cx={12} cy={19} r={1.5} />
        </svg>
      </button>
      {open && portalTarget && createPortal(
        <div ref={menuRef} className="kebab-menu" style={{ position: 'fixed', top: pos.top, left: pos.left, right: 'auto', minWidth: MENU_W, zIndex: 500 }}>
          {items.map((it, i) => (
            <button key={i} className={`kebab-item${it.danger ? ' danger' : ''}`}
              onClick={() => { setOpen(false); it.onClick(); }}>{it.label}</button>
          ))}
        </div>,
        portalTarget
      )}
    </div>
  );
}

function Badge({ label }) {
  const m = { Active: 'amber', Occupied: 'green', Vacant: 'amber', Previous: 'gray', 'On-Going': 'amber', Ongoing: 'amber', Urgent: 'red', Close: 'gray', Closed: 'gray', Open: 'red', New: 'amber', Completed: 'green', Adjourned: 'blue', Dismissed: 'gray', Rescheduled: 'amber', 'Hearing Schedule': 'blue', 'Pending Payment': 'amber', 'Not Owned': 'gray', Resolved: 'green', Defaulted: 'red', 'in-progress': 'amber', open: 'red', closed: 'green', Paid: 'green', Unpaid: 'red', Partial: 'amber', Pending: 'amber', Charged: 'blue' };
  // Canonical display + colour lookup that tolerates casing variants — e.g. a
  // bot-created task ("ongoing"/"Closed") should match an app one ("Ongoing"/"Close").
  const CANON = { ongoing: 'Ongoing', 'on-going': 'Ongoing', urgent: 'Urgent', close: 'Closed', closed: 'Closed', completed: 'Completed', dismissed: 'Dismissed', pending: 'Pending' };
  const raw = String(label ?? '');
  const lc = raw.toLowerCase();
  const display = CANON[lc] || raw;
  const colorKey = m[display] !== undefined ? display : Object.keys(m).find(k => k.toLowerCase() === lc);
  return <span className={`badge badge-${(colorKey && m[colorKey]) || 'gray'}`}>{display}</span>;
}

function SB({ value, onChange, placeholder }) {
  return (
    <div className="search-wrap">
      <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="var(--text3)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" /></svg>
      <input className="search-input" value={value} onChange={ev => onChange(ev.target.value)} placeholder={placeholder || 'Search…'} />
    </div>
  );
}

function Modal({ title, onClose, children, lg, xl, hideX, width }) {
  useEffect(() => {
    const main = document.querySelector('.main');
    if (main) { main.style.overflow = 'hidden'; }
    return () => { if (main) { main.style.overflow = ''; } };
  }, []);
  // Portal out of the card markup: ancestors with CSS transforms (e.g. .card:hover lift)
  // would otherwise hijack position:fixed and make the modal jump around on hover.
  // Target .app (not document.body) — the theme CSS variables live on .app's night/day class.
  return createPortal(
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`modal${xl ? ' modal-xl' : lg ? ' modal-lg' : ''}`} style={width ? { maxWidth: width } : undefined}>
        <div className="modal-header">
          <span className="modal-title">{title}</span>
          {!hideX && <button className="kebab-btn" onClick={onClose}>{ic(I.x, 16)}</button>}
        </div>
        {children}
      </div>
    </div>,
    document.querySelector('.app') || document.body
  );
}

function SavingBtn({ onClick, children, disabled, style, className }) {
  const [saving, setSaving] = useState(false);
  const handle = (e) => {
    if (saving || disabled) return;
    setSaving(true);
    setTimeout(() => { onClick(e); setSaving(false); }, 0);
  };
  return (
    <button
      className={`btn btn-primary${className ? ' ' + className : ''}`}
      onClick={handle}
      disabled={disabled || saving}
      style={style}
    >
      {saving
        ? <><span className="btn-spinner" />Saving…</>
        : children}
    </button>
  );
}

/* HOUSE STYLE (owner preference): every label + field pair renders on ONE ROW —
   "Label: [input]" with the label left at a fixed width. The .form-group CSS in
   index.css enforces this for FI/FS/FTA and all raw form-group markup.
   Follow this format in EVERY modal, entry form, and detail view — current and future. */
function FI({ label, value, onChange, type = 'text', placeholder = '', error = null }) {
  return (
    <div className="form-group">
      {label && <label className="form-label">{label}</label>}
      <div style={{ flex: 1, minWidth: 0 }}>
        <input className="form-input" type={type} value={value || ''} onChange={ev => onChange(ev.target.value)} placeholder={placeholder}
          style={error ? { borderColor: 'var(--red-txt)', boxShadow: '0 0 0 2px var(--red-bg)' } : undefined} />
        {error && <div style={{ fontSize: 11, color: 'var(--red-txt)', marginTop: 3 }}>{error}</div>}
      </div>
    </div>
  );
}

// ── Known legal representatives — picking one auto-fills phone + email ────────
const LEGAL_REPS = [
  { name: 'Juan',      phone: '(929) 266-4376', email: 'juan@amarmgmt.com' },
  { name: 'Jackjaffa', phone: '7188556110',     email: 'Charles@jackjaffa.com, Leo@jackjaffa.com, Client.Care@jackjaffa.com' },
];

// Free-text input with a type-to-filter suggestion dropdown of string options
function SuggestInput({ value, onChange, options, placeholder }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  const q = (value || '').toLowerCase().trim();
  const matches = (options || []).filter(o => !q || o.toLowerCase().includes(q));
  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input className="form-input" value={value || ''} placeholder={placeholder}
        onChange={e => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)} />
      {open && matches.length > 0 && (
        <div style={{ position: 'absolute', left: 0, right: 0, top: '100%', marginTop: 4, zIndex: 400, background: 'var(--bg1)', border: '1px solid var(--border)', borderRadius: 8, boxShadow: '0 6px 20px rgba(0,0,0,0.25)', maxHeight: 200, overflowY: 'auto' }}>
          {matches.map(o => (
            <div key={o} onMouseDown={e => { e.preventDefault(); onChange(o); setOpen(false); }}
              style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, color: 'var(--text1)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--accent-bg)'}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}>{o}</div>
          ))}
        </div>
      )}
    </div>
  );
}

// Free-text name input with a suggestion dropdown of known reps.
// onPick(rep) fires when a rep is chosen so the caller can fill phone/email.
function LegalRepNameInput({ value, onChange, onPick, placeholder = 'Rep name', autoFocus }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  const q = (value || '').toLowerCase().trim();
  const matches = LEGAL_REPS.filter(r => !q || r.name.toLowerCase().includes(q));
  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input className="form-input" value={value || ''} placeholder={placeholder} autoFocus={autoFocus}
        onChange={e => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)} />
      {open && matches.length > 0 && (
        <div style={{ position: 'absolute', left: 0, right: 0, top: '100%', marginTop: 4, zIndex: 400, background: 'var(--bg1)', border: '1px solid var(--border)', borderRadius: 8, boxShadow: '0 6px 20px rgba(0,0,0,0.25)', overflow: 'hidden' }}>
          {matches.map(r => (
            <div key={r.name} onMouseDown={e => { e.preventDefault(); onPick(r); setOpen(false); }}
              style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13 }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--accent-bg)'}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}>
              <div style={{ fontWeight: 600, color: 'var(--text1)' }}>{r.name}</div>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 1 }}>{r.phone} · {r.email}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
function FTA({ label, value, onChange }) {
  return (
    <div className="form-group">
      {label && <label className="form-label">{label}</label>}
      <textarea className="form-textarea" value={value || ''} onChange={ev => onChange(ev.target.value)} />
    </div>
  );
}
function FS({ label, value, onChange, options, placeholder }) {
  return (
    <div className="form-group">
      {label && <label className="form-label">{label}</label>}
      <select className="form-input" value={value || ''} onChange={ev => onChange(ev.target.value)}>
        {(!value) && <option value="" disabled>{placeholder || '— Select —'}</option>}
        {options.map(o => <option key={o.value || o} value={o.value || o}>{o.label || o}</option>)}
      </select>
    </div>
  );
}

function SearchSelect({ label, value, onChange, options, containerStyle }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef(null);
  const inputRef = useRef(null);
  const themeClass = document.querySelector('.app')?.classList.contains('night') ? 'night' : 'day';

  useEffect(() => {
    const h = e => {
      if (!triggerRef.current?.contains(e.target) && !document.getElementById('ss-dropdown')?.contains(e.target)) {
        setOpen(false); setQuery('');
      }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const calcPos = () => {
    if (triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      const isMobile = window.innerWidth < 768;
      if (isMobile) {
        const margin = 12;
        setDropPos({
          top: 72,
          left: margin,
          width: window.innerWidth - margin * 2,
          maxListHeight: window.innerHeight - 72 - 56, // leave room at bottom
        });
      } else {
        const dropHeight = 280;
        const spaceBelow = window.innerHeight - r.bottom;
        const spaceAbove = r.top;
        const showAbove = spaceBelow < dropHeight && spaceAbove > spaceBelow;
        setDropPos({
          top: showAbove ? r.top - dropHeight - 4 : r.bottom + 4,
          left: r.left,
          width: r.width,
          maxListHeight: 220,
        });
      }
    }
  };

  useEffect(() => {
    if (!open) return;
    const onScroll = () => calcPos();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => { window.removeEventListener('scroll', onScroll, true); window.removeEventListener('resize', onScroll); };
  }, [open]); // eslint-disable-line

  const openDropdown = () => {
    calcPos();
    setOpen(o => !o);
    setQuery('');
  };

  useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);

  const filtered = options.filter(o => (o.label || o).toLowerCase().includes(query.toLowerCase()));
  const selected = options.find(o => (o.value ?? o) === value);

  return (
    <div className="form-group" style={containerStyle}>
      {label && <label className="form-label">{label}</label>}
      <div ref={triggerRef} className="form-input" onClick={openDropdown}
        style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', userSelect: 'none' }}>
        <span style={{ color: selected ? 'var(--text1)' : 'var(--text3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selected ? (selected.label ?? selected) : '— Select —'}
        </span>
        <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} style={{ flexShrink: 0, marginLeft: 6, color: 'var(--text3)', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}><path d="M6 9l6 6 6-6"/></svg>
      </div>
      {open && createPortal(
        <div id="ss-dropdown" className={themeClass} style={{ position: 'fixed', top: dropPos.top, left: dropPos.left, width: dropPos.width, zIndex: 9999, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, boxShadow: '0 6px 24px rgba(0,0,0,0.35)', overflow: 'hidden' }}>
          <div style={{ padding: '8px 8px 4px' }}>
            <input ref={inputRef} className="form-input" style={{ fontSize: 13, padding: '6px 10px' }}
              placeholder="Search…" value={query} onChange={e => setQuery(e.target.value)} onClick={e => e.stopPropagation()} />
          </div>
          <div style={{ maxHeight: dropPos.maxListHeight ?? 220, overflowY: 'auto', padding: '0 4px 6px' }}>
            {filtered.length === 0
              ? <div style={{ padding: '10px 10px', fontSize: 13, color: 'var(--text3)' }}>No results</div>
              : filtered.map(o => {
                  const v = o.value ?? o; const l = o.label ?? o;
                  const active = v === value;
                  return (
                    <div key={v} onClick={() => { onChange(v); setOpen(false); setQuery(''); }}
                      style={{ padding: '9px 10px', fontSize: 13, borderRadius: 7, cursor: 'pointer', background: active ? 'var(--accent-bg)' : 'transparent', color: active ? 'var(--accent)' : 'var(--text1)', fontWeight: active ? 600 : 400, transition: 'background 0.1s' }}
                      onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'var(--bg3)'; }}
                      onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}>
                      {l}
                    </div>
                  );
                })
            }
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

function MultiSearchSelect({ label, value = [], onChange, options }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 0, maxListHeight: 220 });
  const triggerRef = useRef(null);
  const inputRef = useRef(null);
  const themeClass = document.querySelector('.app')?.classList.contains('night') ? 'night' : 'day';

  useEffect(() => {
    const h = e => {
      if (!triggerRef.current?.contains(e.target) && !document.getElementById('mss-dropdown')?.contains(e.target)) {
        setOpen(false); setQuery('');
      }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const calcPos = () => {
    if (triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      const isMobile = window.innerWidth < 768;
      if (isMobile) {
        const margin = 12;
        setDropPos({ top: 72, left: margin, width: window.innerWidth - margin * 2, maxListHeight: window.innerHeight - 72 - 56 });
      } else {
        const dropHeight = 300;
        const spaceBelow = window.innerHeight - r.bottom;
        const spaceAbove = r.top;
        const showAbove = spaceBelow < dropHeight && spaceAbove > spaceBelow;
        setDropPos({ top: showAbove ? r.top - dropHeight - 4 : r.bottom + 4, left: r.left, width: r.width, maxListHeight: 220 });
      }
    }
  };

  useEffect(() => {
    if (!open) return;
    const onScroll = () => calcPos();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => { window.removeEventListener('scroll', onScroll, true); window.removeEventListener('resize', onScroll); };
  }, [open]); // eslint-disable-line

  const openDropdown = () => { calcPos(); setOpen(o => !o); setQuery(''); };
  useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);

  const filtered = options.filter(o => (o.label ?? o).toLowerCase().includes(query.toLowerCase()));

  const toggle = v => {
    if (v === 'Building Wide') { onChange(['Building Wide']); return; }
    const without = (value || []).filter(x => x !== 'Building Wide');
    onChange(without.includes(v) ? without.filter(x => x !== v) : [...without, v]);
  };

  const sel = value || [];
  const displayText = sel.length === 0 ? null : sel.length === 1 ? (options.find(o => (o.value ?? o) === sel[0])?.label ?? sel[0]) : `${sel.length} units selected`;

  return (
    <div className="form-group">
      {label && <label className="form-label">{label}</label>}
      <div ref={triggerRef} className="form-input" onClick={openDropdown}
        style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', userSelect: 'none', minHeight: 38 }}>
        <span style={{ color: sel.length ? 'var(--text1)' : 'var(--text3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
          {displayText || '— Select —'}
        </span>
        <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} style={{ flexShrink: 0, marginLeft: 6, color: 'var(--text3)', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}><path d="M6 9l6 6 6-6"/></svg>
      </div>
      {open && createPortal(
        <div id="mss-dropdown" className={themeClass} style={{ position: 'fixed', top: dropPos.top, left: dropPos.left, width: dropPos.width, zIndex: 9999, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, boxShadow: '0 6px 24px rgba(0,0,0,0.35)', overflow: 'hidden' }}>
          <div style={{ padding: '8px 8px 4px' }}>
            <input ref={inputRef} className="form-input" style={{ fontSize: 13, padding: '6px 10px' }}
              placeholder="Search…" value={query} onChange={e => setQuery(e.target.value)} onClick={e => e.stopPropagation()} />
          </div>
          <div style={{ maxHeight: dropPos.maxListHeight ?? 220, overflowY: 'auto', padding: '0 4px 6px' }}>
            {filtered.length === 0
              ? <div style={{ padding: '10px 10px', fontSize: 13, color: 'var(--text3)' }}>No results</div>
              : filtered.map(o => {
                  const v = o.value ?? o; const l = o.label ?? o;
                  const active = sel.includes(v);
                  return (
                    <div key={v} onClick={() => toggle(v)}
                      style={{ padding: '9px 10px', fontSize: 13, borderRadius: 7, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, background: active ? 'var(--accent-bg)' : 'transparent', color: active ? 'var(--accent)' : 'var(--text1)', transition: 'background 0.1s' }}
                      onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'var(--bg3)'; }}
                      onMouseLeave={e => { if (!active) e.currentTarget.style.background = active ? 'var(--accent-bg)' : 'transparent'; }}>
                      <div style={{ width: 15, height: 15, border: `2px solid ${active ? 'var(--accent)' : 'var(--text3)'}`, borderRadius: 3, background: active ? 'var(--accent)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.1s' }}>
                        {active && <svg width={9} height={9} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={3.5}><path d="M20 6L9 17l-5-5"/></svg>}
                      </div>
                      {l}
                    </div>
                  );
                })
            }
          </div>
          {sel.length > 0 && (
            <div style={{ padding: '6px 10px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 11, color: 'var(--text3)' }}>{sel.length} selected</span>
              <button onClick={() => { setOpen(false); setQuery(''); }} style={{ fontSize: 12, color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}>Done</button>
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}

function MultiSelect({ label, options, value, onChange, placeholder = 'Select…' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = value || [];

  useEffect(() => {
    if (!open) return;
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const displayText = selected.length === 0 ? placeholder : selected.join(', ');

  return (
    <div ref={ref} className="form-group" style={{ position: 'relative' }}>
      {label && <label className="form-label">{label}</label>}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{ width: '100%', textAlign: 'left', padding: '8px 10px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, color: selected.length ? 'var(--text1)' : 'var(--text3)', fontSize: 13, fontFamily: 'inherit', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{displayText}</span>
        <span style={{ flexShrink: 0, fontSize: 10 }}>▾</span>
      </button>
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, zIndex: 200, maxHeight: 220, overflowY: 'auto', boxShadow: 'var(--card-shadow)', marginTop: 2 }}>
          {options.length === 0
            ? <div style={{ padding: '10px 12px', fontSize: 12, color: 'var(--text3)' }}>No options available</div>
            : options.map(opt => {
                const checked = selected.includes(opt.value);
                return (
                  <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid var(--border)', background: checked ? 'var(--accent-bg)' : 'transparent' }}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={e => onChange(e.target.checked ? [...selected, opt.value] : selected.filter(v => v !== opt.value))}
                      style={{ accentColor: 'var(--accent)', cursor: 'pointer' }}
                    />
                    {opt.label}
                  </label>
                );
              })
          }
        </div>
      )}
    </div>
  );
}

// ── date helpers (NY timezone, MM/DD/YYYY) ────────────────────────────────
const fmtDate = iso => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${m}/${d}/${y}`;
};
const nyTodayISO = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });

// Canonical HPD violation class format — long labels everywhere
const HPD_CLASS_LABELS = { A: 'A — Immediately Hazardous', B: 'B — Hazardous', C: 'C — Non-Hazardous' };
const hpdClassLabel = c => HPD_CLASS_LABELS[(c || '').toUpperCase().trim()] || (c || '');
const isExpiredISO = iso => iso && new Date(iso) <= new Date(nyTodayISO());
// Task-specific: strictly before today = overdue (red), equals today = due today (amber)
const isTaskOverdue  = iso => !!iso && iso < nyTodayISO();
const isTaskDueToday = iso => !!iso && iso === nyTodayISO();
// A task status is only ever "Active" or "Closed". The status controls in this app write
// nothing else, and every API write path coerces to the same two values (normTaskStatus on
// the purpose-built actions, and the same coercion on setDoc), so the legacy spellings this
// used to fold can no longer be written. taskStatus() is the one place that decides which
// of the two a task is; the comparison stays case-insensitive as a cheap guard.
const taskStatus = t => String((t && t.status) || '').trim().toLowerCase() === 'closed' ? 'Closed' : 'Active';
const taskActive = t => taskStatus(t) === 'Active';

// The only task categories (stored on task.category). taskCategory() folds any
// legacy value ("Inspection Schedule" → Inspection; High/Medium/Low/blank → General)
// so old records still display sensibly without special-casing everywhere.
const TASK_CATEGORY_LIST = ['Inspection', 'Maintenance', 'General', 'Invoice', 'Renewal', 'Collection', 'Complaint'];

// Assignee scoping for the Tasks tab, shared by the list and the category badges so the
// two can never disagree. `filter` is 'all' | 'me' | a user-record name;
// `myName` is the current user's record name. Names are compared trimmed and
// case-insensitively — historical rows carry trailing spaces and odd casing.
const taskAssignedList = t => Array.isArray(t.assignedTo) ? t.assignedTo : (t.assignedTo ? [t.assignedTo] : []);
// `assignedToIds` holds user-record ids and is the source of truth for WHO a task belongs
// to; `assignedTo` is the display copy of their names, kept so anything reading a task
// document (the bot API, readApi) gets a readable name without joining to users. Identity
// questions go through the ids — a name is a label that can drift, be re-spelled, or carry
// stray whitespace, and matching on it is how tasks used to go missing.
const taskAssignedIds = t => Array.isArray(t.assignedToIds) ? t.assignedToIds : [];
const normName = s => String(s === undefined || s === null ? '' : s).trim().toLowerCase();
// Resolve display names to user ids, matched the forgiving way so an odd-spaced or
// differently-cased name still finds its person.
const assigneeIdsFor = (names, users) => (names || [])
  .map(n => (users || []).find(u => normName(u.name) === normName(n))?.id)
  .filter(Boolean);
// Always set both fields together — no write path should be able to update one and
// leave the other behind.
const withAssignees = (names, users) => ({
  assignedTo: names || [],
  assignedToIds: assigneeIdsFor(names, users),
});
// Every path that creates a task seeds the owner rather than leaving the assignee empty:
// a task with nobody named on it is the one that goes missing. The owner is whichever
// user record carries ADMIN_EMAIL, not a hardcoded name, so a rename — or a change of
// owner — carries automatically. Mirrors defaultToOwner in the bot API.
const ownerAssignee = users => {
  const owner = (users || []).find(u => userMatchesEmail(u, ADMIN_EMAIL));
  return owner
    ? { assignedTo: [owner.name].filter(Boolean), assignedToIds: [owner.id] }
    : { assignedTo: [], assignedToIds: [] };
};
// Is this task assigned to this user record? Prefers ids; falls back to a normalized name
// comparison for any task not yet carrying ids.
const isAssignedToUser = (t, u) => {
  if (!u) return false;
  const ids = taskAssignedIds(t);
  if (ids.length) return ids.includes(u.id);
  return taskAssignedList(t).some(n => normName(n) === normName(u.name));
};
// `filter` is 'all' | 'me' | a user-record name. Pass `users` so a name
// filter can be resolved to an id and compared the reliable way.
const matchesTaskAssignee = (t, filter, myName, users) => {
  if (!filter || filter === 'all') return true;
  const list = taskAssignedList(t);
  const want = filter === 'me' ? (myName || '') : filter;
  if (!want) return true; // no user record to match on — don't hide everything
  const wantUser = (users || []).find(u => normName(u.name) === normName(want));
  if (wantUser) return isAssignedToUser(t, wantUser);
  return list.some(n => normName(n) === normName(want));
};

// Task titles are capped so they stay scannable in the list and the detail goes in the
// description instead. Enforced in the app, the bot API and the in-app AI — change this
// number and the matching TASK_TITLE_MAX in functions/index.js together.
const TASK_TITLE_MAX = 100;
// `category` replaced the historical `priority` field name (which never held a priority).
// Reads fall back to `priority` so task docs written before the rename — or by a stale
// browser tab — still display correctly. Drop the fallback once every doc has converted.
const taskCategory = t => {
  const p = (t && (t.category ?? t.priority)) || '';
  if (p === 'Inspection Schedule') return 'Inspection';
  if (p === 'Invoice/Expense') return 'Invoice';
  return TASK_CATEGORY_LIST.includes(p) ? p : 'General';
};
// Normalize a task doc read from Firestore onto the new field name. Applied at load so
// everything downstream — and everything written back — speaks `category` only.
const taskWithCategory = t => {
  if (!t || t.priority === undefined) return t;
  const { priority, ...rest } = t;
  return { ...rest, category: t.category ?? priority };
};
function TaskCatBadge({ task, small }) {
  const map = {
    Inspection:        ['badge-blue',   null, 'Inspection'],
    Maintenance:       ['badge-orange', null, 'Maintenance'],
    General:           ['badge-gray',   null, 'General'],
    Invoice:           ['badge-amber',  null, 'Invoice'],
    Renewal:           ['badge-green',  null, 'Renewal'],
    Collection:        ['',             { background: 'var(--accent-bg)', color: 'var(--accent)' }, 'Collection'],
    Complaint:         ['',             { background: 'rgba(225,29,72,0.12)', color: '#e11d48' }, 'Complaint'],
  };
  const [cls, style, label] = map[taskCategory(task)];
  return <span className={`badge ${cls}`} style={{ ...(small ? { fontSize: 10 } : {}), ...(style || {}) }}>{label}</span>;
}

// Every unit a task applies to — app tasks carry maintUnit[] (or a string), bot tasks
// carry a unit string, and both may encode the unit as the middle segment of a
// "street | unit | description" title.
const taskUnitSet = t => {
  const set = new Set();
  if (t.unit) set.add(String(t.unit).trim());
  if (Array.isArray(t.maintUnit)) t.maintUnit.forEach(u => u && set.add(String(u).trim()));
  else if (typeof t.maintUnit === 'string' && t.maintUnit) set.add(t.maintUnit.trim());
  const parts = String(t.title || '').split('|').map(s => s.trim());
  if (parts.length >= 3 && parts[1]) set.add(parts[1]);
  return set;
};
// Does the title mention this unit as a whole token? ("2nd Floor" in "RABAB - 2nd
// Floor | …") without matching "4E" inside "14E".
const titleMentionsUnit = (title, unit) => {
  if (!unit) return false;
  const esc = String(unit).toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`(^|[^a-z0-9])${esc}([^a-z0-9]|$)`, 'i').test(String(title || '').toLowerCase());
};
// CANONICAL: does this task belong to this tenant? Used by the tenant Activity
// card. Match order: explicit tenant
// link → unit match (scoped to the same property) → unit named in the title.
const taskBelongsToTenant = (t, tenant) => {
  if (!t || !tenant) return false;
  if (t.tenantId && t.tenantId === tenant.id) return true;
  if (tenant.propertyId && t.propertyId && t.propertyId !== tenant.propertyId) return false;
  const u = (tenant.unit || '').trim();
  if (!u) return false;
  return taskUnitSet(t).has(u) || titleMentionsUnit(t.title, u);
};
const invPaid = inv => (inv.payments || []).reduce((s, p) => s + (Number(p.amount) || 0), 0);
const isAlmostDueISO = iso => { if (!iso) return false; const today = new Date(nyTodayISO()); const due = new Date(iso); const daysUntil = (due - today) / 86400000; return daysUntil > 0 && daysUntil <= 56; };
const isLeaseExpiringSoon = iso => { if (!iso) return false; const today = new Date(nyTodayISO()); const due = new Date(iso); const daysUntil = (due - today) / 86400000; return daysUntil > 0 && daysUntil <= 60; };
const addMonths = (dateISO, n) => { const d = new Date(dateISO); d.setMonth(d.getMonth() + n); return d.toISOString().slice(0, 10); };
const fmtSize = bytes => bytes == null ? '' : bytes < 1024 ? `${bytes} B` : bytes < 1048576 ? `${(bytes / 1024).toFixed(1)} KB` : `${(bytes / 1048576).toFixed(1)} MB`;

const INS_TYPES = [
  'Commercial General Liability',
  'Commercial Property Coverage',
  'Workers Compensation',
  'Automobile Policy',
  'Umbrella Liability',
  'Paid Leaves and Benefits',
  'Dishonesty Bond',
  'Fidelity Bonds',
];

// ── pages ──────────────────────────────────────────────────────────────────
// ── Property Detail ────────────────────────────────────────────────────────
function PropertyDetail({ propertyId, properties, setProperties, tenants, user, onBack }) {
  const toast = useToast();
  const isAdmin = user?.email === ADMIN_EMAIL;
  const prop = properties.find(p => p.id === propertyId);

  // ── Property-specific audit log ──
  // Appends a plain-text entry to this property's own auditLog array (separate
  // from the global system audit log). Section = Overview | Insurance | Compliance
  // | Units | Documents. Persists with the property doc.
  const logAudit = (section, action, detail, category) => setProperties(prev => prev.map(p => p.id === propertyId ? {
    ...p,
    auditLog: [
      ...(p.auditLog || []),
      { id: `pa_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, timestamp: new Date().toISOString(), section, category: category || '', action, detail: detail || '', by: user?.email || 'Unknown' },
    ],
  } : p));

  const archiveProp = () => {
    if (!window.confirm('Archive this property? It will be hidden from active views but all data is preserved and can be restored.')) return;
    setProperties(prev => prev.map(p => p.id === propertyId ? { ...p, archived: true, archivedAt: new Date().toISOString() } : p));
    logAudit('Overview', 'Archived', 'Property archived');
    toast('Property archived', 'info');
    onBack();
  };
  const unarchiveProp = () => {
    setProperties(prev => prev.map(p => p.id === propertyId ? { ...p, archived: false, archivedAt: null } : p));
    logAudit('Overview', 'Restored', 'Property restored');
    toast('Property restored');
  };
  const [editingInfo, setEditingInfo] = useState(false);
  const [ef, setEf] = useState({});
  const [finModal, setFinModal] = useState(false);
  const [finForm, setFinForm] = useState({ zelles: [], bankAccounts: [] });
  const [auditOpen, setAuditOpen] = useState(false);
  const [printModal, setPrintModal] = useState(false);
  const [printSections, setPrintSections] = useState({ overview: true, insurance: true, compliance: true, units: true });
  const [printFormat, setPrintFormat] = useState('pdf');
  const [insModal, setInsModal] = useState(null);
  const [insForm, setInsForm] = useState({});
  const [showArchivedIns, setShowArchivedIns] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => { const h = () => setIsMobile(window.innerWidth < 768); window.addEventListener('resize', h); return () => window.removeEventListener('resize', h); }, []);
  const [viewIns, setViewIns] = useState(null);
  const [compModal, setCompModal] = useState(null); // null | 'add' | 'edit'
  const [compForm, setCompForm] = useState({});
  const [unitModal, setUnitModal] = useState(null); // null | 'add' | 'edit'
  const [unitForm, setUnitForm] = useState({});
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [uploadingCompDoc, setUploadingCompDoc] = useState(false);
  const [propTab, setPropTab] = useState('overview');
  const [uploadingPropDoc, setUploadingPropDoc] = useState(false);
  const [renamingDocId, setRenamingDocId] = useState(null);
  const [renameVal, setRenameVal] = useState('');
  const [docSearch, setDocSearch] = useState('');
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [newFolderModal, setNewFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [renamingFolderId, setRenamingFolderId] = useState(null);
  const [renameFolderVal, setRenameFolderVal] = useState('');
  const [docDragOver, setDocDragOver] = useState(false);
  const [draggedDocId, setDraggedDocId] = useState(null); // doc being dragged to a folder
  const [dropFolderId, setDropFolderId] = useState(undefined); // folder currently hovered as a drop target ('' = root)
  const docDragCounter = useRef(0);

  if (!prop) return null;

  const insurance = prop.insurance || [];
  const compliance = (prop.compliance || []).slice().sort((a, b) => {
    const aOver = isExpiredISO(a.dueDate) ? 0 : 1;
    const bOver = isExpiredISO(b.dueDate) ? 0 : 1;
    if (aOver !== bOver) return aOver - bOver;
    if (!a.dueDate && !b.dueDate) return 0;
    if (!a.dueDate) return 1;
    if (!b.dueDate) return -1;
    return a.dueDate.localeCompare(b.dueDate);
  });
  const unitsList = prop.unitsList || [];

  const updateProp = fn => setProperties(prev => prev.map(p => p.id === propertyId ? fn(p) : p));

  const saveInfo = () => { updateProp(p => ({ ...ef, insurance: prop.insurance || [], compliance: prop.compliance || [], unitsList: prop.unitsList || [], auditLog: p.auditLog || [] })); logAudit('Overview', 'Updated', 'Edited property info'); setEditingInfo(false); toast('Property info saved'); };

  // Financial account: multiple Zelle emails + multiple bank accounts.
  // Reads legacy single zelle / bankNumber as a fallback for older records.
  const propZelles = Array.isArray(prop.zelles) ? prop.zelles.filter(Boolean) : (prop.zelle ? [prop.zelle] : []);
  const propBanks = Array.isArray(prop.bankAccounts) ? prop.bankAccounts : (prop.bankNumber ? [{ id: 'legacy', accountName: '', accountNumber: prop.bankNumber, routingNumber: '' }] : []);
  const openEditFinancial = () => {
    const zelles = Array.isArray(prop.zelles) ? [...prop.zelles] : (prop.zelle ? [prop.zelle] : []);
    const bankAccounts = Array.isArray(prop.bankAccounts)
      ? prop.bankAccounts.map(b => ({ ...b }))
      : (prop.bankNumber ? [{ id: `bank_${Date.now()}`, accountName: '', accountNumber: prop.bankNumber, routingNumber: '' }] : []);
    setFinForm({ zelles: zelles.length ? zelles : [''], bankAccounts });
    setFinModal(true);
  };
  const saveFinancial = () => {
    const zelles = (finForm.zelles || []).map(z => (z || '').trim()).filter(Boolean);
    const bankAccounts = (finForm.bankAccounts || [])
      .map(b => ({ id: b.id, accountName: (b.accountName || '').trim(), accountNumber: (b.accountNumber || '').trim(), routingNumber: (b.routingNumber || '').trim() }))
      .filter(b => b.accountName || b.accountNumber || b.routingNumber);

    // Diff against current values so the audit log names exactly what changed.
    const oldZelles = Array.isArray(prop.zelles) ? prop.zelles : (prop.zelle ? [prop.zelle] : []);
    const oldBanks = Array.isArray(prop.bankAccounts) ? prop.bankAccounts : (prop.bankNumber ? [{ id: 'legacy', accountName: '', accountNumber: prop.bankNumber, routingNumber: '' }] : []);

    updateProp(p => ({ ...p, zelles, bankAccounts }));

    zelles.filter(z => !oldZelles.includes(z)).forEach(z => logAudit('Overview', 'Added', z, 'Zelle'));
    oldZelles.filter(z => !zelles.includes(z)).forEach(z => logAudit('Overview', 'Removed', z, 'Zelle'));

    const oldBankById = Object.fromEntries(oldBanks.map(b => [b.id, b]));
    const newBankIds = new Set(bankAccounts.map(b => b.id));
    bankAccounts.forEach(b => {
      const label = b.accountName || b.accountNumber || 'bank account';
      const old = oldBankById[b.id];
      if (!old) { logAudit('Overview', 'Added', `${label}${b.accountNumber ? ` (acct ${b.accountNumber})` : ''}`, 'Bank'); return; }
      const changes = [];
      if ((old.accountName || '') !== b.accountName) changes.push(`account name → ${b.accountName || '—'}`);
      if ((old.accountNumber || '') !== b.accountNumber) changes.push(`account number → ${b.accountNumber || '—'}`);
      if ((old.routingNumber || '') !== b.routingNumber) changes.push(`routing number → ${b.routingNumber || '—'}`);
      if (changes.length) logAudit('Overview', 'Updated', `${label}: ${changes.join(', ')}`, 'Bank');
    });
    oldBanks.forEach(b => { if (!newBankIds.has(b.id)) logAudit('Overview', 'Removed', b.accountName || b.accountNumber || 'bank account', 'Bank'); });

    setFinModal(false);
    toast('Financial account saved');
  };

  const openAddIns = () => { setInsForm({ type: INS_TYPES[0], name: '', policyNumber: '', startDate: '', endDate: '', phone: '', email: '', brokerName: '', brokerPhone: '', brokerEmail: '' }); setInsModal('add'); };
  const openEditIns = ins => { setInsForm({ ...ins }); setInsModal('edit'); };
  const saveIns = () => { updateProp(p => ({ ...p, insurance: [...(p.insurance || []), { ...insForm, id: `ins${Date.now()}`, documents: [] }] })); logAudit('Insurance', 'Created', insForm.name || 'Insurance policy', insForm.type); setInsModal(null); toast('Insurance added'); };
  // Preserve live documents/archived flag so an edit doesn't clobber uploads made outside the form.
  const updateIns = () => { updateProp(p => ({ ...p, insurance: p.insurance.map(i => i.id === insForm.id ? { ...insForm, documents: i.documents || [], archived: i.archived || false } : i) })); logAudit('Insurance', 'Updated', insForm.name || 'Insurance policy', insForm.type); setInsModal(null); toast('Insurance updated'); };
  const delIns = id => { if (window.confirm('Remove this insurance?')) { const gone = (prop.insurance || []).find(i => i.id === id); updateProp(p => ({ ...p, insurance: (p.insurance || []).filter(i => i.id !== id) })); logAudit('Insurance', 'Deleted', gone?.name || 'Insurance policy', gone?.type); toast('Insurance removed', 'info'); } };
  const archiveIns = id => { const it = (prop.insurance || []).find(i => i.id === id); updateProp(p => ({ ...p, insurance: p.insurance.map(i => i.id === id ? { ...i, archived: true } : i) })); logAudit('Insurance', 'Archived', it?.name || 'Insurance policy', it?.type); toast('Insurance archived', 'info'); };
  const restoreIns = id => { const it = (prop.insurance || []).find(i => i.id === id); updateProp(p => ({ ...p, insurance: p.insurance.map(i => i.id === id ? { ...i, archived: false } : i) })); logAudit('Insurance', 'Restored', it?.name || 'Insurance policy', it?.type); toast('Insurance restored'); };

  const handleDocUpload = async (e, insId) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    setUploadingDoc(true);
    try {
      const path = `properties/${propertyId}/insurance/${insId}/${Date.now()}_${file.name}`;
      const storageRef = sRef(storage, path);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      const newDoc = { id: `doc${Date.now()}`, name: file.name, url, storagePath: path, uploadedAt: new Date().toISOString() };
      updateProp(p => ({ ...p, insurance: p.insurance.map(i => i.id === insId ? { ...i, documents: [...(i.documents || []), newDoc] } : i) }));
      logAudit('Insurance', 'Document uploaded', file.name, 'Document');
      setViewIns(v => v ? { ...v, documents: [...(v.documents || []), newDoc] } : v);
    } catch (err) {
      console.error('Upload failed:', err);
      alert('Upload failed. Please try again.');
    }
    setUploadingDoc(false);
  };

  const handleDocDelete = async (insId, docEntry) => {
    if (!window.confirm(`Remove "${docEntry.name}"?`)) return;
    try {
      if (docEntry.storagePath) {
        await deleteObject(sRef(storage, docEntry.storagePath));
      }
    } catch (err) {
      console.error('Storage delete failed:', err);
    }
    updateProp(p => ({ ...p, insurance: p.insurance.map(i => i.id === insId ? { ...i, documents: (i.documents || []).filter(d => d.id !== docEntry.id) } : i) }));
    logAudit('Insurance', 'Document deleted', docEntry.name, 'Document');
    setViewIns(v => v ? { ...v, documents: (v.documents || []).filter(d => d.id !== docEntry.id) } : v);
  };

  const openAddComp = () => { setCompForm({ title: '', dueDate: '', instructions: '' }); setCompModal('add'); };
  const openEditComp = c => { setCompForm({ ...c }); setCompModal('edit'); };
  const saveComp = () => { updateProp(p => ({ ...p, compliance: [...(p.compliance || []), { ...compForm, id: `comp${Date.now()}`, documents: [] }] })); logAudit('Compliance', 'Created', compForm.title || 'Compliance item'); setCompModal(null); toast('Compliance item added'); };
  const updateComp = () => { updateProp(p => ({ ...p, compliance: p.compliance.map(c => c.id === compForm.id ? { ...compForm, documents: compForm.documents || [] } : c) })); logAudit('Compliance', 'Updated', compForm.title || 'Compliance item'); setCompModal(null); toast('Compliance item updated'); };

  const handleCompDocUpload = async (e, compId) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    setUploadingCompDoc(true);
    try {
      const path = `properties/${propertyId}/compliance/${compId}/${Date.now()}_${file.name}`;
      const storageRef = sRef(storage, path);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      const newDoc = { id: `doc${Date.now()}`, name: file.name, url, storagePath: path, uploadedAt: new Date().toISOString() };
      updateProp(p => ({ ...p, compliance: p.compliance.map(c => c.id === compId ? { ...c, documents: [...(c.documents || []), newDoc] } : c) }));
      logAudit('Compliance', 'Document uploaded', file.name, 'Document');
    } catch (err) {
      console.error('Upload failed:', err);
      alert('Upload failed. Please try again.');
    }
    setUploadingCompDoc(false);
  };

  const handleCompDocDelete = async (compId, docEntry) => {
    if (!window.confirm(`Remove "${docEntry.name}"?`)) return;
    try {
      if (docEntry.storagePath) await deleteObject(sRef(storage, docEntry.storagePath));
    } catch (err) { console.error(err); }
    updateProp(p => ({ ...p, compliance: p.compliance.map(c => c.id === compId ? { ...c, documents: (c.documents || []).filter(d => d.id !== docEntry.id) } : c) }));
    logAudit('Compliance', 'Document deleted', docEntry.name, 'Document');
  };
  const delComp = id => { if (window.confirm('Remove compliance item?')) { const gone = (prop.compliance || []).find(c => c.id === id); updateProp(p => ({ ...p, compliance: (p.compliance || []).filter(c => c.id !== id) })); logAudit('Compliance', 'Deleted', gone?.title || 'Compliance item'); toast('Compliance item removed', 'info'); } };

  // Unit fields tracked in the audit log — [display column, object key]
  const UNIT_FIELDS = [['Unit #', 'unitNumber'], ['Type', 'unitType'], ['Bedrooms', 'bedrooms'], ['Bathrooms', 'bathrooms'], ['Size', 'unitSize'], ['Balcony', 'balcony']];
  const saveUnit = () => {
    if (unitModal === 'edit') {
      const before = (prop.unitsList || []).find(u => u.id === unitForm.id) || {};
      updateProp(p => ({ ...p, unitsList: (p.unitsList || []).map(u => u.id === unitForm.id ? { ...unitForm } : u) }));
      const unitLabel = `Unit ${unitForm.unitNumber || before.unitNumber || ''}`.trim();
      // Log one entry per changed field, naming the column and the old → new value.
      let changed = false;
      UNIT_FIELDS.forEach(([col, key]) => {
        const was = before[key] == null ? '' : String(before[key]);
        const now = unitForm[key] == null ? '' : String(unitForm[key]);
        if (was !== now) { changed = true; logAudit('Units', 'Updated', `${unitLabel}: ${was || '—'} → ${now || '—'}`, col); }
      });
      if (!changed) logAudit('Units', 'Updated', unitLabel, 'Unit');
      toast('Unit updated');
    } else {
      updateProp(p => ({ ...p, unitsList: [...(p.unitsList || []), { ...unitForm, id: `unit${Date.now()}` }] }));
      logAudit('Units', 'Created', `Unit ${unitForm.unitNumber || ''}`.trim(), 'Unit');
      toast('Unit added');
    }
    setUnitModal(null);
  };
  const editUnit = u => { setUnitForm({ ...u }); setUnitModal('edit'); };
  const delUnit = id => { if (window.confirm('Remove unit?')) { const gone = (prop.unitsList || []).find(u => u.id === id); updateProp(p => ({ ...p, unitsList: (p.unitsList || []).filter(u => u.id !== id) })); logAudit('Units', 'Deleted', `Unit ${gone?.unitNumber || ''}`.trim(), 'Unit'); toast('Unit removed', 'info'); } };

  const handlePropDocUpload = async e => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (e.target.value !== undefined) e.target.value = '';
    setUploadingPropDoc(true);
    try {
      const newDocs = await Promise.all(files.map(async file => {
        const path = `properties/${propertyId}/documents/${Date.now()}_${file.name}`;
        const storageRef = sRef(storage, path);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        return { id: `pdoc${Date.now()}${Math.random().toString(36).slice(2)}`, name: file.name, url, storagePath: path, uploadedAt: new Date().toISOString(), folderId: currentFolderId || null, size: file.size };
      }));
      updateProp(p => ({ ...p, documents: [...(p.documents || []), ...newDocs] }));
      logAudit('Documents', 'Uploaded', newDocs.map(d => d.name).join(', '), 'File');
    } catch (err) { alert('Upload failed. Please try again.'); }
    setUploadingPropDoc(false);
  };

  const handlePropDocDelete = async doc => {
    if (!window.confirm(`Delete "${doc.name}"?`)) return;
    try { if (doc.storagePath) await deleteObject(sRef(storage, doc.storagePath)); } catch {}
    updateProp(p => ({ ...p, documents: (p.documents || []).filter(d => d.id !== doc.id) }));
    logAudit('Documents', 'Deleted', doc.name, 'File');
  };

  const handlePropDocRename = docId => {
    if (!renameVal.trim()) { setRenamingDocId(null); return; }
    const before = (prop.documents || []).find(d => d.id === docId)?.name;
    updateProp(p => ({ ...p, documents: (p.documents || []).map(d => d.id === docId ? { ...d, name: renameVal.trim() } : d) }));
    logAudit('Documents', 'Renamed', `${before || 'file'} → ${renameVal.trim()}`, 'File');
    setRenamingDocId(null);
  };

  const createFolder = () => {
    if (!newFolderName.trim()) return;
    const folder = { id: `fldr${Date.now()}`, name: newFolderName.trim(), createdAt: new Date().toISOString(), parentId: currentFolderId || null };
    updateProp(p => ({ ...p, folders: [...(p.folders || []), folder] }));
    logAudit('Documents', 'Created', folder.name, 'Folder');
    setNewFolderModal(false);
    setNewFolderName('');
  };

  const renameFolder = folderId => {
    if (!renameFolderVal.trim()) { setRenamingFolderId(null); return; }
    updateProp(p => ({ ...p, folders: (p.folders || []).map(f => f.id === folderId ? { ...f, name: renameFolderVal.trim() } : f) }));
    logAudit('Documents', 'Renamed', renameFolderVal.trim(), 'Folder');
    setRenamingFolderId(null);
  };

  // OS-file drags (uploading) carry a 'Files' type; internal doc-move drags don't —
  // so the "Drop files to upload" overlay only reacts to real files.
  const dragHasFiles = e => Array.from(e.dataTransfer?.types || []).includes('Files');
  const handleDocAreaDragEnter = e => { if (!dragHasFiles(e)) return; e.preventDefault(); docDragCounter.current++; if (docDragCounter.current === 1) setDocDragOver(true); };
  const handleDocAreaDragLeave = e => { if (!dragHasFiles(e)) return; e.preventDefault(); docDragCounter.current--; if (docDragCounter.current === 0) setDocDragOver(false); };
  const handleDocAreaDragOver = e => { if (!dragHasFiles(e)) return; e.preventDefault(); };
  const handleDocAreaDrop = async e => {
    if (!dragHasFiles(e)) return;
    e.preventDefault();
    docDragCounter.current = 0;
    setDocDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length) await handlePropDocUpload({ target: { files, value: '' } });
  };

  // Move a document into a folder (or to root when folderId is null) via drag & drop.
  const moveDocToFolder = (docId, folderId) => {
    const doc = (prop.documents || []).find(d => d.id === docId);
    if (!doc || (doc.folderId || null) === (folderId || null)) return;
    updateProp(p => ({ ...p, documents: (p.documents || []).map(d => d.id === docId ? { ...d, folderId: folderId || null } : d) }));
    const folderName = folderId ? ((prop.folders || []).find(f => f.id === folderId)?.name || 'folder') : 'Documents';
    logAudit('Documents', 'Moved', `${doc.name} → ${folderName}`, 'File');
    toast(`Moved to ${folderName}`);
  };

  const deleteFolder = (folderId, allFolders = prop.folders || []) => {
    // collect this folder + all descendants recursively
    const collectIds = id => {
      const children = allFolders.filter(f => f.parentId === id).map(f => f.id);
      return [id, ...children.flatMap(cid => collectIds(cid))];
    };
    const toDelete = new Set(collectIds(folderId));
    const totalDocs = (prop.documents || []).filter(d => toDelete.has(d.folderId)).length;
    const msg = totalDocs > 0
      ? `Deleting this folder and its subfolders will move ${totalDocs} document(s) to root. Continue?`
      : 'Delete this folder and all subfolders?';
    if (!window.confirm(msg)) return;
    updateProp(p => ({
      ...p,
      folders: (p.folders || []).filter(f => !toDelete.has(f.id)),
      documents: (p.documents || []).map(d => toDelete.has(d.folderId) ? { ...d, folderId: null } : d),
    }));
    logAudit('Documents', 'Deleted', (prop.folders || []).find(f => f.id === folderId)?.name || 'Folder', 'Folder');
    if (toDelete.has(currentFolderId)) setCurrentFolderId(null);
  };

  const InfoItem = ({ label, value }) => (
    <div><div className="info-label">{label}</div><div className="info-val">{value || '—'}</div></div>
  );
  const handlePrint = (sections = { overview: true, insurance: true, compliance: true, units: true }) => {
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const margin = 48;
    const contentW = pageW - margin * 2;
    let y = margin;

    const checkPage = (needed = 24) => {
      if (y + needed > pageH - margin) { pdf.addPage(); y = margin; }
    };

    const sectionTitle = title => {
      checkPage(36);
      y += 8;
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(120, 120, 120);
      pdf.text(title.toUpperCase(), margin, y);
      y += 6;
      pdf.setDrawColor(220, 220, 220);
      pdf.line(margin, y, pageW - margin, y);
      y += 14;
      pdf.setTextColor(30, 30, 30);
    };

    const fieldGrid = (pairs, cols = 2) => {
      const colW = contentW / cols;
      let col = 0;
      for (const [label, value] of pairs) {
        if (!value) { col++; if (col >= cols) { col = 0; y += 30; } continue; }
        checkPage(30);
        const x = margin + col * colW;
        pdf.setFontSize(7.5);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(140, 140, 140);
        pdf.text(label.toUpperCase(), x, y);
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(30, 30, 30);
        pdf.text(String(value), x, y + 11);
        col++;
        if (col >= cols) { col = 0; y += 30; }
      }
      if (col > 0) y += 30;
    };

    // Join non-empty parts onto separate lines for a multi-line table cell.
    const stack = (...parts) => parts.filter(Boolean).join('\n') || '—';

    // Simple table: multi-line cells supported (row height grows to fit).
    const drawTable = (headers, colW, rows) => {
      checkPage(28);
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(100, 100, 100);
      let hx = margin;
      headers.forEach((h, i) => { pdf.text(h, hx, y); hx += colW[i]; });
      y += 5;
      pdf.setDrawColor(200, 200, 200);
      pdf.line(margin, y, pageW - margin, y);
      y += 12;
      for (const row of rows) {
        const cellLines = row.map((cell, i) => pdf.splitTextToSize(String(cell == null ? '' : cell), colW[i] - 6));
        const rowH = Math.max(...cellLines.map(l => l.length)) * 10 + 6;
        checkPage(rowH);
        let cx = margin;
        pdf.setFontSize(8.5);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(30, 30, 30);
        cellLines.forEach((lines, i) => { pdf.text(lines, cx, y); cx += colW[i]; }); // eslint-disable-line no-loop-func
        y += rowH;
        pdf.setDrawColor(235, 235, 235);
        pdf.line(margin, y - 3, pageW - margin, y - 3);
      }
      y += 6;
    };

    // ── Header ──
    pdf.setFontSize(20);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(20, 20, 20);
    pdf.text(prop.name, margin, y);
    y += 18;
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(110, 110, 110);
    pdf.text(prop.address || '', margin, y);
    y += 10;
    pdf.setDrawColor(60, 60, 60);
    pdf.setLineWidth(1.5);
    pdf.line(margin, y, pageW - margin, y);
    pdf.setLineWidth(0.5);
    y += 20;

    // ── Section 1: Property Profile (financial account excluded) ──
    if (sections.overview) {
      sectionTitle('Property Profile');
      fieldGrid([
        ['Property Name', prop.name],           ['Owner / Entity', prop.owner],
        ['Address', prop.address],               ['Email', prop.email],
        ['Phone', prop.phone],                   ['Business Type', prop.businessType],
        ['EIN', prop.ein],                       ['DOS ID', prop.dosId],
        ['DHCR Building Reg #', prop.dhcrRegNum], ['', ''],
        ['BIN', prop.bin],                       ['Block', prop.block],
        ['Lot', prop.lot],                       ['', ''],
      ]);
    }

    // ── Section 2: Insurance (table, documents excluded) ──
    if (sections.insurance && insurance.length > 0) {
      sectionTitle('Insurance');
      drawTable(
        ['Policy Type', 'Insurance', 'Policy #', 'Start / Term', 'Broker'],
        [88, 150, 78, 78, contentW - 394],
        insurance.map(ins => [
          ins.type || '—',
          stack(ins.name, ins.phone, ins.email),
          ins.policyNumber || '—',
          `${ins.startDate ? fmtDate(ins.startDate) : '—'} –\n${ins.endDate ? fmtDate(ins.endDate) : '—'}`,
          stack(ins.brokerName, ins.brokerPhone, ins.brokerEmail),
        ])
      );
    }

    // ── Section 3: Compliance (table, documents excluded) ──
    if (sections.compliance && compliance.length > 0) {
      sectionTitle('Compliance');
      drawTable(
        ['Title', 'Due Date', 'Instructions'],
        [130, 70, contentW - 200],
        compliance.map(c => [
          c.title || '—',
          c.dueDate ? fmtDate(c.dueDate) : '—',
          c.instructions || '—',
        ])
      );
    }

    // ── Section 4: Units (table) ──
    if (sections.units && unitsList.length > 0) {
      sectionTitle('Units');
      drawTable(
        ['Unit #', 'Occupant', 'Type', 'Bed/Bath', 'Size', 'Balcony'],
        [48, 120, 88, 56, 56, contentW - 368],
        unitsList.map(u => {
          const occ = tenants.find(t => t.propertyId === prop.id && t.unit === u.unitNumber && t.status !== 'Previous');
          return [
            u.unitNumber || '—',
            occ ? occ.name : 'VACANT',
            u.unitType || '—',
            `${u.bedrooms || '—'} / ${u.bathrooms || '—'}`,
            String(u.unitSize || '—'),
            u.balcony || '—',
          ];
        })
      );
    }

    // ── Footer on every page ──
    const total = pdf.getNumberOfPages();
    const stamp = `Generated by Amar Mgmt · ${new Date().toLocaleDateString('en-US', { timeZone: 'America/New_York' })}`;
    for (let i = 1; i <= total; i++) {
      pdf.setPage(i);
      pdf.setFontSize(7.5);
      pdf.setTextColor(170, 170, 170);
      pdf.text(stamp, margin, pageH - 20);
      pdf.text(`Page ${i} of ${total}`, pageW - margin, pageH - 20, { align: 'right' });
    }

    pdf.save(`${prop.name.replace(/[^a-z0-9]/gi, '_')}.pdf`);
  };

  // ── Export to Excel (one sheet per selected section; documents excluded) ──
  const handleExcel = (sections = { overview: true, insurance: true, compliance: true, units: true }) => {
    const wb = XLSX.utils.book_new();
    if (sections.overview) {
      const rows = [
        ['Property Name', prop.name || ''], ['Owner / Entity', prop.owner || ''],
        ['Address', prop.address || ''], ['Email', prop.email || ''], ['Phone', prop.phone || ''],
        ['Business Type', prop.businessType || ''], ['EIN', prop.ein || ''], ['DOS ID', prop.dosId || ''],
        ['DHCR Building Reg #', prop.dhcrRegNum || ''], ['BIN', prop.bin || ''], ['Block', prop.block || ''], ['Lot', prop.lot || ''],
      ];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([['Field', 'Value'], ...rows]), 'Overview');
    }
    if (sections.insurance && insurance.length) {
      const header = ['Policy Type', 'Insurance Name', 'Phone', 'Email', 'Policy Number', 'Start Date', 'Term Date', 'Broker Name', 'Broker Phone', 'Broker Email'];
      const rows = insurance.map(ins => [ins.type || '', ins.name || '', ins.phone || '', ins.email || '', ins.policyNumber || '', ins.startDate ? fmtDate(ins.startDate) : '', ins.endDate ? fmtDate(ins.endDate) : '', ins.brokerName || '', ins.brokerPhone || '', ins.brokerEmail || '']);
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([header, ...rows]), 'Insurance');
    }
    if (sections.compliance && compliance.length) {
      const rows = compliance.map(c => [c.title || '', c.dueDate ? fmtDate(c.dueDate) : '', c.instructions || '']);
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([['Title', 'Due Date', 'Instructions'], ...rows]), 'Compliance');
    }
    if (sections.units && unitsList.length) {
      const rows = unitsList.map(u => {
        const occ = tenants.find(t => t.propertyId === prop.id && t.unit === u.unitNumber && t.status !== 'Previous');
        return [u.unitNumber || '', occ ? occ.name : 'VACANT', u.unitType || '', u.bedrooms ?? '', u.bathrooms ?? '', u.unitSize || '', u.balcony || ''];
      });
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([['Unit #', 'Occupant', 'Type', 'Bedrooms', 'Bathrooms', 'Size (sq ft)', 'Balcony'], ...rows]), 'Units');
    }
    if (!wb.SheetNames.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([['No sections selected']]), 'Sheet1');
    XLSX.writeFile(wb, `${prop.name.replace(/[^a-z0-9]/gi, '_')}.xlsx`);
  };

  // ── Audit log: precise timestamp + PDF export ──
  const fmtAuditTime = iso => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('en-US', { timeZone: 'America/New_York', year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit' });
  };
  const auditEntries = [...(prop.auditLog || [])].sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || ''));
  const downloadAuditPdf = () => {
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const margin = 48;
    let y = margin;
    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(15); pdf.setTextColor(20, 20, 20);
    pdf.text('Property Audit Log', margin, y); y += 18;
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(10); pdf.setTextColor(90, 90, 90);
    pdf.text(prop.name || '', margin, y); y += 22;
    pdf.setDrawColor(210, 210, 210); pdf.line(margin, y, pageW - margin, y); y += 16;
    pdf.setFontSize(9);
    if (auditEntries.length === 0) { pdf.setTextColor(120, 120, 120); pdf.text('No activity recorded yet.', margin, y); }
    auditEntries.forEach(e => {
      if (y > pageH - margin) { pdf.addPage(); y = margin; }
      pdf.setTextColor(120, 120, 120); pdf.setFont('helvetica', 'normal');
      pdf.text(fmtAuditTime(e.timestamp), margin, y);
      pdf.setTextColor(20, 20, 20); pdf.setFont('helvetica', 'bold');
      pdf.text(`${e.section || ''}${e.category ? ' · ' + e.category : ''}`, margin + 150, y);
      pdf.setFont('helvetica', 'normal'); pdf.setTextColor(40, 40, 40);
      const line = `${e.action || ''}${e.detail ? ' — ' + e.detail : ''}${e.by ? '  (' + e.by + ')' : ''}`;
      const wrapped = pdf.splitTextToSize(line, pageW - margin - (margin + 290));
      pdf.text(wrapped, margin + 290, y);
      y += Math.max(15, wrapped.length * 11);
    });
    pdf.save(`${(prop.name || 'property').replace(/[^a-z0-9]/gi, '_')}_audit_log.pdf`);
  };

  if (auditOpen) {
    return (
      <div>
        <button className="back-btn" onClick={() => setAuditOpen(false)}>{ic(I.arrowLeft, 15)} Back to property</button>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>Audit Log</h1>
            <div style={{ fontSize: 13, color: 'var(--text2)' }}>{prop.name}</div>
          </div>
          <button className="btn" onClick={downloadAuditPdf}>
            <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>
            Download as PDF
          </button>
        </div>
        <div className="card" style={{ borderRadius: 0 }}>
          {auditEntries.length === 0
            ? <div className="empty">No activity recorded yet.</div>
            : auditEntries.map(e => (
                <div key={e.id} style={{ display: 'flex', gap: 16, padding: '9px 0', borderBottom: '1px solid var(--border)', fontSize: 13, flexWrap: 'wrap' }}>
                  <div style={{ width: 200, flexShrink: 0, color: 'var(--text2)', fontVariantNumeric: 'tabular-nums' }}>{fmtAuditTime(e.timestamp)}</div>
                  <div style={{ width: 160, flexShrink: 0, fontWeight: 600 }}>
                    {e.section}{e.category ? <span style={{ color: 'var(--text2)', fontWeight: 500 }}> · {e.category}</span> : null}
                  </div>
                  <div style={{ flex: 1, minWidth: 180 }}>
                    <span style={{ fontWeight: 500 }}>{e.action}</span>{e.detail ? ` — ${e.detail}` : ''}
                    {e.by ? <span style={{ color: 'var(--text3)' }}> · {e.by}</span> : null}
                  </div>
                </div>
              ))}
        </div>
      </div>
    );
  }

  const kebabItems = [
    { label: 'Export', onClick: () => setPrintModal(true) },
    { label: 'Edit info', onClick: () => { setEf({ ...prop }); setEditingInfo(true); setPropTab('overview'); } },
    { label: 'Audit Log', onClick: () => setAuditOpen(true) },
    ...(isAdmin ? [prop.archived ? { label: 'Restore', onClick: unarchiveProp } : { label: 'Archive', danger: true, onClick: archiveProp }] : []),
  ];

  return (
    <div>
      <button className="back-btn" onClick={onBack}>{ic(I.arrowLeft, 15)} All properties</button>
      {isMobile ? (
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.25, marginBottom: 2 }}>{prop.name}</div>
            <div style={{ fontSize: 11, color: 'var(--text2)' }}>{prop.address}</div>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
            {prop.archived && (
              <span style={{ fontSize: 10, fontWeight: 600, background: 'var(--amber-bg)', color: 'var(--amber-txt)', padding: '2px 7px', borderRadius: 6 }}>Archived</span>
            )}
            <KebabMenu items={kebabItems} />
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <h1 style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>{prop.name}</h1>
            <div style={{ fontSize: 13, color: 'var(--text2)' }}>{prop.address}</div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {prop.archived && (
              <span style={{ fontSize: 11, fontWeight: 600, background: 'var(--amber-bg)', color: 'var(--amber-txt)', padding: '3px 8px', borderRadius: 6 }}>Archived</span>
            )}
            <KebabMenu items={kebabItems} />
          </div>
        </div>
      )}

      {/* ── Tab bar ── */}
      <div style={{ display: 'flex', marginBottom: 20, borderBottom: '1px solid var(--border)' }}>
        {[['overview','Overview'],['insurance','Insurance'],['compliance','Compliance'],['units','Units'],['documents','Docs']].filter(([id]) => !(id === 'units' && prop.businessType === 'Construction')).map(([id, label]) => (
          <button key={id} onClick={() => setPropTab(id)} style={{ flex: 1, background: 'none', border: 'none', borderBottom: propTab === id ? '2px solid var(--accent)' : '2px solid transparent', padding: isMobile ? '7px 2px' : '8px 14px', fontSize: isMobile ? 11 : 13, fontWeight: propTab === id ? 600 : 400, color: propTab === id ? 'var(--accent)' : 'var(--text2)', cursor: 'pointer', marginBottom: -1, fontFamily: 'inherit', transition: 'color 0.12s', textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</button>
        ))}
      </div>

      {/* ── Section 1: Property Profile ── */}
      {propTab === 'overview' && <div className="card" style={{ marginBottom: 16, borderRadius: 0 }}>
        <div className="card-title">{ic(I.building, 14)} Property profile</div>
        {editingInfo ? (
          <div>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 320px', minWidth: 0 }}>
                <FI label="Property name" value={ef.name} onChange={v => setEf(p => ({ ...p, name: v }))} />
                <FI label="Owner name" value={ef.owner} onChange={v => setEf(p => ({ ...p, owner: v }))} />
                <FI label="Address" value={ef.address} onChange={v => setEf(p => ({ ...p, address: v }))} />
                <FI label="Email" value={ef.email} onChange={v => setEf(p => ({ ...p, email: v }))} type="email" placeholder="office@example.com" />
                <FI label="Phone" value={ef.phone} onChange={v => setEf(p => ({ ...p, phone: v }))} placeholder="e.g. (212) 555-1234" />
              </div>
              <div style={{ flex: '1 1 320px', minWidth: 0 }}>
                <FS label="Business type" value={ef.businessType} onChange={v => setEf(p => ({ ...p, businessType: v }))} options={['Multi Family Housing', 'Construction']} />
                <FI label="EIN" value={ef.ein} onChange={v => setEf(p => ({ ...p, ein: v }))} placeholder="XX-XXXXXXX" />
                <FI label="DOS ID" value={ef.dosId} onChange={v => setEf(p => ({ ...p, dosId: v }))} />
                {ef.businessType !== 'Construction' && <FI label="DHCR Building Registration #" value={ef.dhcrRegNum} onChange={v => setEf(p => ({ ...p, dhcrRegNum: v }))} />}
                <div className="form-group">
                  <label className="form-label">BIN / Block / Lot</label>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                    {[['BIN', 'bin'], ['Block', 'block'], ['Lot', 'lot']].map(([lbl, key]) => (
                      <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 12, color: 'var(--text2)', fontWeight: 500 }}>{lbl}</span>
                        <input className="form-input" style={{ width: 72 }} value={ef[key] || ''} onChange={e => setEf(p => ({ ...p, [key]: e.target.value }))} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            {ef.businessType === 'Construction' && (
              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Additional Addresses (for sync)</div>
                {(ef.altAddresses || []).map((addr) => {
                  const upd = (key, v) => setEf(p => ({ ...p, altAddresses: p.altAddresses.map(a => a.id === addr.id ? { ...a, [key]: v } : a) }));
                  return (
                    <div key={addr.id} style={{ marginBottom: 14, paddingBottom: 14, borderBottom: '1px solid var(--border)' }}>
                      <FI label="Address" value={addr.label || ''} onChange={v => upd('label', v)} placeholder="e.g. 123 Main St" />
                      <div className="form-group">
                        <label className="form-label">BIN / Block / Lot</label>
                        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                          {[['BIN', 'bin'], ['Block', 'block'], ['Lot', 'lot']].map(([lbl, key]) => (
                            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ fontSize: 12, color: 'var(--text2)', fontWeight: 500 }}>{lbl}</span>
                              <input className="form-input" style={{ width: 72 }} value={addr[key] || ''} onChange={e => upd(key, e.target.value)} />
                            </div>
                          ))}
                          <button className="btn-ghost" style={{ color: 'var(--text3)', padding: '6px 8px', flexShrink: 0 }} onClick={() => setEf(p => ({ ...p, altAddresses: p.altAddresses.filter(a => a.id !== addr.id) }))}>✕</button>
                        </div>
                      </div>
                    </div>
                  );
                })}
                <button className="btn-ghost" style={{ fontSize: 12 }} onClick={() => setEf(p => ({ ...p, altAddresses: [...(p.altAddresses || []), { id: `alt_${Date.now()}`, label: '', bin: '', block: '', lot: '' }] }))}>+ Add address</button>
              </div>
            )}
            <div className="modal-actions">
              <button className="btn" onClick={() => setEditingInfo(false)}>Cancel</button>
              <SavingBtn onClick={saveInfo}>Save changes</SavingBtn>
            </div>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 40, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 260px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
                <InfoItem label="Property name" value={prop.name} />
                <InfoItem label="Owner name" value={prop.owner} />
                <InfoItem label="Address" value={prop.address} />
                <InfoItem label="Email" value={prop.email} />
                <InfoItem label="Phone" value={prop.phone} />
              </div>
              <div style={{ flex: '1 1 260px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
                <InfoItem label="Business type" value={prop.businessType} />
                <InfoItem label="EIN" value={prop.ein} />
                <InfoItem label="DOS ID" value={prop.dosId} />
                {prop.businessType !== 'Construction' && <InfoItem label="DHCR Building Registration #" value={prop.dhcrRegNum} />}
                <div style={{ display: 'flex', gap: 28 }}>
                  <InfoItem label="BIN" value={prop.bin} />
                  <InfoItem label="Block" value={prop.block} />
                  <InfoItem label="Lot" value={prop.lot} />
                </div>
              </div>
            </div>
            {(prop.altAddresses || []).length > 0 && (
              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Additional Addresses</div>
                {(prop.altAddresses || []).map(addr => (
                  <div key={addr.id} style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 4 }}>
                    {addr.label && <span style={{ fontWeight: 500, color: 'var(--text1)', marginRight: 8 }}>{addr.label}</span>}
                    {[addr.bin && `BIN ${addr.bin}`, addr.block && `Block ${addr.block}`, addr.lot && `Lot ${addr.lot}`].filter(Boolean).join(' · ')}
                  </div>
                ))}
              </div>
            )}
            {prop.ecbRespondentNames && (
              <div style={{ marginTop: 10 }}>
                <InfoItem label="ECB Respondent Keywords" value={prop.ecbRespondentNames} />
              </div>
            )}
            <div style={{ borderTop: '1px solid var(--border)', marginTop: 18, paddingTop: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Financial Account</div>
                <KebabMenu items={[{ label: 'Edit', onClick: openEditFinancial }]} />
              </div>
              <div className="info-grid">
                <div>
                  <div className="info-label">Zelle</div>
                  {propZelles.length
                    ? propZelles.map((z, i) => <div key={i} className="info-val" style={{ marginBottom: 2 }}>{z}</div>)
                    : <div className="info-val">—</div>}
                </div>
                <div>
                  <div className="info-label" style={{ marginBottom: 8 }}>Bank</div>
                  {propBanks.length
                    ? propBanks.map((b, i) => (
                        <div key={b.id || i} style={{ marginBottom: 12 }}>
                          <InfoItem label="Account Name" value={b.accountName} />
                          <InfoItem label="Account Number" value={b.accountNumber} />
                          <InfoItem label="Routing Number" value={b.routingNumber} />
                        </div>
                      ))
                    : <div className="info-val">—</div>}
                </div>
              </div>
            </div>
          </>
        )}
      </div>}

      {/* ── Financial Account edit modal ── */}
      {printModal && (
        <Modal title="Export property" onClose={() => setPrintModal(false)}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Format</div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            {[['pdf', 'PDF'], ['excel', 'Excel']].map(([val, lbl]) => (
              <button key={val} className="btn" onClick={() => setPrintFormat(val)}
                style={{ flex: 1, justifyContent: 'center', ...(printFormat === val ? { borderColor: 'var(--accent)', color: 'var(--accent)', fontWeight: 600 } : {}) }}>{lbl}</button>
            ))}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 8 }}>Select the sections to include:</div>
          {[
            ['overview', 'Overview (excludes financial account)'],
            ['insurance', 'Insurance — table, no documents'],
            ['compliance', 'Compliance — table, no documents'],
            ['units', 'Units — table'],
          ].map(([key, label]) => (
            <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', cursor: 'pointer', fontSize: 13 }}>
              <input type="checkbox" checked={printSections[key]} onChange={e => setPrintSections(s => ({ ...s, [key]: e.target.checked }))} />
              {label}
            </label>
          ))}
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>Documents are never included in the export.</div>
          <div className="modal-actions">
            <button className="btn" onClick={() => setPrintModal(false)}>Cancel</button>
            <button className="btn btn-primary" disabled={!Object.values(printSections).some(Boolean)} onClick={() => { (printFormat === 'excel' ? handleExcel : handlePrint)(printSections); setPrintModal(false); }}>Export {printFormat === 'excel' ? 'Excel' : 'PDF'}</button>
          </div>
        </Modal>
      )}

      {finModal && (
        <Modal title="Edit financial account" onClose={() => setFinModal(false)} lg>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Zelle (email)</div>
          {(finForm.zelles || []).map((z, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
              <input className="form-input" type="email" value={z} placeholder="email@example.com"
                onChange={e => setFinForm(f => ({ ...f, zelles: f.zelles.map((x, j) => j === i ? e.target.value : x) }))} />
              <button className="btn-ghost" style={{ color: 'var(--text3)', padding: '6px 8px', flexShrink: 0 }}
                onClick={() => setFinForm(f => ({ ...f, zelles: f.zelles.filter((_, j) => j !== i) }))}>✕</button>
            </div>
          ))}
          <button className="btn-ghost" style={{ fontSize: 12, marginBottom: 18 }}
            onClick={() => setFinForm(f => ({ ...f, zelles: [...(f.zelles || []), ''] }))}>+ Add Zelle</button>

          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Bank</div>
          {(finForm.bankAccounts || []).map((b, i) => (
            <div key={b.id || i} style={{ marginBottom: 14, paddingBottom: 14, borderBottom: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: 12, color: 'var(--text3)', fontWeight: 600 }}>Bank account {i + 1}</div>
                <button className="btn-ghost" style={{ color: 'var(--text3)', padding: '4px 8px' }}
                  onClick={() => setFinForm(f => ({ ...f, bankAccounts: f.bankAccounts.filter((_, j) => j !== i) }))}>✕ Remove</button>
              </div>
              <FI label="Account Name" value={b.accountName || ''} onChange={v => setFinForm(f => ({ ...f, bankAccounts: f.bankAccounts.map((x, j) => j === i ? { ...x, accountName: v } : x) }))} />
              <FI label="Account Number" value={b.accountNumber || ''} onChange={v => setFinForm(f => ({ ...f, bankAccounts: f.bankAccounts.map((x, j) => j === i ? { ...x, accountNumber: v } : x) }))} />
              <FI label="Routing Number" value={b.routingNumber || ''} onChange={v => setFinForm(f => ({ ...f, bankAccounts: f.bankAccounts.map((x, j) => j === i ? { ...x, routingNumber: v } : x) }))} />
            </div>
          ))}
          <button className="btn-ghost" style={{ fontSize: 12 }}
            onClick={() => setFinForm(f => ({ ...f, bankAccounts: [...(f.bankAccounts || []), { id: `bank_${Date.now()}`, accountName: '', accountNumber: '', routingNumber: '' }] }))}>+ Add Bank</button>

          <div className="modal-actions">
            <button className="btn" onClick={() => setFinModal(false)}>Cancel</button>
            <SavingBtn onClick={saveFinancial}>Save changes</SavingBtn>
          </div>
        </Modal>
      )}

      {/* ── Section 2: Insurance ── */}
      {propTab === 'insurance' && <div className="card" style={{ marginBottom: 16, borderRadius: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div className="card-title" style={{ margin: 0 }}>{ic(I.clipboard, 14)} Insurance</div>
          <button className="btn-ghost" onClick={openAddIns}>{ic(I.plus, 12)} Add insurance</button>
        </div>
        {insurance.length === 0
          ? <div style={{ color: 'var(--text3)', fontSize: 13 }}>No insurance added yet.</div>
          : (() => {
              const sorted = [...insurance].sort((a, b) => {
                const rank = x => isExpiredISO(x.endDate) ? 0 : isAlmostDueISO(x.endDate) ? 1 : 2;
                if (rank(a) !== rank(b)) return rank(a) - rank(b);
                if (!a.endDate && !b.endDate) return 0;
                if (!a.endDate) return 1;
                if (!b.endDate) return -1;
                return a.endDate.localeCompare(b.endDate);
              });
              const activeIns = sorted.filter(i => !i.archived);
              const archivedIns = sorted.filter(i => i.archived);
              const rowsToShow = showArchivedIns ? [...activeIns, ...archivedIns] : activeIns;
              const trunc = s => (s && s.length > 15 ? s.slice(0, 15) + '…' : (s || ''));
              return (
                <div style={{ overflowX: 'auto' }}>
                  <table className="list-table">
                    <thead>
                      <tr>
                        <th>Policy Type</th>
                        <th>Insurance</th>
                        <th>Policy Number</th>
                        <th>Start &amp; Term</th>
                        <th>Broker</th>
                        <th>Documents</th>
                        <th style={{ width: 40 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {rowsToShow.map(ins => {
                        const expired = isExpiredISO(ins.endDate);
                        // Orange once within 2 months (calendar) of the term/expiry date.
                        const almost = !expired && !!ins.endDate && (() => { const warn = new Date(ins.endDate); warn.setMonth(warn.getMonth() - 2); return new Date(nyTodayISO()) >= warn; })();
                        const isArch = !!ins.archived;
                        return (
                          <tr key={ins.id} className="clickable" onClick={() => setViewIns(ins)} style={isArch ? { opacity: 0.55 } : undefined}>
                            <td style={{ fontWeight: 500 }}>{ins.type || '—'}{isArch ? <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 600, color: 'var(--text3)' }}>ARCHIVED</span> : null}</td>
                            <td style={{ fontSize: 12 }}>
                              <div style={{ fontWeight: 500, color: 'var(--text1)' }}>{ins.name || '—'}</div>
                              {ins.phone && <div style={{ color: 'var(--text2)' }}>{ins.phone}</div>}
                              {ins.email && <div style={{ color: 'var(--text2)' }}>{ins.email}</div>}
                            </td>
                            <td style={{ fontSize: 12, color: 'var(--text2)' }}>{ins.policyNumber || '—'}</td>
                            <td style={{ fontSize: 12, whiteSpace: 'nowrap', color: expired ? 'var(--red-txt)' : almost ? '#f97316' : 'var(--text2)', fontWeight: (expired || almost) ? 600 : 400 }}>
                              <div>{ins.startDate ? fmtDate(ins.startDate) : '—'} –</div>
                              <div>{ins.endDate ? fmtDate(ins.endDate) : '—'}</div>
                              {expired ? <div style={{ fontSize: 10 }}>EXPIRED</div> : almost ? <div style={{ fontSize: 10 }}>Almost Due</div> : null}
                            </td>
                            <td style={{ fontSize: 12 }}>
                              <div style={{ fontWeight: 500, color: 'var(--text1)' }}>{ins.brokerName || '—'}</div>
                              {ins.brokerPhone && <div style={{ color: 'var(--text2)' }}>{ins.brokerPhone}</div>}
                              {ins.brokerEmail && <div style={{ color: 'var(--text2)' }}>{ins.brokerEmail}</div>}
                            </td>
                            <td style={{ fontSize: 12 }} onClick={e => e.stopPropagation()}>
                              {(ins.documents || []).length === 0
                                ? <span style={{ color: 'var(--text3)' }}>—</span>
                                : (ins.documents || []).map(d => (
                                    <div key={d.id}>
                                      <a href={d.url} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }} title={d.name}>{trunc(d.name)}</a>
                                    </div>
                                  ))}
                              <input id={`ins-upl-${ins.id}`} type="file" style={{ display: 'none' }} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={e => handleDocUpload(e, ins.id)} />
                            </td>
                            <td onClick={e => e.stopPropagation()}>
                              <KebabMenu items={isArch
                                ? [
                                    { label: 'Restore', onClick: () => restoreIns(ins.id) },
                                    { label: 'Delete', danger: true, onClick: () => delIns(ins.id) },
                                  ]
                                : [
                                    { label: uploadingDoc ? 'Uploading…' : 'Upload document', onClick: () => document.getElementById(`ins-upl-${ins.id}`)?.click() },
                                    { label: 'Edit', onClick: () => openEditIns(ins) },
                                    { label: 'Archive', onClick: () => archiveIns(ins.id) },
                                    { label: 'Delete', danger: true, onClick: () => delIns(ins.id) },
                                  ]} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {archivedIns.length > 0 && (
                    <button className="btn-ghost" style={{ fontSize: 12, marginTop: 8 }} onClick={() => setShowArchivedIns(s => !s)}>
                      {showArchivedIns ? '▾' : '▸'} Archived insurance ({archivedIns.length})
                    </button>
                  )}
                </div>
              );
            })()}
      </div>}

      {/* ── Section 3: Compliance ── */}
      {propTab === 'compliance' && <div className="card" style={{ marginBottom: 16, borderRadius: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div className="card-title" style={{ margin: 0 }}>{ic(I.receipt, 14)} Compliance</div>
          <button className="btn-ghost" onClick={openAddComp}>{ic(I.plus, 12)} Add compliance</button>
        </div>
        {compliance.length === 0
          ? <div style={{ color: 'var(--text3)', fontSize: 13 }}>No compliance items added yet.</div>
          : <div style={{ overflowX: 'auto' }}>
              <table className="list-table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Due Date</th>
                    <th style={{ minWidth: 360, width: '55%' }}>Instructions</th>
                    <th>Documents</th>
                    <th style={{ width: 40 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {compliance.map(c => {
                    const overdue = isExpiredISO(c.dueDate);
                    const almost = !overdue && !!c.dueDate && (() => { const warn = new Date(c.dueDate); warn.setMonth(warn.getMonth() - 2); return new Date(nyTodayISO()) >= warn; })();
                    const trunc = s => (s && s.length > 15 ? s.slice(0, 15) + '…' : (s || ''));
                    return (
                      <tr key={c.id}>
                        <td style={{ fontWeight: 500 }}>{c.title || '—'}</td>
                        <td style={{ fontSize: 12, whiteSpace: 'nowrap', color: overdue ? 'var(--red-txt)' : almost ? '#f97316' : 'var(--text2)', fontWeight: (overdue || almost) ? 600 : 400 }}>
                          {c.dueDate ? fmtDate(c.dueDate) : '—'}
                          {overdue ? <div style={{ fontSize: 10 }}>OVERDUE</div> : almost ? <div style={{ fontSize: 10 }}>Almost Due</div> : null}
                        </td>
                        <td style={{ fontSize: 12, color: 'var(--text2)', whiteSpace: 'normal', minWidth: 360 }}>{c.instructions || '—'}</td>
                        <td style={{ fontSize: 12 }}>
                          {(c.documents || []).length === 0
                            ? <span style={{ color: 'var(--text3)' }}>—</span>
                            : (c.documents || []).map(d => (
                                <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <a href={d.url} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }} title={d.name}>{trunc(d.name)}</a>
                                  <button className="btn-ghost" style={{ color: 'var(--text3)', padding: '0 4px', fontSize: 12 }} title="Delete document" onClick={() => handleCompDocDelete(c.id, d)}>✕</button>
                                </div>
                              ))}
                          <input id={`comp-upl-${c.id}`} type="file" style={{ display: 'none' }} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={e => handleCompDocUpload(e, c.id)} />
                        </td>
                        <td>
                          <KebabMenu items={[
                            { label: uploadingCompDoc ? 'Uploading…' : 'Upload document', onClick: () => document.getElementById(`comp-upl-${c.id}`)?.click() },
                            { label: 'Edit', onClick: () => openEditComp(c) },
                            { label: 'Delete', danger: true, onClick: () => delComp(c.id) },
                          ]} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>}
      </div>}

      {/* ── Section 4: Units ── */}
      {propTab === 'units' && <div className="card" style={{ borderRadius: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text1)', fontSize: 13, fontWeight: 500 }}>
            {ic(I.folder, 14, 'var(--text2)')}
            Units ({unitsList.length})
          </div>
          <button className="btn-ghost" onClick={() => { setUnitForm({ unitNumber: '', bedrooms: '', bathrooms: '', unitSize: '', balcony: 'No Balcony', unitType: '' }); setUnitModal('add'); }}>{ic(I.plus, 12)} Add unit</button>
        </div>
        {unitsList.length === 0
          ? <div style={{ color: 'var(--text3)', fontSize: 13 }}>No units added yet.</div>
          : <div style={{ overflowX: 'auto' }}>
              <table className="list-table">
                <thead><tr><th>Unit #</th><th>Occupant</th><th>Type</th><th>Bed / Bath</th><th>Size (sq ft)</th><th>Balcony</th><th style={{ width: 40 }}></th></tr></thead>
                <tbody>
                  {unitsList.map(u => {
                    const occupant = tenants.find(t => t.propertyId === prop.id && t.unit === u.unitNumber && t.status !== 'Previous');
                    return (
                      <tr key={u.id}>
                        <td style={{ fontWeight: 500 }}>{u.unitNumber}</td>
                        <td>
                          {occupant
                            ? <span className="badge badge-green">{occupant.name}</span>
                            : <span className="badge badge-amber">VACANT</span>}
                        </td>
                        <td style={{ color: 'var(--text2)', fontSize: 12 }}>{u.unitType || occupant?.unitType || '—'}</td>
                        <td style={{ color: 'var(--text2)' }}>{u.bedrooms} / {u.bathrooms}</td>
                        <td style={{ color: 'var(--text2)' }}>{u.unitSize || '—'}</td>
                        <td style={{ color: 'var(--text2)', fontSize: 12 }}>{u.balcony || '—'}</td>
                        <td><KebabMenu items={[{ label: 'Edit', onClick: () => editUnit(u) }, { label: 'Delete', danger: true, onClick: () => delUnit(u.id) }]} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>}
      </div>}

      {/* ── Section 5: Documents ── */}
      {propTab === 'documents' && (() => {
        const folders = prop.folders || [];
        const allDocs = prop.documents || [];
        const currentFolder = folders.find(f => f.id === currentFolderId);
        const visibleFolders = folders.filter(f => (f.parentId || null) === currentFolderId);
        const visibleDocs = allDocs.filter(d => (d.folderId || null) === currentFolderId);
        const docSort = (a, b) => {
          const an = (a.name || '').toLowerCase();
          const bn = (b.name || '').toLowerCase();
          const aIsNum = /^\d/.test(an);
          const bIsNum = /^\d/.test(bn);
          if (aIsNum && !bIsNum) return -1;
          if (!aIsNum && bIsNum) return 1;
          return an.localeCompare(bn, undefined, { numeric: true, sensitivity: 'base' });
        };
        const filteredFolders = visibleFolders.filter(f => !docSearch || f.name.toLowerCase().includes(docSearch.toLowerCase())).sort(docSort);
        const filteredDocs = visibleDocs.filter(d => !docSearch || d.name.toLowerCase().includes(docSearch.toLowerCase())).sort(docSort);
        // Build breadcrumb path
        const buildPath = id => {
          if (!id) return [];
          const f = folders.find(x => x.id === id);
          if (!f) return [];
          return [...buildPath(f.parentId), f];
        };
        const breadcrumb = buildPath(currentFolderId);
        const isEmpty = filteredFolders.length === 0 && filteredDocs.length === 0;
        return (
          <div className="card" style={{ position: 'relative' }}
            onDragEnter={isMobile ? undefined : handleDocAreaDragEnter}
            onDragLeave={isMobile ? undefined : handleDocAreaDragLeave}
            onDragOver={isMobile ? undefined : handleDocAreaDragOver}
            onDrop={isMobile ? undefined : handleDocAreaDrop}>

            {/* Drop zone overlay — only visible while dragging on desktop */}
            {!isMobile && docDragOver && (
              <div style={{ position: 'absolute', inset: 0, zIndex: 10, borderRadius: 12, border: '2px dashed var(--accent)', background: 'var(--accent-bg, rgba(99,102,241,0.08))', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, pointerEvents: 'none' }}>
                {ic(I.file, 28, 'var(--accent)')}
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--accent)' }}>Drop files to upload{currentFolderId ? ' into folder' : ''}</div>
              </div>
            )}
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <button onClick={() => { setCurrentFolderId(null); setDocSearch(''); }}
                  onDragOver={e => { if (draggedDocId) { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = 'move'; if (dropFolderId !== '') setDropFolderId(''); } }}
                  onDragLeave={() => setDropFolderId(cur => cur === '' ? undefined : cur)}
                  onDrop={e => { const id = e.dataTransfer.getData('application/x-amar-doc') || draggedDocId; if (id) { e.preventDefault(); e.stopPropagation(); moveDocToFolder(id, null); } setDraggedDocId(null); setDropFolderId(undefined); }}
                  style={{ background: dropFolderId === '' ? 'var(--accent-bg)' : 'none', border: 'none', cursor: 'pointer', color: currentFolderId ? 'var(--accent)' : 'var(--text1)', fontSize: 13, fontFamily: 'inherit', padding: dropFolderId === '' ? '2px 6px' : 0, borderRadius: 6, fontWeight: currentFolderId ? 400 : 600 }}>
                  {ic(I.file, 14)} Documents
                </button>
                {breadcrumb.map((f, i) => (
                  <React.Fragment key={f.id}>
                    <span style={{ color: 'var(--text3)', fontSize: 13 }}>/</span>
                    <button onClick={() => { setCurrentFolderId(f.id); setDocSearch(''); }}
                      onDragOver={e => { if (draggedDocId && f.id !== currentFolderId) { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = 'move'; if (dropFolderId !== f.id) setDropFolderId(f.id); } }}
                      onDragLeave={() => setDropFolderId(cur => cur === f.id ? undefined : cur)}
                      onDrop={e => { const id = e.dataTransfer.getData('application/x-amar-doc') || draggedDocId; if (id && f.id !== currentFolderId) { e.preventDefault(); e.stopPropagation(); moveDocToFolder(id, f.id); } setDraggedDocId(null); setDropFolderId(undefined); }}
                      style={{ background: dropFolderId === f.id ? 'var(--accent-bg)' : 'none', border: 'none', cursor: 'pointer', color: i === breadcrumb.length - 1 ? 'var(--text1)' : 'var(--accent)', fontSize: 13, fontFamily: 'inherit', padding: dropFolderId === f.id ? '2px 6px' : 0, borderRadius: 6, fontWeight: i === breadcrumb.length - 1 ? 600 : 400 }}>
                      {f.name}
                    </button>
                  </React.Fragment>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button className="btn-ghost" style={{ fontSize: 12 }} onClick={() => { setNewFolderName(''); setNewFolderModal(true); }}>{ic(I.folder, 12)} New folder</button>
                <label style={{ cursor: uploadingPropDoc ? 'default' : 'pointer', opacity: uploadingPropDoc ? 0.6 : 1 }}>
                  <input type="file" multiple style={{ display: 'none' }} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx" onChange={handlePropDocUpload} disabled={uploadingPropDoc} />
                  <span className="btn-ghost" style={{ fontSize: 12, pointerEvents: uploadingPropDoc ? 'none' : 'auto' }}>
                    {uploadingPropDoc ? 'Uploading…' : <>{ic(I.plus, 12)} Upload</>}
                  </span>
                </label>
              </div>
            </div>

            {/* Search */}
            {(folders.length > 0 || allDocs.length > 0) && (
              <div style={{ marginBottom: 12 }}>
                <SB value={docSearch} onChange={setDocSearch} placeholder="Search documents and folders…" />
              </div>
            )}

            {/* Empty state */}
            {isEmpty && !docSearch && <div style={{ color: 'var(--text3)', fontSize: 13 }}>No documents uploaded yet.</div>}
            {isEmpty && docSearch && <div style={{ color: 'var(--text3)', fontSize: 13 }}>No results for "{docSearch}".</div>}

            {/* Folders */}
            {filteredFolders.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: filteredDocs.length > 0 ? 8 : 0 }}>
                {filteredFolders.map(f => (
                  <div key={f.id}
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: dropFolderId === f.id ? 'var(--accent-bg)' : 'var(--bg2)', borderRadius: 8, cursor: 'pointer', border: dropFolderId === f.id ? '1.5px dashed var(--accent)' : '1.5px solid transparent' }}
                    onClick={() => { setCurrentFolderId(f.id); setDocSearch(''); }}
                    onDragOver={e => { if (draggedDocId) { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = 'move'; if (dropFolderId !== f.id) setDropFolderId(f.id); } }}
                    onDragLeave={() => setDropFolderId(cur => cur === f.id ? undefined : cur)}
                    onDrop={e => { const id = e.dataTransfer.getData('application/x-amar-doc') || draggedDocId; if (id) { e.preventDefault(); e.stopPropagation(); moveDocToFolder(id, f.id); } setDraggedDocId(null); setDropFolderId(undefined); }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}>
                      {renamingFolderId === f.id
                        ? <input autoFocus className="form-input" style={{ fontSize: 13, padding: '3px 8px', height: 'auto' }} value={renameFolderVal}
                            onChange={e => setRenameFolderVal(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') renameFolder(f.id); if (e.key === 'Escape') setRenamingFolderId(null); }}
                            onBlur={() => renameFolder(f.id)}
                            onClick={e => e.stopPropagation()} />
                        : <>
                            {ic(I.folder, 16, 'var(--accent)')}
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 500 }}>{f.name}</div>
                              <div style={{ fontSize: 11, color: 'var(--text3)' }}>{allDocs.filter(d => d.folderId === f.id).length} file(s)</div>
                            </div>
                          </>}
                    </div>
                    <div onClick={e => e.stopPropagation()}>
                      <KebabMenu items={[
                        { label: 'Rename', onClick: () => { setRenamingFolderId(f.id); setRenameFolderVal(f.name); } },
                        { label: 'Delete', danger: true, onClick: () => deleteFolder(f.id) },
                      ]} />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Documents */}
            {filteredDocs.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {filteredDocs.map(d => (
                  <div key={d.id}
                    draggable={renamingDocId !== d.id}
                    onDragStart={e => { e.dataTransfer.setData('application/x-amar-doc', d.id); e.dataTransfer.effectAllowed = 'move'; setDraggedDocId(d.id); }}
                    onDragEnd={() => { setDraggedDocId(null); setDropFolderId(undefined); }}
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: 'var(--bg2)', borderRadius: 8, opacity: draggedDocId === d.id ? 0.4 : 1 }}>
                    <div style={{ cursor: renamingDocId === d.id ? 'default' : 'pointer', display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}
                      onClick={() => { if (renamingDocId !== d.id) window.open(d.url, '_blank', 'noopener,noreferrer'); }}>
                      {ic(I.file, 15, 'var(--text2)')}
                      {renamingDocId === d.id
                        ? <input autoFocus className="form-input" style={{ fontSize: 13, padding: '3px 8px', height: 'auto' }} value={renameVal}
                            onChange={e => setRenameVal(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') handlePropDocRename(d.id); if (e.key === 'Escape') setRenamingDocId(null); }}
                            onBlur={() => handlePropDocRename(d.id)}
                            onClick={e => e.stopPropagation()} />
                        : <div>
                            <div style={{ fontSize: 13, fontWeight: 500 }}>{d.name}</div>
                            <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                              {d.uploadedAt && new Date(d.uploadedAt).toLocaleDateString('en-US')}{d.size != null && ` · ${fmtSize(d.size)}`}
                            </div>
                          </div>}
                    </div>
                    <div onClick={e => e.stopPropagation()}>
                      <KebabMenu items={[
                        { label: 'Rename', onClick: () => { setRenamingDocId(d.id); setRenameVal(d.name); } },
                        { label: 'Delete', danger: true, onClick: () => handlePropDocDelete(d) },
                      ]} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* New Folder Modal */}
      {newFolderModal && (
        <Modal title="New Folder" onClose={() => setNewFolderModal(false)}>
          <FI label="Folder name" value={newFolderName} onChange={setNewFolderName} placeholder="e.g. Leases, Insurance, Permits…" />
          <div className="modal-actions">
            <button className="btn" onClick={() => setNewFolderModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={createFolder}>Create</button>
          </div>
        </Modal>
      )}


      {/* ── Insurance Add / Edit Modal ── */}
      {(insModal === 'add' || insModal === 'edit') && (
        <Modal title={insModal === 'edit' ? 'Edit insurance' : 'Add insurance'} onClose={() => setInsModal(null)} lg>
          <FS label="Insurance type" value={insForm.type} onChange={v => setInsForm(p => ({ ...p, type: v }))} options={INS_TYPES} />
          <div className="form-grid form-grid-2">
            <FI label="Insurance name" value={insForm.name} onChange={v => setInsForm(p => ({ ...p, name: v }))} />
            <FI label="Policy number" value={insForm.policyNumber} onChange={v => setInsForm(p => ({ ...p, policyNumber: v }))} />
          </div>
          <div className="form-grid form-grid-2">
            <FI label="Policy start date" value={insForm.startDate} onChange={v => setInsForm(p => ({ ...p, startDate: v }))} type="date" />
            <FI label="Policy end date" value={insForm.endDate} onChange={v => setInsForm(p => ({ ...p, endDate: v }))} type="date" />
          </div>
          <div className="form-grid form-grid-2">
            <FI label="Insurance phone" value={insForm.phone} onChange={v => setInsForm(p => ({ ...p, phone: v }))} />
            <FI label="Insurance email" value={insForm.email} onChange={v => setInsForm(p => ({ ...p, email: v }))} />
          </div>
          {/* Broker name gets its own row; three label+field pairs across left ~80px per
              input, too narrow to read an email in. Phone/email pair up like the row above. */}
          <FI label="Broker name" value={insForm.brokerName} onChange={v => setInsForm(p => ({ ...p, brokerName: v }))} />
          <div className="form-grid form-grid-2">
            <FI label="Broker phone" value={insForm.brokerPhone} onChange={v => setInsForm(p => ({ ...p, brokerPhone: v }))} />
            <FI label="Broker email" value={insForm.brokerEmail} onChange={v => setInsForm(p => ({ ...p, brokerEmail: v }))} />
          </div>
          {insModal === 'edit' && (() => {
            const liveDocs = ((prop.insurance || []).find(i => i.id === insForm.id)?.documents) || [];
            return (
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Documents</div>
                  <label className="btn-ghost" style={{ fontSize: 12, cursor: uploadingDoc ? 'default' : 'pointer' }}>
                    {uploadingDoc ? 'Uploading…' : <>{ic(I.plus, 12)} Upload</>}
                    <input type="file" style={{ display: 'none' }} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={e => handleDocUpload(e, insForm.id)} disabled={uploadingDoc} />
                  </label>
                </div>
                {liveDocs.length === 0
                  ? <div style={{ fontSize: 12, color: 'var(--text3)' }}>No documents uploaded yet.</div>
                  : liveDocs.map(d => (
                      <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '6px 8px', background: 'var(--bg2)', marginBottom: 4 }}>
                        <a href={d.url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: 'var(--accent)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }} title={d.name}>{d.name}</a>
                        <button className="btn-ghost" style={{ fontSize: 11, color: 'var(--red-txt)', flexShrink: 0 }} onClick={() => handleDocDelete(insForm.id, d)}>Delete</button>
                      </div>
                    ))}
              </div>
            );
          })()}
          <div className="modal-actions">
            <button className="btn" onClick={() => setInsModal(null)}>Cancel</button>
            <SavingBtn onClick={insModal === 'edit' ? updateIns : saveIns}>Save changes</SavingBtn>
          </div>
        </Modal>
      )}

      {/* ── Insurance View Modal ── */}
      {viewIns && (
        <Modal title={viewIns.type} onClose={() => setViewIns(null)} lg>
          <div className="info-grid">
            <InfoItem label="Insurance name" value={viewIns.name} />
            <InfoItem label="Policy number" value={viewIns.policyNumber} />
            <InfoItem label="Start date" value={fmtDate(viewIns.startDate)} />
            <InfoItem label="End date" value={fmtDate(viewIns.endDate)} />
            <InfoItem label="Insurance phone" value={viewIns.phone} />
            <InfoItem label="Insurance email" value={viewIns.email} />
            <InfoItem label="Broker name" value={viewIns.brokerName} />
            <InfoItem label="Broker phone" value={viewIns.brokerPhone} />
            <InfoItem label="Broker email" value={viewIns.brokerEmail} />
          </div>
          {/* Documents */}
          <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{ic(I.file, 13, 'var(--text2)')} Documents</div>
              <label style={{ cursor: uploadingDoc ? 'default' : 'pointer', opacity: uploadingDoc ? 0.6 : 1 }}>
                <input type="file" style={{ display: 'none' }} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={e => handleDocUpload(e, viewIns.id)} disabled={uploadingDoc} />
                <span className="btn-ghost" style={{ fontSize: 12, pointerEvents: uploadingDoc ? 'none' : 'auto' }}>
                  {uploadingDoc ? 'Uploading…' : <>{ic(I.plus, 12)} Upload</>}
                </span>
              </label>
            </div>
            {(viewIns.documents || []).length === 0
              ? <div style={{ fontSize: 12, color: 'var(--text3)' }}>No documents uploaded yet.</div>
              : (viewIns.documents || []).map(d => (
                  <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', background: 'var(--bg2)', borderRadius: 6, marginBottom: 4 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                      {ic(I.file, 14, 'var(--text2)')}
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</div>
                        <div style={{ fontSize: 11, color: 'var(--text3)' }}>{d.uploadedAt ? new Date(d.uploadedAt).toLocaleDateString('en-US') : ''}{d.size != null ? ` · ${fmtSize(d.size)}` : ''}</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                      <a href={d.url} target="_blank" rel="noopener noreferrer" className="btn-ghost" style={{ fontSize: 11 }}>View</a>
                      <button className="btn-ghost" style={{ fontSize: 11, color: 'var(--red-txt)' }} onClick={() => handleDocDelete(viewIns.id, d)}>Delete</button>
                    </div>
                  </div>
                ))
            }
          </div>
          <div className="modal-actions"><button className="btn btn-primary" onClick={() => setViewIns(null)}>Close</button></div>
        </Modal>
      )}

      {/* ── Compliance Add / Edit Modal ── */}
      {compModal && (
        <Modal title={compModal === 'edit' ? 'Edit compliance' : 'Add compliance'} onClose={() => setCompModal(null)}>
          <FI label="Title" value={compForm.title} onChange={v => setCompForm(p => ({ ...p, title: v }))} />
          <FI label="Due date" value={compForm.dueDate} onChange={v => setCompForm(p => ({ ...p, dueDate: v }))} type="date" />
          <FTA label="Description / Instructions" value={compForm.instructions} onChange={v => setCompForm(p => ({ ...p, instructions: v }))} />
          <div className="modal-actions">
            <button className="btn" onClick={() => setCompModal(null)}>Cancel</button>
            <SavingBtn onClick={compModal === 'edit' ? updateComp : saveComp}>Save changes</SavingBtn>
          </div>
        </Modal>
      )}

      {/* ── Unit Modal ── */}
      {unitModal && (
        <Modal title={unitModal === 'edit' ? 'Edit unit' : 'Add unit'} onClose={() => setUnitModal(null)}>
          <FI label="Unit number" value={unitForm.unitNumber} onChange={v => setUnitForm(p => ({ ...p, unitNumber: v }))} placeholder="e.g. 1A" />
          <FS label="Unit type" value={unitForm.unitType || ''} onChange={v => setUnitForm(p => ({ ...p, unitType: v }))} options={['Market', 'Affordable 421a', 'Commercial']} placeholder="— Select type —" />
          <div className="form-grid form-grid-2">
            <FI label="Bedrooms" value={unitForm.bedrooms} onChange={v => setUnitForm(p => ({ ...p, bedrooms: v }))} type="number" />
            <FI label="Bathrooms" value={unitForm.bathrooms} onChange={v => setUnitForm(p => ({ ...p, bathrooms: v }))} type="number" />
          </div>
          <FI label="Unit size (sq ft)" value={unitForm.unitSize} onChange={v => setUnitForm(p => ({ ...p, unitSize: v }))} type="number" />
          <FS label="Balcony" value={unitForm.balcony} onChange={v => setUnitForm(p => ({ ...p, balcony: v }))} options={['No Balcony', 'With Balcony', 'Private Terrace', 'Terrace and Balcony']} />
          <div className="modal-actions">
            <button className="btn" onClick={() => setUnitModal(null)}>Cancel</button>
            <SavingBtn onClick={saveUnit}>Save</SavingBtn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Toast system ──────────────────────────────────────────────────────────────
const ToastContext = React.createContext(null);
const useToast = () => React.useContext(ToastContext);

function Toaster({ toasts, onRemove }) {
  if (!toasts.length) return null;
  return (
    <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 99999, display: 'flex', flexDirection: 'column', gap: 8, pointerEvents: 'none' }}>
      {toasts.map(t => (
        <div key={t.id} className={`toast${t.removing ? ' removing' : ''}`}
          onClick={() => onRemove(t.id)}
          style={{
            pointerEvents: 'auto', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '11px 16px', borderRadius: 10,
            fontSize: 13, fontWeight: 500,
            minWidth: 220, maxWidth: 340,
            boxShadow: '0 4px 24px rgba(0,0,0,0.5)',
            background: t.type === 'error' ? '#dc2626' : t.type === 'info' ? '#2563eb' : '#16a34a',
            color: '#fff',
            border: 'none',
          }}>
          <span style={{ fontSize: 16, lineHeight: 1 }}>
            {t.type === 'error' ? '✕' : t.type === 'info' ? 'ℹ' : '✓'}
          </span>
          <span style={{ flex: 1 }}>{t.message}</span>
        </div>
      ))}
    </div>
  );
}

function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const remove = id => setToasts(p => p.map(t => t.id === id ? { ...t, removing: true } : t));
  const toast = React.useCallback((message, type = 'success') => {
    const id = `t${Date.now()}${Math.random().toString(36).slice(2)}`;
    setToasts(p => [...p, { id, message, type, removing: false }]);
    setTimeout(() => remove(id), 3300);
    setTimeout(() => setToasts(p => p.filter(t => t.id !== id)), 3500);
  }, []);
  return (
    <ToastContext.Provider value={toast}>
      {children}
      <Toaster toasts={toasts} onRemove={remove} />
    </ToastContext.Provider>
  );
}

function Properties({ properties, setProperties, tenants, violations, user, initialId, clearNav }) {
  const toast = useToast();
  const isAdmin = user?.email === ADMIN_EMAIL;
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [selectedId, setSelectedId] = useState(initialId || null);
  const [showArchived, setShowArchived] = useState(false);
  useEffect(() => { if (initialId) { setSelectedId(initialId); if (clearNav) clearNav(); } }, [initialId]); // eslint-disable-line
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  const ff = k => v => setForm(p => ({ ...p, [k]: v }));
  const rows = useMemo(() => properties.filter(p => !p.archived && (!search || p.name.toLowerCase().includes(search.toLowerCase()) || (p.address || '').toLowerCase().includes(search.toLowerCase()))), [properties, search]);
  const archivedRows = useMemo(() => properties.filter(p => p.archived), [properties]);
  // Group active properties by business type — Multi Family Housing first, then
  // Construction, then anything else. Empty groups are dropped.
  const groupedRows = useMemo(() => {
    const buckets = { 'Multi Family Housing': [], 'Construction': [], 'Other': [] };
    rows.forEach(p => {
      const key = p.businessType === 'Construction' ? 'Construction'
        : p.businessType === 'Multi Family Housing' ? 'Multi Family Housing'
        : 'Other';
      buckets[key].push(p);
    });
    return Object.entries(buckets).filter(([, list]) => list.length > 0);
  }, [rows]);
  const PROPERTY_OWNERS = ['Amninder Singh', 'Devinder Singh', 'Simranjeet Singh'];
  // Auto-format EIN as the user types: NN-NNNNNNN (2 digits, dash, up to 7 digits)
  const formatEIN = v => {
    const digits = (v || '').replace(/\D/g, '').slice(0, 9);
    return digits.length <= 2 ? digits : `${digits.slice(0, 2)}-${digits.slice(2)}`;
  };
  const openAdd = (businessType = 'Multi Family Housing') => { setForm({ name: '', address: '', ein: '', businessType, owner: '', bin: '', lot: '', block: '', email: '' }); setModal('add'); };
  const openEdit = p => { setForm({ ...p }); setModal('edit'); };
  const save = () => {
    // Property name doubles as the legal entity name — keep entityName in sync so
    // all entityName consumers (notices, lease PDFs, property detail) stay correct.
    const entry = { ...form, entityName: form.name, insurance: form.insurance || [], compliance: form.compliance || [], unitsList: form.unitsList || [] };
    if (modal === 'edit') setProperties(prev => prev.map(p => p.id === form.id ? entry : p));
    else setProperties(prev => [...prev, { ...entry, id: `p${Date.now()}` }]);
    setModal(null);
    toast(modal === 'edit' ? 'Property updated' : 'Property added');
  };
  const archive = id => {
    if (!window.confirm('Archive this property? It will be hidden from active views but all data is preserved.')) return;
    setProperties(prev => prev.map(p => p.id === id ? { ...p, archived: true, archivedAt: new Date().toISOString() } : p));
    toast('Property archived', 'info');
  };
  const unarchive = id => {
    setProperties(prev => prev.map(p => p.id === id ? { ...p, archived: false, archivedAt: null } : p));
    toast('Property restored');
  };

  if (selectedId) return <PropertyDetail propertyId={selectedId} properties={properties} setProperties={setProperties} tenants={tenants} user={user} onBack={() => setSelectedId(null)} />;
  return (
    <div>
      <div className="page-header"><span className="page-title">Properties</span><button className="btn btn-primary" onClick={() => setModal('addChoose')}>{ic(I.plus, 14, '#fff')} Add property</button></div>

      <div style={{ marginBottom: 16 }}><SB value={search} onChange={setSearch} placeholder="Search by name or address…" /></div>

      {/* Desktop property list — full columns */}
      {!isMobile && (
        <table className="list-table" style={{ marginBottom: 24 }}>
          <thead><tr><th>Property</th><th>Address</th><th>Units</th><th>Owner</th><th style={{ width: 40 }}></th></tr></thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={5}><div className="empty">No properties found</div></td></tr> :
              groupedRows.flatMap(([label, list]) => [
                <tr key={`hdr-${label}`}>
                  <td colSpan={5} style={{ background: 'var(--bg2)', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text2)', padding: '10px 12px' }}>
                    {label} <span style={{ color: 'var(--text3)', fontWeight: 500 }}>({list.length})</span>
                  </td>
                </tr>,
                ...list.map(p => {
                  const occ = tenants.filter(t => t.propertyId === p.id && t.status === 'Occupied').length;
                  const totalUnits = (p.unitsList || []).length || p.units || 0;
                  return (
                    <tr key={p.id} className="clickable" onClick={() => setSelectedId(p.id)}>
                      <td><div style={{ fontWeight: 500 }}>{p.name}</div></td>
                      <td style={{ color: 'var(--text2)' }}>{p.address}</td>
                      <td><span style={{ fontWeight: 500 }}>{occ}</span><span style={{ color: 'var(--text2)' }}>/{totalUnits}</span></td>
                      <td style={{ color: 'var(--text2)' }}>{p.owner}</td>
                      <td><div onClick={e => e.stopPropagation()}><KebabMenu items={[{ label: 'View detail', onClick: () => setSelectedId(p.id) }, { label: 'Edit', onClick: () => openEdit(p) }, ...(isAdmin ? [{ label: 'Archive', danger: true, onClick: () => archive(p.id) }] : [])]} /></div></td>
                    </tr>
                  );
                }),
              ])}
          </tbody>
        </table>
      )}

      {/* Mobile property list — Property + Address only */}
      {isMobile && (
        <table className="list-table" style={{ marginBottom: 24, display: 'table', width: '100%', tableLayout: 'fixed', whiteSpace: 'normal' }}>
          <colgroup><col /><col style={{ width: 44 }} /></colgroup>
          <thead><tr><th style={{ whiteSpace: 'normal' }}>Property</th><th></th></tr></thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={2} style={{ whiteSpace: 'normal' }}><div className="empty">No properties found</div></td></tr> :
              groupedRows.flatMap(([label, list]) => [
                <tr key={`hdr-${label}`}>
                  <td colSpan={2} style={{ whiteSpace: 'normal', background: 'var(--bg2)', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text2)', padding: '8px 10px' }}>
                    {label} <span style={{ color: 'var(--text3)', fontWeight: 500 }}>({list.length})</span>
                  </td>
                </tr>,
                ...list.map(p => (
                  <tr key={p.id} className="clickable" onClick={() => setSelectedId(p.id)}>
                    <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                      <div style={{ fontWeight: 500, fontSize: 13 }}>{p.name}</div>
                      {p.address && <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2 }}>{p.address}</div>}
                    </td>
                    <td style={{ width: 44, textAlign: 'center' }}><div onClick={e => e.stopPropagation()}><KebabMenu items={[{ label: 'View detail', onClick: () => setSelectedId(p.id) }, { label: 'Edit', onClick: () => openEdit(p) }, ...(isAdmin ? [{ label: 'Archive', danger: true, onClick: () => archive(p.id) }] : [])]} /></div></td>
                  </tr>
                )),
              ])}
          </tbody>
        </table>
      )}


      {/* ── Archived properties ── */}
      {isAdmin && archivedRows.length > 0 && (
        <div style={{ marginTop: 8, marginBottom: 16 }}>
          <button className="btn-ghost" style={{ fontSize: 13, fontWeight: 500, marginBottom: 10 }} onClick={() => setShowArchived(p => !p)}>
            {showArchived ? '▾' : '▸'} Archived properties ({archivedRows.length})
          </button>
          {showArchived && (
            <table className="list-table" style={{ opacity: 0.75 }}>
              <thead><tr><th>Property</th><th>Address</th><th>Archived</th><th style={{ width: 40 }}></th></tr></thead>
              <tbody>
                {archivedRows.map(p => (
                  <tr key={p.id}>
                    <td><div style={{ fontWeight: 500, color: 'var(--text2)' }}>{p.name}</div></td>
                    <td style={{ color: 'var(--text3)', fontSize: 12 }}>{p.address}</td>
                    <td style={{ color: 'var(--text3)', fontSize: 12 }}>{p.archivedAt ? fmtDate(p.archivedAt.slice(0, 10)) : '—'}</td>
                    <td><KebabMenu items={[{ label: 'Restore', onClick: () => unarchive(p.id) }, { label: 'View detail', onClick: () => setSelectedId(p.id) }, { label: 'Delete permanently', danger: true, onClick: () => { if (window.confirm(`Permanently delete "${p.name}"? This cannot be undone.`)) setProperties(prev => prev.filter(x => x.id !== p.id)); } }]} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {modal === 'addChoose' && (
        <Modal title="Add property" onClose={() => setModal(null)}>
          <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>What type of property are you adding?</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[
              { type: 'Multi Family Housing', desc: 'Residential building with rental units, tenants, and rent roll.' },
              { type: 'Construction', desc: 'Construction / development entity — no tenant units.' },
            ].map(({ type, desc }) => (
              <button key={type} className="btn" style={{ textAlign: 'left', padding: '14px 16px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4, height: 'auto' }} onClick={() => openAdd(type)}>
                <span style={{ fontWeight: 600, fontSize: 14 }}>{type}</span>
                <span style={{ fontSize: 12, color: 'var(--text2)', fontWeight: 400 }}>{desc}</span>
              </button>
            ))}
          </div>
        </Modal>
      )}

      {(modal === 'add' || modal === 'edit') && (
        <Modal title={modal === 'edit' ? 'Edit property' : <span>Add property <span style={{ fontWeight: 400, color: 'var(--text2)' }}>— {form.businessType}</span></span>} onClose={() => setModal(null)} width={isMobile ? undefined : 560}>
          {/* Property name doubles as the legal entity name (LLC / Corp); synced to entityName on save */}
          <FI label="Property name" value={form.name} onChange={ff('name')} placeholder="e.g. Amar Marketing Enterprise 2 LLC" />
          <FI label="Address" value={form.address} onChange={ff('address')} />
          {modal === 'edit'
            ? <div className="form-grid form-grid-2">
                <FI label="EIN" value={form.ein} onChange={v => ff('ein')(formatEIN(v))} placeholder="XX-XXXXXXX" />
                <FS label="Business type" value={form.businessType} onChange={ff('businessType')} options={['Multi Family Housing', 'Construction']} />
              </div>
            : <FI label="EIN" value={form.ein} onChange={v => ff('ein')(formatEIN(v))} placeholder="XX-XXXXXXX" />
          }
          <div className="form-group">
            <label className="form-label">Property Owner</label>
            <SuggestInput value={form.owner} onChange={ff('owner')} options={PROPERTY_OWNERS} placeholder="Select or type an owner" />
          </div>
          <FI label="Email" value={form.email} onChange={ff('email')} type="email" placeholder="office@example.com" />
          <FI label="Phone" value={form.phone} onChange={ff('phone')} placeholder="e.g. (212) 555-1234" />
          <FI label="DOS ID" value={form.dosId} onChange={ff('dosId')} />
          <FI label="DHCR Building Registration #" value={form.dhcrRegNum} onChange={ff('dhcrRegNum')} />
          <div className="form-group">
            <label className="form-label">BIN / Block / Lot</label>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              {[['BIN', 'bin'], ['Block', 'block'], ['Lot', 'lot']].map(([lbl, key]) => (
                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 12, color: 'var(--text2)', fontWeight: 500 }}>{lbl}</span>
                  <input className="form-input" style={{ width: 84 }} value={form[key] || ''} onChange={e => ff(key)(e.target.value)} />
                </div>
              ))}
            </div>
          </div>
          {form.businessType === 'Construction' && (
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Additional Addresses (for sync)</div>
            {(form.altAddresses || []).map((addr) => {
              const upd = (key, v) => setForm(p => ({ ...p, altAddresses: p.altAddresses.map(a => a.id === addr.id ? { ...a, [key]: v } : a) }));
              return (
                <div key={addr.id} style={{ marginBottom: 14, paddingBottom: 14, borderBottom: '1px solid var(--border)' }}>
                  <FI label="Address" value={addr.label || ''} onChange={v => upd('label', v)} placeholder="e.g. 123 Main St" />
                  <div className="form-group">
                    <label className="form-label">BIN / Block / Lot</label>
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                      {[['BIN', 'bin'], ['Block', 'block'], ['Lot', 'lot']].map(([lbl, key]) => (
                        <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 12, color: 'var(--text2)', fontWeight: 500 }}>{lbl}</span>
                          <input className="form-input" style={{ width: 72 }} value={addr[key] || ''} onChange={e => upd(key, e.target.value)} />
                        </div>
                      ))}
                      <button className="btn-ghost" style={{ color: 'var(--text3)', padding: '6px 8px', flexShrink: 0 }} onClick={() => setForm(p => ({ ...p, altAddresses: p.altAddresses.filter(a => a.id !== addr.id) }))}>✕</button>
                    </div>
                  </div>
                </div>
              );
            })}
            <button className="btn-ghost" style={{ fontSize: 12 }} onClick={() => setForm(p => ({ ...p, altAddresses: [...(p.altAddresses || []), { id: `alt_${Date.now()}`, label: '', bin: '', block: '', lot: '' }] }))}>+ Add address</button>
          </div>
          )}
          <div className="modal-actions"><button className="btn" onClick={() => setModal(null)}>Cancel</button><SavingBtn onClick={save}>Save</SavingBtn></div>
        </Modal>
      )}
    </div>
  );
}

function ViolationDetail({ violationId, violations, setViolations, properties, onBack, onDelete }) {
  const toast = useToast();
  const v = violations.find(x => x.id === violationId);
  const [editingNotes, setEditingNotes] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');
  const [editingFinancials, setEditingFinancials] = useState(false);
  const [finDraft, setFinDraft] = useState({});
  const [editingDetails, setEditingDetails] = useState(false);
  const [detailsDraft, setDetailsDraft] = useState({});
  const [editingLegal, setEditingLegal] = useState(false);
  const [legalDraft, setLegalDraft] = useState({});
  const [editingFollowUp, setEditingFollowUp] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  if (!v) return null;

  const updateV = fn => setViolations(prev => prev.map(x => x.id === violationId ? fn(x) : x));

  const handleDocUpload = async e => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (e.target.value !== undefined) e.target.value = '';
    setUploadingDoc(true);
    try {
      const newDocs = await Promise.all(files.map(async file => {
        const path = `violations/${violationId}/documents/${Date.now()}_${file.name}`;
        const ref = sRef(storage, path);
        await uploadBytes(ref, file);
        const url = await getDownloadURL(ref);
        return { id: `vdoc${Date.now()}${Math.random().toString(36).slice(2)}`, name: file.name, url, storagePath: path, uploadedAt: new Date().toISOString(), size: file.size };
      }));
      updateV(x => ({ ...x, documents: [...(x.documents || []), ...newDocs] }));
      toast('Document uploaded');
    } catch { alert('Upload failed. Please try again.'); }
    setUploadingDoc(false);
  };

  const deleteDoc = async doc => {
    if (!window.confirm(`Delete "${doc.name}"?`)) return;
    try { if (doc.storagePath) await deleteObject(sRef(storage, doc.storagePath)); } catch {}
    updateV(x => ({ ...x, documents: (x.documents || []).filter(d => d.id !== doc.id) }));
    toast('Document removed', 'info');
  };
  const propName = properties.find(p => p.id === v.propertyId)?.name || v.propertyId;
  const vStatuses = v.statuses || (v.status ? [v.status] : ['Open']);
  const isOD = v.followUp && v.followUp < nyTodayISO() && !vStatuses.some(s => ['Completed', 'Dismissed', 'Resolved'].includes(s));
  const isDTv = v.followUp && v.followUp === nyTodayISO() && !vStatuses.some(s => ['Completed', 'Dismissed', 'Resolved'].includes(s));
  const InfoItem = ({ label, value }) => (
    <div><div className="info-label">{label}</div><div className="info-val">{value || '—'}</div></div>
  );
  const saveNotes = async () => {
    setViolations(prev => prev.map(x => x.id === violationId ? { ...x, notes: noteDraft } : x));
    setEditingNotes(false);
    try {
      await setDoc(doc(db, 'violations', violationId), { notes: noteDraft }, { merge: true });
      toast('Notes saved');
    } catch (e) {
      toast('Save failed: ' + (e.message || 'unknown error'), 'error');
    }
  };
  const saveFinancials = async () => {
    const patch = { penaltyAmount: finDraft.penaltyAmount, legalRepCharge: finDraft.legalRepCharge, amountPaid: finDraft.amountPaid };
    setViolations(prev => prev.map(x => x.id === violationId ? { ...x, ...patch } : x));
    setEditingFinancials(false);
    try {
      await setDoc(doc(db, 'violations', violationId), patch, { merge: true });
      toast('Financials saved');
    } catch (e) {
      toast('Save failed: ' + (e.message || 'unknown error'), 'error');
    }
  };
  const saveLegal = async () => {
    const patch = { legalRep: legalDraft.legalRep, legalPhone: legalDraft.legalPhone, legalEmail: legalDraft.legalEmail };
    setViolations(prev => prev.map(x => x.id === violationId ? { ...x, ...patch } : x));
    setEditingLegal(false);
    try {
      await setDoc(doc(db, 'violations', violationId), patch, { merge: true });
      toast('Legal representative saved');
    } catch (e) {
      toast('Save failed: ' + (e.message || 'unknown error'), 'error');
    }
  };
  const saveDetails = async () => {
    let statuses = detailsDraft.statuses?.length ? detailsDraft.statuses : ['Open'];
    const RESOLVED = ['Completed', 'Dismissed', 'Resolved'];
    if (statuses.some(s => RESOLVED.includes(s))) statuses = statuses.filter(s => s !== 'Open');
    const patch = { ...detailsDraft, statuses };
    setViolations(prev => prev.map(x => x.id === violationId ? { ...x, ...patch } : x));
    setEditingDetails(false);
    try {
      await setDoc(doc(db, 'violations', violationId), patch, { merge: true });
      toast('Details saved');
    } catch (e) {
      toast('Save failed: ' + (e.message || 'unknown error'), 'error');
    }
  };
  const ALL_V_STATUSES = v.type === 'HPD' ? ['New','Open','Completed'] : ['FDNY','DSNY','DEP'].includes(v.type) ? ['New','Open','Hearing Schedule','Pending Payment','Resolved','Not Owned'] : ['New','Open','On-Going','Hearing Schedule','Pending Payment','Adjourned','Rescheduled','Defaulted','Completed','Dismissed','Resolved','Not Owned'];
  const penalty = parseFloat(v.penaltyAmount) || 0;
  const legalCharge = parseFloat(v.legalRepCharge) || 0;
  const paid = parseFloat(v.amountPaid) || 0;
  const totalCharge = penalty + legalCharge;
  const balance = totalCharge - paid;
  const fmt$ = n => n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  return (
    <div>
      <button className="back-btn" onClick={onBack}>{ic(I.arrowLeft, 15)} All violations</button>

      {/* ── Header card ── */}
      <div className="card" style={{ marginBottom: 16, padding: '14px 18px', borderRadius: 0, border: '1px solid var(--border)', borderBottom: 'none', transform: 'none', transition: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, fontWeight: 700, background: 'var(--blue-bg)', color: 'var(--blue-txt)', padding: '3px 10px', borderRadius: 6 }}>{v.type}</span>
            {v.source === 'ECB' ? (
              <>
                {v.issue && <><span style={{ color: 'var(--text3)' }}>|</span><span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text1)' }}>{v.issue}</span></>}
                {v.caseNum && <><span style={{ color: 'var(--text3)' }}>|</span><span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text1)' }}>{v.caseNum}</span></>}
                {v.respondentName && <><span style={{ color: 'var(--text3)' }}>|</span><span style={{ fontSize: 13, color: 'var(--text2)' }}>{v.respondentName}</span></>}
                {v.issuedDate && <><span style={{ color: 'var(--text3)' }}>|</span><span style={{ fontSize: 13, color: 'var(--text2)' }}>{fmtDate(v.issuedDate)}</span></>}
              </>
            ) : (
              <>
                {v.violationClass && <><span style={{ color: 'var(--text3)' }}>|</span><span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text1)' }}>{v.violationClass}</span></>}
                <span style={{ color: 'var(--text3)' }}>|</span>
                <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text1)' }}>{v.caseNum}</span>
                <span style={{ color: 'var(--text3)' }}>|</span>
                <span style={{ fontSize: 13, color: 'var(--text2)' }}>{propName}</span>
                {v.unit && <><span style={{ color: 'var(--text3)' }}>|</span><span style={{ fontSize: 13, color: 'var(--text2)' }}>Unit {v.unit}</span></>}
                {v.issuedDate && <><span style={{ color: 'var(--text3)' }}>|</span><span style={{ fontSize: 13, color: 'var(--text2)' }}>{fmtDate(v.issuedDate)}</span></>}
              </>
            )}
          </div>
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            <button className="btn-ghost" style={{ fontSize: 12 }} onClick={() => { setDetailsDraft({ caseNum: v.caseNum || '', type: v.type || 'DOB', propertyId: v.propertyId || '', unit: v.unit || '', issuedDate: v.issuedDate || '', followUp: v.followUp || '', statuses: v.statuses || ['Open'], legalRep: v.legalRep || '', legalPhone: v.legalPhone || '', legalEmail: v.legalEmail || '' }); setEditingDetails(true); }}>{ic(I.pencil, 12)} Edit details</button>
          </div>
        </div>

        {/* ── Status + Follow-up ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {vStatuses.map(s => <Badge key={s} label={s} />)}
        <span style={{ color: 'var(--text3)', fontSize: 13 }}>·</span>
        {editingFollowUp
          ? <input
              type="date" autoFocus
              className="form-input"
              style={{ fontSize: 12, padding: '3px 8px', width: 'auto' }}
              defaultValue={v.followUp || ''}
              onBlur={async e => {
                const val = e.target.value;
                setEditingFollowUp(false);
                if (!val || val === (v.followUp || '')) return;
                updateV(x => ({ ...x, followUp: val }));
                try {
                  await setDoc(doc(db, 'violations', violationId), { followUp: val }, { merge: true });
                  toast('Follow-up date saved');
                } catch (err) {
                  toast('Save failed: ' + (err.message || 'unknown error'), 'error');
                }
              }}
              onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') setEditingFollowUp(false); }}
            />
          : <span
              onClick={() => setEditingFollowUp(true)}
              style={{ fontSize: 13, fontWeight: isOD ? 700 : 500, color: isOD ? 'var(--red-txt)' : isDTv ? 'var(--amber-txt)' : 'var(--text2)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
              title="Click to edit follow-up date"
            >
              {v.followUp ? fmtDate(v.followUp) : 'Set follow-up date'}
              <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}><path d="M6 9l6 6 6-6"/></svg>
            </span>
        }
        </div>
        <div style={{ borderTop: '1px solid var(--border)', marginTop: 14, paddingTop: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Violation Description</div>
          {v.source === 'ECB' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <span style={{ color: 'var(--text2)', minWidth: 140, flexShrink: 0 }}>Infraction Codes:</span>
                <span style={{ color: 'var(--text1)' }}>{v.infractionCode || '—'}</span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <span style={{ color: 'var(--text2)', minWidth: 140, flexShrink: 0 }}>Violation Type:</span>
                <span style={{ color: 'var(--text1)' }}>{v.issue || '—'}</span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <span style={{ color: 'var(--text2)', minWidth: 140, flexShrink: 0 }}>Respondent Name:</span>
                <span style={{ color: 'var(--text1)' }}>{v.respondentName || '—'}</span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <span style={{ color: 'var(--text2)', minWidth: 140, flexShrink: 0 }}>Description:</span>
                <span style={{ color: v.violationDesc ? 'var(--text1)' : 'var(--text3)', lineHeight: 1.65, whiteSpace: 'pre-wrap' }}><Linkify text={v.violationDesc || '—'} /></span>
              </div>
            </div>
          ) : (
            <div style={{ fontSize: 13, color: v.violationDesc ? 'var(--text1)' : 'var(--text3)', lineHeight: 1.65, whiteSpace: 'pre-wrap' }}><Linkify text={v.violationDesc || '—'} /></div>
          )}
        </div>
        {(v.type === 'DOB' || v.type === 'FDNY') && (
          <div style={{ borderTop: '1px solid var(--border)', marginTop: 14, paddingTop: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Legal Representative</div>
              {editingLegal
                ? <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn-ghost" onClick={() => setEditingLegal(false)}>Cancel</button>
                    <SavingBtn onClick={saveLegal} style={{ fontSize: 12, padding: '4px 10px' }}>Save</SavingBtn>
                  </div>
                : <button className="btn-ghost" style={{ fontSize: 12 }} onClick={() => { setLegalDraft({ legalRep: v.legalRep || '', legalPhone: v.legalPhone || '', legalEmail: v.legalEmail || '' }); setEditingLegal(true); }}>{ic(I.pencil, 12)} Edit</button>
              }
            </div>
            {editingLegal ? (
              <div className="form-grid form-grid-3">
                <div className="form-group" style={{ marginBottom: 0 }}><div className="info-label">Name</div><LegalRepNameInput value={legalDraft.legalRep} onChange={val => setLegalDraft(p => ({ ...p, legalRep: val }))} onPick={r => setLegalDraft({ legalRep: r.name, legalPhone: r.phone, legalEmail: r.email })} autoFocus /></div>
                <div className="form-group" style={{ marginBottom: 0 }}><div className="info-label">Phone</div><input className="form-input" value={legalDraft.legalPhone} onChange={e => setLegalDraft(p => ({ ...p, legalPhone: e.target.value }))} placeholder="Phone" /></div>
                <div className="form-group" style={{ marginBottom: 0 }}><div className="info-label">Email</div><input className="form-input" value={legalDraft.legalEmail} onChange={e => setLegalDraft(p => ({ ...p, legalEmail: e.target.value }))} placeholder="Email" /></div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <span style={{ color: 'var(--text2)', minWidth: 60, flexShrink: 0 }}>Name:</span>
                  <span style={{ color: v.legalRep ? 'var(--text1)' : 'var(--text3)' }}>{v.legalRep || '—'}</span>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <span style={{ color: 'var(--text2)', minWidth: 60, flexShrink: 0 }}>Phone:</span>
                  <span style={{ color: v.legalPhone ? 'var(--text1)' : 'var(--text3)' }}>{v.legalPhone || '—'}</span>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <span style={{ color: 'var(--text2)', minWidth: 60, flexShrink: 0 }}>Email:</span>
                  <span style={{ color: v.legalEmail ? 'var(--text1)' : 'var(--text3)' }}>{v.legalEmail || '—'}</span>
                </div>
              </div>
            )}
          </div>
        )}
        {v.type !== 'HPD' && v.type !== 'Insurance Claim' && (
          <div style={{ borderTop: '1px solid var(--border)', marginTop: 14, paddingTop: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Penalty</div>
              {editingFinancials
                ? <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn-ghost" onClick={() => setEditingFinancials(false)}>Cancel</button>
                    <SavingBtn onClick={saveFinancials} style={{ fontSize: 12, padding: '4px 10px' }}>Save</SavingBtn>
                  </div>
                : <button className="btn-ghost" onClick={() => { setFinDraft({ penaltyAmount: v.penaltyAmount || '', legalRepCharge: v.legalRepCharge || '', amountPaid: v.amountPaid || '' }); setEditingFinancials(true); }}>{ic(I.pencil, 12)} Edit</button>
              }
            </div>
            {editingFinancials ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div className="form-grid form-grid-3">
                  <div className="form-group" style={{ marginBottom: 0 }}><div className="info-label">Penalty ($)</div><input className="form-input" value={finDraft.penaltyAmount} onChange={e => setFinDraft(p => ({ ...p, penaltyAmount: e.target.value }))} placeholder="0.00" /></div>
                  <div className="form-group" style={{ marginBottom: 0 }}><div className="info-label">Legal Rep Charge ($)</div><input className="form-input" value={finDraft.legalRepCharge} onChange={e => setFinDraft(p => ({ ...p, legalRepCharge: e.target.value }))} placeholder="0.00" /></div>
                  <div className="form-group" style={{ marginBottom: 0 }}><div className="info-label">Amount Paid ($)</div><input className="form-input" value={finDraft.amountPaid} onChange={e => setFinDraft(p => ({ ...p, amountPaid: e.target.value }))} placeholder="0.00" /></div>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span style={{ color: 'var(--text2)' }}>Penalty:</span>
                  <span style={{ fontWeight: 500, color: 'var(--text1)' }}>{penalty ? fmt$(penalty) : '—'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span style={{ color: 'var(--text2)' }}>Legal Representative Charge:</span>
                  <span style={{ fontWeight: 500, color: 'var(--text1)' }}>{legalCharge ? fmt$(legalCharge) : '—'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span style={{ color: 'var(--text2)' }}>Amount Paid:</span>
                  <span style={{ fontWeight: 500, color: 'var(--text1)' }}>{paid ? fmt$(paid) : '—'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, borderTop: '1px solid var(--border)', paddingTop: 6, marginTop: 2 }}>
                  <span style={{ color: 'var(--text2)' }}>Total Amount:</span>
                  <span style={{ fontWeight: 600, color: 'var(--text1)' }}>{totalCharge ? fmt$(totalCharge) : '—'}</span>
                </div>
                {totalCharge > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                    <span style={{ color: 'var(--text2)' }}>Balance Due:</span>
                    <span style={{ fontWeight: 600, color: balance > 0 ? 'var(--red-txt)' : 'var(--green-txt)' }}>{balance > 0 ? fmt$(balance) : 'Paid in full'}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        <div style={{ borderTop: '1px solid var(--border)', marginTop: 14, paddingTop: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Documents</div>
            <label style={{ cursor: 'pointer' }}>
              <input type="file" multiple style={{ display: 'none' }} onChange={handleDocUpload} />
              <span className="btn-ghost" style={{ pointerEvents: 'none', fontSize: 12 }}>{uploadingDoc ? 'Uploading…' : <>{ic(I.plus, 12)} Attach Document</>}</span>
            </label>
          </div>
          {(v.documents || []).length === 0
            ? <div style={{ color: 'var(--text3)', fontSize: 13 }}>No documents uploaded yet.</div>
            : <div className="doc-grid">
                {(v.documents || []).map(doc => (
                  <div key={doc.id} className="doc-card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 4 }}>
                      <a href={doc.url} target="_blank" rel="noopener noreferrer" className="doc-name" style={{ color: 'var(--accent)', textDecoration: 'none' }}>{doc.name}</a>
                      <button onClick={() => deleteDoc(doc)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', padding: '2px 4px', borderRadius: 4, flexShrink: 0 }}>
                        <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      </button>
                    </div>
                    <div className="doc-date">{doc.size ? fmtSize(doc.size) + ' · ' : ''}{doc.uploadedAt ? fmtDate(doc.uploadedAt.slice(0, 10)) : ''}</div>
                  </div>
                ))}
              </div>
          }
        </div>
        <div style={{ borderTop: '1px solid var(--border)', marginTop: 14, paddingTop: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Notes</div>
            {editingNotes
              ? <div style={{ display: 'flex', gap: 6 }}>
                  <button className="btn-ghost" onClick={() => setEditingNotes(false)}>Cancel</button>
                  <SavingBtn onClick={saveNotes} style={{ fontSize: 12, padding: '4px 10px' }}>Save</SavingBtn>
                </div>
              : <button className="btn-ghost" onClick={() => { setNoteDraft(v.notes || ''); setEditingNotes(true); }}>{ic(I.pencil, 12)} Edit</button>
            }
          </div>
          {editingNotes
            ? <textarea className="form-textarea" style={{ minHeight: 100, overflow: 'hidden', resize: 'none' }}
                ref={el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                value={noteDraft}
                onChange={e => { setNoteDraft(e.target.value); e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                placeholder="Add notes…" autoFocus />
            : <div style={{ fontSize: 13, color: v.notes ? 'var(--text1)' : 'var(--text3)', lineHeight: 1.65, whiteSpace: 'pre-wrap' }}><Linkify text={v.notes || 'No notes yet.'} /></div>
          }
        </div>
      </div>

      {/* ── Edit details form ── */}
      {editingDetails && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div className="card-title" style={{ marginBottom: 0 }}>Edit details</div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn-ghost" onClick={() => setEditingDetails(false)}>Cancel</button>
              <SavingBtn onClick={saveDetails} style={{ fontSize: 12, padding: '4px 10px' }}>Save</SavingBtn>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="form-grid form-grid-2">
              <div className="form-group" style={{ marginBottom: 0 }}><div className="info-label">Case number</div><input className="form-input" value={detailsDraft.caseNum} onChange={e => setDetailsDraft(p => ({ ...p, caseNum: e.target.value }))} /></div>
              <div className="form-group" style={{ marginBottom: 0 }}><div className="info-label">Type</div><select className="form-input" value={detailsDraft.type} onChange={e => setDetailsDraft(p => ({ ...p, type: e.target.value }))}>{['HPD','DOB','FDNY','DSNY','DEP','Court','Insurance Claim','Other'].map(t => <option key={t} value={t}>{t}</option>)}</select></div>
            </div>
            <div className="form-grid form-grid-2">
              <div className="form-group" style={{ marginBottom: 0 }}><div className="info-label">Property</div><select className="form-input" value={detailsDraft.propertyId} onChange={e => setDetailsDraft(p => ({ ...p, propertyId: e.target.value }))}>{properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select></div>
              <div className="form-group" style={{ marginBottom: 0 }}><div className="info-label">Unit</div><input className="form-input" value={detailsDraft.unit} onChange={e => setDetailsDraft(p => ({ ...p, unit: e.target.value }))} placeholder="e.g. 2A" /></div>
            </div>
            <div className="form-grid form-grid-2">
              <div className="form-group" style={{ marginBottom: 0 }}><div className="info-label">Issued date</div><input className="form-input" type="date" value={detailsDraft.issuedDate} onChange={e => setDetailsDraft(p => ({ ...p, issuedDate: e.target.value }))} /></div>
              <div className="form-group" style={{ marginBottom: 0 }}><div className="info-label">Follow-up date</div><input className="form-input" type="date" value={detailsDraft.followUp} onChange={e => setDetailsDraft(p => ({ ...p, followUp: e.target.value }))} /></div>
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <div className="info-label" style={{ marginBottom: 6 }}>Status</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px' }}>
                {ALL_V_STATUSES.map(s => {
                  const checked = (detailsDraft.statuses || []).includes(s);
                  return (
                    <label key={s} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, cursor: 'pointer' }}>
                      <input type="checkbox" checked={checked} style={{ accentColor: 'var(--accent)', cursor: 'pointer' }}
                        onChange={() => { const cur = detailsDraft.statuses || []; let next = checked ? cur.filter(x => x !== s) : [...cur, s]; if (!checked && ['Completed','Dismissed','Resolved'].includes(s)) next = next.filter(x => x !== 'Open'); if (next.length > 0) setDetailsDraft(p => ({ ...p, statuses: next })); }} />
                      {s}
                    </label>
                  );
                })}
              </div>
            </div>
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
              <div className="info-label" style={{ marginBottom: 8 }}>Legal representative</div>
              <div className="form-grid form-grid-3">
                <div className="form-group" style={{ marginBottom: 0 }}><div className="info-label">Name</div><LegalRepNameInput value={detailsDraft.legalRep} onChange={val => setDetailsDraft(p => ({ ...p, legalRep: val }))} onPick={r => setDetailsDraft(p => ({ ...p, legalRep: r.name, legalPhone: r.phone, legalEmail: r.email }))} /></div>
                <div className="form-group" style={{ marginBottom: 0 }}><div className="info-label">Phone</div><input className="form-input" value={detailsDraft.legalPhone} onChange={e => setDetailsDraft(p => ({ ...p, legalPhone: e.target.value }))} placeholder="Phone" /></div>
                <div className="form-group" style={{ marginBottom: 0 }}><div className="info-label">Email</div><input className="form-input" value={detailsDraft.legalEmail} onChange={e => setDetailsDraft(p => ({ ...p, legalEmail: e.target.value }))} placeholder="Email" /></div>
              </div>
            </div>
          </div>
        </div>
      )}



    </div>
  );
}

function Violations({ violations, setViolations, properties, tenants, appSettings, setAppSettings, initialId, clearNav, typeFilter = 'all', setTypeFilter }) {
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [propF, _setPropF] = useState(SHARED_PROP_FILTER);
  const setPropF = v => { SHARED_PROP_FILTER = v; _setPropF(v); };
  const [statusFs, setStatusFs] = useState(new Set());
  const [statusDropOpen, setStatusDropOpen] = useState(false);
  const statusDropRef = useRef(null);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [selectedId, setSelectedId] = useState(initialId || null);
  useEffect(() => { if (initialId) { setSelectedId(initialId); if (clearNav) clearNav(); } }, [initialId]); // eslint-disable-line
  useEffect(() => {
    const HPD_ALLOWED = new Set(['New', 'Open', 'Completed']);
    const needsFix = violations.filter(v => v.type === 'HPD' && (
      v.issue !== v.caseNum ||
      (v.autoSynced && v.notes && !v.violationDesc) ||
      (v.statuses || []).some(s => !HPD_ALLOWED.has(s)) ||
      (v.statuses || []).length > 1
    ));
    if (needsFix.length > 0) setViolations(prev => prev.map(v => {
      if (v.type !== 'HPD') return v;
      const next = { ...v };
      if (v.caseNum && v.issue !== v.caseNum) next.issue = v.caseNum;
      if (v.autoSynced && v.notes && !v.violationDesc) { next.violationDesc = v.notes; next.notes = ''; }
      if (v.autoSynced && v.violationDesc && v.violationDesc.startsWith('Order #')) {
        next.violationDesc = v.violationDesc.replace(/^Order #\d+\s*·?\s*/, '');
      }
      const curStatuses = v.statuses || (v.status ? [v.status] : ['Open']);
      const hasCompleted = curStatuses.includes('Completed');
      next.statuses = hasCompleted ? ['Completed'] : curStatuses.includes('New') ? ['New'] : ['Open'];
      return next;
    }));
  }, []); // eslint-disable-line

  // Migrate all ECB violations → DOB
  useEffect(() => {
    const ecbViolations = violations.filter(v => v.type === 'ECB');
    if (ecbViolations.length > 0) {
      setViolations(prev => prev.map(v => v.type === 'ECB' ? { ...v, type: 'DOB', source: v.source === 'ECB' ? 'DOB' : v.source } : v));
    }
  }, []); // eslint-disable-line

  // Migrate raw HPD class letters (A/B/C) → canonical long labels
  useEffect(() => {
    const needsMigration = violations.some(v => v.violationClass && hpdClassLabel(v.violationClass) !== v.violationClass);
    if (needsMigration) {
      setViolations(prev => prev.map(v => v.violationClass && hpdClassLabel(v.violationClass) !== v.violationClass ? { ...v, violationClass: hpdClassLabel(v.violationClass) } : v));
    }
  }, []); // eslint-disable-line

  const [editingFollowUp, setEditingFollowUp] = useState(null);
  const [editingStatus, setEditingStatus] = useState(null);
  const [showResolved, setShowResolved] = useState(false);
  const [exportModal, setExportModal] = useState(false);
  const [exportProps, setExportProps] = useState(new Set());
  const [exportStatuses, setExportStatuses] = useState(new Set());
  const [activePage, setActivePage] = useState(1);
  const [resolvedPage, setResolvedPage] = useState(1);
  const [expandedId, setExpandedId] = useState(null);
  const [notesOpenId, setNotesOpenId] = useState(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [syncing, setSyncing] = useState(false);
  const syncingRef = useRef(false);
  const [selVIds, setSelVIds] = useState(new Set());
  const [bulkVStatus, setBulkVStatus] = useState('');
  const [bulkVDate, setBulkVDate] = useState('');
  const [lookupNum, setLookupNum] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);

  const lastSyncRecord = appSettings.find(s => s.id === 'violationSync');

  const lookupViolation = async () => {
    const num = lookupNum.trim();
    if (!num) return;
    setLookupLoading(true);
    try {
      const parseDate = s => {
        if (!s) return '';
        return s.slice(0, 10);
      };

      // Maps a raw violation type string or description to one of our dropdown values
      const mapViolationType = (text, infractionCode = '') => {
        const s = (text || '').toLowerCase();
        const ic = (infractionCode || '').toUpperCase().trim();
        if (ic === 'ZR' || s.includes('zoning') || s.includes('zoning resolution')) return 'Zoning';
        if (s.includes('site safety') || s.includes('fatality') || s.includes('injury') || s.includes('scaffold') || s.includes('fence') || ic === 'B3C' || ic === 'B4C') return 'Site Safety';
        if (s.includes('electric') || ic.startsWith('E')) return 'Electrical';
        if (s.includes('plumb')) return 'Plumbing';
        if (s.includes('boiler') || ic.startsWith('BL')) return 'Boiler';
        if (s.includes('elevator') || s.includes('hoist') || ic.startsWith('EL')) return 'Elevator';
        if (s.includes('mechanic') || s.includes('hvac') || s.includes('refrigerat')) return 'Mechanical';
        if (s.includes('structural') || s.includes('facade') || s.includes('retaining wall') || s.includes('bulkhead')) return 'Structural';
        if (s.includes('energy') || s.includes('ll97') || s.includes('local law 97') || s.includes('ll87')) return 'Energy';
        if (s.includes('fire protection') || s.includes('sprinkler') || s.includes('standpipe')) return 'Fire Protection';
        if (s.includes('sign')) return 'Sign';
        if (s.includes('construct') || s.includes('permit') || s.includes('build') || s.includes('work without')) return 'Construction';
        return '';
      };

      // Maps text to the FDNY modal's own dropdown values
      const mapFDNYType = text => {
        const s = (text || '').toLowerCase();
        if (s.includes('fire escape')) return 'Fire Escape';
        if (s.includes('sprinkler') || s.includes('standpipe')) return 'Sprinkler System';
        if (s.includes('alarm')) return 'Fire Alarm';
        if (s.includes('exit') || s.includes('egress')) return 'Exit / Egress';
        if (s.includes('detector')) return 'Smoke Detector';
        if (s.includes('storage') || s.includes('combustible') || s.includes('flammable')) return 'Storage / Combustibles';
        if (s.includes('cooking') || s.includes('suppression') || s.includes('hood') || s.includes('duct')) return 'Cooking Suppression';
        return 'Other';
      };

      // Maps text to the DSNY modal's own dropdown values
      const mapDSNYType = text => {
        const s = (text || '').toLowerCase();
        if (s.includes('snow') || /\bice\b/.test(s)) return 'Snow / Ice Removal';
        if (s.includes('recycl')) return 'Recycling Violation';
        if (s.includes('organic') || s.includes('compost') || s.includes('food waste')) return 'Organic Waste';
        if (s.includes('dump')) return 'Illegal Dumping';
        if (s.includes('sidewalk') || s.includes('sweep') || s.includes('litter') || s.includes('clean')) return 'Dirty Sidewalk';
        if (s.includes('refuse') || s.includes('receptacle') || s.includes('garbage') || s.includes('trash') || s.includes('set out') || s.includes('setout') || s.includes('collection')) return 'Improper Refuse Disposal';
        return 'Other';
      };

      // Maps text to the DEP modal's own dropdown values
      const mapDEPType = text => {
        const s = (text || '').toLowerCase();
        if (s.includes('noise')) return 'Noise';
        if (s.includes('hazard')) return 'Hazardous Material';
        if (s.includes('asbestos') || s.includes('air') || s.includes('emission') || s.includes('idling')) return 'Air Quality';
        if (s.includes('industrial') || s.includes('discharge')) return 'Industrial Waste';
        if (s.includes('water') || s.includes('sewer') || s.includes('hydrant') || s.includes('backflow') || s.includes('plumb') || s.includes('catch basin')) return 'Water / Sewer';
        return 'Other';
      };

      const buildDesc = (infCode, vType, respondent, rawDesc) => [
        infCode    ? `Infraction Codes: ${infCode}`   : '',
        vType      ? `Violation Type: ${vType}`       : '',
        respondent ? `Respondent Name: ${respondent}` : '',
        rawDesc    ? `Description:\n${rawDesc}`        : '',
      ].filter(Boolean).join('\n');

      // Try OATH Hearings dataset first
      const oathRes = await fetch(`https://data.cityofnewyork.us/resource/jz4z-kudi.json?ticket_number=${encodeURIComponent(num)}`);
      const oathRows = oathRes.ok ? await oathRes.json().catch(() => []) : [];
      if (oathRows.length > 0) {
        const r = oathRows[0];
        const agency = (r.issuing_agency || '').toUpperCase();
        const type = agency.includes('SANIT') ? 'DSNY' : agency.includes('FIRE') ? 'FDNY' : agency.includes('ENVIRONMENTAL PROTECTION') ? 'DEP' : 'DOB';
        const infCode = r.charge_1_code_section || '';
        const details = r.violation_details || r.violation_description || '';
        const rawDesc = [r.charge_1_code_description, details].filter(Boolean).join('\n');
        const mapText = [r.charge_1_code_description, details].filter(Boolean).join(' ');
        const vType = type === 'FDNY' ? mapFDNYType(mapText) : type === 'DSNY' ? mapDSNYType(mapText) : type === 'DEP' ? mapDEPType(mapText) : (mapViolationType(mapText, infCode) || 'Other');
        const respondent = r.respondent_name ||
          [r.respondent_last_name, r.respondent_first_name].filter(Boolean).join(', ') || '';
        setForm(p => ({
          ...p,
          caseNum: r.ticket_number || num,
          type,
          issuedDate: parseDate(r.violation_date),
          followUp: parseDate(r.hearing_date) || p.followUp,
          penaltyAmount: r.penalty_imposed || '',
          amountPaid: r.paid_amount || '',
          infractionCode: infCode,
          violationTypeDetail: vType,
          violationDesc: buildDesc(infCode, vType, respondent, rawDesc),
          issue: r.charge_1_code_description || '',
        }));
        toast(`Found: ${r.issuing_agency || 'OATH'} violation`, 'success');
        setLookupLoading(false);
        return;
      }

      // Fallback: try ECB Open Data dataset
      const ecbRes = await fetch(`https://data.cityofnewyork.us/resource/6bgk-3dad.json?ecb_violation_number=${encodeURIComponent(num)}`);
      const ecbRows = ecbRes.ok ? await ecbRes.json().catch(() => []) : [];
      if (ecbRows.length > 0) {
        const r = ecbRows[0];
        const infCode = r.infraction_code1 || '';
        const rawDesc = r.violation_description || r.section_law_description1?.trim() || '';
        const vType = mapViolationType([r.violation_type, rawDesc].filter(Boolean).join(' '), infCode) || 'Other';
        const respondent = r.respondent_name || '';
        setForm(p => ({
          ...p,
          caseNum: r.ecb_violation_number || num,
          type: 'DOB',
          issuedDate: parseDate(r.issue_date),
          followUp: parseDate(r.hearing_date) || p.followUp,
          penaltyAmount: r.penality_imposed || '',
          amountPaid: r.amount_paid || '',
          infractionCode: infCode,
          violationTypeDetail: vType,
          violationDesc: buildDesc(infCode, vType, respondent, rawDesc),
          issue: infCode || r.violation_type || '',
        }));
        toast('Found in ECB dataset', 'success');
        setLookupLoading(false);
        return;
      }

      // Fallback: try HPD violations dataset (numeric violation IDs)
      const hpdRes = await fetch(`https://data.cityofnewyork.us/resource/wvxf-dwi5.json?violationid=${encodeURIComponent(num)}`);
      const hpdRows = hpdRes.ok ? await hpdRes.json().catch(() => []) : [];
      if (hpdRows.length > 0) {
        const r = hpdRows[0];
        setForm(p => ({
          ...p,
          caseNum: r.violationid || num,
          type: 'HPD',
          violationClass: hpdClassLabel(r['class']),
          unit: r.apartment || p.unit,
          issuedDate: parseDate(r.inspectiondate),
          violationDesc: r.novdescription || '',
        }));
        toast('Found in HPD dataset', 'success');
        setLookupLoading(false);
        return;
      }

      toast('Violation not found in NYC databases — fill in manually', 'info');
    } catch (e) {
      toast('Lookup failed: ' + e.message, 'error');
    }
    setLookupLoading(false);
  };

  const syncNow = async () => {
    if (syncingRef.current) return;
    syncingRef.current = true;
    setSyncing(true);
    try {
      // Normalize a case number for dedup: strip leading zeros, uppercase
      const normCase = s => (s || '').replace(/^0+/, '').toUpperCase().trim();

      // Deduplicate by externalId AND by normalized caseNum (catches manual entries)
      const existingExtIds = new Set(violations.map(v => v.externalId).filter(Boolean));
      const existingCaseNums = new Set(violations.map(v => normCase(v.caseNum)).filter(Boolean));

      // Parse NYC date strings which may be YYYY-MM-DD or YYYYMMDDTHHMMSS
      const parseNYCDate = s => {
        if (!s) return '';
        const raw = s.slice(0, 10).replace(/\//g, '-');
        // Handle YYYYMMDD (no dashes)
        if (/^\d{8}$/.test(raw)) return `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
        return raw;
      };

      const now = new Date().toISOString();
      const newEntries = [];
      let seq = 0;
      const makeId = () => `vio_sync_${Date.now()}_${seq++}`;

      // Respondent-to-property matcher used for both ECB and DOB violations
      const normStr = s => (s || '').toLowerCase().replace(/[^a-z0-9\s]/g, '').trim();
      // Generic business words that should not count as unique identifiers
      const GENERIC_WORDS = new Set(['group', 'corp', 'corporation', 'inc', 'incorporated', 'llc', 'ltd', 'company', 'the', 'and', 'road', 'street', 'avenue', 'lane', 'drive', 'place', 'court', 'blvd', 'boulevard', 'plaza', 'terrace', 'parkway', 'highway', 'building', 'realty', 'properties', 'management', 'associates', 'services', 'ventures', 'holdings', 'enterprise', 'enterprises', 'construction', 'builders', 'contractors', 'contracting', 'developers', 'development', 'developer']);
      const sigWords = s => new Set(normStr(s).split(/\s+/).filter(w => w.length > 3 && !GENERIC_WORDS.has(w)));
      const matchPropertyByRespondent = respondentName => {
        if (!respondentName) return null;
        const rNorm = normStr(respondentName);
        const rWords = [...sigWords(respondentName)];
        if (rWords.length === 0) return null;
        // Priority 1: explicit ecbRespondentNames keywords configured on the property
        for (const p of properties) {
          const keywords = (p.ecbRespondentNames || '').split(',').map(k => normStr(k)).filter(Boolean);
          if (keywords.some(kw => rNorm.includes(kw))) return p.id;
        }
        // Priority 2: exact word match against property's significant words (no substring matching)
        let best = null, bestScore = 0;
        for (const p of properties) {
          const pWords = sigWords(p.name);
          const score = rWords.filter(w => pWords.has(w)).length;
          if (score > bestScore) { bestScore = score; best = p.id; }
        }
        return bestScore >= 1 ? best : null;
      };

      const nycFetch = async url => {
        const res = await fetch(url);
        if (!res.ok) return [];
        return res.json().catch(() => []);
      };

      const isDup = (eid, caseNum) =>
        existingExtIds.has(eid) || existingCaseNums.has(normCase(caseNum));

      for (const prop of properties) {
        const addrList = [
          { bin: prop.bin, block: prop.block, lot: prop.lot },
          ...(prop.altAddresses || []),
        ];

        for (const addr of addrList) {
          const boro = String(addr.bin || '')[0];

          // ── HPD ── (Multi Family Housing only)
          if (addr.block && addr.lot && boro && prop.businessType === 'Multi Family Housing') {
            const rows = await nycFetch(
              `https://data.cityofnewyork.us/resource/wvxf-dwi5.json?block=${encodeURIComponent(addr.block)}&lot=${encodeURIComponent(addr.lot)}&boroid=${boro}&violationstatus=Open&$where=inspectiondate>='2025-01-01T00:00:00.000'&$limit=200`
            );
            for (const r of rows) {
              if (!r.violationid) continue;
              const eid = `HPD-${r.violationid}`;
              if (isDup(eid, r.violationid)) continue;
              existingExtIds.add(eid);
              existingCaseNums.add(normCase(r.violationid));
              newEntries.push({
                id: makeId(), externalId: eid, source: 'HPD', autoSynced: true, syncedAt: now,
                propertyId: prop.id, type: 'HPD', caseNum: r.violationid,
                issue: r.violationid || '',
                violationClass: hpdClassLabel(r['class']),
                issuedDate: parseNYCDate(r.inspectiondate),
                unit: r.apartment || '', statuses: ['New'],
                violationDesc: [r['class'], r.novdescription].filter(Boolean).join(' · '),
                notes: '',
                legalRep: '', legalPhone: '', legalEmail: '',
                penaltyAmount: '', legalRepCharge: '', amountPaid: '', followUp: now.slice(0, 10),
              });
            }
          }

          // ── DOB ──
          if (addr.block && addr.lot && boro) {
            const rows = await nycFetch(
              `https://data.cityofnewyork.us/resource/3h2n-5cm9.json?block=${encodeURIComponent(addr.block)}&lot=${encodeURIComponent(addr.lot)}&boro=${boro}&$where=issue_date>='2025-01-01T00:00:00.000'&$limit=200`
            );
            for (const r of rows) {
              if (!r.isn_dob_bis_viol) continue;
              const eid = `DOB-${r.isn_dob_bis_viol}`;
              if (isDup(eid, r.isn_dob_bis_viol)) continue;
              // If the record has a respondent name, it must match one of our properties
              const respondent = r.respondent_name || r.owner_name || '';
              const resolvedPropId = matchPropertyByRespondent(respondent) || prop.id;
              existingExtIds.add(eid);
              existingCaseNums.add(normCase(r.isn_dob_bis_viol));
              newEntries.push({
                id: makeId(), externalId: eid, source: 'DOB', autoSynced: true, syncedAt: now,
                propertyId: resolvedPropId, type: 'DOB', caseNum: r.isn_dob_bis_viol,
                respondentName: respondent,
                issue: r.violation_type_code || '',
                issuedDate: parseNYCDate(r.issue_date),
                unit: '', statuses: ['New'],
                violationDesc: [r.violation_type, r.description].filter(Boolean).join(' · '),
                notes: r.disposition_comments || r.disposition_description || '',
                legalRep: '', legalPhone: '', legalEmail: '',
                penaltyAmount: '', legalRepCharge: '375', amountPaid: '', followUp: now.slice(0, 10),
              });
            }
          }

          // ── ECB ──
          if (addr.bin) {
            const rows = await nycFetch(
              `https://data.cityofnewyork.us/resource/6bgk-3dad.json?bin=${encodeURIComponent(addr.bin)}&$where=issue_date>='2025-01-01T00:00:00.000'&$limit=200`
            );
            for (const r of rows) {
              if (!r.ecb_violation_number) continue;
              const eid = `ECB-${r.ecb_violation_number}`;
              if (isDup(eid, r.ecb_violation_number)) continue;
              existingExtIds.add(eid);
              existingCaseNums.add(normCase(r.ecb_violation_number));
              const resolvedPropId = matchPropertyByRespondent(r.respondent_name) || prop.id;
              newEntries.push({
                id: makeId(), externalId: eid, source: 'ECB', autoSynced: true, syncedAt: now,
                propertyId: resolvedPropId, type: 'DOB', caseNum: r.ecb_violation_number,
                issue: r.violation_type || '',
                dobViolId: r.isn_dob_bis_extract || '',
                respondentName: r.respondent_name || '',
                issuedDate: parseNYCDate(r.issue_date),
                unit: '', statuses: ['New'],
                infractionCode: r.infraction_code1 || '',
                violationDesc: r.violation_description || '',
                notes: '',
                legalRep: '', legalPhone: '', legalEmail: '',
                penaltyAmount: r.penality_imposed || '', legalRepCharge: '375',
                amountPaid: r.amount_paid || '',
                followUp: parseNYCDate(r.hearing_date) || now.slice(0, 10),
              });
            }
          }
        }
      }

      // Write new violations to Firestore in batches of 400
      for (let i = 0; i < newEntries.length; i += 400) {
        const batch = writeBatch(db);
        newEntries.slice(i, i + 400).forEach(entry => {
          batch.set(doc(collection(db, 'violations'), entry.id), entry);
        });
        await batch.commit();
      }

      // Update appSettings violationSync record
      const syncRecord = { id: 'violationSync', lastSync: now, added: newEntries.length };
      await setDoc(doc(db, 'appSettings', 'violationSync'), syncRecord);

      // Update local state
      setViolations(prev => [...prev, ...newEntries]);
      setAppSettings(prev => prev.some(s => s.id === 'violationSync')
        ? prev.map(s => s.id === 'violationSync' ? syncRecord : s)
        : [...prev, syncRecord]);

      toast(`Sync complete — ${newEntries.length} new violation${newEntries.length !== 1 ? 's' : ''} added`, 'success');
    } catch (e) {
      toast('Sync failed: ' + (e.message || 'unknown error'), 'error');
    } finally {
      syncingRef.current = false;
      setSyncing(false);
    }
  };
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  useEffect(() => {
    if (!statusDropOpen) return;
    const h = e => { if (statusDropRef.current && !statusDropRef.current.contains(e.target)) setStatusDropOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [statusDropOpen]);
  useEffect(() => { setActivePage(1); setResolvedPage(1); setExpandedId(null); setNotesOpenId(null); }, [search, propF, statusFs]);
  useEffect(() => { setStatusFs(new Set()); setBulkVStatus(''); }, [typeFilter]); // eslint-disable-line
  useEffect(() => {
    if (!editingStatus) return;
    const h = e => { if (!e.target.closest('[data-vstatus-popup]')) setEditingStatus(null); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [editingStatus]);
  const PAGE_SIZE = 20;
  const ff = k => v => setForm(p => ({ ...p, [k]: v }));
  const propName = id => properties.find(p => p.id === id)?.name || id;
  const getStatuses = v => v.statuses || (v.status ? [v.status] : ['Open']);
  const RESOLVED_STATUSES = new Set(['Resolved', 'Completed', 'Dismissed', 'Not Owned']);
  const isOD = v => v.followUp && v.followUp < nyTodayISO() && !getStatuses(v).some(s => RESOLVED_STATUSES.has(s));
  const isDT = v => v.followUp && v.followUp === nyTodayISO() && !getStatuses(v).some(s => RESOLVED_STATUSES.has(s));

  const KNOWN_V_TYPES = new Set(['HPD','DOB','FDNY','DSNY','DEP','Insurance Claim']);
  const allRows = useMemo(() =>
    violations
      .filter(v =>
        (propF === 'all' || v.propertyId === propF) &&
        (statusFs.size === 0 || getStatuses(v).some(s => statusFs.has(s))) &&
        (typeFilter === 'all' || (typeFilter === 'Other' ? !KNOWN_V_TYPES.has(v.type) : v.type === typeFilter)) &&
        (!search || (v.issue || '').toLowerCase().includes(search.toLowerCase()) || v.caseNum.toLowerCase().includes(search.toLowerCase()) || v.unit.includes(search))
      )
      .sort((a, b) => {
        const aNew = getStatuses(a).includes('New');
        const bNew = getStatuses(b).includes('New');
        if (aNew !== bNew) return aNew ? -1 : 1;
        if (aNew && bNew) return (b.syncedAt || '').localeCompare(a.syncedAt || '');
        if (!a.followUp && !b.followUp) return 0;
        if (!a.followUp) return 1;
        if (!b.followUp) return -1;
        return a.followUp.localeCompare(b.followUp);
      }),
    [violations, search, propF, statusFs, typeFilter] // eslint-disable-line
  );
  const rows = allRows.filter(v => !getStatuses(v).some(s => RESOLVED_STATUSES.has(s)));
  const resolvedRows = allRows.filter(v => getStatuses(v).some(s => RESOLVED_STATUSES.has(s)));
  const totalActivePages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const totalResolvedPages = Math.max(1, Math.ceil(resolvedRows.length / PAGE_SIZE));
  const activePageRows = rows.slice((activePage - 1) * PAGE_SIZE, activePage * PAGE_SIZE);
  const resolvedPageRows = resolvedRows.slice((resolvedPage - 1) * PAGE_SIZE, resolvedPage * PAGE_SIZE);

  const Pager = ({ page, total, onChange }) => {
    if (total <= 1) return null;
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, marginTop: 14, flexWrap: 'wrap' }}>
        <button className="btn" style={{ padding: '4px 10px', fontSize: 12 }} disabled={page === 1} onClick={() => onChange(page - 1)}>‹ Prev</button>
        {Array.from({ length: total }, (_, i) => i + 1).map(p => (
          <button key={p} onClick={() => onChange(p)} style={{ padding: '4px 10px', fontSize: 12, borderRadius: 6, border: '1px solid var(--border)', background: p === page ? 'var(--accent)' : 'var(--bg2)', color: p === page ? '#fff' : 'var(--text1)', fontWeight: p === page ? 600 : 400, cursor: 'pointer' }}>{p}</button>
        ))}
        <button className="btn" style={{ padding: '4px 10px', fontSize: 12 }} disabled={page === total} onClick={() => onChange(page + 1)}>Next ›</button>
      </div>
    );
  };

  const ALL_VIOLATION_STATUSES = ['New','Open','On-Going','Hearing Schedule','Pending Payment','Adjourned','Rescheduled','Defaulted','Completed','Dismissed','Resolved','Not Owned'];
  const HPD_STATUSES = ['New','Open','Completed'];
  const DOB_STATUSES = ['New','Open','Hearing Schedule','Pending Payment','Complete','Not Owned'];
  const DOB_FILTER_STATUSES = ['New','Open','Hearing Schedule','Pending Payment','Resolved','Not Owned'];
  const AGENCY_STATUSES = ['New','Open','Hearing Schedule','Pending Payment','Resolved','Not Owned']; // FDNY, DSNY, DEP
  const vStatusOptions = v => v.type === 'HPD' ? HPD_STATUSES : v.type === 'DOB' ? DOB_STATUSES : ['FDNY','DSNY','DEP'].includes(v.type) ? AGENCY_STATUSES : ALL_VIOLATION_STATUSES;

  const CollapseRow = ({ v, dimmed }) => {
    const expanded = expandedId === v.id;
    const overdue = isOD(v);
    const dueToday = isDT(v);
    const pen = parseFloat(v.penaltyAmount) || 0;
    const lrc = parseFloat(v.legalRepCharge) || 0;
    const pd = parseFloat(v.amountPaid) || 0;
    const bal = pen + lrc - pd;
    const fmt$ = n => n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
    return (
      <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden', opacity: dimmed ? 0.7 : 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '13px 14px', cursor: 'pointer' }} onClick={() => setExpandedId(expanded ? null : v.id)}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {v.caseNum}
              {v.autoSynced && <span style={{ fontSize: 10, fontWeight: 500, background: 'var(--green-bg)', color: 'var(--green-txt)', padding: '1px 5px', borderRadius: 4, marginLeft: 6, verticalAlign: 'middle' }}>Auto</span>}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2 }}>{propName(v.propertyId)}{v.unit ? ` · Unit ${v.unit}` : ''}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {getStatuses(v).map(s => <Badge key={s} label={s} />)}
            <span style={{ fontSize: 12, color: 'var(--text3)', display: 'inline-block', transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>▾</span>
          </div>
        </div>
        {expanded && (() => {
          const notesOpen = notesOpenId === v.id;
          const secLabel = { fontSize: 9, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 };
          return (
          <div style={{ borderTop: '1px solid var(--border)', padding: '12px 14px', background: 'var(--bg3)', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, flex: 1, minWidth: 0 }}>
                <div><div style={secLabel}>Type</div><div style={{ fontSize: 12, fontWeight: 600 }}>{v.type}</div></div>
                <div><div style={secLabel}>Issued</div><div style={{ fontSize: 12, fontWeight: 600 }}>{v.issuedDate ? fmtDate(v.issuedDate) : '—'}</div></div>
                <div><div style={secLabel}>Follow-up</div><div style={{ fontSize: 12, fontWeight: 600, color: overdue ? 'var(--red-txt)' : dueToday ? 'var(--amber-txt)' : 'var(--text1)' }}>{v.followUp ? fmtDate(v.followUp) : '—'}</div></div>
              </div>
              <div style={{ flexShrink: 0, marginTop: -4 }} onClick={e => e.stopPropagation()}>
                <KebabMenu items={[{ label: 'Edit', onClick: () => openEdit(v) }, { label: 'Delete', danger: true, onClick: () => remove(v.id) }]} />
              </div>
            </div>
            {v.legalRep && (
              <div><div style={secLabel}>Legal Rep</div><div style={{ fontSize: 12, fontWeight: 500 }}>{v.legalRep}</div></div>
            )}
            {(pen > 0 || lrc > 0) && (
              <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {pen > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}><span style={{ color: 'var(--text2)' }}>Penalty</span><span style={{ fontWeight: 500 }}>{fmt$(pen)}</span></div>}
                {lrc > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}><span style={{ color: 'var(--text2)' }}>Legal Charge</span><span style={{ fontWeight: 500 }}>{fmt$(lrc)}</span></div>}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}><span style={{ color: 'var(--text2)' }}>Paid</span><span style={{ fontWeight: 500, color: 'var(--green-txt)' }}>{fmt$(pd)}</span></div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}><span style={{ fontWeight: 600 }}>Balance</span><span style={{ fontWeight: 700, color: bal > 0 ? 'var(--red-txt)' : 'var(--green-txt)' }}>{fmt$(bal)}</span></div>
              </div>
            )}
            {v.notes && (
              <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10 }}>
                <div style={secLabel}>Notes</div>
                <div style={{ fontSize: 12, color: 'var(--text1)', lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word', ...(notesOpen ? {} : { display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden' }) }}><Linkify text={v.notes} /></div>
                {v.notes.length > 160 && (
                  <button className="btn-ghost" style={{ fontSize: 11, marginTop: 5, padding: 0, color: 'var(--accent)' }} onClick={() => setNotesOpenId(notesOpen ? null : v.id)}>
                    {notesOpen ? 'Show less ▴' : 'Show more ▾'}
                  </button>
                )}
              </div>
            )}
            <button className="btn btn-primary" style={{ fontSize: 12, padding: '9px 4px', marginTop: 2 }} onClick={() => setSelectedId(v.id)}>View detail</button>
          </div>
          );
        })()}
      </div>
    );
  };

  const toggleSelV = (id, e) => { e.stopPropagation(); setSelVIds(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; }); };
  const applyBulkV = async () => {
    const ids = [...selVIds];
    const patch = {};
    if (bulkVStatus) patch.statuses = [bulkVStatus];
    if (bulkVDate) patch.followUp = bulkVDate;
    setViolations(prev => prev.map(v => selVIds.has(v.id) ? { ...v, ...patch } : v));
    const count = selVIds.size;
    setSelVIds(new Set());
    setBulkVDate('');
    if (Object.keys(patch).length) {
      try {
        const batch = writeBatch(db);
        ids.forEach(id => batch.set(doc(db, 'violations', id), patch, { merge: true }));
        await batch.commit();
      } catch (e) {
        toast('Save failed: ' + (e.message || 'unknown error'), 'error');
        return;
      }
    }
    toast(`Updated ${count} violation${count > 1 ? 's' : ''}`);
  };
  const deleteBulkV = () => {
    if (!window.confirm(`Delete ${selVIds.size} violation${selVIds.size > 1 ? 's' : ''}? This cannot be undone.`)) return;
    setViolations(prev => prev.filter(v => !selVIds.has(v.id)));
    toast(`Deleted ${selVIds.size} violation${selVIds.size > 1 ? 's' : ''}`, 'info');
    setSelVIds(new Set());
  };
  useEffect(() => { setSelVIds(new Set()); }, [search, propF, statusFs]);

  const saveFollowUp = async (id, newDate) => {
    setViolations(prev => prev.map(v => v.id === id ? { ...v, followUp: newDate } : v));
    setEditingFollowUp(null);
    try {
      await setDoc(doc(db, 'violations', id), { followUp: newDate }, { merge: true });
    } catch (e) {
      toast('Save failed: ' + (e.message || 'unknown error'), 'error');
    }
  };
  const RESOLVED_STATUSES_SET = new Set(['Completed', 'Dismissed', 'Resolved']);
  const toggleViolationStatus = (id, s) => {
    let nextStatuses = null;
    setViolations(prev => prev.map(v => {
      if (v.id !== id) return v;
      const cur = getStatuses(v);
      if (v.type === 'HPD') {
        nextStatuses = [s];
      } else if (v.type === 'DOB') {
        nextStatuses = [s === 'Complete' ? 'Resolved' : s];
      } else if (['FDNY','DSNY','DEP'].includes(v.type)) {
        nextStatuses = [s];
      } else {
        let next = cur.includes(s) ? cur.filter(x => x !== s) : [...cur, s];
        if (!cur.includes(s) && RESOLVED_STATUSES_SET.has(s)) {
          next = next.filter(x => x !== 'Open');
        }
        nextStatuses = next.length > 0 ? next : cur;
      }
      return { ...v, statuses: nextStatuses };
    }));
    if (nextStatuses) {
      setDoc(doc(db, 'violations', id), { statuses: nextStatuses }, { merge: true })
        .catch(e => toast('Save failed: ' + (e.message || 'unknown error'), 'error'));
    }
  };

  const doExportViolations = () => {
    const filtered = violations.filter(v => {
      const propMatch = exportProps.size === 0 || exportProps.has(v.propertyId);
      const sts = getStatuses(v);
      const statusMatch = exportStatuses.size === 0 || sts.some(s => exportStatuses.has(s));
      return propMatch && statusMatch;
    });
    const rows = filtered.map(v => {
      const sts = getStatuses(v);
      const pen = parseFloat(v.penaltyAmount) || 0;
      const lrc = parseFloat(v.legalRepCharge) || 0;
      const pd = parseFloat(v.amountPaid) || 0;
      return {
        'Case #': v.caseNum || '',
        'Type': v.type || '',
        'Property': propName(v.propertyId),
        'Unit': v.unit || '',
        'Status': sts.join(', '),
        'Issued Date': v.issuedDate || '',
        'Follow-up Date': v.followUp || '',
        'Legal Rep': v.legalRep || '',
        'Legal Phone': v.legalPhone || '',
        'Legal Email': v.legalEmail || '',
        'Penalty ($)': pen || '',
        'Legal Rep Charge ($)': lrc || '',
        'Amount Paid ($)': pd || '',
        'Balance ($)': pen + lrc - pd || '',
        'Notes': v.notes || '',
      };
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Violations');
    XLSX.writeFile(wb, `Violations_${new Date().toISOString().slice(0, 10)}.xlsx`);
    setExportModal(false);
  };

  const openAdd = () => setModal('type-pick');
  const selectType = type => {
    const base = { propertyId: properties[0]?.id || '', unit: '', type, caseNum: '', issuedDate: '', followUp: nyTodayISO(), statuses: ['Open'], notes: '', legalRep: '', legalPhone: '', legalEmail: '', penaltyAmount: '', legalRepCharge: '', amountPaid: '', violationDesc: '' };
    const extras = {
      HPD:  { propertyId: '', violationClass: '' },
      DOB:  { propertyId: '', violationTypeDetail: '', infractionCode: '', respondentName: '', legalRepCharge: '375', statuses: ['New'] },
      FDNY: { propertyId: '', violationTypeDetail: '', infractionCode: '', statuses: ['New'] },
      DSNY: { propertyId: '', violationTypeDetail: '', infractionCode: '', statuses: ['New'] },
      DEP:  { propertyId: '', violationTypeDetail: '', infractionCode: '', statuses: ['New'] },
      'Insurance Claim': { issue: '' },
      Other:{ agency: '', issue: '' },
    };
    setLookupNum('');
    setForm({ ...base, ...(extras[type] || {}) });
    setModal('add');
  };
  const openEdit = v => { setForm({ ...v, statuses: v.statuses || (v.status ? [v.status] : ['Open']) }); setModal('edit'); };
  const save = () => {
    if (!form.propertyId) { toast('Please select a property before saving.', 'error'); return; }
    const entry = modal === 'add' ? { ...form, statuses: form.statuses?.length ? form.statuses : ['Open'], followUp: nyTodayISO() } : form;
    if (modal === 'edit') setViolations(prev => prev.map(v => v.id === form.id ? entry : v));
    else setViolations(prev => [...prev, { ...entry, id: `v${Date.now()}`, createdAt: new Date().toISOString() }]);
    setModal(null);
    toast(modal === 'edit' ? 'Violation updated' : 'Violation added');
  };
  const remove = id => { if (window.confirm('Delete violation?')) { setViolations(prev => prev.filter(v => v.id !== id)); toast('Violation deleted', 'info'); } };

  if (selectedId) return <ViolationDetail violationId={selectedId} violations={violations} setViolations={setViolations} properties={properties} onBack={() => setSelectedId(null)} onDelete={async id => { setViolations(prev => prev.filter(v => v.id !== id)); setSelectedId(null); try { await deleteDoc(doc(db, 'violations', id)); } catch (e) { console.error('Delete failed', e); } }} />;

  const desktopTableRows = (rowSet, dimmed) => rowSet.map(v => (
    <tr key={v.id} className="clickable" onClick={() => setSelectedId(v.id)} style={dimmed ? { opacity: 0.65 } : {}}>
      <td onClick={e => e.stopPropagation()} style={{ width: 36 }}>
        <input type="checkbox" checked={selVIds.has(v.id)} onChange={e => toggleSelV(v.id, e)} style={{ cursor: 'pointer', accentColor: 'var(--accent)' }} />
      </td>
      <td onClick={e => e.stopPropagation()}>
        {editingFollowUp === v.id ? (
          <input type="date" className="form-input" style={{ fontSize: 12, padding: '3px 7px', width: 130 }}
            defaultValue={v.followUp || ''} autoFocus
            onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') setEditingFollowUp(null); }}
            onBlur={e => { if (e.target.value) saveFollowUp(v.id, e.target.value); else setEditingFollowUp(null); }} />
        ) : (
          <span className="edit-hint" title="Click to edit"
            style={{ fontSize: 12, color: isOD(v) ? 'var(--red-txt)' : isDT(v) ? 'var(--amber-txt)' : 'var(--text2)', fontWeight: (isOD(v) || isDT(v)) ? 600 : 400, textDecoration: 'underline dotted', textUnderlineOffset: 3 }}
            onClick={() => setEditingFollowUp(v.id)}>
            {v.followUp ? fmtDate(v.followUp) : '—'}
            <span className="edit-pencil">{ic(I.pencil, 11)}</span>
          </span>
        )}
      </td>
      <td><div style={{ fontWeight: 500, fontSize: 13 }}>{v.caseNum}</div></td>
      <td>
        <span style={{ fontSize: 12, fontWeight: 500, background: 'var(--blue-bg)', color: 'var(--blue-txt)', padding: '2px 7px', borderRadius: 4 }}>{v.type}</span>
        {v.autoSynced && <span style={{ fontSize: 10, fontWeight: 500, background: 'var(--green-bg)', color: 'var(--green-txt)', padding: '1px 5px', borderRadius: 4, marginLeft: 5 }}>Auto</span>}
      </td>
      <td><div>{propName(v.propertyId)}</div>{v.type !== 'DOB' && v.unit && <div style={{ fontSize: 11, color: 'var(--text2)' }}>Unit {v.unit}</div>}</td>
      {typeFilter !== 'HPD' && <td style={{ fontSize: 12, color: 'var(--text2)' }}>{v.legalRep || '—'}</td>}
      <td onClick={e => e.stopPropagation()} style={{ position: 'relative' }}>
        <span data-vstatus-popup title="Click to change" style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }} onClick={e => { e.stopPropagation(); setEditingStatus(editingStatus === v.id ? null : v.id); }}>
          {getStatuses(v).map(s => <Badge key={s} label={s} />)}
          <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--text3)', flexShrink: 0 }}><path d="M6 9l6 6 6-6"/></svg>
        </span>
        {editingStatus === v.id && (
          <div data-vstatus-popup style={{ position: 'absolute', left: 0, top: '100%', zIndex: 200, marginTop: 4, background: 'var(--bg1)', border: '1px solid var(--border)', borderRadius: 8, boxShadow: '0 6px 20px rgba(0,0,0,0.22)', minWidth: 190, padding: '6px 0' }}>
            {vStatusOptions(v).map(s => {
              const cur = getStatuses(v);
              const stored = (v.type === 'DOB' && s === 'Complete') ? 'Resolved' : s;
              const checked = cur.includes(stored);
              return (
                <label key={s} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 14px', cursor: 'pointer', fontSize: 13, color: 'var(--text1)', background: checked ? 'var(--accent-bg)' : 'none' }}>
                  <input type="checkbox" checked={checked} onChange={() => toggleViolationStatus(v.id, s)} disabled={checked && cur.length === 1} style={{ cursor: 'pointer', accentColor: 'var(--accent)' }} />
                  {s}
                </label>
              );
            })}
          </div>
        )}
      </td>
      <td><div onClick={e => e.stopPropagation()}><KebabMenu items={[{ label: 'View detail', onClick: () => setSelectedId(v.id) }, { label: 'Edit', onClick: () => openEdit(v) }, { label: 'Delete', danger: true, onClick: () => remove(v.id) }]} /></div></td>
    </tr>
  ));

  return (
    <div>
      <div className="page-header">
        <div>
          <span className="page-title">Violations & legal</span>
          {lastSyncRecord?.lastSync && (
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3 }}>
              Last NYC sync: {new Date(lastSyncRecord.lastSync).toLocaleString()}
              {lastSyncRecord.added > 0 && ` · ${lastSyncRecord.added} added`}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {!isMobile && (
            <button className="btn" onClick={syncNow} disabled={syncing} style={{ opacity: syncing ? 0.6 : 1 }}>
              <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"><path d="M23 4v6h-6M1 20v-6h6"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg>
              {syncing ? 'Syncing…' : 'Sync NYC'}
            </button>
          )}
          <button className="btn btn-primary" onClick={openAdd}>{ic(I.plus, 14, '#fff')} Add violation</button>
        </div>
      </div>
      {/* ── Mobile type filter chips ── */}
      {isMobile && (() => {
        const NAV = [
          { value: 'all',   label: 'All',   dot: 'var(--accent)' },
          { value: 'HPD',   label: 'HPD',   dot: 'var(--blue-txt)' },
          { value: 'DOB',   label: 'DOB',   dot: 'var(--amber-txt)' },
          { value: 'FDNY',  label: 'FDNY',  dot: '#e11d48' },
          { value: 'DSNY',  label: 'DSNY',  dot: 'var(--green-txt)' },
          { value: 'DEP',   label: 'DEP',   dot: '#8b5cf6' },
          { value: 'Insurance Claim', label: 'Insurance', dot: '#0ea5e9' },
          { value: 'Other', label: 'Other', dot: 'var(--text3)' },
        ];
        const today = nyTodayISO();
        const dueViolations = violations.filter(v => {
          const sts = getStatuses(v);
          if (sts.some(s => RESOLVED_STATUSES.has(s))) return false;
          return v.followUp && v.followUp <= today;
        });
        const countFor = t => t === 'all' ? dueViolations.length : t === 'Other' ? dueViolations.filter(v => !KNOWN_V_TYPES.has(v.type)).length : dueViolations.filter(v => v.type === t).length;
        return (
          <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 8, marginBottom: 10, WebkitOverflowScrolling: 'touch' }}>
            {NAV.map(t => {
              const active = typeFilter === t.value;
              return (
                <button key={t.value} onClick={() => setTypeFilter && setTypeFilter(t.value)}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, padding: '7px 12px', borderRadius: 20, fontSize: 12, fontWeight: active ? 700 : 500, fontFamily: 'inherit', cursor: 'pointer',
                    background: active ? 'var(--accent)' : 'var(--bg2)', color: active ? '#fff' : 'var(--text1)', border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}` }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: active ? '#fff' : t.dot, flexShrink: 0 }} />
                  {t.label}
                  <span style={{ fontSize: 11, fontWeight: 600, opacity: 0.75 }}>{countFor(t.value)}</span>
                </button>
              );
            })}
          </div>
        );
      })()}

      <div className="filter-bar">
        <div style={{ flex: 1 }}><SB value={search} onChange={setSearch} placeholder="Search by issue, case #, or unit…" /></div>
        <select className="form-input" style={{ width: 'auto', minWidth: 180 }} value={propF} onChange={e => setPropF(e.target.value)}>
          <option value="all">All properties</option>
          {(typeFilter === 'HPD' ? properties.filter(p => p.businessType === 'Multi Family Housing') : properties).map(p => <option key={p.id} value={p.id}>{p.name}{p.address ? ` · ${p.address}` : ''}</option>)}
        </select>
      </div>

      {/* ── Bulk action bar ── */}
      {!isMobile && selVIds.size > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'var(--accent-bg)', border: '1px solid var(--accent)', borderRadius: 10, marginBottom: 14, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent)', flexShrink: 0 }}>{selVIds.size} selected</span>
          <button className="btn-ghost" style={{ fontSize: 12 }} onClick={() => setSelVIds(new Set())}>Clear</button>
          <div style={{ flex: 1 }} />
          <select className="form-input" style={{ width: 'auto', minWidth: 160 }} value={bulkVStatus} onChange={e => setBulkVStatus(e.target.value)}>
            <option value="">Keep status</option>
            {(typeFilter === 'HPD' ? HPD_STATUSES : typeFilter === 'DOB' ? DOB_FILTER_STATUSES : ['FDNY','DSNY','DEP'].includes(typeFilter) ? AGENCY_STATUSES : ALL_VIOLATION_STATUSES).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          {typeFilter === 'HPD'
            ? <input type="date" className="form-input" style={{ width: 'auto' }} value={bulkVDate} onChange={e => setBulkVDate(e.target.value)} title="Follow-up date (leave blank to keep existing)" />
            : <>
                <input type="date" className="form-input" style={{ width: 'auto' }} value={bulkVDate} onChange={e => setBulkVDate(e.target.value)} title="Leave blank to keep existing dates" />
              </>
          }
          <button className="btn btn-primary" onClick={applyBulkV}>Update selected</button>
          <button className="btn" style={{ color: 'var(--red-txt)', borderColor: 'var(--red-txt)' }} onClick={deleteBulkV}>Delete selected</button>
        </div>
      )}

      {/* ── Desktop table view ── */}
      {!isMobile && (
        <>
          <table className="list-table">
            <thead><tr>
              <th style={{ width: 36 }}>
                <input type="checkbox" style={{ cursor: 'pointer', accentColor: 'var(--accent)' }}
                  checked={activePageRows.length > 0 && activePageRows.every(v => selVIds.has(v.id))}
                  onChange={e => setSelVIds(p => { const n = new Set(p); if (e.target.checked) activePageRows.forEach(v => n.add(v.id)); else activePageRows.forEach(v => n.delete(v.id)); return n; })} />
              </th>
              <th>Follow-up</th><th>Case Number</th><th>Type</th><th>Property / unit</th>{typeFilter !== 'HPD' && <th>Legal Rep</th>}<th style={{ position: 'relative' }}><div ref={statusDropRef} style={{ position: 'relative', display: 'inline-block' }}><button onClick={() => setStatusDropOpen(p => !p)} style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600, color: statusFs.size > 0 ? 'var(--accent)' : 'inherit', fontFamily: 'inherit', padding: 0, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>Status <span style={{ fontSize: 9 }}>▾</span></button>{statusDropOpen && (<div style={{ position: 'absolute', left: 0, top: 'calc(100% + 6px)', background: 'var(--bg1)', border: '1px solid var(--border)', borderRadius: 8, zIndex: 300, minWidth: 190, boxShadow: '0 6px 20px rgba(0,0,0,0.22)', padding: '6px 0', fontWeight: 400, textTransform: 'none', letterSpacing: 'normal' }}>{(typeFilter === 'HPD' ? HPD_STATUSES : typeFilter === 'DOB' ? DOB_FILTER_STATUSES : ['FDNY','DSNY','DEP'].includes(typeFilter) ? AGENCY_STATUSES : ALL_VIOLATION_STATUSES).map(s => (<label key={s} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 14px', cursor: 'pointer', fontSize: 13, color: 'var(--text1)', background: statusFs.has(s) ? 'var(--accent-bg)' : 'none' }}><input type="checkbox" checked={statusFs.has(s)} onChange={() => setStatusFs(prev => { const n = new Set(prev); n.has(s) ? n.delete(s) : n.add(s); return n; })} style={{ cursor: 'pointer', accentColor: 'var(--accent)' }} />{s}</label>))}{statusFs.size > 0 && (<div style={{ borderTop: '1px solid var(--border)', padding: '6px 14px' }}><button className="btn-ghost" style={{ fontSize: 12, padding: '2px 0' }} onClick={() => setStatusFs(new Set())}>Clear filter</button></div>)}</div>)}</div></th><th style={{ width: 40 }}></th>
            </tr></thead>
            <tbody>
              {rows.length === 0
                ? <tr><td colSpan={typeFilter === 'HPD' ? 7 : 8}><div className="empty">No violations found</div></td></tr>
                : desktopTableRows(activePageRows, false)}
            </tbody>
          </table>
          {rows.length > 0 && <div style={{ fontSize: 12, color: 'var(--text2)', textAlign: 'center', marginTop: 8 }}>Showing {(activePage - 1) * PAGE_SIZE + 1}–{Math.min(activePage * PAGE_SIZE, rows.length)} of {rows.length}</div>}
          <Pager page={activePage} total={totalActivePages} onChange={p => { setActivePage(p); }} />

          {resolvedRows.length > 0 && (
            <div style={{ marginTop: 24 }}>
              <button className="btn-ghost" style={{ fontSize: 13, fontWeight: 500, marginBottom: 10 }} onClick={() => setShowResolved(p => !p)}>
                {showResolved ? '▾' : '▸'} Resolved ({resolvedRows.length})
              </button>
              {showResolved && (
                <>
                  <table className="list-table">
                    <thead><tr>
                      <th style={{ width: 36 }}>
                        <input type="checkbox" style={{ cursor: 'pointer', accentColor: 'var(--accent)' }}
                          checked={resolvedPageRows.length > 0 && resolvedPageRows.every(v => selVIds.has(v.id))}
                          onChange={e => setSelVIds(p => { const n = new Set(p); if (e.target.checked) resolvedPageRows.forEach(v => n.add(v.id)); else resolvedPageRows.forEach(v => n.delete(v.id)); return n; })} />
                      </th>
                      <th>Follow-up</th><th>Case Number</th><th>Type</th><th>Property / unit</th>{typeFilter !== 'HPD' && <th>Legal Rep</th>}<th style={{ position: 'relative' }}><div ref={statusDropRef} style={{ position: 'relative', display: 'inline-block' }}><button onClick={() => setStatusDropOpen(p => !p)} style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600, color: statusFs.size > 0 ? 'var(--accent)' : 'inherit', fontFamily: 'inherit', padding: 0, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>Status <span style={{ fontSize: 9 }}>▾</span></button>{statusDropOpen && (<div style={{ position: 'absolute', left: 0, top: 'calc(100% + 6px)', background: 'var(--bg1)', border: '1px solid var(--border)', borderRadius: 8, zIndex: 300, minWidth: 190, boxShadow: '0 6px 20px rgba(0,0,0,0.22)', padding: '6px 0', fontWeight: 400, textTransform: 'none', letterSpacing: 'normal' }}>{(typeFilter === 'HPD' ? HPD_STATUSES : typeFilter === 'DOB' ? DOB_FILTER_STATUSES : ['FDNY','DSNY','DEP'].includes(typeFilter) ? AGENCY_STATUSES : ALL_VIOLATION_STATUSES).map(s => (<label key={s} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 14px', cursor: 'pointer', fontSize: 13, color: 'var(--text1)', background: statusFs.has(s) ? 'var(--accent-bg)' : 'none' }}><input type="checkbox" checked={statusFs.has(s)} onChange={() => setStatusFs(prev => { const n = new Set(prev); n.has(s) ? n.delete(s) : n.add(s); return n; })} style={{ cursor: 'pointer', accentColor: 'var(--accent)' }} />{s}</label>))}{statusFs.size > 0 && (<div style={{ borderTop: '1px solid var(--border)', padding: '6px 14px' }}><button className="btn-ghost" style={{ fontSize: 12, padding: '2px 0' }} onClick={() => setStatusFs(new Set())}>Clear filter</button></div>)}</div>)}</div></th><th style={{ width: 40 }}></th>
                    </tr></thead>
                    <tbody>{desktopTableRows(resolvedPageRows, true)}</tbody>
                  </table>
                  {resolvedRows.length > 0 && <div style={{ fontSize: 12, color: 'var(--text2)', textAlign: 'center', marginTop: 8 }}>Showing {(resolvedPage - 1) * PAGE_SIZE + 1}–{Math.min(resolvedPage * PAGE_SIZE, resolvedRows.length)} of {resolvedRows.length}</div>}
                  <Pager page={resolvedPage} total={totalResolvedPages} onChange={p => { setResolvedPage(p); }} />
                </>
              )}
            </div>
          )}
        </>
      )}

      {/* ── Mobile collapse view ── */}
      {isMobile && (
        <>
          {rows.length === 0
            ? <div className="empty">No violations found</div>
            : <>
                <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 10 }}>Active · {rows.length} violation{rows.length !== 1 ? 's' : ''} · Page {activePage} of {totalActivePages}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {activePageRows.map(v => <CollapseRow key={v.id} v={v} dimmed={false} />)}
                </div>
                <Pager page={activePage} total={totalActivePages} onChange={p => { setActivePage(p); setExpandedId(null); window.scrollTo(0, 0); }} />
              </>
          }

          {resolvedRows.length > 0 && (
            <div style={{ marginTop: 24 }}>
              <button className="btn-ghost" style={{ fontSize: 13, fontWeight: 500, marginBottom: 10 }} onClick={() => setShowResolved(p => !p)}>
                {showResolved ? '▾' : '▸'} Resolved ({resolvedRows.length})
              </button>
              {showResolved && (
                <>
                  <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 10 }}>Page {resolvedPage} of {totalResolvedPages}</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {resolvedPageRows.map(v => <CollapseRow key={v.id} v={v} dimmed={true} />)}
                  </div>
                  <Pager page={resolvedPage} total={totalResolvedPages} onChange={p => { setResolvedPage(p); setExpandedId(null); window.scrollTo(0, 0); }} />
                </>
              )}
            </div>
          )}
        </>
      )}

      {exportModal && (
        <Modal title="Export Violations to Excel" onClose={() => setExportModal(false)} hideX>
          <div className="form-group">
            <div className="form-label">Properties (leave all unchecked to export all)</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 160, overflowY: 'auto', padding: '6px 0' }}>
              {properties.map(p => (
                <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', padding: '3px 0' }}>
                  <input type="checkbox" checked={exportProps.has(p.id)} onChange={() => setExportProps(prev => { const n = new Set(prev); n.has(p.id) ? n.delete(p.id) : n.add(p.id); return n; })} style={{ cursor: 'pointer', accentColor: 'var(--accent)' }} />
                  {p.name}
                </label>
              ))}
            </div>
            {exportProps.size > 0 && <button className="btn-ghost" style={{ fontSize: 12, marginTop: 4 }} onClick={() => setExportProps(new Set())}>Clear selection</button>}
          </div>
          <div className="form-group">
            <div className="form-label">Statuses (leave all unchecked to export all)</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', padding: '6px 0' }}>
              {ALL_VIOLATION_STATUSES.map(s => (
                <label key={s} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                  <input type="checkbox" checked={exportStatuses.has(s)} onChange={() => setExportStatuses(prev => { const n = new Set(prev); n.has(s) ? n.delete(s) : n.add(s); return n; })} style={{ cursor: 'pointer', accentColor: 'var(--accent)' }} />
                  {s}
                </label>
              ))}
            </div>
            {exportStatuses.size > 0 && <button className="btn-ghost" style={{ fontSize: 12, marginTop: 4 }} onClick={() => setExportStatuses(new Set())}>Clear selection</button>}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 4 }}>
            {(() => {
              const count = violations.filter(v => {
                const propMatch = exportProps.size === 0 || exportProps.has(v.propertyId);
                const statusMatch = exportStatuses.size === 0 || getStatuses(v).some(s => exportStatuses.has(s));
                return propMatch && statusMatch;
              }).length;
              return `${count} violation${count !== 1 ? 's' : ''} will be exported`;
            })()}
          </div>
          <div className="modal-actions">
            <button className="btn" onClick={() => setExportModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={doExportViolations}>Download Excel</button>
          </div>
        </Modal>
      )}

      {modal === 'type-pick' && (
        <Modal title="Add Violation" onClose={() => setModal(null)} hideX>
          <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>Select the type of violation:</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {[
              { type: 'HPD',   name: 'Housing Preservation & Development', desc: 'Tenant complaints, habitability, building conditions', color: 'var(--blue-txt)',  bg: 'var(--blue-bg)' },
              { type: 'DOB',   name: 'Department of Buildings',             desc: 'Construction, safety, permits, structural violations', color: '#a78bfa',          bg: 'rgba(167,139,250,0.12)' },
              { type: 'FDNY',  name: 'Fire Department of NY',               desc: 'Fire safety, sprinklers, exits, smoke detectors',      color: 'var(--red-txt)',   bg: 'var(--red-bg)' },
              { type: 'DSNY',  name: 'Dept. of Sanitation',                 desc: 'Garbage disposal, recycling, sidewalk cleanliness',    color: 'var(--green-txt)', bg: 'var(--green-bg)' },
              { type: 'DEP',   name: 'Dept. of Environmental Protection',   desc: 'Water, sewer, noise, air quality violations',          color: 'var(--amber-txt)', bg: 'var(--amber-bg)' },
              { type: 'Insurance Claim', name: 'Insurance Claim',           desc: 'Insurance claims, legal disputes, litigation',         color: '#0ea5e9',          bg: 'rgba(14,165,233,0.12)' },
              { type: 'Other', name: 'Other Agency',                        desc: 'Court orders, other agency violations',                color: 'var(--text2)',     bg: 'var(--bg3)' },
            ].map(({ type, name, desc, color, bg }) => (
              <button key={type} onClick={() => selectType(type)}
                style={{ textAlign: 'left', background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, padding: '14px 16px', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', flexDirection: 'column', gap: 6, transition: 'border-color 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
                <span style={{ display: 'inline-block', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: bg, color }}>{type}</span>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text1)' }}>{name}</div>
                <div style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.4 }}>{desc}</div>
              </button>
            ))}
          </div>
          <div className="modal-footer" style={{ marginTop: 16 }}>
            <button className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
          </div>
        </Modal>
      )}

      {(modal === 'add' || modal === 'edit') && (() => {
        const t = form.type || 'HPD';
        const isEdit = modal === 'edit';
        const INS_CLAIM_STATUSES = ['New','Open','Adjourned','Defaulted','Close','Hearing Schedule'];
        const statusList = t === 'HPD' ? HPD_STATUSES : t === 'DOB' ? DOB_STATUSES : ['FDNY','DSNY','DEP'].includes(t) ? AGENCY_STATUSES : t === 'Insurance Claim' ? INS_CLAIM_STATUSES : ALL_VIOLATION_STATUSES;
        const StatusCheckboxes = ({ label = 'Status' }) => (
          <div className="form-group">
            <div className="form-label">{label}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', padding: '6px 0' }}>
              {statusList.map(s => {
                const stored = (t === 'DOB' && s === 'Complete') ? 'Resolved' : s;
                const checked = (form.statuses || []).includes(stored);
                return (
                  <label key={s} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, cursor: 'pointer' }}>
                    <input type="checkbox" checked={checked} onChange={() => {
                      if (['HPD','DOB','FDNY','DSNY','DEP','Insurance Claim'].includes(t)) { ff('statuses')([stored]); return; }
                      const cur = form.statuses || []; let next = checked ? cur.filter(x => x !== s) : [...cur, s]; if (!checked && ['Completed','Dismissed','Resolved'].includes(s)) next = next.filter(x => x !== 'Open'); if (next.length > 0) ff('statuses')(next);
                    }} style={{ cursor: 'pointer', accentColor: 'var(--accent)' }} />
                    {s}
                  </label>
                );
              })}
            </div>
          </div>
        );
        const LegalFields = () => (<>
          <div className="form-group">
            <label className="form-label">Legal Representative</label>
            <LegalRepNameInput value={form.legalRep} onChange={ff('legalRep')} onPick={r => setForm(p => ({ ...p, legalRep: r.name, legalPhone: r.phone, legalEmail: r.email }))} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <FI label="Phone" value={form.legalPhone} onChange={ff('legalPhone')} />
            <FI label="Email" value={form.legalEmail} onChange={ff('legalEmail')} />
          </div>
        </>);
        const MoneyFields = ({ showLegalCharge = true }) => (
          <div style={{ display: 'grid', gridTemplateColumns: showLegalCharge ? '1fr 1fr 1fr' : '1fr 1fr', gap: 12 }}>
            <FI label="Penalty Amount ($)" value={form.penaltyAmount} onChange={ff('penaltyAmount')} type="number" />
            {showLegalCharge && <FI label="Legal Rep Charge ($)" value={form.legalRepCharge} onChange={ff('legalRepCharge')} type="number" />}
            <FI label="Amount Paid ($)" value={form.amountPaid} onChange={ff('amountPaid')} type="number" />
          </div>
        );
        const LookupBar = () => (
          <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px', marginBottom: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Look up by ticket / violation #</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input className="form-input" style={{ flex: 1 }} placeholder="e.g. 049696524P" value={lookupNum} onChange={e => setLookupNum(e.target.value)} onKeyDown={e => e.key === 'Enter' && lookupViolation()} />
              <button className="btn" onClick={lookupViolation} disabled={lookupLoading || !lookupNum.trim()} style={{ opacity: lookupLoading ? 0.6 : 1, whiteSpace: 'nowrap' }}>{lookupLoading ? 'Looking up…' : 'Look up'}</button>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6 }}>Searches OATH Hearings, ECB, and HPD datasets. Fields below will be auto-filled if found.</div>
          </div>
        );
        const PropUnit = ({ hpdOnly = false }) => (
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
            <FS label="Property" value={form.propertyId} onChange={ff('propertyId')} options={(hpdOnly ? properties.filter(p => p.businessType === 'Multi Family Housing') : properties).map(p => ({ value: p.id, label: p.name }))} />
            <FI label="Unit" value={form.unit} onChange={ff('unit')} />
          </div>
        );

        return (
          <Modal title={isEdit ? `Edit ${t} Violation` : `Add ${t} Violation`} onClose={() => setModal(null)} hideX width={t === 'Insurance Claim' ? 680 : undefined}>
            {/* ── HPD ─────────────────────────────────────────── */}
            {t === 'HPD' && (<>
              {[
                ['Property', <select key="prop" className="form-input" style={{ marginBottom: 0 }} value={form.propertyId || ''} onChange={e => setForm(p => ({ ...p, propertyId: e.target.value, unit: '' }))}><option value="" disabled>— Select property —</option>{properties.filter(p => p.businessType === 'Multi Family Housing').map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select>],
                ['Unit', (() => {
                  const units = (properties.find(p => p.id === form.propertyId)?.unitsList || [])
                    .slice().sort((a, b) => (a.unitNumber || '').localeCompare(b.unitNumber || '', undefined, { numeric: true }))
                    .map(u => u.unitNumber).filter(Boolean);
                  return <SuggestInput key="unit" value={form.unit} onChange={ff('unit')} options={units} placeholder={form.propertyId ? 'Type or pick a unit' : 'Select a property first'} />;
                })()],
                ['Case / Violation Number', <input key="caseNum" className="form-input" style={{ marginBottom: 0 }} value={form.caseNum || ''} onChange={e => ff('caseNum')(e.target.value)} placeholder="e.g. 12345678" />],
                ['Violation Class', <select key="vclass" className="form-input" style={{ marginBottom: 0 }} value={form.violationClass || ''} onChange={e => ff('violationClass')(e.target.value)}><option value="" disabled>Select class</option>{['A — Immediately Hazardous','B — Hazardous','C — Non-Hazardous'].map(o => <option key={o}>{o}</option>)}</select>],
                ['Issued Date', <input key="issued" className="form-input" style={{ marginBottom: 0 }} type="date" value={form.issuedDate || ''} onChange={e => ff('issuedDate')(e.target.value)} />],
              ].map(([label, ctrl]) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                  <span style={{ width: 180, fontSize: 13, color: 'var(--text2)', fontWeight: 500, flexShrink: 0 }}>{label}:</span>
                  <div style={{ flex: 1 }}>{ctrl}</div>
                </div>
              ))}
              <div className="form-group" style={{ marginTop: 6 }}>
                <label className="form-label">Description / Apartment Condition:</label>
                <textarea className="form-textarea" value={form.violationDesc || ''} onChange={e => ff('violationDesc')(e.target.value)} />
              </div>
            </>)}

            {/* ── DOB ─────────────────────────────────────────── */}
            {t === 'DOB' && (<>
              {!isEdit && <LookupBar />}
              {[
                ['Property', <select key="prop" className="form-input" style={{ marginBottom: 0 }} value={form.propertyId || ''} onChange={e => ff('propertyId')(e.target.value)}><option value="" disabled>— Select property —</option>{properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select>],
                ['Case / Violation Number', <input key="caseNum" className="form-input" style={{ marginBottom: 0 }} value={form.caseNum || ''} onChange={e => ff('caseNum')(e.target.value)} placeholder="e.g. 34503318X" />],
                ['Violation Type', <select key="vtype" className="form-input" style={{ marginBottom: 0 }} value={form.violationTypeDetail || ''} onChange={e => ff('violationTypeDetail')(e.target.value)}><option value="" disabled>Select type</option>{['Site Safety','Construction','Zoning','Electrical','Plumbing','Boiler','Elevator','Mechanical','Structural','Energy','Fire Protection','Sign','Other'].map(o => <option key={o}>{o}</option>)}</select>],
                ['Infraction Code', <input key="infCode" className="form-input" style={{ marginBottom: 0 }} value={form.infractionCode || ''} onChange={e => ff('infractionCode')(e.target.value)} placeholder="e.g. B3C" />],
                ['Issued Date', <input key="issued" className="form-input" style={{ marginBottom: 0 }} type="date" value={form.issuedDate || ''} onChange={e => ff('issuedDate')(e.target.value)} />],
                ['Penalty Amount ($)', <input key="penalty" className="form-input" style={{ marginBottom: 0 }} value={form.penaltyAmount || ''} onChange={e => ff('penaltyAmount')(e.target.value.replace(/[^0-9.]/g, ''))} inputMode="numeric" placeholder="0" />],
              ].map(([label, ctrl]) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                  <span style={{ width: 180, fontSize: 13, color: 'var(--text2)', fontWeight: 500, flexShrink: 0 }}>{label}:</span>
                  <div style={{ flex: 1 }}>{ctrl}</div>
                </div>
              ))}
              <div className="form-group" style={{ marginTop: 6 }}>
                <label className="form-label">Description:</label>
                <textarea className="form-textarea" value={form.violationDesc || ''} onChange={e => ff('violationDesc')(e.target.value)} />
              </div>
            </>)}

            {/* ── FDNY ────────────────────────────────────────── */}
            {t === 'FDNY' && (<>
              {!isEdit && <LookupBar />}
              {[
                ['Property', <select key="prop" className="form-input" style={{ marginBottom: 0 }} value={form.propertyId || ''} onChange={e => ff('propertyId')(e.target.value)}><option value="" disabled>— Select property —</option>{properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select>],
                ['Case / Violation Number', <input key="caseNum" className="form-input" style={{ marginBottom: 0 }} value={form.caseNum || ''} onChange={e => ff('caseNum')(e.target.value)} placeholder="e.g. 049696524P" />],
                ['Violation Type', <select key="vtype" className="form-input" style={{ marginBottom: 0 }} value={form.violationTypeDetail || ''} onChange={e => ff('violationTypeDetail')(e.target.value)}><option value="" disabled>Select type</option>{['Fire Escape','Sprinkler System','Fire Alarm','Exit / Egress','Smoke Detector','Storage / Combustibles','Cooking Suppression','Other'].map(o => <option key={o}>{o}</option>)}</select>],
                ['Infraction Code', <input key="infCode" className="form-input" style={{ marginBottom: 0 }} value={form.infractionCode || ''} onChange={e => ff('infractionCode')(e.target.value)} placeholder="e.g. VC19" />],
                ['Issued Date', <input key="issued" className="form-input" style={{ marginBottom: 0 }} type="date" value={form.issuedDate || ''} onChange={e => ff('issuedDate')(e.target.value)} />],
                ['Penalty Amount ($)', <input key="penalty" className="form-input" style={{ marginBottom: 0 }} value={form.penaltyAmount || ''} onChange={e => ff('penaltyAmount')(e.target.value.replace(/[^0-9.]/g, ''))} inputMode="numeric" placeholder="0" />],
              ].map(([label, ctrl]) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                  <span style={{ width: 180, fontSize: 13, color: 'var(--text2)', fontWeight: 500, flexShrink: 0 }}>{label}:</span>
                  <div style={{ flex: 1 }}>{ctrl}</div>
                </div>
              ))}
              <div className="form-group" style={{ marginTop: 6 }}>
                <label className="form-label">Description:</label>
                <textarea className="form-textarea" value={form.violationDesc || ''} onChange={e => ff('violationDesc')(e.target.value)} />
              </div>
            </>)}

            {/* ── DSNY ────────────────────────────────────────── */}
            {t === 'DSNY' && (<>
              {!isEdit && <LookupBar />}
              {[
                ['Property', <select key="prop" className="form-input" style={{ marginBottom: 0 }} value={form.propertyId || ''} onChange={e => ff('propertyId')(e.target.value)}><option value="" disabled>— Select property —</option>{properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select>],
                ['Case / Violation Number', <input key="caseNum" className="form-input" style={{ marginBottom: 0 }} value={form.caseNum || ''} onChange={e => ff('caseNum')(e.target.value)} placeholder="e.g. 049696524P" />],
                ['Violation Type', <select key="vtype" className="form-input" style={{ marginBottom: 0 }} value={form.violationTypeDetail || ''} onChange={e => ff('violationTypeDetail')(e.target.value)}><option value="" disabled>Select type</option>{['Dirty Sidewalk','Improper Refuse Disposal','Recycling Violation','Snow / Ice Removal','Organic Waste','Illegal Dumping','Other'].map(o => <option key={o}>{o}</option>)}</select>],
                ['Infraction Code', <input key="infCode" className="form-input" style={{ marginBottom: 0 }} value={form.infractionCode || ''} onChange={e => ff('infractionCode')(e.target.value)} placeholder="e.g. AS16" />],
                ['Issued Date', <input key="issued" className="form-input" style={{ marginBottom: 0 }} type="date" value={form.issuedDate || ''} onChange={e => ff('issuedDate')(e.target.value)} />],
                ['Penalty Amount ($)', <input key="penalty" className="form-input" style={{ marginBottom: 0 }} value={form.penaltyAmount || ''} onChange={e => ff('penaltyAmount')(e.target.value.replace(/[^0-9.]/g, ''))} inputMode="numeric" placeholder="0" />],
              ].map(([label, ctrl]) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                  <span style={{ width: 180, fontSize: 13, color: 'var(--text2)', fontWeight: 500, flexShrink: 0 }}>{label}:</span>
                  <div style={{ flex: 1 }}>{ctrl}</div>
                </div>
              ))}
              <div className="form-group" style={{ marginTop: 6 }}>
                <label className="form-label">Description:</label>
                <textarea className="form-textarea" value={form.violationDesc || ''} onChange={e => ff('violationDesc')(e.target.value)} />
              </div>
            </>)}

            {/* ── DEP ─────────────────────────────────────────── */}
            {t === 'DEP' && (<>
              {!isEdit && <LookupBar />}
              {[
                ['Property', <select key="prop" className="form-input" style={{ marginBottom: 0 }} value={form.propertyId || ''} onChange={e => ff('propertyId')(e.target.value)}><option value="" disabled>— Select property —</option>{properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select>],
                ['Case / Violation Number', <input key="caseNum" className="form-input" style={{ marginBottom: 0 }} value={form.caseNum || ''} onChange={e => ff('caseNum')(e.target.value)} placeholder="e.g. 049696524P" />],
                ['Violation Type', <select key="vtype" className="form-input" style={{ marginBottom: 0 }} value={form.violationTypeDetail || ''} onChange={e => ff('violationTypeDetail')(e.target.value)}><option value="" disabled>Select type</option>{['Water / Sewer','Noise','Air Quality','Hazardous Material','Industrial Waste','Other'].map(o => <option key={o}>{o}</option>)}</select>],
                ['Infraction Code', <input key="infCode" className="form-input" style={{ marginBottom: 0 }} value={form.infractionCode || ''} onChange={e => ff('infractionCode')(e.target.value)} placeholder="e.g. 24-227" />],
                ['Issued Date', <input key="issued" className="form-input" style={{ marginBottom: 0 }} type="date" value={form.issuedDate || ''} onChange={e => ff('issuedDate')(e.target.value)} />],
                ['Penalty Amount ($)', <input key="penalty" className="form-input" style={{ marginBottom: 0 }} value={form.penaltyAmount || ''} onChange={e => ff('penaltyAmount')(e.target.value.replace(/[^0-9.]/g, ''))} inputMode="numeric" placeholder="0" />],
              ].map(([label, ctrl]) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                  <span style={{ width: 180, fontSize: 13, color: 'var(--text2)', fontWeight: 500, flexShrink: 0 }}>{label}:</span>
                  <div style={{ flex: 1 }}>{ctrl}</div>
                </div>
              ))}
              <div className="form-group" style={{ marginTop: 6 }}>
                <label className="form-label">Description:</label>
                <textarea className="form-textarea" value={form.violationDesc || ''} onChange={e => ff('violationDesc')(e.target.value)} />
              </div>
            </>)}

            {/* ── Insurance Claim ─────────────────────────────── */}
            {t === 'Insurance Claim' && (<>
              {[
                ['Property', <select key="prop" className="form-input" style={{ marginBottom: 0 }} value={form.propertyId || ''} onChange={e => ff('propertyId')(e.target.value)}><option value="" disabled>— Select property —</option>{properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select>],
                ['Case Number', <input key="caseNum" className="form-input" style={{ marginBottom: 0 }} value={form.caseNum || ''} onChange={e => ff('caseNum')(e.target.value)} placeholder="e.g. MNC92410405" />],
                ['Title / Case Name', <input key="issue" className="form-input" style={{ marginBottom: 0 }} value={form.issue || ''} onChange={e => ff('issue')(e.target.value)} placeholder="e.g. Toyer v. 1090 Franklin" />],
                ['Issued Date', <input key="issued" className="form-input" style={{ marginBottom: 0 }} type="date" value={form.issuedDate || ''} onChange={e => ff('issuedDate')(e.target.value)} />],
                ['Follow-up Date', <input key="followup" className="form-input" style={{ marginBottom: 0 }} type="date" value={form.followUp || ''} onChange={e => ff('followUp')(e.target.value)} />],
                ['Legal Representative', <LegalRepNameInput key="legalRep" value={form.legalRep} onChange={ff('legalRep')} onPick={r => setForm(p => ({ ...p, legalRep: r.name, legalPhone: r.phone, legalEmail: r.email }))} />],
                ['Phone', <input key="phone" className="form-input" style={{ marginBottom: 0 }} value={form.legalPhone || ''} onChange={e => ff('legalPhone')(e.target.value)} />],
                ['Email', <input key="email" className="form-input" style={{ marginBottom: 0 }} value={form.legalEmail || ''} onChange={e => ff('legalEmail')(e.target.value)} />],
                ['Status', <select key="status" className="form-input" style={{ marginBottom: 0 }} value={(form.statuses || [])[0] || 'Open'} onChange={e => ff('statuses')([e.target.value])}>{INS_CLAIM_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}</select>],
              ].map(([label, ctrl]) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                  <span style={{ width: 180, fontSize: 13, color: 'var(--text2)', fontWeight: 500, flexShrink: 0 }}>{label}:</span>
                  <div style={{ flex: 1 }}>{ctrl}</div>
                </div>
              ))}
              <div className="form-group" style={{ marginTop: 6 }}>
                <label className="form-label">Description:</label>
                <textarea className="form-textarea" value={form.violationDesc || ''} onChange={e => ff('violationDesc')(e.target.value)} />
              </div>
            </>)}

            {/* ── Other ───────────────────────────────────────── */}
            {t === 'Other' && (<>
              <PropUnit />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <FI label="Agency" value={form.agency || ''} onChange={ff('agency')} placeholder="e.g. Court, Insurance" />
                <FI label="Case Number" value={form.caseNum} onChange={ff('caseNum')} />
              </div>
              <FI label="Title / Issue" value={form.issue || ''} onChange={ff('issue')} />
              <FTA label="Description" value={form.violationDesc || ''} onChange={ff('violationDesc')} />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <FI label="Issued Date" value={form.issuedDate} onChange={ff('issuedDate')} type="date" />
                <FI label="Follow-up Date" value={form.followUp} onChange={ff('followUp')} type="date" />
              </div>
              <StatusCheckboxes />
              <LegalFields />
              <MoneyFields />
              <FTA label="Notes" value={form.notes} onChange={ff('notes')} />
            </>)}

            {/* ── Edit fallback for legacy types ──────────────── */}
            {isEdit && !['HPD','DOB','FDNY','DSNY','DEP','Other','Insurance Claim'].includes(t) && (<>
              <PropUnit />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <FS label="Type" value={form.type} onChange={ff('type')} options={['HPD','DOB','FDNY','DSNY','DEP','Insurance Claim','Other']} />
                <FI label="Case Number" value={form.caseNum} onChange={ff('caseNum')} />
              </div>
              <FI label="Title" value={form.issue || ''} onChange={ff('issue')} />
              <FI label="Issued Date" value={form.issuedDate} onChange={ff('issuedDate')} type="date" />
              <StatusCheckboxes />
              <LegalFields />
              <MoneyFields />
              <FTA label="Notes" value={form.notes} onChange={ff('notes')} />
            </>)}

            <div className="modal-actions">
              <button className="btn" onClick={() => setModal(null)}>Cancel</button>
              <SavingBtn onClick={save}>Save</SavingBtn>
            </div>
          </Modal>
        );
      })()}
    </div>
  );
}

const MAINTENANCE_STATUSES = ['open', 'in-progress', 'closed'];

function MaintenanceDetail({ maintenanceId, maintenance, setMaintenance, properties, tenants, onBack }) {
  const toast = useToast();
  const m = maintenance.find(x => x.id === maintenanceId);
  const propName = id => properties.find(p => p.id === id)?.name || id;
  const [noteDraft, setNoteDraft] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editNoteText, setEditNoteText] = useState('');
  const [improvingNote, setImprovingNote] = useState(false);
  const [noteSuggestion, setNoteSuggestion] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [statusPopup, setStatusPopup] = useState(false);
  const [closeModal, setCloseModal] = useState(null); // { date }
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const ff = k => v => setForm(p => ({ ...p, [k]: v }));
  const toggleUnit = val => setForm(p => { const cur = p.units || []; return { ...p, units: cur.includes(val) ? cur.filter(u => u !== val) : [...cur, val] }; });

  useEffect(() => {
    if (!statusPopup) return;
    const h = () => setStatusPopup(false);
    document.addEventListener('click', h);
    return () => document.removeEventListener('click', h);
  }, [statusPopup]);

  if (!m) return null;

  const updateM = fn => setMaintenance(prev => prev.map(x => x.id === maintenanceId ? fn(x) : x));

  const saveNote = () => {
    if (!noteDraft.trim()) return;
    updateM(x => ({ ...x, notes: [...(x.notes || []), { id: `mn${Date.now()}`, text: noteDraft.trim(), ts: new Date().toISOString() }] }));
    setNoteDraft(''); setAddingNote(false); setNoteSuggestion(null);
    toast('Note added');
  };

  const improveNoteDraft = async () => {
    if (!noteDraft.trim()) return;
    setImprovingNote(true); setNoteSuggestion(null);
    try {
      const fn = httpsCallable(functions, 'improveNote');
      const res = await fn({ text: noteDraft.trim() });
      setNoteSuggestion(res.data.improved);
    } catch { toast('Could not improve note. Try again.', 'error'); }
    setImprovingNote(false);
  };

  const deleteNote = id => { updateM(x => ({ ...x, notes: (x.notes || []).filter(n => n.id !== id) })); toast('Note removed', 'info'); };

  const handleDocUpload = async e => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (e.target.value !== undefined) e.target.value = '';
    setUploadingDoc(true);
    try {
      const newDocs = await Promise.all(files.map(async file => {
        const path = `maintenance/${maintenanceId}/documents/${Date.now()}_${file.name}`;
        const ref = sRef(storage, path);
        await uploadBytes(ref, file);
        const url = await getDownloadURL(ref);
        return { id: `mdoc${Date.now()}${Math.random().toString(36).slice(2)}`, name: file.name, url, storagePath: path, uploadedAt: new Date().toISOString(), size: file.size };
      }));
      updateM(x => ({ ...x, documents: [...(x.documents || []), ...newDocs] }));
    } catch { alert('Upload failed. Please try again.'); }
    setUploadingDoc(false);
  };

  const deleteDoc = async doc => {
    if (!window.confirm(`Delete "${doc.name}"?`)) return;
    try { if (doc.storagePath) await deleteObject(sRef(storage, doc.storagePath)); } catch {}
    updateM(x => ({ ...x, documents: (x.documents || []).filter(d => d.id !== doc.id) }));
  };

  const saveEdit = () => {
    const unit = (form.units || []).join(', ');
    updateM(() => ({ ...form, unit }));
    setModal(null);
    toast('Request updated');
  };

  const InfoItem = ({ label, value }) => (
    <div><div className="info-label">{label}</div><div className="info-val" style={{ whiteSpace: 'pre-wrap' }}><Linkify text={value || '—'} /></div></div>
  );

  return (
    <div>
      <button className="back-btn" onClick={onBack}>{ic(I.arrowLeft, 15)} All requests</button>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div style={{ flex: 1, minWidth: 0, marginRight: 12 }}>
          <h1 style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>{m.title}</h1>
          <div style={{ fontSize: 13, color: 'var(--text2)' }}>{propName(m.propertyId)}{m.unit ? ` · ${m.unit}` : ''}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <div style={{ position: 'relative' }}>
            <span onClick={e => { e.stopPropagation(); setStatusPopup(p => !p); }} style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }} title="Change status">
              <Badge label={m.status} />
              <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--text3)' }}><path d="M6 9l6 6 6-6"/></svg>
            </span>
            {statusPopup && (
              <div style={{ position: 'absolute', top: '100%', right: 0, zIndex: 50, marginTop: 4, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.3)', minWidth: 130, overflow: 'hidden' }}>
                {MAINTENANCE_STATUSES.map(s => (
                  <button key={s} onClick={e => {
                    e.stopPropagation();
                    if (s === 'closed') { setCloseModal({ date: new Date().toISOString().slice(0, 10) }); setStatusPopup(false); }
                    else { updateM(x => ({ ...x, status: s })); setStatusPopup(false); }
                  }} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 12px', background: m.status === s ? 'var(--bg3)' : 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--text1)', fontFamily: 'inherit' }}>
                    <Badge label={s} />
                  </button>
                ))}
              </div>
            )}
          </div>
          <button className="btn" style={{ fontSize: 12, padding: '4px 12px' }} onClick={() => { setForm({ ...m, units: m.units || (m.unit ? [m.unit] : []) }); setModal('edit'); }}>{ic(I.pencil, 12)} Edit</button>
        </div>
      </div>

      {/* Details card */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title">{ic(I.receipt, 14)} Request details</div>
        <div className="info-grid">
          <InfoItem label="Date" value={m.date} />
          <InfoItem label="Vendor" value={m.vendor} />
          <InfoItem label="Unit / Scope" value={m.unit} />
          {m.completedDate && <InfoItem label="Completed" value={m.completedDate} />}
          {m.cost ? (
            <div>
              <div className="info-label">Total Cost</div>
              <div className="info-val" style={{ color: 'var(--green-txt)', fontWeight: 600 }}>${parseFloat(m.cost).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </div>
          ) : null}
          {m.description && <div style={{ gridColumn: '1 / -1' }}><div className="info-label">Description</div><div className="info-val" style={{ whiteSpace: 'pre-wrap' }}><Linkify text={m.description} /></div></div>}
        </div>
      </div>
      {closeModal && (
        <Modal title="Mark as Closed" onClose={() => setCloseModal(null)}>
          <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>Enter the completion date and final cost for this request.</div>
          <div className="form-grid form-grid-2">
            <FI label="Completion date" value={closeModal.date} onChange={d => setCloseModal(p => ({ ...p, date: d }))} type="date" />
            <FI label="Total cost ($)" value={closeModal.cost ?? ''} onChange={v => setCloseModal(p => ({ ...p, cost: v }))} type="number" />
          </div>
          <div className="modal-actions">
            <button className="btn" onClick={() => setCloseModal(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={() => { updateM(x => ({ ...x, status: 'closed', completedDate: closeModal.date, cost: closeModal.cost })); setCloseModal(null); }}>Mark as Closed</button>
          </div>
        </Modal>
      )}

      {/* Notes card */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div className="card-title" style={{ margin: 0 }}>{ic(I.file, 14)} Notes</div>
          {!addingNote && <button className="btn-ghost" onClick={() => setAddingNote(true)}>{ic(I.plus, 12)} Add note</button>}
        </div>
        {addingNote && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ position: 'relative' }}>
              <textarea className="form-input" rows={3} style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit', fontSize: 13 }} value={noteDraft} onChange={e => { setNoteDraft(e.target.value); setNoteSuggestion(null); }} placeholder="Write a note…" autoFocus />
              {noteDraft.trim() && (
                <button onClick={improveNoteDraft} disabled={improvingNote}
                  style={{ position: 'absolute', bottom: 8, right: 8, fontSize: 11, padding: '3px 10px', background: 'var(--accent-bg)', border: '1px solid var(--accent)', color: 'var(--accent)', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 4 }}>
                  {improvingNote ? '…' : '✨'} {improvingNote ? 'Improving…' : 'Improve'}
                </button>
              )}
            </div>
            {noteSuggestion && (
              <div style={{ marginTop: 8, padding: '10px 12px', background: 'var(--accent-bg)', border: '1px solid var(--accent)', borderRadius: 8, fontSize: 13 }}>
                <div style={{ fontSize: 11, color: 'var(--accent)', fontWeight: 600, marginBottom: 6 }}>✨ Suggested improvement</div>
                <div style={{ color: 'var(--text1)', lineHeight: 1.6, marginBottom: 8 }}>{noteSuggestion}</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-primary" style={{ fontSize: 11, padding: '3px 10px' }} onClick={() => { setNoteDraft(noteSuggestion); setNoteSuggestion(null); }}>Use this</button>
                  <button className="btn" style={{ fontSize: 11, padding: '3px 10px' }} onClick={() => setNoteSuggestion(null)}>Dismiss</button>
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
              <SavingBtn onClick={saveNote} style={{ fontSize: 12, padding: '4px 12px' }}>Save</SavingBtn>
              <button className="btn" style={{ fontSize: 12, padding: '4px 12px' }} onClick={() => { setAddingNote(false); setNoteDraft(''); setNoteSuggestion(null); }}>Cancel</button>
            </div>
          </div>
        )}
        {(m.notes || []).length === 0 && !addingNote && <div style={{ fontSize: 13, color: 'var(--text3)' }}>No notes yet.</div>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {(m.notes || []).map(n => (
            <div key={n.id} style={{ background: 'var(--bg2)', borderRadius: 8, padding: '10px 14px' }}>
              {editingNoteId === n.id ? (
                <div>
                  <textarea className="form-input" rows={3} style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit', fontSize: 13 }} value={editNoteText} onChange={e => setEditNoteText(e.target.value)} autoFocus />
                  <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                    <SavingBtn onClick={() => { updateM(x => ({ ...x, notes: (x.notes || []).map(nn => nn.id === n.id ? { ...nn, text: editNoteText.trim() } : nn) })); setEditingNoteId(null); toast('Note updated'); }} style={{ fontSize: 12, padding: '4px 12px' }}>Save</SavingBtn>
                    <button className="btn" style={{ fontSize: 12, padding: '4px 12px' }} onClick={() => setEditingNoteId(null)}>Cancel</button>
                  </div>
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap', flex: 1 }}><Linkify text={n.text} /></div>
                    <KebabMenu items={[
                      { label: 'Edit', onClick: () => { setEditingNoteId(n.id); setEditNoteText(n.text); } },
                      { label: 'Delete', danger: true, onClick: () => deleteNote(n.id) },
                    ]} />
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>{new Date(n.ts).toLocaleString('en-US')}</div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Documents card */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div className="card-title" style={{ margin: 0 }}>{ic(I.file, 14)} Documents</div>
          <label style={{ cursor: uploadingDoc ? 'not-allowed' : 'pointer' }}>
            <input type="file" multiple style={{ display: 'none' }} onChange={handleDocUpload} disabled={uploadingDoc} />
            <span className="btn-ghost" style={{ pointerEvents: 'none' }}>{uploadingDoc ? 'Uploading…' : <>{ic(I.plus, 12)} Upload</>}</span>
          </label>
        </div>
        {(m.documents || []).length === 0 && <div style={{ fontSize: 13, color: 'var(--text3)' }}>No documents uploaded yet.</div>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {(m.documents || []).map(doc => (
            <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
              {ic(I.file, 14, 'var(--text2)')}
              <div style={{ flex: 1, minWidth: 0 }}>
                <a href={doc.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, fontWeight: 500, color: 'var(--accent)', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{doc.name}</a>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>{doc.uploadedAt && new Date(doc.uploadedAt).toLocaleDateString('en-US')}{doc.size != null ? ` · ${fmtSize(doc.size)}` : ''}</div>
              </div>
              <button onClick={() => deleteDoc(doc)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', fontSize: 18, lineHeight: 1, padding: '0 4px' }}>×</button>
            </div>
          ))}
        </div>
      </div>

      {/* Edit modal */}
      {modal === 'edit' && (
        <Modal title="Edit request" onClose={() => setModal(null)}>
          <FS label="Property" value={form.propertyId} onChange={v => setForm(p => ({ ...p, propertyId: v, units: [] }))} options={properties.map(p => ({ value: p.id, label: p.name }))} />
          <div className="form-group" style={{ marginBottom: 12 }}>
            <label className="form-label">Unit / Scope</label>
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '6px 10px', background: 'var(--bg1)', maxHeight: 180, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
              {[{ value: 'Building Wide', label: 'Building Wide' }, ...tenants.filter(t => t.propertyId === form.propertyId && t.status !== 'Previous').sort((a, b) => (a.unit || '').localeCompare(b.unit || '', undefined, { numeric: true })).map(t => ({ value: `${t.unit} - ${t.name}`, label: `Unit ${t.unit} - ${t.name}` }))].map(opt => (
                <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 4px', cursor: 'pointer', fontSize: 13, borderRadius: 6, background: (form.units || []).includes(opt.value) ? 'var(--bg3)' : 'transparent' }}>
                  <input type="checkbox" checked={(form.units || []).includes(opt.value)} onChange={() => toggleUnit(opt.value)} style={{ accentColor: 'var(--accent)', width: 14, height: 14, flexShrink: 0 }} />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>
          <FI label="Title" value={form.title} onChange={ff('title')} />
          <FI label="Description" value={form.description} onChange={ff('description')} />
          <div className="form-grid form-grid-2">
            <FS label="Status" value={form.status} onChange={ff('status')} options={[{ value: 'open', label: 'Open' }, { value: 'in-progress', label: 'In progress' }, { value: 'closed', label: 'Closed' }]} />
            <FI label="Date" value={form.date} onChange={ff('date')} type="date" />
          </div>
          <div className="form-grid form-grid-2">
            <FI label="Vendor" value={form.vendor} onChange={ff('vendor')} />
            <FI label="Total cost ($)" value={form.cost || ''} onChange={ff('cost')} type="number" />
          </div>
          <div className="modal-actions"><button className="btn" onClick={() => setModal(null)}>Cancel</button><SavingBtn onClick={saveEdit}>Save</SavingBtn></div>
        </Modal>
      )}
    </div>
  );
}

// eslint-disable-next-line no-unused-vars
function Ledger({ ledger, setLedger, tenants, properties }) {
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [tenantF, setTenantF] = useState('all');
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const ff = k => v => setForm(p => ({ ...p, [k]: v }));
  const active = tenants.filter(t => t.status === 'Occupied');
  const tName = id => tenants.find(t => t.id === id)?.name || id;
  const rows = useMemo(() => ledger.filter(l => (tenantF === 'all' || l.tenantId === tenantF) && (!search || l.description.toLowerCase().includes(search.toLowerCase()) || (tenants.find(t => t.id === l.tenantId)?.name || l.tenantId).toLowerCase().includes(search.toLowerCase()))).sort((a, b) => b.date.localeCompare(a.date)), [ledger, tenants, tenantF, search]);
  const total = rows.reduce((s, l) => s + l.amount, 0);
  const paid = rows.filter(l => l.status === 'Paid').reduce((s, l) => s + l.amount, 0);
  const outstanding = rows.filter(l => l.type === 'charge').reduce((s, l) => s + Math.max(0, (+l.amount || 0) - (+l.paidAmount || 0)), 0);
  const openAdd = () => { setForm({ tenantId: active[0]?.id || '', date: new Date().toISOString().slice(0, 10), description: '', amount: '', status: 'Charged' }); setModal('add'); };
  const openEdit = l => { setForm({ ...l }); setModal('edit'); };
  const save = () => { const entry = { ...form, amount: Number(form.amount) }; if (modal === 'edit') setLedger(prev => prev.map(l => l.id === form.id ? entry : l)); else setLedger(prev => [...prev, { ...entry, id: `l${Date.now()}` }]); setModal(null); toast(modal === 'edit' ? 'Entry updated' : 'Entry added'); };
  const remove = id => { if (window.confirm('Delete entry?')) { setLedger(prev => prev.filter(l => l.id !== id)); toast('Entry deleted', 'info'); } };
  return (
    <div>
      <div className="page-header"><span className="page-title">Payment ledger</span><button className="btn btn-primary" onClick={openAdd}>{ic(I.plus, 14, '#fff')} Add entry</button></div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 16 }}>
        {[['Total charged', '$' + total.toLocaleString(), 'var(--text1)'], ['Total paid', '$' + paid.toLocaleString(), 'var(--green-txt)'], ['Outstanding', '$' + outstanding.toLocaleString(), outstanding > 0 ? 'var(--red-txt)' : 'var(--green-txt)']].map(([l, v, c]) => (
          <div key={l} style={{ background: 'var(--bg1)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px' }}>
            <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{l}</div>
            <div style={{ fontSize: 20, fontWeight: 500, color: c }}>{v}</div>
          </div>
        ))}
      </div>
      <div className="filter-bar">
        <div style={{ flex: 1 }}><SB value={search} onChange={setSearch} placeholder="Search description or tenant…" /></div>
        <select className="form-input" style={{ width: 'auto', minWidth: 180 }} value={tenantF} onChange={ev => setTenantF(ev.target.value)}>
          <option value="all">All tenants</option>
          {active.map(t => <option key={t.id} value={t.id}>{t.name} – Unit {t.unit}</option>)}
        </select>
      </div>
      <table className="list-table">
        <thead><tr><th>Date</th><th>Tenant</th><th>Description</th><th>Amount</th><th>Status</th><th style={{ width: 40 }}></th></tr></thead>
        <tbody>
          {rows.length === 0 ? <tr><td colSpan={6}><div className="empty">No entries found</div></td></tr> :
            rows.map(l => (
              <tr key={l.id}>
                <td style={{ color: 'var(--text2)', fontSize: 12, whiteSpace: 'nowrap' }}>{l.date}</td>
                <td><span style={{ fontWeight: 500 }}>{tName(l.tenantId)}</span></td>
                <td style={{ color: 'var(--text2)' }}>{l.description}</td>
                <td style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>${l.amount.toLocaleString()}</td>
                <td><Badge label={l.status} /></td>
                <td><KebabMenu items={[{ label: 'Edit', onClick: () => openEdit(l) }, { label: 'Delete', danger: true, onClick: () => remove(l.id) }]} /></td>
              </tr>
            ))}
        </tbody>
      </table>
      {modal && (
        <Modal title={modal === 'edit' ? 'Edit entry' : 'Add entry'} onClose={() => setModal(null)}>
          <FS label="Tenant" value={form.tenantId} onChange={ff('tenantId')} options={active.map(t => ({ value: t.id, label: t.name + ' – Unit ' + t.unit }))} />
          <div className="form-grid form-grid-2"><FI label="Date" value={form.date} onChange={ff('date')} type="date" /><FI label="Amount ($)" value={form.amount} onChange={ff('amount')} type="number" /></div>
          <FI label="Description" value={form.description} onChange={ff('description')} />
          <FS label="Status" value={form.status} onChange={ff('status')} options={['Charged', 'Paid', 'Unpaid', 'Pending']} />
          <div className="modal-actions"><button className="btn" onClick={() => setModal(null)}>Cancel</button><SavingBtn onClick={save}>Save</SavingBtn></div>
        </Modal>
      )}
    </div>
  );
}

function CollapsibleCard({ title, right, isMobile, children, cardStyle, collapsible, defaultOpen = false, ...divProps }) {
  // collapsible: card can be expanded/collapsed on desktop too (starts collapsed unless defaultOpen).
  const canCollapse = isMobile || collapsible;
  const [open, setOpen] = useState(collapsible ? defaultOpen : !isMobile);
  useEffect(() => { if (!collapsible && !isMobile) setOpen(true); }, [isMobile, collapsible]);
  return (
    <div className="card" style={{ marginBottom: 16, ...cardStyle }} {...divProps}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: canCollapse ? 'pointer' : 'default', userSelect: 'none' }}
        onClick={canCollapse ? () => setOpen(p => !p) : undefined}>
        <div className="card-title" style={{ margin: 0 }}>{title}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {right && <div onClick={e => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>{right}</div>}
          {canCollapse && (
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}
              style={{ color: 'var(--text3)', flexShrink: 0, transition: 'transform 0.25s ease', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}>
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          )}
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateRows: open ? '1fr' : '0fr', transition: 'grid-template-rows 0.3s ease' }}>
        <div style={{ overflow: 'hidden' }}>
          <div style={{ paddingTop: 12 }}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Tenants (list + detail) ────────────────────────────────────────────────
function TenantDetail({ tenantId, tenants, setTenants, users, properties, ledger, setLedger, maintenance, setMaintenance, tasks, setTasks, user, onBack, backLabel, setTab, setNavTarget }) {
  const toast = useToast();
  const tenant = tenants.find(t => t.id === tenantId);
  const [editingInfo, setEditingInfo] = useState(false);
  const [ef, setEf] = useState({});
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => { const h = () => setIsMobile(window.innerWidth < 768); window.addEventListener('resize', h); return () => window.removeEventListener('resize', h); }, []);
  const [editingNotes, setEditingNotes] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [renamingDoc, setRenamingDoc] = useState(null);
  const [renameVal, setRenameVal] = useState('');
  const [addEntry, setAddEntry] = useState(false);
  const [profileTab, setProfileTab] = useState('overview');
  const [showAllLedger, setShowAllLedger] = useState(false);
  const [ledgerPayerF, setLedgerPayerF] = useState('all'); // 'all' | 'Tenant' | 'Program'
  const [showLedgerAudit, setShowLedgerAudit] = useState(false);
  const [exportLedgerModal, setExportLedgerModal] = useState(false);
  const [exportMode, setExportMode] = useState('all'); // 'all' | 'specific'
  const [exportSel, setExportSel] = useState(new Set());
  const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const chargeDesc = date => { if (!date) return ''; const [y,m] = date.split('-'); return `Rent - ${MONTHS[+m-1]} ${y}`; };
  const chargeDescFull = (date, payer) => { const base = chargeDesc(date); if (!base) return ''; return payer === 'Program' ? `${base} (Program)` : `${base} (Tenant)`; };
  const isAutoDesc = (desc, date) => { const base = chargeDesc(date); return !desc || desc === base || desc === `${base} (Program)` || desc === `${base} (Tenant)`; };
  const blankEntry = () => ({ type: 'charge', date: nyTodayISO(), description: chargeDescFull(nyTodayISO(), 'Tenant'), amount: '', payer: 'Tenant' });
  const splitEntries = (date) => {
    const items = [];
    if ((tenant.programShare || 0) > 0) items.push({ type: 'charge', date, description: chargeDescFull(date, 'Program'), amount: tenant.programShare, payer: 'Program' });
    const tShare = tenant.tenantShare || 0;
    const pFee = tenant.hasParking ? (tenant.parkingFee || 0) : 0;
    const wFee = tenant.hasWasherDryer ? (tenant.washerDryerFee || 0) : 0;
    const tenantTotal = tShare + pFee + wFee;
    if (tenantTotal > 0) {
      const breakdown = {};
      if (tShare > 0) breakdown.tenantShare = tShare;
      if (pFee > 0) breakdown.parkingFee = pFee;
      if (wFee > 0) breakdown.washerDryerFee = wFee;
      items.push({ type: 'charge', date, description: chargeDescFull(date, 'Tenant'), amount: tenantTotal, payer: 'Tenant', breakdown });
    }
    return items.length > 0 ? items : [blankEntry()];
  };
  const [entries, setEntries] = useState([blankEntry()]);
  const updateEntry = (i, k, v) => setEntries(prev => prev.map((e, idx) => {
    if (idx !== i) return e;
    const next = { ...e, [k]: v };
    if (k === 'date' && e.type === 'charge' && isAutoDesc(e.description, e.date)) next.description = chargeDescFull(v, e.payer);
    if (k === 'payer' && e.type === 'charge' && isAutoDesc(e.description, e.date)) next.description = chargeDescFull(e.date, v);
    if (k === 'type' && v === 'payment' && isAutoDesc(e.description, e.date)) next.description = '';
    if (k === 'type' && v === 'charge' && !e.description) next.description = chargeDescFull(e.date, e.payer);
    return next;
  }));
  const addRow = () => setEntries(prev => [...prev, blankEntry()]);
  const removeRow = i => setEntries(prev => prev.filter((_, idx) => idx !== i));
  const duplicateRow = i => setEntries(prev => { const copy = [...prev]; copy.splice(i + 1, 0, { ...prev[i] }); return copy; });
  const prop = properties.find(p => p.id === tenant?.propertyId) || {};
  const propName = prop.name || '';
  const propUnit = (prop.unitsList || []).find(u => u.unitNumber === tenant?.unit);
  const tenantLedgerAll = useMemo(() => ledger.filter(l => l.tenantId === tenantId).sort((a, b) => b.date.localeCompare(a.date)), [ledger, tenantId]);
  const tenantLedger = useMemo(() => tenantLedgerAll.filter(l => !l.deleted), [tenantLedgerAll]);
  const programCharged = useMemo(() => tenantLedger.filter(l => l.type === 'charge' && l.payer === 'Program').reduce((s, l) => s + (+l.amount || 0), 0), [tenantLedger]);
  const programPaid = useMemo(() => tenantLedger.filter(l => l.type === 'charge' && l.payer === 'Program').reduce((s, l) => s + (+l.paidAmount || 0), 0), [tenantLedger]);
  const tenantCharged = useMemo(() => tenantLedger.filter(l => l.type === 'charge' && l.payer !== 'Program').reduce((s, l) => s + (+l.amount || 0), 0), [tenantLedger]);
  const tenantPaid = useMemo(() => tenantLedger.filter(l => l.type === 'charge' && l.payer !== 'Program').reduce((s, l) => s + (+l.paidAmount || 0), 0), [tenantLedger]);
  const charged = useMemo(() => programCharged + tenantCharged, [programCharged, tenantCharged]);
  const paid = useMemo(() => programPaid + tenantPaid, [programPaid, tenantPaid]);
  const totalBalance = useMemo(() => charged - paid, [charged, paid]);
  const [editEntry, setEditEntry] = useState(null);
  const [payModal, setPayModal] = useState(null);
  const [payDragOver, setPayDragOver] = useState(false);
  const [proofUploading, setProofUploading] = useState(false);
  // Let user paste (Ctrl+V) a document/image straight into the Record-payment modal
  const payModalOpen = !!payModal;
  useEffect(() => {
    if (!payModalOpen) return;
    const onPaste = e => {
      const items = (e.clipboardData && e.clipboardData.items) || [];
      for (const it of items) {
        if (it.kind === 'file') {
          const f = it.getAsFile();
          if (f) { setPayModal(p => (p ? { ...p, proofFile: f } : p)); e.preventDefault(); break; }
        }
      }
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [payModalOpen]);
  const [payHistoryEntry, setPayHistoryEntry] = useState(null);
  const [receiptModal, setReceiptModal] = useState(null);
  const [moveOutModal, setMoveOutModal] = useState(null); // { date, note }
  const [renewalModal, setRenewalModal] = useState(false);
  const [renewalForm, setRenewalForm] = useState({ pct1: '', pct2: '' });
  const [showLogs, setShowLogs] = useState(false);
  const [occupantModal, setOccupantModal] = useState(null); // null | 'add' | { index, ...occupant }
  const [smsMessages, setSmsMessages] = useState([]);
  const [smsInput, setSmsInput] = useState('');
  const [smsSending, setSmsSending] = useState(false);
  const msgEndRef = useRef(null);
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailLogView, setEmailLogView] = useState(null);
  const [emailSearch, setEmailSearch] = useState('');
  const [emailPage, setEmailPage] = useState(1);
  const [emailTypeFilter, setEmailTypeFilter] = useState('all'); // 'all' | 'direct' | 'general'
  const [taskSearch, setTaskSearch] = useState('');
  const [taskPage, setTaskPage] = useState(1);
  const [maintSearch, setMaintSearch] = useState('');
  const [maintFilter, setMaintFilter] = useState('open'); // 'open' | 'closed' — Activity → Maintenance & Inspection
  const [maintTypeFilter, setMaintTypeFilter] = useState('all'); // 'all' | 'maintenance' | 'inspection'
  const [paymentNotifs, setPaymentNotifs] = useState([]);
  const [maintPage, setMaintPage] = useState(1);
  useEffect(() => { setMaintPage(1); }, [tenantId]);
  const [viewingMaintId, setViewingMaintId] = useState(null);
  const [addMaintModal, setAddMaintModal] = useState(null); // maintenance quick-add from Activity
  const [addTaskModal, setAddTaskModal] = useState(null); // task quick-add from Activity
  useEffect(() => {
    const q = query(collection(db, 'emailLogs'), where('tenantId', '==', tenantId));
    return onSnapshot(q, snap => {
      const logs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      logs.sort((a, b) => (b.sentAt || '').localeCompare(a.sentAt || ''));
      setEmailLogs(logs);
    });
  }, [tenantId]);
  useEffect(() => {
    const q = query(collection(db, 'paymentNotifications'), where('tenantId', '==', tenantId));
    return onSnapshot(q, snap => {
      const notifs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      notifs.sort((a, b) => (b.submittedAt || '').localeCompare(a.submittedAt || ''));
      setPaymentNotifs(notifs);
    });
  }, [tenantId]);
  useEffect(() => {
    const q = query(collection(db, 'sms', tenantId, 'messages'), orderBy('timestamp', 'asc'));
    return onSnapshot(q, snap => setSmsMessages(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, [tenantId]);
  useEffect(() => { msgEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [smsMessages]);
  const sendSmsMsg = async () => {
    if (!smsInput.trim() || !tenant?.phone || smsSending) return;
    setSmsSending(true);
    try {
      await httpsCallable(functions, 'sendSms')({ tenantId, to: tenant.phone, body: smsInput.trim() });
      setSmsInput('');
    } catch (e) { toast('SMS failed: ' + (e.message || 'Unknown error'), 'error'); }
    finally { setSmsSending(false); }
  };
  const [occupantForm, setOccupantForm] = useState({ name: '', relationship: '', dob: '', phone: '' });
  const saveOccupant = () => {
    if (!occupantForm.name.trim()) return;
    const members = [...(tenant.householdMembers || [])];
    if (occupantModal === 'add') members.push({ ...occupantForm, id: `occ${Date.now()}` });
    else members[occupantModal.index] = { ...members[occupantModal.index], ...occupantForm };
    setTenants(prev => prev.map(t => t.id === tenantId ? { ...t, householdMembers: members } : t));
    setOccupantModal(null);
  };
  const removeOccupant = idx => {
    const members = (tenant.householdMembers || []).filter((_, i) => i !== idx);
    setTenants(prev => prev.map(t => t.id === tenantId ? { ...t, householdMembers: members } : t));
  };

  const addLog = (action) => {
    const entry = { ts: new Date().toISOString(), action, by: user?.email || user?.displayName || 'Unknown' };
    setTenants(prev => prev.map(t => t.id === tenantId ? { ...t, auditLog: [...(t.auditLog || []), entry] } : t));
  };

  const _addDaysISO = (iso, n) => { const d = new Date(iso + 'T12:00:00'); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
  const _addYrsMinusDay = (iso, yrs) => { const d = new Date(iso + 'T12:00:00'); d.setFullYear(d.getFullYear() + yrs); d.setDate(d.getDate() - 1); return d.toISOString().slice(0, 10); };


  if (!tenant) return <div>Tenant not found.</div>;
  if (viewingMaintId) return <MaintenanceDetail maintenanceId={viewingMaintId} maintenance={maintenance} setMaintenance={setMaintenance} properties={properties} tenants={tenants} onBack={() => setViewingMaintId(null)} />;
  const isPrevious = tenant.status === 'Previous';
  const confirmMoveOut = () => {
    setTenants(prev => prev.map(t => t.id === tenantId ? { ...t, status: 'Previous', moveOutDate: moveOutModal.date, moveOutNote: moveOutModal.note } : t));
    setMoveOutModal(null);
    toast('Tenant moved out', 'info');
  };
  const reinstate = () => {
    if (window.confirm('Reinstate this tenant as Occupied?')) {
      setTenants(prev => prev.map(t => t.id === tenantId ? { ...t, status: 'Occupied', moveOutDate: '', moveOutNote: '' } : t));
      toast('Tenant reinstated');
    }
  };
  const saveInfo = () => {
    if (ef.leaseStart && ef.leaseEnd && ef.leaseEnd < ef.leaseStart) {
      toast('Lease end date cannot be before the start date', 'error'); return;
    }
    const ts = +ef.tenantShare || 0, ps = +ef.programShare || 0, pk = ef.hasParking ? (+ef.parkingFee || 0) : 0, wd = ef.hasWasherDryer ? (+ef.washerDryerFee || 0) : 0;
    const u = { ...ef, rent: ts + ps + pk + wd, tenantShare: ts, programShare: ps, parkingFee: pk, washerDryerFee: wd, bedrooms: +ef.bedrooms || 0, bathrooms: +ef.bathrooms || 0, dueDay: +ef.dueDay || 1 };
    delete u.pendingRentChange; // scheduled rent changes were removed
    setTenants(prev => prev.map(t => t.id === tenantId ? u : t));
    addLog('Edited tenant information');
    toast('Tenant info saved');
    setEditingInfo(false);
  };
  const saveNotes = () => {
    setTenants(prev => prev.map(t => t.id === tenantId ? { ...t, notes: noteDraft } : t));
    addLog('Updated notes');
    toast('Notes saved');
    setEditingNotes(false);
  };

  const updateTenantDocs = docs => setTenants(prev => prev.map(t => t.id === tenantId ? { ...t, documents: docs } : t));
  const uploadFile = async file => {
    if (!file) return;
    setUploadingDoc(true);
    try {
      const path = `tenants/${tenantId}/${Date.now()}_${file.name}`;
      const storageRef = sRef(storage, path);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      const newDoc = { id: `doc${Date.now()}`, name: file.name, url, storagePath: path, uploadedAt: new Date().toISOString() };
      updateTenantDocs([...(tenant.documents || []), newDoc]);
      addLog(`Uploaded document: ${file.name}`);
    } catch (err) {
      console.error('Tenant doc upload failed:', err);
      alert('Upload failed: ' + (err?.code || err?.message || 'unknown error'));
    }
    setUploadingDoc(false);
  };
  const handleTenantDocUpload = async e => { const file = e.target.files[0]; e.target.value = ''; await uploadFile(file); };
  const handleDrop = async e => { e.preventDefault(); setDragOver(false); const file = e.dataTransfer.files[0]; if (file) await uploadFile(file); };
  const handleDragOver = e => { e.preventDefault(); setDragOver(true); };
  const handleDragLeave = e => { e.preventDefault(); if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false); };
  const handleTenantDocDelete = async doc => {
    if (!window.confirm(`Delete "${doc.name}"?`)) return;
    try { if (doc.storagePath) await deleteObject(sRef(storage, doc.storagePath)); } catch (_) {}
    updateTenantDocs((tenant.documents || []).filter(d => d.id !== doc.id));
    addLog(`Deleted document: ${doc.name}`);
  };
  const handleTenantDocRename = doc => { setRenamingDoc(doc.id); setRenameVal(doc.name); };
  const saveRename = () => {
    if (!renameVal.trim()) return;
    const oldDoc = (tenant.documents || []).find(d => d.id === renamingDoc);
    updateTenantDocs((tenant.documents || []).map(d => d.id === renamingDoc ? { ...d, name: renameVal.trim() } : d));
    if (oldDoc) addLog(`Renamed document: "${oldDoc.name}" → "${renameVal.trim()}"`);
    setRenamingDoc(null);
  };
  const saveEntry = () => {
    const by = user?.displayName || user?.email || '';
    const now = new Date().toISOString();
    const newItems = entries.filter(e => e.amount !== '' && +e.amount > 0).map((e, i) => ({ ...e, id: `l${Date.now()}${i}`, tenantId, amount: +e.amount, status: e.type === 'payment' ? 'Paid' : 'Unpaid', processedBy: by, createdAt: now }));
    if (!newItems.length) return;
    const dups = newItems.filter(n => tenantLedger.some(l =>
      l.date === n.date &&
      Number(l.amount) === Number(n.amount) &&
      (l.description || '').trim() === (n.description || '').trim()
    ));
    if (dups.length > 0 && !window.confirm(`${dups.length} entr${dups.length > 1 ? 'ies' : 'y'} appear to already exist (same date, amount, and description). Save anyway?`)) return;
    setLedger(prev => [...prev, ...newItems]);
    newItems.forEach(e => addLog(`Added ledger entry: ${e.description} ($${e.amount})`));
    setEntries([blankEntry()]);
    setAddEntry(false);
    toast(`${newItems.length} entr${newItems.length > 1 ? 'ies' : 'y'} added`);
  };
  const dupLedger = entry => { const by = user?.displayName || user?.email || ''; setLedger(prev => [...prev, { ...entry, id: `l${Date.now()}`, processedBy: by, createdAt: new Date().toISOString() }]); toast('Entry duplicated'); };
  const delEntry = id => {
    const entry = tenantLedger.find(l => l.id === id);
    if (window.confirm('Delete entry?')) {
      const by = user?.displayName || user?.email || '';
      setLedger(prev => prev.map(l => l.id === id ? { ...l, deleted: true, deletedBy: by, deletedAt: new Date().toISOString() } : l));
      if (entry) addLog(`Deleted ledger entry: ${entry.description} ($${entry.amount})`);
      toast('Entry deleted', 'info');
    }
  };
  const openEditEntry = entry => setEditEntry({ ...entry, type: entry.status === 'Paid' ? 'payment' : 'charge' });
  const recordPayment = async (entry, amount, proofFile) => {
    const payAmt = +amount;
    if (!payAmt) return;
    const chargeTotal = +entry.amount || 0;
    const alreadyPaid = +entry.paidAmount || 0;
    const newTotal = Math.min(chargeTotal, alreadyPaid + payAmt);
    const fullyPaid = newTotal >= chargeTotal;
    const by = user?.displayName || user?.email || '';
    let proofUrl = null;
    let proofName = null;
    if (proofFile) {
      setProofUploading(true);
      try {
        const path = `ledger/${entry.id}/payments/${Date.now()}_${proofFile.name}`;
        const storageRef = sRef(storage, path);
        await uploadBytes(storageRef, proofFile);
        proofUrl = await getDownloadURL(storageRef);
        proofName = proofFile.name;
      } catch (err) {
        toast('Proof upload failed — payment recorded without attachment', 'warn');
      } finally {
        setProofUploading(false);
      }
    }
    const payEvent = { amount: payAmt, by, at: new Date().toISOString(), newTotal, ...(proofUrl ? { proofUrl, proofName } : {}) };
    setLedger(prev => prev.map(l => l.id === entry.id ? { ...l, paidAmount: newTotal, status: fullyPaid ? 'Paid' : newTotal > 0 ? 'Partial' : 'Unpaid', paymentHistory: [...(l.paymentHistory || []), payEvent] } : l));
    setPayModal(null);
    toast(fullyPaid ? 'Marked as fully paid' : 'Payment recorded');
  };
  const generateReceiptPDF = (selectedEntries) => {
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });
    const pageW = pdf.internal.pageSize.getWidth();
    const margin = 48;
    const contentW = pageW - margin * 2;
    let y = margin;
    // Header bar
    pdf.setFillColor(40, 60, 120);
    pdf.rect(0, 0, pageW, 54, 'F');
    pdf.setFontSize(16); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(255, 255, 255);
    pdf.text('PAYMENT RECEIPT', margin, 34);
    pdf.setFontSize(9); pdf.setFont('helvetica', 'normal');
    pdf.text(prop.name || 'Amar Mgmt', pageW - margin, 34, { align: 'right' });
    y = 78;
    // Entity info
    pdf.setFontSize(10); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(30, 30, 30);
    pdf.text(prop.name || '—', margin, y); y += 16;
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9); pdf.setTextColor(80, 80, 80);
    pdf.text(prop.address || '—', margin, y); y += 14;
    pdf.text(`Unit: ${tenant.unit}`, margin, y); y += 14;
    pdf.text(`Tenant: ${tenant.name}`, margin, y); y += 14;
    const dates = [...new Set(selectedEntries.map(e => fmtDate(e.date)))].join(', ');
    pdf.text(`Date: ${dates}`, margin, y); y += 22;
    // Divider
    pdf.setDrawColor(200, 200, 200); pdf.line(margin, y, margin + contentW, y); y += 14;
    // Table header
    pdf.setFontSize(9); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(100, 100, 100);
    pdf.text('DESCRIPTION', margin, y);
    pdf.text('AMOUNT', margin + contentW, y, { align: 'right' }); y += 10;
    pdf.setDrawColor(200, 200, 200); pdf.line(margin, y, margin + contentW, y); y += 16;
    // Each selected entry
    let totalCharged = 0;
    let totalPaid = 0;
    selectedEntries.forEach(entry => {
      pdf.setFont('helvetica', 'normal'); pdf.setFontSize(10); pdf.setTextColor(30, 30, 30);
      if (entry.breakdown && Object.keys(entry.breakdown).length > 1) {
        pdf.setFont('helvetica', 'bold');
        pdf.text(entry.description || '—', margin, y); y += 18;
        pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9); pdf.setTextColor(60, 60, 60);
        if (entry.breakdown.tenantShare) { pdf.text('Tenant Share', margin + 14, y); pdf.text(`$${entry.breakdown.tenantShare.toLocaleString()}`, margin + contentW, y, { align: 'right' }); y += 16; }
        if (entry.breakdown.parkingFee) { pdf.text('Parking', margin + 14, y); pdf.text(`$${entry.breakdown.parkingFee.toLocaleString()}`, margin + contentW, y, { align: 'right' }); y += 16; }
        if (entry.breakdown.washerDryerFee) { pdf.text('Washer / Dryer', margin + 14, y); pdf.text(`$${entry.breakdown.washerDryerFee.toLocaleString()}`, margin + contentW, y, { align: 'right' }); y += 16; }
        y += 6;
      } else {
        pdf.text(entry.description || '—', margin, y);
        pdf.text(`$${(+entry.amount || 0).toLocaleString()}`, margin + contentW, y, { align: 'right' }); y += 20;
      }
      totalCharged += (+entry.amount || 0);
      totalPaid += (+entry.paidAmount || 0);
    });
    y += 4;
    // Payment row
    pdf.setDrawColor(220, 220, 220); pdf.line(margin, y, margin + contentW, y); y += 14;
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(10); pdf.setTextColor(22, 101, 52);
    pdf.text('Payment', margin, y);
    pdf.text(`-$${totalPaid.toLocaleString()}`, margin + contentW, y, { align: 'right' }); y += 10;
    pdf.setDrawColor(180, 180, 180); pdf.line(margin, y, margin + contentW, y); y += 14;
    // Total balance
    const balance = Math.max(0, totalCharged - totalPaid);
    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(11);
    pdf.setTextColor(30, 30, 30); pdf.text('Total Balance', margin, y);
    pdf.setTextColor(...(balance > 0 ? [153, 27, 27] : [22, 101, 52]));
    pdf.text(`$${balance.toLocaleString()}`, margin + contentW, y, { align: 'right' });
    pdf.save(`Receipt_${tenant.name.replace(/\s+/g, '_')}.pdf`);
  };
  const saveEditEntry = () => {
    const original = tenantLedgerAll.find(l => l.id === editEntry.id);
    const by = user?.displayName || user?.email || '';
    const now = new Date().toISOString();
    const changes = [];
    if (original) {
      if ((original.description || '') !== (editEntry.description || ''))
        changes.push({ field: 'Description', before: original.description || '', after: editEntry.description || '' });
      if (+original.amount !== +editEntry.amount)
        changes.push({ field: 'Amount', before: String(original.amount), after: String(editEntry.amount) });
      if ((original.date || '') !== (editEntry.date || ''))
        changes.push({ field: 'Date', before: original.date || '', after: editEntry.date || '' });
      if ((original.payer || '') !== (editEntry.payer || ''))
        changes.push({ field: 'Payer', before: original.payer || '', after: editEntry.payer || '' });
    }
    const updated = {
      ...editEntry,
      amount: +editEntry.amount,
      status: editEntry.type === 'payment' ? 'Paid' : 'Unpaid',
      ...(changes.length > 0 ? { editHistory: [...(original?.editHistory || []), { by, at: now, changes }] } : {}),
    };
    setLedger(prev => prev.map(l => l.id === updated.id ? updated : l));
    setEditEntry(null);
    toast('Entry updated');
  };

  const exportLedgerPDF = (rows = tenantLedger) => {
    const prop = properties.find(p => p.id === tenant.propertyId) || {};
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const margin = 48;
    const contentW = pageW - margin * 2;
    let y = margin;

    const checkPage = (needed = 20) => {
      if (y + needed > pageH - margin) { pdf.addPage(); y = margin; }
    };

    // ── Header bar ──────────────────────────────────────────────
    pdf.setFillColor(40, 60, 120);
    pdf.rect(0, 0, pageW, 46, 'F');
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(255, 255, 255);
    pdf.text('Tenant Ledger', margin, 29);
    pdf.setFontSize(8.5);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(200, 210, 255);
    pdf.text(`Generated: ${new Date().toLocaleDateString('en-US', { timeZone: 'America/New_York' })}`, pageW - margin, 29, { align: 'right' });
    y = 72;

    // ── Tenant info block ────────────────────────────────────────
    const infoFields = [
      ['Entity Name', prop.name || '—'],
      ['Address', prop.address || '—'],
      ['Unit Number', tenant.unit || '—'],
      ["Tenant's Name", tenant.name || '—'],
      ['Total Rent Amount', `$${(tenant.rent || 0).toLocaleString()}`],
      ['Program Share', `$${(tenant.programShare || 0).toLocaleString()}`],
      ['Tenant Share', `$${(tenant.tenantShare || 0).toLocaleString()}`],
      // Break out add-on fees so the tenant charge composition is clear.
      ...(tenant.hasParking && (+tenant.parkingFee || 0) > 0 ? [['Parking Fee', `$${(+tenant.parkingFee || 0).toLocaleString()}`]] : []),
      ...(tenant.hasWasherDryer && (+tenant.washerDryerFee || 0) > 0 ? [['Washer/Dryer Fee', `$${(+tenant.washerDryerFee || 0).toLocaleString()}`]] : []),
    ];

    const colW = contentW / 2;
    infoFields.forEach(([label, value], i) => {
      const col = i % 2;
      const x = margin + col * colW;
      if (col === 0 && i > 0) y += 22;
      pdf.setFontSize(6.5);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(120, 120, 120);
      pdf.text(label.toUpperCase(), x, y);
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(20, 20, 20);
      pdf.text(value, x, y + 10);
    });
    y += 30;

    // ── Divider ──────────────────────────────────────────────────
    pdf.setDrawColor(200, 200, 200);
    pdf.line(margin, y, pageW - margin, y);
    y += 16;

    // ── Summary row (reflects the exported rows) ─────────────────
    const expCharged = rows.filter(l => l.type === 'charge').reduce((s, l) => s + (+l.amount || 0), 0);
    const expPaid = rows.filter(l => l.type === 'charge').reduce((s, l) => s + (+l.paidAmount || 0), 0);
    const expBalance = expCharged - expPaid;
    const summaryItems = [
      ['Total Charged', `$${expCharged.toLocaleString()}`],
      ['Total Paid', `$${expPaid.toLocaleString()}`],
      ['Balance Due', `$${Math.max(0, expBalance).toLocaleString()}`],
    ];
    const sumColW = contentW / 3;
    summaryItems.forEach(([label, value], i) => {
      const x = margin + i * sumColW;
      pdf.setFontSize(6.5);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(120, 120, 120);
      pdf.text(label.toUpperCase(), x, y);
      pdf.setFontSize(10.5);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(i === 2 && expBalance > 0 ? 200 : 20, i === 2 && expBalance > 0 ? 60 : 20, 20);
      pdf.text(value, x, y + 11);
    });
    y += 28;

    // ── Table header ─────────────────────────────────────────────
    pdf.setFillColor(245, 245, 247);
    pdf.rect(margin, y, contentW, 16, 'F');
    const cols = [
      { label: 'Date',        x: margin,               w: 70 },
      { label: 'Description', x: margin + 70,          w: 190 },
      { label: 'Charge',      x: margin + 260,         w: 70 },
      { label: 'Amount Paid', x: margin + 330,         w: 80 },
      { label: 'Balance',     x: margin + 410,         w: 60 },
    ];
    pdf.setFontSize(6.5);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(80, 80, 80);
    cols.forEach(c => pdf.text(c.label.toUpperCase(), c.x + 4, y + 11));
    y += 16;

    // ── Table rows ───────────────────────────────────────────────
    rows.forEach((l, idx) => {
      checkPage(16);
      if (idx % 2 === 0) {
        pdf.setFillColor(252, 252, 253);
        pdf.rect(margin, y, contentW, 16, 'F');
      }
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(40, 40, 40);

      const isCharge = l.type === 'charge';
      const balAmt = isCharge ? Math.max(0, (+l.amount || 0) - (+l.paidAmount || 0)) : 0;
      const rowData = [
        { x: cols[0].x, text: fmtDate(l.date) },
        { x: cols[1].x, text: l.description || '—' },
        { x: cols[2].x, text: isCharge ? `$${(+l.amount || 0).toLocaleString()}` : '—' },
        { x: cols[3].x, text: isCharge ? (l.paidAmount ? `$${(+l.paidAmount).toLocaleString()}` : '—') : `$${(+l.amount || 0).toLocaleString()}` },
        { x: cols[4].x, text: isCharge ? `$${balAmt.toLocaleString()}` : '—', red: isCharge && balAmt > 0 },
      ];

      rowData.forEach(cell => {
        const maxW = 175;
        const truncated = pdf.getStringUnitWidth(cell.text) * 8 > maxW ? cell.text.slice(0, 44) + '…' : cell.text;
        pdf.setTextColor(cell.red ? 153 : 40, cell.red ? 27 : 40, cell.red ? 27 : 40);
        pdf.text(truncated, cell.x + 4, y + 11);
      });

      // row bottom border
      pdf.setDrawColor(235, 235, 235);
      pdf.line(margin, y + 16, pageW - margin, y + 16);
      y += 16;

      // Break out add-on components (parking / washer-dryer) under a combined tenant charge.
      let bd = l.breakdown;
      if (isCharge && !bd && (l.payer || 'Tenant') !== 'Program') {
        const tShare = +tenant.tenantShare || 0;
        const pFee = tenant.hasParking ? (+tenant.parkingFee || 0) : 0;
        const wFee = tenant.hasWasherDryer ? (+tenant.washerDryerFee || 0) : 0;
        if ((pFee > 0 || wFee > 0) && Math.round(+l.amount || 0) === Math.round(tShare + pFee + wFee)) {
          bd = {};
          if (tShare > 0) bd.tenantShare = tShare;
          if (pFee > 0) bd.parkingFee = pFee;
          if (wFee > 0) bd.washerDryerFee = wFee;
        }
      }
      if (isCharge && bd && (bd.parkingFee || bd.washerDryerFee)) {
        const parts = [];
        if (bd.tenantShare) parts.push(['Rent share', bd.tenantShare]);
        if (bd.parkingFee) parts.push(['Parking', bd.parkingFee]);
        if (bd.washerDryerFee) parts.push(['Washer / Dryer', bd.washerDryerFee]);
        parts.forEach(([label, amt]) => {
          checkPage(12);
          pdf.setFontSize(7);
          pdf.setFont('helvetica', 'italic');
          pdf.setTextColor(120, 120, 120);
          pdf.text(`- ${label}`, cols[1].x + 10, y + 9);
          pdf.text(`$${(+amt).toLocaleString()}`, cols[2].x + 4, y + 9);
          pdf.setDrawColor(240, 240, 240);
          pdf.line(margin, y + 12, pageW - margin, y + 12);
          y += 12;
        });
      }
    });

    if (rows.length === 0) {
      pdf.setFontSize(9);
      pdf.setTextColor(160, 160, 160);
      pdf.text('No ledger entries found.', margin + 4, y + 12);
    } else {
      // ── Totals row ──
      const expBalanceCol = rows.filter(l => l.type === 'charge').reduce((s, l) => s + Math.max(0, (+l.amount || 0) - (+l.paidAmount || 0)), 0);
      checkPage(24);
      y += 3;
      pdf.setDrawColor(120, 120, 120);
      pdf.setLineWidth(1);
      pdf.line(margin, y, pageW - margin, y);
      pdf.setLineWidth(0.5);
      y += 4;
      pdf.setFontSize(8.5);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(20, 20, 20);
      pdf.text('TOTAL', cols[1].x + 4, y + 11);
      pdf.text(`$${expCharged.toLocaleString()}`, cols[2].x + 4, y + 11);
      pdf.text(`$${expPaid.toLocaleString()}`, cols[3].x + 4, y + 11);
      pdf.setTextColor(expBalanceCol > 0 ? 200 : 20, expBalanceCol > 0 ? 60 : 20, expBalanceCol > 0 ? 60 : 20);
      pdf.text(`$${expBalanceCol.toLocaleString()}`, cols[4].x + 4, y + 11);
      y += 16;
    }

    pdf.save(`${tenant.name.replace(/\s+/g, '_')}_Ledger.pdf`);
  };

  const exportAuditLogPDF = () => {
    const prop = properties.find(p => p.id === tenant.propertyId) || {};
    const fmtTs = ts => ts ? new Date(ts).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';
    const fmt$ = n => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

    // Build events (same logic as audit log modal)
    const events = [];
    for (const l of tenantLedgerAll) {
      events.push({ chargeDate: l.date, description: l.description || '—', eventType: 'Charge', amount: fmt$(l.amount), isPayment: false, isDeletion: false, isEdit: false, isDeleted: !!l.deleted, enteredBy: l.autoGenerated ? 'Auto (System)' : (l.processedBy || '—'), enteredOn: fmtTs(l.createdAt), sortKey: l.createdAt || l.date || '' });
      if (l.paymentHistory && l.paymentHistory.length > 0) {
        l.paymentHistory.forEach((p, pi) => {
          events.push({ chargeDate: l.date, description: l.description || '—', eventType: 'Payment', amount: fmt$(p.amount), isPayment: true, isDeletion: false, isEdit: false, isDeleted: !!l.deleted, enteredBy: p.by || '—', enteredOn: fmtTs(p.at), sortKey: p.at || '' });
        });
      } else if ((+l.paidAmount || 0) > 0 && !l.paymentHistory) {
        events.push({ chargeDate: l.date, description: l.description || '—', eventType: 'Payment', amount: fmt$(l.paidAmount), isPayment: true, isDeletion: false, isEdit: false, isDeleted: !!l.deleted, enteredBy: l.processedBy || '—', enteredOn: '—', sortKey: '' });
      }
      if (l.editHistory && l.editHistory.length > 0) {
        l.editHistory.forEach((edit) => {
          const changeDesc = edit.changes.map(c => `${c.field}: "${c.before}" → "${c.after}"`).join(' · ');
          events.push({ chargeDate: l.date, description: changeDesc, eventType: 'Edited', amount: '—', isPayment: false, isDeletion: false, isEdit: true, isDeleted: !!l.deleted, enteredBy: edit.by || '—', enteredOn: fmtTs(edit.at), sortKey: edit.at || '' });
        });
      }
      if (l.deleted) {
        events.push({ chargeDate: l.date, description: l.description || '—', eventType: 'Deleted', amount: fmt$(l.amount), isPayment: false, isDeletion: true, isEdit: false, isDeleted: true, enteredBy: l.deletedBy || '—', enteredOn: fmtTs(l.deletedAt), sortKey: l.deletedAt || '' });
      }
    }
    events.sort((a, b) => b.sortKey.localeCompare(a.sortKey));

    const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'letter' });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const margin = 48;
    const contentW = pageW - margin * 2;
    let y = margin;

    const checkPage = (needed = 20) => {
      if (y + needed > pageH - margin) { pdf.addPage(); y = margin; }
    };

    // Header bar
    pdf.setFillColor(40, 60, 120);
    pdf.rect(0, 0, pageW, 54, 'F');
    pdf.setFontSize(18); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(255, 255, 255);
    pdf.text('Ledger Audit Log', margin, 34);
    pdf.setFontSize(10); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(200, 210, 255);
    pdf.text(`Generated: ${new Date().toLocaleDateString('en-US', { timeZone: 'America/New_York' })}`, pageW - margin, 34, { align: 'right' });
    y = 76;

    // Tenant / property info
    const infoFields = [["Tenant", tenant.name || '—'], ["Property", prop.name || '—'], ["Unit", tenant.unit || '—'], ["Total Events", String(events.length)]];
    const colW = contentW / 4;
    infoFields.forEach(([label, value], i) => {
      const x = margin + i * colW;
      pdf.setFontSize(7.5); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(120, 120, 120);
      pdf.text(label.toUpperCase(), x, y);
      pdf.setFontSize(10.5); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(20, 20, 20);
      pdf.text(value, x, y + 12);
    });
    y += 36;

    // Divider
    pdf.setDrawColor(200, 200, 200); pdf.line(margin, y, pageW - margin, y); y += 14;

    // Table columns
    const cols = [
      { label: 'Charge Date', x: margin,          w: 72 },
      { label: 'Description', x: margin + 72,     w: 210 },
      { label: 'Type',        x: margin + 282,    w: 58 },
      { label: 'Amount',      x: margin + 340,    w: 72 },
      { label: 'Entered By',  x: margin + 412,    w: 150 },
      { label: 'Entered On',  x: margin + 562,    w: contentW - 562 },
    ];

    // Table header row
    pdf.setFillColor(245, 245, 247);
    pdf.rect(margin, y, contentW, 18, 'F');
    pdf.setFontSize(7.5); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(80, 80, 80);
    cols.forEach(c => pdf.text(c.label.toUpperCase(), c.x + 4, y + 12));
    y += 18;

    // Table rows
    const truncate = (text, maxW, fontSize) => {
      if (pdf.getStringUnitWidth(text) * fontSize / pdf.internal.scaleFactor > maxW) {
        let t = text;
        while (t.length > 4 && pdf.getStringUnitWidth(t + '…') * fontSize / pdf.internal.scaleFactor > maxW) t = t.slice(0, -1);
        return t + '…';
      }
      return text;
    };

    events.forEach((ev, idx) => {
      checkPage(18);
      if (idx % 2 === 0) { pdf.setFillColor(252, 252, 253); pdf.rect(margin, y, contentW, 18, 'F'); }
      if (ev.isDeletion) { pdf.setFillColor(255, 242, 242); pdf.rect(margin, y, contentW, 18, 'F'); }
      if (ev.isEdit) { pdf.setFillColor(239, 246, 255); pdf.rect(margin, y, contentW, 18, 'F'); }

      pdf.setFontSize(8.5); pdf.setFont('helvetica', 'normal');

      const typeColor = ev.isDeletion ? [153,27,27] : ev.isEdit ? [29,78,216] : ev.isPayment ? [22,101,52] : [120,70,0];

      const rowData = [
        { col: cols[0], text: ev.chargeDate || '—', color: ev.isDeleted && !ev.isDeletion ? [160,160,160] : [40,40,40] },
        { col: cols[1], text: ev.description, color: ev.isDeleted && !ev.isDeletion ? [160,160,160] : [40,40,40] },
        { col: cols[2], text: ev.eventType, color: typeColor },
        { col: cols[3], text: ev.amount, color: ev.isPayment ? [22,101,52] : ev.isDeletion ? [153,27,27] : [40,40,40] },
        { col: cols[4], text: ev.enteredBy, color: ev.isDeletion ? [153,27,27] : ev.isEdit ? [29,78,216] : [40,40,40] },
        { col: cols[5], text: ev.enteredOn, color: [100,100,100] },
      ];

      rowData.forEach(cell => {
        pdf.setTextColor(...cell.color);
        const text = truncate(cell.text, cell.col.w - 8, 8.5);
        pdf.text(text, cell.col.x + 4, y + 12);
      });

      pdf.setDrawColor(235, 235, 235);
      pdf.line(margin, y + 18, pageW - margin, y + 18);
      y += 18;
    });

    if (events.length === 0) {
      pdf.setFontSize(9); pdf.setTextColor(160, 160, 160);
      pdf.text('No audit events found.', margin + 4, y + 12);
    }

    pdf.save(`${tenant.name.replace(/\s+/g, '_')}_Audit_Log.pdf`);
  };

  const CopyBtn = ({ text }) => (
    <button title="Copy" onClick={() => navigator.clipboard.writeText(text)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', padding: '2px 4px', borderRadius: 4, display: 'flex', alignItems: 'center' }} onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'} onMouseLeave={e => e.currentTarget.style.color = 'var(--text3)'}>
      <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
    </button>
  );
  const InfoRow = ({ label, value, children }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0' }}>
      <div className="info-label" style={{ marginBottom: 0, width: 110, flexShrink: 0 }}>{label}</div>
      {children || <div className="info-val">{value || '—'}</div>}
    </div>
  );
  return (
    <div>
      <button className="back-btn" onClick={onBack}>{ic(I.arrowLeft, 15)} {backLabel || 'All tenants'}</button>
      {isPrevious && (
        <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Moved Out</span>
            {tenant.moveOutDate && <span style={{ fontSize: 12, color: 'var(--text2)' }}>· {fmtDate(tenant.moveOutDate)}</span>}
            {tenant.moveOutNote && <span style={{ fontSize: 12, color: 'var(--text3)' }}>· {tenant.moveOutNote}</span>}
          </div>
          <button className="btn" style={{ fontSize: 12, padding: '4px 12px' }} onClick={reinstate}>Reinstate</button>
        </div>
      )}
      {isMobile ? (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 16, fontWeight: 700, lineHeight: 1.2, marginBottom: 3 }}>{tenant.name}</div>
          <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 8 }}>{prop.address || propName} · Unit {tenant.unit} · {propName}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <Badge label={tenant.status} />
            {isPrevious
              ? null
              : (editingInfo
                ? <button className="btn btn-primary" style={{ fontSize: 12, padding: '4px 12px' }} onClick={saveInfo}>Save changes</button>
                : <>
                    <button className="btn" style={{ fontSize: 12, padding: '4px 12px' }} onClick={() => { setEf({ ...tenant }); setEditingInfo(true); }}>{ic(I.pencil, 12)} Edit info</button>
                    <button className="btn" style={{ fontSize: 12, padding: '4px 12px', color: 'var(--red-txt)', borderColor: 'var(--red-txt)' }} onClick={() => setMoveOutModal({ date: nyTodayISO(), note: '' })}>Move Out</button>
                  </>
              )
            }
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}><h1 style={{ fontSize: 18, fontWeight: 600 }}>{tenant.name}</h1><Badge label={tenant.status} /></div>
            <div style={{ fontSize: 13, color: 'var(--text2)' }}>{prop.address || propName} · Unit {tenant.unit} · {propName}</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {isPrevious
              ? null
              : (editingInfo
                ? <SavingBtn onClick={saveInfo}>Save changes</SavingBtn>
                : <>
                    <button className="btn" onClick={() => { setEf({ ...tenant }); setEditingInfo(true); }}>{ic(I.pencil, 13)} Edit info</button>
                    <button className="btn" style={{ color: 'var(--red-txt)', borderColor: 'var(--red-txt)' }} onClick={() => setMoveOutModal({ date: nyTodayISO(), note: '' })}>Move Out</button>
                  </>
              )
            }
          </div>
        </div>
      )}
      {/* ── Tab bar ── */}
      <div style={{ display: 'flex', marginBottom: 20, borderBottom: '1px solid var(--border)' }}>
        {[['overview','Overview'],['ledger','Ledger'],['messages','Messages'],['activity','Activity']].map(([id, label]) => (
          <button key={id} onClick={() => setProfileTab(id)} style={{ flex: 1, background: 'none', border: 'none', borderBottom: profileTab === id ? '2px solid var(--accent)' : '2px solid transparent', padding: isMobile ? '7px 2px' : '8px 14px', fontSize: isMobile ? 11 : 13, fontWeight: profileTab === id ? 600 : 400, color: profileTab === id ? 'var(--accent)' : 'var(--text2)', cursor: 'pointer', marginBottom: -1, fontFamily: 'inherit', transition: 'color 0.12s', textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</button>
        ))}
      </div>

      {profileTab === 'overview' && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-title">{ic(I.user, 14)} Tenant information</div>
          {editingInfo ? (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 0 : 36, alignItems: 'start' }}>
                <div>
                  {/* ── Contact ── */}
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>Contact</div>
                  <div className="form-grid form-grid-2"><FI label="Full name" value={ef.name} onChange={v => setEf(p => ({ ...p, name: v }))} /><FI label="Unit" value={ef.unit} onChange={v => setEf(p => ({ ...p, unit: v }))} /></div>
                  <FS label="Status" value={ef.status} onChange={v => setEf(p => ({ ...p, status: v }))} options={['Occupied', 'Vacant', 'Previous']} />
                  <div className="form-grid form-grid-2"><FI label="Phone" value={ef.phone} onChange={v => setEf(p => ({ ...p, phone: v }))} /><FI label="Email" value={ef.email} onChange={v => setEf(p => ({ ...p, email: v }))} /></div>
                  <FI label="Alternative phone (optional)" value={ef.altPhone || ''} onChange={v => setEf(p => ({ ...p, altPhone: v }))} placeholder="e.g. (718) 555-0000" />

                  {/* ── Lease ── */}
                  <div style={{ borderTop: '1px solid var(--border)', margin: '16px 0 10px' }} />
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>Lease</div>
                  <div className="form-grid form-grid-2"><FI label="Lease start" value={ef.leaseStart} onChange={v => setEf(p => ({ ...p, leaseStart: v }))} type="date" /><FI label="Lease end" value={ef.leaseEnd} onChange={v => setEf(p => ({ ...p, leaseEnd: v }))} type="date" /></div>
                  {ef.leaseStart && ef.leaseStart > nyTodayISO() && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#92600a', background: '#fef9c3', border: '1px solid #fde68a', borderRadius: 8, padding: '6px 10px', marginBottom: 4 }}>
                      {ic(I.alert, 13, '#92600a')} Future-dated lease — start date is ahead of today. This is allowed.
                    </div>
                  )}
                </div>

                <div>
                  {/* ── Financials ── */}
                  {isMobile && <div style={{ borderTop: '1px solid var(--border)', margin: '16px 0 10px' }} />}
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>Financials</div>
                  <div className="form-grid form-grid-2"><FS label="Program" value={ef.program} onChange={v => setEf(p => ({ ...p, program: v }))} options={['Cash', 'Section 8 NYCHA', 'Section 8 HPD', 'Section 8 CVR', 'CityFheps NYCHA', 'CityFheps HPD', 'OHS Voucher']} /><FI label="Voucher #" value={ef.voucherNumber} onChange={v => setEf(p => ({ ...p, voucherNumber: v }))} /></div>
                  <div className="form-grid form-grid-2"><FS label="Payment type" value={ef.paymentType} onChange={v => setEf(p => ({ ...p, paymentType: v }))} options={['Cash', 'Check', 'Zelle', 'Money Order', 'App', 'No Portion']} /><FI label="Due day" value={ef.dueDay} onChange={v => setEf(p => ({ ...p, dueDay: v }))} type="number" /></div>
                  <div className="form-grid form-grid-2"><FI label="Tenant share ($)" value={ef.tenantShare} onChange={v => setEf(p => ({ ...p, tenantShare: v }))} type="number" /><FI label="Program share ($)" value={ef.programShare} onChange={v => setEf(p => ({ ...p, programShare: v }))} type="number" /></div>
                  <div className="form-grid form-grid-2">
                    <div className="form-group">
                      <label className="form-label">Parking</label>
                      <select className="form-input" value={ef.hasParking ? 'yes' : 'no'} onChange={e => setEf(p => ({ ...p, hasParking: e.target.value === 'yes' }))}>
                        <option value="no">No</option><option value="yes">Yes</option>
                      </select>
                    </div>
                    {ef.hasParking && <FI label="Parking amount ($)" value={ef.parkingFee} onChange={v => setEf(p => ({ ...p, parkingFee: v }))} type="number" />}
                  </div>
                  {ef.hasParking && <FI label="Parking start date" value={ef.parkingStart} onChange={v => setEf(p => ({ ...p, parkingStart: v }))} type="date" />}
                  <div className="form-grid form-grid-2">
                    <div className="form-group">
                      <label className="form-label">Washer / Dryer</label>
                      <select className="form-input" value={ef.hasWasherDryer ? 'yes' : 'no'} onChange={e => setEf(p => ({ ...p, hasWasherDryer: e.target.value === 'yes' }))}>
                        <option value="no">No</option><option value="yes">Yes</option>
                      </select>
                    </div>
                    {ef.hasWasherDryer && <FI label="W/D amount ($)" value={ef.washerDryerFee} onChange={v => setEf(p => ({ ...p, washerDryerFee: v }))} type="number" />}
                  </div>
                  {ef.hasWasherDryer && <FI label="W/D start date" value={ef.washerDryerStart} onChange={v => setEf(p => ({ ...p, washerDryerStart: v }))} type="date" />}
                  <div className="form-group">
                    <label className="form-label">Total rent (computed)</label>
                    <div style={{ padding: '8px 10px', background: 'var(--bg3)', borderRadius: 8, fontSize: 13, fontWeight: 600, color: 'var(--accent)' }}>
                      ${((+ef.tenantShare || 0) + (+ef.programShare || 0) + (ef.hasParking ? (+ef.parkingFee || 0) : 0) + (ef.hasWasherDryer ? (+ef.washerDryerFee || 0) : 0)).toLocaleString()}
                    </div>
                  </div>

                </div>
              </div>
              <div className="modal-actions"><button className="btn" onClick={() => setEditingInfo(false)}>Cancel</button><SavingBtn onClick={saveInfo}>Save changes</SavingBtn></div>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 0 : 36, alignItems: 'start' }}>
              <div>
              {/* ── Contact ── */}
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Contact</div>
              <InfoRow label="Full name" value={tenant.name} />
              {/* Unit type comes from the property's unit record (single source of truth) */}
              <InfoRow label="Unit" value={(() => { const ut = propUnit?.unitType || tenant.unitType; return `${tenant.unit}${ut ? ' (' + ut + ')' : ''}`; })()} />
              <InfoRow label="Phone">
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span className="info-val">{tenant.phone || '—'}</span>
                  {tenant.phone && <CopyBtn text={tenant.phone} />}
                </div>
              </InfoRow>
              <InfoRow label="Email">
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                  <span className="info-val" style={{ overflowWrap: 'anywhere' }}>{tenant.email || '—'}</span>
                  {tenant.email && <CopyBtn text={tenant.email} />}
                </div>
              </InfoRow>
              {tenant.altPhone && (
                <InfoRow label="Alt phone">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span className="info-val">{tenant.altPhone}</span>
                    <CopyBtn text={tenant.altPhone} />
                  </div>
                </InfoRow>
              )}

              {/* ── Lease ── */}
              <div style={{ borderTop: '1px solid var(--border)', margin: '16px 0 10px' }} />
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Lease</div>
              <InfoRow label="Lease start" value={fmtDate(tenant.leaseStart)} />
              <InfoRow label="Lease end">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <div className="info-val" style={{ color: tenant.leaseEnd && isExpiredISO(tenant.leaseEnd) ? 'var(--red-txt)' : tenant.leaseEnd && isLeaseExpiringSoon(tenant.leaseEnd) ? '#f97316' : 'var(--text1)', fontWeight: tenant.leaseEnd && (isExpiredISO(tenant.leaseEnd) || isLeaseExpiringSoon(tenant.leaseEnd)) ? 600 : 400 }}>
                    {tenant.leaseEnd ? `${isExpiredISO(tenant.leaseEnd) ? 'EXPIRED · ' : isLeaseExpiringSoon(tenant.leaseEnd) ? 'Expiring Soon · ' : ''}${fmtDate(tenant.leaseEnd)}` : '—'}
                  </div>
                </div>
              </InfoRow>
              <InfoRow label="Broker" value={tenant.brokerName} />
              <InfoRow label="Bed / Bath" value={(() => { const br = propUnit?.bedrooms ?? tenant.bedrooms; const ba = propUnit?.bathrooms ?? tenant.bathrooms; return (br != null || ba != null) ? `${br ?? 0} BR / ${ba ?? 0} BA` : '—'; })()} />
              </div>

              <div>
              {/* ── Financials ── */}
              {isMobile && <div style={{ borderTop: '1px solid var(--border)', margin: '16px 0 10px' }} />}
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Financials</div>
              <InfoRow label="Program" value={tenant.program || 'Cash'} />
              <InfoRow label="Payment type" value={tenant.paymentType} />
              {tenant.voucherNumber && <InfoRow label="Voucher #" value={tenant.voucherNumber} />}
              {(tenant.programShare > 0) && <InfoRow label="Program share" value={`$${(tenant.programShare || 0).toLocaleString()}`} />}
              {(tenant.tenantShare > 0) && <InfoRow label="Tenant share" value={`$${(tenant.tenantShare || 0).toLocaleString()}`} />}
              {tenant.hasParking && (
                <InfoRow label="Parking">
                  <div className="info-val">
                    ${(tenant.parkingFee || 0).toLocaleString()}
                    {tenant.parkingStart && <span style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 400 }}> · from {fmtDate(tenant.parkingStart)}</span>}
                  </div>
                </InfoRow>
              )}
              {tenant.hasWasherDryer && (
                <InfoRow label="Washer / Dryer">
                  <div className="info-val">
                    ${(tenant.washerDryerFee || 0).toLocaleString()}
                    {tenant.washerDryerStart && <span style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 400 }}> · from {fmtDate(tenant.washerDryerStart)}</span>}
                  </div>
                </InfoRow>
              )}
              <InfoRow label="Total rent">
                <div className="info-val" style={{ fontWeight: 700 }}>${(tenant.rent || 0).toLocaleString()}</div>
              </InfoRow>

              {/* ── Other Occupants ── */}
              <div style={{ borderTop: '1px solid var(--border)', margin: '16px 0 10px' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Other Occupants</div>
                {!isPrevious && (
                  <button className="btn-ghost" style={{ fontSize: 12 }} onClick={() => { setOccupantForm({ name: '', relationship: '', dob: '', phone: '' }); setOccupantModal('add'); }}>
                    {ic(I.plus, 12)} Add
                  </button>
                )}
              </div>
              {(tenant.householdMembers || []).length === 0
                ? <div style={{ fontSize: 13, color: 'var(--text3)' }}>No other occupants recorded.</div>
                : (tenant.householdMembers || []).map((m, idx) => (
                    <div key={m.id || idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: '1px solid var(--border)' }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>{m.name}</div>
                        <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2 }}>
                          {[m.relationship, m.phone, m.dob ? `DOB: ${fmtDate(m.dob)}` : ''].filter(Boolean).join(' · ') || '—'}
                        </div>
                      </div>
                      {!isPrevious && (
                        <KebabMenu items={[
                          { label: 'Edit', onClick: () => { setOccupantForm({ name: m.name, relationship: m.relationship || '', dob: m.dob || '', phone: m.phone || '' }); setOccupantModal({ index: idx, ...m }); } },
                          { label: 'Remove', danger: true, onClick: () => removeOccupant(idx) },
                        ]} />
                      )}
                    </div>
                  ))
              }
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── SMS Messages ── */}
      {profileTab === 'messages' && (
        <div className="card" style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', height: 'min(560px, 70vh)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <div className="card-title" style={{ margin: 0 }}>
              {ic('M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z', 14)} Messages
            </div>
            {tenant.phone && <span style={{ fontSize: 12, color: 'var(--text3)', fontWeight: 400 }}>{tenant.phone}</span>}
          </div>
          {!tenant.phone ? (
            <div className="empty">No phone number on file — add one in Tenant Information to enable SMS.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
              <div className="thin-scroll" style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6, padding: '4px 0', marginBottom: 12 }}>
                {smsMessages.length === 0 && (
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text3)', fontSize: 13 }}>No messages yet</div>
                )}
                {smsMessages.map(msg => {
                  const isOut = msg.direction === 'outbound';
                  const ts = msg.timestamp?.toDate ? msg.timestamp.toDate().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/New_York' }) : '';
                  return (
                    <div key={msg.id} style={{ display: 'flex', flexDirection: 'column', alignItems: isOut ? 'flex-end' : 'flex-start', padding: '0 4px' }}>
                      <div style={{ maxWidth: '72%', padding: '8px 12px', borderRadius: 16, fontSize: 13, lineHeight: 1.4, background: isOut ? 'var(--accent)' : 'var(--bg3)', color: isOut ? '#fff' : 'var(--text1)', borderBottomRightRadius: isOut ? 4 : 16, borderBottomLeftRadius: isOut ? 16 : 4 }}>
                        {msg.body}
                      </div>
                      {ts && <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 3, padding: '0 4px' }}>{ts}</div>}
                    </div>
                  );
                })}
                <div ref={msgEndRef} />
              </div>
              <div style={{ display: 'flex', gap: 8, borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 'auto' }}>
                <input className="form-input" style={{ flex: 1 }} placeholder="Type a message…" value={smsInput}
                  onChange={e => setSmsInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendSmsMsg(); } }} />
                <button className="btn btn-primary" style={{ flexShrink: 0 }} disabled={!smsInput.trim() || smsSending} onClick={sendSmsMsg}>
                  {smsSending ? 'Sending…' : ic('M12 19l9 2-9-18-9 18 9-2zm0 0v-8', 14, '#fff')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Notes ── */}
      {profileTab === 'overview' && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div className="card-title" style={{ margin: 0 }}>{ic(I.clipboard, 14)} Notes</div>
            {editingNotes
              ? <div style={{ display: 'flex', gap: 6 }}><button className="btn-ghost" onClick={() => setEditingNotes(false)}>Cancel</button><SavingBtn onClick={saveNotes} style={{ fontSize: 12, padding: '4px 10px' }}>Save</SavingBtn></div>
              : <button className="btn-ghost" onClick={() => { setNoteDraft(tenant.notes || ''); setEditingNotes(true); }}>{ic(I.pencil, 12)} Edit</button>}
          </div>
          {editingNotes
            ? <textarea className="form-textarea" style={{ minHeight: 160 }} value={noteDraft} onChange={ev => setNoteDraft(ev.target.value)} placeholder="Add notes…" />
            : <div style={{ fontSize: 13, color: tenant.notes ? 'var(--text1)' : 'var(--text3)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}><Linkify text={tenant.notes || 'No notes yet.'} /></div>}
        </div>
      )}

      {/* ── Occupant modal ── */}
      {occupantModal && (
        <Modal title={occupantModal === 'add' ? 'Add occupant' : 'Edit occupant'} onClose={() => setOccupantModal(null)}>
          <FI label="Full name" value={occupantForm.name} onChange={v => setOccupantForm(p => ({ ...p, name: v }))} placeholder="e.g. John Doe" />
          <FS label="Relationship" value={occupantForm.relationship} onChange={v => setOccupantForm(p => ({ ...p, relationship: v }))}
            options={['Spouse', 'Partner', 'Child', 'Parent', 'Sibling', 'Roommate', 'Other']} placeholder="— Select relationship —" />
          <div className="form-grid form-grid-2">
            <FI label="Contact number (optional)" value={occupantForm.phone} onChange={v => setOccupantForm(p => ({ ...p, phone: v }))} placeholder="e.g. (347) 555-0000" />
            <FI label="Date of birth (optional)" value={occupantForm.dob} onChange={v => setOccupantForm(p => ({ ...p, dob: v }))} type="date" />
          </div>
          <div className="modal-actions">
            <button className="btn" onClick={() => setOccupantModal(null)}>Cancel</button>
            <SavingBtn onClick={saveOccupant} disabled={!occupantForm.name.trim()}>Save</SavingBtn>
          </div>
        </Modal>
      )}
      {profileTab === 'ledger' && (
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div className="card-title" style={{ margin: 0 }}>{ic(I.dollar, 14)} Ledger</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-ghost" onClick={() => { setExportMode('all'); setExportSel(new Set()); setExportLedgerModal(true); }}>
              <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>
              Export PDF
            </button>
            {!isPrevious && (
              <button className="btn-ghost" onClick={() => { setEntries(splitEntries(nyTodayISO())); setAddEntry(true); }}>{ic(I.plus, 12)} Add entry</button>
            )}
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: isMobile ? '12px 18px' : '14px 32px', padding: '2px 0 14px', borderBottom: '1px solid var(--border)', marginBottom: 14 }}>
          {[
            ['Program charged', programCharged, 'var(--text1)'],
            ['Program paid', programPaid, 'var(--text1)'],
            ['Program balance', Math.max(0, programCharged - programPaid), (programCharged - programPaid) > 0 ? 'var(--red-txt)' : 'var(--text1)'],
            ['Tenant charged', tenantCharged, 'var(--text1)'],
            ['Tenant paid', tenantPaid, 'var(--text1)'],
            ['Tenant balance', Math.max(0, tenantCharged - tenantPaid), (tenantCharged - tenantPaid) > 0 ? 'var(--red-txt)' : 'var(--text1)'],
          ].map(([l, v, c]) => (
            <div key={l}>
              <div className="info-label">{l}</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: c }}>${(+v).toLocaleString()}</div>
            </div>
          ))}
        </div>
        {(() => {
          const filteredLedger = ledgerPayerF === 'all' ? tenantLedger : tenantLedger.filter(l => (l.payer || 'Tenant') === ledgerPayerF);
          const visibleRows = showAllLedger ? filteredLedger : filteredLedger.slice(0, 10);
          const hiddenCount = filteredLedger.length - 10;
          return (
            <>
              <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                {[['all', 'All'], ['Tenant', 'Tenant'], ['Program', 'Program']].map(([val, label]) => (
                  <button key={val} onClick={() => { setLedgerPayerF(val); setShowAllLedger(false); }}
                    style={{ fontSize: 11, padding: '4px 12px', borderRadius: 0, border: `1px solid ${ledgerPayerF === val ? 'var(--accent)' : 'var(--border)'}`, background: ledgerPayerF === val ? 'var(--accent-bg)' : 'var(--bg2)', color: ledgerPayerF === val ? 'var(--accent)' : 'var(--text2)', fontWeight: ledgerPayerF === val ? 600 : 400, cursor: 'pointer', fontFamily: 'inherit' }}>
                    {label}
                  </button>
                ))}
              </div>
              <table className="ledger-tbl">
                <thead><tr><th>Date</th><th>Description</th><th>Payer</th><th>Charge</th><th>Amount Paid</th><th>Balance</th><th>Status</th><th>Payment</th><th style={{ width: 32 }}></th></tr></thead>
                <tbody>
                  {visibleRows.length === 0 && (
                    <tr><td colSpan={9} style={{ textAlign: 'center', color: 'var(--text3)', padding: 16 }}>No {ledgerPayerF === 'all' ? '' : ledgerPayerF + ' '}entries.</td></tr>
                  )}
                  {visibleRows.map(l => {
                    const isCharge = l.type === 'charge';
                    const isProgram = l.payer === 'Program';
                    const bal = Math.max(0, (+l.amount || 0) - (+l.paidAmount || 0));
                    return (
                      <tr key={l.id}>
                        <td style={{ color: 'var(--text2)', whiteSpace: 'nowrap' }}>{fmtDate(l.date)}</td>
                        <td>
                          {isCharge ? (
                            <span
                              onClick={() => setPayHistoryEntry(l)}
                              style={{ cursor: 'pointer', borderBottom: '1px dashed var(--text3)', paddingBottom: 1 }}
                              title="View payment history"
                            >
                              {l.description}
                            </span>
                          ) : l.description}
                        </td>
                        <td><span className={`badge ${isProgram ? 'badge-blue' : 'badge-amber'}`}>{isProgram ? 'Program' : 'Tenant'}</span></td>
                        <td style={{ fontWeight: 500, whiteSpace: 'nowrap', color: 'var(--red-txt)' }}>{isCharge ? `$${(+l.amount || 0).toLocaleString()}` : '—'}</td>
                        <td style={{ fontWeight: 500, whiteSpace: 'nowrap', color: 'var(--green-txt)' }}>
                          {isCharge ? (l.paidAmount ? `$${(+l.paidAmount).toLocaleString()}` : '—') : `$${(+l.amount || 0).toLocaleString()}`}
                        </td>
                        <td style={{ fontWeight: 500, whiteSpace: 'nowrap', color: isCharge && bal > 0 ? 'var(--red-txt)' : 'var(--text2)' }}>
                          {isCharge ? `$${bal.toLocaleString()}` : '—'}
                        </td>
                        <td><Badge label={l.status} /></td>
                        <td>{isCharge && l.status !== 'Paid' && <button className="btn-ghost" style={{ fontSize: 11, color: 'var(--accent)' }} onClick={() => setPayModal({ entry: l, customAmt: '' })}>Pay</button>}</td>
                        <td><KebabMenu items={[{ label: 'Edit', onClick: () => openEditEntry(l) }, { label: 'Duplicate', onClick: () => dupLedger(l) }, { label: 'Delete', danger: true, onClick: () => delEntry(l.id) }]} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {!showAllLedger && hiddenCount > 0 && (
                <div style={{ textAlign: 'center', paddingTop: 10 }}>
                  <button className="btn-ghost" style={{ fontSize: 12, color: 'var(--accent)' }} onClick={() => setShowAllLedger(true)}>
                    View all {filteredLedger.length} entries ({hiddenCount} more)
                  </button>
                </div>
              )}
              {showAllLedger && filteredLedger.length > 10 && (
                <div style={{ textAlign: 'center', paddingTop: 10 }}>
                  <button className="btn-ghost" style={{ fontSize: 12, color: 'var(--text3)' }} onClick={() => setShowAllLedger(false)}>
                    Show less
                  </button>
                </div>
              )}
            </>
          );
        })()}
      </div>
      )}
      {payHistoryEntry && (() => {
        const e = payHistoryEntry;
        const fmt$ = n => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
        const fmtTs = ts => ts ? new Date(ts).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';
        const history = e.paymentHistory || [];
        const hasLegacy = (!history.length) && (+e.paidAmount || 0) > 0;
        const totalPaid = +e.paidAmount || 0;
        const balance = Math.max(0, (+e.amount || 0) - totalPaid);
        return (
          <Modal title="Payment History" onClose={() => setPayHistoryEntry(null)} lg>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text1)', marginBottom: 4 }}>{e.description}</div>
              <div style={{ display: 'flex', gap: 20, fontSize: 12, color: 'var(--text2)' }}>
                <span>Charge date: <strong style={{ color: 'var(--text1)' }}>{e.date}</strong></span>
                <span>Total charge: <strong style={{ color: 'var(--red-txt)' }}>{fmt$(e.amount)}</strong></span>
                <span>Total paid: <strong style={{ color: 'var(--green-txt)' }}>{fmt$(totalPaid)}</strong></span>
                <span>Balance: <strong style={{ color: balance > 0 ? 'var(--red-txt)' : 'var(--green-txt)' }}>{fmt$(balance)}</strong></span>
              </div>
            </div>
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14 }}>
              {(history.length > 0 || hasLegacy) ? (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      {['#', 'Amount Paid', 'Entered By', 'Date & Time', 'Proof'].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: '5px 10px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text2)', fontWeight: 600 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {history.length > 0 ? history.map((p, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--border)', background: i % 2 === 0 ? 'transparent' : 'var(--bg2)' }}>
                        <td style={{ padding: '9px 10px', color: 'var(--text3)', fontWeight: 600 }}>#{i + 1}</td>
                        <td style={{ padding: '9px 10px', fontWeight: 700, color: 'var(--green-txt)' }}>+{fmt$(p.amount)}</td>
                        <td style={{ padding: '9px 10px', color: 'var(--text1)' }}>{p.by || '—'}</td>
                        <td style={{ padding: '9px 10px', color: 'var(--text2)' }}>{fmtTs(p.at)}</td>
                        <td style={{ padding: '9px 10px' }}>
                          {p.proofUrl ? (
                            <a href={p.proofUrl} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--accent)', textDecoration: 'none', fontWeight: 600 }}>
                              <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                              {p.proofName || 'View'}
                            </a>
                          ) : <span style={{ color: 'var(--text3)', fontSize: 11 }}>—</span>}
                        </td>
                      </tr>
                    )) : (
                      <tr style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '9px 10px', color: 'var(--text3)', fontWeight: 600 }}>#1</td>
                        <td style={{ padding: '9px 10px', fontWeight: 700, color: 'var(--green-txt)' }}>+{fmt$(e.paidAmount)}</td>
                        <td style={{ padding: '9px 10px', color: 'var(--text1)' }}>{e.processedBy || '—'}</td>
                        <td style={{ padding: '9px 10px', color: 'var(--text2)', fontStyle: 'italic' }}>—</td>
                        <td style={{ padding: '9px 10px', color: 'var(--text3)', fontSize: 11 }}>—</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              ) : (
                <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text3)', fontSize: 13 }}>
                  No payments recorded for this charge yet.
                </div>
              )}
            </div>
            {history.length > 1 && (
              <div style={{ marginTop: 12, padding: '10px 14px', background: 'var(--bg2)', borderRadius: 8, fontSize: 12, color: 'var(--text2)', display: 'flex', justifyContent: 'space-between' }}>
                <span>{history.length} payments recorded</span>
                <span>Total paid: <strong style={{ color: 'var(--green-txt)' }}>{fmt$(totalPaid)}</strong> of <strong>{fmt$(e.amount)}</strong></span>
              </div>
            )}
            <div className="modal-actions" style={{ marginTop: 14 }}><button className="btn" onClick={() => setPayHistoryEntry(null)}>Close</button></div>
          </Modal>
        );
      })()}
      {exportLedgerModal && (
        <Modal title="Export ledger PDF" onClose={() => setExportLedgerModal(false)} lg>
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            {[['all', 'All entries'], ['specific', 'Select specific entries']].map(([val, label]) => (
              <button key={val} onClick={() => setExportMode(val)}
                style={{ flex: 1, padding: '10px 14px', borderRadius: 0, border: `2px solid ${exportMode === val ? 'var(--accent)' : 'var(--border)'}`, background: exportMode === val ? 'var(--accent-bg)' : 'var(--bg2)', color: exportMode === val ? 'var(--accent)' : 'var(--text1)', fontWeight: exportMode === val ? 600 : 400, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13 }}>
                {label}
              </button>
            ))}
          </div>
          {exportMode === 'specific' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
                <button className="btn-ghost" style={{ fontSize: 12 }} onClick={() => setExportSel(exportSel.size === tenantLedger.length ? new Set() : new Set(tenantLedger.map(l => l.id)))}>
                  {exportSel.size === tenantLedger.length ? 'Deselect all' : `Select all (${tenantLedger.length})`}
                </button>
              </div>
              <div className="thin-scroll" style={{ border: '1px solid var(--border)', maxHeight: 320, overflowY: 'auto' }}>
                {tenantLedger.map(l => {
                  const bal = Math.max(0, (+l.amount || 0) - (+l.paidAmount || 0));
                  return (
                    <label key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}>
                      <input type="checkbox" checked={exportSel.has(l.id)} onChange={() => setExportSel(prev => { const n = new Set(prev); n.has(l.id) ? n.delete(l.id) : n.add(l.id); return n; })} style={{ accentColor: 'var(--accent)', width: 15, height: 15, flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0, fontSize: 13 }}>
                        <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.description || '—'}</div>
                        <div style={{ fontSize: 11, color: 'var(--text3)' }}>{fmtDate(l.date)} · {l.payer || 'Tenant'} · ${(+l.amount || 0).toLocaleString()}{bal > 0 ? ` · Balance $${bal.toLocaleString()}` : ''}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
              <div style={{ fontSize: 12, marginTop: 6, color: exportSel.size > 0 ? 'var(--green-txt)' : 'var(--text3)' }}>
                {exportSel.size > 0 ? `${exportSel.size} entr${exportSel.size !== 1 ? 'ies' : 'y'} selected` : 'No entries selected'}
              </div>
            </div>
          )}
          <div className="modal-actions">
            <button className="btn" onClick={() => setExportLedgerModal(false)}>Cancel</button>
            <button className="btn btn-primary" disabled={exportMode === 'specific' && exportSel.size === 0}
              onClick={() => { const rows = exportMode === 'all' ? tenantLedger : tenantLedger.filter(l => exportSel.has(l.id)); exportLedgerPDF(rows); setExportLedgerModal(false); }}>
              Export PDF
            </button>
          </div>
        </Modal>
      )}

      {addEntry && (
        <Modal title="Add ledger entries" onClose={() => { setAddEntry(false); setEntries([blankEntry()]); }} lg>
          {entries.map((e, i) => (
            <div key={i} style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, padding: 14, marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)' }}>Entry {i + 1}</span>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="btn-ghost" style={{ fontSize: 11 }} onClick={() => duplicateRow(i)}>Duplicate</button>
                  {entries.length > 1 && <button className="btn-ghost" style={{ fontSize: 11, color: 'var(--red-txt)' }} onClick={() => removeRow(i)}>Remove</button>}
                </div>
              </div>
              <FI label="Date" value={e.date} onChange={v => updateEntry(i, 'date', v)} type="date" />
              <FI label="Description" value={e.description} onChange={v => updateEntry(i, 'description', v)} placeholder="Rent - Month Year" />
              <div className="form-group">
                <label className="form-label">Payer</label>
                <select className="form-input" value={e.payer || 'Tenant'} onChange={ev => updateEntry(i, 'payer', ev.target.value)}>
                  <option value="Tenant">Tenant</option>
                  <option value="Program">Program (Gov't)</option>
                </select>
              </div>
              <FI label="Amount ($)" value={e.amount} onChange={v => updateEntry(i, 'amount', v)} type="number" />
            </div>
          ))}
          <button className="btn-ghost" style={{ marginBottom: 10, fontSize: 12 }} onClick={addRow}>{ic(I.plus, 12)} Add another entry</button>
          <div className="modal-actions"><button className="btn" onClick={() => { setAddEntry(false); setEntries([blankEntry()]); }}>Cancel</button><SavingBtn onClick={saveEntry}>Save {entries.length > 1 ? `${entries.length} entries` : 'entry'}</SavingBtn></div>
        </Modal>
      )}
      {editEntry && (
        <Modal title="Edit ledger entry" onClose={() => setEditEntry(null)}>
          <div className="form-group">
            <label className="form-label">Entry type</label>
            <select className="form-input" value={editEntry.type} onChange={e => setEditEntry(p => ({ ...p, type: e.target.value }))}>
              <option value="charge">Charge only</option>
              <option value="payment">Payment</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Payer</label>
            <select className="form-input" value={editEntry.payer || 'Tenant'} onChange={e => setEditEntry(p => ({ ...p, payer: e.target.value }))}>
              <option value="Tenant">Tenant</option>
              <option value="Program">Program (Gov't)</option>
            </select>
          </div>
          <div className="form-grid form-grid-2">
            <FI label="Date" value={editEntry.date} onChange={v => setEditEntry(p => ({ ...p, date: v }))} type="date" />
            <FI label="Amount ($)" value={editEntry.amount} onChange={v => setEditEntry(p => ({ ...p, amount: v }))} type="number" />
          </div>
          <FI label="Description" value={editEntry.description} onChange={v => setEditEntry(p => ({ ...p, description: v }))} />
          <div className="modal-actions"><button className="btn" onClick={() => setEditEntry(null)}>Cancel</button><SavingBtn onClick={saveEditEntry}>Save changes</SavingBtn></div>
        </Modal>
      )}
      {payModal && (() => {
        const pmEntry = payModal.entry;
        const pmCharge = +pmEntry.amount || 0;
        const pmPaid = +pmEntry.paidAmount || 0;
        const pmRemaining = Math.max(0, pmCharge - pmPaid);
        return (
          <Modal title="Record payment" onClose={() => setPayModal(null)}>
            <div style={{ marginBottom: 14, fontSize: 13, color: 'var(--text2)' }}>
              <strong style={{ color: 'var(--text1)' }}>{pmEntry.description}</strong>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 16 }}>
              <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px' }}>
                <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Total Charge</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text1)' }}>${pmCharge.toLocaleString()}</div>
              </div>
              <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px' }}>
                <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Already Paid</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--green-txt)' }}>${pmPaid.toLocaleString()}</div>
              </div>
              <div style={{ background: 'var(--red-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px' }}>
                <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Remaining</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--red-txt)' }}>${pmRemaining.toLocaleString()}</div>
              </div>
            </div>
            {/* Proof of payment upload */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Proof of Payment (optional)</div>
              <label
                onDragOver={e => { e.preventDefault(); if (!payDragOver) setPayDragOver(true); }}
                onDragLeave={e => { e.preventDefault(); setPayDragOver(false); }}
                onDrop={e => { e.preventDefault(); setPayDragOver(false); const f = e.dataTransfer.files && e.dataTransfer.files[0]; if (f) setPayModal(p => ({ ...p, proofFile: f })); }}
                style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', border: `1px dashed ${payDragOver ? 'var(--accent, #2563eb)' : 'var(--border)'}`, borderRadius: 8, padding: '10px 14px', background: payDragOver ? 'var(--accent-bg, rgba(37,99,235,0.08))' : 'var(--bg2)', transition: 'border-color .12s, background .12s' }}>
                <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--text3)', flexShrink: 0 }}><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"/></svg>
                <span style={{ fontSize: 12, color: payModal.proofFile ? 'var(--text1)' : 'var(--text3)' }}>
                  {payModal.proofFile ? payModal.proofFile.name : (payDragOver ? 'Drop the file to attach' : 'Drag & drop, paste (Ctrl+V), or click to attach (PNG, JPG, PDF)')}
                </span>
                <input type="file" accept="image/*,.pdf" style={{ display: 'none' }} onChange={e => { if (e.target.files[0]) setPayModal(p => ({ ...p, proofFile: e.target.files[0] })); }} />
              </label>
              {payModal.proofFile && (
                <button className="btn-ghost" style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }} onClick={() => setPayModal(p => ({ ...p, proofFile: null }))}>Remove</button>
              )}
            </div>
            <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', marginBottom: 12 }} onClick={() => recordPayment(pmEntry, pmRemaining, payModal.proofFile)} disabled={pmRemaining <= 0 || proofUploading}>
              {proofUploading ? 'Uploading proof…' : `Mark fully paid — pay remaining $${pmRemaining.toLocaleString()}`}
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
              <span style={{ fontSize: 11, color: 'var(--text3)' }}>or enter partial amount</span>
              <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input className="form-input" type="number" placeholder={`Amount up to $${pmRemaining.toLocaleString()}`} min="0.01" max={pmRemaining} value={payModal.customAmt} onChange={e => setPayModal(p => ({ ...p, customAmt: e.target.value }))} style={{ flex: 1 }} />
              <button className="btn btn-primary" onClick={() => recordPayment(pmEntry, payModal.customAmt, payModal.proofFile)} disabled={!payModal.customAmt || +payModal.customAmt <= 0 || +payModal.customAmt > pmRemaining || proofUploading}>
                {proofUploading ? 'Uploading…' : 'Record'}
              </button>
            </div>
            {payModal.customAmt && +payModal.customAmt > pmRemaining && (
              <div style={{ marginTop: 8, fontSize: 12, color: 'var(--amber-txt)' }}>Amount exceeds remaining balance — will be capped at ${pmRemaining.toLocaleString()}</div>
            )}
            <div className="modal-actions" style={{ marginTop: 16 }}><button className="btn" onClick={() => setPayModal(null)}>Cancel</button></div>
          </Modal>
        );
      })()}
      {receiptModal && (
        <Modal title="Send Receipt" onClose={() => setReceiptModal(null)} lg>
          <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 12 }}>Select the entries to include in the receipt:</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16, maxHeight: 300, overflowY: 'auto' }}>
            {tenantLedger.map(l => {
              const checked = receiptModal.selectedIds.has(l.id);
              return (
                <label key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: checked ? 'var(--accent-bg)' : 'var(--bg2)', border: `1px solid ${checked ? 'var(--accent)' : 'var(--border)'}`, borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>
                  <input type="checkbox" checked={checked} onChange={() => {
                    const next = new Set(receiptModal.selectedIds);
                    checked ? next.delete(l.id) : next.add(l.id);
                    setReceiptModal(p => ({ ...p, selectedIds: next }));
                  }} />
                  <span style={{ flex: 1 }}>{l.description}</span>
                  <span style={{ color: 'var(--text2)', whiteSpace: 'nowrap' }}>{fmtDate(l.date)}</span>
                  <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>${(+l.amount || 0).toLocaleString()}</span>
                  <span className={`badge ${l.payer === 'Program' ? 'badge-blue' : 'badge-amber'}`}>{l.payer || 'Tenant'}</span>
                </label>
              );
            })}
          </div>
          {receiptModal.selectedIds.size > 0 && (() => {
            const selected = tenantLedger.filter(l => receiptModal.selectedIds.has(l.id));
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }}
                  onClick={() => generateReceiptPDF(selected)}>
                  <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>
                  Download PDF Receipt ({receiptModal.selectedIds.size} {receiptModal.selectedIds.size === 1 ? 'entry' : 'entries'})
                </button>
                {tenant.email && (
                  <button className="btn" style={{ width: '100%', justifyContent: 'center' }}
                    onClick={() => {
                      generateReceiptPDF(selected);
                      const subject = encodeURIComponent(`Payment Receipt – ${tenant.name}`);
                      const body = encodeURIComponent(`Dear ${tenant.name},\n\nPlease find attached your payment receipt.\n\nThank you for your payment.\n\nBest regards,\n${prop.name || 'Amar Management'}`);
                      window.open(`https://mail.google.com/mail/?view=cm&to=${encodeURIComponent(tenant.email)}&su=${subject}&body=${body}`, '_blank');
                    }}>
                    <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                    Download PDF &amp; Open Gmail Draft
                  </button>
                )}
              </div>
            );
          })()}
          <div className="modal-actions" style={{ marginTop: 12 }}><button className="btn" onClick={() => setReceiptModal(null)}>Close</button></div>
        </Modal>
      )}
      {showLedgerAudit && (() => {
        const fmtTs = ts => ts ? new Date(ts).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';
        const fmt$ = n => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
        // Build flat event list using ALL entries (including deleted)
        const events = [];
        for (const l of tenantLedgerAll) {
          // Original entry creation row
          events.push({
            key: l.id,
            chargeDate: l.date,
            description: l.description || '—',
            eventType: 'Charge',
            amount: fmt$(l.amount),
            isPayment: false,
            isDeleted: !!l.deleted,
            enteredBy: l.autoGenerated ? 'Auto (System)' : (l.processedBy || '—'),
            isAuto: !!l.autoGenerated,
            enteredOn: fmtTs(l.createdAt),
            sortKey: l.createdAt || l.date || '',
          });
          // Payment events
          if (l.paymentHistory && l.paymentHistory.length > 0) {
            l.paymentHistory.forEach((p, pi) => {
              events.push({
                key: `${l.id}-pay-${pi}`,
                chargeDate: l.date,
                description: l.description || '—',
                eventType: 'Payment',
                amount: fmt$(p.amount),
                isPayment: true,
                isDeleted: !!l.deleted,
                enteredBy: p.by || '—',
                isAuto: false,
                enteredOn: fmtTs(p.at),
                sortKey: p.at || '',
              });
            });
          } else if ((+l.paidAmount || 0) > 0 && !l.paymentHistory) {
            events.push({
              key: `${l.id}-legacy-pay`,
              chargeDate: l.date,
              description: l.description || '—',
              eventType: 'Payment',
              amount: fmt$(l.paidAmount),
              isPayment: true,
              isDeleted: !!l.deleted,
              enteredBy: l.processedBy || '—',
              isAuto: false,
              enteredOn: '—',
              sortKey: '',
            });
          }
          // Edit history events
          if (l.editHistory && l.editHistory.length > 0) {
            l.editHistory.forEach((edit, ei) => {
              const changeDesc = edit.changes.map(c => `${c.field}: "${c.before}" → "${c.after}"`).join(' · ');
              events.push({
                key: `${l.id}-edit-${ei}`,
                chargeDate: l.date,
                description: changeDesc,
                eventType: 'Edited',
                amount: '—',
                isPayment: false,
                isDeleted: !!l.deleted,
                isEdit: true,
                enteredBy: edit.by || '—',
                isAuto: false,
                enteredOn: fmtTs(edit.at),
                sortKey: edit.at || '',
              });
            });
          }
          // Deletion event
          if (l.deleted) {
            events.push({
              key: `${l.id}-deleted`,
              chargeDate: l.date,
              description: l.description || '—',
              eventType: 'Deleted',
              amount: fmt$(l.amount),
              isPayment: false,
              isDeleted: true,
              isDeletion: true,
              enteredBy: l.deletedBy || '—',
              isAuto: false,
              enteredOn: fmtTs(l.deletedAt),
              sortKey: l.deletedAt || '',
            });
          }
        }
        events.sort((a, b) => b.sortKey.localeCompare(a.sortKey));
        return (
          <Modal title="Ledger Audit Log" onClose={() => setShowLedgerAudit(false)} xl>
            <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 14 }}>
              Full history for <strong style={{ color: 'var(--text1)' }}>{tenant.name}</strong> — {events.length} event{events.length !== 1 ? 's' : ''}
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    {['Charge Date', 'Description', 'Type', 'Amount', 'Entered By', 'Entered On'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '6px 10px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text2)', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {events.map((ev, i) => {
                    const rowBg = ev.isDeletion
                      ? 'var(--red-bg, #fef2f2)'
                      : ev.isDeleted && !ev.isDeletion
                        ? (i % 2 === 0 ? 'rgba(239,68,68,0.04)' : 'rgba(239,68,68,0.08)')
                        : (i % 2 === 0 ? 'transparent' : 'var(--bg2)');
                    const badgeBg = ev.isDeletion ? 'var(--red-bg, #fee2e2)' : ev.isEdit ? 'var(--blue-bg, #eff6ff)' : ev.isPayment ? 'var(--green-bg, #dcfce7)' : 'var(--amber-bg, #fef3c7)';
                    const badgeColor = ev.isDeletion ? 'var(--red-txt)' : ev.isEdit ? 'var(--blue-txt, #1d4ed8)' : ev.isPayment ? 'var(--green-txt, #166534)' : 'var(--amber-txt, #92400e)';
                    const textColor = ev.isDeleted ? 'var(--text3)' : 'var(--text1)';
                    return (
                      <tr key={ev.key} style={{ borderBottom: '1px solid var(--border)', background: rowBg }}>
                        <td style={{ padding: '8px 10px', whiteSpace: 'nowrap', color: textColor, textDecoration: ev.isDeleted && !ev.isDeletion ? 'line-through' : 'none' }}>{ev.chargeDate || '—'}</td>
                        <td style={{ padding: '8px 10px', color: textColor, textDecoration: ev.isDeleted && !ev.isDeletion ? 'line-through' : 'none' }}>{ev.description}</td>
                        <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                          <span style={{ display: 'inline-block', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4, background: badgeBg, color: badgeColor }}>
                            {ev.eventType}
                          </span>
                        </td>
                        <td style={{ padding: '8px 10px', whiteSpace: 'nowrap', fontWeight: 600, color: ev.isDeletion ? 'var(--red-txt)' : ev.isPayment ? 'var(--green-txt)' : textColor, textDecoration: ev.isDeleted && !ev.isDeletion ? 'line-through' : 'none' }}>
                          {ev.isPayment ? '+' : ''}{ev.amount}
                        </td>
                        <td style={{ padding: '8px 10px', color: ev.isAuto ? 'var(--blue-txt, #1d4ed8)' : ev.isDeletion ? 'var(--red-txt)' : 'var(--text1)', whiteSpace: 'nowrap' }}>{ev.enteredBy}</td>
                        <td style={{ padding: '8px 10px', color: 'var(--text2)', whiteSpace: 'nowrap' }}>{ev.enteredOn}</td>
                      </tr>
                    );
                  })}
                  {events.length === 0 && (
                    <tr><td colSpan={6} style={{ padding: '20px 10px', textAlign: 'center', color: 'var(--text3)' }}>No ledger entries found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="modal-actions" style={{ marginTop: 14 }}>
              <button className="btn btn-primary" onClick={exportAuditLogPDF}>
                <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>
                Download PDF
              </button>
              <button className="btn" onClick={() => setShowLedgerAudit(false)}>Close</button>
            </div>
          </Modal>
        );
      })()}
      {moveOutModal && (
        <Modal title="Move Out Tenant" onClose={() => setMoveOutModal(null)}>
          <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
            This will mark <strong style={{ color: 'var(--text1)' }}>{tenant.name}</strong> as a previous tenant. Their ledger history will be preserved and the profile will become read-only.
          </div>
          <FI label="Move-out date" value={moveOutModal.date} onChange={v => setMoveOutModal(p => ({ ...p, date: v }))} type="date" />
          <FI label="Note (optional)" value={moveOutModal.note} onChange={v => setMoveOutModal(p => ({ ...p, note: v }))} placeholder="e.g. Lease ended, Early termination…" />
          <div className="modal-actions">
            <button className="btn" onClick={() => setMoveOutModal(null)}>Cancel</button>
            <button className="btn btn-primary" style={{ background: 'var(--red-txt)', borderColor: 'var(--red-txt)' }} onClick={confirmMoveOut}>Confirm Move Out</button>
          </div>
        </Modal>
      )}

      {/* ── Email choice modal ── */}
      {/* ── Renewal offer modal ── */}
      {renewalModal && (() => {
        const prop = properties.find(p => p.id === tenant.propertyId) || {};
        const currentRent = (tenant.programShare || 0) + (tenant.tenantShare || 0);
        const pct1 = parseFloat(renewalForm.pct1) || 0;
        const pct2 = parseFloat(renewalForm.pct2) || 0;
        const yr1Amt = Math.round(currentRent * (1 + pct1 / 100));
        const yr2Amt = Math.round(yr1Amt * (1 + pct2 / 100));
        const addDaysISO = (iso, n) => { const d = new Date(iso + 'T12:00:00'); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
        const addYrsMinusDay = (iso, yrs) => { const d = new Date(iso + 'T12:00:00'); d.setFullYear(d.getFullYear() + yrs); d.setDate(d.getDate() - 1); return d.toISOString().slice(0, 10); };
        const leaseEnd = tenant.leaseEnd || '';
        const newStart = leaseEnd ? addDaysISO(leaseEnd, 1) : '';
        const yr1End = newStart ? addYrsMinusDay(newStart, 1) : '';
        const yr2End = newStart ? addYrsMinusDay(newStart, 2) : '';
        const addr = prop.address || propName;
        const fmt$ = n => `$${n.toLocaleString()}`;

        const openGmail = () => {
          const subject = `Lease Renewal Notice - ${tenant.name}, Unit ${tenant.unit}`;
          const body = `Dear ${tenant.name},

I hope this message finds you well. Your current lease for the unit at ${addr}, Unit ${tenant.unit} is coming up for renewal on ${fmtDate(leaseEnd)}. We have truly valued having you as a tenant and would love for you to continue being part of our community.

We would like to offer you the opportunity to renew your lease under the following terms:

Current monthly rent: ${fmt$(currentRent)}
1 year lease renewal contract: ${fmt$(yr1Amt)}${pct1 > 0 ? ` (${pct1}% increase)` : ''}
2 year lease renewal contract: Year 1 — ${fmt$(yr1Amt)}${pct1 > 0 ? ` (${pct1}% increase)` : ''} / Year 2 — ${fmt$(yr2Amt)}${pct2 > 0 ? ` (${pct2}% increase)` : ''}
Lease Renewal Start Date: ${fmtDate(newStart)}
Lease Renewal End Dates: 1-year — ${fmtDate(yr1End)} / 2-year — ${fmtDate(yr2End)}

To accept this renewal offer, please respond to this email or contact us. If we do not hear from you, we will assume you are not renewing your lease and will begin the process of re-listing the unit.

If you have any questions or would like to discuss the terms, please don't hesitate to reach out. We are happy to talk.

Warm regards,
Amar Management`;
          const gmailUrl = `https://mail.google.com/mail/?view=cm${tenant.email ? `&to=${encodeURIComponent(tenant.email)}` : ''}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
          window.open(gmailUrl, '_blank');
        };

        return (
          <Modal title="Renewal Offer" onClose={() => setRenewalModal(false)} lg>
            {/* Current rent summary */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 18 }}>
              {(tenant.programShare > 0) && (
                <div style={{ flex: 1, background: 'var(--blue-bg)', borderRadius: 8, padding: '10px 14px' }}>
                  <div style={{ fontSize: 10, color: 'var(--blue-txt)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 3 }}>Program share</div>
                  <div style={{ fontSize: 18, fontWeight: 700 }}>${(tenant.programShare || 0).toLocaleString()}</div>
                </div>
              )}
              {(tenant.tenantShare > 0) && (
                <div style={{ flex: 1, background: 'var(--amber-bg)', borderRadius: 8, padding: '10px 14px' }}>
                  <div style={{ fontSize: 10, color: 'var(--amber-txt)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 3 }}>Tenant share</div>
                  <div style={{ fontSize: 18, fontWeight: 700 }}>${(tenant.tenantShare || 0).toLocaleString()}</div>
                </div>
              )}
              <div style={{ flex: 1, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px' }}>
                <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 3 }}>Current rent</div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>${currentRent.toLocaleString()}</div>
              </div>
            </div>

            {/* Increase inputs */}
            <div className="form-grid form-grid-2" style={{ marginBottom: 16 }}>
              <div className="form-group">
                <label className="form-label">1-year lease increase (%)</label>
                <input className="form-input" type="number" min="0" max="100" step="0.1"
                  placeholder="e.g. 3" value={renewalForm.pct1}
                  onChange={e => setRenewalForm(p => ({ ...p, pct1: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">2-year lease increase (%) — year 2 only</label>
                <input className="form-input" type="number" min="0" max="100" step="0.1"
                  placeholder="e.g. 3" value={renewalForm.pct2}
                  onChange={e => setRenewalForm(p => ({ ...p, pct2: e.target.value }))} />
              </div>
            </div>

            {/* Computed results */}
            {(pct1 > 0 || pct2 > 0) && (
              <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, padding: 14, marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 10 }}>Renewal amounts</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                    <span>1-year contract</span>
                    <span style={{ fontWeight: 700, color: 'var(--accent)' }}>${yr1Amt.toLocaleString()}/mo{pct1 > 0 ? ` (+${pct1}%)` : ''}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                    <span>2-year contract — year 1</span>
                    <span style={{ fontWeight: 700, color: 'var(--accent)' }}>${yr1Amt.toLocaleString()}/mo{pct1 > 0 ? ` (+${pct1}%)` : ''}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                    <span>2-year contract — year 2</span>
                    <span style={{ fontWeight: 700, color: 'var(--accent)' }}>${yr2Amt.toLocaleString()}/mo{pct2 > 0 ? ` (+${pct2}% on year 1)` : ''}</span>
                  </div>
                  {newStart && (
                    <div style={{ borderTop: '1px solid var(--border)', paddingTop: 8, marginTop: 2, fontSize: 12, color: 'var(--text2)', display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <div>New start: <strong>{fmtDate(newStart)}</strong></div>
                      <div>1-year end: <strong>{fmtDate(yr1End)}</strong> &nbsp;·&nbsp; 2-year end: <strong>{fmtDate(yr2End)}</strong></div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="modal-actions">
              <button className="btn" onClick={() => setRenewalModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={openGmail} disabled={!pct1 && !pct2}>
                {ic(I.mail, 13)} Open in Gmail
              </button>
            </div>
          </Modal>
        );
      })()}

      {/* ── Linked Tasks ── */}
      {profileTab === 'activity' && (() => {
        // Maintenance & Inspection tasks live in their own card below — keep them out here.
        const linkedTasks = (tasks || []).filter(t => t.tenantId === tenantId && !['Maintenance', 'Inspection'].includes(taskCategory(t)));
        const tq = taskSearch.toLowerCase().trim();
        const taskMatches = t => !tq || (t.title || '').toLowerCase().includes(tq) || (t.description || '').toLowerCase().includes(tq);
        const activeTasks = linkedTasks.filter(t => taskActive(t) && taskMatches(t));
        const closedTasks = linkedTasks.filter(t => !taskActive(t) && taskMatches(t));
        const openTaskCount = linkedTasks.filter(taskActive).length;
        const closedTaskCount = linkedTasks.length - openTaskCount;
        const TASK_PAGE = 10;
        const taskTotalPages = Math.max(1, Math.ceil(activeTasks.length / TASK_PAGE));
        const taskSafePage = Math.min(taskPage, taskTotalPages);
        const pagedActive = activeTasks.slice((taskSafePage - 1) * TASK_PAGE, taskSafePage * TASK_PAGE);
        const isODt = t => taskActive(t) && isTaskOverdue(t.followUp);
        const isDTt = t => taskActive(t) && isTaskDueToday(t.followUp);
        const TaskRow = ({ t }) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text1)', marginBottom: 2 }}>{t.title}</div>
              {t.description && <div style={{ fontSize: 11, color: 'var(--text2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.description}</div>}
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <div style={{ marginBottom: 3 }}><Badge label={taskStatus(t)} /></div>
              {t.followUp && <div style={{ fontSize: 11, color: isODt(t) ? 'var(--red-txt)' : isDTt(t) ? 'var(--amber-txt)' : 'var(--text2)', fontWeight: (isODt(t) || isDTt(t)) ? 600 : 400 }}>{fmtDate(t.followUp)}</div>}
            </div>
          </div>
        );
        return (
          <CollapsibleCard collapsible title={<>{ic(I.clipboard, 14)} Tasks</>} right={<span style={{ fontSize: 11, fontWeight: 400 }}><span style={{ color: openTaskCount ? 'var(--amber-txt)' : 'var(--text3)', fontWeight: openTaskCount ? 600 : 400 }}>{openTaskCount} open</span> <span style={{ color: 'var(--text3)' }}>· {closedTaskCount} closed</span></span>} isMobile={isMobile}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <div style={{ flex: 1 }}>
                <SB value={taskSearch} onChange={v => { setTaskSearch(v); setTaskPage(1); }} placeholder="Search tasks by title…" />
              </div>
              <button className="btn" style={{ fontSize: 12, padding: '4px 12px', whiteSpace: 'nowrap' }} onClick={() => setAddTaskModal({ title: '', description: '', followUp: nyTodayISO(), status: 'Active' })}>{ic(I.plus, 12)} Add</button>
            </div>
            {activeTasks.length === 0 && closedTasks.length === 0 ? (
              <div className="empty" style={{ padding: '4px 0 8px' }}>{linkedTasks.length === 0 ? 'No tasks linked to this tenant yet.' : `No tasks match “${taskSearch}”.`}</div>
            ) : (
              <>
                {pagedActive.map(t => <TaskRow key={t.id} t={t} />)}
                {taskTotalPages > 1 && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10, gap: 8 }}>
                    <button className="btn-ghost" style={{ fontSize: 12, padding: '4px 10px' }} disabled={taskSafePage <= 1} onClick={() => setTaskPage(p => p - 1)}>← Prev</button>
                    <span style={{ fontSize: 12, color: 'var(--text2)' }}>Page {taskSafePage} of {taskTotalPages}</span>
                    <button className="btn-ghost" style={{ fontSize: 12, padding: '4px 10px' }} disabled={taskSafePage >= taskTotalPages} onClick={() => setTaskPage(p => p + 1)}>Next →</button>
                  </div>
                )}
                {closedTasks.length > 0 && (
                  <details style={{ marginTop: activeTasks.length ? 8 : 0 }}>
                    <summary style={{ fontSize: 12, color: 'var(--text2)', cursor: 'pointer', padding: '6px 0', listStyle: 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><polyline points="6 9 12 15 18 9"/></svg>
                      {closedTasks.length} closed {closedTasks.length === 1 ? 'task' : 'tasks'}
                    </summary>
                    <div style={{ marginTop: 4 }}>{closedTasks.map(t => <TaskRow key={t.id} t={t} />)}</div>
                  </details>
                )}
              </>
            )}
          </CollapsibleCard>
        );
      })()}

      {/* ── Documents ── */}
      {profileTab === 'overview' && (
      <CollapsibleCard
        title={<>{ic(I.file, 14)} Documents{dragOver && <span style={{ fontSize: 11, color: 'var(--accent)', fontWeight: 600, marginLeft: 8 }}>Drop to upload</span>}</>}
        right={<label style={{ cursor: uploadingDoc ? 'not-allowed' : 'pointer' }}><input type="file" style={{ display: 'none' }} onChange={handleTenantDocUpload} disabled={uploadingDoc} /><span className="btn-ghost" style={{ pointerEvents: 'none' }}>{uploadingDoc ? 'Uploading…' : <>{ic(I.plus, 12)} Upload</>}</span></label>}
        isMobile={isMobile}
        cardStyle={{ border: dragOver ? '2px solid var(--accent)' : '2px solid transparent', background: dragOver ? 'var(--accent-bg)' : undefined, transition: 'border 0.15s, background 0.15s' }}
        onDrop={!isMobile ? handleDrop : undefined}
        onDragOver={!isMobile ? handleDragOver : undefined}
        onDragLeave={!isMobile ? handleDragLeave : undefined}>
        {(tenant.documents || []).length === 0 && <div style={{ fontSize: 13, color: 'var(--text3)' }}>No documents yet.</div>}
        {(tenant.documents || []).map(doc => (
          <div key={doc.id} style={{ borderBottom: '1px solid var(--border)' }}>
            {renamingDoc === doc.id ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0' }}>
                <input className="form-input" style={{ flex: 1, fontSize: 13, padding: '4px 8px' }} value={renameVal} onChange={e => setRenameVal(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') saveRename(); if (e.key === 'Escape') setRenamingDoc(null); }} autoFocus />
                <button className="btn" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => setRenamingDoc(null)}>Cancel</button>
                <button className="btn btn-primary" style={{ fontSize: 12, padding: '4px 10px' }} onClick={saveRename}>Save</button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 0' }}>
                <a href={doc.url} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: 'var(--accent)', textDecoration: 'none', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ic(I.file, 12)} {doc.name}</a>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, marginLeft: 12 }}>
                  <span style={{ fontSize: 11, color: 'var(--text3)' }}>{doc.uploadedAt ? fmtDate(doc.uploadedAt.slice(0, 10)) : ''}</span>
                  <KebabMenu items={[
                    { label: 'Rename', onClick: () => handleTenantDocRename(doc) },
                    { label: 'Delete', danger: true, onClick: () => handleTenantDocDelete(doc) },
                  ]} />
                </div>
              </div>
            )}
          </div>
        ))}
      </CollapsibleCard>
      )}

      {/* ── Payment Notifications ── */}
      {paymentNotifs.filter(n => n.status === 'pending').length > 0 && (
        <div className="card" style={{ marginBottom: 16, border: '1px solid var(--amber-txt)', background: 'var(--amber-bg)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <div className="card-title" style={{ margin: 0, color: 'var(--amber-txt)' }}>{ic(I.dollar, 14, 'var(--amber-txt)')} Payment Notifications</div>
            <span style={{ fontSize: 11, fontWeight: 700, background: 'var(--amber-txt)', color: '#fff', borderRadius: 20, padding: '1px 7px' }}>{paymentNotifs.filter(n => n.status === 'pending').length}</span>
          </div>
          {paymentNotifs.filter(n => n.status === 'pending').map(n => (
            <div key={n.id} style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px', marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--green-txt)' }}>${(+n.amount || 0).toLocaleString()} sent via Zelle</div>
                  {n.zelleRef && <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>Ref: {n.zelleRef}</div>}
                  {n.note && <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>{n.note}</div>}
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>
                    {n.submittedAt ? new Date(n.submittedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' }) : ''}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary" style={{ fontSize: 12, padding: '5px 12px' }}
                  onClick={async () => {
                    await setDoc(doc(db, 'paymentNotifications', n.id), { status: 'confirmed', confirmedAt: new Date().toISOString() }, { merge: true });
                    toast(`Payment of $${n.amount} confirmed`);
                  }}>
                  ✓ Mark Confirmed
                </button>
                <button className="btn" style={{ fontSize: 12, padding: '5px 12px' }}
                  onClick={async () => {
                    await setDoc(doc(db, 'paymentNotifications', n.id), { status: 'dismissed', dismissedAt: new Date().toISOString() }, { merge: true });
                  }}>
                  Dismiss
                </button>
              </div>
            </div>
          ))}
          <div style={{ fontSize: 11, color: 'var(--amber-txt)', marginTop: 4 }}>
            After confirming, use the Pay button on the relevant charge to update the ledger.
          </div>
        </div>
      )}

      {/* ── Maintenance & Inspection ── */}
      {profileTab === 'activity' && (() => {
        const mStatusColor = s => s === 'open' ? 'var(--amber-txt)' : s === 'in-progress' ? 'var(--blue-txt)' : s === 'closed' ? 'var(--green-txt)' : 'var(--text2)';
        const mStatusBg = s => s === 'open' ? 'var(--amber-bg)' : s === 'in-progress' ? 'var(--blue-bg)' : s === 'closed' ? 'var(--green-bg)' : 'var(--bg3)';
        // Maintenance records linked to this tenant/unit
        const maintItems = (maintenance || [])
          .filter(m => {
            if (m.propertyId !== tenant.propertyId) return false;
            const units = (m.unit || '').split(', ');
            return units.includes(`${tenant.unit} - ${tenant.name}`) || units.includes('Building Wide');
          })
          .map(m => ({
            kind: 'maintenance', id: m.id, title: m.title, sub: m.vendor || '',
            date: m.status === 'closed' ? (m.completedDate || m.date) : m.date,
            statusLabel: m.status || 'open', statusColor: mStatusColor(m.status), statusBg: mStatusBg(m.status),
            isClosed: m.status === 'closed',
            onClick: () => setViewingMaintId(m.id),
          }));
        // Inspection tasks linked to this tenant
        const inspectionItems = (tasks || [])
          .filter(t => taskCategory(t) === 'Inspection' && taskBelongsToTenant(t, tenant))
          .map(t => {
            const cl = (t.inspectionData && t.inspectionData.checklist) || [];
            const pass = cl.filter(i => i.status === 'pass').length;
            const fail = cl.filter(i => i.status === 'fail').length;
            const na = cl.filter(i => i.status === 'na').length;
            const done = !taskActive(t);
            return {
              kind: 'inspection', id: t.id, title: t.title,
              sub: cl.length ? `${pass} pass · ${fail} fail · ${na} n/a` : 'Checklist not started',
              date: done ? (t.updatedAt || t.followUp) : t.followUp,
              statusLabel: done ? 'closed' : 'active',
              statusColor: done ? 'var(--green-txt)' : 'var(--amber-txt)',
              statusBg: done ? 'var(--green-bg)' : 'var(--amber-bg)',
              isClosed: done,
              onClick: () => { if (setNavTarget && setTab) { setNavTarget({ tab: 'tasks', id: t.id }); setTab('tasks'); } },
            };
          });
        // Maintenance-category TASKS linked to this tenant that don't already have a
        // maintenance record (e.g. bot-created ones) — show them here too, not in Tasks.
        const maintRecordTaskIds = new Set((maintenance || []).map(m => m.taskId).filter(Boolean));
        const maintTaskItems = (tasks || [])
          .filter(t => taskCategory(t) === 'Maintenance' && taskBelongsToTenant(t, tenant) && !maintRecordTaskIds.has(t.id))
          .map(t => {
            const done = !taskActive(t);
            return {
              kind: 'maintenance', id: t.id, title: t.title, sub: t.description || '',
              date: done ? (t.updatedAt || t.followUp) : t.followUp,
              statusLabel: done ? 'closed' : 'open',
              statusColor: done ? 'var(--green-txt)' : 'var(--amber-txt)',
              statusBg: done ? 'var(--green-bg)' : 'var(--amber-bg)',
              isClosed: done,
              onClick: () => { if (setNavTarget && setTab) { setNavTarget({ tab: 'tasks', id: t.id }); setTab('tasks'); } },
            };
          });
        const allItems = [...maintItems, ...maintTaskItems, ...inspectionItems];
        const typeFiltered = allItems.filter(it => maintTypeFilter === 'all' || it.kind === maintTypeFilter);
        const openCount = typeFiltered.filter(it => !it.isClosed).length;
        const closedCount = typeFiltered.length - openCount;
        const mq = maintSearch.toLowerCase().trim();
        const shown = typeFiltered
          .filter(it => (maintFilter === 'closed' ? it.isClosed : !it.isClosed))
          .filter(it => !mq || (it.title || '').toLowerCase().includes(mq) || (it.sub || '').toLowerCase().includes(mq))
          .sort((a, b) => (b.date || '').localeCompare(a.date || ''));
        const PAGE_SIZE = 10;
        const totalPages = Math.max(1, Math.ceil(shown.length / PAGE_SIZE));
        const safePage = Math.min(maintPage, totalPages);
        const pageItems = shown.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
        const Row = ({ it }) => (
          <div onClick={it.onClick} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg2)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = ''; }}>
            <span style={{ flexShrink: 0, fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', padding: '2px 6px', borderRadius: 4, background: it.kind === 'inspection' ? 'var(--blue-bg)' : 'var(--bg3)', color: it.kind === 'inspection' ? 'var(--blue-txt)' : 'var(--text2)' }}>{it.kind === 'inspection' ? 'Inspect' : 'Maint'}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text1)', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.title}</div>
              {it.sub && <div style={{ fontSize: 11, color: 'var(--text2)' }}>{it.sub}</div>}
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 20, background: it.statusBg, color: it.statusColor, marginBottom: 3 }}>{it.statusLabel}</span>
              <div style={{ fontSize: 11, color: 'var(--text2)' }}>{it.date ? fmtDate(it.date) : '—'}</div>
            </div>
            <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--text3)', flexShrink: 0 }}><path d="M9 18l6-6-6-6"/></svg>
          </div>
        );
        const maintToggle = (val, label, activeColor) => (
          <button onClick={() => { setMaintFilter(val); setMaintPage(1); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', padding: 0, fontSize: 11, color: maintFilter === val ? activeColor : 'var(--text3)', fontWeight: maintFilter === val ? 700 : 400 }}>
            {label}
          </button>
        );
        const typeChip = (val, label) => (
          <button onClick={() => { setMaintTypeFilter(val); setMaintPage(1); }}
            style={{ fontFamily: 'inherit', cursor: 'pointer', fontSize: 11, fontWeight: maintTypeFilter === val ? 600 : 400, padding: '3px 10px', borderRadius: 20, border: '1px solid var(--border)', background: maintTypeFilter === val ? 'var(--accent-bg)' : 'transparent', color: maintTypeFilter === val ? 'var(--accent)' : 'var(--text2)' }}>
            {label}
          </button>
        );
        const openAddMaint = () => setAddMaintModal({ title: '', description: '', vendor: 'Jose', date: new Date().toISOString().slice(0, 10), status: 'open' });
        const addInspection = () => {
          const today = nyTodayISO();
          const id = `task${Date.now()}`;
          const entry = {
            id, title: `Inspection — Unit ${tenant.unit} | ${tenant.name}`, description: 'General Inspection',
            propertyId: tenant.propertyId, tenantId, maintUnit: [], followUp: today, status: 'Active', category: 'Inspection',
            ...ownerAssignee(users),
            inspectionData: { checklist: makeDefaultChecklist() },
            createdAt: today, updatedAt: today, notes: [], documents: [],
          };
          setTasks(prev => [...prev, entry]);
          toast('Inspection created — fill out the checklist');
          if (setNavTarget && setTab) { setNavTarget({ tab: 'tasks', id }); setTab('tasks'); }
        };
        return (
          <CollapsibleCard collapsible title={<>{ic(I.wrench, 14)} Maintenance &amp; Inspection</>}
            right={<span style={{ fontSize: 11, display: 'inline-flex', gap: 12, alignItems: 'center' }}>{maintToggle('open', `${openCount} open`, 'var(--amber-txt)')}{maintToggle('closed', `${closedCount} closed`, 'var(--green-txt)')}</span>}
            isMobile={isMobile}>
            <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              {typeChip('all', 'All')}
              {typeChip('maintenance', 'Maintenance')}
              {typeChip('inspection', 'Inspection')}
              <div style={{ flex: 1 }} />
              <button className="btn" style={{ fontSize: 12, padding: '4px 10px', whiteSpace: 'nowrap' }} onClick={openAddMaint}>{ic(I.plus, 12)} Maintenance</button>
              <button className="btn" style={{ fontSize: 12, padding: '4px 10px', whiteSpace: 'nowrap' }} onClick={addInspection}>{ic(I.plus, 12)} Inspection</button>
            </div>
            <div style={{ marginBottom: 10 }}>
              <SB value={maintSearch} onChange={v => { setMaintSearch(v); setMaintPage(1); }} placeholder="Search by title or vendor…" />
            </div>
            {pageItems.length === 0 ? (
              <div className="empty" style={{ padding: '4px 0 8px' }}>{typeFiltered.length === 0 ? 'Nothing linked to this tenant yet.' : `No ${maintFilter} items${maintSearch ? ` matching “${maintSearch}”` : ''}.`}</div>
            ) : (
              pageItems.map(it => <Row key={`${it.kind}_${it.id}`} it={it} />)
            )}
            {totalPages > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10, gap: 8 }}>
                <button className="btn-ghost" style={{ fontSize: 12, padding: '4px 10px' }} disabled={safePage <= 1} onClick={() => setMaintPage(p => p - 1)}>← Prev</button>
                <span style={{ fontSize: 12, color: 'var(--text2)' }}>Page {safePage} of {totalPages}</span>
                <button className="btn-ghost" style={{ fontSize: 12, padding: '4px 10px' }} disabled={safePage >= totalPages} onClick={() => setMaintPage(p => p + 1)}>Next →</button>
              </div>
            )}
          </CollapsibleCard>
        );
      })()}

      {/* ── Add maintenance (from Activity → Maintenance) ── */}
      {addMaintModal && (
        <Modal title="Add Maintenance" onClose={() => setAddMaintModal(null)}>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 12 }}>
            Linked to <strong>{tenant.name}</strong> · Unit {tenant.unit} · {propName}
          </div>
          <FI label="Title" value={addMaintModal.title} onChange={v => setAddMaintModal(p => ({ ...p, title: v }))} placeholder="e.g. Leaking kitchen faucet" />
          <FTA label="Description" value={addMaintModal.description} onChange={v => setAddMaintModal(p => ({ ...p, description: v }))} />
          <FI label="Vendor" value={addMaintModal.vendor} onChange={v => setAddMaintModal(p => ({ ...p, vendor: v }))} placeholder="Assigned vendor / worker" />
          <FI label="Date" type="date" value={addMaintModal.date} onChange={v => setAddMaintModal(p => ({ ...p, date: v }))} />
          <FS label="Status" value={addMaintModal.status} onChange={v => setAddMaintModal(p => ({ ...p, status: v }))}
            options={[{ value: 'open', label: 'Open' }, { value: 'in-progress', label: 'In-progress' }, { value: 'closed', label: 'Closed' }]} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
            <button className="btn" onClick={() => setAddMaintModal(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={() => {
              const f = addMaintModal;
              if (!f.title.trim()) { toast('Title is required', 'error'); return; }
              const unit = `${tenant.unit} - ${tenant.name}`;
              const entry = {
                id: `m${Date.now()}`,
                propertyId: tenant.propertyId,
                unit, units: [unit],
                title: f.title.trim(),
                description: f.description || '',
                status: f.status || 'open',
                date: f.date || new Date().toISOString().slice(0, 10),
                vendor: f.vendor || '',
                cost: '',
                ...(f.status === 'closed' ? { completedDate: f.date || new Date().toISOString().slice(0, 10) } : {}),
              };
              setMaintenance(prev => [...prev, entry]);
              setAddMaintModal(null);
              setMaintFilter(f.status === 'closed' ? 'closed' : 'open');
              toast('Maintenance added');
            }}>Add Maintenance</button>
          </div>
        </Modal>
      )}

      {/* ── Add task (from Activity → Tasks) ── */}
      {addTaskModal && (
        <Modal title="Add Task" onClose={() => setAddTaskModal(null)}>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 12 }}>
            Linked to <strong>{tenant.name}</strong> · Unit {tenant.unit} · {propName}
          </div>
          {!addTaskModal.category ? (
            <>
              <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 12 }}>Select a category:</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  { value: 'Collection', label: 'Collection', desc: 'Rent arrears or debt collection' },
                  { value: 'General',    label: 'General',    desc: 'Follow-up, action item, or to-do' },
                  { value: 'Renewal',    label: 'Renewal',    desc: 'Lease renewal or contract update' },
                  { value: 'Complaint',  label: 'Complaint',  desc: 'Tenant or neighbor complaint' },
                ].map(c => (
                  <button key={c.value} onClick={() => setAddTaskModal(p => ({ ...p, category: c.value }))}
                    style={{ textAlign: 'left', background: 'var(--bg2)', border: '1.5px solid var(--border)', borderRadius: 10, padding: '12px 14px', cursor: 'pointer', fontFamily: 'inherit', width: '100%' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text1)' }}>{c.label}</div>
                    <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>{c.desc}</div>
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                <button className="btn" onClick={() => setAddTaskModal(null)}>Cancel</button>
              </div>
            </>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <button className="btn-ghost" style={{ fontSize: 12, padding: '3px 8px' }} onClick={() => setAddTaskModal(p => ({ ...p, category: '' }))}>← Back</button>
                <span className="badge badge-gray">{addTaskModal.category}</span>
              </div>
              <FI label="Title" value={addTaskModal.title} onChange={v => setAddTaskModal(p => ({ ...p, title: v }))} placeholder="e.g. Follow up on lease paperwork" />
              <FTA label="Description" value={addTaskModal.description} onChange={v => setAddTaskModal(p => ({ ...p, description: v }))} />
              <FI label="Follow-up" type="date" value={addTaskModal.followUp} onChange={v => setAddTaskModal(p => ({ ...p, followUp: v }))} />
              <FS label="Status" value={addTaskModal.status} onChange={v => setAddTaskModal(p => ({ ...p, status: v }))}
                options={[{ value: 'Active', label: 'Active' }, { value: 'Closed', label: 'Closed' }]} />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                <button className="btn" onClick={() => setAddTaskModal(null)}>Cancel</button>
                <button className="btn btn-primary" onClick={() => {
                  const f = addTaskModal;
                  if (!f.title.trim()) { toast('Title is required', 'error'); return; }
                  const today = nyTodayISO();
                  const entry = {
                    id: `task${Date.now()}`,
                    title: f.title.trim(),
                    description: f.description || '',
                    propertyId: tenant.propertyId,
                    tenantId,
                    maintUnit: [],
                    followUp: f.followUp || today,
                    status: f.status || 'Active',
                    category: f.category,
                    createdAt: today,
                    updatedAt: today,
                    notes: [],
                    documents: [],
                  };
                  setTasks(prev => [...prev, entry]);
                  setAddTaskModal(null);
                  setTaskSearch('');
                  setTaskPage(1);
                  toast('Task added');
                }}>Add Task</button>
              </div>
            </>
          )}
        </Modal>
      )}

      {/* ── Email History ── */}
      {profileTab === 'activity' && (
      <CollapsibleCard collapsible title={<>{ic('M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z', 14)} Email History</>} right={emailLogs.length > 0 && <span style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 400 }}>{emailLogs.length} {emailLogs.length === 1 ? 'email' : 'emails'}</span>} isMobile={isMobile}>
        {emailLogs.length === 0 ? (
          <div className="empty" style={{ padding: '4px 0 8px' }}>No emails sent to this tenant yet.</div>
        ) : (
          <>
            <div style={{ marginBottom: 10 }}>
              <SB value={emailSearch} onChange={v => { setEmailSearch(v); setEmailPage(1); }} placeholder="Search emails by subject or body…" />
            </div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
              {[['all', 'All'], ['direct', 'Direct'], ['general', 'General']].map(([val, label]) => (
                <button key={val} onClick={() => { setEmailTypeFilter(val); setEmailPage(1); }}
                  style={{ fontSize: 11, padding: '4px 10px', borderRadius: 0, border: `1px solid ${emailTypeFilter === val ? 'var(--accent)' : 'var(--border)'}`, background: emailTypeFilter === val ? 'var(--accent-bg)' : 'var(--bg2)', color: emailTypeFilter === val ? 'var(--accent)' : 'var(--text2)', fontWeight: emailTypeFilter === val ? 600 : 400, cursor: 'pointer', fontFamily: 'inherit' }}>
                  {label}
                </button>
              ))}
            </div>
            {(() => {
              const q = emailSearch.toLowerCase().trim();
              const filtered = emailLogs.filter(l =>
                (emailTypeFilter === 'all' || (emailTypeFilter === 'direct' ? l.type === 'direct' : l.type !== 'direct')) &&
                (!q || (l.subject || '').toLowerCase().includes(q) || (l.body || '').toLowerCase().includes(q))
              );
              if (filtered.length === 0) return <div className="empty" style={{ padding: '4px 0 8px' }}>No {emailTypeFilter !== 'all' ? emailTypeFilter + ' ' : ''}emails{emailSearch ? ` match “${emailSearch}”` : ' found'}.</div>;
              const EMAIL_PAGE = 10;
              const totalPages = Math.max(1, Math.ceil(filtered.length / EMAIL_PAGE));
              const page = Math.min(emailPage, totalPages);
              const paged = filtered.slice((page - 1) * EMAIL_PAGE, page * EMAIL_PAGE);
              return (
                <>
                  {paged.map(log => {
                    const d = new Date(log.sentAt);
                    const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'America/New_York' });
                    const timeStr = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/New_York' });
                    return (
                      <div key={log.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 0', borderBottom: '1px solid var(--border)', gap: 12 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.subject}</div>
                          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span>{dateStr} · {timeStr}</span>
                            <span style={{ background: log.type === 'direct' ? 'var(--blue-bg)' : 'var(--accent-bg)', color: log.type === 'direct' ? 'var(--blue-txt)' : 'var(--accent)', borderRadius: 4, padding: '1px 6px', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                              {log.type === 'direct' ? 'Direct' : 'General'}
                            </span>
                            {log.sentBy && <span>{/telegram/i.test(log.sentBy) ? 'Via Telegram' : `by ${log.sentBy}`}</span>}
                          </div>
                        </div>
                        <button className="btn-ghost" style={{ fontSize: 12, flexShrink: 0 }} onClick={() => setEmailLogView(log)}>View</button>
                      </div>
                    );
                  })}
                  {totalPages > 1 && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginTop: 12 }}>
                      <button className="btn" style={{ padding: '4px 10px', fontSize: 12 }} disabled={page === 1} onClick={() => setEmailPage(page - 1)}>‹ Prev</button>
                      <span style={{ fontSize: 12, color: 'var(--text3)' }}>Page {page} of {totalPages}</span>
                      <button className="btn" style={{ padding: '4px 10px', fontSize: 12 }} disabled={page === totalPages} onClick={() => setEmailPage(page + 1)}>Next ›</button>
                    </div>
                  )}
                </>
              );
            })()}
          </>
        )}
      </CollapsibleCard>
      )}

      {emailLogView && (
        <Modal
          title={
            <span style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 400, display: 'inline-flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <span>{new Date(emailLogView.sentAt).toLocaleString('en-US', { timeZone: 'America/New_York' })}</span>
              <span style={{ background: emailLogView.type === 'direct' ? 'var(--blue-bg)' : 'var(--accent-bg)', color: emailLogView.type === 'direct' ? 'var(--blue-txt)' : 'var(--accent)', padding: '1px 6px', fontSize: 10, fontWeight: 600, textTransform: 'uppercase' }}>
                {emailLogView.type === 'direct' ? 'Direct' : 'General'}
              </span>
              {emailLogView.sentBy && <span>{/telegram/i.test(emailLogView.sentBy) ? 'Sent Via Telegram' : `Sent by ${emailLogView.sentBy}`}</span>}
            </span>
          }
          onClose={() => setEmailLogView(null)}
          lg
        >
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text1)', background: 'var(--bg2)', borderRadius: 0, padding: 14, marginBottom: 12 }}>
            {emailLogView.subject}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text1)', lineHeight: 1.7, whiteSpace: 'pre-wrap', background: 'var(--bg2)', borderRadius: 0, padding: 14 }}>
            <Linkify text={emailLogView.body} />
          </div>
        </Modal>
      )}

      {false && showLogs && (
        <Modal title="Edit Logs" onClose={() => setShowLogs(false)}>
        </Modal>
      )}
    </div>
  );
}

const TENANT_PAGE_SIZE = 50;

function Tenants({ tenants, setTenants, users, properties, ledger, setLedger, maintenance, setMaintenance, tasks, setTasks, user, initialTenantId, clearNav, setTab, setNavTarget, returnTab }) {
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [propF, _setPropF] = useState(SHARED_PROP_FILTER);
  const setPropF = v => { SHARED_PROP_FILTER = v; _setPropF(v); };
  const [statusF, setStatusF] = useState('all');
  const [selectedId, setSelectedId] = useState(initialTenantId || null);
  const [savedReturnTab] = useState(returnTab);
  useEffect(() => { if (initialTenantId) { setSelectedId(initialTenantId); if (clearNav) clearNav(); } }, [initialTenantId]); // eslint-disable-line
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [showPrev, setShowPrev] = useState(false);
  const [page, setPage] = useState(1);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => { const h = () => setIsMobile(window.innerWidth < 768); window.addEventListener('resize', h); return () => window.removeEventListener('resize', h); }, []);
  const [exportModal, setExportModal] = useState(false);
  const [exportPropId, setExportPropId] = useState('all');
  const [noticeModal, setNoticeModal] = useState(false);
  const [noticePage, setNoticePage] = useState(1);
  const [noticeType, setNoticeType] = useState('general');
  const [noticeForm, setNoticeForm] = useState({ propertyId: 'all', subject: '', body: '' });
  const [noticeAttachments, setNoticeAttachments] = useState([]);
  const [noticeSelectedIds, setNoticeSelectedIds] = useState(new Set());
  const [noticeSearch, setNoticeSearch] = useState('');
  const [noticeAiPrompt, setNoticeAiPrompt] = useState('');
  const [noticeAiLoading, setNoticeAiLoading] = useState(false);
  const [noticeExtraTo, setNoticeExtraTo] = useState([]);
  const [noticeToInput, setNoticeToInput] = useState('');
  const [noticeSending, setNoticeSending] = useState(false);
  const [noticeResult, setNoticeResult] = useState(null);
  const nff = k => v => setNoticeForm(p => ({ ...p, [k]: v }));
  const EXPORT_COLS = [
    { key: 'name', label: 'Full Name' },
    { key: 'unit', label: 'Unit' },
    { key: '_propertyName', label: 'Property' },
    { key: 'status', label: 'Status' },
    { key: 'unitType', label: 'Unit Type' },
    { key: 'program', label: 'Program' },
    { key: 'voucherNumber', label: 'Voucher #' },
    { key: 'tenantShare', label: 'Tenant Share ($)' },
    { key: 'programShare', label: 'Program Share ($)' },
    { key: 'hasParking', label: 'Parking' },
    { key: 'parkingFee', label: 'Parking Amount ($)' },
    { key: 'parkingStart', label: 'Parking Start Date' },
    { key: 'hasWasherDryer', label: 'Washer/Dryer' },
    { key: 'washerDryerFee', label: 'W/D Amount ($)' },
    { key: 'washerDryerStart', label: 'W/D Start Date' },
    { key: 'rent', label: 'Total Rent ($)' },
    { key: 'leaseStart', label: 'Lease Start' },
    { key: 'leaseEnd', label: 'Lease End' },
    { key: 'phone', label: 'Phone' },
    { key: 'email', label: 'Email' },
    { key: 'paymentType', label: 'Payment Type' },
    { key: 'dueDay', label: 'Due Day' },
    { key: 'brokerName', label: 'Broker' },
  ];
  const [exportCols, setExportCols] = useState(() => EXPORT_COLS.map(c => c.key));
  const toggleCol = key => setExportCols(p => p.includes(key) ? p.filter(k => k !== key) : [...p, key]);
  const doExport = () => {
    const prop = properties.find(p => p.id === exportPropId);
    // Never export moved-out tenants (status 'Previous').
    const exportTenants = tenants.filter(t => t.status !== 'Previous' && (exportPropId === 'all' || t.propertyId === exportPropId));
    const selectedCols = EXPORT_COLS.filter(c => exportCols.includes(c.key));
    const rows = exportTenants.map(t => {
      const row = {};
      selectedCols.forEach(c => {
        let val = c.key === '_propertyName' ? (properties.find(p => p.id === t.propertyId)?.name || '')
          : c.key === 'unitType' ? (((properties.find(p => p.id === t.propertyId)?.unitsList || []).find(u => u.unitNumber === t.unit)?.unitType) || t.unitType || '')
          : t[c.key];
        if (typeof val === 'boolean') val = val ? 'Yes' : 'No';
        if (val === undefined || val === null) val = '';
        row[c.label] = val;
      });
      return row;
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Tenants');
    const propLabel = prop ? prop.name.replace(/\s+/g, '_') : 'All_Properties';
    const date = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `Tenants_${propLabel}_${date}.xlsx`);
    setExportModal(false);
  };
  const ff = k => v => { setForm(p => ({ ...p, [k]: v })); if (formErrors[k]) setFormErrors(p => ({ ...p, [k]: null })); };
  const tenantMatches = (t, q) => { if (!q) return true; const name = (t.name || '').toLowerCase(); const unit = (t.unit || '').toLowerCase(); const email = (t.email || '').toLowerCase(); return name.includes(q) || unit.includes(q) || email.includes(q); };
  const allRows = useMemo(() => {
    const q = search.toLowerCase().trim();
    const filtered = tenants.filter(t => t.status !== 'Previous' && (propF === 'all' || t.propertyId === propF) && (statusF === 'all' || t.status === statusF) && tenantMatches(t, q));
    // Order tenants by their unit's position in the property's Units list (falls back
    // to natural unit order for units not found in the list), grouped by property.
    const unitIdx = t => { const list = properties.find(p => p.id === t.propertyId)?.unitsList || []; const i = list.findIndex(u => u.unitNumber === t.unit); return i === -1 ? Number.MAX_SAFE_INTEGER : i; };
    const propNm = id => properties.find(p => p.id === id)?.name || '';
    return filtered.sort((a, b) => {
      if (a.propertyId !== b.propertyId) return propNm(a.propertyId).localeCompare(propNm(b.propertyId));
      const ai = unitIdx(a), bi = unitIdx(b);
      if (ai !== bi) return ai - bi;
      return (a.unit || '').localeCompare(b.unit || '', undefined, { numeric: true });
    });
  }, [tenants, properties, propF, statusF, search]);
  const prevRows = useMemo(() => { const q = search.toLowerCase().trim(); return tenants.filter(t => t.status === 'Previous' && (propF === 'all' || t.propertyId === propF) && tenantMatches(t, q)); }, [tenants, propF, search]);

  useEffect(() => { setPage(1); }, [search, propF, statusF]);

  const totalPages = Math.max(1, Math.ceil(allRows.length / TENANT_PAGE_SIZE));
  const rows = allRows.slice((page - 1) * TENANT_PAGE_SIZE, page * TENANT_PAGE_SIZE);
  const propName = id => properties.find(p => p.id === id)?.name || id;
  const openAdd = () => { setForm({ name: '', unit: '', propertyId: properties[0]?.id || '', status: 'Occupied', rent: '', tenantShare: '', programShare: '', program: 'Cash', email: '', phone: '', altPhone: '', leaseStart: '', leaseEnd: '', bedrooms: '', bathrooms: '', hasBalcony: false, hasParking: false, hasWasherDryer: false, parkingFee: '', parkingStart: '', washerDryerFee: '', washerDryerStart: '', brokerName: '', paymentType: 'Cash', dueDay: '1', voucherNumber: '', notes: '', documents: [], householdMembers: [] }); setFormErrors({}); setModal('add'); };
  const openEdit = t => { setForm({ ...t }); setFormErrors({}); setModal('edit'); };
  const save = () => {
    const errs = {};
    if (!form.name?.trim()) errs.name = 'Full name is required';
    if (!form.unit?.trim()) errs.unit = 'Unit is required';
    if (Object.keys(errs).length > 0) { setFormErrors(errs); toast('Please fill in all required fields', 'error'); return; }
    if (form.leaseStart && form.leaseEnd && form.leaseEnd < form.leaseStart) {
      toast('Lease end date cannot be before the start date', 'error'); return;
    }
    const ts = +form.tenantShare || 0, ps = +form.programShare || 0, pk = form.hasParking ? (+form.parkingFee || 0) : 0, wd = form.hasWasherDryer ? (+form.washerDryerFee || 0) : 0;
    const entry = { ...form, rent: ts + ps + pk + wd, tenantShare: ts, programShare: ps, parkingFee: pk, washerDryerFee: wd, bedrooms: +form.bedrooms || 0, bathrooms: +form.bathrooms || 0, dueDay: +form.dueDay || 1 };
    if (modal === 'edit') setTenants(prev => prev.map(t => t.id === form.id ? entry : t));
    else setTenants(prev => [...prev, { ...entry, id: `t${Date.now()}`, documents: [], householdMembers: [] }]);
    setFormErrors({});
    setModal(null);
    toast(modal === 'edit' ? 'Tenant updated' : 'Tenant added');
  };
  const remove = id => {
    const t = tenants.find(x => x.id === id);
    if (!t) return;
    if (t.status === 'Previous') {
      if (window.confirm(`Permanently delete ${t.name}? This cannot be undone.`)) {
        setTenants(prev => prev.filter(x => x.id !== id));
        toast('Tenant permanently deleted', 'info');
      }
    } else {
      if (window.confirm(`Move ${t.name} to Previous tenants?`)) {
        setTenants(prev => prev.map(x => x.id === id ? { ...x, status: 'Previous' } : x));
        toast(`${t.name} moved to Previous`, 'info');
      }
    }
  };
  if (selectedId) return <TenantDetail tenantId={selectedId} tenants={tenants} setTenants={setTenants} users={users} properties={properties} ledger={ledger} setLedger={setLedger} maintenance={maintenance} setMaintenance={setMaintenance} tasks={tasks} setTasks={setTasks} user={user} onBack={() => { setSelectedId(null); if (savedReturnTab) setTab(savedReturnTab); }} backLabel={savedReturnTab === 'rentroll' ? 'Rent Roll' : 'All tenants'} setTab={setTab} setNavTarget={setNavTarget} />;
  return (
    <div>
      <div className="page-header"><span className="page-title">Tenants</span><div style={{ display: 'flex', gap: 8 }}><button className="btn" onClick={() => { setExportPropId('all'); setExportCols(EXPORT_COLS.map(c => c.key)); setExportModal(true); }}>{ic('M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4', 14)} Export Excel</button><button className="btn" onClick={() => { setNoticeForm({ propertyId: 'all', subject: '', body: '' }); setNoticeSelectedIds(new Set()); setNoticeSearch(''); setNoticePage(1); setNoticeType('general'); setNoticeAiPrompt(''); setNoticeExtraTo([]); setNoticeToInput(''); setNoticeResult(null); setNoticeModal(true); }}>{ic('M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z M22 6l-10 7L2 6', 14)} Send Notice</button><button className="btn btn-primary" onClick={openAdd}>{ic(I.plus, 14, '#fff')} Add tenant</button></div></div>
      <div className="filter-bar">
        <div style={{ flex: 1 }}><SB value={search} onChange={setSearch} placeholder="Search name, unit, email…" /></div>
        <select className="form-input" style={{ width: 'auto', minWidth: 200 }} value={propF} onChange={ev => setPropF(ev.target.value)}>
          <option value="all">All properties</option>
          {properties.filter(p => !(p.businessType || '').toLowerCase().includes('construction')).map(p => <option key={p.id} value={p.id}>{p.name}{p.address ? ` — ${p.address}` : ''}</option>)}
        </select>
        <select className="form-input" style={{ width: 'auto', minWidth: 130 }} value={statusF} onChange={ev => setStatusF(ev.target.value)}><option value="all">All statuses</option><option value="Occupied">Occupied</option><option value="Vacant">Vacant</option></select>
      </div>
      <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 8 }}>
        Showing {allRows.length} of {tenants.filter(t => t.status !== 'Previous').length} tenants
      </div>
      {/* Desktop table */}
      {!isMobile && (
        <table className="list-table">
          <thead><tr><th>Unit</th><th>Tenant</th><th>Program</th><th>Rent</th><th>Lease end</th></tr></thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={5}><div className="empty">No tenants found</div></td></tr> :
              rows.map(t => (
                <tr key={t.id} className="clickable" onClick={() => setSelectedId(t.id)}>
                  <td style={{ fontWeight: 500 }}>{t.unit || '—'}</td>
                  <td><div style={{ fontWeight: 500 }}>{t.name}</div></td>
                  <td style={{ fontSize: 12, color: 'var(--text2)' }}>{t.program || '—'}</td>
                  <td><div style={{ fontWeight: 500 }}>${(t.rent || 0).toLocaleString()}</div></td>
                  <td style={{ fontSize: 12 }}>
                    {t.leaseEnd
                      ? <span style={{ color: isExpiredISO(t.leaseEnd) ? 'var(--red-txt)' : isLeaseExpiringSoon(t.leaseEnd) ? '#f97316' : 'var(--text2)', fontWeight: (isExpiredISO(t.leaseEnd) || isLeaseExpiringSoon(t.leaseEnd)) ? 600 : 400 }}>
                          {isExpiredISO(t.leaseEnd) ? 'EXPIRED · ' : isLeaseExpiringSoon(t.leaseEnd) ? 'Soon · ' : ''}{t.leaseEnd}
                        </span>
                      : <span style={{ color: 'var(--text3)' }}>—</span>}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      )}
      {/* Mobile table — Tenant + Property/Unit only */}
      {isMobile && (
        <table className="list-table" style={{ display: 'table', width: '100%', tableLayout: 'fixed', whiteSpace: 'normal' }}>
          <colgroup><col /></colgroup>
          <thead><tr><th style={{ whiteSpace: 'normal' }}>Tenant</th></tr></thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={1}><div className="empty">No tenants found</div></td></tr> :
              rows.map(t => (
                <tr key={t.id} className="clickable" onClick={() => setSelectedId(t.id)}>
                  <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                    <div style={{ fontWeight: 500, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                      {t.name}
                      {t.leaseEnd && isExpiredISO(t.leaseEnd) && <span style={{ fontSize: 10, fontWeight: 700, background: 'var(--red-bg)', color: 'var(--red-txt)', padding: '1px 6px', borderRadius: 10 }}>Lease Expired</span>}
                      {t.leaseEnd && !isExpiredISO(t.leaseEnd) && isLeaseExpiringSoon(t.leaseEnd) && <span style={{ fontSize: 10, fontWeight: 700, background: 'var(--amber-bg)', color: 'var(--amber-txt)', padding: '1px 6px', borderRadius: 10 }}>Lease Soon</span>}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2 }}>Unit {t.unit || '—'}</div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      )}
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, marginTop: 16, flexWrap: 'wrap' }}>
          <button className="btn" style={{ padding: '4px 10px', fontSize: 12 }} disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹ Prev</button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
            <button key={p} onClick={() => setPage(p)} style={{ padding: '4px 10px', fontSize: 12, borderRadius: 6, border: '1px solid var(--border)', background: p === page ? 'var(--accent)' : 'var(--bg2)', color: p === page ? '#fff' : 'var(--text1)', fontWeight: p === page ? 600 : 400, cursor: 'pointer' }}>{p}</button>
          ))}
          <button className="btn" style={{ padding: '4px 10px', fontSize: 12 }} disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next ›</button>
        </div>
      )}

      {prevRows.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <button className="btn-ghost" style={{ fontSize: 13, marginBottom: 10, color: 'var(--text2)' }} onClick={() => setShowPrev(p => !p)}>
            {ic(showPrev ? 'M19 9l-7 7-7-7' : 'M9 18l6-6-6-6', 14)} Previous tenants ({prevRows.length})
          </button>
          {showPrev && (
            <table className="list-table" style={{ opacity: 0.7 }}>
              <thead><tr><th>Tenant</th><th>Property / unit</th><th>Program</th><th>Rent</th><th>Lease end</th><th>Moved out</th><th style={{ width: 40 }}></th></tr></thead>
              <tbody>
                {prevRows.map(t => (
                  <tr key={t.id} className="clickable" onClick={() => setSelectedId(t.id)}>
                    <td><div style={{ fontWeight: 500 }}>{t.name}</div>{t.email && <div style={{ fontSize: 11, color: 'var(--text2)' }}>{t.email}</div>}</td>
                    <td><div>{propName(t.propertyId)}</div><div style={{ fontSize: 11, color: 'var(--text2)' }}>Unit {t.unit}</div></td>
                    <td style={{ fontSize: 12, color: 'var(--text2)' }}>{t.program || '—'}</td>
                    <td style={{ fontSize: 12, color: 'var(--text2)' }}>${(t.rent || 0).toLocaleString()}</td>
                    <td style={{ fontSize: 12, color: 'var(--text2)' }}>{t.leaseEnd ? fmtDate(t.leaseEnd) : '—'}</td>
                    <td style={{ fontSize: 12, color: 'var(--text2)' }}>{t.moveOutDate ? fmtDate(t.moveOutDate) : '—'}</td>
                    <td><div onClick={ev => ev.stopPropagation()}><KebabMenu items={[{ label: 'View detail', onClick: () => setSelectedId(t.id) }, { label: 'Delete permanently', danger: true, onClick: () => remove(t.id) }]} /></div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
      {modal && (
        <Modal title={modal === 'edit' ? 'Edit tenant' : 'Add tenant'} onClose={() => { setModal(null); setFormErrors({}); }} lg>
          <div className="form-grid form-grid-2"><FI label="Full name *" value={form.name} onChange={ff('name')} error={formErrors.name} /><FI label="Unit *" value={form.unit} onChange={ff('unit')} error={formErrors.unit} /></div>
          <FS label="Property" value={form.propertyId} onChange={ff('propertyId')} options={properties.map(p => ({ value: p.id, label: p.name }))} />
          <FS label="Status" value={form.status} onChange={ff('status')} options={['Occupied', 'Vacant', 'Previous']} />
          <div className="form-grid form-grid-2"><FS label="Program" value={form.program} onChange={ff('program')} options={['Cash', 'Section 8 NYCHA', 'Section 8 HPD', 'Section 8 CVR', 'CityFheps NYCHA', 'CityFheps HPD', 'OHS Voucher']} /><FI label="Voucher #" value={form.voucherNumber} onChange={ff('voucherNumber')} /></div>
          <div className="form-grid form-grid-2"><FI label="Tenant share ($)" value={form.tenantShare} onChange={ff('tenantShare')} type="number" /><FI label="Program share ($)" value={form.programShare} onChange={ff('programShare')} type="number" /></div>
          <div className="form-grid form-grid-2">
            <div className="form-group">
              <label className="form-label">Parking</label>
              <select className="form-input" value={form.hasParking ? 'yes' : 'no'} onChange={e => ff('hasParking')(e.target.value === 'yes')}>
                <option value="no">No</option><option value="yes">Yes</option>
              </select>
            </div>
            {form.hasParking && <FI label="Parking amount ($)" value={form.parkingFee} onChange={ff('parkingFee')} type="number" />}
          </div>
          {form.hasParking && <FI label="Parking start date" value={form.parkingStart} onChange={ff('parkingStart')} type="date" />}
          <div className="form-grid form-grid-2">
            <div className="form-group">
              <label className="form-label">Washer / Dryer</label>
              <select className="form-input" value={form.hasWasherDryer ? 'yes' : 'no'} onChange={e => ff('hasWasherDryer')(e.target.value === 'yes')}>
                <option value="no">No</option><option value="yes">Yes</option>
              </select>
            </div>
            {form.hasWasherDryer && <FI label="W/D amount ($)" value={form.washerDryerFee} onChange={ff('washerDryerFee')} type="number" />}
          </div>
          {form.hasWasherDryer && <FI label="W/D start date" value={form.washerDryerStart} onChange={ff('washerDryerStart')} type="date" />}
          <div className="form-group">
            <label className="form-label">Total rent (computed)</label>
            <div style={{ padding: '8px 10px', background: 'var(--bg3)', borderRadius: 8, fontSize: 13, fontWeight: 600, color: 'var(--accent)' }}>
              ${((+form.tenantShare || 0) + (+form.programShare || 0) + (form.hasParking ? (+form.parkingFee || 0) : 0) + (form.hasWasherDryer ? (+form.washerDryerFee || 0) : 0)).toLocaleString()}
            </div>
          </div>
          <div className="form-grid form-grid-2"><FI label="Lease start" value={form.leaseStart} onChange={ff('leaseStart')} type="date" /><FI label="Lease end" value={form.leaseEnd} onChange={ff('leaseEnd')} type="date" /></div>
          {form.leaseStart && form.leaseStart > nyTodayISO() && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#92600a', background: '#fef9c3', border: '1px solid #fde68a', borderRadius: 8, padding: '6px 10px', marginBottom: 4 }}>
              {ic(I.alert, 13, '#92600a')} Future-dated lease — start date is ahead of today. This is allowed.
            </div>
          )}
          <div className="form-grid form-grid-2"><FI label="Phone" value={form.phone} onChange={ff('phone')} /><FI label="Email" value={form.email} onChange={ff('email')} /></div>
          <FI label="Alternative phone (optional)" value={form.altPhone || ''} onChange={ff('altPhone')} placeholder="e.g. (718) 555-0000" />
          <div className="form-grid form-grid-2"><FS label="Payment type" value={form.paymentType} onChange={ff('paymentType')} options={['Cash', 'Check', 'Zelle', 'Money Order', 'App', 'No Portion']} /><FI label="Due day" value={form.dueDay} onChange={ff('dueDay')} type="number" /></div>
          <FI label="Broker" value={form.brokerName} onChange={ff('brokerName')} />
          <div className="modal-actions"><button className="btn" onClick={() => { setModal(null); setFormErrors({}); }}>Cancel</button><SavingBtn onClick={save}>Save</SavingBtn></div>
        </Modal>
      )}
{exportModal && (
        <Modal title="Export Tenants to Excel" onClose={() => setExportModal(false)}>
          <div className="form-group">
            <label className="form-label">Which property?</label>
            <select className="form-input" value={exportPropId} onChange={e => setExportPropId(e.target.value)}>
              <option value="all">All Properties</option>
              {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <label className="form-label" style={{ margin: 0 }}>Select columns to include</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn" style={{ padding: '2px 10px', fontSize: 12 }} onClick={() => setExportCols(EXPORT_COLS.map(c => c.key))}>Select all</button>
                <button className="btn" style={{ padding: '2px 10px', fontSize: 12 }} onClick={() => setExportCols([])}>Clear all</button>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px', maxHeight: 300, overflowY: 'auto', padding: '10px 12px', background: 'var(--bg2)', borderRadius: 8, border: '1px solid var(--border)' }}>
              {EXPORT_COLS.map(c => (
                <label key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', userSelect: 'none' }}>
                  <input type="checkbox" checked={exportCols.includes(c.key)} onChange={() => toggleCol(c.key)} style={{ accentColor: 'var(--accent)', width: 15, height: 15 }} />
                  {c.label}
                </label>
              ))}
            </div>
          </div>
          <div className="modal-actions">
            <button className="btn" onClick={() => setExportModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={doExport} disabled={exportCols.length === 0}>Download Excel</button>
          </div>
        </Modal>
      )}

      {noticeModal && (() => {
        const props = properties.filter(p => !(p.businessType || '').toLowerCase().includes('construction'));
        const propertyTenants = tenants.filter(t => {
          if (noticeForm.propertyId !== 'all' && t.propertyId !== noticeForm.propertyId) return false;
          return t.status !== 'Previous';
        });
        const withEmail = propertyTenants.filter(t => t.email && t.email.trim());
        const withoutEmail = propertyTenants.filter(t => !t.email || !t.email.trim());
        const q = noticeSearch.trim().toLowerCase();
        const visibleWithEmail = q ? withEmail.filter(t => (t.name || '').toLowerCase().includes(q) || (t.unit || '').toLowerCase().includes(q) || (t.email || '').toLowerCase().includes(q)) : withEmail;
        const visibleWithoutEmail = q ? withoutEmail.filter(t => (t.name || '').toLowerCase().includes(q) || (t.unit || '').toLowerCase().includes(q)) : withoutEmail;
        const allSelected = withEmail.length > 0 && withEmail.every(t => noticeSelectedIds.has(t.id));
        const selectedCount = withEmail.filter(t => noticeSelectedIds.has(t.id)).length;
        const selectedEmails = withEmail.filter(t => noticeSelectedIds.has(t.id)).map(t => t.email.trim());

        const toggleAll = () => {
          if (allSelected) setNoticeSelectedIds(new Set());
          else setNoticeSelectedIds(new Set(withEmail.map(t => t.id)));
        };
        const toggleOne = id => setNoticeSelectedIds(prev => {
          const next = new Set(prev);
          next.has(id) ? next.delete(id) : next.add(id);
          return next;
        });
        const addToEmail = () => {
          const v = noticeToInput.trim();
          if (v && !noticeExtraTo.includes(v)) setNoticeExtraTo(prev => [...prev, v]);
          setNoticeToInput('');
        };
        const generateWithAI = async () => {
          if (!noticeAiPrompt.trim()) return;
          setNoticeAiLoading(true);
          try {
            const fn = httpsCallable(functions, 'generateNotice');
            const res = await fn({ prompt: noticeAiPrompt });
            nff('subject')(res.data.subject);
            nff('body')(res.data.body);
          } catch (e) {
            toast('AI generation failed: ' + e.message, 'error');
          }
          setNoticeAiLoading(false);
        };
        const addAttachments = async (e) => {
          const files = Array.from(e.target.files || []);
          if (!files.length) return;
          e.target.value = '';
          const toBase64 = f => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result.split(',')[1]); r.onerror = rej; r.readAsDataURL(f); });
          const newAtts = await Promise.all(files.map(async f => ({ name: f.name, mimeType: f.type || 'application/octet-stream', data: await toBase64(f), size: f.size })));
          setNoticeAttachments(prev => [...prev, ...newAtts]);
        };
        const sendNotice = async () => {
          if (!noticeForm.subject.trim() || !noticeForm.body.trim()) return;
          setNoticeSending(true);
          setNoticeResult(null);
          try {
            const fn = httpsCallable(functions, 'sendTenantNotice');
            const res = await fn({ tenantIds: [...noticeSelectedIds], extraTo: noticeExtraTo, type: noticeType, subject: noticeForm.subject, body: noticeForm.body, attachments: noticeAttachments });
            setNoticeResult(res.data);
          } catch (e) {
            setNoticeResult({ sent: 0, failed: 0, errors: [e.message] });
          }
          setNoticeSending(false);
        };
        const closeModal = () => { setNoticeModal(false); setNoticeResult(null); setNoticePage(1); setNoticeAttachments([]); };

        const isDirect = noticeType === 'direct';
        const pageTitle = noticeResult ? 'Notice Sent' : noticePage === 1 ? 'Send Notice — Recipients' : 'Send Notice — Compose';

        return (
          <Modal title={pageTitle} onClose={closeModal} lg>
            {noticeResult ? (
              /* ── Result ── */
              <div>
                <div style={{ textAlign: 'center', padding: '16px 0' }}>
                  <div style={{ fontSize: 40, fontWeight: 700, color: 'var(--green-txt)' }}>{noticeResult.sent}</div>
                  <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 12 }}>email{noticeResult.sent !== 1 ? 's' : ''} sent successfully</div>
                  {noticeResult.failed > 0 && <div style={{ fontSize: 13, color: 'var(--red-txt)', marginBottom: 8 }}>{noticeResult.failed} failed</div>}
                  {(noticeResult.errors || []).length > 0 && (
                    <div style={{ background: 'var(--bg2)', borderRadius: 8, padding: 10, textAlign: 'left', fontSize: 12, color: 'var(--text2)', maxHeight: 120, overflowY: 'auto' }}>
                      {noticeResult.errors.map((e, i) => <div key={i}>{e}</div>)}
                    </div>
                  )}
                </div>
                <div className="modal-actions">
                  <button className="btn btn-primary" onClick={closeModal}>Done</button>
                </div>
              </div>
            ) : noticePage === 1 ? (
              /* ── Page 1: Property + Recipients ── */
              <div>
                {/* Message type toggle */}
                <div className="form-group">
                  <label className="form-label">Message type</label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    {[
                      { value: 'general', label: 'General Notice', desc: 'To: office · BCC: tenants' },
                      { value: 'direct',  label: 'Direct Message', desc: 'To: tenant · CC: office' },
                    ].map(opt => (
                      <button key={opt.value} onClick={() => { setNoticeType(opt.value); setNoticeSelectedIds(new Set()); }}
                        style={{ textAlign: 'left', padding: '10px 14px', borderRadius: 8, border: `2px solid ${noticeType === opt.value ? 'var(--accent)' : 'var(--border)'}`, background: noticeType === opt.value ? 'var(--accent-bg)' : 'var(--bg2)', cursor: 'pointer', transition: 'all 0.15s' }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: noticeType === opt.value ? 'var(--accent)' : 'var(--text1)' }}>{opt.label}</div>
                        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{opt.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Property</label>
                  <select className="form-input" value={noticeForm.propertyId} onChange={e => { nff('propertyId')(e.target.value); setNoticeSelectedIds(new Set()); setNoticeSearch(''); }}>
                    <option value="all">All Properties</option>
                    {props.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Recipients</label>
                  <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                    <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
                      <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)', pointerEvents: 'none' }}>
                        {ic('M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z', 14)}
                      </span>
                      <input className="form-input" style={{ paddingLeft: 32, fontSize: 13 }} placeholder="Search by name, unit, or email…" value={noticeSearch} onChange={e => setNoticeSearch(e.target.value)} />
                    </div>
                    {withEmail.length > 0 && (
                      <button className="btn-ghost" style={{ fontSize: 12, whiteSpace: 'nowrap', flexShrink: 0 }} onClick={toggleAll}>
                        {allSelected ? 'Deselect all' : `Select all (${withEmail.length})`}
                      </button>
                    )}
                  </div>
                  <div className="thin-scroll" style={{ border: '1px solid var(--border)', borderRadius: 8, maxHeight: 280, overflowY: 'auto' }}>
                    {propertyTenants.length === 0 ? (
                      <div style={{ padding: 14, fontSize: 13, color: 'var(--text3)', textAlign: 'center' }}>No tenants found for this property</div>
                    ) : [...visibleWithEmail, ...visibleWithoutEmail].length === 0 ? (
                      <div style={{ padding: 14, fontSize: 13, color: 'var(--text3)', textAlign: 'center' }}>No results for "{noticeSearch}"</div>
                    ) : (
                      [...visibleWithEmail, ...visibleWithoutEmail].map(t => {
                        const hasEmail = !!(t.email && t.email.trim());
                        return (
                          <label key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderBottom: '1px solid var(--border)', cursor: hasEmail ? 'pointer' : 'default', opacity: hasEmail ? 1 : 0.4 }}>
                            <input type="checkbox" checked={noticeSelectedIds.has(t.id)} onChange={() => hasEmail && toggleOne(t.id)} disabled={!hasEmail} style={{ accentColor: 'var(--accent)', width: 15, height: 15, flexShrink: 0 }} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 500 }}>{t.name} <span style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 400 }}>· Unit {t.unit}</span></div>
                              <div style={{ fontSize: 11, color: hasEmail ? 'var(--text3)' : 'var(--red-txt)' }}>{hasEmail ? t.email : 'No email on file'}</div>
                            </div>
                          </label>
                        );
                      })
                    )}
                  </div>
                  <div style={{ fontSize: 12, marginTop: 6, color: selectedCount > 0 ? 'var(--green-txt)' : 'var(--text3)', fontWeight: selectedCount > 0 ? 600 : 400 }}>
                    {selectedCount > 0 ? `${selectedCount} tenant${selectedCount !== 1 ? 's' : ''} selected` : 'No tenants selected'}
                  </div>
                  </div>
                </div>

                <div className="modal-actions">
                  <button className="btn" onClick={closeModal}>Cancel</button>
                  <button className="btn btn-primary" disabled={selectedCount === 0} onClick={() => setNoticePage(2)}>
                    Next →
                  </button>
                </div>
              </div>
            ) : (
              /* ── Page 2: Compose ── */
              <div>
                {/* AI Generate */}
                <div className="form-group" style={{ background: 'var(--bg2)', borderRadius: 10, padding: 12, border: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent)' }}>✦ Generate with AI</span>
                    <span style={{ fontSize: 11, color: 'var(--text3)' }}>Describe the notice and AI writes it for you</span>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input className="form-input" style={{ flex: 1, fontSize: 13 }} placeholder="e.g. Remind tenants rent is due June 1st, late fee applies after the 5th" value={noticeAiPrompt} onChange={e => setNoticeAiPrompt(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') generateWithAI(); }} />
                    <button className="btn btn-primary" style={{ flexShrink: 0 }} onClick={generateWithAI} disabled={!noticeAiPrompt.trim() || noticeAiLoading}>
                      {noticeAiLoading ? 'Writing…' : 'Generate'}
                    </button>
                  </div>
                </div>

                {/* To / BCC summary */}
                <div className="form-group" style={{ background: 'var(--bg2)', borderRadius: 10, padding: 12, border: '1px solid var(--border)', fontSize: 13 }}>
                  {isDirect ? (
                    /* Direct: To = tenant emails, CC = office */
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 600, color: 'var(--text2)', minWidth: 30 }}>To:</span>
                        {selectedEmails.slice(0, 3).map((email, i) => (
                          <span key={i} style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 6, padding: '2px 8px', fontSize: 12 }}>{email}</span>
                        ))}
                        {selectedEmails.length > 3 && (
                          <span style={{ fontSize: 12, color: 'var(--text3)', fontWeight: 500 }}>+{selectedEmails.length - 3} more</span>
                        )}
                        <span style={{ fontSize: 11, color: 'var(--text3)', marginLeft: 4 }}>({selectedEmails.length} tenant{selectedEmails.length !== 1 ? 's' : ''})</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 600, color: 'var(--text2)', minWidth: 30 }}>CC:</span>
                        <span style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 6, padding: '2px 8px', fontSize: 12 }}>office@amarmgmt.com</span>
                      </div>
                    </div>
                  ) : (
                    /* General: To = office + extras, BCC = tenants */
                    <>
                      <div style={{ marginBottom: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                          <span style={{ fontWeight: 600, color: 'var(--text2)', minWidth: 30, paddingTop: 3 }}>To:</span>
                          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, alignItems: 'center' }}>
                              <span style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 6, padding: '2px 8px', fontSize: 12 }}>office@amarmgmt.com</span>
                              {noticeExtraTo.map((email, i) => (
                                <span key={i} style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 6, padding: '2px 8px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                                  {email}
                                  <button onClick={() => setNoticeExtraTo(prev => prev.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', padding: 0, lineHeight: 1, fontSize: 14 }}>×</button>
                                </span>
                              ))}
                            </div>
                            <div style={{ display: 'flex', gap: 6 }}>
                              <input className="form-input" style={{ flex: 1, fontSize: 12, padding: '4px 8px' }} placeholder="Add another email…" value={noticeToInput} onChange={e => setNoticeToInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addToEmail(); }} />
                              <button className="btn" style={{ fontSize: 12, padding: '4px 10px', flexShrink: 0 }} onClick={addToEmail} disabled={!noticeToInput.trim()}>+ Add</button>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 600, color: 'var(--text2)', minWidth: 30 }}>BCC:</span>
                        {selectedEmails.slice(0, 3).map((email, i) => (
                          <span key={i} style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 6, padding: '2px 8px', fontSize: 12 }}>{email}</span>
                        ))}
                        {selectedEmails.length > 3 && (
                          <span style={{ fontSize: 12, color: 'var(--text3)', fontWeight: 500 }}>+{selectedEmails.length - 3} more</span>
                        )}
                        <span style={{ fontSize: 11, color: 'var(--text3)', marginLeft: 4 }}>({selectedEmails.length} tenant{selectedEmails.length !== 1 ? 's' : ''})</span>
                      </div>
                    </>
                  )}
                </div>

                {/* Subject */}
                <div className="form-group">
                  <label className="form-label">Subject</label>
                  <input className="form-input" placeholder="e.g. Rent Reminder — June 2026" value={noticeForm.subject} onChange={e => nff('subject')(e.target.value)} />
                </div>

                {/* Body */}
                <div className="form-group">
                  <label className="form-label">Message body</label>
                  <textarea className="form-input" rows={7} placeholder="Type your message here, or use AI to generate it above…" value={noticeForm.body} onChange={e => nff('body')(e.target.value)} style={{ resize: 'vertical', fontFamily: 'inherit' }} />
                </div>

                {/* Attachments */}
                <div className="form-group">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: noticeAttachments.length ? 8 : 0 }}>
                    <label className="form-label" style={{ margin: 0 }}>Attachments <span style={{ color: 'var(--text3)', fontWeight: 400 }}>(optional)</span></label>
                    <label style={{ cursor: 'pointer' }}>
                      <input type="file" multiple style={{ display: 'none' }} onChange={addAttachments} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xlsx,.xls,.txt" />
                      <span className="btn-ghost" style={{ fontSize: 12, pointerEvents: 'none' }}>{ic(I.plus, 12)} Attach file</span>
                    </label>
                  </div>
                  {noticeAttachments.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {noticeAttachments.map((att, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: '4px 10px', fontSize: 12 }}>
                          {ic(I.file, 12, 'var(--text2)')}
                          <span style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{att.name}</span>
                          <span style={{ color: 'var(--text3)', fontSize: 11 }}>{fmtSize(att.size)}</span>
                          <button onClick={() => setNoticeAttachments(prev => prev.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', padding: 0, lineHeight: 1, fontSize: 14, marginLeft: 2 }}>×</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="modal-actions">
                  <button className="btn" onClick={() => setNoticePage(1)}>← Back</button>
                  <button className="btn btn-primary" onClick={sendNotice} disabled={noticeSending || !noticeForm.subject.trim() || !noticeForm.body.trim()}>
                    {noticeSending ? 'Sending…' : `Send to ${selectedCount} tenant${selectedCount !== 1 ? 's' : ''}`}
                  </button>
                </div>
              </div>
            )}
          </Modal>
        );
      })()}
    </div>
  );
}

// ── Task Detail ────────────────────────────────────────────────────────────
function TaskDetail({ taskId, tasks, setTasks, maintenance, setMaintenance, properties, tenants, users, user, onBack }) {
  const toast = useToast();
  const task = (tasks || []).find(t => t.id === taskId);
  const [addingNote, setAddingNote] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');
  const [editingNoteIdx, setEditingNoteIdx] = useState(null);
  const [editNoteDraft, setEditNoteDraft] = useState('');
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [renamingDoc, setRenamingDoc] = useState(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [editingDetails, setEditingDetails] = useState(false);
  const [editForm, setEditForm] = useState({});
  // Inline edits on the merged task card
  const [editingFollowUp, setEditingFollowUp] = useState(false);
  const [editingAssigned, setEditingAssigned] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [showAudit, setShowAudit] = useState(false); // swaps the whole detail view
  const [taskAudit, setTaskAudit] = useState(null);  // null = still loading
  const [editingDesc, setEditingDesc] = useState(false);
  const [descDraft, setDescDraft] = useState('');
  const [docView, setDocView] = useState('list'); // 'list' | 'grid'
  const ef2 = k => v => setEditForm(p => ({ ...p, [k]: v }));
  const [improvingDesc, setImprovingDesc] = useState(false);
  const [descSuggestion, setDescSuggestion] = useState(null);
  const [improvingTitle, setImprovingTitle] = useState(false);
  const [titleSuggestion, setTitleSuggestion] = useState(null);
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  const [promoteModal, setPromoteModal] = useState(false);
  const [promoteForm, setPromoteForm] = useState({});
  const togglePromoteUnit = val => setPromoteForm(p => { const cur = p.units || []; return { ...p, units: cur.includes(val) ? cur.filter(u => u !== val) : [...cur, val] }; });
  const [lightboxDoc, setLightboxDoc] = useState(null);
  const isImageDoc = name => /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(name || '');
  const [uploadingChecklistPhoto, setUploadingChecklistPhoto] = useState(null);
  const [openNoteItems, setOpenNoteItems] = useState({});
  const toggleNoteItem = id => setOpenNoteItems(p => ({ ...p, [id]: !p[id] }));
  const [submitModal, setSubmitModal] = useState(false);
  const [signatureModal, setSignatureModal] = useState(false);
  const [leaseFormModal, setLeaseFormModal] = useState(false);
  const [leaseForm, setLeaseForm] = useState({});
  const [sendingLease, setSendingLease] = useState(false);
  const [downloadingLease, setDownloadingLease] = useState(false);
  const lf = k => v => setLeaseForm(p => ({ ...p, [k]: v }));
  // Rent stabilized renewals use the DHCR RTP-8 packet, which asks for different
  // information than the market-rate lease — a rent table quoted cell by cell, a
  // sprinkler disclosure, and a rider that is signed separately.
  const [stabFormModal, setStabFormModal] = useState(false);
  const [stabForm, setStabForm] = useState({});
  const [sendingStab, setSendingStab] = useState(false);
  const sf = k => v => setStabForm(p => ({ ...p, [k]: v }));
  // A lease term runs to the day before the anniversary: commence 08/01/2026 and a
  // one year term ends 07/31/2027, not 08/01. Counting 365 days gets this wrong in a
  // leap year, so step the calendar year and subtract a day.
  const termEndISO = (startISO, years) => {
    if (!startISO) return '';
    const [y, m, d] = startISO.split('-').map(Number);
    const dt = new Date(Date.UTC(y + years, m - 1, d));
    dt.setUTCDate(dt.getUTCDate() - 1);
    return dt.toISOString().slice(0, 10);
  };

  const openLeaseForm = () => {
    const tenant   = (tenants   || []).find(t => t.id === task.tenantId);
    const property = (properties|| []).find(p => p.id === task.propertyId);
    // Commencement, expiration, and rent are intentionally NOT pre-filled —
    // the owner must enter them manually (required before sending).
    setLeaseForm({
      ownerName:        property?.entityName || property?.name || 'Amar Management',
      ownerAddress:     property?.address || '',
      tenantName:       tenant?.name      || '',
      tenantEmail:      tenant?.email     || '',
      propertyAddress:  property?.address || '',
      unitNumber:       tenant?.unit      || '',
      tenantAddress:    (property?.address || '') + (tenant?.unit ? ', Unit ' + tenant.unit : ''),
      leaseTerm:        '1 Year',
      commencementDate: '',
      expirationDate:   '',
      monthlyRent:      '',
      securityDeposit:  '',
      originalLeaseDate: tenant?.leaseStart || '',
    });
    setSignatureModal(false);
    setLeaseFormModal(true);
  };

  const openStabForm = () => {
    const tenant   = (tenants   || []).find(t => t.id === task.tenantId);
    const property = (properties|| []).find(p => p.id === task.propertyId);
    const today    = nyTodayISO();
    // Rents, guideline figures and the owner's mailing address are deliberately left
    // blank. The guideline arithmetic on a filed RTP-8 does not always follow from the
    // percentage printed beside it, so these are transcribed from what the owner
    // actually intends to file rather than computed here.
    setStabForm({
      ownerName:        property?.entityName || property?.name || '',
      ownerAddress:     '',
      tenantName:       tenant?.name  || '',
      tenantEmail:      tenant?.email || '',
      propertyAddress:  property?.address || '',
      unitNumber:       tenant?.unit || '',
      offerDate:           today,
      currentLeaseExpires: tenant?.leaseEnd || '',
      colB: '', colC1pct: '', colC1amt: '', colC2pct: '', colC2amt: '',
      colD1: '', colD2: '', colE1: '', colE2: '', colF1: '', colF2: '',
      separateCharges: '0',
      rent1Yr: '', rent2Yr: '',
      commencementDate: '', termination1Yr: '', termination2Yr: '',
      sprinkler: 'does',
      sprinklerInspectedOn: '',
      selectedTerm: '1 Year',
      leaseDated: '',
      _touched: false,
    });
    setSignatureModal(false);
    setStabFormModal(true);
  };

  const submitStabLease = async () => {
    const required = ['ownerName', 'ownerAddress', 'tenantEmail', 'commencementDate', 'colB', 'rent1Yr', 'leaseDated'];
    if (required.some(k => !String(stabForm[k] || '').trim())) {
      setStabForm(p => ({ ...p, _touched: true }));
      toast('Please fill in every required field before sending', 'error');
      return;
    }
    setSendingStab(true);
    try {
      const fn = httpsCallable(functions, 'sendStabilizedLeaseForSignature');
      await fn({ taskId, formData: stabForm });
      setStabFormModal(false);
      toast('Rent stabilized renewal sent for signature');
    } catch (err) {
      toast('Failed to send: ' + (err?.message || 'unknown error'), 'error');
    }
    setSendingStab(false);
  };

  const submitLease = async () => {
    if (!leaseForm.tenantEmail) { toast('Tenant email is required', 'error'); return; }
    if (!leaseForm.originalLeaseDate || !leaseForm.commencementDate || !leaseForm.expirationDate || !String(leaseForm.monthlyRent || '').trim()) {
      setLeaseForm(p => ({ ...p, _touched: true }));
      toast('Please fill in all required fields before sending', 'error');
      return;
    }
    setSendingLease(true);
    try {
      const fn = httpsCallable(functions, 'sendLeaseForSignature');
      await fn({ taskId, formData: leaseForm });
      setLeaseFormModal(false);
      toast('Lease sent for signature!');
    } catch (e) {
      toast(e.message || 'Failed to send lease', 'error');
    }
    setSendingLease(false);
  };

  const downloadSignedLease = async () => {
    setDownloadingLease(true);
    try {
      const fn = httpsCallable(functions, 'downloadSignedLease');
      await fn({ taskId });
      toast('Signed lease attached to Documents!');
    } catch (e) {
      toast(e.message || 'Download failed', 'error');
    }
    setDownloadingLease(false);
  };

  const SIG_STATUSES = [
    { value: 'sent',    label: 'Lease Sent for Signature',  color: 'var(--blue-txt)',  bg: 'var(--blue-bg)'  },
    { value: 'pending', label: 'Lease Pending Signature',   color: 'var(--amber-txt)', bg: 'var(--amber-bg)' },
    { value: 'signed',  label: 'Lease Signed',              color: 'var(--green-txt)', bg: 'var(--green-bg)' },
  ];
  const updateSignature = patch => setTasks(prev => prev.map(t => t.id === taskId ? { ...t, ...patch, updatedAt: nyTodayISO() } : t));

  if (!task) return null;

  const openEditDetails = () => {
    setEditForm({
      title: task.title || '',
      description: task.description || '',
      followUp: task.followUp || '',
      category: taskCategory(task),
      propertyId: task.propertyId || '',
      tenantId: task.tenantId || '',
      assignedTo: taskAssignedList(task),
      assignedToIds: taskAssignedIds(task),
    });
    setEditingDetails(true);
  };

  // Assignable people: the managed-user record names only. These must match the name
  // used for per-user visibility — never the Google display name, which can differ.
  // `extra` keeps any name already on the task so existing assignments stay selectable.
  const assignUserOptions = (extra = []) => {
    const s = new Set((users || []).map(u => u.name).filter(Boolean));
    extra.forEach(n => n && s.add(n));
    return [...s].sort((a, b) => a.localeCompare(b)).map(n => ({ value: n, label: n }));
  };
  const taskAssignees = Array.isArray(task.assignedTo) ? task.assignedTo : (task.assignedTo ? [task.assignedTo] : []);

  const saveDetails = () => {
    if (!editForm.title?.trim()) { toast('Title is required', 'error'); return; }
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, ...editForm, updatedAt: nyTodayISO() } : t));
    setEditingDetails(false);
    toast('Task updated');
  };
  // Direct field patch for the inline edits on the merged card.
  const patchTask = patch => setTasks(prev => prev.map(t => t.id === taskId ? { ...t, ...patch, updatedAt: nyTodayISO() } : t));

  // Every audit entry recorded against THIS task, so its history can be read here instead
  // of hunting for it in the Audit Log tab. Queried on docId alone — adding a second
  // equality clause or an orderBy would demand a composite index, so `tasks` rows are
  // filtered and sorted in memory (one task's history is always a small set).
  const openTaskAudit = async () => {
    setShowAudit(true);
    setTaskAudit(null);
    try {
      const snap = await getDocs(query(collection(db, 'auditLog'), where('docId', '==', taskId)));
      setTaskAudit(snap.docs
        .map(d => d.data())
        .filter(e => !e.collection || e.collection === 'tasks')
        .sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || '')));
    } catch (err) {
      console.error('Task audit load error:', err);
      setTaskAudit([]);
      toast('Could not load this task\'s history', 'error');
    }
  };

  const saveTitle = () => {
    const next = titleDraft.trim();
    // The title is how the task is identified everywhere — never let it be blanked.
    if (!next) { toast('Task name cannot be empty', 'error'); return; }
    if (next !== task.title) patchTask({ title: next });
    setEditingTitle(false);
  };

  const linkedMaint = task.maintenanceId ? (maintenance || []).find(m => m.id === task.maintenanceId) : null;
  const isInspection = taskCategory(task) === 'Inspection';
  const inspectionData = task.inspectionData || {};
  const checklist = (inspectionData.checklist && inspectionData.checklist.length) ? inspectionData.checklist : makeDefaultChecklist();

  const updateInspection = patch => setTasks(prev => prev.map(t => t.id === taskId ? { ...t, inspectionData: { ...(t.inspectionData || {}), ...patch }, updatedAt: nyTodayISO() } : t));
  const updateChecklistItem = (idx, patch) => {
    const next = checklist.map((item, i) => i === idx ? { ...item, ...patch } : item);
    updateInspection({ checklist: next });
  };
  const handleChecklistPhoto = async (idx, e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    setUploadingChecklistPhoto(idx);
    try {
      const item = checklist[idx];
      const path = `tasks/${taskId}/inspection/${item.id}_${Date.now()}_${file.name}`;
      const storageRef = sRef(storage, path);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      updateChecklistItem(idx, { photoUrl: url, photoStoragePath: path });
    } catch { /* silent */ }
    setUploadingChecklistPhoto(null);
  };
  const removeChecklistPhoto = async idx => {
    const item = checklist[idx];
    if (item.photoStoragePath) { try { await deleteObject(sRef(storage, item.photoStoragePath)); } catch { /* ignore */ } }
    updateChecklistItem(idx, { photoUrl: '', photoStoragePath: '' });
  };

  const exportInspectionPDF = () => {
    const prop = properties.find(p => p.id === task.propertyId)?.name || '';
    const tnt = task.tenantId ? (tenants || []).find(t => t.id === task.tenantId)?.name : null;
    const cl = (inspectionData.checklist && inspectionData.checklist.length) ? inspectionData.checklist : makeDefaultChecklist();
    const statusLabel = s => s === 'pass' ? '<span style="color:green;font-weight:600">Pass</span>' : s === 'fail' ? '<span style="color:#c00;font-weight:600">Fail</span>' : s === 'na' ? '<span style="color:#999">N/A</span>' : '<span style="color:#bbb">—</span>';
    const rows = cl.map(item => `<tr><td style="font-weight:600;padding:10px 12px;border:1px solid #ddd;vertical-align:top">${item.label}</td><td style="padding:10px 12px;border:1px solid #ddd;text-align:center;vertical-align:top">${statusLabel(item.status)}</td><td style="padding:10px 12px;border:1px solid #ddd;vertical-align:top;font-size:13px">${item.notes || ''}</td><td style="padding:10px 12px;border:1px solid #ddd;vertical-align:top">${item.photoUrl ? `<img src="${item.photoUrl}" style="max-width:180px;max-height:130px;border-radius:6px;display:block"/>` : ''}</td></tr>`).join('');
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Inspection Report</title><style>body{font-family:Arial,sans-serif;margin:40px;color:#111;font-size:14px}h2{margin:0 0 4px}table{width:100%;border-collapse:collapse;margin-top:20px}th{background:#f0f0f0;padding:10px 12px;text-align:left;border:1px solid #ddd;font-size:12px}@media print{body{margin:20px}}</style></head><body>
<div style="border-bottom:2px solid #111;padding-bottom:14px;margin-bottom:20px">
  <div style="font-size:11px;color:#888;text-transform:uppercase;letter-spacing:1px;margin-bottom:6px">Amar Management — Inspection Report</div>
  <h2>${task.title}</h2>
</div>
<div style="display:flex;flex-wrap:wrap;gap:30px;font-size:13px;margin-bottom:20px">
  ${prop ? `<div><div style="font-size:10px;text-transform:uppercase;color:#888;margin-bottom:2px">Property</div>${prop}</div>` : ''}
  ${tnt ? `<div><div style="font-size:10px;text-transform:uppercase;color:#888;margin-bottom:2px">Tenant</div>${tnt}</div>` : ''}
  <div><div style="font-size:10px;text-transform:uppercase;color:#888;margin-bottom:2px">Access Date</div>${inspectionData.accessDate || '—'}</div>
  <div><div style="font-size:10px;text-transform:uppercase;color:#888;margin-bottom:2px">Report Date</div>${new Date().toLocaleDateString('en-US')}</div>
</div>
${inspectionData.accessNotes ? `<div style="font-size:13px;margin-bottom:16px"><strong>Access Notes:</strong> ${inspectionData.accessNotes}</div>` : ''}
<table><thead><tr><th style="width:18%">Area</th><th style="width:10%;text-align:center">Status</th><th>Notes</th><th style="width:210px">Photo</th></tr></thead><tbody>${rows}</tbody></table>
</body></html>`;
    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 600);
  };

  const submitInspection = () => {
    const incomplete = checklist.filter(item => !item.status);
    if (incomplete.length > 0) {
      toast(`${incomplete.length} checklist item${incomplete.length > 1 ? 's still need' : ' still needs'} a status (Pass / Fail / N/A)`, 'error');
      return;
    }
    setSubmitModal(true);
  };

  const completeInspection = download => {
    if (download) exportInspectionPDF();
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: 'Close', updatedAt: nyTodayISO() } : t));
    setSubmitModal(false);
    toast('Inspection submitted and task closed');
  };

  const openPromote = () => {
    const tenant = task.tenantId ? (tenants || []).find(t => t.id === task.tenantId) : null;
    const unitStr = tenant ? `${tenant.unit} - ${tenant.name}` : '';
    setPromoteForm({
      propertyId: task.propertyId || (properties[0]?.id || ''),
      units: unitStr ? [unitStr] : [],
      title: task.title || '',
      description: task.description || '',
      status: 'open',
      date: new Date().toISOString().slice(0, 10),
      vendor: 'Jose',
      cost: '',
      recurring: false,
      recurringInterval: 'monthly',
    });
    setPromoteModal(true);
  };

  const savePromote = () => {
    if (!promoteForm.title?.trim()) { toast('Title is required', 'error'); return; }
    const unit = (promoteForm.units || []).join(', ');
    const newId = `m${Date.now()}`;
    setMaintenance(prev => [...(prev || []), { ...promoteForm, unit, id: newId, taskId: taskId }]);
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, maintenanceId: newId, updatedAt: nyTodayISO() } : t));
    setPromoteModal(false);
    toast('Maintenance record created and linked');
  };

  const propName = properties.find(p => p.id === task.propertyId)?.name || '';
  const authorName =
    (users || []).find(u => userMatchesEmail(u, user.email))?.name ||
    user.displayName ||
    user.email.split('@')[0];

  const updateNotes = notes => setTasks(prev => prev.map(t => t.id === taskId ? { ...t, notes } : t));
  const updateDocs = docs => setTasks(prev => prev.map(t => t.id === taskId ? { ...t, documents: docs } : t));

  const uploadFile = async file => {
    if (!file) return;
    setUploadingDoc(true);
    try {
      const path = `tasks/${taskId}/${Date.now()}_${file.name}`;
      const storageRef = sRef(storage, path);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      const newDoc = { id: `doc${Date.now()}`, name: file.name, url, storagePath: path, uploadedAt: new Date().toISOString() };
      updateDocs([...(task.documents || []), newDoc]);
    } catch (err) {
      alert('Upload failed. Please try again.');
    }
    setUploadingDoc(false);
  };

  const handleDocUpload = async e => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    await uploadFile(file);
  };

  const handleDocDrop = async e => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) await uploadFile(file);
  };

  const handleDocDelete = async docEntry => {
    if (!window.confirm(`Remove "${docEntry.name}"?`)) return;
    try {
      if (docEntry.storagePath) await deleteObject(sRef(storage, docEntry.storagePath));
    } catch (err) { /* ignore if already deleted */ }
    updateDocs((task.documents || []).filter(d => d.id !== docEntry.id));
  };

  const handleDocRename = () => {
    const name = renameDraft.trim();
    if (!name) return;
    updateDocs((task.documents || []).map(d => d.id === renamingDoc.id ? { ...d, name } : d));
    setRenamingDoc(null);
    setRenameDraft('');
    toast('Document renamed');
  };

  const addNote = () => {
    if (!noteDraft.trim()) return;
    updateNotes([...(task.notes || []), { date: nyTodayISO(), text: noteDraft.trim(), author: authorName }]);
    setNoteDraft('');
    setAddingNote(false);
    toast('Note added');
  };

  const callImproveTitle = async () => {
    if (!editForm.title?.trim()) return;
    setImprovingTitle(true);
    setTitleSuggestion(null);
    try {
      const fn = httpsCallable(functions, 'improveNote');
      const res = await fn({ text: editForm.title.trim() });
      setTitleSuggestion(res.data.improved);
    } catch (e) {
      toast('Could not improve title. Try again.', 'error');
    } finally {
      setImprovingTitle(false);
    }
  };

  // Rewrites whatever is in the inline description editor. mode:'description' keeps the
  // structure of a long write-up and sizes the token budget to the input; a truncated
  // rewrite is refused outright rather than offered as a replacement.
  const callImproveDesc = async () => {
    if (!descDraft.trim()) return;
    setImprovingDesc(true);
    setDescSuggestion(null);
    try {
      const fn = httpsCallable(functions, 'improveNote');
      const res = await fn({ text: descDraft.trim(), mode: 'description' });
      if (res.data.truncated) {
        toast('The description is too long to rewrite in one pass — nothing was changed.', 'error');
      } else {
        setDescSuggestion(res.data.improved);
      }
    } catch (e) {
      toast('Could not improve description. Try again.', 'error');
    } finally {
      setImprovingDesc(false);
    }
  };

  const startListening = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast('Speech recognition not supported in this browser', 'error'); return; }
    const rec = new SR();
    rec.lang = 'en-US';
    rec.interimResults = false;
    rec.continuous = false;
    rec.onresult = e => {
      const transcript = Array.from(e.results).map(r => r[0].transcript).join(' ');
      setNoteDraft(prev => prev ? prev + ' ' + transcript : transcript);
    };
    rec.onerror = () => { setListening(false); };
    rec.onend = () => { setListening(false); };
    rec.start();
    recognitionRef.current = rec;
    setListening(true);
  };

  const stopListening = () => {
    recognitionRef.current?.stop();
    setListening(false);
  };

  const deleteNote = idx => {
    if (!window.confirm('Delete this note?')) return;
    updateNotes((task.notes || []).filter((_, i) => i !== idx));
    toast('Note removed', 'info');
  };

  const startEditNote = (idx, text) => { setEditingNoteIdx(idx); setEditNoteDraft(text); };
  const saveEditNote = idx => {
    if (!editNoteDraft.trim()) return;
    const updated = (task.notes || []).map((n, i) => i === idx ? { ...n, text: editNoteDraft.trim() } : n);
    updateNotes(updated);
    setEditingNoteIdx(null);
    toast('Note updated');
  };

  const notes = task.notes || [];
  const active = taskActive(task);
  const isOD      = active && isTaskOverdue(task.followUp);
  const isDueToday = active && isTaskDueToday(task.followUp);

  // The kebab's "Audit Log" replaces this whole view with the task's history rather than
  // stacking a modal on top of it. Safe as an early return: every hook runs above.
  if (showAudit) {
    return (
      <div>
        <button className="back-btn" onClick={() => setShowAudit(false)}>{ic(I.arrowLeft, 15)} Back to task</button>

        <div className="card">
          <div className="card-title" style={{ marginBottom: 3, display: 'flex', alignItems: 'center', gap: 6 }}>
            {ic(I.clipboard, 14)} Audit log
          </div>
          <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 4 }}>{task.title || '—'}</div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 14 }}>
            {taskAudit === null ? 'Loading…'
              : `${taskAudit.length} ${taskAudit.length === 1 ? 'change' : 'changes'} recorded, newest first`}
          </div>

          {taskAudit === null ? (
            <div style={{ padding: 28, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>Loading history…</div>
          ) : taskAudit.length === 0 ? (
            <div style={{ padding: 28, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>No changes recorded for this task yet.</div>
          ) : (
            taskAudit.map((e, i) => (
              <div key={e.id || i} style={{ padding: '3px 0' }}>
                <div style={{ fontSize: 13, color: 'var(--text1)', lineHeight: 1.3 }}>
                  <span style={{ color: 'var(--text3)', fontSize: 12, marginRight: 6 }}>{auditWhenLabel(e)}:</span>
                  <span><Linkify text={e.summary || e.label || e.action || '—'} /></span>
                  <span style={{ color: 'var(--text3)', margin: '0 6px' }}>-</span>
                  <span style={{ color: 'var(--text3)', fontSize: 12 }}>{e.changedBy || '—'}</span>
                </div>
                {(e.changes || []).length > 0 && (
                  <div style={{ margin: '1px 0 6px 0' }}>
                    {e.changes.map((c, j) => <AuditChange key={j} change={c} />)}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <button className="back-btn" onClick={onBack}>{ic(I.arrowLeft, 15)} All tasks</button>

      {/* The title now sits inside the Task details card, so this block renders only when
          there's actually something to show — otherwise it left an empty 20px gap. */}
      {(isInspection || linkedMaint) && (
        <div style={{ marginBottom: 20 }}>
          {isInspection && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
              <button className="btn btn-primary" style={{ fontSize: 12, padding: '5px 14px' }} onClick={exportInspectionPDF}>
                {ic(I.download, 12, '#fff')} Export PDF
              </button>
            </div>
          )}

          {/* Maintenance link */}
          {linkedMaint && (
            <div style={{ marginTop: 4 }}>
              <span style={{ fontSize: 12, background: 'var(--green-bg)', color: 'var(--green-txt)', padding: '3px 10px', borderRadius: 20, fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                {ic(I.wrench, 11, 'var(--green-txt)')} Maintenance: {linkedMaint.title || task.maintenanceId}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Task info */}
      <div className="card" style={{ marginBottom: 16, position: 'relative', outline: (!isInspection && dragOver) ? '2px dashed var(--accent)' : '2px dashed transparent', transition: 'outline 0.15s' }}
        onDragOver={!isInspection ? e => { e.preventDefault(); setDragOver(true); } : undefined}
        onDragLeave={!isInspection ? e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false); } : undefined}
        onDrop={!isInspection ? handleDocDrop : undefined}>
        {!isInspection && dragOver && (
          <div style={{ position: 'absolute', inset: 0, borderRadius: 'inherit', background: 'var(--accent-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, pointerEvents: 'none' }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent)' }}>{ic(I.paperclip, 14, 'var(--accent)')} Drop to upload</span>
          </div>
        )}
        <div className="card-title" style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>{ic(I.tasks, 14)} Task details</span>
          <span style={{ color: 'var(--text3)', fontWeight: 400 }}>-</span>
          <select
            className="form-input"
            style={{ width: 'auto', fontSize: 12, padding: '3px 8px', borderRadius: 20, cursor: 'pointer', fontWeight: 600 }}
            value={taskStatus(task)}
            onChange={e => setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: e.target.value, updatedAt: nyTodayISO() } : t))}
          >
            <option value="Active">Active</option>
            <option value="Closed">Closed</option>
          </select>
          <div style={{ marginLeft: 'auto' }}>
            <KebabMenu items={[{ label: 'Audit Log', onClick: openTaskAudit }]} />
          </div>
        </div>

        {false ? (
          <div>
            <div className="form-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <label className="form-label" style={{ marginBottom: 0 }}>Title</label>
                {(editForm.title || '').trim() && (
                  <button className="btn" onClick={callImproveTitle} disabled={improvingTitle}
                    style={{ fontSize: 11, padding: '2px 10px', display: 'flex', alignItems: 'center', gap: 4 }}>
                    {improvingTitle ? '…' : '✨'} {improvingTitle ? 'Improving…' : 'Improve'}
                  </button>
                )}
              </div>
              <input className="form-input" value={editForm.title || ''} onChange={e => { ef2('title')(e.target.value); setTitleSuggestion(null); }} placeholder="Task title" />
              {titleSuggestion && (
                <div style={{ marginTop: 8, padding: '10px 12px', background: 'var(--accent-bg)', border: '1px solid var(--accent)', borderRadius: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--accent)', marginBottom: 5, textTransform: 'uppercase', letterSpacing: 0.5 }}>✨ Suggested improvement</div>
                  <div style={{ fontSize: 13, color: 'var(--text1)', lineHeight: 1.6, marginBottom: 8 }}>{titleSuggestion}</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-primary" style={{ fontSize: 12, padding: '4px 12px' }} onClick={() => { ef2('title')(titleSuggestion); setTitleSuggestion(null); }}>Use this</button>
                    <button className="btn" style={{ fontSize: 12, padding: '4px 12px' }} onClick={() => setTitleSuggestion(null)}>Dismiss</button>
                  </div>
                </div>
              )}
            </div>
            <div className="form-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <label className="form-label" style={{ marginBottom: 0 }}>Description</label>
                {(editForm.description || '').trim() && (
                  <button className="btn" onClick={callImproveDesc} disabled={improvingDesc}
                    style={{ fontSize: 11, padding: '2px 10px', display: 'flex', alignItems: 'center', gap: 4 }}>
                    {improvingDesc ? '…' : '✨'} {improvingDesc ? 'Improving…' : 'Improve'}
                  </button>
                )}
              </div>
              <textarea className="form-textarea" value={editForm.description || ''} onChange={e => { ef2('description')(e.target.value); setDescSuggestion(null); }} />
              {descSuggestion && (
                <div style={{ marginTop: 8, padding: '10px 12px', background: 'var(--accent-bg)', border: '1px solid var(--accent)', borderRadius: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--accent)', marginBottom: 5, textTransform: 'uppercase', letterSpacing: 0.5 }}>✨ Suggested improvement</div>
                  <div style={{ fontSize: 13, color: 'var(--text1)', lineHeight: 1.6, marginBottom: 8 }}>{descSuggestion}</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-primary" style={{ fontSize: 12, padding: '4px 12px' }} onClick={() => { ef2('description')(descSuggestion); setDescSuggestion(null); }}>Use this</button>
                    <button className="btn" style={{ fontSize: 12, padding: '4px 12px' }} onClick={() => setDescSuggestion(null)}>Dismiss</button>
                  </div>
                </div>
              )}
            </div>
            <div className="form-grid form-grid-2">
              <FI label="Follow-up date" value={editForm.followUp} onChange={ef2('followUp')} type="date" />
              <FS label="Category" value={editForm.category}
                onChange={v => setEditForm(p => {
                  const next = { ...p, category: v };
                  if (v === 'Inspection') {
                    if (!next.description) next.description = 'General Inspection';
                    if (next.tenantId && next.propertyId) {
                      const tenant = (tenants || []).find(t => t.id === next.tenantId);
                      const prop = properties.find(pr => pr.id === next.propertyId);
                      if (tenant && prop) next.title = `Unit ${tenant.unit} | ${tenant.name} | ${prop.name}`;
                    }
                  }
                  return next;
                })}
                options={[{ value: 'Inspection', label: 'Inspection' }, { value: 'Maintenance', label: 'Maintenance' }]} />
            </div>
            <FS
              label="Property (optional)"
              value={editForm.propertyId}
              onChange={v => setEditForm(p => ({ ...p, propertyId: v, tenantId: '' }))}
              options={[{ value: '', label: '— None —' }, ...properties.map(p => ({ value: p.id, label: p.name }))]}
            />
            {editForm.propertyId && (() => {
              const propTenants = (tenants || []).filter(t => t.propertyId === editForm.propertyId && t.status !== 'Vacant');
              if (!propTenants.length) return null;
              return (
                <FS
                  label={editForm.category === 'Inspection' ? 'Tenant / Unit *' : 'Linked tenant (optional)'}
                  value={editForm.tenantId || ''}
                  onChange={v => setEditForm(p => {
                    const next = { ...p, tenantId: v };
                    if (p.category === 'Inspection' && v) {
                      const tenant = propTenants.find(t => t.id === v);
                      const prop = properties.find(pr => pr.id === p.propertyId);
                      if (tenant && prop) next.title = `Unit ${tenant.unit} | ${tenant.name} | ${prop.name}`;
                    }
                    return next;
                  })}
                  options={[{ value: '', label: '— None —' }, ...propTenants.map(t => ({ value: t.id, label: `${t.name} · Unit ${t.unit}` }))]}
                />
              );
            })()}
            <MultiSearchSelect
              label="Assigned to"
              value={editForm.assignedTo || []}
              onChange={v => setEditForm(p => ({ ...p, ...withAssignees(v, users) }))}
              options={assignUserOptions(editForm.assignedTo)}
            />
          </div>
        ) : (
          <>
            {/* Details rows */}
            {(() => {
              const Row = ({ label, children }) => (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '5px 0', fontSize: 13 }}>
                  <span style={{ width: 110, flexShrink: 0, color: 'var(--text2)', fontWeight: 500 }}>{label}:</span>
                  <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
                </div>
              );
              return (
                <div>
                  {/* The task's name lives here rather than as a page heading above the card */}
                  <Row label="Task">
                    {editingTitle
                      ? <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                          <input className="form-input" style={{ flex: 1, minWidth: 220, fontSize: 13, padding: '3px 8px', fontWeight: 600 }}
                            value={titleDraft} autoFocus
                            onChange={e => setTitleDraft(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') saveTitle(); if (e.key === 'Escape') setEditingTitle(false); }} />
                          <button className="btn btn-primary" style={{ fontSize: 12, padding: '3px 12px' }} onClick={saveTitle}>Save</button>
                          <button className="btn" style={{ fontSize: 12, padding: '3px 12px' }} onClick={() => setEditingTitle(false)}>Cancel</button>
                        </div>
                      : <span style={{ display: 'inline-flex', alignItems: 'flex-start', gap: 6, minWidth: 0 }}>
                          <span style={{ color: task.title ? 'var(--text1)' : 'var(--text3)', fontWeight: 600 }}>{task.title || '—'}</span>
                          <button className="btn-ghost" title="Rename this task" style={{ padding: '1px 5px', flexShrink: 0 }}
                            onClick={() => { setTitleDraft(task.title || ''); setEditingTitle(true); }}>
                            {ic(I.pencil, 12)}
                          </button>
                        </span>}
                  </Row>
                  <Row label="Property"><span style={{ color: propName ? 'var(--text1)' : 'var(--text3)' }}>{propName || '—'}</span></Row>
                  {task.tenantId && (() => {
                    const tn = (tenants || []).find(x => x.id === task.tenantId);
                    return tn ? <Row label="Tenant"><span style={{ color: 'var(--text1)' }}>{tn.name}{tn.unit ? ` · Unit ${tn.unit}` : ''}</span></Row> : null;
                  })()}
                  <Row label="Category"><TaskCatBadge task={task} /></Row>
                  <Row label="Created"><span style={{ color: 'var(--text1)' }}>{task.createdAt ? fmtDate(task.createdAt) : '—'}</span></Row>
                  <Row label="Follow up">
                    {editingFollowUp
                      ? <input type="date" className="form-input" style={{ width: 'auto', fontSize: 13, padding: '3px 8px' }} defaultValue={task.followUp || ''} autoFocus
                          onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') setEditingFollowUp(false); }}
                          onBlur={e => { if (e.target.value) patchTask({ followUp: e.target.value }); setEditingFollowUp(false); }} />
                      : <span className="edit-hint" title="Click to change" onClick={() => setEditingFollowUp(true)}
                          style={{ cursor: 'pointer', color: isOD ? 'var(--red-txt)' : isDueToday ? 'var(--amber-txt)' : 'var(--text1)', fontWeight: (isOD || isDueToday) ? 600 : 400, textDecoration: 'underline dotted', textUnderlineOffset: 3 }}>
                          {task.followUp ? `${isOD ? 'OVERDUE · ' : isDueToday ? 'DUE TODAY · ' : ''}${fmtDate(task.followUp)}` : 'Set date'}
                          <span className="edit-pencil">{ic(I.pencil, 11)}</span>
                        </span>}
                  </Row>
                  <Row label="Assigned to">
                    {editingAssigned
                      ? <div>
                          <MultiSearchSelect label="" value={taskAssignees} onChange={v => patchTask(withAssignees(v, users))} options={assignUserOptions(taskAssignees)} />
                          <button className="btn-ghost" style={{ fontSize: 11, marginTop: 4 }} onClick={() => setEditingAssigned(false)}>Done</button>
                        </div>
                      : <span title="Click to assign" onClick={() => setEditingAssigned(true)} style={{ cursor: 'pointer', display: 'inline-flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                          {taskAssignees.length
                            ? taskAssignees.map(n => <span key={n} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, background: 'var(--bg3)', color: 'var(--text1)', borderRadius: 10, padding: '2px 9px', border: '1px solid var(--border)' }}>{ic(I.user, 11, 'var(--text2)')} {n}</span>)
                            : <span style={{ color: 'var(--text3)' }}>Unassigned</span>}
                          <span style={{ opacity: 0.5 }}>{ic(I.pencil, 11)}</span>
                        </span>}
                  </Row>
                </div>
              );
            })()}

            {/* Description */}
            <div style={{ borderTop: '1px solid var(--border)', marginTop: 14, paddingTop: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <div className="info-label" style={{ marginBottom: 0 }}>Description</div>
                {!editingDesc && <KebabMenu items={[{ label: 'Edit', onClick: () => { setDescDraft(task.description || ''); setEditingDesc(true); } }]} />}
              </div>
              {editingDesc
                ? <div>
                    <AutoTextarea className="form-textarea" minHeight={90} value={descDraft} onChange={e => { setDescDraft(e.target.value); setDescSuggestion(null); }} autoFocus />
                    {descSuggestion && (
                      <div style={{ marginTop: 8, padding: '10px 12px', background: 'var(--accent-bg)', border: '1px solid var(--accent)' }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--accent)', marginBottom: 5, textTransform: 'uppercase', letterSpacing: 0.5 }}>✨ Suggested rewrite</div>
                        <div style={{ fontSize: 13, color: 'var(--text1)', lineHeight: 1.6, marginBottom: 8, whiteSpace: 'pre-wrap', maxHeight: '40vh', overflowY: 'auto' }}>{descSuggestion}</div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button className="btn btn-primary" style={{ fontSize: 12, padding: '4px 12px' }} onClick={() => { setDescDraft(descSuggestion); setDescSuggestion(null); }}>Use this</button>
                          <button className="btn" style={{ fontSize: 12, padding: '4px 12px' }} onClick={() => setDescSuggestion(null)}>Dismiss</button>
                        </div>
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                      <button className="btn" onClick={() => { setEditingDesc(false); setDescSuggestion(null); }}>Cancel</button>
                      <button className="btn" onClick={callImproveDesc} disabled={improvingDesc || !descDraft.trim()}
                        title="Fix grammar and clarity with AI — you review before it replaces anything"
                        style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        {improvingDesc ? '…' : '✨'} {improvingDesc ? 'Rewriting…' : 'AI Suggest'}
                      </button>
                      <SavingBtn onClick={() => { patchTask({ description: descDraft }); setEditingDesc(false); setDescSuggestion(null); }}>Save</SavingBtn>
                    </div>
                  </div>
                : <div style={{ fontSize: 13, color: task.description ? 'var(--text1)' : 'var(--text3)', lineHeight: 1.65, whiteSpace: 'pre-wrap' }}><Linkify text={task.description || 'No description.'} /></div>}
            </div>

            {!isInspection && <>
            {/* Documents */}
            <div style={{ borderTop: '1px solid var(--border)', marginTop: 14, paddingTop: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: (task.documents || []).length ? 12 : 0, flexWrap: 'wrap', gap: 8 }}>
                <div className="info-label" style={{ marginBottom: 0 }}>Documents</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ display: 'inline-flex', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: 2 }}>
                    {[['list', 'List'], ['grid', 'Images']].map(([v, l]) => (
                      <button key={v} onClick={() => setDocView(v)} style={{ fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 6, border: 'none', cursor: 'pointer', background: docView === v ? 'var(--accent)' : 'transparent', color: docView === v ? '#fff' : 'var(--text2)' }}>{l}</button>
                    ))}
                  </div>
                  <label style={{ cursor: uploadingDoc ? 'not-allowed' : 'pointer' }}>
                    <input type="file" style={{ display: 'none' }} onChange={handleDocUpload} disabled={uploadingDoc} />
                    <span className="btn-ghost" style={{ pointerEvents: 'none' }}>{uploadingDoc ? 'Uploading…' : <>{ic(I.plus, 12)} Upload</>}</span>
                  </label>
                </div>
              </div>
              {(task.documents || []).length === 0 && !uploadingDoc && (<div style={{ fontSize: 13, color: 'var(--text3)' }}>No documents uploaded yet.</div>)}
              {docView === 'grid' ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                  {(task.documents || []).map(doc => (
                    <div key={doc.id} style={{ width: 120 }}>
                      {isImageDoc(doc.name)
                        ? <img src={doc.url} alt={doc.name} onClick={() => setLightboxDoc(doc)} style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 8, cursor: 'zoom-in', border: '1px solid var(--border)' }} />
                        : <a href={doc.url} target="_blank" rel="noreferrer" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: 120, height: 120, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg2)', textDecoration: 'none', color: 'var(--text2)', gap: 6 }}>{ic(I.file, 26, 'var(--text2)')}<span style={{ fontSize: 10, textTransform: 'uppercase' }}>{(doc.name || '').split('.').pop()}</span></a>}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
                        <span style={{ fontSize: 11, color: 'var(--text2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }} title={doc.name}>{doc.name}</span>
                        <KebabMenu items={[{ label: 'Open in new tab', onClick: () => window.open(doc.url, '_blank') }, { label: 'Edit name', onClick: () => { setRenamingDoc(doc); setRenameDraft(doc.name); } }, { label: 'Delete', danger: true, onClick: () => handleDocDelete(doc) }]} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                // Same treatment as Notes: no dividers, single-spaced, and the date leads
                // the line — "05/18/2026: HPD_Violation_Checklist.pdf".
                (task.documents || []).map(doc => {
                  const docDate = doc.uploadedAt ? fmtDate(doc.uploadedAt.slice(0, 10)) : '';
                  return (
                  <div key={doc.id}>
                    {isImageDoc(doc.name) ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '2px 0' }}>
                        <img src={doc.url} alt={doc.name} onClick={() => setLightboxDoc(doc)} style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 8, cursor: 'zoom-in', flexShrink: 0, border: '1px solid var(--border)' }} />
                        <div style={{ flex: 1, minWidth: 0, fontSize: 13, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {docDate && <span style={{ color: 'var(--text3)', fontSize: 12, marginRight: 6 }}>{docDate}:</span>}
                          <span onClick={() => setLightboxDoc(doc)} style={{ color: 'var(--accent)', cursor: 'pointer' }}>{doc.name}</span>
                        </div>
                        <KebabMenu compact items={[{ label: 'View', onClick: () => setLightboxDoc(doc) }, { label: 'Open in new tab', onClick: () => window.open(doc.url, '_blank') }, { label: 'Edit name', onClick: () => { setRenamingDoc(doc); setRenameDraft(doc.name); } }, { label: 'Delete', danger: true, onClick: () => handleDocDelete(doc) }]} />
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                        <div style={{ flex: 1, minWidth: 0, fontSize: 13, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {docDate && <span style={{ color: 'var(--text3)', fontSize: 12, marginRight: 6 }}>{docDate}:</span>}
                          <a href={doc.url} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)', textDecoration: 'none' }}>{doc.name}</a>
                        </div>
                        <div style={{ flexShrink: 0, marginLeft: 8 }}>
                          <KebabMenu compact items={[{ label: 'Edit name', onClick: () => { setRenamingDoc(doc); setRenameDraft(doc.name); } }, { label: 'Delete', danger: true, onClick: () => handleDocDelete(doc) }]} />
                        </div>
                      </div>
                    )}
                  </div>
                  );
                })
              )}
            </div>

            {/* Notes */}
            <div style={{ borderTop: '1px solid var(--border)', marginTop: 14, paddingTop: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: notes.length || addingNote ? 12 : 0 }}>
                <div className="info-label" style={{ marginBottom: 0 }}>Notes</div>
                {!addingNote && (<button className="btn-ghost" onClick={() => { setAddingNote(true); setNoteDraft(''); }}>{ic(I.plus, 12)} Add note</button>)}
              </div>
              {notes.length > 0 && (
                <div style={{ marginBottom: addingNote ? 12 : 0, display: 'flex', flexDirection: 'column' }}>
                  {notes.map((n, i) => (
                    <div key={i}>
                      {editingNoteIdx === i ? (
                        <div style={{ padding: '8px 0' }}>
                          <textarea className="form-textarea" style={{ minHeight: 64, marginBottom: 8, width: '100%' }} value={editNoteDraft} onChange={e => setEditNoteDraft(e.target.value)} autoFocus />
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button className="btn" onClick={() => setEditingNoteIdx(null)}>Cancel</button>
                            <button className="btn btn-primary" onClick={() => saveEditNote(i)}>Save</button>
                          </div>
                        </div>
                      ) : (
                        // One tight line per note, no divider: "05/18/2026: the note text - Author"
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div style={{ fontSize: 13, color: 'var(--text1)', lineHeight: 1.3, flex: 1, minWidth: 0 }}>
                            <span style={{ color: 'var(--text3)', fontSize: 12, marginRight: 6 }}>{fmtDate(n.date)}:</span>
                            <span style={{ whiteSpace: 'pre-wrap' }}><Linkify text={n.text} /></span>
                            <span style={{ color: 'var(--text3)', margin: '0 6px' }}>-</span>
                            {/* Notes written through the API historically set `by` instead
                                of `author`, which rendered as a blank name. */}
                            <span style={{ color: 'var(--text3)', fontSize: 12 }}>{n.author || n.by}</span>
                          </div>
                          <div style={{ flexShrink: 0, marginLeft: 8 }}>
                            <KebabMenu compact items={[{ label: 'Edit', onClick: () => startEditNote(i, n.text) }, { label: 'Delete', danger: true, onClick: () => deleteNote(i) }]} />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {!notes.length && !addingNote && (<div style={{ fontSize: 13, color: 'var(--text3)' }}>No notes yet.</div>)}
              {addingNote && (
                <div>
                  <textarea className="form-textarea" style={{ minHeight: 72, marginBottom: 8, width: '100%' }} value={noteDraft} onChange={e => setNoteDraft(e.target.value)} placeholder="Write a note…" autoFocus />
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <button className="btn" onClick={() => { setAddingNote(false); setNoteDraft(''); }}>Cancel</button>
                    <SavingBtn onClick={addNote}>Add</SavingBtn>
                  </div>
                </div>
              )}
            </div>
            </>}
          </>
        )}
      </div>

      {/* Inspection Checklist */}
      {isInspection && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 16 }}>
            <div className="card-title" style={{ margin: 0 }}>{ic(I.clipboard, 14)} Inspection Checklist</div>
          </div>

          {/* Access info */}
          <div className="form-grid form-grid-2" style={{ marginBottom: 12 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Access Date</label>
              <input type="date" className="form-input" value={inspectionData.accessDate || ''} onChange={e => updateInspection({ accessDate: e.target.value })} />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Access Notes</label>
              <input type="text" className="form-input" placeholder="e.g. Contact tenant to schedule entry" value={inspectionData.accessNotes || ''} onChange={e => updateInspection({ accessNotes: e.target.value })} />
            </div>
          </div>

          <div style={{ borderTop: '1px solid var(--border)', marginBottom: 4 }} />

          {/* Checklist items */}
          {checklist.map((item, idx) => {
            const noteOpen = !!(openNoteItems[item.id] || item.notes);
            const noteActive = !!(item.notes);
            const photoActive = !!(item.photoUrl);
            return (
              <div key={item.id} style={{ padding: '16px 0', borderBottom: idx < checklist.length - 1 ? '1px solid var(--border)' : 'none' }}>
                {/* Label */}
                <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 14 }}>{item.label}</div>

                {/* Pass / Fail / N/A — big pill buttons */}
                <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
                  {[['pass','Pass','var(--green-txt)','var(--green-bg)'],['fail','Fail','var(--red-txt)','rgba(239,68,68,0.13)'],['na','N/A','var(--text2)','var(--bg3)']].map(([val, lbl, ac, ab]) => (
                    <button key={val} onClick={() => updateChecklistItem(idx, { status: item.status === val ? null : val })}
                      style={{ flex: 1, padding: '10px 0', fontSize: 15, fontWeight: 700, borderRadius: 12,
                        border: `2px solid ${item.status === val ? ac : 'var(--border)'}`,
                        background: item.status === val ? ab : 'transparent',
                        color: item.status === val ? ac : 'var(--text3)',
                        cursor: 'pointer', transition: 'all 0.15s' }}>
                      {lbl}
                    </button>
                  ))}
                </div>

                {/* Note + Photo icon buttons */}
                <div style={{ display: 'flex', gap: 10, marginBottom: noteOpen || photoActive ? 12 : 0 }}>
                  <button onClick={() => toggleNoteItem(item.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', fontSize: 13, borderRadius: 8,
                      border: `1px solid ${noteActive ? 'var(--accent)' : 'var(--border)'}`,
                      background: noteActive ? 'var(--accent-bg)' : 'transparent',
                      color: noteActive ? 'var(--accent)' : 'var(--text2)', cursor: 'pointer' }}>
                    {ic(I.pencil, 13, noteActive ? 'var(--accent)' : 'var(--text2)')} {noteActive ? 'Note added' : 'Add note'}
                  </button>
                  {!photoActive && (navigator.maxTouchPoints > 0 ? (
                    <>
                      <label style={{ cursor: uploadingChecklistPhoto !== null ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', fontSize: 13, borderRadius: 8, border: '1px solid var(--border)', color: 'var(--text2)' }}>
                        <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }} disabled={uploadingChecklistPhoto !== null} onChange={e => handleChecklistPhoto(idx, e)} />
                        {ic(I.paperclip, 13)} {uploadingChecklistPhoto === idx ? 'Uploading…' : 'Camera'}
                      </label>
                      <label style={{ cursor: uploadingChecklistPhoto !== null ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', fontSize: 13, borderRadius: 8, border: '1px solid var(--border)', color: 'var(--text2)' }}>
                        <input type="file" accept="image/*" style={{ display: 'none' }} disabled={uploadingChecklistPhoto !== null} onChange={e => handleChecklistPhoto(idx, e)} />
                        {ic(I.paperclip, 13)} Gallery
                      </label>
                    </>
                  ) : (
                    <label style={{ cursor: uploadingChecklistPhoto !== null ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', fontSize: 13, borderRadius: 8, border: '1px solid var(--border)', color: 'var(--text2)' }}>
                      <input type="file" accept="image/*" style={{ display: 'none' }} disabled={uploadingChecklistPhoto !== null} onChange={e => handleChecklistPhoto(idx, e)} />
                      {ic(I.paperclip, 13)} {uploadingChecklistPhoto === idx ? 'Uploading…' : 'Add photo'}
                    </label>
                  ))}
                </div>

                {/* Note textarea */}
                {noteOpen && (
                  <textarea className="form-textarea" style={{ minHeight: 64, fontSize: 13, marginBottom: photoActive ? 10 : 0 }}
                    placeholder="Describe the issue…" autoFocus={!item.notes}
                    value={item.notes || ''} onChange={e => updateChecklistItem(idx, { notes: e.target.value })} />
                )}

                {/* Photo thumbnail */}
                {photoActive && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <img src={item.photoUrl} alt={item.label} onClick={() => setLightboxDoc({ url: item.photoUrl, name: item.label })}
                      style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 10, cursor: 'zoom-in', border: '1px solid var(--border)', flexShrink: 0 }} />
                    <input id={`cl-replace-${idx}`} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handleChecklistPhoto(idx, e)} />
                    <KebabMenu items={[
                      { label: 'View', onClick: () => setLightboxDoc({ url: item.photoUrl, name: item.label }) },
                      { label: 'Replace', onClick: () => document.getElementById(`cl-replace-${idx}`)?.click() },
                      { label: 'Remove', danger: true, onClick: () => removeChecklistPhoto(idx) },
                    ]} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!isInspection && <>
      {/* Documents — moved into merged Task details card above */}
      {false && (
      <div
        className="card"
        style={{ marginBottom: 16, position: 'relative', outline: dragOver ? '2px dashed var(--accent)' : '2px dashed transparent', transition: 'outline 0.15s' }}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false); }}
        onDrop={handleDocDrop}
      >
        {dragOver && (
          <div style={{ position: 'absolute', inset: 0, borderRadius: 'inherit', background: 'var(--accent-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, pointerEvents: 'none' }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent)' }}>{ic(I.paperclip, 14, 'var(--accent)')} Drop to upload</span>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: (task.documents || []).length ? 14 : 0 }}>
          <div className="card-title" style={{ margin: 0 }}>{ic(I.paperclip, 14)} Documents</div>
          <label style={{ cursor: uploadingDoc ? 'not-allowed' : 'pointer' }}>
            <input type="file" style={{ display: 'none' }} onChange={handleDocUpload} disabled={uploadingDoc} />
            <span className="btn-ghost" style={{ pointerEvents: 'none' }}>
              {uploadingDoc ? 'Uploading…' : <>{ic(I.plus, 12)} Upload</>}
            </span>
          </label>
        </div>
        {(task.documents || []).length === 0 && !uploadingDoc && (
          <div style={{ fontSize: 13, color: 'var(--text3)' }}>No documents uploaded yet.</div>
        )}
        {(task.documents || []).map(doc => (
          <div key={doc.id} style={{ borderBottom: '1px solid var(--border)', padding: '8px 0' }}>
            {isImageDoc(doc.name) ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <img
                  src={doc.url}
                  alt={doc.name}
                  onClick={() => setLightboxDoc(doc)}
                  style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8, cursor: 'zoom-in', flexShrink: 0, border: '1px solid var(--border)' }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    onClick={() => setLightboxDoc(doc)}
                    style={{ fontSize: 13, color: 'var(--accent)', cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 3 }}
                  >{doc.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--text3)' }}>{doc.uploadedAt ? fmtDate(doc.uploadedAt.slice(0, 10)) : ''}</div>
                </div>
                <KebabMenu items={[
                  { label: 'View', onClick: () => setLightboxDoc(doc) },
                  { label: 'Open in new tab', onClick: () => window.open(doc.url, '_blank') },
                  { label: 'Edit name', onClick: () => { setRenamingDoc(doc); setRenameDraft(doc.name); } },
                  { label: 'Delete', danger: true, onClick: () => handleDocDelete(doc) },
                ]} />
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <a href={doc.url} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: 'var(--accent)', textDecoration: 'none', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.name}</a>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, marginLeft: 12 }}>
                  <span style={{ fontSize: 11, color: 'var(--text3)' }}>{doc.uploadedAt ? fmtDate(doc.uploadedAt.slice(0, 10)) : ''}</span>
                  <KebabMenu items={[
                    { label: 'Edit name', onClick: () => { setRenamingDoc(doc); setRenameDraft(doc.name); } },
                    { label: 'Delete', danger: true, onClick: () => handleDocDelete(doc) },
                  ]} />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      )}

      {/* Signature — Renewal tasks only */}
      {taskCategory(task) === 'Renewal' && (
        <div className="card" style={{ marginBottom: 16 }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div className="card-title" style={{ margin: 0 }}>
              {ic('M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z', 14)} Signature
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {task.signatureStatus && (
                <button className="btn" style={{ fontSize: 10, padding: '3px 10px', color: 'var(--text3)' }}
                  onClick={() => { if (window.confirm('Reset signature status?')) updateSignature({ signatureStatus: null, signatureLeaseType: null }); }}>
                  Reset
                </button>
              )}
              {!task.signatureStatus && (
                <button className="btn btn-primary" style={{ fontSize: 12, padding: '5px 14px' }} onClick={() => setSignatureModal(true)}>
                  Send Lease
                </button>
              )}
            </div>
          </div>

          {/* Status display */}
          {task.signatureStatus ? (() => {
            const signedDoc = (task.documents || []).find(d => d.name && d.name.startsWith('Signed_Lease'));
            const s1Done = true;
            const s2Done = task.signatureStatus === 'signed';
            const s2Active = task.signatureStatus === 'pending';
            const s3Done = s2Done && !!signedDoc;
            const s3Active = s2Done && !signedDoc;

            const StepCircle = ({ done, active, next, num, onClick }) => (
              <div onClick={onClick} style={{
                width: 38, height: 38, borderRadius: '50%', flexShrink: 0, cursor: onClick ? 'pointer' : 'default',
                background: done || active ? 'var(--accent)' : 'var(--bg3)',
                border: `2.5px solid ${done || active || next ? 'var(--accent)' : 'var(--border)'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.3s',
                boxShadow: active || next ? '0 0 0 4px var(--accent-bg)' : 'none',
              }}>
                {done
                  ? <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                  : <span style={{ fontSize: 13, fontWeight: 700, color: active ? '#fff' : next ? 'var(--accent)' : 'var(--text3)' }}>{num}</span>}
              </div>
            );
            const StepLine = ({ done }) => (
              <div style={{ flex: 1, height: 2.5, background: done ? 'var(--accent)' : 'var(--border)', transition: 'background 0.4s', margin: '0 4px' }} />
            );

            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {/* Lease type label */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 13px', background: 'var(--bg2)', borderRadius: 9, border: '1px solid var(--border)' }}>
                  {ic(I.file, 15, 'var(--text2)')}
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text1)', flex: 1 }}>
                    {task.signatureLeaseType === 'stabilized' ? 'Rent Stabilized Lease' : 'Non-Rent Stabilized Lease'}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--text3)' }}>via Firma</span>
                </div>

                {/* 3-step progression */}
                <div style={{ padding: '6px 4px' }}>
                  {/* Circles + connecting lines */}
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
                    <StepCircle done={s1Done} active={false} num={1} />
                    <StepLine done={true} />
                    <StepCircle done={s2Done} active={s2Active} next={!s2Active && !s2Done} num={2} />
                    <StepLine done={s3Active || s3Done} />
                    <StepCircle done={s3Done} active={s3Active} next={s2Done && !s3Active && !s3Done} num={3}
                      onClick={s3Done && signedDoc ? () => window.open(signedDoc.url, '_blank') : null} />
                  </div>

                  {/* Step labels */}
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div style={{ width: 80 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--accent)' }}>Lease Sent</div>
                      {task.signatureSentAt && (
                        <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>
                          {new Date(task.signatureSentAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </div>
                      )}
                    </div>
                    <div style={{ textAlign: 'center', width: 100 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: s2Done ? 'var(--accent)' : s2Active ? 'var(--accent)' : 'var(--text3)' }}>
                        {s2Done ? 'Both Parties Signed' : 'Pending Signatures'}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', width: 90 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: s3Done ? 'var(--accent)' : s3Active ? 'var(--accent)' : 'var(--text3)' }}>
                        {s3Done ? 'Signing Complete' : 'View Document'}
                      </div>
                      {s3Done && signedDoc && (
                        <a href={signedDoc.url} target="_blank" rel="noreferrer"
                          style={{ fontSize: 10, color: 'var(--accent)', marginTop: 2, display: 'block', textDecoration: 'none' }}>
                          Open PDF →
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                {/* Actions row */}
                {task.signatureRequestId && !s3Done && (
                  <div style={{ paddingTop: 6, borderTop: '1px solid var(--border)' }}>
                    <button className="btn btn-primary" style={{ fontSize: 11, padding: '5px 14px' }}
                      disabled={downloadingLease} onClick={downloadSignedLease}>
                      {downloadingLease ? <><span className="btn-spinner" /> Downloading…</> : <>{ic(I.paperclip, 12)} Download Signed Lease</>}
                    </button>
                  </div>
                )}
              </div>
            );
          })() : (
            <div style={{ fontSize: 13, color: 'var(--text3)' }}>No lease sent yet. Click "Send Lease" to begin.</div>
          )}

          {/* Lease type modal */}
          {signatureModal && (
            <Modal title="Select Lease Type" onClose={() => setSignatureModal(false)}>
              <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>Choose the lease type to send for e-signature via Firma.</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[
                  // Stays disabled until sendStabilizedLeaseForSignature exists and the
                  // coordinate map in functions/rent-stab-renewal.js has been checked
                  // against the real form. The form behind it is finished; flipping this
                  // one flag is what turns the feature on.
                  { value: 'stabilized',    label: 'Rent Stabilized Lease',     desc: 'DHCR RTP-8 + RA-LR1 rider — not yet live', disabled: true },
                  { value: 'non-stabilized',label: 'Non-Rent Stabilized Lease', desc: 'For market-rate units', disabled: false },
                ].map(opt => (
                  <button key={opt.value}
                    onClick={() => { if (opt.disabled) return; if (opt.value === 'stabilized') openStabForm(); else openLeaseForm(); }}
                    disabled={opt.disabled}
                    style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', background: 'var(--bg2)', border: '1.5px solid var(--border)', borderRadius: 10, cursor: opt.disabled ? 'not-allowed' : 'pointer', textAlign: 'left', width: '100%', fontFamily: 'inherit', transition: 'border-color .15s', opacity: opt.disabled ? 0.5 : 1 }}
                    onMouseEnter={e => { if (!opt.disabled) e.currentTarget.style.borderColor = 'var(--accent)'; }}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
                    <div style={{ width: 36, height: 36, borderRadius: 9, background: 'var(--bg3)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {ic(I.file, 16, 'var(--text2)')}
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text1)' }}>{opt.label}</div>
                      <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>{opt.desc}</div>
                    </div>
                    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="var(--text3)" strokeWidth={2} style={{ marginLeft: 'auto', flexShrink: 0 }}><path d="M9 18l6-6-6-6"/></svg>
                  </button>
                ))}
              </div>
              <div style={{ marginTop: 14 }}>
                <button className="btn" style={{ width: '100%', justifyContent: 'center' }} onClick={() => setSignatureModal(false)}>Cancel</button>
              </div>
            </Modal>
          )}

          {/* Lease fill form */}
          {leaseFormModal && (
            <Modal title="Non-Rent Stabilized Lease Renewal" onClose={() => setLeaseFormModal(false)}>
              <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 16 }}>Review and confirm the details below. Pre-filled fields come from the tenant record.</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                <FI label="Owner Legal Entity Name" value={leaseForm.ownerName} onChange={lf('ownerName')} />
                <FI label="Owner Address" value={leaseForm.ownerAddress} onChange={lf('ownerAddress')} />
                <FI label="Tenant Name" value={leaseForm.tenantName} onChange={lf('tenantName')} />
                <FI label="Tenant Email" value={leaseForm.tenantEmail} onChange={lf('tenantEmail')} type="email" />
                <FI label="Property Address" value={leaseForm.propertyAddress} onChange={v => setLeaseForm(p => ({ ...p, propertyAddress: v, tenantAddress: v + (p.unitNumber ? ', Unit ' + p.unitNumber : '') }))} />
                <FI label="Unit / Apt #" value={leaseForm.unitNumber} onChange={v => setLeaseForm(p => ({ ...p, unitNumber: v, tenantAddress: p.propertyAddress + (v ? ', Unit ' + v : '') }))} />
                <FS label="Lease Term" value={leaseForm.leaseTerm} onChange={v => {
                  const days = v === '2 Years' ? 730 : 365;
                  const newEnd = leaseForm.commencementDate
                    ? new Date(new Date(leaseForm.commencementDate + 'T12:00:00').getTime() + days * 86400000).toISOString().slice(0, 10)
                    : leaseForm.expirationDate;
                  setLeaseForm(p => ({ ...p, leaseTerm: v, expirationDate: newEnd }));
                }} options={['1 Year', '2 Years']} />
                <FI label="Original Lease Date *" value={leaseForm.originalLeaseDate} onChange={lf('originalLeaseDate')} type="date"
                  error={leaseForm.originalLeaseDate === '' && leaseForm._touched ? 'Required' : null} />
                <FI label="Commencement Date *" value={leaseForm.commencementDate} onChange={v => {
                  const days = leaseForm.leaseTerm === '2 Years' ? 730 : 365;
                  const newEnd = v ? new Date(new Date(v + 'T12:00:00').getTime() + days * 86400000).toISOString().slice(0, 10) : '';
                  setLeaseForm(p => ({ ...p, commencementDate: v, expirationDate: newEnd }));
                }} type="date"
                  error={!leaseForm.commencementDate && leaseForm._touched ? 'Required' : null} />
                <FI label="Expiration Date *" value={leaseForm.expirationDate} onChange={lf('expirationDate')} type="date"
                  error={!leaseForm.expirationDate && leaseForm._touched ? 'Required' : null} />
                <FI label="Monthly Rent ($) *" value={leaseForm.monthlyRent} onChange={lf('monthlyRent')} type="number"
                  error={!String(leaseForm.monthlyRent || '').trim() && leaseForm._touched ? 'Required' : null} />
                <FI label="Security Deposit ($) — optional" value={leaseForm.securityDeposit} onChange={lf('securityDeposit')} type="number" />
              </div>
              <div className="modal-actions">
                <button className="btn" onClick={() => setLeaseFormModal(false)}>Cancel</button>
                <SavingBtn onClick={submitLease} disabled={sendingLease}>
                  {sendingLease ? 'Sending…' : 'Send for Signature'}
                </SavingBtn>
              </div>
            </Modal>
          )}

          {/* Rent stabilized renewal — DHCR RTP-8 plus the RA-LR1 rider.
              Laid out in the order the paper form asks for things, and the rent table is
              addressed by its column letters rather than by what each column means, so a
              value entered here lands in the cell the owner expects on the filed form. */}
          {stabFormModal && (() => {
            const Head = ({ children }) => (
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text3)', margin: '16px 0 6px' }}>{children}</div>
            );
            const req = k => (!String(stabForm[k] || '').trim() && stabForm._touched ? 'Required' : null);
            const setCommence = v => setStabForm(pp => ({
              ...pp,
              commencementDate: v,
              termination1Yr: termEndISO(v, 1),
              termination2Yr: termEndISO(v, 2),
            }));
            return (
            <Modal title="Rent Stabilized Lease Renewal (RTP-8)" onClose={() => setStabFormModal(false)}>
              <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 4 }}>
                Fills the official DHCR renewal packet — the RTP-8 form plus the RA-LR1 rider. Both are signed.
              </div>

              <Head>Parties</Head>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                <FI label="Owner Legal Entity" value={stabForm.ownerName} onChange={sf('ownerName')} error={req('ownerName')} />
                <FI label="Owner Mailing Address *" value={stabForm.ownerAddress} onChange={sf('ownerAddress')} error={req('ownerAddress')} />
                <FI label="Tenant Name" value={stabForm.tenantName} onChange={sf('tenantName')} />
                <FI label="Tenant Email *" value={stabForm.tenantEmail} onChange={sf('tenantEmail')} type="email" error={req('tenantEmail')} />
                <FI label="Property Address" value={stabForm.propertyAddress} onChange={sf('propertyAddress')} />
                <FI label="Unit / Apt #" value={stabForm.unitNumber} onChange={sf('unitNumber')} />
              </div>

              <Head>Dates</Head>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                <FI label="Offer Dated" value={stabForm.offerDate} onChange={sf('offerDate')} type="date" />
                <FI label="Current Lease Expires" value={stabForm.currentLeaseExpires} onChange={sf('currentLeaseExpires')} type="date" />
                <FI label="Commencement *" value={stabForm.commencementDate} onChange={setCommence} type="date" error={req('commencementDate')} />
                <FI label="Terminates (1 Year)" value={stabForm.termination1Yr} onChange={sf('termination1Yr')} type="date" />
                <FI label="Terminates (2 Years)" value={stabForm.termination2Yr} onChange={sf('termination2Yr')} type="date" />
                <FI label="Lease Dated *" value={stabForm.leaseDated} onChange={sf('leaseDated')} type="date" error={req('leaseDated')} />
              </div>

              <Head>Item 2 — Rent Table</Head>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8, lineHeight: 1.5 }}>
                Entered exactly as you would write it on the form. Nothing is calculated — a
                filed RTP-8 does not always follow from the percentage printed beside it.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                <FI label="Col B — Legal Rent *" value={stabForm.colB} onChange={sf('colB')} error={req('colB')} />
                <FI label="Col C — 1 Yr %" value={stabForm.colC1pct} onChange={sf('colC1pct')} />
                <FI label="Col C — 1 Yr $" value={stabForm.colC1amt} onChange={sf('colC1amt')} />
                <FI label="Col C — 2 Yr %" value={stabForm.colC2pct} onChange={sf('colC2pct')} />
                <FI label="Col C — 2 Yr $" value={stabForm.colC2amt} onChange={sf('colC2amt')} />
                <FI label="Col D — 1 Yr" value={stabForm.colD1} onChange={sf('colD1')} />
                <FI label="Col D — 2 Yr" value={stabForm.colD2} onChange={sf('colD2')} />
                <FI label="Col E — 1 Yr" value={stabForm.colE1} onChange={sf('colE1')} />
                <FI label="Col E — 2 Yr" value={stabForm.colE2} onChange={sf('colE2')} />
                <FI label="Col F — 1 Yr" value={stabForm.colF1} onChange={sf('colF1')} />
                <FI label="Col F — 2 Yr" value={stabForm.colF2} onChange={sf('colF2')} />
              </div>

              <Head>Item 6 — Monthly Payment</Head>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                <FI label="Monthly Rent — 1 Yr *" value={stabForm.rent1Yr} onChange={sf('rent1Yr')} error={req('rent1Yr')} />
                <FI label="Monthly Rent — 2 Yr" value={stabForm.rent2Yr} onChange={sf('rent2Yr')} />
                <FI label="Separate Charges" value={stabForm.separateCharges} onChange={sf('separateCharges')} />
              </div>

              <Head>Item 10 — Sprinkler System</Head>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                <FS label="Leased Premises" value={stabForm.sprinkler} onChange={sf('sprinkler')}
                  options={[{ value: 'does', label: 'Does have an operative sprinkler system' }, { value: 'doesNot', label: 'Does NOT have one' }]} />
                {stabForm.sprinkler === 'does' && (
                  <FI label="Last Inspected" value={stabForm.sprinklerInspectedOn} onChange={sf('sprinklerInspectedOn')} type="date" />
                )}
              </div>

              <Head>Part B — Term Offered</Head>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8, lineHeight: 1.5 }}>
                The chosen term is checked on the form before it goes out, so the tenant only signs.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                <FS label="Renewal Term" value={stabForm.selectedTerm} onChange={sf('selectedTerm')} options={['1 Year', '2 Years']} />
              </div>

              <div className="modal-actions">
                <button className="btn" onClick={() => setStabFormModal(false)}>Cancel</button>
                <SavingBtn onClick={submitStabLease} disabled={sendingStab}>
                  {sendingStab ? 'Sending…' : 'Send for Signature'}
                </SavingBtn>
              </div>
            </Modal>
            );
          })()}
        </div>
      )}

      {/* Notes — moved into merged Task details card above */}
      {false && (<div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: notes.length || addingNote ? 14 : 0 }}>
          <div className="card-title" style={{ margin: 0 }}>{ic(I.clipboard, 14)} Documentation</div>
          {!addingNote && (
            <button className="btn-ghost" onClick={() => { setAddingNote(true); setNoteDraft(''); }}>
              {ic(I.plus, 12)} Add note
            </button>
          )}
        </div>

        {/* Note list */}
        {notes.length > 0 && (
          <div style={{ marginBottom: addingNote ? 14 : 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {notes.map((n, i) => (
              <div key={i}>
                {editingNoteIdx === i ? (
                  <div style={{ padding: '8px 0' }}>
                    <textarea
                      className="form-textarea"
                      style={{ minHeight: 64, marginBottom: 8, width: '100%' }}
                      value={editNoteDraft}
                      onChange={e => setEditNoteDraft(e.target.value)}
                      autoFocus
                    />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="btn" onClick={() => setEditingNoteIdx(null)}>Cancel</button>
                      <button className="btn btn-primary" onClick={() => saveEditNote(i)}>Save</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 13, color: 'var(--text1)', lineHeight: 1.6, flex: 1, minWidth: 0 }}>
                      <span style={{ color: 'var(--text3)', fontSize: 12 }}>{fmtDate(n.date)}</span>
                      <span style={{ color: 'var(--text3)', margin: '0 6px' }}>·</span>
                      <span style={{ whiteSpace: 'pre-wrap' }}>{n.text}</span>
                      <span style={{ color: 'var(--text3)', margin: '0 6px' }}>·</span>
                      <span style={{ color: 'var(--text3)', fontSize: 12 }}>{n.author}</span>
                    </div>
                    <div style={{ flexShrink: 0, marginLeft: 8 }}>
                      <KebabMenu items={[
                        { label: 'Edit', onClick: () => startEditNote(i, n.text) },
                        { label: 'Delete', danger: true, onClick: () => deleteNote(i) },
                      ]} />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {!notes.length && !addingNote && (
          <div style={{ fontSize: 13, color: 'var(--text3)' }}>No notes yet.</div>
        )}

        {/* Inline add form */}
        {addingNote && (
          <div>
            <textarea
              className="form-textarea"
              style={{ minHeight: 72, marginBottom: 8, width: '100%' }}
              value={noteDraft}
              onChange={e => setNoteDraft(e.target.value)}
              placeholder="Write a note…"
              autoFocus
            />
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button className="btn" onClick={() => { setAddingNote(false); setNoteDraft(''); }}>Cancel</button>
              <button
                className="btn"
                onMouseDown={startListening}
                onMouseUp={stopListening}
                onTouchStart={e => { e.preventDefault(); startListening(); }}
                onTouchEnd={e => { e.preventDefault(); stopListening(); }}
                title="Hold to speak"
                style={{ display: 'flex', alignItems: 'center', gap: 5, background: listening ? 'var(--red)' : undefined, color: listening ? '#fff' : undefined, borderColor: listening ? 'var(--red)' : undefined, userSelect: 'none' }}
              >
                {ic('M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3zM19 10v2a7 7 0 0 1-14 0v-2M12 19v4M8 23h8', 13, listening ? '#fff' : 'currentColor')}
                {listening ? 'Listening…' : 'Hold to speak'}
              </button>
              <SavingBtn onClick={addNote}>Add</SavingBtn>
            </div>
          </div>
        )}
      </div>)}
      </>}

      {lightboxDoc && (
        <div
          onClick={() => setLightboxDoc(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', zIndex: 1000, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 20 }}
        >
          <div onClick={e => e.stopPropagation()} style={{ position: 'relative', maxWidth: '90vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            <img
              src={lightboxDoc.url}
              alt={lightboxDoc.name}
              style={{ maxWidth: '90vw', maxHeight: '78vh', objectFit: 'contain', borderRadius: 10, boxShadow: '0 8px 40px rgba(0,0,0,0.6)' }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 13, color: '#ccc', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lightboxDoc.name}</span>
              <a href={lightboxDoc.url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: 'var(--accent)', textDecoration: 'none', padding: '4px 12px', border: '1px solid var(--accent)', borderRadius: 6 }} onClick={e => e.stopPropagation()}>Open in new tab</a>
            </div>
          </div>
          <button onClick={() => setLightboxDoc(null)} style={{ position: 'fixed', top: 16, right: 20, background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', fontSize: 22, width: 36, height: 36, borderRadius: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>×</button>
        </div>
      )}

      {renamingDoc && (
        <Modal title="Rename document" onClose={() => setRenamingDoc(null)}>
          <FI label="Name" value={renameDraft} onChange={v => setRenameDraft(v)} placeholder="Document name" />
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button className="btn-primary" onClick={handleDocRename}>Save</button>
            <button className="btn-ghost" onClick={() => setRenamingDoc(null)}>Cancel</button>
          </div>
        </Modal>
      )}

      {promoteModal && (
        <Modal title="Create Maintenance Record" onClose={() => setPromoteModal(false)}>
          <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 14 }}>
            Review and adjust the details before creating the linked maintenance record.
          </div>
          <FS
            label="Property"
            value={promoteForm.propertyId}
            onChange={v => setPromoteForm(p => ({ ...p, propertyId: v, units: [] }))}
            options={properties.map(p => ({ value: p.id, label: p.name }))}
          />
          <div className="form-group" style={{ marginBottom: 12 }}>
            <label className="form-label">Unit / Scope</label>
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '6px 10px', background: 'var(--bg1)', maxHeight: 160, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
              {[
                { value: 'Building Wide', label: 'Building Wide' },
                ...(tenants || [])
                  .filter(t => t.propertyId === promoteForm.propertyId && t.status !== 'Previous')
                  .sort((a, b) => (a.unit || '').localeCompare(b.unit || '', undefined, { numeric: true }))
                  .map(t => ({ value: `${t.unit} - ${t.name}`, label: `Unit ${t.unit} - ${t.name}` }))
              ].map(opt => (
                <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 4px', cursor: 'pointer', fontSize: 13, borderRadius: 6, background: (promoteForm.units || []).includes(opt.value) ? 'var(--bg3)' : 'transparent' }}>
                  <input type="checkbox" checked={(promoteForm.units || []).includes(opt.value)} onChange={() => togglePromoteUnit(opt.value)} style={{ accentColor: 'var(--accent)', width: 14, height: 14, flexShrink: 0 }} />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>
          <FI label="Title" value={promoteForm.title || ''} onChange={v => setPromoteForm(p => ({ ...p, title: v }))} />
          <FI label="Description" value={promoteForm.description || ''} onChange={v => setPromoteForm(p => ({ ...p, description: v }))} />
          <div className="form-grid form-grid-2">
            <FI label="Date" value={promoteForm.date || ''} onChange={v => setPromoteForm(p => ({ ...p, date: v }))} type="date" />
            <FI label="Vendor" value={promoteForm.vendor || ''} onChange={v => setPromoteForm(p => ({ ...p, vendor: v }))} />
          </div>
          <FI label="Total cost ($)" value={promoteForm.cost || ''} onChange={v => setPromoteForm(p => ({ ...p, cost: v }))} type="number" />
          <div className="modal-actions">
            <button className="btn" onClick={() => setPromoteModal(false)}>Cancel</button>
            <SavingBtn onClick={savePromote}>Create &amp; Link</SavingBtn>
          </div>
        </Modal>
      )}

      {/* Inspection submit button */}
      {isInspection && taskActive(task) && (
        <div style={{ marginTop: 8, marginBottom: 24 }}>
          <button
            className="btn"
            style={{ width: '100%', padding: '14px 0', fontSize: 15, fontWeight: 700, borderRadius: 12, textAlign: 'center', justifyContent: 'center', display: 'flex' }}
            onClick={submitInspection}
          >
            Submit Inspection
          </button>
        </div>
      )}

      {/* Inspection submit confirmation modal */}
      {submitModal && (
        <Modal title="Submit Inspection" onClose={() => setSubmitModal(false)}>
          <div style={{ textAlign: 'center', padding: '8px 0 20px' }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>✅</div>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>All items completed!</div>
            <div style={{ fontSize: 14, color: 'var(--text2)', lineHeight: 1.6 }}>
              Would you like to download the inspection report before closing this task?
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <button className="btn btn-primary" style={{ padding: '11px 0', fontSize: 14, fontWeight: 600, justifyContent: 'center' }} onClick={() => completeInspection(true)}>
              Download Report &amp; Close Task
            </button>
            <button className="btn" style={{ padding: '11px 0', fontSize: 14, justifyContent: 'center' }} onClick={() => completeInspection(false)}>
              Close Task without Downloading
            </button>
            <button className="btn-ghost" style={{ padding: '8px 0', fontSize: 13 }} onClick={() => setSubmitModal(false)}>
              Cancel
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Inspection checklist ───────────────────────────────────────────────────
const INSPECTION_ITEMS = ['Windows','Kitchen','Bathroom','Walls','Appliances','Safety Devices','Electrical','Plumbing (No leaks, hot water, and good pressure)','Doors and locks','Floor and ceilings','Occupants','Others'];
const makeDefaultChecklist = () => INSPECTION_ITEMS.map(label => ({ id: label.toLowerCase(), label, status: null, notes: '', photoUrl: '', photoStoragePath: '' }));

// ── Tasks ─────────────────────────────────────────────────────────────────
function Tasks({ tasks, setTasks, maintenance, setMaintenance, properties, tenants, users, user, initialId, clearNav, categoryFilter, setCategoryFilter, assigneeFilter, setAssigneeFilter }) {
  const toast = useToast();
  // The status-collapsing migration that used to run here is done — every task document
  // holds "Active" or "Closed", and no write path can produce anything else now.
  const [search, setSearch] = useState('');
  const [propFilter, setPropFilter] = useState('all');
  const [tenantFilter, setTenantFilter] = useState('all'); // 'all' | 'building-wide' | <tenantId>

  // A task counts as building-wide when its unit/scope is "Building Wide".
  const isBuildingWide = t => {
    const units = [];
    if (t.unit) units.push(String(t.unit).trim());
    if (Array.isArray(t.maintUnit)) t.maintUnit.forEach(u => u && units.push(String(u).trim()));
    else if (typeof t.maintUnit === 'string' && t.maintUnit) units.push(t.maintUnit.trim());
    return units.some(u => u.toLowerCase() === 'building wide')
      || /(^|[^a-z0-9])building wide([^a-z0-9]|$)/i.test(String(t.title || ''));
  };
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [selectedId, setSelectedId] = useState(initialId || null);
  useEffect(() => { if (initialId) { setSelectedId(initialId); if (clearNav) clearNav(); } }, [initialId]); // eslint-disable-line
  const ff = k => v => setForm(p => ({ ...p, [k]: v }));

  const today = nyTodayISO();
  const propName = id => properties.find(p => p.id === id)?.name || '';
  const isAdmin = user.email === ADMIN_EMAIL;
  const managedUser = (users || []).find(u => userMatchesEmail(u, user.email));

  const [editingFollowUp, setEditingFollowUp] = useState(null);
  const [editingStatus, setEditingStatus] = useState(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => { const h = () => setIsMobile(window.innerWidth < 768); window.addEventListener('resize', h); return () => window.removeEventListener('resize', h); }, []);

  // Assignable people are the managed-user records. Picking from this list is what keeps
  // an assignment pointing at a real person: the id is stored alongside the name, and
  // visibility is matched on that id. `extra` keeps any name already on the task so an
  // existing assignment stays selectable even if that person's record has since changed.
  const assignUserOptions = (extra = []) => {
    const s = new Set((users || []).map(u => u.name).filter(Boolean));
    extra.forEach(n => n && s.add(n));
    return [...s].sort((a, b) => a.localeCompare(b)).map(n => ({ value: n, label: n }));
  };

  const sorted = useMemo(() => {
    // Non-admin staff only see tasks assigned to them; the admin sees everything.
    // Matched through isAssignedToUser, which compares user ids and only falls back to
    // names for rows that carry none. An exact name comparison here used to hide a
    // person's own work from them whenever the stored spelling had drifted, while the
    // dashboard — which already matched on id — still showed it.
    const visible = isAdmin
      ? (tasks || [])
      : (tasks || []).filter(t => isAssignedToUser(t, managedUser));
    return visible
      .filter(t => {
        if (categoryFilter !== 'all' && taskCategory(t) !== categoryFilter) return false;
        if (!matchesTaskAssignee(t, assigneeFilter, managedUser?.name, users)) return false;
        if (propFilter !== 'all' && t.propertyId !== propFilter) return false;
        // Linked-tenant filter (Maintenance only): a specific tenant, or building-wide.
        if (categoryFilter === 'Maintenance' && tenantFilter !== 'all') {
          if (tenantFilter === 'building-wide') { if (!isBuildingWide(t)) return false; }
          else if (t.tenantId !== tenantFilter) return false;
        }
        if (!search) return true;
        const sl = search.toLowerCase();
        return t.title.toLowerCase().includes(sl);
      })
      .sort((a, b) => {
        const pRank = { Inspection: 0, Maintenance: 1, Complaint: 1 };
        const pa = pRank[taskCategory(a)] ?? 1;
        const pb = pRank[taskCategory(b)] ?? 1;
        if (pa !== pb) return pa - pb;
        if (!a.followUp && !b.followUp) return 0;
        if (!a.followUp) return 1;
        if (!b.followUp) return -1;
        return a.followUp.localeCompare(b.followUp);
      });
  }, [tasks, search, categoryFilter, propFilter, tenantFilter, assigneeFilter]); // eslint-disable-line

  // Property-filter options. For Maintenance, only Multi Family Housing properties.
  const propFilterOptions = useMemo(() => {
    const list = categoryFilter === 'Maintenance'
      ? properties.filter(p => p.businessType === 'Multi Family Housing')
      : properties;
    return [...list].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [properties, categoryFilter]);
  // If the selected property is no longer offered (e.g. switched to Maintenance), reset.
  useEffect(() => {
    if (propFilter !== 'all' && !propFilterOptions.some(p => p.id === propFilter)) setPropFilter('all');
  }, [propFilterOptions, propFilter]);

  // Linked-tenant filter options — scoped to the selected property when one is chosen.
  const tenantFilterOptions = useMemo(() => {
    return (tenants || [])
      .filter(t => t.status !== 'Previous' && t.status !== 'Vacant' && (propFilter === 'all' || t.propertyId === propFilter))
      .sort((a, b) => String(a.unit || '').localeCompare(String(b.unit || ''), undefined, { numeric: true }));
  }, [tenants, propFilter]);
  // Reset the tenant filter if the chosen tenant is no longer in the list.
  useEffect(() => {
    if (tenantFilter !== 'all' && tenantFilter !== 'building-wide' && !tenantFilterOptions.some(t => t.id === tenantFilter)) setTenantFilter('all');
  }, [tenantFilterOptions, tenantFilter]);

  const activeRows = sorted.filter(taskActive);
  const closedRows = sorted.filter(t => !taskActive(t));
  const [showClosed, setShowClosed] = useState(false);
  const [taskPage, setTaskPage] = useState(1);
  useEffect(() => { setTaskPage(1); }, [categoryFilter]); // eslint-disable-line
  const TASK_PAGE_SIZE = isMobile ? 10 : 30;

  const saveFollowUp = (taskId, newDate) => {
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, followUp: newDate } : t));
    setEditingFollowUp(null);
  };

  const saveStatus = (taskId, newStatus) => {
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: newStatus, updatedAt: nyTodayISO() } : t));
    setEditingStatus(null);
  };

  const isOD = t => taskActive(t) && isTaskOverdue(t.followUp);
  const isDT = t => taskActive(t) && isTaskDueToday(t.followUp);

  const openAdd = () => {
    setForm({ title: '', description: '', propertyId: '', tenantId: '', maintUnit: [], followUp: today, status: 'Active', category: '', ...ownerAssignee(users) });
    setFormError('');
    setModalStep(1);
    setModal('add');
  };

  const TASK_CATEGORIES = [
    { value: 'Inspection',       label: 'Inspection',       desc: 'Unit condition check with checklist', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4', color: 'var(--blue-txt)', bg: 'var(--blue-bg)', border: '#1d4ed8' },
    { value: 'Maintenance',      label: 'Maintenance',      desc: 'Repair, work order, or vendor job',   icon: 'M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z', color: '#fb923c', bg: 'rgba(251,146,60,0.12)', border: '#ea580c' },
    { value: 'General',          label: 'General',          desc: 'Follow-up, action item, or to-do',   icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2M9 12h6M9 16h6', color: 'var(--text2)', bg: 'var(--bg3)', border: 'var(--border)' },
    { value: 'Invoice',  label: 'Invoice', desc: 'Bill, payment, or expense tracking', icon: 'M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z', color: 'var(--amber-txt)', bg: 'var(--amber-bg)', border: '#d97706' },
    { value: 'Renewal',          label: 'Renewal',          desc: 'Lease renewal or contract update',   icon: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15', color: 'var(--green-txt)', bg: 'var(--green-bg)', border: '#15803d' },
    { value: 'Collection',       label: 'Collection',       desc: 'Rent arrears or debt collection',     icon: 'M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6', color: 'var(--accent)', bg: 'var(--accent-bg)', border: 'var(--accent)' },
    { value: 'Complaint',        label: 'Complaint',        desc: 'Tenant or neighbor complaint',         icon: 'M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z', color: '#e11d48', bg: 'rgba(225,29,72,0.12)', border: '#be123c' },
  ];

  const applyCategoryDefaults = (cat, current) => {
    const next = { ...current, category: cat };
    if (cat === 'Inspection') {
      if (!next.description) next.description = 'General Inspection';
      const t = buildInspectionTitle(next.tenantId, next.propertyId);
      if (t) next.title = t;
      next.followUp = new Date().toISOString().slice(0, 10);
    }
    if (cat === 'Maintenance') {
      next.maintUnit = [];
      next.tenantId = '';
      next.followUp = new Date().toISOString().slice(0, 10);
      if (!next.issueDate) next.issueDate = new Date().toISOString().slice(0, 10);
    }
    if (cat === 'Complaint') {
      next.maintUnit = [];
      next.tenantId = '';
      next.followUp = new Date().toISOString().slice(0, 10);
      if (!next.issueDate) next.issueDate = new Date().toISOString().slice(0, 10);
    }
    if (cat === 'General') {
      next.maintUnit = [];
      next.tenantId = '';
    }
    if (cat === 'Renewal' && next.tenantId && next.propertyId) {
      const rTenant = (tenants || []).find(t => t.id === next.tenantId);
      const rProp   = (properties || []).find(p => p.id === next.propertyId);
      if (rTenant && rProp) next.title = `Unit ${rTenant.unit} | ${rTenant.name} | ${rProp.name}`;
    }
    return next;
  };
  const openEdit = t => {
    let maintUnit = t.maintUnit;
    if (!maintUnit) maintUnit = [];
    else if (typeof maintUnit === 'string') maintUnit = maintUnit ? [maintUnit] : [];
    setForm({ ...t, maintUnit });
    setFormError('');
    setModal('edit');
  };

  const [formError, setFormError] = useState('');
  const [modalStep, setModalStep] = useState(1);
  const [generatingTitle, setGeneratingTitle] = useState(false);
  const generateTitleFromDesc = async () => {
    if (!form.description?.trim()) return;
    setGeneratingTitle(true);
    try {
      const fn = httpsCallable(functions, 'improveNote');
      const res = await fn({ text: form.description.trim(), mode: 'title' });
      setForm(p => ({ ...p, title: res.data.improved }));
    } catch (e) {
      // silent — user can still type the title manually
    }
    setGeneratingTitle(false);
  };

  const buildInspectionTitle = (tenantId, propertyId) => {
    const tenant = (tenants || []).find(t => t.id === tenantId);
    const prop = properties.find(p => p.id === propertyId);
    if (!tenant || !prop) return '';
    // Auto-generated, so clamp it rather than blocking the user on a title they can't edit.
    return `Unit ${tenant.unit} | ${tenant.name} | ${prop.name}`.slice(0, TASK_TITLE_MAX).trim();
  };

  const save = () => {
    if (!form.title?.trim()) { setFormError('Title is required.'); return; }
    if (form.title.trim().length > TASK_TITLE_MAX) {
      setFormError(`Title is limited to ${TASK_TITLE_MAX} characters — put the detail in the description.`);
      return;
    }
    // There is no "unassigned" — every task has someone on it.
    if (!taskAssignedList(form).length && !taskAssignedIds(form).length) { setFormError('Assign the task to someone.'); return; }
    if (form.category === 'Inspection') {
      if (!form.propertyId) { setFormError('Property is required for Inspection tasks.'); return; }
      if (!form.tenantId) { setFormError('Tenant / Unit is required for Inspection tasks.'); return; }
    }
    if (form.category === 'Maintenance') {
      if (!form.propertyId) { setFormError('Property is required for Maintenance tasks.'); return; }
      if (!form.maintUnit?.length) { setFormError('Unit / Scope is required for Maintenance tasks.'); return; }
    }
    if (form.category === 'Complaint') {
      if (!form.propertyId) { setFormError('Property is required for Complaint tasks.'); return; }
      if (!form.maintUnit?.length) { setFormError('Unit / Scope is required for Complaint tasks.'); return; }
    }
    if (form.category === 'Collection') {
      if (!form.propertyId) { setFormError('Property is required for Collection tasks.'); return; }
      if (!form.tenantId) { setFormError('Tenant / Unit is required for Collection tasks.'); return; }
    }
    if (form.category === 'Renewal') {
      if (!form.propertyId) { setFormError('Property is required for Renewal tasks.'); return; }
      if (!form.tenantId) { setFormError('Tenant is required for Renewal tasks.'); return; }
    }
    setFormError('');
    if (modal === 'edit') {
      setTasks(prev => prev.map(t => t.id === form.id ? { ...form, updatedAt: nyTodayISO() } : t));
    } else {
      const newId = `task${Date.now()}`;
      const newTask = { ...form, id: newId, createdAt: today, updatedAt: today, notes: [], documents: [] };
      if (form.category === 'Maintenance') {
        const newMaintId = `m${Date.now() + 1}`;
        newTask.maintenanceId = newMaintId;
        setMaintenance(prev => [...(prev || []), {
          id: newMaintId,
          propertyId: form.propertyId,
          units: form.maintUnit,
          unit: form.maintUnit.join(', '),
          title: form.title,
          description: form.description || '',
          status: 'open',
          date: today,
          vendor: '',
          cost: '',
          recurring: false,
          recurringInterval: 'monthly',
          taskId: newId,
        }]);
      }
      setTasks(prev => [...(prev || []), newTask]);
    }
    setModal(null);
    toast(modal === 'edit' ? 'Task updated' : 'Task added');
  };

  const remove = id => {
    if (window.confirm('Delete task?')) { setTasks(prev => prev.filter(t => t.id !== id)); toast('Task deleted', 'info'); }
  };

  if (selectedId) return <TaskDetail taskId={selectedId} tasks={tasks} setTasks={setTasks} maintenance={maintenance} setMaintenance={setMaintenance} properties={properties} tenants={tenants} users={users} user={user} onBack={() => setSelectedId(null)} />;

  return (
    <div>
      <div className="page-header">
        <span className="page-title">Tasks</span>
        <button className="btn btn-primary" onClick={openAdd}>{ic(I.plus, 14, '#fff')} Add task</button>
      </div>
      <div style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <SB value={search} onChange={v => { setSearch(v); setTaskPage(1); }} placeholder="Search by title…" />
        </div>
        {/* Assignee scope. Only admins get the control — everyone else is already limited
            to their own tasks by the per-user visibility filter above, so a picker that
            can't widen anything would just mislead. */}
        {isAdmin && (
          <SearchSelect
            value={assigneeFilter}
            onChange={v => { setAssigneeFilter(v); setTaskPage(1); }}
            options={[
              { value: 'me', label: managedUser?.name ? `Assigned to me (${managedUser.name})` : 'Assigned to me' },
              { value: 'all', label: 'Everyone' },
              ...(users || [])
                .filter(u => u.name && u.name.trim() !== (managedUser?.name || '').trim())
                .map(u => ({ value: u.name, label: u.name.trim() })),
            ]}
            containerStyle={{ marginBottom: 0, width: 220 }}
          />
        )}
        <SearchSelect
          value={propFilter}
          onChange={v => { setPropFilter(v); setTenantFilter('all'); setTaskPage(1); }}
          options={[{ value: 'all', label: 'All properties' }, ...propFilterOptions.map(p => ({ value: p.id, label: p.name }))]}
          containerStyle={{ marginBottom: 0, width: 220 }}
        />
        {categoryFilter === 'Maintenance' && (
          <SearchSelect
            value={tenantFilter}
            onChange={v => { setTenantFilter(v); setTaskPage(1); }}
            options={[{ value: 'all', label: 'All tenants' }, { value: 'building-wide', label: 'Building Wide' },
              ...tenantFilterOptions.map(t => ({ value: t.id, label: t.unit ? `${t.unit} · ${t.name}` : t.name }))]}
            containerStyle={{ marginBottom: 0, width: 220 }}
          />
        )}
        {isMobile && (
          <select className="form-input" style={{ width: 'auto', minWidth: 150 }}
            value={categoryFilter} onChange={e => { setCategoryFilter(e.target.value); setTaskPage(1); }}>
            <option value="all">All categories</option>
            <option value="Inspection">Inspection</option>
            <option value="Maintenance">Maintenance</option>
            <option value="General">General</option>
            <option value="Invoice">Invoice</option>
            <option value="Renewal">Renewal</option>
            <option value="Collection">Collection</option>
            <option value="Complaint">Complaint</option>
          </select>
        )}
      </div>
      {/* ── Active tasks ── */}
      {isMobile
        ? <TaskCardList
            rows={activeRows.slice((taskPage - 1) * TASK_PAGE_SIZE, taskPage * TASK_PAGE_SIZE)} propName={propName} tenants={tenants}
            editingFollowUp={editingFollowUp} setEditingFollowUp={setEditingFollowUp} saveFollowUp={saveFollowUp}
            editingStatus={editingStatus} setEditingStatus={setEditingStatus} saveStatus={saveStatus}
            isOD={isOD} isDT={isDT} onSelect={setSelectedId} onEdit={openEdit} onRemove={remove}
            emptyMsg="No active tasks — add one to get started"
          />
        : <TaskTable
            rows={activeRows.slice((taskPage - 1) * TASK_PAGE_SIZE, taskPage * TASK_PAGE_SIZE)} propName={propName} tenants={tenants}
            editingFollowUp={editingFollowUp} setEditingFollowUp={setEditingFollowUp} saveFollowUp={saveFollowUp}
            editingStatus={editingStatus} setEditingStatus={setEditingStatus} saveStatus={saveStatus}
            isOD={isOD} isDT={isDT} onSelect={setSelectedId} onEdit={openEdit} onRemove={remove}
            emptyMsg="No active tasks — add one to get started"
          />
      }
      {activeRows.length > TASK_PAGE_SIZE && (() => {
        const totalPages = Math.ceil(activeRows.length / TASK_PAGE_SIZE);
        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, marginTop: 16, flexWrap: 'wrap' }}>
            <button className="btn" style={{ padding: '4px 10px', fontSize: 12 }} disabled={taskPage === 1} onClick={() => setTaskPage(p => p - 1)}>‹ Prev</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
              <button key={p} onClick={() => setTaskPage(p)} style={{ padding: '4px 10px', fontSize: 12, borderRadius: 6, border: '1px solid var(--border)', background: p === taskPage ? 'var(--accent)' : 'var(--bg2)', color: p === taskPage ? '#fff' : 'var(--text1)', fontWeight: p === taskPage ? 600 : 400, cursor: 'pointer' }}>{p}</button>
            ))}
            <button className="btn" style={{ padding: '4px 10px', fontSize: 12 }} disabled={taskPage === totalPages} onClick={() => setTaskPage(p => p + 1)}>Next ›</button>
          </div>
        );
      })()}

      {/* ── Closed tasks ── */}
      {closedRows.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <button
            style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text2)', fontFamily: 'inherit', fontSize: 13, fontWeight: 500, padding: '0 0 12px 0' }}
            onClick={() => setShowClosed(o => !o)}
          >
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d={showClosed ? 'M18 15l-6-6-6 6' : 'M6 9l6 6 6-6'} />
            </svg>
            Closed tasks ({closedRows.length})
          </button>
          {showClosed && (isMobile
            ? <TaskCardList
                rows={closedRows} propName={propName} tenants={tenants}
                editingFollowUp={editingFollowUp} setEditingFollowUp={setEditingFollowUp} saveFollowUp={saveFollowUp}
                editingStatus={editingStatus} setEditingStatus={setEditingStatus} saveStatus={saveStatus}
                isOD={isOD} isDT={isDT} onSelect={setSelectedId} onEdit={openEdit} onRemove={remove}
                emptyMsg=""
              />
            : <TaskTable
                rows={closedRows} propName={propName} tenants={tenants}
                editingFollowUp={editingFollowUp} setEditingFollowUp={setEditingFollowUp} saveFollowUp={saveFollowUp}
                editingStatus={editingStatus} setEditingStatus={setEditingStatus} saveStatus={saveStatus}
                isOD={isOD} isDT={isDT} onSelect={setSelectedId} onEdit={openEdit} onRemove={remove}
                emptyMsg=""
              />
          )}
        </div>
      )}

      {modal && (
        <Modal title={modal === 'edit' ? 'Edit task' : 'Add task'} onClose={() => setModal(null)}
          lg={modal === 'edit' || modalStep === 2}>

          {/* ── Step 1: Category selection (add mode only) ── */}
          {modal === 'add' && modalStep === 1 && (
            <div>
              <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>Select a category to get started</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {TASK_CATEGORIES.map(cat => (
                  <button key={cat.value} onClick={() => { setForm(p => applyCategoryDefaults(cat.value, p)); setModalStep(2); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', background: 'var(--bg2)', border: `1.5px solid var(--border)`, borderRadius: 12, cursor: 'pointer', textAlign: 'left', transition: 'border-color .15s, background .15s', width: '100%' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = cat.border; e.currentTarget.style.background = cat.bg; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--bg2)'; }}>
                    <div style={{ width: 38, height: 38, borderRadius: 10, background: 'var(--bg3)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="var(--text2)" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"><path d={cat.icon}/></svg>
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text1)', marginBottom: 2 }}>{cat.label}</div>
                      <div style={{ fontSize: 12, color: 'var(--text2)' }}>{cat.desc}</div>
                    </div>
                    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="var(--text3)" strokeWidth={2} style={{ marginLeft: 'auto', flexShrink: 0 }}><path d="M9 18l6-6-6-6"/></svg>
                  </button>
                ))}
              </div>
              {/* No Cancel button — the modal's X closes it */}
            </div>
          )}

          {/* ── Step 2 / Edit: Task form ── */}
          {(modal === 'edit' || modalStep === 2) && (
            <div>
              {modal === 'add' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                  <button className="btn-ghost" style={{ fontSize: 12, padding: '3px 8px' }} onClick={() => setModalStep(1)}>← Back</button>
                  {(() => { const cat = TASK_CATEGORIES.find(c => c.value === form.category); return cat ? <span style={{ fontSize: 12, fontWeight: 600, padding: '3px 12px', borderRadius: 20, background: cat.bg, color: cat.color, border: `1px solid ${cat.border}` }}>{cat.label}</span> : null; })()}
                </div>
              )}

              {/* Order: Title → Property (+ its unit/tenant selectors) → Assigned to →
                  Description last, since it's the big free-text field. */}

              {/* Title — not shown for Inspection (auto-filled) */}
              {form.category !== 'Inspection' && (
                <div className="form-group">
                  <label className="form-label">Title *</label>
                  <div>
                    <input className="form-input" value={form.title || ''} onChange={e => ff('title')(e.target.value)}
                      maxLength={TASK_TITLE_MAX} placeholder="e.g. 8615 | 4E | Sink leak" />
                    <div style={{ fontSize: 11, color: (form.title || '').length >= TASK_TITLE_MAX ? 'var(--amber-txt)' : 'var(--text3)', marginTop: 4 }}>
                      {(form.title || '').length}/{TASK_TITLE_MAX} — keep it short, the detail goes in the description below.
                    </div>
                  </div>
                </div>
              )}

              {/* Category — only shown in edit mode */}
              {modal === 'edit' && (
                <FS label="Category" value={form.category || 'General'}
                  onChange={v => setForm(p => applyCategoryDefaults(v, p))}
                  options={[
                    { value: 'Inspection', label: 'Inspection' },
                    { value: 'Maintenance', label: 'Maintenance' },
                    { value: 'General', label: 'General' },
                    { value: 'Invoice', label: 'Invoice' },
                    { value: 'Renewal', label: 'Renewal' },
                    { value: 'Collection', label: 'Collection' },
                    { value: 'Complaint', label: 'Complaint' },
                  ]} />
              )}

              <SearchSelect
                label={(form.category === 'Inspection' || form.category === 'Maintenance' || form.category === 'Complaint' || form.category === 'Collection' || form.category === 'Renewal') ? 'Property *' : 'Property (optional)'}
                value={form.propertyId || ''}
                onChange={v => setForm(p => ({ ...p, propertyId: v, tenantId: '', maintUnit: [] }))}
                options={[
                  ...((form.category === 'Inspection' || form.category === 'Maintenance' || form.category === 'Complaint' || form.category === 'Collection' || form.category === 'Renewal') ? [] : [{ value: '', label: '— None —' }]),
                  ...properties.map(p => ({ value: p.id, label: p.name }))
                ]}
              />

              {form.propertyId && (form.category === 'Maintenance' || form.category === 'Complaint') && (() => {
                const propTenants = (tenants || []).filter(t => t.propertyId === form.propertyId && t.status !== 'Previous').sort((a, b) => (a.unit || '').localeCompare(b.unit || '', undefined, { numeric: true }));
                return (
                  <MultiSearchSelect label="Unit / Scope *" value={form.maintUnit || []}
                    onChange={v => setForm(p => {
                      const next = { ...p, maintUnit: v };
                      if (!v.length || v.includes('Building Wide')) { next.tenantId = ''; }
                      else if (v.length === 1) { const tenant = propTenants.find(t => `${t.unit} - ${t.name}` === v[0]); next.tenantId = tenant?.id || ''; }
                      else { next.tenantId = ''; }
                      return next;
                    })}
                    options={[{ value: 'Building Wide', label: 'Building Wide' }, ...propTenants.map(t => ({ value: `${t.unit} - ${t.name}`, label: t.status === 'Vacant' ? `Unit ${t.unit} - Vacant` : `Unit ${t.unit} - ${t.name}` }))]}
                  />
                );
              })()}

              {form.propertyId && form.category === 'General' && (() => {
                const propTenants = (tenants || []).filter(t => t.propertyId === form.propertyId && t.status !== 'Previous').sort((a, b) => (a.unit || '').localeCompare(b.unit || '', undefined, { numeric: true }));
                if (!propTenants.length) return null;
                return (
                  <MultiSearchSelect label="Linked Units (optional)" value={form.maintUnit || []}
                    onChange={v => setForm(p => ({ ...p, maintUnit: v, tenantId: v.length === 1 && !v.includes('Building Wide') ? ((tenants || []).find(t => `${t.unit} - ${t.name}` === v[0])?.id || '') : '' }))}
                    options={[{ value: 'Building Wide', label: 'Building Wide' }, ...propTenants.map(t => ({ value: `${t.unit} - ${t.name}`, label: t.status === 'Vacant' ? `Unit ${t.unit} - Vacant` : `Unit ${t.unit} - ${t.name}` }))]}
                  />
                );
              })()}

              {form.propertyId && form.category !== 'Maintenance' && form.category !== 'Complaint' && form.category !== 'General' && (() => {
                const propTenants = (tenants || []).filter(t => t.propertyId === form.propertyId && t.status !== 'Vacant');
                if (!propTenants.length) return null;
                return (
                  <SearchSelect
                    label={(form.category === 'Inspection' || form.category === 'Collection' || form.category === 'Renewal') ? 'Tenant / Unit *' : 'Linked tenant (optional)'}
                    value={form.tenantId || ''}
                    onChange={v => setForm(p => {
                      const next = { ...p, tenantId: v };
                      if ((p.category === 'Inspection' || p.category === 'Renewal') && v) { const t = buildInspectionTitle(v, p.propertyId); if (t) next.title = t; }
                      return next;
                    })}
                    options={[...((form.category === 'Inspection' || form.category === 'Collection' || form.category === 'Renewal') ? [] : [{ value: '', label: '— None —' }]), ...propTenants.map(t => ({ value: t.id, label: `${t.name} · Unit ${t.unit}` }))]}
                  />
                );
              })()}

              <MultiSearchSelect
                label="Assigned to"
                value={form.assignedTo || []}
                onChange={v => setForm(p => ({ ...p, ...withAssignees(v, users) }))}
                options={assignUserOptions(form.assignedTo)}
              />

              {modal === 'edit' && form.category !== 'Inspection' && form.category !== 'Maintenance' && form.category !== 'Complaint' && (
                <FI label="Follow-up date" value={form.followUp || ''} onChange={ff('followUp')} type="date" />
              )}

              {/* Description last and roomy. A direct .form-label keeps it on one row like
                  every other field; Generate title sits under the box rather than beside
                  the label, which would break that row. */}
              <div className="form-group">
                <label className="form-label">Description{form.category === 'Inspection' ? '' : ' (optional)'}</label>
                <div>
                  <textarea className="form-textarea" style={{ minHeight: 280 }}
                    value={form.description || ''} onChange={e => ff('description')(e.target.value)} />
                  {modal === 'add' && form.category !== 'Inspection' && (form.description || '').trim() && (
                    <button className="btn" onClick={generateTitleFromDesc} disabled={generatingTitle}
                      style={{ fontSize: 11, padding: '2px 10px', marginTop: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                      {generatingTitle ? '…' : '✨'} {generatingTitle ? 'Generating…' : 'Generate title'}
                    </button>
                  )}
                </div>
              </div>

              {formError && (
                <div style={{ marginBottom: 12, padding: '8px 12px', background: 'var(--red-bg)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, color: 'var(--red-txt)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  {ic(I.alert, 13, 'var(--red-txt)')} {formError}
                </div>
              )}

              <div className="modal-actions">
                <button className="btn" onClick={() => setModal(null)}>Cancel</button>
                <SavingBtn onClick={save}>Save</SavingBtn>
              </div>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

function TaskTable({ rows, propName, tenants, editingFollowUp, setEditingFollowUp, saveFollowUp, editingStatus, setEditingStatus, saveStatus, isOD, isDT, onSelect, onEdit, onRemove, emptyMsg }) {
  return (
    <table className="list-table">
      <thead>
        <tr>
          <th>Follow-up</th>
          <th>Title</th>
          <th>Category</th>
          <th>Property</th>
          <th>Assigned To</th>
          <th>Status</th>
          <th style={{ width: 40 }}></th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          emptyMsg ? <tr><td colSpan={7}><div className="empty">{emptyMsg}</div></td></tr> : null
        ) : rows.map(t => {
          return (
          <tr key={t.id} className="clickable" onClick={() => onSelect(t.id)}>
            <td onClick={e => e.stopPropagation()}>
              {editingFollowUp === t.id ? (
                <input
                  type="date"
                  className="form-input"
                  style={{ fontSize: 12, padding: '3px 7px', width: 'auto' }}
                  defaultValue={t.followUp || ''}
                  autoFocus
                  onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') setEditingFollowUp(null); }}
                  onBlur={e => { if (e.target.value) saveFollowUp(t.id, e.target.value); else setEditingFollowUp(null); }}
                />
              ) : (
                <span
                  className="edit-hint"
                  title="Click to edit"
                  style={{ fontSize: 12, color: isOD(t) ? 'var(--red-txt)' : isDT(t) ? 'var(--amber-txt)' : 'var(--text2)', fontWeight: (isOD(t) || isDT(t)) ? 600 : 400, textDecoration: 'underline dotted', textUnderlineOffset: 3 }}
                  onClick={() => setEditingFollowUp(t.id)}
                >
                  {t.followUp ? fmtDate(t.followUp) : '—'}
                  <span className="edit-pencil">{ic(I.pencil, 11)}</span>
                </span>
              )}
            </td>
            <td style={{ maxWidth: 360 }}>
              {/* Title (and the tenant chip) only. No description preview and no note
                  count/preview — those are read on the task itself. */}
              <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</div>
              {t.tenantId && (() => {
                const tnt = (tenants || []).find(x => x.id === t.tenantId);
                if (!tnt) return null;
                const label = [tnt.unit, tnt.name].map(s => (s || '').trim()).filter(Boolean).join(' ● ');
                return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 4, fontSize: 10, fontWeight: 600, background: 'var(--accent-bg)', color: 'var(--accent)', borderRadius: 10, padding: '1px 7px', border: '1px solid var(--accent)' }}>{ic(I.user, 10, 'var(--accent)')} {label}</span>;
              })()}
            </td>
            <td>
              <TaskCatBadge task={t} />
            </td>
            <td style={{ fontSize: 12, color: 'var(--text2)' }}>
              {t.propertyId ? propName(t.propertyId) : <span style={{ color: 'var(--text3)' }}>—</span>}
            </td>
            <td>
              {(() => {
                const a = Array.isArray(t.assignedTo) ? t.assignedTo : (t.assignedTo ? [t.assignedTo] : []);
                return a.length
                  ? <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>{a.map(n => (
                      <span key={n} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 600, background: 'var(--bg3)', color: 'var(--text2)', borderRadius: 10, padding: '1px 7px', border: '1px solid var(--border)' }}>{ic(I.user, 10, 'var(--text2)')} {n}</span>
                    ))}</div>
                  : <span style={{ color: 'var(--text3)', fontSize: 12 }}>—</span>;
              })()}
            </td>
            <td onClick={e => e.stopPropagation()}>
              {editingStatus === t.id ? (
                <select
                  className="form-input"
                  style={{ fontSize: 12, padding: '3px 7px', width: 'auto' }}
                  defaultValue={taskStatus(t)}
                  autoFocus
                  onChange={e => saveStatus(t.id, e.target.value)}
                  onBlur={() => setEditingStatus(null)}
                >
                  <option value="Active">Active</option>
                  <option value="Closed">Closed</option>
                </select>
              ) : (
                <span title="Click to change" style={{ cursor: 'pointer' }} onClick={() => setEditingStatus(t.id)}>
                  <Badge label={taskStatus(t)} />
                </span>
              )}
            </td>
            <td>
              <div onClick={e => e.stopPropagation()}>
                <KebabMenu items={[
                  { label: 'View detail', onClick: () => onSelect(t.id) },
                  { label: 'Edit', onClick: () => onEdit(t) },
                  { label: 'Delete', danger: true, onClick: () => onRemove(t.id) },
                ]} />
              </div>
            </td>
          </tr>
        );
        })}
      </tbody>
    </table>
  );
}

function TaskCardList({ rows, propName, tenants, editingFollowUp, setEditingFollowUp, saveFollowUp, editingStatus, setEditingStatus, saveStatus, isOD, isDT, onSelect, onEdit, onRemove, emptyMsg }) {
  if (rows.length === 0) return emptyMsg ? <div className="empty">{emptyMsg}</div> : null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {rows.map(t => {
        const overdue = isOD(t);
        const dueToday = isDT(t);
        return (
          <div
            key={t.id}
            onClick={() => onSelect(t.id)}
            style={{ background: 'var(--bg1)', border: '1px solid var(--border)', borderRadius: 12, padding: '12px 14px', cursor: 'pointer' }}
          >
            {/* Top row: date + kebab */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
              <div onClick={e => e.stopPropagation()}>
                {editingFollowUp === t.id ? (
                  <input
                    type="date"
                    className="form-input"
                    style={{ fontSize: 12, padding: '3px 7px', width: 'auto' }}
                    defaultValue={t.followUp || ''}
                    autoFocus
                    onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') setEditingFollowUp(null); }}
                    onBlur={e => { if (e.target.value) saveFollowUp(t.id, e.target.value); else setEditingFollowUp(null); }}
                  />
                ) : (
                  <span
                    className="edit-hint"
                    onClick={() => setEditingFollowUp(t.id)}
                    style={{ fontSize: 12, fontWeight: (overdue || dueToday) ? 700 : 500, color: overdue ? 'var(--red-txt)' : dueToday ? 'var(--amber-txt)' : 'var(--text2)', textDecoration: 'underline dotted', textUnderlineOffset: 3 }}
                  >
                    {t.followUp ? fmtDate(t.followUp) : '—'}
                    <span className="edit-pencil">{ic(I.pencil, 11)}</span>
                  </span>
                )}
              </div>
              <div onClick={e => e.stopPropagation()}>
                <KebabMenu items={[
                  { label: 'View detail', onClick: () => onSelect(t.id) },
                  { label: 'Edit', onClick: () => onEdit(t) },
                  { label: 'Delete', danger: true, onClick: () => onRemove(t.id) },
                ]} />
              </div>
            </div>
            {/* Title + description */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
              <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text1)', lineHeight: 1.3, flex: 1 }}>{t.title}</div>
              <TaskCatBadge task={t} small />
            </div>
            {t.tenantId && (() => {
              const tnt = (tenants || []).find(x => x.id === t.tenantId);
              if (!tnt) return null;
              const label = [tnt.unit, tnt.name].map(s => (s || '').trim()).filter(Boolean).join(' ● ');
              return <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginBottom: 6, fontSize: 11, fontWeight: 600, background: 'var(--accent-bg)', color: 'var(--accent)', borderRadius: 10, padding: '2px 8px', border: '1px solid var(--accent)' }}>{ic(I.user, 11, 'var(--accent)')} {label}</div>;
            })()}
            {(() => {
              const a = Array.isArray(t.assignedTo) ? t.assignedTo : (t.assignedTo ? [t.assignedTo] : []);
              return a.length ? <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>{a.map(n => (
                <span key={n} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, background: 'var(--bg3)', color: 'var(--text2)', borderRadius: 10, padding: '2px 8px', border: '1px solid var(--border)' }}>{ic(I.user, 11, 'var(--text2)')} {n}</span>
              ))}</div> : null;
            })()}
            {t.description && (
              <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.4, marginBottom: 8, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{t.description}</div>
            )}
            {(t.notes || []).length > 0 && (() => {
              const last = t.notes[t.notes.length - 1];
              return (
                <div style={{ marginBottom: 8, padding: '6px 10px', background: 'var(--bg2)', borderRadius: 7, borderLeft: '2px solid var(--border)' }}>
                  <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 2 }}>
                    {ic(I.clipboard, 10, 'var(--text3)')} {t.notes.length} note{t.notes.length > 1 ? 's' : ''} · {last.author} · {fmtDate(last.date)}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text2)', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{last.text}</div>
                </div>
              );
            })()}
            {/* Bottom row: status + meta */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 }}>
              <div onClick={e => e.stopPropagation()}>
                {editingStatus === t.id ? (
                  <select
                    className="form-input"
                    style={{ fontSize: 12, padding: '3px 7px', width: 'auto' }}
                    defaultValue={taskStatus(t)}
                    autoFocus
                    onChange={e => saveStatus(t.id, e.target.value)}
                    onBlur={() => setEditingStatus(null)}
                  >
                    <option value="Active">Active</option>
                    <option value="Closed">Closed</option>
                  </select>
                ) : (
                  <span title="Tap to change" onClick={() => setEditingStatus(t.id)} style={{ cursor: 'pointer' }}>
                    <Badge label={taskStatus(t)} />
                  </span>
                )}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text2)', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                {t.propertyId && <span style={{ color: 'var(--text3)' }}>{propName(t.propertyId)}</span>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Rent Roll ─────────────────────────────────────────────────────────────
function RentRoll({ tenants: allTenants, setTenants, properties, ledger: allLedger, onTenantClick, initialPropId, initialMonth, onPropIdChange, onMonthChange }) {
  const [propId, setPropId] = useState(() => RR_PERSIST.propId ?? (initialPropId || ''));
  const [month, setMonth] = useState(() => RR_PERSIST.month ?? (initialMonth || nyTodayISO().slice(0, 7)));
  const [sortCol, setSortCol] = useState(() => RR_PERSIST.sortCol);
  const [sortDir, setSortDir] = useState(() => RR_PERSIST.sortDir);
  const [view, setView] = useState(() => RR_PERSIST.view);
  const detailed = view === 'detailed';
  const [hiddenCols, setHiddenCols] = useState(() => RR_PERSIST.hiddenCols);
  const [hiddenRows, setHiddenRows] = useState(() => RR_PERSIST.hiddenRows);
  const [colMenu, setColMenu] = useState(null);            // key of the open column menu
  const [colMenuPos, setColMenuPos] = useState({ top: 0, left: 0 });
  const [colFilters, setColFilters] = useState(() => RR_PERSIST.colFilters); // { colKey: Set(allowedValues) }
  // Persist view state across tab switches (module store, cleared on page reload).
  useEffect(() => { RR_PERSIST.propId = propId; if (onPropIdChange) onPropIdChange(propId); }, [propId]); // eslint-disable-line
  useEffect(() => { RR_PERSIST.month = month; if (onMonthChange) onMonthChange(month); }, [month]); // eslint-disable-line
  useEffect(() => { RR_PERSIST.view = view; }, [view]);
  useEffect(() => { RR_PERSIST.sortCol = sortCol; RR_PERSIST.sortDir = sortDir; }, [sortCol, sortDir]);
  useEffect(() => { RR_PERSIST.hiddenCols = hiddenCols; }, [hiddenCols]);
  useEffect(() => { RR_PERSIST.hiddenRows = hiddenRows; }, [hiddenRows]);
  useEffect(() => { RR_PERSIST.colFilters = colFilters; }, [colFilters]);
  const showCol = k => !hiddenCols.has(k);
  const hideCol = k => { setHiddenCols(prev => { const n = new Set(prev); n.add(k); return n; }); setColFilters(prev => { if (!prev[k]) return prev; const n = { ...prev }; delete n[k]; return n; }); };
  // When the property or month actually changes, drop row/value filters (they're data-specific).
  const resetDataViews = () => { setHiddenRows(new Set()); setColFilters({}); setColMenu(null); };
  useEffect(() => {
    if (!colMenu) return;
    const onDown = e => { if (!e.target.closest('[data-colmenu]') && !e.target.closest('[data-colhdr]')) setColMenu(null); };
    const onScroll = () => setColMenu(null);
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', onScroll, true);
    return () => { document.removeEventListener('mousedown', onDown); window.removeEventListener('scroll', onScroll, true); };
  }, [colMenu]);
  const toast = useToast();
  const [genMode, setGenMode] = useState(''); // '' | 'pdf' | 'summary' — which export is generating
  const [exportModal, setExportModal] = useState(false);
  const [reportNotes, setReportNotes] = useState({});
  const [editingReportNote, setEditingReportNote] = useState(false);
  const [reportNoteDraft, setReportNoteDraft] = useState('');
  const [editingTenantNoteId, setEditingTenantNoteId] = useState(null);
  const [tenantNoteDraft, setTenantNoteDraft] = useState('');

  const reportNoteKey = propId && month ? `${propId}-${month}` : null;
  const reportNote = reportNoteKey ? (reportNotes[reportNoteKey] || '') : '';

  const openReportNote = () => { setReportNoteDraft(reportNote); setEditingReportNote(true); };
  const saveReportNote = () => {
    if (reportNoteKey) setReportNotes(p => ({ ...p, [reportNoteKey]: reportNoteDraft }));
    setEditingReportNote(false);
  };

  const openTenantNote = (t) => { setTenantNoteDraft(t.rentRollNote || ''); setEditingTenantNoteId(t.id); };
  const saveTenantNote = (tenantId) => {
    setTenants(prev => prev.map(t => t.id === tenantId ? { ...t, rentRollNote: tenantNoteDraft } : t));
    setEditingTenantNoteId(null);
  };

  const tenants = useMemo(() => {
    const monthStart = month ? `${month}-01` : null;
    const monthEnd = month ? `${month}-31` : null;
    // Anyone charged in the selected month belongs on that month's roll, whatever the
    // lease dates say. Renewing a lease overwrites leaseStart with the new term, which
    // would otherwise erase a long-standing tenant from every earlier month; and someone
    // who has moved out can still be billed later (final utilities, arrears). Having been
    // billed is the fact that matters, so it wins over both date rules.
    const billedThisMonth = new Set(
      month
        ? allLedger.filter(l => l.type === 'charge' && !l.deleted && (l.date || '').startsWith(month)).map(l => l.tenantId)
        : []
    );
    return allTenants
      .filter(t => {
        if (t.propertyId !== propId) return false;
        if (billedThisMonth.has(t.id)) return true;
        if (t.status !== 'Previous') return true;
        // Include previous tenants only if they were active during the selected month
        if (!monthStart) return false;
        const movedOut = t.moveOutDate || '';
        return movedOut >= monthStart;
      })
      .filter(t => {
        if (billedThisMonth.has(t.id)) return true;
        // Exclude tenants whose lease hadn't started yet by end of selected month
        if (!monthEnd || !t.leaseStart) return true;
        return t.leaseStart <= monthEnd;
      })
      .sort((a, b) => (a.unit || '').localeCompare(b.unit || '', undefined, { numeric: true }));
  }, [allTenants, allLedger, propId, month]);

  const rows = useMemo(() => {
    if (!propId || !month) return [];
    const cutoff = `${month}-01`;
    return tenants.map(t => {
      const ledger = allLedger.filter(l => l.tenantId === t.id && l.type === 'charge' && !l.deleted);
      // This month charges
      const monthEntries = ledger.filter(l => (l.date || '').startsWith(month));
      const monthlyRent = monthEntries.reduce((s, l) => s + (+l.amount || 0), 0);
      const paymentMade = monthEntries.reduce((s, l) => s + (+l.paidAmount || 0), 0);
      // Past months overdue (unpaid balance before selected month)
      const pastOverdue = ledger
        .filter(l => l.date < cutoff)
        .reduce((s, l) => s + Math.max(0, (+l.amount || 0) - (+l.paidAmount || 0)), 0);
      const totalBalance = monthlyRent + pastOverdue - paymentMade;
      // Rent composition from the lease (for the Detailed view)
      const programRent = +t.programShare || 0;
      const tenantPortion = +t.tenantShare || 0;
      const parking = t.hasParking ? (+t.parkingFee || 0) : 0;
      const washerDryer = t.hasWasherDryer ? (+t.washerDryerFee || 0) : 0;
      return { t, programRent, tenantPortion, parking, washerDryer, monthlyRent, paymentMade, pastOverdue, totalBalance };
    });
  }, [tenants, allLedger, propId, month]);

  // Categorical columns that support value-filtering via their header menu.
  const FILTER_ACCESSOR = {
    unit: r => r.t.unit || '—',
    name: r => r.t.name || '—',
    program: r => r.t.program || 'Cash',
    paymentType: r => r.t.paymentType || '—',
    status: r => r.t.status || '—',
  };
  const SORTABLE_COLS = new Set(['programRent', 'tenantPortion', 'parking', 'washerDryer', 'monthlyRent', 'paymentMade', 'pastOverdue', 'totalBalance']);
  const colDistinct = col => Array.from(new Set(rows.map(r => FILTER_ACCESSOR[col](r)))).sort();
  const toggleColFilter = (col, v) => setColFilters(prev => {
    const all = colDistinct(col);
    const cur = prev[col] ? new Set(prev[col]) : new Set(all);
    if (cur.has(v)) cur.delete(v); else cur.add(v);
    const next = { ...prev };
    if (cur.size >= all.length) delete next[col]; else next[col] = cur;
    return next;
  });
  const filteredRows = useMemo(() => rows.filter(r =>
    !hiddenRows.has(r.t.id) &&
    Object.entries(colFilters).every(([col, set]) => !FILTER_ACCESSOR[col] || set.has(FILTER_ACCESSOR[col](r)))
  ), [rows, colFilters, hiddenRows]); // eslint-disable-line react-hooks/exhaustive-deps

  const totals = useMemo(() => filteredRows.reduce(
    (acc, r) => ({
      programRent: acc.programRent + r.programRent,
      tenantPortion: acc.tenantPortion + r.tenantPortion,
      parking: acc.parking + r.parking,
      washerDryer: acc.washerDryer + r.washerDryer,
      monthlyRent: acc.monthlyRent + r.monthlyRent,
      paymentMade: acc.paymentMade + r.paymentMade,
      pastOverdue: acc.pastOverdue + r.pastOverdue,
      totalBalance: acc.totalBalance + r.totalBalance,
    }),
    { programRent: 0, tenantPortion: 0, parking: 0, washerDryer: 0, monthlyRent: 0, paymentMade: 0, pastOverdue: 0, totalBalance: 0 }
  ), [filteredRows]);

  const fmt = n => `$${(+n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const ready = propId && month;
  const propLabel = properties.find(p => p.id === propId)?.name || 'Property';
  const fileBase = `RentRoll_${propLabel.replace(/\s+/g, '_')}_${month}`;

  const exportExcel = () => {
    const wsRows = sortedRows.map(({ t, programRent, tenantPortion, parking, washerDryer, monthlyRent, paymentMade, pastOverdue, totalBalance }) => ({
      'Unit': t.unit, 'Tenant Name': t.name, 'Program': t.program || 'Cash',
      'Payment Type': t.paymentType || '', 'Status': t.status, 'Due Day': t.dueDay || 1,
      ...(detailed ? { 'Program Rent': programRent, 'Tenant Portion': tenantPortion, 'Parking': parking, 'Washer/Dryer': washerDryer } : {}),
      'Monthly Rent': monthlyRent, 'Payment Made': paymentMade,
      'Past Overdue': pastOverdue, 'Total Balance': totalBalance,
      'Notes': t.rentRollNote || '',
    }));
    wsRows.push({ 'Unit': 'TOTALS', 'Tenant Name': '', 'Program': '', 'Payment Type': '', 'Status': '', 'Due Day': '',
      ...(detailed ? { 'Program Rent': totals.programRent, 'Tenant Portion': totals.tenantPortion, 'Parking': totals.parking, 'Washer/Dryer': totals.washerDryer } : {}),
      'Monthly Rent': totals.monthlyRent, 'Payment Made': totals.paymentMade,
      'Past Overdue': totals.pastOverdue, 'Total Balance': totals.totalBalance, 'Notes': reportNote ? `Report note: ${reportNote}` : '' });
    const ws = XLSX.utils.json_to_sheet(wsRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Rent Roll');
    XLSX.writeFile(wb, `${fileBase}.xlsx`);
    setExportModal(false);
  };

  // Build the AI audit-summary payload (shared by the full PDF and the summary-only export)
  const fetchAuditSummary = async () => {
    const res = await httpsCallable(functions, 'generateRentRollAudit')({
      property: propLabel,
      month,
      totals: { monthlyRent: totals.monthlyRent, paymentMade: totals.paymentMade, pastOverdue: totals.pastOverdue, totalBalance: totals.totalBalance },
      rows: sortedRows.map(({ t, monthlyRent, paymentMade, pastOverdue, totalBalance }) => ({
        unit: t.unit, name: t.name, program: t.program || 'Cash',
        monthlyRent, paymentMade, pastOverdue, totalBalance, note: t.rentRollNote || '',
      })),
    });
    return res?.data?.summary || '';
  };

  // Summary-only: a clean one-pager with just the AI-written audit summary.
  const exportSummary = async () => {
    setGenMode('summary');
    let auditSummary = '';
    try {
      auditSummary = await fetchAuditSummary();
    } catch (e) {
      console.warn('Audit summary generation failed:', e);
    }
    if (!auditSummary) {
      setGenMode('');
      toast('Could not generate the AI summary — please try again', 'error');
      return;
    }
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const margin = 56;
    let y = margin;
    pdf.setFontSize(15); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(20, 20, 20);
    pdf.text('Rent Roll — Audit Summary', margin, y); y += 18;
    pdf.setFontSize(10); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(110, 110, 110);
    pdf.text(`${propLabel}  ·  ${month}`, margin, y); y += 14;
    pdf.setDrawColor(180, 180, 180); pdf.line(margin, y, pageW - margin, y); y += 20;
    pdf.setFontSize(11); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(40, 40, 40);
    pdf.splitTextToSize(auditSummary, pageW - margin * 2).forEach(line => {
      if (y > pageH - margin) { pdf.addPage(); y = margin; }
      pdf.text(line, margin, y); y += 15;
    });
    pdf.setFontSize(7); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(170, 170, 170);
    pdf.text(`Generated ${new Date().toLocaleDateString('en-US', { timeZone: 'America/New_York' })}`, pageW - margin, pageH - 16, { align: 'right' });
    pdf.save(`${fileBase}_Summary.pdf`);
    setGenMode('');
    setExportModal(false);
  };

  const exportPDF = async () => {
    setGenMode('pdf');
    // Ask the AI for an audit summary that gets placed on the final Notes page.
    let auditSummary = '';
    try {
      auditSummary = await fetchAuditSummary();
    } catch (e) {
      console.warn('Audit summary generation failed (non-fatal):', e);
    }

    // Detailed has 14 columns — give it tabloid (11x17) so nothing overlaps. Simple fits on letter.
    const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: detailed ? 'tabloid' : 'letter' });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const margin = 36;
    let y = margin;
    // Trim a string to fit a column width at the current font size, adding an ellipsis.
    const fitText = (str, w) => {
      let s = String(str ?? '');
      if (pdf.getTextWidth(s) <= w) return s;
      while (s.length > 1 && pdf.getTextWidth(s + '…') > w) s = s.slice(0, -1);
      return s + '…';
    };

    pdf.setFontSize(14); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(20, 20, 20);
    pdf.text('Rent Roll', margin, y); y += 16;
    pdf.setFontSize(9); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(110, 110, 110);
    pdf.text(`${propLabel}  ·  ${month}`, margin, y); y += 16;
    if (reportNote) {
      pdf.setFontSize(8.5); pdf.setFont('helvetica', 'italic'); pdf.setTextColor(80, 80, 80);
      const noteLines = pdf.splitTextToSize(`Note: ${reportNote}`, pageW - margin * 2);
      pdf.text(noteLines, margin, y); y += noteLines.length * 11 + 4;
    }
    pdf.setDrawColor(180, 180, 180); pdf.line(margin, y, pageW - margin, y); y += 12;

    const cols = detailed ? [
      { label: 'Unit', w: 42, align: 'left' }, { label: 'Tenant Name', w: 150, align: 'left' },
      { label: 'Program', w: 95, align: 'left' }, { label: 'Pay Type', w: 78, align: 'left' },
      { label: 'Status', w: 66, align: 'left' }, { label: 'Due', w: 38, align: 'center' },
      { label: 'Prog Rent', w: 78, align: 'right' }, { label: 'Tenant', w: 78, align: 'right' },
      { label: 'Parking', w: 70, align: 'right' }, { label: 'W/D', w: 60, align: 'right' },
      { label: 'Monthly Rent', w: 82, align: 'right' }, { label: 'Payment Made', w: 82, align: 'right' },
      { label: 'Past Overdue', w: 82, align: 'right' }, { label: 'Total Balance', w: 85, align: 'right' },
    ] : [
      { label: 'Unit', w: 38, align: 'left' }, { label: 'Tenant Name', w: 130, align: 'left' },
      { label: 'Program', w: 70, align: 'left' }, { label: 'Payment Type', w: 72, align: 'left' },
      { label: 'Status', w: 55, align: 'left' }, { label: 'Due Day', w: 40, align: 'center' },
      { label: 'Monthly Rent', w: 72, align: 'right' }, { label: 'Payment Made', w: 72, align: 'right' },
      { label: 'Past Overdue', w: 72, align: 'right' }, { label: 'Total Balance', w: 72, align: 'right' },
    ];

    pdf.setFontSize(7.5); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(100, 100, 100);
    let x = margin;
    cols.forEach(c => {
      const tx = c.align === 'right' ? x + c.w : c.align === 'center' ? x + c.w / 2 : x;
      pdf.text(c.label.toUpperCase(), tx, y, { align: c.align === 'left' ? 'left' : c.align });
      x += c.w;
    });
    y += 4; pdf.setDrawColor(200, 200, 200); pdf.line(margin, y, pageW - margin, y); y += 9;

    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8.5);
    sortedRows.forEach(({ t, programRent, tenantPortion, parking, washerDryer, monthlyRent, paymentMade, pastOverdue, totalBalance }) => {
      if (y > pdf.internal.pageSize.getHeight() - 50) { pdf.addPage(); y = margin + 20; }
      x = margin; pdf.setTextColor(30, 30, 30);
      const vals = [t.unit, t.name, t.program || 'Cash', t.paymentType || '—', t.status, String(t.dueDay || 1),
        ...(detailed ? [fmt(programRent), fmt(tenantPortion), fmt(parking), fmt(washerDryer)] : []),
        fmt(monthlyRent), fmt(paymentMade), fmt(pastOverdue), fmt(totalBalance)];
      const lastIdx = cols.length - 1; // Total Balance column
      cols.forEach((c, i) => {
        const tx = c.align === 'right' ? x + c.w : c.align === 'center' ? x + c.w / 2 : x;
        // Total Balance turns red when the tenant still owes money.
        if (i === lastIdx && totalBalance > 0) pdf.setTextColor(200, 30, 30);
        else pdf.setTextColor(30, 30, 30);
        // Left/center columns hold free text — truncate so they never bleed into the next column.
        const cell = c.align === 'right' ? String(vals[i]) : fitText(vals[i], c.w - 4);
        pdf.text(cell, tx, y, { align: c.align === 'left' ? 'left' : c.align });
        x += c.w;
      });
      y += 14;
    });

    y += 4; pdf.setDrawColor(100, 100, 100); pdf.setLineWidth(0.8); pdf.line(margin, y, pageW - margin, y); y += 10;
    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(8.5); pdf.setTextColor(30, 30, 30);
    x = margin;
    const totVals = ['TOTALS', '', '', '', '', '',
      ...(detailed ? [fmt(totals.programRent), fmt(totals.tenantPortion), fmt(totals.parking), fmt(totals.washerDryer)] : []),
      fmt(totals.monthlyRent), fmt(totals.paymentMade), fmt(totals.pastOverdue), fmt(totals.totalBalance)];
    const totLastIdx = cols.length - 1;
    cols.forEach((c, i) => {
      const tx = c.align === 'right' ? x + c.w : c.align === 'center' ? x + c.w / 2 : x;
      if (i === totLastIdx && totals.totalBalance > 0) pdf.setTextColor(200, 30, 30);
      else pdf.setTextColor(30, 30, 30);
      pdf.text(totVals[i], tx, y, { align: c.align === 'left' ? 'left' : c.align });
      x += c.w;
    });

    // ── Final page: AI audit summary + tenant notes ──────────────────────────
    const noted = sortedRows.filter(({ t }) => t.rentRollNote);
    if (auditSummary || noted.length) {
      pdf.addPage();
      y = margin;
      pdf.setFontSize(14); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(20, 20, 20);
      pdf.text('Audit Summary & Notes', margin, y); y += 16;
      pdf.setFontSize(9); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(110, 110, 110);
      pdf.text(`${propLabel}  ·  ${month}`, margin, y); y += 14;
      pdf.setDrawColor(180, 180, 180); pdf.line(margin, y, pageW - margin, y); y += 18;

      if (auditSummary) {
        pdf.setFontSize(10.5); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(40, 40, 40);
        pdf.text('Summary', margin, y); y += 15;
        pdf.setFontSize(9.5); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(55, 55, 55);
        pdf.splitTextToSize(auditSummary, pageW - margin * 2).forEach(line => {
          if (y > pageH - 40) { pdf.addPage(); y = margin; }
          pdf.text(line, margin, y); y += 13;
        });
        y += 14;
      }

      if (noted.length) {
        if (y > pageH - 60) { pdf.addPage(); y = margin; }
        pdf.setFontSize(10.5); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(40, 40, 40);
        pdf.text('Tenant Notes', margin, y); y += 15;
        noted.forEach(({ t, totalBalance }) => {
          const nlines = pdf.splitTextToSize(t.rentRollNote, pageW - margin * 2 - 12);
          if (y + 12 + nlines.length * 11 > pageH - 30) { pdf.addPage(); y = margin; }
          pdf.setFontSize(9); pdf.setFont('helvetica', 'bold');
          let hx = margin;
          // Lead with the owed amount (red) when the tenant still owes
          if (totalBalance > 0) {
            pdf.setTextColor(200, 30, 30);
            const amt = `${fmt(totalBalance)}  —  `;
            pdf.text(amt, hx, y);
            hx += pdf.getTextWidth(amt);
          }
          pdf.setTextColor(30, 30, 30);
          pdf.text(`Unit ${t.unit} — ${t.name}`, hx, y); y += 12;
          pdf.setFont('helvetica', 'normal'); pdf.setTextColor(75, 75, 75);
          nlines.forEach(line => { pdf.text(line, margin + 12, y); y += 11; });
          y += 7;
        });
      }
    }

    const stamp = `Generated ${new Date().toLocaleDateString('en-US', { timeZone: 'America/New_York' })}`;
    pdf.setFontSize(7); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(170, 170, 170);
    pdf.text(stamp, pageW - margin, pageH - 16, { align: 'right' });
    pdf.save(`${fileBase}.pdf`);
    setGenMode('');
    setExportModal(false);
  };
  const sortedRows = useMemo(() => {
    if (!sortCol) return filteredRows;
    return [...filteredRows].sort((a, b) => (sortDir === 'desc' ? b[sortCol] - a[sortCol] : a[sortCol] - b[sortCol]));
  }, [filteredRows, sortCol, sortDir]);
  // Click a column header's label to open a menu (Hide / Sort / Filter). Menu is
  // portaled so the table's horizontal scroll can't clip it.
  const openColMenu = (col, e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setColMenuPos({ top: r.bottom + 4, left: Math.min(r.left, window.innerWidth - 210) });
    setColMenu(prev => (prev === col ? null : col));
  };
  const ColHeader = ({ col, label, align = 'left', thStyle }) => {
    const active = sortCol === col;
    const hasFilter = !!colFilters[col];
    return (
      <th style={{ textAlign: align, whiteSpace: 'nowrap', ...thStyle }}>
        <span data-colhdr onClick={e => openColMenu(col, e)}
          style={{ cursor: 'pointer', userSelect: 'none', display: 'inline-flex', alignItems: 'center', gap: 3, color: (active || hasFilter || colMenu === col) ? 'var(--accent)' : undefined }}>
          {label}
          {active && <span style={{ fontSize: 11 }}>{sortDir === 'desc' ? '↓' : '↑'}</span>}
          {hasFilter && <span title="Filtered" style={{ fontSize: 16, lineHeight: 0 }}>•</span>}
          <svg width={9} height={9} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} style={{ opacity: 0.45 }}><polyline points="6 9 12 15 18 9"/></svg>
        </span>
      </th>
    );
  };

  return (
    <div>
      <div className="page-header" style={{ marginBottom: 16 }}>
        <div className="page-title">{ic(I.receipt, 15)} Rent Roll</div>
        {ready && rows.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ display: 'inline-flex', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: 2 }}>
              {[['simple', 'Simple'], ['detailed', 'Detailed']].map(([v, label]) => (
                <button key={v} onClick={() => setView(v)}
                  style={{ fontSize: 12, fontWeight: 600, padding: '5px 12px', borderRadius: 6, border: 'none', cursor: 'pointer',
                    background: view === v ? 'var(--accent)' : 'transparent', color: view === v ? '#fff' : 'var(--text2)' }}>
                  {label}
                </button>
              ))}
            </div>
            <button className="btn" onClick={() => setExportModal(true)}>{ic('M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4', 14)} Export</button>
          </div>
        )}
      </div>
      <div className="filter-bar" style={{ marginBottom: 20 }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div className="form-label" style={{ marginBottom: 4 }}>Property</div>
          <select className="form-input" value={propId} onChange={e => { setPropId(e.target.value); resetDataViews(); }}>
            <option value="">Select property…</option>
            {properties.filter(p => !(p.businessType || '').toLowerCase().includes('construction')).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div style={{ minWidth: 160 }}>
          <div className="form-label" style={{ marginBottom: 4 }}>Month &amp; Year</div>
          <input type="month" className="form-input" value={month} onChange={e => { setMonth(e.target.value); resetDataViews(); }} />
        </div>
      </div>

      {!ready && (
        <div className="empty">Select a property and month to generate the rent roll.</div>
      )}

      {ready && rows.length === 0 && (
        <div className="empty">No tenants found for this property.</div>
      )}

      {ready && rows.length > 0 && (hiddenCols.size > 0 || hiddenRows.size > 0 || Object.keys(colFilters).length > 0) && (
        <div style={{ marginBottom: 10, fontSize: 12, color: 'var(--text2)', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {hiddenCols.size > 0 && (
            <span>{hiddenCols.size} column{hiddenCols.size !== 1 ? 's' : ''} hidden · <button className="btn-ghost" style={{ fontSize: 12, color: 'var(--accent)', padding: 0 }} onClick={() => setHiddenCols(new Set())}>Show all columns</button></span>
          )}
          {hiddenRows.size > 0 && (
            <span>{hiddenRows.size} row{hiddenRows.size !== 1 ? 's' : ''} hidden · <button className="btn-ghost" style={{ fontSize: 12, color: 'var(--accent)', padding: 0 }} onClick={() => setHiddenRows(new Set())}>Show all rows</button></span>
          )}
          {Object.keys(colFilters).length > 0 && (
            <span>Filters active · <button className="btn-ghost" style={{ fontSize: 12, color: 'var(--accent)', padding: 0 }} onClick={() => setColFilters({})}>Clear filters</button></span>
          )}
        </div>
      )}
      {ready && rows.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table className="list-table">
            <thead>
              <tr>
                {showCol('unit') && <ColHeader col="unit" label="Unit" />}
                {showCol('name') && <ColHeader col="name" label="Tenant Name" />}
                {showCol('program') && <ColHeader col="program" label="Program" />}
                {showCol('paymentType') && <ColHeader col="paymentType" label="Payment Type" />}
                {showCol('status') && <ColHeader col="status" label="Status" />}
                {showCol('dueDay') && <ColHeader col="dueDay" label="Due Day" align="center" />}
                {detailed && showCol('programRent') && <ColHeader col="programRent" label="Program Rent" align="right" />}
                {detailed && showCol('tenantPortion') && <ColHeader col="tenantPortion" label="Tenant Portion" align="right" />}
                {detailed && showCol('parking') && <ColHeader col="parking" label="Parking" align="right" />}
                {detailed && showCol('washerDryer') && <ColHeader col="washerDryer" label="W/D" align="right" />}
                {showCol('monthlyRent') && <ColHeader col="monthlyRent" label="Monthly Rent" align="right" />}
                {showCol('paymentMade') && <ColHeader col="paymentMade" label="Payment Made" align="right" />}
                {showCol('pastOverdue') && <ColHeader col="pastOverdue" label="Past Overdue" align="right" />}
                {showCol('totalBalance') && <ColHeader col="totalBalance" label="Total Balance" align="right" />}
                {showCol('notes') && <ColHeader col="notes" label="Notes" thStyle={{ minWidth: 160 }} />}
                <th style={{ width: 32 }}></th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map(({ t, programRent, tenantPortion, parking, washerDryer, monthlyRent, paymentMade, pastOverdue, totalBalance }) => (
                <tr key={t.id}>
                  {showCol('unit') && <td style={{ fontWeight: 500 }}>{t.unit}</td>}
                  {showCol('name') && <td style={{ fontWeight: 500, cursor: onTenantClick ? 'pointer' : 'default', color: onTenantClick ? 'var(--accent)' : undefined }} onClick={() => onTenantClick?.(t.id)}>{t.name}</td>}
                  {showCol('program') && <td><span className="badge badge-blue">{t.program || 'Cash'}</span></td>}
                  {showCol('paymentType') && <td style={{ color: 'var(--text2)', fontSize: 12 }}>{t.paymentType || '—'}</td>}
                  {showCol('status') && <td><Badge label={t.status} /></td>}
                  {showCol('dueDay') && <td style={{ textAlign: 'center', color: 'var(--text2)', fontSize: 12 }}>{t.dueDay || 1}</td>}
                  {detailed && showCol('programRent') && <td style={{ textAlign: 'right', fontSize: 12, color: 'var(--blue-txt)' }}>{programRent > 0 ? fmt(programRent) : <span style={{ color: 'var(--text3)' }}>—</span>}</td>}
                  {detailed && showCol('tenantPortion') && <td style={{ textAlign: 'right', fontSize: 12, color: 'var(--amber-txt)' }}>{tenantPortion > 0 ? fmt(tenantPortion) : <span style={{ color: 'var(--text3)' }}>—</span>}</td>}
                  {detailed && showCol('parking') && <td style={{ textAlign: 'right', fontSize: 12 }}>{parking > 0 ? fmt(parking) : <span style={{ color: 'var(--text3)' }}>—</span>}</td>}
                  {detailed && showCol('washerDryer') && <td style={{ textAlign: 'right', fontSize: 12 }}>{washerDryer > 0 ? fmt(washerDryer) : <span style={{ color: 'var(--text3)' }}>—</span>}</td>}
                  {showCol('monthlyRent') && <td style={{ textAlign: 'right', fontWeight: 500 }}>{monthlyRent > 0 ? fmt(monthlyRent) : <span style={{ color: 'var(--text3)' }}>—</span>}</td>}
                  {showCol('paymentMade') && <td style={{ textAlign: 'right', fontWeight: 500, color: paymentMade > 0 ? 'var(--green-txt)' : 'var(--text3)' }}>{paymentMade > 0 ? fmt(paymentMade) : '—'}</td>}
                  {showCol('pastOverdue') && <td style={{ textAlign: 'right', fontWeight: 500, color: pastOverdue > 0 ? 'var(--amber-txt)' : 'var(--text3)' }}>{pastOverdue > 0 ? fmt(pastOverdue) : '—'}</td>}
                  {showCol('totalBalance') && <td style={{ textAlign: 'right', fontWeight: 700, color: totalBalance > 0 ? 'var(--red-txt)' : totalBalance < 0 ? 'var(--green-txt)' : 'var(--text2)' }}>{fmt(totalBalance)}</td>}
                  {showCol('notes') && <td style={{ minWidth: 160 }}>
                    {editingTenantNoteId === t.id ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <textarea className="form-input" rows={2} autoFocus style={{ fontSize: 12, resize: 'none' }}
                          value={tenantNoteDraft} onChange={e => setTenantNoteDraft(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveTenantNote(t.id); } if (e.key === 'Escape') setEditingTenantNoteId(null); }} />
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn btn-primary" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => saveTenantNote(t.id)}>Save</button>
                          <button className="btn-ghost" style={{ fontSize: 11 }} onClick={() => setEditingTenantNoteId(null)}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', minHeight: 28 }} onClick={() => openTenantNote(t)}>
                        {t.rentRollNote
                          ? <span style={{ fontSize: 12, color: 'var(--text1)', whiteSpace: 'pre-wrap' }}><Linkify text={t.rentRollNote} /></span>
                          : <span style={{ fontSize: 12, color: 'var(--text3)', fontStyle: 'italic' }}>Add note…</span>}
                        <span style={{ marginLeft: 'auto', flexShrink: 0, opacity: 0.45 }}>{ic('M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z', 12, 'var(--text2)')}</span>
                      </div>
                    )}
                  </td>}
                  <td style={{ textAlign: 'center' }}>
                    <button title="Hide row temporarily" onClick={() => setHiddenRows(prev => { const n = new Set(prev); n.add(t.id); return n; })}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', padding: 4, display: 'inline-flex' }}>
                      <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ background: 'var(--bg2)', borderTop: '2px solid var(--border)' }}>
                <td colSpan={Math.max(1, ['unit', 'name', 'program', 'paymentType', 'status', 'dueDay'].filter(showCol).length)} style={{ padding: '10px 12px', fontSize: 11, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Totals</td>
                {detailed && showCol('programRent') && <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, fontSize: 13, color: 'var(--blue-txt)' }}>{fmt(totals.programRent)}</td>}
                {detailed && showCol('tenantPortion') && <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, fontSize: 13, color: 'var(--amber-txt)' }}>{fmt(totals.tenantPortion)}</td>}
                {detailed && showCol('parking') && <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, fontSize: 13 }}>{fmt(totals.parking)}</td>}
                {detailed && showCol('washerDryer') && <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, fontSize: 13 }}>{fmt(totals.washerDryer)}</td>}
                {showCol('monthlyRent') && <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, fontSize: 13 }}>{fmt(totals.monthlyRent)}</td>}
                {showCol('paymentMade') && <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, fontSize: 13, color: 'var(--green-txt)' }}>{fmt(totals.paymentMade)}</td>}
                {showCol('pastOverdue') && <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, fontSize: 13, color: totals.pastOverdue > 0 ? 'var(--amber-txt)' : 'var(--text2)' }}>{fmt(totals.pastOverdue)}</td>}
                {showCol('totalBalance') && <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, fontSize: 13, color: totals.totalBalance > 0 ? 'var(--red-txt)' : 'var(--green-txt)' }}>{fmt(totals.totalBalance)}</td>}
                {showCol('notes') && <td></td>}
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {colMenu && createPortal(
        <div data-colmenu className="kebab-menu" style={{ position: 'fixed', top: colMenuPos.top, left: colMenuPos.left, minWidth: 190, maxWidth: 240, zIndex: 500, padding: 4 }}>
          <button className="kebab-item" onClick={() => { hideCol(colMenu); setColMenu(null); }}>Hide column</button>
          {SORTABLE_COLS.has(colMenu) && (<>
            <button className="kebab-item" onClick={() => { setSortCol(colMenu); setSortDir('desc'); setColMenu(null); }}>Sort high → low</button>
            <button className="kebab-item" onClick={() => { setSortCol(colMenu); setSortDir('asc'); setColMenu(null); }}>Sort low → high</button>
          </>)}
          {FILTER_ACCESSOR[colMenu] && (
            <div style={{ borderTop: '1px solid var(--border)', marginTop: 4, paddingTop: 4, maxHeight: 240, overflowY: 'auto' }}>
              <div style={{ fontSize: 10, color: 'var(--text3)', padding: '2px 10px 4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Filter</div>
              {colDistinct(colMenu).map(v => {
                const set = colFilters[colMenu];
                const checked = !set || set.has(v);
                return (
                  <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 10px', fontSize: 12, cursor: 'pointer' }}>
                    <input type="checkbox" checked={checked} onChange={() => toggleColFilter(colMenu, v)} style={{ accentColor: 'var(--accent)', flexShrink: 0 }} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v || '—'}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>,
        document.querySelector('.app') || document.body
      )}

      {exportModal && (
        <Modal title="Export Rent Roll" onClose={() => setExportModal(false)}>
          <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 20 }}>Choose a format to export <strong>{propLabel}</strong> — {month}.</div>
          <div style={{ display: 'flex', gap: 12, marginBottom: 10 }}>
            <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center', padding: '12px' }} onClick={exportPDF} disabled={!!genMode}>
              {genMode === 'pdf' ? 'Generating…' : <>{ic(I.file, 15, '#fff')} PDF</>}
            </button>
            <button className="btn" style={{ flex: 1, justifyContent: 'center', padding: '12px' }} onClick={exportExcel} disabled={!!genMode}>
              {ic('M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4', 15)} Excel
            </button>
          </div>
          <button className="btn" style={{ width: '100%', justifyContent: 'center', padding: '12px' }} onClick={exportSummary} disabled={!!genMode}>
            {genMode === 'summary' ? 'Generating summary…' : '✦ Summary only (AI)'}
          </button>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 10, textAlign: 'center' }}>PDF includes the full table plus an AI audit summary &amp; tenant notes. “Summary only” exports just the written AI summary.</div>
          <div className="modal-actions" style={{ marginTop: 16 }}><button className="btn" onClick={() => setExportModal(false)} disabled={!!genMode}>Cancel</button></div>
        </Modal>
      )}
    </div>
  );
}

// ── Logins ────────────────────────────────────────────────────────────────
function Logins({ logins, setLogins, properties, appSettings }) {
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [copiedId, setCopiedId] = useState(null);
  const copyToClipboard = (text, key) => { navigator.clipboard.writeText(text).then(() => { setCopiedId(key); setTimeout(() => setCopiedId(null), 1500); }); };
  const [expandedNotes, setExpandedNotes] = useState(new Set());
  const toggleNotes = id => setExpandedNotes(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  const [propFilter, _setPropFilter] = useState(SHARED_PROP_FILTER);
  const setPropFilter = v => { SHARED_PROP_FILTER = v; _setPropFilter(v); };
  const [exportModal, setExportModal] = useState(false);
  const [exportPwInput, setExportPwInput] = useState('');
  const [exportPwError, setExportPwError] = useState(false);
  const [showExportPw, setShowExportPw] = useState(false);
  const ff = k => v => setForm(p => ({ ...p, [k]: v }));
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => { const h = () => setIsMobile(window.innerWidth < 768); window.addEventListener('resize', h); return () => window.removeEventListener('resize', h); }, []);

  const openAdd = () => { setForm({ title: '', link: '', propertyId: '', username: '', password: '', notes: '' }); setModal('add'); };
  const openEdit = entry => { setForm({ ...entry }); setModal('edit'); };
  const save = () => {
    if (modal === 'edit') setLogins(prev => prev.map(l => l.id === form.id ? { ...form } : l));
    else setLogins(prev => [...(prev || []), { ...form, id: `login${Date.now()}` }]);
    setModal(null);
    toast(modal === 'edit' ? 'Login updated' : 'Login added');
  };
  const remove = id => { if (window.confirm('Remove this login?')) { setLogins(prev => prev.filter(l => l.id !== id)); toast('Login removed', 'info'); } };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return (logins || []).filter(e => {
      const propName = properties.find(p => p.id === e.propertyId)?.name || e.propertyLabel || '';
      if (propFilter !== 'all' && e.propertyId !== propFilter) return false;
      return !q ||
        (e.title || '').toLowerCase().includes(q) ||
        (e.username || '').toLowerCase().includes(q) ||
        propName.toLowerCase().includes(q) ||
        (e.notes || '').toLowerCase().includes(q);
    });
  }, [logins, search, propFilter, properties]);

  const doExport = () => {
    const vaultPassword = (appSettings || []).find(s => s.id === 'vault')?.vaultPassword || 'JGq2w3e4$$';
    if (exportPwInput !== vaultPassword) { setExportPwError(true); setExportPwInput(''); return; }
    const rows = (logins || []).map(e => ({
      Title: e.title || '',
      Link: e.link || '',
      'Property / Entity': e.propertyLabel || '',
      Username: e.username || '',
      Password: e.password || '',
      Notes: e.notes || '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Logins');
    XLSX.writeFile(wb, `Logins_${new Date().toISOString().slice(0, 10)}.xlsx`);
    setExportModal(false);
    setExportPwInput('');
    setExportPwError(false);
  };


  return (
    <div>
      <div className="page-header">
        <span className="page-title">Logins</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn" onClick={() => { setExportPwInput(''); setExportPwError(false); setShowExportPw(false); setExportModal(true); }}>{ic('M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4', 14)} Export Excel</button>
          <button className="btn btn-primary" onClick={openAdd}>{ic(I.plus, 14, '#fff')} Add login</button>
        </div>
      </div>

      <div className="filter-bar" style={{ marginBottom: 16 }}>
        <div style={{ flex: 1 }}><SB value={search} onChange={setSearch} placeholder="Search title, username, property…" /></div>
        <select className="form-input" style={{ width: 'auto', minWidth: 200 }} value={propFilter} onChange={e => setPropFilter(e.target.value)}>
          <option value="all">All properties</option>
          {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>

      {filtered.length === 0 ? (
        <div className="empty">{search ? 'No results found.' : 'No logins saved yet.'}</div>
      ) : isMobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map(entry => {
            const propLabel = properties.find(p => p.id === entry.propertyId)?.name || entry.propertyLabel || '';
            return (
              <div key={entry.id} style={{ background: 'var(--bg1)', border: '1px solid var(--border)', borderRadius: 12, padding: '12px 14px' }}>
                {/* Title row */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                  <div>
                    <div onClick={entry.notes ? () => toggleNotes(entry.id) : undefined} style={{ fontWeight: 600, fontSize: 14, color: 'var(--text1)', display: 'inline-flex', alignItems: 'center', gap: 6, cursor: entry.notes ? 'pointer' : 'default' }}>
                      {entry.title || '—'}
                      {entry.notes && (
                        <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round"
                          style={{ color: 'var(--text3)', flexShrink: 0, transform: expandedNotes.has(entry.id) ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.18s ease' }}>
                          <polyline points="9 18 15 12 9 6" />
                        </svg>
                      )}
                    </div>
                    {propLabel && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>For {propLabel}</div>}
                  </div>
                  <KebabMenu items={[
                    { label: 'Edit', onClick: () => openEdit(entry) },
                    { label: 'Remove', danger: true, onClick: () => remove(entry.id) },
                  ]} />
                </div>
                {/* Username */}
                {entry.username && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                    <span style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text3)', width: 68, flexShrink: 0 }}>Username</span>
                    <span style={{ fontSize: 13, color: 'var(--text1)', flex: 1 }}>{entry.username}</span>
                    <button onClick={() => copyToClipboard(entry.username, `${entry.id}-user`)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: copiedId === `${entry.id}-user` ? 'var(--green-txt)' : 'var(--text3)', padding: 0, flexShrink: 0, fontSize: 11, fontFamily: 'inherit' }}>
                      {copiedId === `${entry.id}-user` ? '✓' : ic('M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z', 15)}
                    </button>
                  </div>
                )}
                {/* Password */}
                {entry.password && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                    <span style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text3)', width: 68, flexShrink: 0 }}>Password</span>
                    <span style={{ fontFamily: 'monospace', fontSize: 13, flex: 1 }}>{entry.password}</span>
                    <button onClick={() => copyToClipboard(entry.password, `${entry.id}-pw`)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: copiedId === `${entry.id}-pw` ? 'var(--green-txt)' : 'var(--text3)', padding: 0, flexShrink: 0, fontSize: 11, fontFamily: 'inherit' }}>
                      {copiedId === `${entry.id}-pw` ? '✓' : ic('M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z', 15)}
                    </button>
                  </div>
                )}
                {/* Notes — slide down from the title chevron */}
                {entry.notes && (
                  <div style={{ maxHeight: expandedNotes.has(entry.id) ? 240 : 0, opacity: expandedNotes.has(entry.id) ? 1 : 0, overflow: 'hidden', transition: 'max-height 0.22s ease, opacity 0.22s ease' }}>
                    <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 6, lineHeight: 1.4 }}>{entry.notes}</div>
                  </div>
                )}
                {/* Link */}
                {entry.link && (
                  <a href={entry.link} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--accent)', marginTop: 2 }}>
                    {ic(I.link, 12)} Open link
                  </a>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <table className="list-table">
          <thead><tr><th>Title</th><th>Property</th><th>Username</th><th>Password</th><th>Link</th><th style={{ width: 40 }}></th></tr></thead>
          <tbody>
            {filtered.map(entry => (
              <tr key={entry.id}>
                <td style={{ verticalAlign: 'top' }}>
                  <div
                    onClick={entry.notes ? () => toggleNotes(entry.id) : undefined}
                    style={{ fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 6, cursor: entry.notes ? 'pointer' : 'default' }}
                    title={entry.notes ? 'Click to view description' : undefined}
                  >
                    {entry.title || '—'}
                    {entry.notes && (
                      <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round"
                        style={{ color: 'var(--text3)', flexShrink: 0, transform: expandedNotes.has(entry.id) ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.18s ease' }}>
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                    )}
                  </div>
                  {entry.notes && (
                    <div style={{ maxHeight: expandedNotes.has(entry.id) ? 240 : 0, opacity: expandedNotes.has(entry.id) ? 1 : 0, overflow: 'hidden', transition: 'max-height 0.22s ease, opacity 0.22s ease' }}>
                      <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 4, lineHeight: 1.5 }}>{entry.notes}</div>
                    </div>
                  )}
                </td>
                <td style={{ fontSize: 13, color: 'var(--text2)', verticalAlign: 'top' }}>{properties.find(p => p.id === entry.propertyId)?.name || entry.propertyLabel || '—'}</td>
                <td style={{ verticalAlign: 'top' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 13 }}>{entry.username || '—'}</span>
                    {entry.username && (
                      <button onClick={() => copyToClipboard(entry.username, `${entry.id}-user`)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: copiedId === `${entry.id}-user` ? 'var(--green-txt)' : 'var(--text3)', padding: 0, flexShrink: 0, fontSize: 11, fontFamily: 'inherit' }}>
                        {copiedId === `${entry.id}-user` ? '✓' : ic('M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z', 14)}
                      </button>
                    )}
                  </div>
                </td>
                <td style={{ verticalAlign: 'top' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontFamily: 'monospace', fontSize: 13 }}>
                      {entry.password || '—'}
                    </span>
                    {entry.password && (
                      <button onClick={() => copyToClipboard(entry.password, `${entry.id}-pw`)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: copiedId === `${entry.id}-pw` ? 'var(--green-txt)' : 'var(--text3)', padding: 0, flexShrink: 0, fontSize: 11, fontFamily: 'inherit' }}>
                        {copiedId === `${entry.id}-pw` ? '✓' : ic('M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z', 14)}
                      </button>
                    )}
                  </div>
                </td>
                <td style={{ verticalAlign: 'top' }}>
                  {entry.link
                    ? <a href={entry.link} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)', fontSize: 13, display: 'flex', alignItems: 'center', gap: 4 }} onClick={e => e.stopPropagation()}>{ic(I.link, 13)} Open</a>
                    : <span style={{ color: 'var(--text3)', fontSize: 13 }}>—</span>}
                </td>
                <td style={{ verticalAlign: 'top' }}>
                  <KebabMenu items={[
                    { label: 'Edit', onClick: () => openEdit(entry) },
                    { label: 'Remove', danger: true, onClick: () => remove(entry.id) },
                  ]} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {modal && (
        <Modal title={modal === 'edit' ? 'Edit login' : 'Add login'} onClose={() => setModal(null)}>
          <FI label="Title" value={form.title} onChange={ff('title')} placeholder="e.g. HPD Online Portal" />
          <FI label="Link (URL)" value={form.link} onChange={ff('link')} placeholder="https://…" />
          <FS label="Property (optional)" value={form.propertyId || ''} onChange={ff('propertyId')} options={[{ value: '', label: '— None —' }, ...properties.map(p => ({ value: p.id, label: p.name }))]} />
          <div className="form-grid form-grid-2">
            <FI label="Username" value={form.username} onChange={ff('username')} />
            <FI label="Password" value={form.password} onChange={ff('password')} type="password" />
          </div>
          <FTA label="Notes" value={form.notes} onChange={ff('notes')} />
          <div className="modal-actions">
            <button className="btn" onClick={() => setModal(null)}>Cancel</button>
            <SavingBtn onClick={save}>Save</SavingBtn>
          </div>
        </Modal>
      )}

      {exportModal && (
        <Modal title="Export Logins to Excel" onClose={() => setExportModal(false)}>
          <div style={{ textAlign: 'center', marginBottom: 20 }}>{ic(I.lock, 32, 'var(--accent)')}</div>
          <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16, textAlign: 'center' }}>
            Enter your vault password to export all login credentials.
          </p>
          <div className="form-group">
            <label className="form-label">Vault password</label>
            <div style={{ position: 'relative' }}>
              <input
                className="form-input"
                type={showExportPw ? 'text' : 'password'}
                placeholder="Enter vault password…"
                value={exportPwInput}
                onChange={e => { setExportPwInput(e.target.value); setExportPwError(false); }}
                onKeyDown={e => e.key === 'Enter' && doExport()}
                autoFocus
                style={{ paddingRight: 40, borderColor: exportPwError ? 'var(--red-txt)' : undefined }}
              />
              <button onClick={() => setShowExportPw(p => !p)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', padding: 0 }}>
                {ic(showExportPw ? I.eyeOff : I.eye, 16)}
              </button>
            </div>
            {exportPwError && <div style={{ fontSize: 12, color: 'var(--red-txt)', marginTop: 6 }}>Incorrect password.</div>}
          </div>
          <div className="modal-actions">
            <button className="btn" onClick={() => setExportModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={doExport}>Download Excel</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Admin ─────────────────────────────────────────────────────────────────
const ALL_MANAGED_TABS = [
  { id: 'properties', label: 'Properties' },
  { id: 'tenants',    label: 'Tenants' },
  { id: 'violations', label: 'Violations' },
  { id: 'permits',    label: 'Work Permits' },
  { id: 'tasks',      label: 'Tasks' },
  { id: 'calendar',   label: 'Calendar' },
  { id: 'vendors',    label: 'Vendors' },
  { id: 'financials', label: 'Financials' },
  { id: 'rentroll',   label: 'Rent Roll' },
  { id: 'logins',     label: 'Logins' },
];

function Admin({ users, setUsers, tasks, setTasks, logins, setLogins, vendors, setVendors, appSettings, setAppSettings, properties, violations, setViolations, user }) {
  const toast = useToast();
  const [dayReport, setDayReport] = useState(null);
  const [dayReportLoading, setDayReportLoading] = useState(false);

  // Asks the server to read today's audit log and have Claude summarise the task and
  // violation work this person did. Server-side on purpose: the log is the record of what
  // actually landed, including edits made from another browser.
  const runDayReport = async () => {
    setDayReportLoading(true);
    try {
      const fn = httpsCallable(functions, 'generateDayReport');
      const res = await fn({ name: user?.displayName || user?.email || '' });
      setDayReport(res.data);
      if (res.data?.entryCount === 0) toast('No task or violation changes recorded for you today', 'info');
    } catch (e) {
      toast('Could not build the report: ' + (e.message || 'unknown error'), 'error');
    }
    setDayReportLoading(false);
  };

  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const ff = k => v => setForm(p => ({ ...p, [k]: v }));
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => { const h = () => setIsMobile(window.innerWidth < 768); window.addEventListener('resize', h); return () => window.removeEventListener('resize', h); }, []);

  const toggleTab = id => setForm(p => ({
    ...p,
    allowedTabs: p.allowedTabs.includes(id)
      ? p.allowedTabs.filter(t => t !== id)
      : [...p.allowedTabs, id],
  }));

  const openAdd = () => { setForm({ name: '', emails: [''], phone: '', allowedTabs: [] }); setModal('add'); };
  const openEdit = u => {
    const emails = getUserEmails(u);
    setForm({ ...u, emails: emails.length ? emails : [''], phone: u.phone || '', allowedTabs: [...(u.allowedTabs || [])] });
    setModal('edit');
  };

  const save = () => {
    const cleanEmails = (form.emails || []).map(e => e.trim()).filter(Boolean);
    const saved = { ...form, emails: cleanEmails, email: cleanEmails[0] || '' };
    if (modal === 'edit') {
      const oldUser = (users || []).find(u => u.id === form.id);
      const oldName = oldUser?.name;
      const newName = form.name;
      setUsers(prev => prev.map(u => u.id === form.id ? saved : u));
      if (oldName && newName && oldName !== newName) {
        // Match on the user id, so a task whose cached name drifted (odd casing, a stray
        // space) is still caught. Names are rebuilt from the ids rather than patched in
        // place, so no stale spelling can survive a rename.
        setTasks(prev => (prev || []).map(t => {
          const ids = taskAssignedIds(t);
          const names = taskAssignedList(t);
          const assigned = ids.length
            ? ids.includes(form.id)
            : names.some(n => normName(n) === normName(oldName));
          if (!assigned) return t;
          const nextNames = ids.length
            ? ids.map(uid => uid === form.id ? newName : ((users || []).find(u => u.id === uid)?.name || '')).filter(Boolean)
            : names.map(n => normName(n) === normName(oldName) ? newName : n);
          return { ...t, assignedTo: nextNames };
        }));
      }
    } else {
      setUsers(prev => [...(prev || []), { ...saved, id: `u${Date.now()}` }]);
    }
    setModal(null);
    toast(modal === 'edit' ? 'User updated' : 'User added');
  };

  const remove = id => {
    const name = (users || []).find(u => u.id === id)?.name;
    if (window.confirm('Remove this user? Their name will also be removed from all task assignments.')) {
      setUsers(prev => prev.filter(u => u.id !== id));
      // Unassign by id so a task whose cached name drifted is still cleaned up.
      setTasks(prev => (prev || []).map(t => {
        const ids = taskAssignedIds(t);
        const names = taskAssignedList(t);
        const assigned = ids.length
          ? ids.includes(id)
          : (name && names.some(n => normName(n) === normName(name)));
        if (!assigned) return t;
        const nextIds = ids.filter(x => x !== id);
        const nextNames = ids.length
          ? nextIds.map(x => (users || []).find(u => u.id === x)?.name || '').filter(Boolean)
          : names.filter(n => normName(n) !== normName(name));
        // Never leave a task with nobody on it — fall back to the owner.
        if (!nextNames.length && !nextIds.length) return { ...t, ...ownerAssignee(users) };
        return { ...t, assignedTo: nextNames, assignedToIds: nextIds };
      }));
      toast('User removed', 'info');
    }
  };

  const [aiUsage, setAiUsage] = useState([]);
  const [aiUsageLoading, setAiUsageLoading] = useState(false);
  const [aiUsageLoaded, setAiUsageLoaded] = useState(false);
  const [aiMonthFilter, setAiMonthFilter] = useState('all');
  const aiUsageMonthOf = u => u.month || (u.timestamp || '').slice(0, 7) || '?';
  const loadAiUsage = async () => {
    setAiUsageLoading(true);
    try {
      const snap = await getDocs(query(collection(db, 'aiUsage'), orderBy('timestamp', 'desc'), limit(200)));
      setAiUsage(snap.docs.map(d => ({ ...d.data(), id: d.id })));
      setAiUsageLoaded(true);
    } catch (e) {
      console.error('loadAiUsage:', e);
      toast('Failed to load AI usage data', 'error');
    }
    setAiUsageLoading(false);
  };

  return (
    <div>
      <div className="page-header">
        <span className="page-title">Admin — User Access</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn" onClick={runDayReport} disabled={dayReportLoading}>
            {dayReportLoading ? '…' : '✨'} {dayReportLoading ? 'Building report…' : 'My day report'}
          </button>
          <button className="btn btn-primary" onClick={openAdd}>{ic(I.plus, 14, '#fff')} Add user</button>
        </div>
      </div>
      <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
        Add users by their Google account email and choose which tabs they can access.
      </p>

      {dayReport && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
            <div>
              <div className="card-title" style={{ margin: 0 }}>{ic(I.clipboard, 14)} My day — {fmtDate(dayReport.date)}</div>
              <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 3 }}>
                Built from {dayReport.entryCount} audit {dayReport.entryCount === 1 ? 'entry' : 'entries'} on tasks and violations
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              <button className="btn" style={{ fontSize: 12, padding: '4px 12px' }}
                onClick={() => { navigator.clipboard?.writeText(dayReport.report); toast('Report copied'); }}>Copy</button>
              <button className="btn-ghost" onClick={() => setDayReport(null)} style={{ padding: '2px 8px' }}>✕</button>
            </div>
          </div>
          <div style={{ fontSize: 13, color: 'var(--text1)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
            <Linkify text={dayReport.report} />
          </div>
        </div>
      )}

      {isMobile ? (
        users.length === 0
          ? <div className="empty">No users added yet</div>
          : <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {users.map(u => (
                <div key={u.id} style={{ background: 'var(--bg1)', border: '1px solid var(--border)', borderRadius: 12, padding: '14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 15, color: 'var(--text1)', marginBottom: 2 }}>{u.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 2 }}>{getUserEmails(u).join(', ') || '—'}</div>
                      {u.phone && <div style={{ fontSize: 12, color: 'var(--text2)' }}>{u.phone}</div>}
                    </div>
                    <KebabMenu items={[
                      { label: 'Edit', onClick: () => openEdit(u) },
                      { label: 'Remove', danger: true, onClick: () => remove(u.id) },
                    ]} />
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 8 }}>
                    {(u.allowedTabs || []).filter(t => ALL_MANAGED_TABS.some(x => x.id === t)).length === 0
                      ? <span style={{ fontSize: 12, color: 'var(--text3)' }}>No tab access</span>
                      : (u.allowedTabs || []).filter(t => ALL_MANAGED_TABS.some(x => x.id === t)).map(t => (
                          <span key={t} style={{ fontSize: 11, fontWeight: 500, background: 'var(--accent-bg)', color: 'var(--accent)', padding: '3px 10px', borderRadius: 20 }}>
                            {ALL_MANAGED_TABS.find(x => x.id === t)?.label || t}
                          </span>
                        ))}
                  </div>
                </div>
              ))}
            </div>
      ) : (
        <table className="list-table">
          <thead>
            <tr><th>Name</th><th>Email</th><th>Phone</th><th>Tab access</th><th style={{ width: 40 }}></th></tr>
          </thead>
          <tbody>
            {users.length === 0
              ? <tr><td colSpan={5}><div className="empty">No users added yet</div></td></tr>
              : users.map(u => (
                <tr key={u.id}>
                  <td style={{ fontWeight: 500, verticalAlign: 'top' }}>{u.name}</td>
                  <td style={{ color: 'var(--text2)', fontSize: 12, verticalAlign: 'top' }}>
                    {getUserEmails(u).length === 0
                      ? '—'
                      : <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                          {getUserEmails(u).map((e, i) => <span key={i}>{e}</span>)}
                        </div>}
                  </td>
                  <td style={{ color: 'var(--text2)', fontSize: 12, verticalAlign: 'top', whiteSpace: 'nowrap' }}>{u.phone || '—'}</td>
                  <td style={{ verticalAlign: 'top' }}>
                    {(u.allowedTabs || []).filter(t => ALL_MANAGED_TABS.some(x => x.id === t)).length === 0
                      ? <span style={{ fontSize: 12, color: 'var(--text3)' }}>No access</span>
                      : <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, max-content)', gap: 4, justifyItems: 'start' }}>
                          {(u.allowedTabs || []).filter(t => ALL_MANAGED_TABS.some(x => x.id === t)).map(t => (
                            <span key={t} style={{ fontSize: 11, fontWeight: 500, background: 'var(--accent-bg)', color: 'var(--accent)', padding: '2px 8px', borderRadius: 20 }}>
                              {ALL_MANAGED_TABS.find(x => x.id === t)?.label || t}
                            </span>
                          ))}
                        </div>}
                  </td>
                  <td>
                    <KebabMenu items={[
                      { label: 'Edit', onClick: () => openEdit(u) },
                      { label: 'Remove', danger: true, onClick: () => remove(u.id) },
                    ]} />
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      )}



      {/* Stale assignee cleanup */}
      {(() => {
        const currentNorm = new Set((users || []).map(u => normName(u.name)).filter(Boolean));
        // A task carrying user ids is correctly assigned whatever its cached names say,
        // so only name-only assignments can go stale.
        const stale = [...new Set((tasks || []).flatMap(t =>
          taskAssignedIds(t).length
            ? []
            : taskAssignedList(t).filter(n => n && !currentNorm.has(normName(n)))
        ))].sort();
        if (!stale.length) return null;
        return (
          <div style={{ marginTop: 16, padding: '12px 16px', background: 'var(--amber-bg)', border: '1px solid var(--amber-border)', borderRadius: 10 }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--amber-txt)', marginBottom: 6 }}>
              {ic('M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z', 13, 'var(--amber-txt)')} Stale assignees found in tasks
            </div>
            <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 10 }}>
              The following names appear in task assignments but are no longer active users. Click "Remove" to take them off all tasks (a task left with nobody goes to the owner).
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {stale.map(name => (
                <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: '4px 10px' }}>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>{name}</span>
                  <button
                    onClick={() => {
                      if (window.confirm(`Remove "${name}" from all task assignments?`)) {
                        setTasks(prev => (prev || []).map(t => {
                          const names = taskAssignedList(t);
                          if (!names.some(n => normName(n) === normName(name))) return t;
                          const nextNames = names.filter(n => normName(n) !== normName(name));
                          // Never leave a task with nobody on it — fall back to the owner.
                          if (!nextNames.length) return { ...t, ...ownerAssignee(users) };
                          return { ...t, assignedTo: nextNames };
                        }));
                        toast(`"${name}" removed from all tasks`, 'info');
                      }
                    }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', color: 'var(--red)', fontSize: 11, fontWeight: 600, borderRadius: 4 }}
                  >Remove</button>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {modal && (
        <Modal title={modal === 'edit' ? 'Edit user' : 'Add user'} onClose={() => setModal(null)}>
          <FI label="Name" value={form.name} onChange={ff('name')} placeholder="e.g. Maria Santos" />
          <div className="form-group" style={{ alignItems: 'flex-start' }}>
            <label className="form-label">Google account email(s)</label>
            <div style={{ flex: 1, minWidth: 0 }}>
              {(form.emails || ['']).map((email, i) => (
                <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                  <input
                    value={email}
                    onChange={e => setForm(p => { const arr = [...(p.emails || [])]; arr[i] = e.target.value; return { ...p, emails: arr }; })}
                    placeholder="user@example.com"
                    style={{ flex: 1, minWidth: 0, padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text1)', fontSize: 13 }}
                  />
                  {(form.emails || []).length > 1 && (
                    <button onClick={() => setForm(p => ({ ...p, emails: p.emails.filter((_, j) => j !== i) }))} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 8, padding: '0 10px', cursor: 'pointer', color: 'var(--red)', fontSize: 16, flexShrink: 0 }}>×</button>
                  )}
                </div>
              ))}
              <button onClick={() => setForm(p => ({ ...p, emails: [...(p.emails || []), ''] }))} style={{ fontSize: 12, color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0', fontWeight: 500 }}>+ Add another email</button>
            </div>
          </div>
          <FI label="Contact number" value={form.phone} onChange={ff('phone')} placeholder="e.g. (347) 555-1234" />
          <div className="form-group">
            <label className="form-label">Tab access</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 4 }}>
              {ALL_MANAGED_TABS.map(t => {
                const checked = (form.allowedTabs || []).includes(t.id);
                return (
                  <label key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: 'var(--bg2)', borderRadius: 8, cursor: 'pointer', border: `1px solid ${checked ? 'var(--accent)' : 'var(--border)'}`, transition: 'border-color 0.12s' }}>
                    <input type="checkbox" checked={checked} onChange={() => toggleTab(t.id)} style={{ accentColor: 'var(--accent)', width: 14, height: 14 }} />
                    <span style={{ fontSize: 13 }}>{t.label}</span>
                  </label>
                );
              })}
            </div>
          </div>
          <div className="modal-actions">
            <button className="btn" onClick={() => setModal(null)}>Cancel</button>
            <SavingBtn onClick={save}>Save</SavingBtn>
          </div>
        </Modal>
      )}

      {/* ── AI Usage Tracker ── */}
      <div style={{ marginTop: 32 }}>
        <div className="page-header" style={{ marginBottom: 8 }}>
          <span style={{ fontWeight: 700, fontSize: 15 }}>AI Usage</span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {aiUsageLoaded && aiUsage.length > 0 && (() => {
              const months = [...new Set(aiUsage.map(aiUsageMonthOf))].sort((a, b) => b.localeCompare(a));
              return (
                <select className="form-input" style={{ width: 'auto', fontSize: 12, padding: '5px 8px' }} value={aiMonthFilter} onChange={e => setAiMonthFilter(e.target.value)}>
                  <option value="all">All months</option>
                  {months.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              );
            })()}
            <button className="btn" onClick={loadAiUsage} disabled={aiUsageLoading}>
              {aiUsageLoading ? 'Loading…' : aiUsageLoaded ? 'Refresh' : 'Load'}
            </button>
          </div>
        </div>
        <p style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 14 }}>
          Tracks every AI call: note improvements (Gemini), in-app chat (Claude Sonnet), and notice generation.
        </p>
        {aiUsageLoaded && (
          <>
            {(() => {
              const byMonth = {};
              aiUsage.forEach(u => {
                const m = u.month || (u.timestamp || '').slice(0, 7) || '?';
                if (!byMonth[m]) byMonth[m] = { calls: 0, tokens: 0, cost: 0 };
                byMonth[m].calls++;
                byMonth[m].tokens += u.totalTokens || 0;
                byMonth[m].cost += u.estimatedCostUSD || 0;
              });
              const months = Object.entries(byMonth).sort(([a], [b]) => b.localeCompare(a));
              if (!months.length) return <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 14 }}>No AI usage recorded yet.</div>;
              return (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 10, marginBottom: 18 }}>
                  {months.map(([month, stats]) => {
                    const active = aiMonthFilter === month;
                    return (
                      <div key={month} onClick={() => setAiMonthFilter(active ? 'all' : month)} title={active ? 'Click to show all months' : 'Click to filter to this month'}
                        style={{ background: active ? 'var(--accent-bg)' : 'var(--bg1)', border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`, borderRadius: 10, padding: '12px 14px', cursor: 'pointer' }}>
                        <div style={{ fontSize: 11, color: active ? 'var(--accent)' : 'var(--text3)', fontWeight: 600, marginBottom: 5 }}>{month}</div>
                        <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--text1)' }}>{stats.calls} calls</div>
                        <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 3 }}>{(stats.tokens / 1000).toFixed(0)}K tokens · ${stats.cost.toFixed(4)}</div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
            <table className="list-table">
              <thead>
                <tr>
                  <th>Time</th><th>Feature</th><th>Model</th><th>Triggered by</th>
                  <th style={{ textAlign: 'right' }}>In</th><th style={{ textAlign: 'right' }}>Out</th><th style={{ textAlign: 'right' }}>Cost</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const filtered = aiMonthFilter === 'all' ? aiUsage : aiUsage.filter(u => aiUsageMonthOf(u) === aiMonthFilter);
                  if (filtered.length === 0) return <tr><td colSpan={7}><div className="empty">{aiUsage.length === 0 ? 'No AI usage recorded yet' : `No AI usage for ${aiMonthFilter}`}</div></td></tr>;
                  return filtered.slice(0, 100).map(u => (
                      <tr key={u.id}>
                        <td style={{ fontSize: 11, color: 'var(--text2)', whiteSpace: 'nowrap' }}>
                          {u.timestamp ? new Date(u.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'}
                        </td>
                        <td style={{ fontSize: 12 }}>{u.feature || '—'}</td>
                        <td style={{ fontSize: 11, color: 'var(--text2)' }}>{(u.model || '—').replace('claude-', '').replace('-20251001', '')}</td>
                        <td style={{ fontSize: 11, color: 'var(--text2)', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {u.triggeredBy || '—'}
                        </td>
                        <td style={{ textAlign: 'right', fontSize: 11, color: 'var(--text2)' }}>{((u.inputTokens || 0) / 1000).toFixed(1)}K</td>
                        <td style={{ textAlign: 'right', fontSize: 11, color: 'var(--text2)' }}>{((u.outputTokens || 0) / 1000).toFixed(1)}K</td>
                        <td style={{ textAlign: 'right', fontSize: 12, fontWeight: 500 }}>
                          {(u.estimatedCostUSD || 0) > 0 ? `$${u.estimatedCostUSD.toFixed(4)}` : <span style={{ color: 'var(--text3)' }}>free</span>}
                        </td>
                      </tr>
                  ));
                })()}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}

// ── Tenant Portal ──────────────────────────────────────────────────────────
const ZELLE_HANDLE = 'office@amarmgmt.com';

function TenantPortal({ data }) {
  const { tenant, property, ledger: rawLedger, maintenance: rawMaint } = data;
  const ledger = useMemo(() => [...rawLedger].sort((a, b) => (b.date || '').localeCompare(a.date || '')), [rawLedger]);
  const maintenance = useMemo(() => [...rawMaint].sort((a, b) => (b.date || b.createdAt || '').localeCompare(a.date || a.createdAt || '')), [rawMaint]);

  const tenantCharges = useMemo(() => ledger.filter(l => l.type === 'charge' && l.payer !== 'Program'), [ledger]);
  const totalCharged  = useMemo(() => tenantCharges.reduce((s, l) => s + (+l.amount || 0), 0), [tenantCharges]);
  const totalPaid     = useMemo(() => tenantCharges.reduce((s, l) => s + (+l.paidAmount || 0), 0), [tenantCharges]);
  const balance       = totalCharged - totalPaid;

  const [paymentModal, setPaymentModal] = useState(false);
  const [payForm, setPayForm] = useState({ amount: '', zelleRef: '', note: '' });
  const [payLoading, setPayLoading] = useState(false);
  const [paySuccess, setPaySuccess] = useState(false);
  const [payError, setPayError] = useState('');
  const [copied, setCopied] = useState(null);

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 1800);
  };

  const openPayModal = () => {
    setPayForm({ amount: balance > 0 ? String(balance) : '', zelleRef: '', note: '' });
    setPayError('');
    setPaymentModal(true);
  };

  const submitPayment = async () => {
    if (!payForm.amount || +payForm.amount <= 0) { setPayError('Enter a valid amount'); return; }
    setPayLoading(true); setPayError('');
    try {
      const fn = httpsCallable(functions, 'submitPaymentNotification');
      await fn({ tenantId: tenant.id, amount: +payForm.amount, zelleRef: payForm.zelleRef, note: payForm.note, memo: `${tenant.name} - Unit ${tenant.unit}` });
      setPaySuccess(true);
      setPaymentModal(false);
    } catch (e) { setPayError(e.message || 'Submission failed. Try again.'); }
    setPayLoading(false);
  };

  const todayISO = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
  const today = new Date(todayISO + 'T12:00:00');

  const daysLeft = tenant.leaseEnd
    ? Math.ceil((new Date(tenant.leaseEnd + 'T12:00:00') - today) / 86400000)
    : null;

  const leaseProgress = (() => {
    if (!tenant.leaseStart || !tenant.leaseEnd) return null;
    const start = new Date(tenant.leaseStart + 'T12:00:00');
    const end   = new Date(tenant.leaseEnd + 'T12:00:00');
    return Math.min(100, Math.max(0, Math.round(((today - start) / (end - start)) * 100)));
  })();

  const nextDueDate = (() => {
    if (!tenant.dueDay) return null;
    const now = new Date(); const day = tenant.dueDay;
    let month = now.getMonth(); let year = now.getFullYear();
    if (now.getDate() > day) { month += 1; if (month > 11) { month = 0; year += 1; } }
    return new Date(year, month, day).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  })();

  const initials = (tenant.name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const leaseActive = !tenant.leaseEnd || daysLeft === null || daysLeft > 0;

  // Design tokens
  const C = {
    bg0: '#070B14', bg1: '#0B0F19', bg2: '#111827', bg3: '#1A2233', bg4: '#1F2B3E',
    border: 'rgba(255,255,255,0.06)', border2: 'rgba(255,255,255,0.10)',
    text1: '#F9FAFB', text2: '#9CA3AF', text3: '#4B5563',
    accent: '#6366F1', accentLight: '#818CF8',
    accentGrad: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
    green: '#10B981', greenBg: 'rgba(16,185,129,0.09)', greenBorder: 'rgba(16,185,129,0.2)',
    red: '#EF4444',   redBg:   'rgba(239,68,68,0.09)',  redBorder:   'rgba(239,68,68,0.2)',
    amber: '#F59E0B', amberBg: 'rgba(245,158,11,0.09)', amberBorder: 'rgba(245,158,11,0.2)',
    cardShadow: '0 1px 2px rgba(0,0,0,0.5), 0 8px 32px rgba(0,0,0,0.45)',
  };

  const card = (extra = {}) => ({
    background: C.bg2, border: `1px solid ${C.border}`, borderRadius: 20,
    padding: '24px', boxShadow: C.cardShadow, marginBottom: 16, ...extra,
  });

  const ordinal = n => `${n}${['st','nd','rd'][((n % 10)-1)] || 'th'}`;

  return (
    <div style={{ height: '100vh', overflowY: 'auto', background: C.bg1, fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', color: C.text1 }}>

      {/* ── Sticky Header ───────────────────────────────────────── */}
      <header style={{
        position: 'sticky', top: 0, zIndex: 50,
        background: 'rgba(11,15,25,0.80)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
        borderBottom: `1px solid ${C.border}`, padding: '0 24px', height: 60,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 30, height: 30, borderRadius: 9, background: C.accentGrad, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 10px rgba(99,102,241,0.4)' }}>
            {ic(I.building, 14, '#fff')}
          </div>
          <span style={{ fontSize: 14, fontWeight: 700, color: C.text1, letterSpacing: '-0.2px' }}>Amar Management</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: '50%', background: C.accentGrad, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, color: '#fff', boxShadow: '0 2px 10px rgba(99,102,241,0.35)' }}>
            {initials}
          </div>
          <button onClick={() => signOut(auth)}
            style={{ background: 'none', border: `1px solid ${C.border2}`, borderRadius: 9, padding: '6px 14px', fontSize: 12, color: C.text2, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500, transition: 'all 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.background = C.bg3; e.currentTarget.style.color = C.text1; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = C.text2; }}>
            Sign out
          </button>
        </div>
      </header>

      {/* ── Main Content ─────────────────────────────────────────── */}
      <main style={{ maxWidth: 720, margin: '0 auto', padding: '36px 16px 80px' }}>

        {/* Welcome Hero */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 32 }}>
          <div style={{ width: 56, height: 56, borderRadius: 18, background: C.accentGrad, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 800, color: '#fff', flexShrink: 0, boxShadow: '0 4px 20px rgba(99,102,241,0.45)' }}>
            {initials}
          </div>
          <div>
            <div style={{ fontSize: 24, fontWeight: 800, color: C.text1, letterSpacing: '-0.5px', lineHeight: 1.2 }}>
              {greeting}, {tenant.name?.split(' ')[0] || 'Tenant'}
            </div>
            <div style={{ fontSize: 13, color: C.text2, marginTop: 4 }}>
              {property?.address || property?.name || 'Amar Management'} &middot; Unit {tenant.unit}
            </div>
          </div>
        </div>

        {/* Quick Stats Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 20 }}>
          {/* Balance */}
          <div style={{ background: balance > 0 ? C.redBg : C.greenBg, border: `1px solid ${balance > 0 ? C.redBorder : C.greenBorder}`, borderRadius: 18, padding: '18px 20px', boxShadow: C.cardShadow }}>
            <div style={{ fontSize: 10, color: balance > 0 ? C.red : C.green, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, marginBottom: 8 }}>Balance Due</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: balance > 0 ? C.red : C.green, letterSpacing: '-0.5px' }}>${Math.abs(balance).toLocaleString()}</div>
            <div style={{ fontSize: 11, color: C.text3, marginTop: 5 }}>{balance > 0 ? 'Payment required' : 'Account current'}</div>
          </div>
          {/* Next Due */}
          <div style={{ background: C.bg2, border: `1px solid ${C.border}`, borderRadius: 18, padding: '18px 20px', boxShadow: C.cardShadow }}>
            <div style={{ fontSize: 10, color: C.text2, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, marginBottom: 8 }}>Next Due Date</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: C.text1 }}>{nextDueDate || '—'}</div>
            <div style={{ fontSize: 11, color: C.text3, marginTop: 5 }}>{tenant.tenantShare ? `$${(tenant.tenantShare || 0).toLocaleString()} monthly` : 'Monthly rent'}</div>
          </div>
          {/* Lease Status */}
          <div style={{ background: C.bg2, border: `1px solid ${C.border}`, borderRadius: 18, padding: '18px 20px', boxShadow: C.cardShadow }}>
            <div style={{ fontSize: 10, color: C.text2, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, marginBottom: 8 }}>Lease Status</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: leaseActive ? C.green : C.red, boxShadow: `0 0 8px ${leaseActive ? C.green : C.red}` }} />
              <span style={{ fontSize: 18, fontWeight: 700, color: C.text1 }}>{leaseActive ? 'Active' : 'Expired'}</span>
            </div>
            <div style={{ fontSize: 11, color: C.text3 }}>
              {daysLeft !== null && daysLeft > 0 ? `${daysLeft} days remaining` : daysLeft === 0 ? 'Expires today' : tenant.leaseEnd ? 'Lease expired' : '—'}
            </div>
          </div>
          {/* Payment Status */}
          <div style={{ background: C.bg2, border: `1px solid ${C.border}`, borderRadius: 18, padding: '18px 20px', boxShadow: C.cardShadow }}>
            <div style={{ fontSize: 10, color: C.text2, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, marginBottom: 8 }}>Payment Status</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: balance > 0 ? C.red : C.green, marginBottom: 5 }}>{balance > 0 ? 'Outstanding' : 'Current'}</div>
            <div style={{ fontSize: 11, color: C.text3 }}>${totalPaid.toLocaleString()} of ${totalCharged.toLocaleString()} paid</div>
          </div>
        </div>

        {/* ── Pay via Zelle ─────────────────────────────────────── */}
        {balance > 0 && (
          <div style={{ ...card(), background: 'linear-gradient(145deg, #111827 0%, #1A2233 100%)', border: `1px solid rgba(99,102,241,0.18)`, position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: -60, right: -60, width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />
            <div style={{ position: 'relative' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 12, background: C.accentGrad, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 4px 14px rgba(99,102,241,0.4)' }}>
                    {ic(I.dollar, 18, '#fff')}
                  </div>
                  <div>
                    <div style={{ fontSize: 17, fontWeight: 700, color: C.text1, marginBottom: 3 }}>Pay via Zelle</div>
                    <div style={{ fontSize: 12, color: C.text2 }}>Send payment and notify us to confirm receipt</div>
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 10, color: C.text3, marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.07em' }}>Amount Due</div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: C.text1, letterSpacing: '-0.5px' }}>${balance.toLocaleString()}</div>
                </div>
              </div>

              <div style={{ background: C.bg1, border: `1px solid ${C.border}`, borderRadius: 14, marginBottom: 16, overflow: 'hidden' }}>
                {[
                  { label: 'Zelle Handle', value: ZELLE_HANDLE, key: 'handle', copyVal: ZELLE_HANDLE },
                  { label: 'Amount', value: `$${balance.toLocaleString()}`, key: 'amount', copyVal: String(balance) },
                  { label: 'Required Memo', value: `${tenant.name} – Unit ${tenant.unit}`, key: 'memo', copyVal: `${tenant.name} - Unit ${tenant.unit}` },
                ].map((row, i, arr) => (
                  <div key={row.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: i < arr.length - 1 ? `1px solid ${C.border}` : 'none', gap: 12 }}>
                    <div>
                      <div style={{ fontSize: 10, color: C.text3, textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 600, marginBottom: 4 }}>{row.label}</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: C.text1 }}>{row.value}</div>
                    </div>
                    <button onClick={() => copy(row.copyVal, row.key)}
                      style={{ display: 'flex', alignItems: 'center', gap: 5, background: copied === row.key ? C.greenBg : C.bg3, border: `1px solid ${copied === row.key ? C.greenBorder : C.border2}`, borderRadius: 9, padding: '6px 12px', fontSize: 11, fontWeight: 600, cursor: 'pointer', color: copied === row.key ? C.green : C.text2, fontFamily: 'inherit', whiteSpace: 'nowrap', flexShrink: 0, transition: 'all 0.15s' }}>
                      {copied === row.key ? ic('M9 11l3 3L22 4', 11, C.green) : ic(I.clipboard, 11)}
                      {copied === row.key ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                ))}
              </div>

              {paySuccess ? (
                <div style={{ padding: '14px 18px', background: C.greenBg, border: `1px solid ${C.greenBorder}`, borderRadius: 12, fontSize: 13, color: C.green, display: 'flex', alignItems: 'center', gap: 8, fontWeight: 500 }}>
                  {ic('M9 11l3 3L22 4M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11', 15, C.green)}
                  Notification sent. Your landlord will confirm when received.
                </div>
              ) : (
                <button onClick={openPayModal}
                  style={{ width: '100%', padding: '14px', borderRadius: 13, border: 'none', background: C.accentGrad, color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 22px rgba(99,102,241,0.45)', transition: 'all 0.15s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, letterSpacing: '-0.1px' }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 8px 30px rgba(99,102,241,0.55)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 22px rgba(99,102,241,0.45)'; }}>
                  {ic(I.dollar, 16, '#fff')} I've Sent Payment →
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── Lease Information ─────────────────────────────────── */}
        <div style={card()}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <div style={{ width: 36, height: 36, borderRadius: 11, background: `rgba(99,102,241,0.12)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {ic(I.clipboard, 16, C.accent)}
            </div>
            <span style={{ fontSize: 16, fontWeight: 700, color: C.text1, letterSpacing: '-0.2px' }}>Lease Information</span>
          </div>

          {leaseProgress !== null && (
            <div style={{ marginBottom: 22, background: C.bg3, borderRadius: 14, padding: '16px 18px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                <span style={{ fontSize: 12, color: C.text2, fontWeight: 500 }}>Lease Progress</span>
                <span style={{ fontSize: 12, color: C.text1, fontWeight: 700 }}>{leaseProgress}%</span>
              </div>
              <div style={{ height: 6, background: C.bg4, borderRadius: 99, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${leaseProgress}%`, background: leaseProgress > 85 ? `linear-gradient(90deg, ${C.amber}, ${C.red})` : C.accentGrad, borderRadius: 99, transition: 'width 0.6s ease' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
                <span style={{ fontSize: 10, color: C.text3 }}>{fmtDate(tenant.leaseStart)}</span>
                <span style={{ fontSize: 10, color: C.text3 }}>{fmtDate(tenant.leaseEnd)}</span>
              </div>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {[
              { label: 'Property', value: property?.name || '—' },
              { label: 'Unit', value: tenant.unit || '—' },
              { label: 'Lease Start', value: fmtDate(tenant.leaseStart) || '—' },
              { label: 'Lease End', value: fmtDate(tenant.leaseEnd) || '—', color: daysLeft !== null && daysLeft <= 0 ? C.red : daysLeft !== null && daysLeft <= 60 ? C.amber : undefined, sub: daysLeft !== null && daysLeft > 0 && daysLeft <= 90 ? `${daysLeft} days left` : daysLeft !== null && daysLeft <= 0 ? 'Expired' : undefined },
              ...(tenant.tenantShare > 0 ? [
                { label: 'Monthly Rent', value: `$${(tenant.tenantShare || 0).toLocaleString()}` },
                { label: 'Due Day', value: tenant.dueDay ? `${ordinal(tenant.dueDay)} of month` : '—' },
              ] : []),
            ].map(item => (
              <div key={item.label} style={{ background: C.bg3, borderRadius: 13, padding: '14px 16px' }}>
                <div style={{ fontSize: 10, color: C.text3, textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 600, marginBottom: 5 }}>{item.label}</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: item.color || C.text1 }}>{item.value}</div>
                {item.sub && <div style={{ fontSize: 10, color: item.color || C.text3, marginTop: 3, fontWeight: 500 }}>{item.sub}</div>}
              </div>
            ))}
          </div>
        </div>

        {/* ── Payment History ───────────────────────────────────── */}
        <div style={card()}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <div style={{ width: 36, height: 36, borderRadius: 11, background: `rgba(16,185,129,0.12)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {ic(I.receipt, 16, C.green)}
            </div>
            <span style={{ fontSize: 16, fontWeight: 700, color: C.text1, letterSpacing: '-0.2px' }}>Payment History</span>
            <span style={{ marginLeft: 'auto', fontSize: 11, color: C.text3, fontWeight: 500 }}>{ledger.length} entries</span>
          </div>

          {ledger.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '36px 20px' }}>
              <div style={{ width: 48, height: 48, borderRadius: 14, background: C.bg3, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                {ic(I.receipt, 22, C.text3)}
              </div>
              <div style={{ fontSize: 13, color: C.text3 }}>No ledger entries on file</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {ledger.slice(0, 15).map(l => {
                const isCharge = l.type === 'charge';
                const isProg   = l.payer === 'Program';
                const isPaid   = l.status === 'Paid';
                return (
                  <div key={l.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 16px', background: C.bg3, borderRadius: 13, border: `1px solid ${C.border}`, gap: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                      <div style={{ width: 38, height: 38, borderRadius: 11, flexShrink: 0, background: isCharge ? (isPaid ? `rgba(16,185,129,0.12)` : `rgba(239,68,68,0.10)`) : `rgba(16,185,129,0.12)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {ic(isCharge ? I.dollar : 'M12 19V5M5 12l7-7 7 7', 15, isCharge ? (isPaid ? C.green : C.red) : C.green)}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: C.text1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {l.description || (isCharge ? 'Charge' : 'Payment')}
                          {isProg && <span style={{ fontSize: 10, color: C.text3, marginLeft: 6, fontWeight: 400 }}>Program</span>}
                        </div>
                        <div style={{ fontSize: 11, color: C.text3, marginTop: 2 }}>{fmtDate(l.date)}</div>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: isCharge ? C.text1 : C.green }}>{isCharge ? `$${(+l.amount || 0).toLocaleString()}` : `+$${(+l.amount || 0).toLocaleString()}`}</div>
                      {isCharge && (
                        <div style={{ display: 'inline-flex', fontSize: 10, fontWeight: 700, padding: '2px 9px', borderRadius: 20, marginTop: 4, background: isPaid ? C.greenBg : C.redBg, color: isPaid ? C.green : C.red, border: `1px solid ${isPaid ? C.greenBorder : C.redBorder}`, letterSpacing: '0.03em' }}>
                          {l.status || 'Unpaid'}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {ledger.length > 15 && <div style={{ textAlign: 'center', padding: '10px 0 0', fontSize: 12, color: C.text3 }}>Showing 15 of {ledger.length} entries</div>}
            </div>
          )}
        </div>

        {/* ── Maintenance ───────────────────────────────────────── */}
        {maintenance.length > 0 && (
          <div style={card()}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
              <div style={{ width: 36, height: 36, borderRadius: 11, background: `rgba(245,158,11,0.12)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {ic(I.wrench, 16, C.amber)}
              </div>
              <span style={{ fontSize: 16, fontWeight: 700, color: C.text1, letterSpacing: '-0.2px' }}>Maintenance</span>
              <span style={{ marginLeft: 'auto', fontSize: 11, color: C.text3, fontWeight: 500 }}>{maintenance.length} total</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {maintenance.slice(0, 6).map(m => {
                const sc = m.status === 'Completed' ? { c: C.green, bg: C.greenBg, border: C.greenBorder } : m.status === 'In Progress' ? { c: C.accentLight, bg: 'rgba(99,102,241,0.09)', border: 'rgba(99,102,241,0.2)' } : { c: C.amber, bg: C.amberBg, border: C.amberBorder };
                return (
                  <div key={m.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 16px', background: C.bg3, borderRadius: 13, border: `1px solid ${C.border}`, gap: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                      <div style={{ width: 38, height: 38, borderRadius: 11, background: `rgba(245,158,11,0.12)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        {ic(I.wrench, 15, C.amber)}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: C.text1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.issue || m.description || '—'}</div>
                        <div style={{ fontSize: 11, color: C.text3, marginTop: 2 }}>{fmtDate(m.date || m.createdAt)}</div>
                      </div>
                    </div>
                    <div style={{ display: 'inline-flex', fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 20, flexShrink: 0, background: sc.bg, color: sc.c, border: `1px solid ${sc.border}`, letterSpacing: '0.03em' }}>
                      {m.status || 'Open'}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Contact ───────────────────────────────────────────── */}
        <div style={{ ...card(), background: 'linear-gradient(145deg, #111827 0%, #1A2233 100%)', border: `1px solid ${C.border}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <div style={{ width: 36, height: 36, borderRadius: 11, background: `rgba(99,102,241,0.12)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {ic(I.mail, 16, C.accent)}
            </div>
            <span style={{ fontSize: 16, fontWeight: 700, color: C.text1, letterSpacing: '-0.2px' }}>Contact Management</span>
          </div>
          <div style={{ fontSize: 13, color: C.text2, marginBottom: 16 }}>Questions about your account? Reach us directly:</div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <a href="mailto:office@amarmgmt.com" style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, color: C.accentLight, textDecoration: 'none', background: 'rgba(99,102,241,0.10)', border: `1px solid rgba(99,102,241,0.2)`, borderRadius: 10, padding: '9px 16px', fontWeight: 600 }}>
              {ic(I.mail, 13, C.accentLight)} office@amarmgmt.com
            </a>
            <a href="https://amarmgmt.com" target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, color: C.text2, textDecoration: 'none', background: C.bg3, border: `1px solid ${C.border2}`, borderRadius: 10, padding: '9px 16px', fontWeight: 500 }}>
              amarmgmt.com
            </a>
          </div>
        </div>

        <div style={{ textAlign: 'center', fontSize: 11, color: C.text3, paddingTop: 4 }}>© {new Date().getFullYear()} Amar Management Group</div>
      </main>

      {/* ── Payment Modal ─────────────────────────────────────────── */}
      {paymentModal && (
        <div className="modal-overlay" onClick={() => setPaymentModal(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#111827', border: `1px solid rgba(255,255,255,0.08)`, borderRadius: 22, padding: 28, width: '100%', maxWidth: 420, boxShadow: '0 8px 60px rgba(0,0,0,0.7)', fontFamily: 'Inter, -apple-system, sans-serif' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 800, color: C.text1, letterSpacing: '-0.3px' }}>Confirm Payment</div>
                <div style={{ fontSize: 13, color: C.text2, marginTop: 4 }}>Enter your Zelle payment details</div>
              </div>
              <button onClick={() => setPaymentModal(false)} style={{ width: 34, height: 34, borderRadius: 9, background: C.bg3, border: 'none', cursor: 'pointer', color: C.text2, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {ic(I.x, 14)}
              </button>
            </div>
            {[
              { key: 'amount', label: 'Amount Sent ($)', type: 'number', placeholder: '0.00' },
              { key: 'zelleRef', label: 'Zelle Reference #', type: 'text', placeholder: 'e.g. ABC123456', optional: true },
              { key: 'note', label: 'Note', type: 'text', placeholder: 'e.g. June rent payment', optional: true },
            ].map((f, i, arr) => (
              <div key={f.key} style={{ marginBottom: i < arr.length - 1 ? 14 : 20 }}>
                <label style={{ fontSize: 12, color: C.text2, fontWeight: 600, display: 'block', marginBottom: 7 }}>
                  {f.label} {f.optional && <span style={{ fontWeight: 400, color: C.text3 }}>(optional)</span>}
                </label>
                <input type={f.type} placeholder={f.placeholder} min={f.type === 'number' ? 1 : undefined}
                  value={payForm[f.key]}
                  onChange={e => { setPayForm(p => ({ ...p, [f.key]: e.target.value })); if (f.key === 'amount') setPayError(''); }}
                  style={{ width: '100%', padding: '10px 14px', background: C.bg3, border: `1px solid ${C.border2}`, borderRadius: 11, color: C.text1, fontSize: 14, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }}
                  onFocus={e => e.target.style.borderColor = C.accent}
                  onBlur={e => e.target.style.borderColor = C.border2} />
              </div>
            ))}
            {payError && <div style={{ marginBottom: 16, padding: '11px 14px', background: C.redBg, border: `1px solid ${C.redBorder}`, borderRadius: 11, fontSize: 13, color: C.red, fontWeight: 500 }}>{payError}</div>}
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setPaymentModal(false)} style={{ flex: 1, padding: '12px', background: C.bg3, border: `1px solid ${C.border2}`, borderRadius: 12, color: C.text2, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}>Cancel</button>
              <button onClick={submitPayment} disabled={!payForm.amount || payLoading}
                style={{ flex: 2, padding: '12px', background: C.accentGrad, border: 'none', borderRadius: 12, color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', opacity: (!payForm.amount || payLoading) ? 0.6 : 1, boxShadow: '0 4px 18px rgba(99,102,241,0.4)' }}>
                {payLoading ? 'Submitting…' : 'Submit Notification'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── No access screen ───────────────────────────────────────────────────────
function NoAccessScreen({ user }) {
  return (
    <div className="night" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#0f1117' }}>
      <div style={{ textAlign: 'center', maxWidth: 360 }}>
        <div style={{ width: 48, height: 48, borderRadius: 12, background: 'var(--red-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
          {ic(I.shield, 22, 'var(--red-txt)')}
        </div>
        <div style={{ fontSize: 16, fontWeight: 600, color: '#e8eaf0', marginBottom: 8 }}>Access restricted</div>
        <div style={{ fontSize: 13, color: '#8b90a0', marginBottom: 24, lineHeight: 1.6 }}>
          <strong style={{ color: '#e8eaf0' }}>{user.email}</strong> has not been granted access. Contact your administrator.
        </div>
        <button className="btn" onClick={() => signOut(auth)} style={{ margin: '0 auto' }}>
          {ic(I.logout, 14)} Sign out
        </button>
      </div>
    </div>
  );
}

// ── Login screen ───────────────────────────────────────────────────────────
function LoginScreen() {
  const [loginTab, setLoginTab] = useState('tenant');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Tenant — access code
  const [code, setCode] = useState('');
  // Tenant — email/password
  const [email, setEmail] = useState('');
  const [pass, setPass] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [tenantMethod, setTenantMethod] = useState(null); // null | 'code' | 'email' | 'google'
  const isDesktop = window.innerWidth > 768;

  // Landing panel features
  const landingFeatures = [
    { icon: 'M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2zM9 22V12h6v10', label: 'Properties, tenants & leases' },
    { icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2', label: 'Violations, compliance & tasks' },
  ];
  const [landingSection, setLandingSection] = useState(null);
  const [slideIn, setSlideIn] = useState(true);
  const navigateTo = (s) => { setSlideIn(false); setTimeout(() => { setLandingSection(s); setSlideIn(true); }, 200); };

  const privacySections = [
    { h: 'Information We Collect', b: 'Name, address, contact information (phone & email), lease and rental payment information, maintenance request details, and property violation records.' },
    { h: 'How We Use Your Information', b: 'Solely for property management purposes: rent reminders and payment notifications via SMS, maintenance schedules, violation or lease matter notices, and general property communications.' },
    { h: 'SMS Messaging', b: 'Tenants may opt in to receive SMS notifications. Consent is not required as a condition of housing services. Up to 10 messages/month. Msg & data rates may apply. Reply STOP to opt out at any time. Reply HELP for assistance.' },
    { h: 'Information Sharing', b: 'We do not sell, rent, or share your personal information with third parties for marketing purposes. SMS consent and opt-in data are never shared with third parties or affiliates under any circumstances.' },
    { h: 'Data Security', b: 'We implement reasonable security measures to protect your personal information from unauthorized access, disclosure, or misuse.' },
    { h: 'Contact', b: 'Questions? Email office@amarmgmt.com or visit amarmgmt.com' },
  ];
  const termsSections = [
    { h: 'Program Description', b: 'AMAR Management uses SMS to communicate with tenants about property management matters including rent reminders, maintenance updates, violation notices, and lease communications.' },
    { h: 'How to Opt In', b: 'Text START to our designated number, or complete a standalone SMS Consent Form provided at or after lease signing. Consent is not required as a condition of receiving housing services.' },
    { h: 'Message Frequency', b: 'Up to 10 messages per month covering your tenancy, rent payments, maintenance, violations, or other property-related matters.' },
    { h: 'Message & Data Rates', b: 'Message and data rates may apply depending on your mobile carrier and plan.' },
    { h: 'How to Opt Out', b: 'Reply STOP to any message at any time. You will receive one final confirmation and no further messages will be sent.' },
    { h: 'How to Get Help', b: 'Reply HELP to any message, or contact us at office@amarmgmt.com or amarmgmt.com' },
    { h: 'Privacy', b: 'Your phone number and personal information will not be shared with third parties for marketing purposes. See our Privacy Policy for full details.' },
  ];

  const handleAdminLogin = async () => {
    setLoading(true); setError('');
    try { await signInWithPopup(auth, provider); }
    catch (e) { console.error(e); setError('Sign-in failed. Try again.'); setLoading(false); }
  };

  const handleCodeLogin = async () => {
    if (!code.trim()) return;
    setLoading(true); setError('');
    try {
      const fn = httpsCallable(functions, 'tenantLoginWithCode');
      const res = await fn({ code: code.trim().toUpperCase() });
      await signInWithCustomToken(auth, res.data.token);
    } catch (e) {
      setError(e.message?.replace('Firebase: ', '').replace(/\s*\(auth\/.*?\)\.?/, '') || 'Invalid access code');
      setLoading(false);
    }
  };

  const handleEmailLogin = async () => {
    if (!email.trim() || !pass) return;
    setLoading(true); setError('');
    try {
      await signInWithEmailAndPassword(auth, email.trim(), pass);
    } catch (e) {
      const authErr = ['auth/invalid-credential', 'auth/wrong-password', 'auth/user-not-found', 'auth/invalid-email'];
      setError(authErr.includes(e.code) ? 'Invalid email or password' : (e.message?.replace('Firebase: ', '').replace(/\s*\(auth\/.*?\)\.?/, '') || 'Login failed'));
      setLoading(false);
    }
  };

  // Grayish-black theme — scoped to login screen only via CSS custom props
  const blackTheme = {
    '--bg0': '#1a1a1a',
    '--bg1': '#212121',
    '--bg2': '#2a2a2a',
    '--bg3': '#333333',
    '--border': '#3a3a3a',
    '--text1': '#f0f0f0',
    '--text2': '#999999',
    '--text3': '#606060',
    '--accent': '#f0f0f0',
    '--accent-bg': '#2a2a2a',
    '--red-bg': '#2a1212',
    '--red-txt': '#f87171',
    '--btn-grad': '#555555',
  };


  const gSvg = <svg width={18} height={18} viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20H24v8h11.3C33.6 33.1 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.1 6.5 29.3 4.5 24 4.5 12.7 4.5 3.5 13.7 3.5 25S12.7 45.5 24 45.5c10.5 0 19.5-7.6 19.5-20.5 0-1-.1-2-.1-3z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.5 16 19 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34.1 6.5 29.3 4.5 24 4.5c-7.7 0-14.3 4.4-17.7 10.2z"/><path fill="#4CAF50" d="M24 45.5c5.2 0 9.9-1.9 13.5-5l-6.2-5.2C29.4 37 26.8 38 24 38c-5.2 0-9.5-2.9-11.3-7.1l-6.6 4.9C9.8 41.2 16.4 45.5 24 45.5z"/><path fill="#1976D2" d="M43.6 20H24v8h11.3c-.9 2.5-2.6 4.5-4.8 5.8l6.2 5.2c3.6-3.3 5.8-8.2 5.8-14 0-1-.1-2z"/></svg>;
  const buildingIcon = (sz, sw) => <svg width={sz} height={sz} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 3v18M15 3v18M3 9h18M3 15h18"/></svg>;
  const navItems = [['about','About Us'],['contact','Contact Us'],['privacy','Privacy Policy'],['terms','Terms']];
  const sectionTitle = { about: 'About Us.', contact: 'Contact Us.', privacy: 'Privacy Policy.', terms: 'Terms & Conditions.' };

  return (
    <div style={{ minHeight: '100vh', background: '#0d1117', display: 'flex', flexDirection: 'column', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', overflow: 'hidden' }}>
      <style>{`
        .lp-main{display:flex;flex:1;align-items:center;padding:0 52px;gap:52px;overflow:hidden}
        .lp-hero{flex:1;min-width:0}
        .lp-title{font-size:62px;font-weight:800;color:#fff;line-height:1.04;letter-spacing:-2.5px;margin-bottom:8px}
        .lp-subtitle{font-size:30px;font-weight:700;color:#22304a;line-height:1.15;letter-spacing:-0.8px;margin-bottom:22px}
        .lp-card-wrap{width:380px;flex-shrink:0}
        .lp-nav{display:flex;gap:2px}
        .lp-scroll{scrollbar-width:none;-ms-overflow-style:none}
        .lp-scroll::-webkit-scrollbar{display:none}
        @media(max-width:900px){
          .lp-main{flex-direction:column;padding:28px 20px 0;gap:32px;overflow-y:auto;align-items:stretch}
          .lp-hero{padding-bottom:0}
          .lp-title{font-size:40px;letter-spacing:-1.5px}
          .lp-subtitle{font-size:20px}
          .lp-card-wrap{width:100%}
          .lp-nav{display:none}
        }
      `}</style>

      {/* ── Top nav ── */}
      <nav style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 52px', height: 64, borderBottom: '1px solid rgba(255,255,255,0.05)', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 9, background: 'linear-gradient(135deg,#2563eb,#6c8fff)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {buildingIcon(14, 2.2)}
          </div>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#e8eaf0', letterSpacing: '-0.3px' }}>Amar Management</span>
        </div>
        <div className="lp-nav">
          {navItems.map(([id, label]) => (
            <button key={id} onClick={() => navigateTo(landingSection === id ? null : id)}
              style={{ background: landingSection === id ? 'rgba(108,143,255,0.1)' : 'none', border: `1px solid ${landingSection === id ? 'rgba(108,143,255,0.22)' : 'transparent'}`, color: landingSection === id ? '#6c8fff' : '#8b95b0', fontSize: 13, fontWeight: 500, cursor: 'pointer', padding: '6px 14px', borderRadius: 8, fontFamily: 'inherit', transition: 'all 0.15s' }}>
              {label}
            </button>
          ))}
        </div>
      </nav>

      {/* ── Main ── */}
      <div className="lp-main">

        {/* Left: hero / section content */}
        <div className="lp-hero">
          {landingSection && (
            <button onClick={() => navigateTo(null)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: '#6c8fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', marginBottom: 28, padding: 0, fontFamily: 'inherit' }}>
              <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
              Back
            </button>
          )}
          <div style={{ transition: 'opacity 0.22s ease, transform 0.22s ease', opacity: slideIn ? 1 : 0, transform: slideIn ? 'translateX(0)' : 'translateX(22px)' }}>

            {/* ── Home ── */}
            {!landingSection && (<>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 28, background: 'rgba(108,143,255,0.08)', border: '1px solid rgba(108,143,255,0.16)', borderRadius: 20, padding: '5px 14px' }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#6c8fff', display: 'inline-block' }} />
                <span style={{ fontSize: 12, color: '#6c8fff', fontWeight: 500 }}>Internal Platform · Amar Marketing Enterprise</span>
              </div>
              <div className="lp-title">One Dashboard.</div>
              <div className="lp-subtitle">Your Entire Portfolio.</div>
              <p style={{ fontSize: 15, color: '#8b95b0', lineHeight: 1.75, maxWidth: 440, margin: '0 0 36px 0' }}>
                Amar Marketing Enterprise's private platform for managing properties, tenants, payments, and compliance.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 36 }}>
                {landingFeatures.map((f, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#2563eb', flexShrink: 0 }} />
                    <span style={{ fontSize: 14, color: '#c8d0e8', fontWeight: 500 }}>{f.label}</span>
                  </div>
                ))}
              </div>
            </>)}

            {/* ── About ── */}
            {landingSection === 'about' && (<>
              <div style={{ fontSize: 46, fontWeight: 800, color: '#fff', letterSpacing: '-1.5px', marginBottom: 24 }}>{sectionTitle.about}</div>
              <div style={{ fontSize: 15, color: '#9ca3b8', lineHeight: 1.85, maxWidth: 480 }}>
                <p>Amar Marketing Enterprise LLC is a Queens, NY-based real estate company managing a portfolio of residential and mixed-use properties across Jamaica and surrounding neighborhoods.</p>
                <br/><p>This platform is our internal operating system — built from the ground up to connect our properties, tenants, financials, and compliance workflows in one private dashboard.</p>
                <br/><p style={{ color: '#555e7a', fontSize: 13 }}>Inquiries: <span style={{ color: '#6c8fff' }}>office@amarmgmt.com</span></p>
              </div>
            </>)}

            {/* ── Contact ── */}
            {landingSection === 'contact' && (<>
              <div style={{ fontSize: 46, fontWeight: 800, color: '#fff', letterSpacing: '-1.5px', marginBottom: 28 }}>{sectionTitle.contact}</div>
              {[{ label: 'Email', value: 'office@amarmgmt.com' }, { label: 'Website', value: 'amarmgmt.com' }, { label: 'Location', value: 'Queens, New York' }].map(({ label, value }) => (
                <div key={label} style={{ display: 'flex', gap: 24, marginBottom: 22, alignItems: 'flex-start' }}>
                  <div style={{ width: 80, fontSize: 11, color: '#555e7a', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', paddingTop: 2, flexShrink: 0 }}>{label}</div>
                  <div style={{ fontSize: 15, color: '#c8d0e8', fontWeight: 500 }}>{value}</div>
                </div>
              ))}
              <div style={{ marginTop: 12, padding: '14px 18px', background: 'rgba(108,143,255,0.07)', border: '1px solid rgba(108,143,255,0.15)', borderRadius: 12, fontSize: 13, color: '#8b95b0', lineHeight: 1.65, maxWidth: 420 }}>
                <strong style={{ color: '#c8d0e8' }}>Tenants:</strong> Use your access code to log into your portal, or reach out to your property manager directly.
              </div>
            </>)}

            {/* ── Privacy ── */}
            {landingSection === 'privacy' && (
              <div className="lp-scroll" style={{ maxHeight: 'calc(100vh - 130px)', overflowY: 'auto', paddingRight: 8 }}>
                <div style={{ fontSize: 46, fontWeight: 800, color: '#fff', letterSpacing: '-1.5px', marginBottom: 6 }}>{sectionTitle.privacy}</div>
                <div style={{ fontSize: 12, color: '#555e7a', marginBottom: 26 }}>Effective Date: May 21, 2026</div>
                <div style={{ fontSize: 14, color: '#9ca3b8', lineHeight: 1.85, maxWidth: 500 }}>
                  {privacySections.map(({ h, b }) => (
                    <div key={h} style={{ marginBottom: 20 }}>
                      <div style={{ fontWeight: 700, color: '#c8d0e8', marginBottom: 6, fontSize: 14 }}>{h}</div>
                      <div>{b}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Terms ── */}
            {landingSection === 'terms' && (
              <div className="lp-scroll" style={{ maxHeight: 'calc(100vh - 130px)', overflowY: 'auto', paddingRight: 8 }}>
                <div style={{ fontSize: 46, fontWeight: 800, color: '#fff', letterSpacing: '-1.5px', marginBottom: 6 }}>{sectionTitle.terms}</div>
                <div style={{ fontSize: 12, color: '#555e7a', marginBottom: 26 }}>SMS Program · Effective Date: May 21, 2026</div>
                <div style={{ fontSize: 14, color: '#9ca3b8', lineHeight: 1.85, maxWidth: 500 }}>
                  {termsSections.map(({ h, b }) => (
                    <div key={h} style={{ marginBottom: 20 }}>
                      <div style={{ fontWeight: 700, color: '#c8d0e8', marginBottom: 6, fontSize: 14 }}>{h}</div>
                      <div>{b}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>
        </div>

        {/* Right: login card */}
        <div className="lp-card-wrap" style={{ ...blackTheme }}>
          <div style={{ background: '#161b27', border: '1px solid #2a2f40', borderRadius: 20, padding: '30px 26px', boxShadow: '0 0 0 1px rgba(108,143,255,0.08), 0 20px 60px rgba(0,0,0,0.6)' }}>

            {/* Brand */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 22 }}>
              <div style={{ width: 28, height: 28, borderRadius: 8, background: 'linear-gradient(135deg,#2563eb,#6c8fff)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {buildingIcon(13, 2.2)}
              </div>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#e8eaf0', letterSpacing: '-0.3px' }}>Amar Management</span>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', background: '#21242e', borderRadius: 10, padding: 4, marginBottom: 22, gap: 4 }}>
              {[{id:'tenant',label:'Tenant Portal'},{id:'admin',label:'Staff Login'}].map(t => (
                <button key={t.id} onClick={() => { setLoginTab(t.id); setError(''); setTenantMethod(null); }}
                  style={{ flex: 1, padding: '7px 0', background: loginTab === t.id ? '#2a2d38' : 'none', border: `1px solid ${loginTab === t.id ? '#363a4e' : 'transparent'}`, borderRadius: 7, color: loginTab === t.id ? '#e8eaf0' : '#555e7a', fontSize: 12, fontWeight: loginTab === t.id ? 600 : 400, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s' }}>
                  {t.label}
                </button>
              ))}
            </div>

            {loginTab === 'admin' ? (<>
              <div style={{ fontSize: 11, color: '#555e7a', marginBottom: 18, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>Property Manager Portal</div>
              <div style={{ height: 1, background: '#2a2f40', marginBottom: 18 }} />
              <button className="btn-google" onClick={handleAdminLogin} disabled={loading} style={{ background: '#21242e', border: '1px solid #2a2f40', color: '#e8eaf0' }}>
                {gSvg}{loading ? 'Signing in…' : 'Sign in with Google'}
              </button>
            </>) : (<>

              {/* ── Method picker ── */}
              {!tenantMethod && (<>
                <div style={{ fontSize: 11, color: '#555e7a', marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>How would you like to sign in?</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {[
                    { id: 'code',   icon: 'M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z', label: 'Access Code', desc: 'Enter your tenant access code' },
                    { id: 'email',  icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',               label: 'Email & Password', desc: 'Sign in with your email' },
                    { id: 'google', icon: null, label: 'Sign in with Google', desc: 'Use your Google account', isGoogle: true },
                  ].map(m => (
                    <button key={m.id} onClick={() => { setTenantMethod(m.id); setError(''); if (m.id === 'google') handleAdminLogin(); }}
                      style={{ display: 'flex', alignItems: 'center', gap: 13, padding: '12px 14px', background: '#21242e', border: '1px solid #2a2f40', borderRadius: 10, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left', transition: 'border-color 0.15s, background 0.15s', width: '100%' }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = '#3a3f54'; e.currentTarget.style.background = '#252936'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2f40'; e.currentTarget.style.background = '#21242e'; }}>
                      <div style={{ width: 34, height: 34, borderRadius: 8, background: 'rgba(108,143,255,0.1)', border: '1px solid rgba(108,143,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        {m.isGoogle
                          ? <span style={{ fontSize: 16, lineHeight: 1 }}>G</span>
                          : <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="#6c8fff" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d={m.icon}/></svg>
                        }
                      </div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#e8eaf0', marginBottom: 2 }}>{m.label}</div>
                        <div style={{ fontSize: 11, color: '#555e7a' }}>{m.desc}</div>
                      </div>
                      <svg style={{ marginLeft: 'auto', flexShrink: 0 }} width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="#555e7a" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
                    </button>
                  ))}
                </div>
              </>)}

              {/* ── Access Code form ── */}
              {tenantMethod === 'code' && (<>
                <button onClick={() => { setTenantMethod(null); setError(''); setCode(''); }} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', color: '#6c8fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0, fontFamily: 'inherit', marginBottom: 18 }}>
                  <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
                  Change method
                </button>
                <label style={{ fontSize: 11, color: '#555e7a', fontWeight: 600, display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Access Code</label>
                <input className="form-input" placeholder="e.g. ABC12345" value={code}
                  onChange={e => { setCode(e.target.value.toUpperCase()); setError(''); }}
                  onKeyDown={e => e.key === 'Enter' && handleCodeLogin()} maxLength={8} autoFocus
                  style={{ fontFamily: 'monospace', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 10, background: '#21242e', border: '1px solid #2a2f40', color: '#e8eaf0' }} />
                <button className="btn btn-primary" style={{ width: '100%', color: '#fff', fontWeight: 600, justifyContent: 'center' }}
                  onClick={handleCodeLogin} disabled={!code.trim() || loading}>
                  {loading ? 'Signing in…' : 'Enter Portal'}
                </button>
              </>)}

              {/* ── Email & Password form ── */}
              {tenantMethod === 'email' && (<>
                <button onClick={() => { setTenantMethod(null); setError(''); setEmail(''); setPass(''); }} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', color: '#6c8fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0, fontFamily: 'inherit', marginBottom: 18 }}>
                  <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
                  Change method
                </button>
                <label style={{ fontSize: 11, color: '#555e7a', fontWeight: 600, display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Email</label>
                <input className="form-input" type="email" placeholder="your@email.com" value={email}
                  onChange={e => { setEmail(e.target.value); setError(''); }}
                  onKeyDown={e => e.key === 'Enter' && handleEmailLogin()} autoFocus
                  style={{ marginBottom: 10, background: '#21242e', border: '1px solid #2a2f40', color: '#e8eaf0' }} />
                <label style={{ fontSize: 11, color: '#555e7a', fontWeight: 600, display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Password</label>
                <div style={{ position: 'relative', marginBottom: 12 }}>
                  <input className="form-input" type={showPass ? 'text' : 'password'} placeholder="••••••••" value={pass}
                    onChange={e => { setPass(e.target.value); setError(''); }}
                    onKeyDown={e => e.key === 'Enter' && handleEmailLogin()}
                    style={{ paddingRight: 36, background: '#21242e', border: '1px solid #2a2f40', color: '#e8eaf0' }} />
                  <button onClick={() => setShowPass(p => !p)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#555e7a', padding: 2, display: 'flex', alignItems: 'center' }}>
                    {ic(showPass ? I.eyeOff : I.eye, 14)}
                  </button>
                </div>
                <button className="btn btn-primary" style={{ width: '100%', color: '#fff', fontWeight: 600, justifyContent: 'center' }}
                  onClick={handleEmailLogin} disabled={!email.trim() || !pass || loading}>
                  {loading ? 'Signing in…' : 'Sign In'}
                </button>
              </>)}

            </>)}

            {error && (
              <div style={{ marginTop: 12, padding: '8px 12px', background: '#2a0f0f', border: '1px solid #3d1515', borderRadius: 8, fontSize: 12, color: '#f87171' }}>
                {error}
              </div>
            )}

            {/* Footer */}
            <div style={{ marginTop: 20, textAlign: 'center', fontSize: 11, color: '#555e7a', borderTop: '1px solid #2a2f40', paddingTop: 16 }}>
              © {new Date().getFullYear()} Amar Management Group
              <div style={{ marginTop: 7, display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '4px 10px' }}>
                {navItems.map(([id, label]) => (
                  <button key={id} onClick={() => navigateTo(id)}
                    style={{ background: 'none', border: 'none', color: '#555e7a', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit', padding: '2px 0' }}
                    onMouseEnter={e => e.currentTarget.style.color = '#6c8fff'}
                    onMouseLeave={e => e.currentTarget.style.color = '#555e7a'}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ── Mobile bottom sheet ── */}
      {landingSection && !isDesktop && (
        <div onClick={() => navigateTo(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 50, display: 'flex', alignItems: 'flex-end' }}>
          <div onClick={e => e.stopPropagation()} className="thin-scroll"
            style={{ background: '#13171f', border: '1px solid rgba(108,143,255,0.15)', borderRadius: '20px 20px 0 0', padding: '20px 22px 36px', width: '100%', maxHeight: '78vh', overflowY: 'auto', transition: 'transform 0.3s cubic-bezier(0.4,0,0.2,1)', transform: slideIn ? 'translateY(0)' : 'translateY(100%)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <button onClick={() => navigateTo(null)} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', color: '#6c8fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}>
                <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
                Back
              </button>
              <div style={{ width: 36, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.1)' }} />
            </div>
            <div style={{ transition: 'opacity 0.2s ease', opacity: slideIn ? 1 : 0 }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#e8eaf0', marginBottom: 16 }}>{sectionTitle[landingSection]}</div>
              {landingSection === 'about' && (
                <div style={{ fontSize: 13, color: '#9ca3b8', lineHeight: 1.85 }}>
                  <p>Amar Marketing Enterprise LLC is a Queens, NY-based real estate company managing a portfolio of residential and mixed-use properties across Jamaica and surrounding neighborhoods.</p>
                  <br/><p>This platform is our internal operating system — built to connect our properties, tenants, financials, and compliance in one private dashboard.</p>
                </div>
              )}
              {landingSection === 'contact' && (
                <div style={{ fontSize: 13, color: '#9ca3b8' }}>
                  {[{ label: 'Email', value: 'office@amarmgmt.com' }, { label: 'Website', value: 'amarmgmt.com' }, { label: 'Location', value: 'Queens, New York' }].map(({ label, value }) => (
                    <div key={label} style={{ display: 'flex', gap: 14, marginBottom: 14 }}>
                      <div style={{ width: 68, fontSize: 10, color: '#555e7a', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', paddingTop: 1, flexShrink: 0 }}>{label}</div>
                      <div style={{ color: '#c8d0e8', fontWeight: 500 }}>{value}</div>
                    </div>
                  ))}
                  <div style={{ marginTop: 8, padding: '11px 13px', background: 'rgba(108,143,255,0.07)', border: '1px solid rgba(108,143,255,0.15)', borderRadius: 10, fontSize: 12, color: '#8b95b0', lineHeight: 1.65 }}>
                    <strong style={{ color: '#c8d0e8' }}>Tenants:</strong> Use your access code to log in or contact your property manager.
                  </div>
                </div>
              )}
              {landingSection === 'privacy' && (
                <div style={{ fontSize: 12, color: '#9ca3b8', lineHeight: 1.8 }}>
                  <div style={{ fontSize: 11, color: '#555e7a', marginBottom: 14 }}>Effective Date: May 21, 2026</div>
                  {privacySections.map(({ h, b }) => (<div key={h} style={{ marginBottom: 14 }}><div style={{ fontWeight: 600, color: '#c8d0e8', marginBottom: 4 }}>{h}</div><div>{b}</div></div>))}
                </div>
              )}
              {landingSection === 'terms' && (
                <div style={{ fontSize: 12, color: '#9ca3b8', lineHeight: 1.8 }}>
                  <div style={{ fontSize: 11, color: '#555e7a', marginBottom: 14 }}>SMS Program · Effective Date: May 21, 2026</div>
                  {termsSections.map(({ h, b }) => (<div key={h} style={{ marginBottom: 14 }}><div style={{ fontWeight: 600, color: '#c8d0e8', marginBottom: 4 }}>{h}</div><div>{b}</div></div>))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

// ── Vendors ───────────────────────────────────────────────────────────────
function CopyBtn({ value }) {
  const [copied, setCopied] = useState(false);
  if (!value || value === 'Pending') return null;
  const copy = e => {
    e.stopPropagation();
    navigator.clipboard.writeText(value).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
  };
  return (
    <button onClick={copy} title={copied ? 'Copied!' : 'Copy'} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', color: copied ? 'var(--green-txt)' : 'var(--text3)', display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>
      {copied
        ? ic('M9 11l3 3L22 4M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11', 13)
        : ic(I.clipboard, 13)}
    </button>
  );
}

const VENDOR_PAGE_SIZE = 50;

function Vendors({ vendors: allVendors, setVendors, properties, invoices, initialId, clearNav }) {
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [propFilter, setPropFilter] = useState('');
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState(initialId || null);
  useEffect(() => {
    if (!initialId) return;
    setExpandedId(initialId);
    if (clearNav) clearNav();
    // Jump to the correct page and scroll the row into view
    setTimeout(() => {
      const el = document.getElementById(`vendor-row-${initialId}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 120);
  }, [initialId]); // eslint-disable-line
  const [exportModal, setExportModal] = useState(false);
  const [exportPropId, setExportPropId] = useState('all');
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => { const h = () => setIsMobile(window.innerWidth < 768); window.addEventListener('resize', h); return () => window.removeEventListener('resize', h); }, []);
  const ff = k => v => setForm(p => ({ ...p, [k]: v }));

  const doExport = () => {
    const rows = (allVendors || [])
      .filter(v => exportPropId === 'all' || (v.propertyIds || []).includes(exportPropId))
      .map(v => ({
        'Name': v.name || '',
        'Phone': v.phone || '',
        'Email': v.email || '',
        'Address': v.address || '',
        'Properties': (v.propertyIds || []).map(id => properties.find(p => p.id === id)?.name || id).join(', '),
        'Insurance End Date': v.insuranceEndDate || '',
        'Notes': v.notes || '',
      }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Vendors');
    const propLabel = exportPropId === 'all' ? 'All_Properties' : (properties.find(p => p.id === exportPropId)?.name || exportPropId).replace(/\s+/g, '_');
    XLSX.writeFile(wb, `Vendors_${propLabel}_${new Date().toISOString().slice(0, 10)}.xlsx`);
    setExportModal(false);
    toast('Exported successfully');
  };

  const filtered = useMemo(() => {
    return (allVendors || []).filter(v => {
      const sl = search.toLowerCase();
      const matchSearch = !search ||
        v.name.toLowerCase().includes(sl) ||
        (v.phone || '').includes(search) ||
        (v.email || '').toLowerCase().includes(sl);
      const matchProp = !propFilter || (v.propertyIds || []).includes(propFilter);
      return matchSearch && matchProp;
    });
  }, [allVendors, search, propFilter]);

  // Reset to page 1 and collapse when filters change
  useEffect(() => { setPage(1); setExpandedId(null); }, [search, propFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / VENDOR_PAGE_SIZE));
  const vendors = filtered.slice((page - 1) * VENDOR_PAGE_SIZE, page * VENDOR_PAGE_SIZE);

  const openAdd = () => {
    setForm({ id: `vendor${Date.now()}`, name: '', address: '', phone: '', email: '', propertyIds: [], insuranceEndDate: '', notes: '', documents: [] });
    setModal('add');
  };
  const openEdit = v => { setForm({ ...v, propertyIds: v.propertyIds || [], documents: v.documents || [] }); setModal('edit'); };

  const save = () => {
    if (modal === 'edit') setVendors(prev => (prev || []).map(v => v.id === form.id ? form : v));
    else setVendors(prev => [...(prev || []), form]);
    setModal(null);
    toast(modal === 'edit' ? 'Vendor updated' : 'Vendor added');
  };

  const remove = id => {
    if (window.confirm('Delete vendor?')) { setVendors(prev => (prev || []).filter(v => v.id !== id)); toast('Vendor deleted', 'info'); }
  };

  const handleDocUpload = async e => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    setUploadingDoc(true);
    try {
      const path = `vendors/${form.id}/${Date.now()}_${file.name}`;
      const storageRef = sRef(storage, path);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      const newDoc = { id: `doc${Date.now()}`, name: file.name, url, storagePath: path, uploadedAt: new Date().toISOString() };
      setForm(p => ({ ...p, documents: [...(p.documents || []), newDoc] }));
    } catch (err) {
      console.error('Upload failed:', err);
      alert('Upload failed. Please try again.');
    }
    setUploadingDoc(false);
  };

  const removeDoc = async docEntry => {
    if (!window.confirm(`Remove "${docEntry.name}"?`)) return;
    try {
      if (docEntry.storagePath) await deleteObject(sRef(storage, docEntry.storagePath));
    } catch (err) { console.error(err); }
    setForm(p => ({ ...p, documents: (p.documents || []).filter(d => d.id !== docEntry.id) }));
  };

  const propName = id => properties.find(p => p.id === id)?.name || id;

  return (
    <div>
      <div className="page-header">
        <span className="page-title">Vendors</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn" onClick={() => { setExportPropId('all'); setExportModal(true); }}>
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            Export
          </button>
          <button className="btn btn-primary" onClick={openAdd}>{ic(I.plus, 14, '#fff')} Add vendor</button>
        </div>
      </div>

      {/* Search + Filter */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 180 }}>
          <SB value={search} onChange={setSearch} placeholder="Search by name, phone, or email…" />
        </div>
        <select
          className="form-input"
          style={{ width: 'auto', minWidth: 160, fontSize: 13 }}
          value={propFilter}
          onChange={e => setPropFilter(e.target.value)}
        >
          <option value="">All properties</option>
          {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>

      {/* Table */}
      <table className="list-table" style={isMobile ? { display: 'table', width: '100%', tableLayout: 'fixed', whiteSpace: 'normal' } : {}}>
        {!isMobile && (
          <thead>
            <tr>
              <th>Vendor Name</th>
              <th>Phone</th>
              <th>Email</th>
              <th>Properties</th>
              <th style={{ width: 40 }}></th>
            </tr>
          </thead>
        )}
        {isMobile && (
          <thead>
            <tr>
              <th style={{ whiteSpace: 'normal' }}>Vendor Name</th>
              <th style={{ width: 44 }}></th>
            </tr>
          </thead>
        )}
        <tbody>
          {vendors.length === 0 ? (
            <tr><td colSpan={isMobile ? 2 : 5}><div className="empty">No vendors found — add one to get started</div></td></tr>
          ) : vendors.map(v => {
            const expanded = expandedId === v.id;
            return (
              <React.Fragment key={v.id}>
                {isMobile ? (
                  <tr id={`vendor-row-${v.id}`} className="clickable" onClick={() => setExpandedId(expanded ? null : v.id)} style={{ background: expanded ? 'var(--bg2)' : undefined }}>
                    <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 12, color: 'var(--text3)', display: 'inline-block', transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s', flexShrink: 0 }}>▾</span>
                        <div>
                          <div style={{ fontWeight: 500, fontSize: 13 }}>{v.name}</div>
                          {v.address && v.address !== 'Pending' && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{v.address}</div>}
                        </div>
                      </div>
                    </td>
                    <td style={{ width: 44, textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                      <KebabMenu items={[{ label: 'Edit', onClick: () => openEdit(v) }, { label: 'Delete', danger: true, onClick: () => remove(v.id) }]} />
                    </td>
                  </tr>
                ) : (
                  <tr id={`vendor-row-${v.id}`} className="clickable" onClick={() => setExpandedId(expanded ? null : v.id)} style={{ background: expanded ? 'var(--bg2)' : undefined }}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 12, color: 'var(--text3)', display: 'inline-block', transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s', flexShrink: 0 }}>▾</span>
                        <div>
                          <div style={{ fontWeight: 500 }}>{v.name}</div>
                          {v.address && v.address !== 'Pending' && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{v.address}</div>}
                        </div>
                      </div>
                    </td>
                    <td style={{ fontSize: 13, color: 'var(--text2)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        {v.phone || <span style={{ color: 'var(--text3)' }}>—</span>}
                        <CopyBtn value={v.phone} />
                      </div>
                    </td>
                    <td style={{ fontSize: 13, color: 'var(--text2)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        {v.email || <span style={{ color: 'var(--text3)' }}>—</span>}
                        <CopyBtn value={v.email} />
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {(v.propertyIds || []).length === 0
                          ? <span style={{ fontSize: 12, color: 'var(--text3)' }}>—</span>
                          : (v.propertyIds || []).map(pid => (
                              <span key={pid} style={{ fontSize: 11, padding: '2px 7px', borderRadius: 12, background: 'var(--accent)', color: '#fff', fontWeight: 500 }}>
                                {propName(pid)}
                              </span>
                            ))
                        }
                      </div>
                    </td>
                    <td onClick={e => e.stopPropagation()}>
                      <KebabMenu items={[{ label: 'Edit', onClick: () => openEdit(v) }, { label: 'Delete', danger: true, onClick: () => remove(v.id) }]} />
                    </td>
                  </tr>
                )}
                {expanded && (
                  <tr>
                    <td colSpan={isMobile ? 2 : 5} style={{ padding: 0 }}>
                      <div style={{ padding: '16px 20px', background: 'var(--bg3)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
                        {/* Info grid */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px 24px', marginBottom: 14 }}>
                          <div><div className="info-label">Address</div><div className="info-val">{v.address || '—'}</div></div>
                          <div>
                            <div className="info-label">Phone</div>
                            <div className="info-val" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              {v.phone || '—'}<CopyBtn value={v.phone} />
                            </div>
                          </div>
                          <div>
                            <div className="info-label">Email</div>
                            <div className="info-val" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              {v.email || '—'}<CopyBtn value={v.email} />
                            </div>
                          </div>
                          <div>
                            <div className="info-label">Insurance Expiry</div>
                            <div className="info-val" style={{ color: v.insuranceEndDate && isExpiredISO(v.insuranceEndDate) ? 'var(--red-txt)' : v.insuranceEndDate && isAlmostDueISO(v.insuranceEndDate) ? '#f97316' : 'var(--text1)', fontWeight: v.insuranceEndDate && (isExpiredISO(v.insuranceEndDate) || isAlmostDueISO(v.insuranceEndDate)) ? 600 : 400 }}>
                              {v.insuranceEndDate ? `${isExpiredISO(v.insuranceEndDate) ? 'EXPIRED · ' : isAlmostDueISO(v.insuranceEndDate) ? 'Almost Due · ' : ''}${fmtDate(v.insuranceEndDate)}` : '—'}
                            </div>
                          </div>
                        </div>
                        {/* Properties */}
                        {(v.propertyIds || []).length > 0 && (
                          <div style={{ marginBottom: 14 }}>
                            <div className="info-label" style={{ marginBottom: 6 }}>Properties</div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                              {(v.propertyIds || []).map(pid => (
                                <span key={pid} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 12, background: 'var(--accent)', color: '#fff', fontWeight: 500 }}>
                                  {propName(pid)}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {/* Notes */}
                        {v.notes && (
                          <div style={{ marginBottom: 14 }}>
                            <div className="info-label" style={{ marginBottom: 4 }}>Notes</div>
                            <div style={{ fontSize: 13, color: 'var(--text1)', lineHeight: 1.6 }}>{v.notes}</div>
                          </div>
                        )}
                        {/* Documents */}
                        {(v.documents || []).length > 0 && (
                          <div style={{ marginBottom: 14 }}>
                            <div className="info-label" style={{ marginBottom: 6 }}>Documents</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                              {(v.documents || []).map(d => (
                                <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                                  {ic(I.file, 13, 'var(--text2)')}
                                  <a href={d.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', fontWeight: 500 }}>{d.name}</a>
                                  <span style={{ color: 'var(--text3)', fontSize: 11 }}>{d.uploadedAt ? new Date(d.uploadedAt).toLocaleDateString('en-US') : ''}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {/* Actions */}
                        <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                          <button className="btn" style={{ fontSize: 12, padding: '6px 14px' }} onClick={() => openEdit(v)}>Edit</button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>

      {/* Pager */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, marginTop: 16, flexWrap: 'wrap' }}>
          <button className="btn" style={{ padding: '4px 10px', fontSize: 12 }} disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹ Prev</button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
            <button key={p} onClick={() => setPage(p)} style={{ padding: '4px 10px', fontSize: 12, borderRadius: 6, border: '1px solid var(--border)', background: p === page ? 'var(--accent)' : 'var(--bg2)', color: p === page ? '#fff' : 'var(--text1)', fontWeight: p === page ? 600 : 400, cursor: 'pointer' }}>{p}</button>
          ))}
          <button className="btn" style={{ padding: '4px 10px', fontSize: 12 }} disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next ›</button>
        </div>
      )}

      {/* Add / Edit Modal */}
      {modal && (
        <Modal title={modal === 'edit' ? 'Edit vendor' : 'Add vendor'} onClose={() => setModal(null)} lg>
          <div className="form-grid form-grid-2">
            <FI label="Vendor name" value={form.name} onChange={ff('name')} placeholder="e.g. ABC Plumbing" />
            <FI label="Phone" value={form.phone} onChange={ff('phone')} placeholder="718-555-0100" />
          </div>
          <div className="form-grid form-grid-2">
            <FI label="Email" value={form.email} onChange={ff('email')} type="email" placeholder="vendor@example.com" />
            <FI label="Address (optional)" value={form.address} onChange={ff('address')} />
          </div>

          {/* Properties multi-select */}
          <div style={{ marginBottom: 14 }}>
            <div className="form-label">Properties</div>
            {properties.length === 0
              ? <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>No properties added yet.</div>
              : <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
                  {properties.map(p => {
                    const checked = (form.propertyIds || []).includes(p.id);
                    return (
                      <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={e => setForm(prev => ({
                            ...prev,
                            propertyIds: e.target.checked
                              ? [...(prev.propertyIds || []), p.id]
                              : (prev.propertyIds || []).filter(id => id !== p.id),
                          }))}
                        />
                        {p.name}
                        {p.address && <span style={{ fontSize: 11, color: 'var(--text3)' }}>{p.address}</span>}
                      </label>
                    );
                  })}
                </div>
            }
          </div>

          <FI label="Insurance termination date" value={form.insuranceEndDate} onChange={ff('insuranceEndDate')} type="date" />
          <FTA label="Notes" value={form.notes} onChange={ff('notes')} />

          {/* Documents */}
          <div style={{ marginBottom: 14 }}>
            <div className="form-label">Documents</div>
            <div style={{ marginTop: 6 }}>
              {(form.documents || []).map(d => (
                <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 10px', background: 'var(--bg2)', borderRadius: 6, marginBottom: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    {ic(I.file, 14, 'var(--text2)')}
                    <div style={{ fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    <a href={d.url} target="_blank" rel="noopener noreferrer" className="btn-ghost" style={{ fontSize: 11 }}>View</a>
                    <button className="btn-ghost" style={{ fontSize: 11, color: 'var(--red-txt)' }} onClick={() => removeDoc(d)}>Remove</button>
                  </div>
                </div>
              ))}
              <label style={{ cursor: uploadingDoc ? 'default' : 'pointer', display: 'inline-block', marginTop: 4, opacity: uploadingDoc ? 0.6 : 1 }}>
                <input type="file" style={{ display: 'none' }} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={handleDocUpload} disabled={uploadingDoc} />
                <span className="btn-ghost" style={{ fontSize: 12, pointerEvents: uploadingDoc ? 'none' : 'auto' }}>
                  {uploadingDoc ? 'Uploading…' : <>{ic(I.plus, 12)} Upload document</>}
                </span>
              </label>
            </div>
          </div>

          <div className="modal-actions">
            <button className="btn" onClick={() => setModal(null)}>Cancel</button>
            <SavingBtn onClick={save}>Save</SavingBtn>
          </div>
        </Modal>
      )}

      {exportModal && (
        <Modal title="Export Vendors" onClose={() => setExportModal(false)}>
          <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>Choose which vendors to include in the export.</div>
          <div className="form-group">
            <label className="form-label">Property</label>
            <select className="form-input" value={exportPropId} onChange={e => setExportPropId(e.target.value)}>
              <option value="all">All properties</option>
              {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 16 }}>
            {exportPropId === 'all'
              ? `${(allVendors || []).length} vendor(s) will be exported`
              : `${(allVendors || []).filter(v => (v.propertyIds || []).includes(exportPropId)).length} vendor(s) for this property`}
          </div>
          <div className="modal-actions">
            <button className="btn" onClick={() => setExportModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={doExport}>
              <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Export Excel
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Invoice Detail ─────────────────────────────────────────────────────────
function InvoiceDetail({ invoiceId, invoices, setInvoices, properties, onBack }) {
  const toast = useToast();
  const inv = (invoices || []).find(x => x.id === invoiceId);
  const today = new Date().toISOString().slice(0, 10);
  const [addingPay, setAddingPay] = useState(false);
  const [payForm, setPayForm] = useState({ date: today, amount: '', method: 'Check', note: '' });
  const [payMenuId, setPayMenuId] = useState(null);
  const [editingPayId, setEditingPayId] = useState(null);
  const [editForm, setEditForm] = useState({ date: '', amount: '', method: 'Check', note: '' });
  const payMenuRef = useRef(null);
  const [editingInv, setEditingInv] = useState(false);
  const [invEditForm, setInvEditForm] = useState({});
  const fiei = k => v => setInvEditForm(p => ({ ...p, [k]: v }));

  const openEditInv = () => {
    const items = inv.items && inv.items.length > 0
      ? inv.items
      : inv.description
        ? [{ description: inv.description, price: inv.amount || '' }]
        : [{ description: '', price: '' }];
    setInvEditForm({ ...inv, items });
    setEditingInv(true);
  };

  const saveEditInv = () => {
    const payments = inv.payments || [];
    const totalPaidAmt = payments.reduce((s, p) => s + (p.amount || 0), 0);
    const items = (invEditForm.items || []).filter(it => it.description || it.price);
    const amount = items.length > 0
      ? items.reduce((s, it) => s + (parseFloat(it.price) || 0), 0)
      : Number(invEditForm.amount || 0);
    const description = items.map(it => it.description).filter(Boolean).join(', ');
    const status = totalPaidAmt <= 0 ? 'Unpaid' : totalPaidAmt >= amount ? 'Paid' : 'Partial';
    setInvoices(prev => prev.map(x => x.id === invoiceId
      ? { ...x, ...invEditForm, items, description, amount, status }
      : x
    ));
    setEditingInv(false);
  };

  // Assign IDs to any payments that were imported without them
  useEffect(() => {
    if (!inv) return;
    if ((inv.payments || []).some(p => !p.id)) {
      let seq = 0;
      setInvoices(prev => prev.map(x => {
        if (x.id !== invoiceId) return x;
        const fixed = (x.payments || []).map(p => p.id ? p : { ...p, id: `pay${Date.now()}${seq++}` });
        return { ...x, payments: fixed };
      }));
    }
  }, [invoiceId]); // eslint-disable-line

  useEffect(() => {
    if (!payMenuId) return;
    const handler = e => { if (payMenuRef.current && !payMenuRef.current.contains(e.target)) setPayMenuId(null); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [payMenuId]);

  if (!inv) return null;

  const propName = id => properties.find(p => p.id === id)?.name || id;
  const fmt$ = n => `$${(+n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const payments = [...(inv.payments || [])].sort((a, b) => (a.date || '').localeCompare(b.date || ''));

  const updateInv = fn => setInvoices(prev => prev.map(x => x.id === invoiceId ? fn(x) : x));

  const recompute = pays => {
    const totalPaid = pays.reduce((s, p) => s + (p.amount || 0), 0);
    const status = totalPaid <= 0 ? 'Unpaid' : totalPaid >= (inv.amount || 0) ? 'Paid' : 'Partial';
    return { amountPaid: totalPaid, status };
  };

  const addPayment = () => {
    const amt = Number(payForm.amount || 0);
    if (!amt) return;
    const existing = inv?.payments || [];
    const isDup = existing.some(p =>
      p.date === payForm.date &&
      Number(p.amount) === amt &&
      (p.note || '').trim() === (payForm.note || '').trim()
    );
    if (isDup && !window.confirm('A payment with the same date, amount, and note already exists. Save anyway?')) return;
    const newPay = { id: `pay${Date.now()}`, date: payForm.date, amount: amt, method: payForm.method, note: payForm.note };
    updateInv(x => {
      const newPayments = [...(x.payments || []), newPay];
      return { ...x, payments: newPayments, ...recompute(newPayments) };
    });
    setPayForm({ date: today, amount: '', method: 'Check', note: '' });
    setAddingPay(false);
    toast('Payment recorded');
  };

  const deletePayment = payId => {
    if (!window.confirm('Remove this payment?')) return;
    updateInv(x => {
      const newPayments = (x.payments || []).filter(p => p.id !== payId);
      return { ...x, payments: newPayments, ...recompute(newPayments) };
    });
    toast('Payment removed', 'info');
  };

  const editPayment = () => {
    const amt = Number(editForm.amount || 0);
    if (!amt) return;
    updateInv(x => {
      const newPayments = (x.payments || []).map(p =>
        p.id === editingPayId ? { ...p, date: editForm.date, amount: amt, method: editForm.method, note: editForm.note } : p
      );
      return { ...x, payments: newPayments, ...recompute(newPayments) };
    });
    setEditingPayId(null);
    toast('Payment updated');
  };

  const totalPaid = payments.reduce((s, p) => s + (p.amount || 0), 0);
  const balance = (inv.amount || 0) - totalPaid;
  const computedStatus = totalPaid <= 0 ? 'Unpaid' : totalPaid >= (inv.amount || 0) ? 'Paid' : 'Partial';
  const isOverdue = inv.dueDate && isExpiredISO(inv.dueDate) && computedStatus !== 'Paid';

  const statusBadge = s => s === 'Paid'
    ? <span className="badge badge-green">{s}</span>
    : s === 'Partial'
      ? <span className="badge badge-amber">{s}</span>
      : <span className="badge badge-red">{s}</span>;

  const PAY_METHODS = ['Check', 'Wire Transfer', 'ACH', 'Zelle', 'Cash', 'Credit Card', 'Other'];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <button className="back-btn" onClick={onBack}>{ic(I.arrowLeft, 15)} All invoices</button>
        <button className="btn" onClick={openEditInv}>{ic(I.edit, 14)} Edit Invoice</button>
      </div>

      {/* Header */}
      <div style={{ marginBottom: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <h1 style={{ fontSize: 18, fontWeight: 600 }}>{inv.title || 'Untitled Invoice'}</h1>
          {statusBadge(computedStatus)}
          {isOverdue && <span className="badge badge-red">Overdue</span>}
        </div>
        <div style={{ fontSize: 13, color: 'var(--text2)' }}>
          {propName(inv.propertyId)}{inv.vendor ? ` · ${inv.vendor}` : ''}{inv.category ? ` · ${inv.category}` : ''}
        </div>
      </div>

      {/* Balance KPI tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, margin: '20px 0' }}>
        <div className="card" style={{ padding: '14px 18px', borderLeft: '3px solid var(--text3)' }}>
          <div className="info-label">Invoice total</div>
          <div style={{ fontSize: 22, fontWeight: 700, marginTop: 5 }}>{fmt$(inv.amount)}</div>
        </div>
        <div className="card" style={{ padding: '14px 18px', borderLeft: '3px solid var(--green-txt)', background: 'var(--green-bg)' }}>
          <div className="info-label">Total paid</div>
          <div style={{ fontSize: 22, fontWeight: 700, marginTop: 5, color: 'var(--green-txt)' }}>{fmt$(totalPaid)}</div>
        </div>
        <div className="card" style={{ padding: '14px 18px', borderLeft: `3px solid ${balance <= 0 ? 'var(--green-txt)' : 'var(--red-txt)'}`, background: balance <= 0 ? 'var(--green-bg)' : 'var(--red-bg)' }}>
          <div className="info-label">Balance remaining</div>
          <div style={{ fontSize: 22, fontWeight: 700, marginTop: 5, color: balance <= 0 ? 'var(--green-txt)' : 'var(--red-txt)' }}>{fmt$(balance)}</div>
        </div>
      </div>

      {/* Add payment form */}
      {addingPay && (
        <div className="card" style={{ marginBottom: 16, borderLeft: '3px solid var(--accent)' }}>
          <div className="card-title" style={{ marginBottom: 14 }}>{ic(I.dollar, 14)} Record a payment</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Date</label>
              <input type="date" className="form-input" value={payForm.date} onChange={e => setPayForm(p => ({ ...p, date: e.target.value }))} />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Amount ($)</label>
              <input type="number" className="form-input" placeholder="0.00" value={payForm.amount}
                onChange={e => setPayForm(p => ({ ...p, amount: e.target.value }))} autoFocus />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Method</label>
              <select className="form-input" value={payForm.method} onChange={e => setPayForm(p => ({ ...p, method: e.target.value }))}>
                {PAY_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group" style={{ marginBottom: 12 }}>
            <label className="form-label">Note (optional)</label>
            <input type="text" className="form-input" placeholder="e.g. Check #1042, confirmation #ABC123" value={payForm.note}
              onChange={e => setPayForm(p => ({ ...p, note: e.target.value }))} />
          </div>
          {/* Quick-fill buttons */}
          {balance > 0 && (
            <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
              <span style={{ fontSize: 11, color: 'var(--text3)', alignSelf: 'center' }}>Quick fill:</span>
              <button className="btn-ghost" style={{ fontSize: 11 }} onClick={() => setPayForm(p => ({ ...p, amount: balance.toFixed(2) }))}>
                Full balance ({fmt$(balance)})
              </button>
              {balance > 0 && (
                <button className="btn-ghost" style={{ fontSize: 11 }} onClick={() => setPayForm(p => ({ ...p, amount: (balance / 2).toFixed(2) }))}>
                  Half ({fmt$(balance / 2)})
                </button>
              )}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn" onClick={() => { setAddingPay(false); setPayForm({ date: today, amount: '', method: 'Check', note: '' }); }}>Cancel</button>
            <SavingBtn onClick={addPayment}>Save payment</SavingBtn>
          </div>
        </div>
      )}

      {/* Invoice details + line items */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div className="card-title" style={{ marginBottom: 0 }}>{ic(I.receipt, 14)} Invoice details</div>
          {!addingPay && (
            <button className="btn btn-primary" onClick={() => setAddingPay(true)}>{ic(I.plus, 14, '#fff')} Add payment</button>
          )}
        </div>
        <div className="info-grid" style={{ marginBottom: 16 }}>
          <div><div className="info-label">Vendor</div><div className="info-val">{inv.vendor || '—'}</div></div>
          <div><div className="info-label">Category</div><div className="info-val">{inv.category || '—'}</div></div>
          <div><div className="info-label">Invoice date</div><div className="info-val">{fmtDate(inv.invoiceDate)}</div></div>
          <div>
            <div className="info-label">Due date</div>
            <div className="info-val" style={{ color: isOverdue ? 'var(--red-txt)' : 'var(--text1)', fontWeight: isOverdue ? 600 : 400 }}>
              {inv.dueDate ? `${isOverdue ? 'OVERDUE · ' : ''}${fmtDate(inv.dueDate)}` : '—'}
            </div>
          </div>
        </div>

        {/* Line items */}
        {inv.items && inv.items.length > 0 && (
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14 }}>
            <div className="info-label" style={{ marginBottom: 10 }}>Line items</div>
            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', color: 'var(--text2)', fontWeight: 500, fontSize: 11, paddingBottom: 6, borderBottom: '1px solid var(--border)' }}>Description</th>
                  <th style={{ textAlign: 'right', color: 'var(--text2)', fontWeight: 500, fontSize: 11, paddingBottom: 6, borderBottom: '1px solid var(--border)' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {inv.items.map((item, i) => (
                  <tr key={i}>
                    <td style={{ padding: '8px 0', borderBottom: '1px solid var(--border)', color: 'var(--text1)' }}>{item.description || '—'}</td>
                    <td style={{ padding: '8px 0', borderBottom: '1px solid var(--border)', textAlign: 'right', fontWeight: 500 }}>
                      {item.price ? parseFloat(item.price).toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td style={{ paddingTop: 10, fontWeight: 600, fontSize: 13 }}>Total</td>
                  <td style={{ paddingTop: 10, textAlign: 'right', fontWeight: 700, fontSize: 14 }}>{fmt$(inv.amount)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
        {(!inv.items || inv.items.length === 0) && inv.description && (
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14, fontSize: 13, color: 'var(--text1)', lineHeight: 1.65 }}>{inv.description}</div>
        )}
      </div>

      {/* Edit payment form */}
      {editingPayId && (
        <div className="card" style={{ marginBottom: 16, borderLeft: '3px solid var(--accent)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div className="card-title" style={{ marginBottom: 0 }}>{ic(I.dollar, 14)} Edit payment</div>
            <button className="btn-ghost" onClick={() => setEditingPayId(null)}>Cancel</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Date</label>
              <input type="date" className="form-input" value={editForm.date} onChange={e => setEditForm(f => ({ ...f, date: e.target.value }))} />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Amount ($)</label>
              <input type="number" className="form-input" value={editForm.amount} onChange={e => setEditForm(f => ({ ...f, amount: e.target.value }))} autoFocus />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Method</label>
              <select className="form-input" value={editForm.method} onChange={e => setEditForm(f => ({ ...f, method: e.target.value }))}>
                {PAY_METHODS.map(m => <option key={m}>{m}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group" style={{ marginBottom: 10 }}>
            <label className="form-label">Note</label>
            <input type="text" className="form-input" placeholder="Reference, check #, etc." value={editForm.note} onChange={e => setEditForm(f => ({ ...f, note: e.target.value }))} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" onClick={editPayment}>Save changes</button>
            <button className="btn btn-secondary" onClick={() => setEditingPayId(null)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Payment history */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: payments.length > 0 ? 14 : 0 }}>
          <div className="card-title" style={{ marginBottom: 0 }}>{ic(I.dollar, 14)} Payment history</div>
          {!addingPay && (
            <button className="btn-ghost" onClick={() => setAddingPay(true)}>{ic(I.plus, 12)} Add payment</button>
          )}
        </div>

        {payments.length === 0
          ? <div style={{ color: 'var(--text3)', fontSize: 13 }}>No payments recorded yet. Use the button above to record your first payment.</div>
          : (() => {
              let running = 0;
              return (
                <table className="ledger-tbl">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Method</th>
                      <th>Note</th>
                      <th style={{ textAlign: 'right' }}>Amount</th>
                      <th style={{ textAlign: 'right' }}>Balance after</th>
                      <th style={{ width: 32 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map(p => {
                      running += p.amount || 0;
                      const remaining = (inv.amount || 0) - running;
                      return (
                        <tr key={p.id}>
                          <td style={{ color: 'var(--text2)', fontSize: 12 }}>{fmtDate(p.date)}</td>
                          <td style={{ fontSize: 12 }}>
                            <span className="badge badge-gray">{p.method || 'Check'}</span>
                          </td>
                          <td style={{ color: 'var(--text2)', fontSize: 12 }}>{p.note || '—'}</td>
                          <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--green-txt)' }}>{fmt$(p.amount)}</td>
                          <td style={{ textAlign: 'right', fontWeight: 600, color: remaining <= 0 ? 'var(--green-txt)' : 'var(--red-txt)', fontSize: 12 }}>
                            {remaining <= 0 ? 'Paid in full' : fmt$(remaining)}
                          </td>
                          <td style={{ position: 'relative' }}>
                            <button
                              onClick={() => setPayMenuId(payMenuId === p.id ? null : p.id)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', padding: '2px 4px', borderRadius: 4, display: 'flex', alignItems: 'center' }}
                            >
                              {ic(I.moreVert, 16)}
                            </button>
                            {payMenuId === p.id && (
                              <div ref={payMenuRef} style={{ position: 'absolute', right: 0, top: '100%', background: 'var(--bg1)', border: '1px solid var(--border)', borderRadius: 8, boxShadow: 'var(--card-shadow-hover)', zIndex: 200, minWidth: 110, overflow: 'hidden' }}>
                                <button
                                  onClick={() => { setEditingPayId(p.id); setEditForm({ date: p.date || today, amount: String(p.amount || ''), method: p.method || 'Check', note: p.note || '' }); setPayMenuId(null); }}
                                  style={{ display: 'block', width: '100%', padding: '9px 14px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--text1)' }}
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => { deletePayment(p.id); setPayMenuId(null); }}
                                  style={{ display: 'block', width: '100%', padding: '9px 14px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--red-txt)' }}
                                >
                                  Delete
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              );
            })()
        }
      </div>

      {/* Edit Invoice modal */}
      {editingInv && (
        <Modal title="Edit Invoice" onClose={() => setEditingInv(false)}>
          <div className="form-grid form-grid-2">
            <FI label="Title" value={invEditForm.title || ''} onChange={fiei('title')} placeholder="e.g. HVAC Annual Maintenance" />
            <FI label="Vendor" value={invEditForm.vendor || ''} onChange={fiei('vendor')} placeholder="e.g. CoolAir HVAC" />
          </div>
          <div className="form-grid form-grid-2">
            <FI label="Invoice date" value={invEditForm.invoiceDate || ''} onChange={fiei('invoiceDate')} type="date" />
            <FI label="Due date" value={invEditForm.dueDate || ''} onChange={fiei('dueDate')} type="date" />
          </div>
          <div className="form-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <label className="form-label">Line Items</label>
              <button type="button" className="btn-ghost" style={{ fontSize: 12 }}
                onClick={() => setInvEditForm(p => ({ ...p, items: [...(p.items || []), { description: '', price: '' }] }))}>
                {ic(I.plus, 12)} Add item
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {(invEditForm.items || [{ description: '', price: '' }]).map((item, idx) => {
                const updateItem = (field, val) => {
                  const items = (invEditForm.items || []).map((it, i) => i === idx ? { ...it, [field]: val } : it);
                  setInvEditForm(p => ({ ...p, items }));
                };
                const removeItem = () => {
                  const items = (invEditForm.items || []).filter((_, i) => i !== idx);
                  setInvEditForm(p => ({ ...p, items: items.length ? items : [{ description: '', price: '' }] }));
                };
                return (
                  <div key={idx} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <input className="form-input" style={{ flex: 1 }} placeholder="Item description"
                      value={item.description} onChange={e => updateItem('description', e.target.value)} />
                    <input className="form-input" style={{ width: 110 }} placeholder="0.00" type="number"
                      value={item.price} onChange={e => updateItem('price', e.target.value)} />
                    {(invEditForm.items || []).length > 1 && (
                      <button type="button" className="btn-ghost" style={{ color: 'var(--red-txt)', padding: '4px 6px', flexShrink: 0 }} onClick={removeItem}>
                        {ic(I.x, 13)}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10, paddingTop: 8, borderTop: '1px solid var(--border)', fontSize: 13, fontWeight: 600, color: 'var(--text1)' }}>
              Total: {((invEditForm.items || []).reduce((s, it) => s + (parseFloat(it.price) || 0), 0)).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
            </div>
          </div>
          <div className="modal-actions">
            <button className="btn" onClick={() => setEditingInv(false)}>Cancel</button>
            <SavingBtn onClick={saveEditInv}>Save</SavingBtn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Financials ─────────────────────────────────────────────────────────────
const INVOICE_CATS = ['Maintenance', 'Utilities', 'Insurance', 'Management Fee', 'Legal', 'Other'];
const INCOME_CATS = ['Rent', 'Laundry', 'Parking', 'Late Fee', 'Other'];
const MONTH_NAMES_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function Financials({ invoices, setInvoices, income, setIncome, fVendors, setFVendors, properties, vendors, setVendors }) {
  const toast = useToast();
  // Navigation state — 3-step drill-down
  const [selectedPropId, setSelectedPropId] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedInvId, setSelectedInvId] = useState(null);

  // Invoices
  const [invSearch, setInvSearch] = useState('');
  const [invCatF, setInvCatF] = useState('all');
  const [invModal, setInvModal] = useState(null);
  const [invForm, setInvForm] = useState({});
  const ffi = k => v => setInvForm(p => ({ ...p, [k]: v }));

  // Vendor registration — unified with data.vendors
  const [vendorModal, setVendorModal] = useState(false);
  const [vendorForm, setVendorForm] = useState({});
  const ffv = k => v => setVendorForm(p => ({ ...p, [k]: v }));
  const [editVendorModal, setEditVendorModal] = useState(false);
  const [editVendorForm, setEditVendorForm] = useState({});
  const ffev = k => v => setEditVendorForm(p => ({ ...p, [k]: v }));
  const openAddVendorModal = () => { setVendorForm({ id: `vendor${Date.now()}`, name: '', phone: '', email: '', notes: '', propertyIds: [], address: '', insuranceEndDate: '', documents: [] }); setVendorModal(true); };
  const openEditVendorModal = vendorName => {
    const rec = (vendors || []).find(v => v.name === vendorName);
    setEditVendorForm(rec ? { ...rec } : { id: `vendor${Date.now()}`, name: vendorName, phone: '', email: '', notes: '', propertyIds: [], address: '', insuranceEndDate: '', documents: [] });
    setEditVendorModal(true);
  };
  const saveVendorReg = () => {
    if (!vendorForm.name.trim()) return;
    const exists = (vendors || []).some(v => v.name.toLowerCase() === vendorForm.name.trim().toLowerCase());
    if (exists) { toast('Vendor already exists', 'info'); return; }
    setVendors(prev => [...(prev || []), { ...vendorForm, name: vendorForm.name.trim() }]);
    setVendorModal(false);
    toast('Vendor added');
  };
  const saveEditVendor = () => {
    if (!editVendorForm.name.trim()) return;
    const existing = (vendors || []).find(v => v.id === editVendorForm.id);
    if (existing) {
      setVendors(prev => (prev || []).map(v => v.id === editVendorForm.id ? { ...editVendorForm } : v));
    } else {
      setVendors(prev => [...(prev || []), { ...editVendorForm }]);
    }
    setEditVendorModal(false);
    toast('Vendor updated');
  };
  const deleteVendorReg = vendorName => {
    if (!window.confirm(`Remove vendor "${vendorName}"? Their invoices will remain.`)) return;
    setVendors(prev => (prev || []).filter(v => v.name !== vendorName));
    toast('Vendor removed', 'info');
  };

  // Income
  const [incSearch, setIncSearch] = useState('');
  const [incPropF, setIncPropF] = useState('all');
  const [incModal, setIncModal] = useState(null);
  const [incForm, setIncForm] = useState({});
  const ffinc = k => v => setIncForm(p => ({ ...p, [k]: v }));

  // Reports
  const [repPropId, setRepPropId] = useState(properties[0]?.id || '');
  const [repMonth, setRepMonth] = useState(new Date().toISOString().slice(0, 7));
  const [repYear, setRepYear] = useState(String(new Date().getFullYear()));
  const [repView, setRepView] = useState('monthly');

  const propName = id => (properties || []).find(p => p.id === id)?.name || id;
  const fmt$ = n => `$${(+n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const today = new Date().toISOString().slice(0, 10);

  // ── Invoices CRUD ──
  const openAddInv = (vendor = '') => { setInvForm({ propertyId: selectedPropId || (properties || [])[0]?.id || '', title: '', vendor: vendor || '', description: '', items: [{ description: '', price: '' }], category: 'Maintenance', amount: '', invoiceDate: today, dueDate: '', payments: [], amountPaid: 0, status: 'Unpaid' }); setInvModal('add'); };
  const openEditInv = inv => {
    const items = inv.items && inv.items.length > 0
      ? inv.items
      : inv.description
        ? [{ description: inv.description, price: inv.amount || '' }]
        : [{ description: '', price: '' }];
    setInvForm({ ...inv, items });
    setInvModal('edit');
  };
  const saveInv = () => {
    const payments = invForm.payments || [];
    const amountPaid = payments.reduce((s, p) => s + (p.amount || 0), 0);
    const items = (invForm.items || []).filter(it => it.description || it.price);
    const amount = items.length > 0
      ? items.reduce((s, it) => s + (parseFloat(it.price) || 0), 0)
      : Number(invForm.amount || 0);
    const description = items.map(it => it.description).filter(Boolean).join(', ');
    const status = amountPaid <= 0 ? 'Unpaid' : amountPaid >= amount ? 'Paid' : 'Partial';
    const entry = { ...invForm, items, description, amount, amountPaid, status, payments };
    if (invModal === 'edit') setInvoices(prev => (prev || []).map(x => x.id === entry.id ? entry : x));
    else setInvoices(prev => [...(prev || []), { ...entry, id: `inv${Date.now()}` }]);
    setInvModal(null);
    toast(invModal === 'edit' ? 'Invoice updated' : 'Invoice added');
  };
  const removeInv = id => { if (window.confirm('Delete invoice?')) { setInvoices(prev => (prev || []).filter(x => x.id !== id)); toast('Invoice deleted', 'info'); } };

  // ── Income CRUD ──
  const openAddInc = () => { setIncForm({ propertyId: (properties || [])[0]?.id || '', description: '', category: 'Other', amount: '', date: today, status: 'Paid' }); setIncModal('add'); };
  const openEditInc = inc => { setIncForm({ ...inc }); setIncModal('edit'); };
  const saveInc = () => {
    const entry = { ...incForm, amount: Number(incForm.amount || 0) };
    if (incModal === 'edit') setIncome(prev => (prev || []).map(x => x.id === entry.id ? entry : x));
    else setIncome(prev => [...(prev || []), { ...entry, id: `inc${Date.now()}` }]);
    setIncModal(null);
    toast(incModal === 'edit' ? 'Income updated' : 'Income added');
  };
  const removeInc = id => { if (window.confirm('Delete entry?')) { setIncome(prev => (prev || []).filter(x => x.id !== id)); toast('Entry deleted', 'info'); } };

  // ── Filtered lists ──

  // Step 2: vendors scoped to selectedPropId (via invoices OR explicit propertyIds)
  const vendorGroups = useMemo(() => {
    if (!selectedPropId) return [];
    const groups = {};
    invoices
      .filter(inv => inv.propertyId === selectedPropId)
      .forEach(inv => {
        const key = (inv.vendor || '').trim() || '(No vendor)';
        if (!groups[key]) groups[key] = { vendor: key, invoices: [], total: 0, paid: 0 };
        groups[key].invoices.push(inv);
        groups[key].total += inv.amount || 0;
        groups[key].paid += invPaid(inv);
      });
    (vendors || [])
      .filter(v => (v.propertyIds || []).includes(selectedPropId))
      .forEach(v => {
        const key = (v.name || '').trim();
        if (!key) return;
        if (!groups[key]) groups[key] = { vendor: key, invoices: [], total: 0, paid: 0 };
      });
    return Object.values(groups)
      .filter(g => !invSearch || g.vendor.toLowerCase().includes(invSearch.toLowerCase()))
      .map(g => ({
        vendor: g.vendor,
        invoices: g.invoices,
        count: g.invoices.length,
        total: g.total,
        paid: g.paid,
        balance: g.total - g.paid,
        status: g.invoices.length === 0 ? null : g.invoices.every(i => i.status === 'Paid') ? 'Paid' : g.invoices.some(i => invPaid(i) > 0) ? 'Partial' : 'Unpaid',
      }))
      .sort((a, b) => a.vendor.localeCompare(b.vendor));
  }, [invoices, vendors, selectedPropId, invSearch]);

  // Step 3: invoices for selected vendor + property
  const vendorInvoices = useMemo(() =>
    selectedVendor && selectedPropId
      ? invoices.filter(i => (i.vendor || '').trim() === selectedVendor && i.propertyId === selectedPropId)
          .sort((a, b) => (b.invoiceDate || '').localeCompare(a.invoiceDate || ''))
      : [],
    [invoices, selectedVendor, selectedPropId]);

  // Step 3: all individual payment entries across all invoices for this vendor+property
  const vendorPaymentHistory = useMemo(() =>
    vendorInvoices.flatMap(inv =>
      (inv.payments || []).map(p => ({
        ...p,
        invoiceId: inv.id,
        invoiceTitle: inv.title || inv.description || 'Invoice',
      }))
    ).sort((a, b) => (b.date || '').localeCompare(a.date || '')),
    [vendorInvoices]);

  const selectedVendorRecord = (vendors || []).find(v => v.name === selectedVendor) || null;

  const filteredInc = useMemo(() =>
    income
      .filter(x => (incPropF === 'all' || x.propertyId === incPropF) && (!incSearch || (x.description || '').toLowerCase().includes(incSearch.toLowerCase())))
      .sort((a, b) => (b.date || '').localeCompare(a.date || '')),
    [income, incPropF, incSearch]);

  // ── Reports ──
  const monthlyReport = useMemo(() => {
    const invs = invoices.filter(x => x.propertyId === repPropId && (x.invoiceDate || '').startsWith(repMonth));
    const incs = income.filter(x => x.propertyId === repPropId && (x.date || '').startsWith(repMonth));
    const totalExpenses = invs.reduce((s, x) => s + (x.amount || 0), 0);
    const totalIncome = incs.filter(x => x.status === 'Paid').reduce((s, x) => s + (x.amount || 0), 0);
    const byCategory = INVOICE_CATS.map(cat => ({ cat, total: invs.filter(x => x.category === cat).reduce((s, x) => s + (x.amount || 0), 0) })).filter(x => x.total > 0);
    return { invs, incs, totalExpenses, totalIncome, byCategory, net: totalIncome - totalExpenses };
  }, [invoices, income, repPropId, repMonth]);

  const annualReport = useMemo(() =>
    Array.from({ length: 12 }, (_, i) => {
      const monthStr = `${repYear}-${String(i + 1).padStart(2, '0')}`;
      const invs = invoices.filter(x => x.propertyId === repPropId && (x.invoiceDate || '').startsWith(monthStr));
      const incs = income.filter(x => x.propertyId === repPropId && (x.date || '').startsWith(monthStr));
      const expenses = invs.reduce((s, x) => s + (x.amount || 0), 0);
      const inc = incs.filter(x => x.status === 'Paid').reduce((s, x) => s + (x.amount || 0), 0);
      return { month: MONTH_NAMES_SHORT[i], monthStr, expenses, income: inc, net: inc - expenses };
    }),
    [invoices, income, repPropId, repYear]);

  const annualTotals = useMemo(() => annualReport.reduce((acc, r) => ({ expenses: acc.expenses + r.expenses, income: acc.income + r.income, net: acc.net + r.net }), { expenses: 0, income: 0, net: 0 }), [annualReport]);

  const exportAnnual = () => {
    const wsRows = annualReport.map(r => ({ Month: r.month, Income: r.income, Expenses: r.expenses, Net: r.net }));
    wsRows.push({ Month: 'TOTAL', Income: annualTotals.income, Expenses: annualTotals.expenses, Net: annualTotals.net });
    const ws = XLSX.utils.json_to_sheet(wsRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `${repYear} Annual`);
    XLSX.writeFile(wb, `Financials_${propName(repPropId).replace(/\s+/g, '_')}_${repYear}.xlsx`);
    toast('Exported');
  };

  const statusBadge = s => !s ? <span style={{ color: 'var(--text3)' }}>—</span> : s === 'Paid' ? <span className="badge badge-green">{s}</span> : s === 'Partial' ? <span className="badge badge-amber">{s}</span> : <span className="badge badge-red">{s}</span>;

  const InvModal = () => !invModal ? null : (
    <Modal title={invModal === 'edit' ? 'Edit Invoice' : 'Add Invoice'} onClose={() => setInvModal(null)}>
      <FS label="Property" value={invForm.propertyId} onChange={ffi('propertyId')} options={(properties || []).map(p => ({ value: p.id, label: p.name }))} />
      <FI label="Title" value={invForm.title} onChange={ffi('title')} placeholder="e.g. HVAC Annual Maintenance" />
      <FI label="Vendor" value={invForm.vendor} onChange={ffi('vendor')} placeholder="e.g. CoolAir HVAC" />
      <FS label="Category" value={invForm.category} onChange={ffi('category')} options={INVOICE_CATS.map(c => ({ value: c, label: c }))} />
      <div className="form-grid form-grid-2">
        <FI label="Invoice date" value={invForm.invoiceDate} onChange={ffi('invoiceDate')} type="date" />
        <FI label="Due date" value={invForm.dueDate} onChange={ffi('dueDate')} type="date" />
      </div>
      <div className="form-group">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <label className="form-label">Items</label>
          <button type="button" className="btn-ghost" style={{ fontSize: 12 }}
            onClick={() => setInvForm(p => ({ ...p, items: [...(p.items || []), { description: '', price: '' }] }))}>
            {ic(I.plus, 12)} Add item
          </button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {(invForm.items || [{ description: '', price: '' }]).map((item, idx) => {
            const updateItem = (field, val) => {
              const items = (invForm.items || []).map((it, i) => i === idx ? { ...it, [field]: val } : it);
              const total = items.reduce((s, it) => s + (parseFloat(it.price) || 0), 0);
              setInvForm(p => ({ ...p, items, amount: total || '' }));
            };
            const removeItem = () => {
              const items = (invForm.items || []).filter((_, i) => i !== idx);
              const total = items.reduce((s, it) => s + (parseFloat(it.price) || 0), 0);
              setInvForm(p => ({ ...p, items: items.length ? items : [{ description: '', price: '' }], amount: total || '' }));
            };
            return (
              <div key={idx} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input className="form-input" style={{ flex: 1 }} placeholder="Item description"
                  value={item.description} onChange={e => updateItem('description', e.target.value)} />
                <input className="form-input" style={{ width: 110 }} placeholder="0.00" type="number"
                  value={item.price} onChange={e => updateItem('price', e.target.value)} />
                {(invForm.items || []).length > 1 && (
                  <button type="button" className="btn-ghost" style={{ color: 'var(--red-txt)', padding: '4px 6px', flexShrink: 0 }} onClick={removeItem}>
                    {ic(I.x, 13)}
                  </button>
                )}
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10, paddingTop: 8, borderTop: '1px solid var(--border)', fontSize: 13, fontWeight: 600, color: 'var(--text1)' }}>
          Total: {((invForm.items || []).reduce((s, it) => s + (parseFloat(it.price) || 0), 0)).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
        </div>
      </div>
      <div className="modal-actions">
        <button className="btn" onClick={() => setInvModal(null)}>Cancel</button>
        <SavingBtn onClick={saveInv}>Save</SavingBtn>
      </div>
    </Modal>
  );

  const VendorModals = () => (
    <>
      {vendorModal && (
        <Modal title="Add Vendor" onClose={() => setVendorModal(false)}>
          <FI label="Vendor name" value={vendorForm.name} onChange={ffv('name')} placeholder="e.g. Mike's Plumbing" />
          <div className="form-grid form-grid-2">
            <FI label="Phone" value={vendorForm.phone} onChange={ffv('phone')} placeholder="e.g. 718-555-0100" />
            <FI label="Email" value={vendorForm.email} onChange={ffv('email')} placeholder="e.g. vendor@email.com" />
          </div>
          <FI label="Notes" value={vendorForm.notes} onChange={ffv('notes')} placeholder="e.g. Plumbing contractor, licensed & insured" />
          <div className="form-group">
            <div className="form-label">Properties served</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginTop: 4 }}>
              {(properties || []).map(p => (
                <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: 'var(--text1)' }}>
                  <input type="checkbox" style={{ accentColor: 'var(--accent)', width: 14, height: 14 }}
                    checked={(vendorForm.propertyIds || []).includes(p.id)}
                    onChange={e => { const ids = vendorForm.propertyIds || []; ffv('propertyIds')(e.target.checked ? [...ids, p.id] : ids.filter(i => i !== p.id)); }} />
                  {p.name}
                </label>
              ))}
            </div>
          </div>
          <div className="modal-actions">
            <button className="btn" onClick={() => setVendorModal(false)}>Cancel</button>
            <SavingBtn onClick={saveVendorReg}>Save</SavingBtn>
          </div>
        </Modal>
      )}
      {editVendorModal && (
        <Modal title="Edit Vendor" onClose={() => setEditVendorModal(false)}>
          <FI label="Vendor name" value={editVendorForm.name} onChange={ffev('name')} placeholder="Vendor name" />
          <div className="form-grid form-grid-2">
            <FI label="Phone" value={editVendorForm.phone} onChange={ffev('phone')} placeholder="e.g. 718-555-0100" />
            <FI label="Email" value={editVendorForm.email} onChange={ffev('email')} placeholder="e.g. vendor@email.com" />
          </div>
          <FI label="Notes" value={editVendorForm.notes} onChange={ffev('notes')} placeholder="e.g. Plumbing contractor, licensed & insured" />
          <div className="form-group">
            <div className="form-label">Properties served</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginTop: 4 }}>
              {(properties || []).map(p => (
                <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: 'var(--text1)' }}>
                  <input type="checkbox" style={{ accentColor: 'var(--accent)', width: 14, height: 14 }}
                    checked={(editVendorForm.propertyIds || []).includes(p.id)}
                    onChange={e => { const ids = editVendorForm.propertyIds || []; ffev('propertyIds')(e.target.checked ? [...ids, p.id] : ids.filter(i => i !== p.id)); }} />
                  {p.name}
                </label>
              ))}
            </div>
          </div>
          <div className="modal-actions">
            <button className="btn" onClick={() => setEditVendorModal(false)}>Cancel</button>
            <SavingBtn onClick={saveEditVendor}>Save</SavingBtn>
          </div>
        </Modal>
      )}
    </>
  );

  // ── Step 4: Invoice Detail ──────────────────────────────────────────────────
  if (selectedInvId) {
    return (
      <div>
        <InvoiceDetail invoiceId={selectedInvId} invoices={invoices} setInvoices={setInvoices} properties={properties} onBack={() => setSelectedInvId(null)} />
        {InvModal()}
      </div>
    );
  }

  // ── Step 3: Vendor Detail ──────────────────────────────────────────────────
  if (selectedPropId && selectedVendor) {
    const totalAmt = vendorInvoices.reduce((s, i) => s + (i.amount || 0), 0);
    const totalPaid = vendorInvoices.reduce((s, i) => s + invPaid(i), 0);
    const totalBal = totalAmt - totalPaid;
    const currentInvoices = vendorInvoices.filter(i => i.status !== 'Paid');

    const exportVendorPDF = () => {
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 48;
      const contentW = pageW - margin * 2;
      let y = margin;

      const checkPage = (needed = 20) => {
        if (y + needed > pageH - margin) { pdf.addPage(); y = margin; }
      };

      // Header bar
      pdf.setFillColor(40, 60, 120);
      pdf.rect(0, 0, pageW, 54, 'F');
      pdf.setFontSize(18); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(255, 255, 255);
      pdf.text('Vendor Report', margin, 34);
      pdf.setFontSize(10); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(200, 210, 255);
      pdf.text(`Generated: ${new Date().toLocaleDateString('en-US', { timeZone: 'America/New_York' })}`, pageW - margin, 34, { align: 'right' });
      y = 80;

      // Vendor info block
      const infoFields = [
        ['Vendor', selectedVendor],
        ['Property', propName(selectedPropId)],
        ['Phone', selectedVendorRecord?.phone || '—'],
        ['Email', selectedVendorRecord?.email || '—'],
      ];
      const colW = contentW / 2;
      infoFields.forEach(([label, value], i) => {
        const col = i % 2;
        const x = margin + col * colW;
        if (col === 0 && i > 0) y += 30;
        pdf.setFontSize(7.5); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(120, 120, 120);
        pdf.text(label.toUpperCase(), x, y);
        pdf.setFontSize(10.5); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(20, 20, 20);
        pdf.text(String(value), x, y + 12);
      });
      y += 40;

      // Summary row
      pdf.setDrawColor(220, 220, 220); pdf.line(margin, y, pageW - margin, y); y += 16;
      const summaries = [['Total Contract', fmt$(totalAmt)], ['Total Paid', fmt$(totalPaid)], ['Outstanding', fmt$(totalBal)]];
      const sw = contentW / 3;
      summaries.forEach(([label, val], i) => {
        const x = margin + i * sw;
        pdf.setFontSize(7.5); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(120, 120, 120);
        pdf.text(label.toUpperCase(), x, y);
        pdf.setFontSize(13); pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(i === 2 && totalBal > 0 ? 180 : i === 1 ? 34 : 20, i === 1 ? 120 : 20, 20);
        pdf.text(val, x, y + 14);
      });
      y += 40;
      pdf.setDrawColor(220, 220, 220); pdf.line(margin, y, pageW - margin, y); y += 20;

      // Current Invoices section
      if (currentInvoices.length > 0) {
        pdf.setFontSize(11); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(20, 20, 20);
        pdf.text('Current Invoices', margin, y); y += 16;
        const iCols = [['Due', 80], ['Title', 160], ['Amount', 90], ['Paid', 90], ['Balance', 90], ['Status', 70]];
        let cx = margin;
        pdf.setFontSize(7.5); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(120, 120, 120);
        iCols.forEach(([h, w]) => { pdf.text(h.toUpperCase(), cx, y); cx += w; });
        y += 4; pdf.setDrawColor(200, 200, 200); pdf.line(margin, y, pageW - margin, y); y += 10;
        currentInvoices.forEach(inv => {
          checkPage(18);
          cx = margin;
          const bal = (inv.amount || 0) - invPaid(inv);
          const row = [fmtDate(inv.dueDate) || '—', inv.title || inv.description || '—', fmt$(inv.amount), fmt$(invPaid(inv)), fmt$(bal), inv.status || '—'];
          pdf.setFontSize(9); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(40, 40, 40);
          iCols.forEach(([, w], ci) => { pdf.text(String(row[ci]).slice(0, 22), cx, y); cx += w; });
          y += 16;
        });
        y += 10;
      }

      // Payment History section
      checkPage(30);
      pdf.setFontSize(11); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(20, 20, 20);
      pdf.text('Payment History', margin, y); y += 16;
      const pCols = [['Date', 80], ['Invoice', 150], ['Amount', 90], ['Method', 90], ['Note', 170]];
      let px = margin;
      pdf.setFontSize(7.5); pdf.setFont('helvetica', 'bold'); pdf.setTextColor(120, 120, 120);
      pCols.forEach(([h, w]) => { pdf.text(h.toUpperCase(), px, y); px += w; });
      y += 4; pdf.setDrawColor(200, 200, 200); pdf.line(margin, y, pageW - margin, y); y += 10;
      vendorPaymentHistory.forEach(p => {
        checkPage(18);
        px = margin;
        const row = [fmtDate(p.date) || '—', p.invoiceTitle || '—', fmt$(p.amount), p.method || '—', p.note || '—'];
        pdf.setFontSize(9); pdf.setFont('helvetica', 'normal'); pdf.setTextColor(40, 40, 40);
        pCols.forEach(([, w], ci) => { pdf.text(String(row[ci]).slice(0, 28), px, y); px += w; });
        y += 16;
      });

      pdf.save(`${selectedVendor.replace(/\s+/g, '_')}_${propName(selectedPropId).replace(/\s+/g, '_')}.pdf`);
    };

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="back-btn" onClick={() => setSelectedVendor(null)}>{ic(I.arrowLeft, 14)} Vendors</button>
            <span style={{ color: 'var(--text3)', fontSize: 13 }}>{propName(selectedPropId)}</span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-ghost" onClick={exportVendorPDF}>{ic(I.download, 14)} Export PDF</button>
            <button className="btn" onClick={() => openEditVendorModal(selectedVendor)}>{ic(I.edit, 14)} Edit Vendor</button>
            <button className="btn btn-primary" onClick={() => openAddInv(selectedVendor)}>{ic(I.plus, 14, '#fff')} Add invoice</button>
          </div>
        </div>

        {/* Vendor info card */}
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ marginBottom: 16 }}>
            <div className="card-title" style={{ marginBottom: 0 }}>{selectedVendor}</div>
          </div>
          <div className="info-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
            <div>
              <div className="info-label">Phone</div>
              <div className="info-val">{selectedVendorRecord?.phone || <span style={{ color: 'var(--text3)', fontWeight: 400 }}>—</span>}</div>
            </div>
            <div>
              <div className="info-label">Email</div>
              <div className="info-val" style={{ fontSize: 12 }}>{selectedVendorRecord?.email || <span style={{ color: 'var(--text3)', fontWeight: 400 }}>—</span>}</div>
            </div>
            <div>
              <div className="info-label">Notes</div>
              <div className="info-val" style={{ fontWeight: 400 }}>{selectedVendorRecord?.notes || <span style={{ color: 'var(--text3)' }}>—</span>}</div>
            </div>
            <div>
              <div className="info-label">Insurance Expiration</div>
              {(() => {
                const d = selectedVendorRecord?.insuranceEndDate;
                if (!d) return <span style={{ color: 'var(--text3)', fontWeight: 400 }}>—</span>;
                const daysLeft = Math.ceil((new Date(d) - new Date(today)) / 86400000);
                const color = daysLeft <= 0 ? 'var(--red-txt)' : daysLeft <= 30 ? '#f59e0b' : 'var(--green-txt)';
                const label = daysLeft <= 0 ? 'Expired' : daysLeft <= 30 ? `Expires in ${daysLeft}d` : null;
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div className="info-val" style={{ color, marginBottom: 0 }}>{fmtDate(d)}</div>
                    {label && <span className="badge" style={{ background: daysLeft <= 0 ? 'var(--red-bg)' : '#fef3c7', color, border: 'none', fontSize: 10 }}>{label}</span>}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Summary tiles */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 24 }}>
          <div className="card" style={{ padding: '16px 20px' }}>
            <div className="info-label">Total Contract</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text1)', marginTop: 6 }}>{fmt$(totalAmt)}</div>
          </div>
          <div className="card" style={{ padding: '16px 20px' }}>
            <div className="info-label">Total Paid</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: totalPaid > 0 ? 'var(--green-txt)' : 'var(--text3)', marginTop: 6 }}>{fmt$(totalPaid)}</div>
          </div>
          <div className="card" style={{ padding: '16px 20px' }}>
            <div className="info-label">Outstanding Balance</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: totalBal > 0 ? 'var(--red-txt)' : 'var(--green-txt)', marginTop: 6 }}>{fmt$(totalBal)}</div>
          </div>
        </div>

        {/* All Invoices */}
        <div className="card-title" style={{ marginBottom: 10 }}>Invoices</div>
        {vendorInvoices.length === 0
          ? <div className="empty" style={{ marginBottom: 24 }}>No invoices yet</div>
          : <table className="list-table" style={{ marginBottom: 24 }}>
              <thead><tr><th>Due</th><th>Title</th><th>Amount</th><th>Paid</th><th>Balance</th><th>Status</th><th style={{ width: 40 }}></th></tr></thead>
              <tbody>
                {vendorInvoices.map(inv => {
                  const bal = (inv.amount || 0) - invPaid(inv);
                  return (
                    <tr key={inv.id} className="clickable" onClick={() => setSelectedInvId(inv.id)}>
                      <td style={{ fontSize: 12, color: inv.dueDate && isExpiredISO(inv.dueDate) && inv.status !== 'Paid' ? 'var(--red-txt)' : 'var(--text2)', fontWeight: inv.dueDate && isExpiredISO(inv.dueDate) && inv.status !== 'Paid' ? 600 : 400 }}>{fmtDate(inv.dueDate) || '—'}</td>
                      <td style={{ fontWeight: 500 }}>{inv.title || inv.description || <span style={{ color: 'var(--text3)' }}>—</span>}</td>
                      <td style={{ fontWeight: 600 }}>{fmt$(inv.amount)}</td>
                      <td style={{ color: invPaid(inv) > 0 ? 'var(--green-txt)' : 'var(--text3)' }}>{invPaid(inv) > 0 ? fmt$(invPaid(inv)) : '—'}</td>
                      <td style={{ color: bal > 0 ? 'var(--red-txt)' : 'var(--text2)', fontWeight: bal > 0 ? 600 : 400 }}>{bal > 0 ? fmt$(bal) : '—'}</td>
                      <td>{statusBadge(inv.status)}</td>
                      <td onClick={e => e.stopPropagation()}><KebabMenu items={[{ label: 'Edit', onClick: () => openEditInv(inv) }, { label: 'Delete', danger: true, onClick: () => removeInv(inv.id) }]} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
        }

        {/* Payment History */}
        <div className="card-title" style={{ marginBottom: 10 }}>Payment History</div>
        {vendorPaymentHistory.length === 0
          ? <div className="empty">No payments recorded</div>
          : <table className="list-table">
              <thead><tr><th>Date</th><th>Invoice</th><th>Amount</th><th>Method</th><th>Note</th></tr></thead>
              <tbody>
                {vendorPaymentHistory.map((p, i) => (
                  <tr key={p.id || i}>
                    <td style={{ fontSize: 12, color: 'var(--text2)' }}>{fmtDate(p.date) || '—'}</td>
                    <td style={{ fontSize: 12, color: 'var(--text2)' }}>{p.invoiceTitle}</td>
                    <td style={{ fontWeight: 600, color: p.amount > 0 ? 'var(--green-txt)' : 'var(--text3)' }}>{p.amount > 0 ? fmt$(p.amount) : <span style={{ color: 'var(--text3)' }}>—</span>}</td>
                    <td style={{ fontSize: 12 }}>{p.method || '—'}</td>
                    <td style={{ fontSize: 12, color: 'var(--text2)' }}>{p.note || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
        }

        {InvModal()}
        {VendorModals()}
      </div>
    );
  }

  // ── Step 2: Vendor List ────────────────────────────────────────────────────
  if (selectedPropId) {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="back-btn" onClick={() => { setSelectedPropId(null); setInvSearch(''); }}>{ic(I.arrowLeft, 14)} Properties</button>
            <span className="page-title" style={{ marginBottom: 0 }}>{propName(selectedPropId)}</span>
          </div>
          <button className="btn btn-primary" onClick={openAddVendorModal}>{ic(I.plus, 14, '#fff')} Add Vendor</button>
        </div>

        <div style={{ marginBottom: 14 }}>
          <SB value={invSearch} onChange={setInvSearch} placeholder="Search vendor…" />
        </div>

        {vendorGroups.length === 0
          ? <div className="empty">No vendors for this property — add a vendor to start tracking expenses</div>
          : <table className="list-table">
              <thead><tr><th>Vendor</th><th>Invoices</th><th>Total Contract</th><th>Paid</th><th>Balance</th><th>Status</th><th style={{ width: 40 }}></th></tr></thead>
              <tbody>
                {vendorGroups.map(g => (
                  <tr key={g.vendor} className="clickable" onClick={() => setSelectedVendor(g.vendor)}>
                    <td style={{ fontWeight: 500 }}>{g.vendor}</td>
                    <td style={{ color: 'var(--text2)', fontSize: 12 }}>{g.count || '—'}</td>
                    <td style={{ fontWeight: 600 }}>{g.total ? fmt$(g.total) : '—'}</td>
                    <td style={{ color: g.paid ? 'var(--green-txt)' : 'var(--text3)' }}>{g.paid ? fmt$(g.paid) : '—'}</td>
                    <td style={{ color: g.balance > 0 ? 'var(--red-txt)' : 'var(--text2)', fontWeight: g.balance > 0 ? 600 : 400 }}>{g.balance ? fmt$(g.balance) : '—'}</td>
                    <td>{statusBadge(g.status)}</td>
                    <td onClick={e => e.stopPropagation()}>
                      <KebabMenu items={[
                        { label: 'Add invoice', onClick: () => openAddInv(g.vendor) },
                        { label: 'Edit vendor', onClick: () => openEditVendorModal(g.vendor) },
                        { label: 'Delete vendor', danger: true, onClick: () => deleteVendorReg(g.vendor) },
                      ]} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
        }

        {InvModal()}
        {VendorModals()}
      </div>
    );
  }

  // ── Step 1: Property Selection ─────────────────────────────────────────────
  return (
    <div>
      <div className="page-header"><span className="page-title">Financials</span></div>
      <table className="list-table">
        <thead><tr><th>Property</th><th>Address</th><th>Vendors</th><th>Invoices</th><th>Total Paid</th><th>Outstanding</th></tr></thead>
        <tbody>
          {(properties || []).map(prop => {
            const propInvs = invoices.filter(i => i.propertyId === prop.id);
            const propVendors = new Set(propInvs.map(i => (i.vendor || '').trim()).filter(Boolean));
            const totalAmt = propInvs.reduce((s, i) => s + (i.amount || 0), 0);
            const totalPaid = propInvs.reduce((s, i) => s + invPaid(i), 0);
            const outstanding = Math.max(0, totalAmt - totalPaid);
            return (
              <tr key={prop.id} className="clickable" onClick={() => { setSelectedPropId(prop.id); setSelectedVendor(null); setSelectedInvId(null); setInvSearch(''); }}>
                <td style={{ fontWeight: 500 }}>{prop.name}</td>
                <td style={{ fontSize: 12, color: 'var(--text2)' }}>{prop.address || '—'}</td>
                <td style={{ color: 'var(--text2)', fontSize: 12 }}>{propVendors.size}</td>
                <td style={{ color: 'var(--text2)', fontSize: 12 }}>{propInvs.length}</td>
                <td style={{ fontWeight: 600, color: totalPaid > 0 ? 'var(--green-txt)' : 'var(--text3)' }}>{totalPaid > 0 ? fmt$(totalPaid) : '—'}</td>
                <td style={{ fontWeight: outstanding > 0 ? 600 : 400, color: outstanding > 0 ? 'var(--red-txt)' : 'var(--text3)' }}>{outstanding > 0 ? fmt$(outstanding) : '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {VendorModals()}
    </div>
  );
}

// ── Dashboard ─────────────────────────────────────────────────────────────
const MONTH_NAMES_S = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function Dashboard({ properties, tenants, tasks, violations, maintenance, ledger, setTab }) {
  const fmt$ = n => (n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
  const propName = id => (properties || []).find(p => p.id === id)?.name || '';

  const todayISO = nyTodayISO(); // 'YYYY-MM-DD' in NY timezone — string comparison avoids UTC drift
  const [tyStr, tmStr] = todayISO.split('-');
  const thisYear = +tyStr;
  const thisMonth = +tmStr; // 1-indexed: May = 5
  const todayDate = new Date(todayISO + 'T12:00:00'); // noon avoids any DST edge
  const [leaseModal, setLeaseModal] = useState(false);

  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => { const h = () => setIsMobile(window.innerWidth < 768); window.addEventListener('resize', h); return () => window.removeEventListener('resize', h); }, []);

  // The PWA install banner was removed. The sidebar's "Install App" link still works —
  // it uses the window.__pwaInstallPrompt captured in index.js, not this state.

  // Parse just the YM from a date string safely (no timezone drift)
  const entryYM = iso => { const [y, m] = (iso || '').split('-'); return { y: +y, m: +m }; };
  const isThisMonth = iso => { const { y, m } = entryYM(iso); return y === thisYear && m === thisMonth; };

  // ── Financial snapshot ──
  const occupiedTenants = (tenants || []).filter(t => t.status === 'Occupied');

  // Only look at charge entries for the current month — use paidAmount (actual receipts), not amount (billed)
  const thisMonthCharges = (ledger || []).filter(e => e.type === 'charge' && isThisMonth(e.date));
  const collectedThisMonth = thisMonthCharges.reduce((s, e) => s + (Number(e.paidAmount) || 0), 0);
  const expectedRent      = thisMonthCharges.reduce((s, e) => s + (Number(e.amount)     || 0), 0);

  // Outstanding = sum of unpaid/partial balances on this month's charges
  const outstandingBalance = thisMonthCharges.reduce((s, e) => s + Math.max(0, (Number(e.amount) || 0) - (Number(e.paidAmount) || 0)), 0);

  // ── 6-month income sparkline ──
  const multiFamilyProps = (properties || []).filter(p => (p.businessType || '').toLowerCase().includes('multi family'));
  const [chartPropId, setChartPropId] = useState(null);
  const chartTenantIds = chartPropId
    ? new Set((tenants || []).filter(t => t.propertyId === chartPropId).map(t => t.id))
    : null;
  const monthlyBars = Array.from({ length: 6 }, (_, i) => {
    let y = thisYear, m = thisMonth - 5 + i;
    while (m <= 0) { m += 12; y -= 1; }
    while (m > 12) { m -= 12; y += 1; }
    // Sum paidAmount on charge entries only — avoids double-counting payment entries
    const val = (ledger || [])
      .filter(e => {
        if (e.type !== 'charge') return false;
        const em = entryYM(e.date);
        if (em.y !== y || em.m !== m) return false;
        if (chartTenantIds && !chartTenantIds.has(e.tenantId)) return false;
        return true;
      })
      .reduce((s, e) => s + (Number(e.paidAmount) || 0), 0);
    return { label: MONTH_NAMES_S[m - 1], val, isCurrent: y === thisYear && m === thisMonth };
  });
  const maxBar = Math.max(...monthlyBars.map(b => b.val), 1);

  // ── Portfolio: leases expiring / expired ──
  // Use string comparison for dates — 'YYYY-MM-DD' sorts lexicographically
  const offsetISO = (iso, days) => { const d = new Date(iso + 'T12:00:00'); d.setDate(d.getDate() + days); return d.toISOString().slice(0, 10); };
  const iso30 = offsetISO(todayISO, 30);
  const iso60 = offsetISO(todayISO, 60);
  const iso100 = offsetISO(todayISO, 100);
  const expiredTenants = occupiedTenants
    .filter(t => t.leaseEnd && t.leaseEnd < todayISO)
    .map(t => ({ ...t, daysExpired: Math.ceil((new Date(todayISO + 'T12:00:00') - new Date(t.leaseEnd + 'T12:00:00')) / 86400000) }))
    .sort((a, b) => b.leaseEnd.localeCompare(a.leaseEnd)); // most recently expired first
  const expiringTenants = occupiedTenants
    .filter(t => t.leaseEnd && t.leaseEnd >= todayISO && t.leaseEnd <= iso100)
    .map(t => ({ ...t, daysLeft: Math.ceil((new Date(t.leaseEnd + 'T12:00:00') - new Date(todayISO + 'T12:00:00')) / 86400000) }))
    .sort((a, b) => a.leaseEnd.localeCompare(b.leaseEnd));
  const expExpired = expiredTenants.length;
  const exp30 = expiringTenants.filter(t => t.leaseEnd <= iso30).length;
  const exp60 = expiringTenants.filter(t => t.leaseEnd > iso30 && t.leaseEnd <= iso60).length;
  const exp90 = expiringTenants.filter(t => t.leaseEnd > iso60).length;

  // ── New violations — anything added in the last 3 days, synced or manual ──
  const violationAddedAt = v => {
    if (v.syncedAt) return v.syncedAt;
    if (v.createdAt) return v.createdAt;
    const m = /^(?:vio_sync_|vio_bot_|v)(\d{13})/.exec(v.id || '');
    return m ? new Date(Number(m[1])).toISOString() : '';
  };
  const newVCutoff = new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString();
  const newViolations = (violations || [])
    .map(v => ({ ...v, addedAt: violationAddedAt(v) }))
    .filter(v => v.addedAt && v.addedAt >= newVCutoff)
    .sort((a, b) => b.addedAt.localeCompare(a.addedAt));

  // ── Recent payments ──
  const recentPayments = (ledger || [])
    .filter(e => e.status === 'Paid')
    .sort((a, b) => {
      const aKey = a.createdAt || a.date || '';
      const bKey = b.createdAt || b.date || '';
      return bKey.localeCompare(aKey);
    })
    .slice(0, 10)
    .map(e => ({ ...e, tenant: (tenants || []).find(t => t.id === e.tenantId) }));

  const KpiTile = ({ label, value, sub, color, accent, accentBg }) => (
    <div className="card" style={{
      padding: isMobile ? '12px 14px' : '16px 20px',
      borderLeft: accent ? `3px solid ${accent}` : undefined,
      background: accentBg || undefined,
    }}>
      <div className="info-label" style={{ fontSize: isMobile ? 9 : undefined }}>{label}</div>
      <div style={{ fontSize: isMobile ? 18 : 22, fontWeight: 700, color: color || 'var(--text1)', marginTop: 5 }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 3 }}>{sub}</div>}
    </div>
  );

  return (
    <div>
      <div className="page-header" style={{ marginBottom: 20 }}>
        <span className="page-title">Dashboard</span>
        <span style={{ fontSize: 12, color: 'var(--text3)' }}>
          {todayDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </span>
      </div>

      {/* ── KPI tiles ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 16 }}>
        <KpiTile label="Collected this month" value={fmt$(collectedThisMonth)}
          sub={`of ${fmt$(expectedRent)} expected`}
          accent="var(--green-txt)" accentBg="var(--green-bg)"
          color={collectedThisMonth >= expectedRent ? 'var(--green-txt)' : 'var(--text1)'} />
        <KpiTile label="Still owed this month" value={fmt$(outstandingBalance)}
          sub={outstandingBalance === 0 ? 'Fully collected' : `of ${fmt$(expectedRent)} expected`}
          accent="var(--red-txt)" accentBg="var(--red-bg)"
          color={outstandingBalance > 0 ? 'var(--red-txt)' : 'var(--green-txt)'} />
        <KpiTile label="New violations" value={newViolations.length}
          accent="var(--blue-txt)" accentBg="var(--blue-bg)"
          color={newViolations.length > 0 ? 'var(--blue-txt)' : 'var(--green-txt)'}
          sub={newViolations.length > 0 ? 'Added in last 3 days' : 'None in last 3 days'} />
      </div>

      {/* ── Middle row ── */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 12, marginBottom: 16, alignItems: 'stretch' }}>

        {/* Sparkline */}
        <div className="card" style={{ display: 'flex', flexDirection: 'column' }}>
          <div className="card-title">{ic(I.dollar, 14)} Rent collected — last 6 months</div>
          {/* Property filter dropdown — multi-family only */}
          {multiFamilyProps.length > 0 && (
            <select className="form-input" style={{ fontSize: 12, marginBottom: 10, width: '100%' }}
              value={chartPropId || ''}
              onChange={e => setChartPropId(e.target.value || null)}>
              <option value="">All properties</option>
              {multiFamilyProps.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          )}
          <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', gap: 6, minHeight: 120, paddingBottom: 4 }}>
            {monthlyBars.map((b, i) => (
              <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, height: '100%', justifyContent: 'flex-end' }}>
                <div style={{ fontSize: 9, color: 'var(--text3)', fontWeight: b.isCurrent ? 600 : 400, textAlign: 'center' }}>
                  {b.val > 0 ? fmt$(b.val) : ''}
                </div>
                <div style={{
                  width: '100%', borderRadius: 4,
                  height: `${Math.max(4, (b.val / maxBar) * 90)}px`,
                  background: b.isCurrent ? 'var(--accent)' : 'var(--bg3)',
                  border: `1px solid ${b.isCurrent ? 'var(--accent)' : 'var(--border)'}`,
                }} />
                <div style={{ fontSize: 9, color: b.isCurrent ? 'var(--accent)' : 'var(--text3)', fontWeight: b.isCurrent ? 600 : 400 }}>{b.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Lease expirations */}
        <div className="card" style={{ display: 'flex', flexDirection: 'column', cursor: 'pointer' }}
          onClick={() => setLeaseModal(true)}>
          <div className="card-title">
            {ic(I.file, 14)} Leases expiring / expired
          </div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            {[{ label: 'Expired', count: expExpired, color: 'var(--red-txt)', bg: 'var(--red-bg)' },
              { label: '30d', count: exp30, color: 'var(--red-txt)', bg: 'var(--red-bg)' },
              { label: '60d', count: exp60, color: 'var(--amber-txt)', bg: 'var(--amber-bg)' },
              { label: '90d', count: exp90, color: 'var(--blue-txt)', bg: 'var(--blue-bg)' }].map(b => (
              <div key={b.label} style={{ flex: 1, background: b.bg, borderRadius: 8, padding: '8px 6px', textAlign: 'center' }}>
                <div style={{ fontSize: b.label === 'Expired' ? 16 : 20, fontWeight: 700, color: b.color }}>{b.count}</div>
                <div style={{ fontSize: 9, color: b.color, marginTop: 2 }}>{b.label}</div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {(() => {
              const PREVIEW = 5;
              const expSlice = expiredTenants.slice(0, PREVIEW);
              const expiringSlice = expiringTenants.slice(0, PREVIEW - expSlice.length);
              const total = expiredTenants.length + expiringTenants.length;
              return (<>
                {expSlice.map(t => (
                  <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text2)' }}>{propName(t.propertyId)}{t.unit ? ` · Unit ${t.unit}` : ''}</div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 10 }}>
                      <div style={{ fontWeight: 600, fontSize: 11, color: 'var(--red-txt)' }}>Expired {t.daysExpired}d ago</div>
                      <div style={{ fontSize: 10, color: 'var(--text3)' }}>{fmtDate(t.leaseEnd)}</div>
                    </div>
                  </div>
                ))}
                {expiringSlice.map(t => (
                  <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text2)' }}>{propName(t.propertyId)}{t.unit ? ` · Unit ${t.unit}` : ''}</div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 10 }}>
                      <div style={{ fontWeight: 600, fontSize: 12, color: t.daysLeft <= 30 ? 'var(--red-txt)' : t.daysLeft <= 60 ? 'var(--amber-txt)' : 'var(--blue-txt)' }}>{t.daysLeft}d</div>
                      <div style={{ fontSize: 10, color: 'var(--text3)' }}>{fmtDate(t.leaseEnd)}</div>
                    </div>
                  </div>
                ))}
                {total === 0 && <div style={{ fontSize: 12, color: 'var(--text3)', padding: '6px 0' }}>No expired or expiring leases</div>}
                {total > PREVIEW && (
                  <div style={{ fontSize: 11, color: 'var(--accent)', padding: '6px 0', textAlign: 'center', cursor: 'pointer' }}>
                    View all
                  </div>
                )}
              </>);
            })()}
          </div>
        </div>

        {/* New violations (added in last 3 days) */}
        <div className="card" style={{ display: 'flex', flexDirection: 'column' }}>
          <div className="card-title" style={{ justifyContent: 'space-between' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>{ic(I.gavel, 14)} New Violations</span>
            {newViolations.length > 0 && <span style={{ background: 'var(--blue-bg)', color: 'var(--blue-txt)', fontSize: 11, fontWeight: 700, padding: '1px 8px', borderRadius: 20 }}>{newViolations.length}</span>}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {newViolations.length === 0
              ? <div style={{ fontSize: 12, color: 'var(--text3)', padding: '6px 0' }}>No new violations in the last 3 days</div>
              : newViolations.slice(0, 6).map(v => (
                  <div key={v.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '7px 0', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.caseNum}</div>
                      <div style={{ fontSize: 11, color: 'var(--text2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{propName(v.propertyId)}</div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 10 }}>
                      <span style={{ fontSize: 10, fontWeight: 600, background: 'var(--blue-bg)', color: 'var(--blue-txt)', padding: '1px 5px', borderRadius: 4 }}>{v.type}</span>
                      <div style={{ fontSize: 10, color: 'var(--text2)', fontWeight: 600, marginTop: 2 }}>Added {fmtDate(v.addedAt.slice(0, 10))}</div>
                    </div>
                  </div>
                ))
            }
            {newViolations.length > 6 && (
              <button className="btn-ghost" style={{ fontSize: 11, marginTop: 6, padding: '2px 0' }} onClick={() => setTab('violations')}>
                +{newViolations.length - 6} more → View all
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Recent payments ── */}
      <div className="card">
        <div className="card-title">{ic(I.receipt, 14)} Recent payments</div>
        {recentPayments.length === 0
          ? <div className="empty">No payments recorded yet</div>
          : isMobile
            ? <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {recentPayments.map(e => (
                  <div key={e.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 500, fontSize: 13 }}>{e.tenant?.name || '—'}</div>
                      <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 1 }}>{propName(e.tenant?.propertyId)}{e.tenant?.unit ? ` · ${e.tenant.unit}` : ''}</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 1 }}>{fmtDate(e.date)}{e.processedBy ? ` · ${e.processedBy}` : ''}</div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 12 }}>
                      <div style={{ fontWeight: 700, color: 'var(--green-txt)', fontSize: 14 }}>{(Number(e.amount) || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</div>
                      <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2 }}>{e.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            : <table className="list-table">
                <thead><tr><th>Date</th><th>Tenant</th><th>Property / unit</th><th>Description</th><th>Processed by</th><th style={{ textAlign: 'right' }}>Amount</th></tr></thead>
                <tbody>
                  {recentPayments.map(e => (
                    <tr key={e.id}>
                      <td style={{ color: 'var(--text2)', fontSize: 12, whiteSpace: 'nowrap' }}>{fmtDate(e.date)}</td>
                      <td style={{ fontWeight: 500 }}>{e.tenant?.name || '—'}</td>
                      <td style={{ fontSize: 12, color: 'var(--text2)' }}>{propName(e.tenant?.propertyId)}{e.tenant?.unit ? ` · ${e.tenant.unit}` : ''}</td>
                      <td style={{ fontSize: 12, color: 'var(--text2)' }}>{e.description}</td>
                      <td style={{ fontSize: 12, color: 'var(--text2)' }}>{e.processedBy || <span style={{ color: 'var(--text3)' }}>—</span>}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--green-txt)', whiteSpace: 'nowrap' }}>{(Number(e.amount) || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
        }
      </div>

      {/* ── Jose's Schedule ── */}
      <div className="card" style={{ marginTop: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <div className="card-title" style={{ margin: 0 }}>Jose's Schedule</div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 8 }}>
          {[
            { day: 'Monday',    detail: '2–3 hrs @ 8615' },
            { day: 'Tuesday',   detail: 'Pending'         },
            { day: 'Wednesday', detail: 'Pending'         },
            { day: 'Thursday',  detail: '2–3 hrs @ 8615' },
            { day: 'Friday',    detail: 'Pending'         },
            { day: 'Saturday',  detail: 'Pending'         },
            { day: 'Sunday',    detail: 'Pending'         },
          ].map(({ day, detail }) => {
            const isConfirmed = detail !== 'Pending';
            return (
              <div key={day} style={{ padding: '10px 12px', borderRadius: 10, background: isConfirmed ? 'var(--accent-bg)' : 'var(--bg2)', border: `1px solid ${isConfirmed ? 'var(--accent)' : 'var(--border)'}` }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: isConfirmed ? 'var(--accent)' : 'var(--text3)', marginBottom: 3 }}>{day}</div>
                <div style={{ fontSize: 12, color: isConfirmed ? 'var(--text1)' : 'var(--text3)', fontWeight: isConfirmed ? 500 : 400 }}>{detail}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Lease modal ── */}
      {leaseModal && (
        <Modal title="Leases — expired & expiring" onClose={() => setLeaseModal(false)}>
          <div style={{ overflowY: 'auto', maxHeight: '60vh' }}>
            {expiredTenants.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--red-txt)', textTransform: 'uppercase', letterSpacing: 1, padding: '12px 20px 6px' }}>
                  Expired ({expiredTenants.length})
                </div>
                {expiredTenants.map(t => (
                  <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 20px', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 500, fontSize: 13 }}>{t.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>{propName(t.propertyId)}{t.unit ? ` · Unit ${t.unit}` : ''}</div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 16 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--red-txt)' }}>Expired {t.daysExpired}d ago</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{fmtDate(t.leaseEnd)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {expiringTenants.length > 0 && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: 1, padding: '12px 20px 6px' }}>
                  Expiring within 100 days ({expiringTenants.length})
                </div>
                {expiringTenants.map(t => (
                  <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 20px', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 500, fontSize: 13 }}>{t.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>{propName(t.propertyId)}{t.unit ? ` · Unit ${t.unit}` : ''}</div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 16 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: t.daysLeft <= 30 ? 'var(--red-txt)' : t.daysLeft <= 60 ? 'var(--amber-txt)' : 'var(--blue-txt)' }}>
                        {t.daysLeft}d left
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{fmtDate(t.leaseEnd)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {expiredTenants.length === 0 && expiringTenants.length === 0 && (
              <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>No expired or expiring leases</div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── App shell ──────────────────────────────────────────────────────────────
// ─── Calendar ────────────────────────────────────────────────────────────────
function Calendar({ tasks, tenants, violations, properties, maintenance, setTab, setNavTarget }) {
  const today = nyTodayISO();
  const [cur, setCur] = useState(() => { const d = new Date(today); return { y: d.getFullYear(), m: d.getMonth() }; });
  const [selected, setSelected] = useState(today);
  const [activeFilters, setActiveFilters] = useState({ task: true, violation: true, maintenance: true });
  const toggleFilter = type => setActiveFilters(f => ({ ...f, [type]: !f[type] }));

  const propName = id => (properties || []).find(p => p.id === id)?.name || id || '—';

  // Build event list: each event has { date, type, label, sub, id, status }
  const events = useMemo(() => {
    const list = [];
    (tasks || []).forEach(t => {
      if (t.followUp) list.push({ date: t.followUp, type: 'task', label: t.title, sub: propName(t.propertyId), id: t.id, status: t.status, category: taskCategory(t) });
    });
    (violations || []).forEach(v => {
      if (v.followUp) list.push({ date: v.followUp, type: 'violation', label: v.issue || v.type || 'Violation', sub: `${propName(v.propertyId)}${v.unit ? ' · Unit ' + v.unit : ''}`, id: v.id, status: (v.statuses || [])[0] || 'Open' });
    });
    (maintenance || []).forEach(m => {
      const d = m.date || m.completedDate;
      if (d) list.push({ date: d, type: 'maintenance', label: m.title, sub: `${propName(m.propertyId)}${m.unit ? ' · Unit ' + m.unit : ''}`, id: m.id, status: m.status });
    });
    return list;
  }, [tasks, violations, maintenance]); // eslint-disable-line

  // Group by date string, respecting active filters
  const byDate = useMemo(() => {
    const map = {};
    events.filter(e => activeFilters[e.type]).forEach(e => { (map[e.date] = map[e.date] || []).push(e); });
    return map;
  }, [events, activeFilters]);

  const prevMonth = () => setCur(c => c.m === 0 ? { y: c.y - 1, m: 11 } : { y: c.y, m: c.m - 1 });
  const nextMonth = () => setCur(c => c.m === 11 ? { y: c.y + 1, m: 0 } : { y: c.y, m: c.m + 1 });
  const goToday  = () => { const d = new Date(today); setCur({ y: d.getFullYear(), m: d.getMonth() }); setSelected(today); };

  // Build grid cells
  const cells = useMemo(() => {
    const first = new Date(cur.y, cur.m, 1).getDay(); // 0=Sun
    const days  = new Date(cur.y, cur.m + 1, 0).getDate();
    const grid  = [];
    for (let i = 0; i < first; i++) grid.push(null);
    for (let d = 1; d <= days; d++) {
      const iso = `${cur.y}-${String(cur.m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      grid.push(iso);
    }
    while (grid.length % 7 !== 0) grid.push(null);
    return grid;
  }, [cur]);

  const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const DAY_NAMES   = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

  const typeColor = { task: '#6366f1', violation: 'var(--red-txt)', maintenance: '#f59e0b' };
  const typeBg    = { task: '#eef2ff', violation: 'var(--red-bg)', maintenance: '#fef3c7' };

  const urgencyRank = e => {
    const overdue = e.date < today;
    if (overdue) return 0;
    if (e.type === 'violation') return 1;
    if (e.type === 'task' && taskCategory(e) === 'Inspection') return 2;
    if (e.type === 'maintenance' && e.status === 'open') return 3;
    return 4;
  };
  const selectedEvents = (byDate[selected] || []).slice().sort((a, b) => urgencyRank(a) - urgencyRank(b));

  const navigate = e => {
    const tabMap = { task: 'tasks', violation: 'violations', maintenance: 'maintenance' };
    setNavTarget({ tab: tabMap[e.type], id: e.id });
    setTab(tabMap[e.type]);
  };

  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', width: '100%', minWidth: 0, overflow: 'hidden', paddingBottom: 40 }}>
      {/* ── Calendar grid ── */}
      <div className="card" style={{ flex: 1, minWidth: 0, overflow: 'hidden', padding: 16 }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button onClick={prevMonth} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', color: 'var(--text1)', fontSize: 16, lineHeight: 1 }}>‹</button>
            <h2 style={{ fontSize: 15, fontWeight: 600, margin: 0, minWidth: 148, textAlign: 'center' }}>{MONTH_NAMES[cur.m]} {cur.y}</h2>
            <button onClick={nextMonth} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', color: 'var(--text1)', fontSize: 16, lineHeight: 1 }}>›</button>
          </div>
          <button onClick={goToday} className="btn" style={{ fontSize: 12, padding: '5px 12px' }}>Today</button>
        </div>

        {/* Day-of-week headers */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2, marginBottom: 4 }}>
          {DAY_NAMES.map(d => (
            <div key={d} style={{ textAlign: 'center', fontSize: 11, fontWeight: 600, color: 'var(--text3)', padding: '3px 0' }}>{d}</div>
          ))}
        </div>

        {/* Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2 }}>
          {cells.map((iso, i) => {
            if (!iso) return <div key={i} style={{ minHeight: 70 }} />;
            const dayEvents = byDate[iso] || [];
            const isToday   = iso === today;
            const isSel     = iso === selected;
            const dayNum    = parseInt(iso.split('-')[2], 10);
            return (
              <div key={iso} onClick={() => setSelected(iso)}
                style={{
                  minHeight: 70, borderRadius: 6, padding: '5px 4px 3px',
                  cursor: 'pointer', overflow: 'hidden',
                  border: isSel ? '2px solid var(--accent)' : '1px solid var(--border)',
                  background: isSel ? 'var(--accent-bg, #eef2ff)' : isToday ? 'var(--bg2)' : 'var(--bg1)',
                  transition: 'background 0.15s', boxSizing: 'border-box',
                }}>
                <div style={{
                  fontSize: 11, fontWeight: isToday ? 700 : 500,
                  color: isToday ? 'var(--accent)' : 'var(--text1)',
                  marginBottom: 3, display: 'flex', alignItems: 'center',
                }}>
                  {isToday
                    ? <span style={{ background: 'var(--accent)', color: '#fff', borderRadius: '50%', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10 }}>{dayNum}</span>
                    : dayNum
                  }
                </div>
                {/* Event pills — show up to 3, strictly clipped */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, overflow: 'hidden' }}>
                  {dayEvents.slice(0, 3).map((e, j) => (
                    <div key={j} style={{
                      fontSize: 9, lineHeight: 1.35, padding: '1px 4px', borderRadius: 3,
                      background: typeBg[e.type], color: typeColor[e.type],
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      fontWeight: 500, maxWidth: '100%',
                    }}>{e.label}</div>
                  ))}
                  {dayEvents.length > 3 && (
                    <div style={{ fontSize: 9, color: 'var(--text3)', paddingLeft: 3 }}>+{dayEvents.length - 3} more</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Filter toggles */}
        <div style={{ display: 'flex', gap: 8, marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
          {[['task','Tasks'],['violation','Violations'],['maintenance','Maintenance']].map(([t, l]) => {
            const on = activeFilters[t];
            return (
              <button key={t} onClick={() => toggleFilter(t)} style={{
                display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, padding: '5px 10px',
                borderRadius: 99, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500,
                border: `1.5px solid ${on ? typeColor[t] : 'var(--border)'}`,
                background: on ? typeBg[t] : 'var(--bg2)',
                color: on ? typeColor[t] : 'var(--text3)',
                transition: 'all 0.15s',
                opacity: on ? 1 : 0.55,
              }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: on ? typeColor[t] : 'var(--text3)', display: 'inline-block', flexShrink: 0 }} />
                {l}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Day panel ── */}
      <div className="card" style={{ width: 260, minWidth: 220, maxWidth: 280, padding: 16, flexShrink: 0, overflow: 'hidden' }}>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2, color: 'var(--text1)' }}>
          {new Date(selected + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 14 }}>
          {selectedEvents.length === 0 ? 'No events' : `${selectedEvents.length} event${selectedEvents.length !== 1 ? 's' : ''}${selectedEvents.length > 5 ? ' · showing top 5' : ''}`}
        </div>
        {selectedEvents.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text3)', fontSize: 13 }}>Nothing scheduled</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {selectedEvents.slice(0, 5).map((e, i) => (
              <div key={i} onClick={() => navigate(e)}
                style={{
                  padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                  border: `1px solid var(--border)`,
                  borderLeft: `3px solid ${typeColor[e.type]}`,
                  background: 'var(--bg2)',
                  transition: 'background 0.15s',
                }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.label}</div>
                  <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 99, background: typeBg[e.type], color: typeColor[e.type], whiteSpace: 'nowrap', fontWeight: 600 }}>
                    {e.type === 'task' ? 'Task' : e.type === 'violation' ? 'Violation' : 'Maint.'}
                  </span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.sub}</div>
                {e.status && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{e.status}</div>}
              </div>
            ))}
            {selectedEvents.length > 5 && (
              <div style={{ fontSize: 11, color: 'var(--text3)', textAlign: 'center', paddingTop: 4 }}>
                +{selectedEvents.length - 5} more — click a day cell to see all
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Audit Log viewer ─────────────────────────────────────────────────────────
const AUDIT_COL_LABELS    = { tenants: 'Tenant', violations: 'Violation', ledger: 'Ledger', tasks: 'Task', maintenance: 'Maintenance', financials_invoices: 'Invoice', properties: 'Property', vendors: 'Vendor', login: 'Login' };
const AUDIT_ACTION_CLASS  = { CREATE: 'badge-green', UPDATE: 'badge-blue', DELETE: 'badge-red', LOGIN: 'badge-green', LOGOUT: 'badge-red' };
const AUDIT_SOURCE_LABEL  = { app: 'App', bot: 'Bot', sync: 'Sync', system: 'System' };

// A day is scoped by TIMESTAMP range, not by the stored `date` field: entries save `date`
// as the UTC day, so anything logged after 8pm New York carries tomorrow's date and would
// vanish from "today". Timestamps are ISO-8601 UTC strings, which sort chronologically as
// plain text, so a range on that one field is both correct and index-free.
// Returns the UTC instant of 00:00 New York on the given YYYY-MM-DD.
const nyDayStartUTC = d => {
  const edt = new Date(`${d}T04:00:00Z`); // NY midnight on daylight time (UTC-4)
  return edt.toLocaleDateString('en-CA', { timeZone: 'America/New_York' }) === d
    ? edt.toISOString()
    : new Date(`${d}T05:00:00Z`).toISOString(); // standard time (UTC-5)
};
const addDaysISO = (d, n) => new Date(new Date(`${d}T12:00:00Z`).getTime() + n * 86400000).toISOString().slice(0, 10);

function AuditLog() {
  const [entries,   setEntries]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [selected,  setSelected]  = useState(null);
  const [colFilter, setColFilter] = useState('all');
  const [srcFilter, setSrcFilter] = useState('all');
  const [search,    setSearch]    = useState('');
  // Opens on TODAY (New York). '' means every date — falls back to the most recent 500.
  const [dateFilter, setDateFilter] = useState(() => nyTodayISO());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const q = dateFilter
          ? query(collection(db, 'auditLog'),
              where('timestamp', '>=', nyDayStartUTC(dateFilter)),
              where('timestamp', '<',  nyDayStartUTC(addDaysISO(dateFilter, 1))),
              orderBy('timestamp', 'desc'))
          : query(collection(db, 'auditLog'), orderBy('timestamp', 'desc'), limit(500));
        const snap = await getDocs(q);
        if (!cancelled) setEntries(snap.docs.map(d => d.data()));
      } catch (e) { console.error('Audit log load error:', e); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [dateFilter]);

  const filtered = useMemo(() => {
    let list = entries;
    if (colFilter !== 'all') list = list.filter(e => e.collection === colFilter);
    if (srcFilter !== 'all') list = list.filter(e => e.source === srcFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(e =>
        (e.label   || '').toLowerCase().includes(q) ||
        (e.summary || '').toLowerCase().includes(q) ||
        (e.changedBy || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [entries, colFilter, srcFilter, search]);

  const cols    = [...new Set(entries.map(e => e.collection).filter(Boolean))].sort();
  const selSt   = { padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text1)', fontSize: 12, fontFamily: 'inherit', cursor: 'pointer', outline: 'none' };

  // Every row leads with the full New York date AND time, whether the list is scoped to a
  // single day or showing all dates.
  const auditWhen = auditWhenLabel;

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>

      {/* ── Entry list ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
        {/* Filters */}
        <div style={{ display: 'flex', gap: 8, padding: '12px 16px', borderBottom: '1px solid var(--border)', flexWrap: 'wrap', alignItems: 'center', flexShrink: 0 }}>
          <SB value={search} onChange={setSearch} placeholder="Search entries…" />
          <select value={colFilter} onChange={e => setColFilter(e.target.value)} style={selSt}>
            <option value="all">All types</option>
            {cols.map(c => <option key={c} value={c}>{AUDIT_COL_LABELS[c] || c}</option>)}
          </select>
          <select value={srcFilter} onChange={e => setSrcFilter(e.target.value)} style={selSt}>
            <option value="all">All sources</option>
            <option value="app">App</option>
            <option value="bot">Bot</option>
            <option value="sync">Sync</option>
            <option value="system">System</option>
          </select>
          <input type="date" className="form-input" value={dateFilter} onChange={e => setDateFilter(e.target.value)}
            title="Show the changes made on this day (New York time)"
            style={{ width: 'auto', fontSize: 12, padding: '5px 8px' }} />
          <button className="btn-ghost" onClick={() => setDateFilter(dateFilter ? '' : nyTodayISO())}>
            {dateFilter ? 'All dates' : 'Today'}
          </button>
          <span style={{ fontSize: 12, color: 'var(--text3)', marginLeft: 'auto' }}>{filtered.length} of {entries.length} entries</span>
        </div>

        {/* Rows — same one-line shape as task Notes/Documents: "when: summary - who",
            no dividers, single-spaced. Action/type/source live in the detail panel. */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 16px' }}>
          {loading
            ? <div style={{ padding: 48, color: 'var(--text3)', textAlign: 'center', fontSize: 13 }}>Loading audit log…</div>
            : filtered.length === 0
            ? <div style={{ padding: 48, color: 'var(--text3)', textAlign: 'center', fontSize: 13 }}>
                {dateFilter ? `No changes on ${fmtDate(dateFilter)}` : 'No entries found'}
              </div>
            : filtered.map(e => (
              <div key={e.id} className="audit-row" onClick={() => setSelected(p => p?.id === e.id ? null : e)}
                style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer', padding: '1px 0', background: selected?.id === e.id ? 'var(--accent-bg)' : undefined }}>
                <div style={{ fontSize: 13, color: 'var(--text1)', lineHeight: 1.3, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <span style={{ color: 'var(--text3)', fontSize: 12, marginRight: 6 }}>{auditWhen(e)}:</span>
                  <span>{e.summary || e.label || e.docId}</span>
                  <span style={{ color: 'var(--text3)', margin: '0 6px' }}>-</span>
                  <span style={{ color: 'var(--text3)', fontSize: 12 }}>{e.changedBy || '—'}</span>
                </div>
              </div>
            ))
          }
        </div>
      </div>

      {/* ── Detail panel ── */}
      {selected && (
        <div style={{ width: 320, borderLeft: '1px solid var(--border)', background: 'var(--bg1)', display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
            <span style={{ fontWeight: 600, fontSize: 13 }}>Entry Details</span>
            <button className="btn-ghost" onClick={() => setSelected(null)} style={{ padding: '2px 8px' }}>✕</button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
            {[
              ['Action',     selected.action],
              ['Type',       AUDIT_COL_LABELS[selected.collection] || selected.collection],
              ['Record',     selected.label || selected.docId],
              ['Changed by', selected.changedBy],
              ['Source',     AUDIT_SOURCE_LABEL[selected.source] || selected.source],
              ['Time',       selected.timestamp ? new Date(selected.timestamp).toLocaleString() : null],
              ['Doc ID',     selected.docId],
            ].map(([lbl, val]) => !val ? null : (
              <div key={lbl} style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 3 }}>{lbl}</div>
                <div style={{ fontSize: 13, color: 'var(--text1)', wordBreak: 'break-all' }}>
                  {/* Colour moved here from the row: CREATE/UPDATE/DELETE at a glance,
                      without badges cluttering the one-line list. */}
                  {lbl === 'Action'
                    ? <span className={`badge ${AUDIT_ACTION_CLASS[val] || 'badge-gray'}`} style={{ fontSize: 10, textTransform: 'uppercase' }}>{val}</span>
                    : val}
                </div>
              </div>
            ))}

            {selected.changes && selected.changes.length > 0 && (
              <div style={{ marginTop: 4 }}>
                <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
                  Field Changes ({selected.changes.length})
                </div>
                {selected.changes.map((c, i) => (
                  <div key={i} style={{ marginBottom: 10, padding: '8px 10px', background: 'var(--bg2)', borderRadius: 6, fontSize: 12 }}>
                    <AuditChange change={c} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const MOBILE_PRIMARY_IDS = ['dashboard', 'properties', 'tenants', 'rentroll', 'tasks'];

const WORK_TYPES = ['SH', 'GC', 'EL', 'PL', 'FO/EA', 'ST', 'FN'];

const PERMIT_BLANK = { permitNumber: '', address: '', expiration: '', workType: WORK_TYPES[0], propertyId: '' };

function WorkPermits({ properties, typeFilter = 'all' }) {
  const toast = useToast();
  const constructionProps = properties.filter(p => p.businessType === 'Construction' && !p.archived);
  const [permits, setPermits] = useState([]);
  const [search, setSearch] = useState('');
  const [propF, setPropF] = useState('all');
  const [addModal, setAddModal] = useState(false);
  const [exportModal, setExportModal] = useState(false);
  const [form, setForm] = useState(PERMIT_BLANK);
  const [saving, setSaving] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [parsedFileName, setParsedFileName] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [page, setPage] = useState(1);
  const [showClosed, setShowClosed] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => { const h = () => setIsMobile(window.innerWidth < 768); window.addEventListener('resize', h); return () => window.removeEventListener('resize', h); }, []);
  const fileInputRef = useRef(null);
  const addDocInputRef = useRef(null);
  const [addDocPermitId, setAddDocPermitId] = useState(null);
  const [docEdit, setDocEdit] = useState(null); // { permitId, docId, issuedDate, expiration }
  const saveDocDetails = async () => {
    const permit = permits.find(p => p.id === docEdit.permitId);
    if (!permit) { setDocEdit(null); return; }
    const documents = (permit.documents || []).map(d => d.id === docEdit.docId
      ? { ...d, issuedDate: docEdit.issuedDate || '', expiration: docEdit.expiration || '' }
      : d);
    await setDoc(doc(db, 'workPermits', docEdit.permitId), { documents }, { merge: true });
    setDocEdit(null);
    toast('Document details updated');
  };
  const removeDocFromPermit = async (permitId, docId) => {
    if (!window.confirm('Delete this file? This cannot be undone.')) return;
    const permit = permits.find(p => p.id === permitId);
    if (!permit) return;
    const documents = (permit.documents || []).filter(d => d.id !== docId);
    await setDoc(doc(db, 'workPermits', permitId), { documents }, { merge: true });
    if (docEdit && docEdit.docId === docId) setDocEdit(null);
    toast('File deleted', 'info');
  };

  useEffect(() => {
    const q = query(collection(db, 'workPermits'), orderBy('createdAt', 'desc'));
    return onSnapshot(q, snap => setPermits(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, []);

  const propName = id => properties.find(p => p.id === id)?.name || '—';
  const normPermitNo = s => (s || '').trim().toUpperCase();
  // While adding, detect if the typed/parsed permit number already exists (a renewal)
  const dupPermit = !editingId && form.permitNumber?.trim()
    ? permits.find(x => normPermitNo(x.permitNumber) === normPermitNo(form.permitNumber))
    : null;

  const openAdd = () => {
    const defaultProp = propF !== 'all' ? propF : '';
    const prop = constructionProps.find(p => p.id === defaultProp);
    setForm({ ...PERMIT_BLANK, propertyId: defaultProp, address: prop?.address || '' });
    setParsedFileName('');
    setPendingFile(null);
    setEditingId(null);
    setAddModal(true);
  };

  const openEdit = (p) => {
    setForm({
      permitNumber: p.permitNumber || '',
      address: p.address || '',
      expiration: p.expiration || '',
      workType: WORK_TYPES.includes(p.workType) ? p.workType : WORK_TYPES[0],
      propertyId: p.propertyId || '',
    });
    setParsedFileName('');
    setPendingFile(null);
    setEditingId(p.id);
    setAddModal(true);
  };

  const handlePermitFile = async (file) => {
    if (!file || parsing) return;
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) { toast('Please upload a PDF file', 'error'); return; }
    if (file.size > 6 * 1024 * 1024) { toast('PDF is too large (max 6MB)', 'error'); return; }
    setPendingFile(file);
    setParsedFileName(file.name);
    setParsing(true);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = () => reject(new Error('Could not read file'));
        reader.readAsDataURL(file);
      });
      const fn = httpsCallable(functions, 'parsePermitPdf');
      const res = await fn({ pdfBase64: base64, properties: constructionProps.map(p => ({ name: p.name, address: p.address || '' })) });
      const d = res.data || {};
      const matchedProp = d.propertyName ? constructionProps.find(p => p.name === d.propertyName) : null;
      setForm(f => ({
        ...f,
        propertyId: matchedProp ? matchedProp.id : '',
        permitNumber: d.permitNumber || f.permitNumber,
        address: d.address || f.address,
        expiration: d.expiration || f.expiration,
        workType: WORK_TYPES.includes(d.workType) ? d.workType : f.workType,
      }));
      toast('Permit details extracted — review before saving');
    } catch (e) {
      toast('Could not extract details (the PDF will still be attached): ' + (e.message || 'unknown error'), 'error');
    }
    setParsing(false);
  };

  const savePermit = async () => {
    if (!form.permitNumber.trim()) { toast('Permit number is required', 'error'); return; }
    if (!form.propertyId) { toast('Select a property', 'error'); return; }
    setSaving(true);
    try {
      // When adding (not editing), a matching permit number means this is a renewal of an
      // existing permit — merge into that entry (update expiration + append the document)
      // instead of creating a duplicate.
      const dup = !editingId ? permits.find(x => normPermitNo(x.permitNumber) === normPermitNo(form.permitNumber)) : null;
      const id = editingId || dup?.id || `permit_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      const existing = editingId ? permits.find(x => x.id === editingId) : dup;
      let documents = existing?.documents || [];
      if (pendingFile) {
        if (editingId) {
          // Editing: replace previously attached files with the newly dropped PDF
          for (const d of documents) {
            if (d.storagePath) await deleteObject(sRef(storage, d.storagePath)).catch(() => {});
          }
          documents = [];
        }
        // New or renewal-merge: keep existing documents and append the new one
        const docName = `${form.permitNumber.trim().replace(/[\\/:*?"<>|]/g, '-')}.pdf`;
        const path = `workPermits/${id}/${Date.now()}_${docName}`;
        const storageRef = sRef(storage, path);
        await uploadBytes(storageRef, pendingFile);
        const url = await getDownloadURL(storageRef);
        documents = [...documents, { id: `doc${Date.now()}`, name: docName, url, storagePath: path, uploadedAt: new Date().toISOString() }];
      }
      if (editingId) {
        await setDoc(doc(db, 'workPermits', id), { ...form, documents }, { merge: true });
        toast('Permit updated');
      } else if (dup) {
        await setDoc(doc(db, 'workPermits', id), { ...form, documents }, { merge: true });
        toast(`Permit ${form.permitNumber.trim()} already existed — updated its expiration and added the document`);
      } else {
        await setDoc(doc(db, 'workPermits', id), { ...form, documents, createdAt: new Date().toISOString() });
        toast('Permit added');
      }
      setAddModal(false);
    } catch (e) {
      toast('Failed to save: ' + (e.message || 'unknown error'), 'error');
    }
    setSaving(false);
  };

  const removePermit = async (id) => {
    if (!window.confirm('Delete this permit?')) return;
    try {
      const permit = permits.find(x => x.id === id);
      for (const d of (permit?.documents || [])) {
        if (d.storagePath) await deleteObject(sRef(storage, d.storagePath)).catch(() => {});
      }
      await deleteDoc(doc(db, 'workPermits', id));
      toast('Permit deleted');
    } catch (e) {
      toast('Failed to delete: ' + (e.message || 'unknown error'), 'error');
    }
  };

  const signOffPermit = async (id) => {
    const permit = permits.find(x => x.id === id);
    if (!window.confirm(`⚠️ Sign off permit "${permit?.permitNumber || ''}"?\n\nThis marks the permit as signed-off and moves it out of the active list into Closed Work Permits. You can reopen it later if needed.`)) return;
    try {
      await setDoc(doc(db, 'workPermits', id), { signedOff: true, signedOffAt: new Date().toISOString() }, { merge: true });
      toast('Permit signed off');
    } catch (e) {
      toast('Failed to sign off: ' + (e.message || 'unknown error'), 'error');
    }
  };

  const reopenPermit = async (id) => {
    try {
      await setDoc(doc(db, 'workPermits', id), { signedOff: false, signedOffAt: null }, { merge: true });
      toast('Permit reopened');
    } catch (e) {
      toast('Failed to reopen: ' + (e.message || 'unknown error'), 'error');
    }
  };

  // Append an extra document to an existing permit (does not replace existing files)
  const handleAddDocFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const permitId = addDocPermitId;
    setAddDocPermitId(null);
    if (!file || !permitId) return;
    if (file.size > 10 * 1024 * 1024) { toast('File is too large (max 10MB)', 'error'); return; }
    try {
      const permit = permits.find(x => x.id === permitId);
      const safeName = file.name.replace(/[\\/:*?"<>|]/g, '-');
      const path = `workPermits/${permitId}/${Date.now()}_${safeName}`;
      const storageRef = sRef(storage, path);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      const newDoc = { id: `doc${Date.now()}`, name: safeName, url, storagePath: path, uploadedAt: new Date().toISOString() };
      await setDoc(doc(db, 'workPermits', permitId), { documents: [...(permit?.documents || []), newDoc] }, { merge: true });
      setExpandedId(permitId); // open the drawer so the new doc is visible
      toast('Document added');
    } catch (err) {
      toast('Failed to add document: ' + (err.message || 'unknown error'), 'error');
    }
  };

  const matchesFilters = p => {
    const typeMatch = typeFilter === 'all' || p.workType === typeFilter;
    const propMatch = propF === 'all' || p.propertyId === propF;
    const q = search.toLowerCase().trim();
    const searchMatch = !q || [p.permitNumber, p.address, p.workType, propName(p.propertyId)].some(x => (x || '').toLowerCase().includes(q));
    return typeMatch && propMatch && searchMatch;
  };
  const byExpiration = (a, b) => {
    // Soonest/expired first; permits without an expiration date sink to the bottom
    if (!a.expiration && !b.expiration) return 0;
    if (!a.expiration) return 1;
    if (!b.expiration) return -1;
    return a.expiration.localeCompare(b.expiration);
  };
  const allRows = permits.filter(p => !p.signedOff && matchesFilters(p)).sort(byExpiration);
  const closedRows = permits.filter(p => p.signedOff && matchesFilters(p)).sort((a, b) => (b.signedOffAt || '').localeCompare(a.signedOffAt || ''));

  // ── Export ────────────────────────────────────────────────────────────────
  // Exports what's on screen — the type, property and search filters all apply —
  // so "export" always matches what the user is looking at. Active permits first,
  // then signed-off ones, with a Status column so the two are distinguishable.
  const exportRows = [...allRows, ...closedRows];
  const exportScope = [
    typeFilter === 'all' ? 'All types' : typeFilter,
    propF === 'all' ? 'All construction properties' : propName(propF),
    search.trim() ? `search "${search.trim()}"` : null,
  ].filter(Boolean).join(' · ');
  const EXPORT_COLS = ['Permit #', 'Type', 'Property', 'Address', 'Expiration', 'Status', 'Documents'];
  const exportRow = p => [
    p.permitNumber || '—',
    p.workType || '—',
    propName(p.propertyId),
    p.address || '—',
    p.expiration ? fmtDate(p.expiration) : '—',
    p.signedOff ? 'Signed off' : (isExpired(p.expiration) ? 'Expired' : 'Active'),
    String((p.documents || []).length),
  ];
  const exportStamp = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
  const exportBase = () => `Work_Permits_${(propF === 'all' ? 'All' : propName(propF)).replace(/[^a-z0-9]/gi, '_')}_${exportStamp()}`;

  const exportExcel = () => {
    if (!exportRows.length) { toast('Nothing to export with the current filters', 'error'); return; }
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([EXPORT_COLS, ...exportRows.map(exportRow)]);
    ws['!cols'] = [{ wch: 18 }, { wch: 7 }, { wch: 30 }, { wch: 34 }, { wch: 12 }, { wch: 11 }, { wch: 10 }];
    XLSX.utils.book_append_sheet(wb, ws, 'Work Permits');
    XLSX.writeFile(wb, `${exportBase()}.xlsx`);
    setExportModal(false);
    toast(`Exported ${exportRows.length} permit${exportRows.length === 1 ? '' : 's'} to Excel`);
  };

  const exportPdf = () => {
    if (!exportRows.length) { toast('Nothing to export with the current filters', 'error'); return; }
    // Landscape: seven columns don't fit portrait without truncating the address.
    const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'letter' });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const margin = 40;
    const colX = [0, 105, 145, 320, 545, 625, 700].map(x => margin + x);
    let y = margin;

    const header = () => {
      pdf.setFont('helvetica', 'bold'); pdf.setFontSize(15); pdf.setTextColor(20, 20, 20);
      pdf.text('Work Permits', margin, y); y += 16;
      pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9); pdf.setTextColor(110, 110, 110);
      pdf.text(`${exportScope}  ·  ${exportRows.length} permit${exportRows.length === 1 ? '' : 's'}  ·  generated ${fmtDate(exportStamp())}`, margin, y);
      y += 18;
      pdf.setDrawColor(205, 205, 205); pdf.line(margin, y, pageW - margin, y); y += 14;
      pdf.setFont('helvetica', 'bold'); pdf.setFontSize(8); pdf.setTextColor(90, 90, 90);
      EXPORT_COLS.forEach((c, i) => pdf.text(c.toUpperCase(), colX[i], y));
      y += 6; pdf.setDrawColor(230, 230, 230); pdf.line(margin, y, pageW - margin, y); y += 12;
    };
    header();

    pdf.setFontSize(9);
    exportRows.forEach(p => {
      if (y > pageH - margin - 20) { pdf.addPage(); y = margin; header(); pdf.setFontSize(9); }
      const cells = exportRow(p);
      cells.forEach((val, i) => {
        // Expired/signed-off status is the one thing worth colouring — it's why you'd read this.
        if (i === 5 && val === 'Expired') pdf.setTextColor(190, 45, 45);
        else if (i === 5 && val === 'Signed off') pdf.setTextColor(120, 120, 120);
        else pdf.setTextColor(i === 0 ? 20 : 55, i === 0 ? 20 : 55, i === 0 ? 20 : 55);
        pdf.setFont('helvetica', i === 0 ? 'bold' : 'normal');
        const width = (colX[i + 1] || pageW - margin) - colX[i] - 8;
        pdf.text(pdf.splitTextToSize(String(val), width)[0] || '', colX[i], y);
      });
      y += 14;
    });

    pdf.save(`${exportBase()}.pdf`);
    setExportModal(false);
    toast(`Exported ${exportRows.length} permit${exportRows.length === 1 ? '' : 's'} to PDF`);
  };

  // Spell out this tab's scope: permits belong to the registered Construction entities
  // (never Multi Family Housing), with the number on file for each.
  const permitCountByProp = permits.reduce((acc, p) => { acc[p.propertyId] = (acc[p.propertyId] || 0) + 1; return acc; }, {});
  const constructionEntityLabel = constructionProps.length === 0
    ? 'no Construction properties are registered yet'
    : constructionProps.map(p => `${p.name}${permits.length ? ` (${permitCountByProp[p.id] || 0})` : ''}`).join(', ');

  useEffect(() => { setPage(1); setExpandedId(null); }, [search, propF, typeFilter]);

  const pageSize = isMobile ? 10 : 50;
  const totalPages = Math.max(1, Math.ceil(allRows.length / pageSize));
  const curPage = Math.min(page, totalPages);
  const rows = allRows.slice((curPage - 1) * pageSize, curPage * pageSize);

  const isExpired = exp => exp && exp < new Date().toISOString().slice(0, 10);
  const isExpiringSoon = exp => {
    if (!exp) return false;
    // Yellow only within 3 weeks (21 days) of expiring, and not already expired.
    const today = new Date().toISOString().slice(0, 10);
    const t = new Date(); t.setDate(t.getDate() + 21);
    const threshold = t.toISOString().slice(0, 10);
    return exp >= today && exp <= threshold;
  };

  return (
    <div>
      <input ref={addDocInputRef} type="file" style={{ display: 'none' }} onChange={handleAddDocFile}
        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xlsx,.xls" />
      <div className="page-header">
        <div>
          <span className="page-title">Work Permits</span>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 3, maxWidth: 720, lineHeight: 1.5 }}>
            DOB construction work permits (SH, GC, EL, PL, FO/EA, ST, FN) for every registered
            Construction property{constructionEntityLabel ? ` — ${constructionEntityLabel}` : ''}.
            Multi Family Housing properties don't carry work permits and aren't listed here.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <button className="btn" onClick={() => setExportModal(true)}>
            {ic('M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4', 14)} Export
          </button>
          <button className="btn btn-primary" onClick={openAdd}>{ic(I.plus, 14, '#fff')} Add New Permit</button>
        </div>
      </div>

      <div className="filter-bar">
        <div style={{ flex: 1 }}><SB value={search} onChange={setSearch} placeholder="Search by permit #, address, or property…" /></div>
        <select className="form-input" style={{ width: 'auto', minWidth: 180 }} value={propF} onChange={e => setPropF(e.target.value)}>
          <option value="all">All construction properties</option>
          {constructionProps.map(p => <option key={p.id} value={p.id}>{p.name}{p.address ? ` · ${p.address}` : ''}</option>)}
        </select>
      </div>

      <table className="list-table">
        <thead><tr>
          <th>Permit #</th><th>Type</th><th>Property</th><th>Address</th><th>Expiration</th><th style={{ width: 40 }}></th>
        </tr></thead>
        <tbody>
          {rows.length === 0
            ? <tr><td colSpan={6}><div className="empty">{propF === 'all' ? 'No work permits found' : `No work permits on file for ${propName(propF)}`}</div></td></tr>
            : rows.map(p => {
                const expired = isExpired(p.expiration);
                const soon = !expired && isExpiringSoon(p.expiration);
                const open = expandedId === p.id;
                return (
                  <React.Fragment key={p.id}>
                    <tr onClick={() => setExpandedId(open ? null : p.id)} style={{ cursor: 'pointer' }}>
                      <td>
                        <div style={{ fontWeight: 500, fontSize: 13, display: 'flex', alignItems: 'center', gap: 7 }}>
                          <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--text3)', flexShrink: 0, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}><path d="M9 18l6-6-6-6"/></svg>
                          {p.permitNumber || '—'}
                          {(p.documents || []).length > 0 && <span style={{ color: 'var(--text3)', display: 'inline-flex', alignItems: 'center' }}>{ic(I.paperclip, 12)}</span>}
                        </div>
                      </td>
                      <td><span style={{ fontSize: 12, fontWeight: 500, background: 'var(--blue-bg)', color: 'var(--blue-txt)', padding: '2px 7px', borderRadius: 4 }}>{p.workType || '—'}</span></td>
                      <td><div>{propName(p.propertyId)}</div></td>
                      <td style={{ fontSize: 12, color: 'var(--text2)' }}>{p.address || '—'}</td>
                      <td style={{ fontSize: 12, color: expired ? 'var(--red-txt)' : soon ? 'var(--amber-txt)' : 'inherit', fontWeight: (expired || soon) ? 600 : 400 }}>{p.expiration ? fmtDate(p.expiration) : '—'}</td>
                      <td><div onClick={e => e.stopPropagation()}><KebabMenu items={[{ label: 'Edit', onClick: () => openEdit(p) }, { label: 'Add document', onClick: () => { setAddDocPermitId(p.id); addDocInputRef.current?.click(); } }, { label: 'Sign off', onClick: () => signOffPermit(p.id) }, { label: 'Delete', danger: true, onClick: () => removePermit(p.id) }]} /></div></td>
                    </tr>
                    {open && (
                      <tr>
                        <td colSpan={6} style={{ background: 'var(--bg2)', padding: 0 }}>
                          <div className="permit-drawer" style={{ padding: '12px 18px' }}>
                            {(p.documents || []).length === 0 ? (
                              <>
                                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Documents</div>
                                <div style={{ fontSize: 13, color: 'var(--text3)' }}>No documents attached.</div>
                              </>
                            ) : (
                              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 108px 138px 138px 60px', columnGap: 16, rowGap: 8, alignItems: 'center' }}>
                                {['Document', 'Upload Date', 'Issued Date', 'Expiration Date', ''].map((h, i) => (
                                  <div key={i} style={{ fontSize: 10, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</div>
                                ))}
                                {(p.documents || []).map(d => {
                                  const editing = docEdit && docEdit.permitId === p.id && docEdit.docId === d.id;
                                  return (
                                  <React.Fragment key={d.id}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                                      <span style={{ color: 'var(--text3)', display: 'inline-flex', flexShrink: 0 }}>{ic(I.file, 14)}</span>
                                      <a href={d.url} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: 'var(--accent)', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</a>
                                    </div>
                                    <div style={{ fontSize: 12, color: d.uploadedAt ? 'var(--text2)' : 'var(--text3)' }}>{d.uploadedAt ? fmtDate(d.uploadedAt.slice(0, 10)) : '—'}</div>
                                    {editing ? (
                                      <>
                                        <input type="date" className="form-input" onClick={e => e.stopPropagation()} value={docEdit.issuedDate} onChange={e => setDocEdit(x => ({ ...x, issuedDate: e.target.value }))} style={{ width: '100%', padding: '3px 6px', fontSize: 12 }} />
                                        <input type="date" className="form-input" onClick={e => e.stopPropagation()} value={docEdit.expiration} onChange={e => setDocEdit(x => ({ ...x, expiration: e.target.value }))} style={{ width: '100%', padding: '3px 6px', fontSize: 12 }} />
                                        <div onClick={e => e.stopPropagation()} style={{ display: 'flex', gap: 6 }}>
                                          <button title="Save" onClick={saveDocDetails} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--green-txt)', padding: 2, fontSize: 16, fontWeight: 700, lineHeight: 1 }}>✓</button>
                                          <button title="Cancel" onClick={() => setDocEdit(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', padding: 2, fontSize: 15, lineHeight: 1 }}>✕</button>
                                        </div>
                                      </>
                                    ) : (
                                      <>
                                        <div style={{ fontSize: 12, color: d.issuedDate ? 'var(--text1)' : 'var(--text3)' }}>{d.issuedDate ? fmtDate(d.issuedDate) : '—'}</div>
                                        <div style={{ fontSize: 12, color: d.expiration ? 'var(--text1)' : 'var(--text3)' }}>{d.expiration ? fmtDate(d.expiration) : '—'}</div>
                                        <div onClick={e => e.stopPropagation()}>
                                          <KebabMenu items={[
                                            { label: 'Edit details', onClick: () => setDocEdit({ permitId: p.id, docId: d.id, issuedDate: d.issuedDate || '', expiration: d.expiration || p.expiration || '' }) },
                                            { label: 'Open', onClick: () => window.open(d.url, '_blank') },
                                            { label: 'Delete file', danger: true, onClick: () => removeDocFromPermit(p.id, d.id) },
                                          ]} />
                                        </div>
                                      </>
                                    )}
                                  </React.Fragment>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
        </tbody>
      </table>

      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, marginTop: 16, flexWrap: 'wrap' }}>
          <button className="btn" style={{ padding: '4px 10px', fontSize: 12 }} disabled={curPage === 1} onClick={() => setPage(curPage - 1)}>‹ Prev</button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
            <button key={p} onClick={() => setPage(p)} style={{ padding: '4px 10px', fontSize: 12, borderRadius: 6, border: '1px solid var(--border)', background: p === curPage ? 'var(--accent)' : 'var(--bg2)', color: p === curPage ? '#fff' : 'var(--text1)', fontWeight: p === curPage ? 600 : 400, cursor: 'pointer' }}>{p}</button>
          ))}
          <button className="btn" style={{ padding: '4px 10px', fontSize: 12 }} disabled={curPage === totalPages} onClick={() => setPage(curPage + 1)}>Next ›</button>
        </div>
      )}

      {closedRows.length > 0 && (
        <div style={{ marginTop: 22 }}>
          <button onClick={() => setShowClosed(s => !s)}
            style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text2)', fontSize: 13, fontWeight: 600, padding: '6px 0', fontFamily: 'inherit' }}>
            <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" style={{ transform: showClosed ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}><path d="M9 18l6-6-6-6"/></svg>
            Closed Work Permits ({closedRows.length})
          </button>
          {showClosed && (
            <table className="list-table" style={{ marginTop: 8 }}>
              <thead><tr>
                <th>Permit #</th><th>Type</th><th>Property</th><th>Address</th><th>Signed off</th><th style={{ width: 40 }}></th>
              </tr></thead>
              <tbody>
                {closedRows.map(p => (
                  <tr key={p.id} style={{ opacity: 0.7 }}>
                    <td>
                      <div style={{ fontWeight: 500, fontSize: 13, display: 'flex', alignItems: 'center', gap: 7 }}>
                        {p.permitNumber || '—'}
                        {(p.documents || []).length > 0 && <span style={{ color: 'var(--text3)', display: 'inline-flex', alignItems: 'center' }}>{ic(I.paperclip, 12)}</span>}
                      </div>
                    </td>
                    <td><span style={{ fontSize: 12, fontWeight: 500, background: 'var(--bg3)', color: 'var(--text2)', padding: '2px 7px', borderRadius: 4 }}>{p.workType || '—'}</span></td>
                    <td><div>{propName(p.propertyId)}</div></td>
                    <td style={{ fontSize: 12, color: 'var(--text2)' }}>{p.address || '—'}</td>
                    <td style={{ fontSize: 12, color: 'var(--text3)' }}>{p.signedOffAt ? fmtDate(p.signedOffAt.slice(0, 10)) : '—'}</td>
                    <td><div onClick={e => e.stopPropagation()}><KebabMenu items={[{ label: 'Reopen', onClick: () => reopenPermit(p.id) }, { label: 'Delete', danger: true, onClick: () => removePermit(p.id) }]} /></div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {exportModal && (
        <Modal title="Export work permits" onClose={() => setExportModal(false)}>
          <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6, marginBottom: 4 }}>
            Exporting <strong style={{ color: 'var(--text1)' }}>{exportRows.length} permit{exportRows.length === 1 ? '' : 's'}</strong> — whatever the current filters show.
          </div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 16 }}>
            {exportScope}
            {closedRows.length > 0 && ` · includes ${closedRows.length} signed off`}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <button className="btn" style={{ justifyContent: 'flex-start', padding: '12px 14px', textAlign: 'left' }} onClick={exportExcel}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>Excel (.xlsx)</div>
                <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>One row per permit — sortable and filterable in Excel.</div>
              </div>
            </button>
            <button className="btn" style={{ justifyContent: 'flex-start', padding: '12px 14px', textAlign: 'left' }} onClick={exportPdf}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>PDF</div>
                <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>Landscape table for printing or sending on. Expired permits are flagged in red.</div>
              </div>
            </button>
          </div>
        </Modal>
      )}

      {addModal && (
          <Modal title={editingId ? 'Edit Permit' : 'Add New Permit'} onClose={() => setAddModal(false)} hideX>
              <div
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={e => { e.preventDefault(); setDragOver(false); handlePermitFile(e.dataTransfer.files?.[0]); }}
                onClick={() => { if (!parsing) fileInputRef.current?.click(); }}
                style={{
                  border: `2px dashed ${dragOver ? 'var(--accent)' : 'var(--border)'}`,
                  borderRadius: 8, padding: '22px 16px', textAlign: 'center', marginBottom: 20,
                  cursor: parsing ? 'wait' : 'pointer', background: dragOver ? 'var(--accent-bg)' : 'var(--bg2)',
                  transition: 'border-color 0.15s, background 0.15s',
                }}
              >
                <input ref={fileInputRef} type="file" accept="application/pdf,.pdf" style={{ display: 'none' }}
                  onChange={e => { handlePermitFile(e.target.files?.[0]); e.target.value = ''; }} />
                {parsing ? (
                  <div style={{ fontSize: 13, color: 'var(--text2)' }}>
                    <div style={{ marginBottom: 6 }}>{ic(I.file, 22)}</div>
                    Reading permit… extracting details with AI
                  </div>
                ) : parsedFileName ? (
                  <div style={{ fontSize: 13, color: 'var(--green-txt)' }}>
                    <div style={{ marginBottom: 6 }}>{ic(I.file, 22)}</div>
                    <span style={{ fontWeight: 600 }}>{parsedFileName}</span> — attached &amp; details filled in below.
                    <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>The PDF will be saved with this permit. Drop another to replace it.</div>
                  </div>
                ) : (
                  <div style={{ fontSize: 13, color: 'var(--text2)' }}>
                    <div style={{ marginBottom: 6, color: 'var(--text3)' }}>{ic(I.file, 22)}</div>
                    <span style={{ fontWeight: 600 }}>Drop a permit PDF here</span> or click to browse
                    <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>The details will be filled in automatically and the PDF saved with the permit.</div>
                  </div>
                )}
              </div>
              {[
                ['Property', <select key="prop" className="form-input" style={{ marginBottom: 0 }} value={form.propertyId || ''} onChange={e => { const prop = constructionProps.find(p => p.id === e.target.value); setForm(f => ({ ...f, propertyId: e.target.value, address: f.address || prop?.address || '' })); }}><option value="" disabled>— Select property —</option>{constructionProps.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select>],
                ['Permit Number', <input key="permitNum" className="form-input" style={{ marginBottom: 0 }} value={form.permitNumber} onChange={e => setForm(f => ({ ...f, permitNumber: e.target.value }))} placeholder="e.g. M01010387-I1-SH" />],
                ['Address', <input key="addr" className="form-input" style={{ marginBottom: 0 }} value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} placeholder="Job site address" />],
                ['Expiration Date', <input key="exp" className="form-input" style={{ marginBottom: 0 }} type="date" value={form.expiration} onChange={e => setForm(f => ({ ...f, expiration: e.target.value }))} />],
                ['Work Type', <select key="wtype" className="form-input" style={{ marginBottom: 0 }} value={form.workType} onChange={e => setForm(f => ({ ...f, workType: e.target.value }))}>{WORK_TYPES.map(t => <option key={t}>{t}</option>)}</select>],
              ].map(([label, ctrl]) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                  <span style={{ width: 180, fontSize: 13, color: 'var(--text2)', fontWeight: 500, flexShrink: 0 }}>{label}:</span>
                  <div style={{ flex: 1 }}>{ctrl}</div>
                </div>
              ))}
            {dupPermit && (
              <div style={{ marginTop: 14, padding: '10px 12px', background: 'var(--amber-bg)', border: '1px solid var(--amber-txt)', borderRadius: 8, fontSize: 12, color: 'var(--text1)', lineHeight: 1.5 }}>
                Permit <strong>{form.permitNumber.trim()}</strong> already exists for {propName(dupPermit.propertyId)}. Saving will <strong>update that permit's expiration</strong> and attach this document to it instead of creating a duplicate.
              </div>
            )}
            <div className="modal-actions">
              <button className="btn" onClick={() => setAddModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={savePermit} disabled={saving}>{saving ? 'Saving…' : editingId ? 'Save Changes' : dupPermit ? 'Update Existing Permit' : 'Add Permit'}</button>
            </div>
          </Modal>
        )}
    </div>
  );
}

const ALL_TABS = [
  { id: 'dashboard',  label: 'Dashboard',  icon: I.chart },
  { id: 'properties', label: 'Properties', icon: I.building },
  { id: 'tenants',    label: 'Tenants',    icon: I.users },
  { id: 'violations',   label: 'Violations',   icon: I.gavel },
  { id: 'permits',     label: 'Work Permits', icon: I.paperclip },
  { id: 'tasks',      label: 'Tasks',      icon: I.tasks },
  { id: 'vendors',    label: 'Vendors',    icon: I.truck },
  { id: 'financials', label: 'Financials', icon: I.dollar },
  { id: 'rentroll',   label: 'Rent Roll',  icon: I.receipt },
  { id: 'logins',     label: 'Logins',     icon: I.lock },
  { id: 'admin',      label: 'Admin',      icon: I.shield },
  { id: 'audit',      label: 'Audit Log',  icon: I.clipboard },
];

export default function App() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [tab, setTab] = useState('dashboard');
  const [taskCatFilter, setTaskCatFilter] = useState('all');
  // Who the Tasks tab is scoped to. 'me' is the default so you open the tab on your own
  // work rather than everyone's; 'all' | <user name> are the alternatives.
  // Lives here rather than inside <Tasks> so the category badges count the same set the
  // list shows — otherwise the rail would say 59 General while the list showed 12.
  const [taskAssigneeFilter, setTaskAssigneeFilter] = useState('me');
  const [violationTypeFilter, setViolationTypeFilter] = useState('all');
  const [permitTypeFilter, setPermitTypeFilter] = useState('all');
  const [navKeys, setNavKeys] = useState({});
  const [navTenantId, setNavTenantId] = useState(null);
  const [navTenantFromTab, setNavTenantFromTab] = useState(null);
  const [rentRollPropId, setRentRollPropId] = useState('');
  const [rentRollMonth, setRentRollMonth] = useState(() => nyTodayISO().slice(0, 7));
  const [moreOpen, setMoreOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiMessages, setAiMessages] = useState([]);
  const [aiInput, setAiInput] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiHistory, setAiHistory] = useState([]);
  const aiEndRef = useRef(null);
  const aiFileRef = useRef(null);
  const [navTarget, setNavTarget] = useState(null);
  const [gsQuery, setGsQuery] = useState('');
  const [gsOpen, setGsOpen] = useState(false);
  const gsRef = useRef(null);
  const notifRef = useRef(null);
  const [botNotifs, setBotNotifs] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  // Persisted per browser so the badge survives a reload instead of re-alerting.
  const [notifSeenAt, setNotifSeenAt] = useState(() => {
    try { return localStorage.getItem('amar_botTaskSeenAt') || ''; } catch { return ''; }
  });
  const unseenNotifs = botNotifs.filter(n => !notifSeenAt || (n.timestamp || '') > notifSeenAt);
  const markNotifsSeen = () => {
    const now = new Date().toISOString();
    try { localStorage.setItem('amar_botTaskSeenAt', now); } catch { /* private mode */ }
    setNotifSeenAt(now);
  };

  const handleTabClick = id => {
    if (id === tab) {
      setNavKeys(prev => ({ ...prev, [id]: (prev[id] || 0) + 1 }));
    } else {
      setTab(id);
    }
  };

  const handleSignOut = async () => {
    if (user) {
      const now = new Date();
      const entry = {
        id: `audit_${now.getTime()}_logout`,
        timestamp: now.toISOString(),
        date: now.toISOString().slice(0, 10),
        action: 'LOGOUT',
        collection: 'login',
        docId: user.uid,
        label: user.displayName || user.email,
        summary: `Signed out: ${user.displayName || user.email}`,
        changes: [],
        changedBy: user.displayName || user.email,
        changedById: user.uid,
        source: 'app',
        propertyId: '',
      };
      await setDoc(doc(db, 'auditLog', entry.id), entry).catch(() => {});
      sessionStorage.removeItem('auditLoginLogged');
    }
    SHARED_PROP_FILTER = 'all'; // clear the cross-tab property selection on sign-out
    signOut(auth);
  };

  const [properties,  setProperties]  = useState([]);
  const [tenants,     setTenants]     = useState([]);
  const [violations,  setViolations]  = useState([]);
  const [maintenance, setMaintenance] = useState([]);
  const [tasks,       setTasks]       = useState([]);
  const [ledger,      setLedger]      = useState([]);
  const [vendors,     setVendors]     = useState([]);
  const [invoices,    setInvoices]    = useState([]);
  const [income,      setIncome]      = useState([]);
  const [fVendors,    setFVendors]    = useState([]);
  const [users,       setUsers]       = useState([]);
  const [logins,      setLogins]      = useState([]);
  const [appSettings, setAppSettings] = useState([]);
  const [dataReady,   setDataReady]   = useState(false);
  // Dark is the design, not a preference — the light theme and its toggle are shelved for
  // now. The .day CSS is still in index.css if it's ever brought back.
  const [dbLoading, setDbLoading] = useState(false);
  const prevDataRef = useRef(null);
  const [tenantPortalData, setTenantPortalData] = useState(null);
  const [allPermits, setAllPermits] = useState([]);

  // All work permits (for the permit-type secondary nav counts)
  useEffect(() => {
    if (!user || !dataReady) return;
    return onSnapshot(collection(db, 'workPermits'), snap => setAllPermits(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, [user, dataReady]);

  // Global search results
  const gsResults = useMemo(() => {
    if (!gsQuery.trim() || !dataReady) return [];
    const q = gsQuery.toLowerCase().trim();
    const hits = [];
    tenants.forEach(t => {
      if (hits.length >= 12) return;
      if ((t.name || '').toLowerCase().includes(q) || (t.unit || '').toLowerCase().includes(q)) {
        const prop = properties.find(p => p.id === t.propertyId);
        hits.push({ tab: 'tenants', id: t.id, type: 'Tenant', label: t.name, sub: prop ? `${prop.name} · Unit ${t.unit}` : `Unit ${t.unit}`, icon: I.users });
      }
    });
    properties.forEach(p => {
      if (hits.length >= 12) return;
      if ((p.name || '').toLowerCase().includes(q) || (p.address || '').toLowerCase().includes(q)) {
        hits.push({ tab: 'properties', id: p.id, type: 'Property', label: p.name, sub: p.address || '', icon: I.building });
      }
    });
    tasks.forEach(t => {
      if (hits.length >= 12) return;
      if ((t.title || '').toLowerCase().includes(q)) {
        hits.push({ tab: 'tasks', id: t.id, type: 'Task', label: t.title, sub: t.status || '', icon: I.tasks });
      }
    });
    violations.forEach(v => {
      if (hits.length >= 12) return;
      if ((v.description || '').toLowerCase().includes(q) || (v.tenantName || '').toLowerCase().includes(q)) {
        hits.push({ tab: 'violations', id: v.id, type: 'Violation', label: v.description || v.type || 'Violation', sub: v.tenantName || '', icon: I.gavel });
      }
    });
    vendors.forEach(v => {
      if (hits.length >= 12) return;
      if ((v.name || '').toLowerCase().includes(q) || (v.phone || '').toLowerCase().includes(q) || (v.email || '').toLowerCase().includes(q) || (v.notes || '').toLowerCase().includes(q)) {
        hits.push({ tab: 'vendors', id: v.id, type: 'Vendor', label: v.name, sub: v.phone || v.email || '', icon: I.truck });
      }
    });
    return hits;
  }, [gsQuery, dataReady, tenants, properties, tasks, violations, maintenance, vendors]);

  // Close search dropdown on outside click
  useEffect(() => {
    const handler = e => { if (gsRef.current && !gsRef.current.contains(e.target)) setGsOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Bell: tasks created by the bot ────────────────────────────────────────
  // Every task the bot API creates writes an audit entry with source "bot", so the
  // audit log is the signal — no schema change, and it still catches writes made
  // while this browser was closed. "Seen" is remembered per browser.
  useEffect(() => {
    if (!dataReady) return undefined;
    // A week is the window: enough to catch a weekend away, short enough that a
    // long-idle browser doesn't replay months of history on open.
    const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString();
    const q = query(collection(db, 'auditLog'), where('timestamp', '>=', weekAgo), orderBy('timestamp', 'desc'), limit(60));
    return onSnapshot(q, snap => {
      setBotNotifs(snap.docs.map(d => d.data())
        .filter(e => e.source === 'bot' && e.collection === 'tasks' && e.action === 'CREATE'));
    }, err => console.warn('Bot notification listener stopped:', err.message));
  }, [dataReady]);

  // Close the notification dropdown on outside click
  useEffect(() => {
    const handler = e => { if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Listen for login/logout — load from Firestore, then set up real-time listeners
  useEffect(() => {
    let liveUnsubs = [];

    const unsubAuth = onAuthStateChanged(auth, async u => {
      setUser(u);
      setAuthLoading(false);

      // Clean up live listeners from previous session
      liveUnsubs.forEach(fn => fn());
      liveUnsubs = [];

      if (!u) {
        setTenantPortalData(null);
      } else {
        setDbLoading(true);

        // Check for tenant custom-token claim (access-code login)
        let isTenantClaim = false;
        try {
          const tok = await getIdTokenResult(u);
          isTenantClaim = tok.claims.role === 'tenant';
        } catch {}

        if (isTenantClaim) {
          try {
            const fn = httpsCallable(functions, 'getTenantData');
            const res = await fn();
            setTenantPortalData(res.data);
          } catch (e) { console.error('getTenantData failed:', e); }
          setDataReady(true);
          setDbLoading(false);
        } else {
          // Admin / managed user flow
          try {
            const getDataFn = httpsCallable(functions, 'getData');
            const result = await getDataFn();
            const loaded = result.data;

            const existingLoginIds = new Set((loaded.logins || []).map(l => l.id));
            const missingLogins = SEED_LOGINS.filter(l => !existingLoginIds.has(l.id));
            const mergedLogins = missingLogins.length > 0 ? [...(loaded.logins || []), ...missingLogins] : (loaded.logins || []);
            const mergedSettings = (!loaded.appSettings || loaded.appSettings.length === 0) ? [{ id: 'vault', vaultPassword: 'JGq2w3e4$$' }] : loaded.appSettings;

            // Check if this is a tenant who logged in with email/password (not admin, not staff)
            const isAdminEmail = u.email === ADMIN_EMAIL;
            const isManagedUser = (loaded.users || []).some(mu => userMatchesEmail(mu, u.email));
            if (!isAdminEmail && !isManagedUser) {
              try {
                const fn = httpsCallable(functions, 'getTenantData');
                const res = await fn();
                setTenantPortalData(res.data);
              } catch {}
              setDataReady(true);
              setDbLoading(false);
            } else {
              setProperties(loaded.properties || []);
              setTenants(loaded.tenants || []);
              setViolations(loaded.violations || []);
              setMaintenance(loaded.maintenance || []);
              setTasks((loaded.tasks || []).map(taskWithCategory));
              setLedger(loaded.ledger || []);
              setVendors(loaded.vendors || []);
              setInvoices(loaded.financials_invoices || []);
              setIncome(loaded.financials_income || []);
              setFVendors(loaded.financials_vendors || []);
              setUsers(loaded.users || []);
              setLogins(mergedLogins);
              setAppSettings(mergedSettings);
              setDataReady(true);
              // Tasks are normalized above; seed the diff baseline with the same shape so
              // the rename alone doesn't rewrite (and audit-log) every task on first login.
              // Docs convert as they're edited, or in bulk via scripts/backfill-task-category.js.
              prevDataRef.current = {
                ...loaded,
                tasks: (loaded.tasks || []).map(taskWithCategory),
                logins: mergedLogins,
                appSettings: mergedSettings,
              };
              setDbLoading(false);

              // Log the login session (once per tab — sessionStorage clears when tab is closed)
              if (!sessionStorage.getItem('auditLoginLogged')) {
                sessionStorage.setItem('auditLoginLogged', '1');
                const now = new Date();
                const loginEntry = {
                  id: `audit_${now.getTime()}_login`,
                  timestamp: now.toISOString(),
                  date: now.toISOString().slice(0, 10),
                  action: 'LOGIN',
                  collection: 'login',
                  docId: u.uid,
                  label: u.displayName || u.email,
                  summary: `Signed in: ${u.displayName || u.email}`,
                  changes: [],
                  changedBy: u.displayName || u.email,
                  changedById: u.uid,
                  source: 'app',
                  propertyId: '',
                };
                setDoc(doc(db, 'auditLog', loginEntry.id), loginEntry).catch(() => {});
              }

              // Real-time listeners — only react to actual document changes, not the initial load
              const LIVE_COLS = ['tasks', 'ledger', 'violations'];
              const liveSetters = { tasks: setTasks, ledger: setLedger, violations: setViolations };
              liveUnsubs = LIVE_COLS.map(col =>
                onSnapshot(collection(db, col), snapshot => {
                  if (snapshot.metadata.hasPendingWrites) return; // skip local app writes
                  const realChanges = snapshot.docChanges().filter(c => c.type !== 'added');
                  if (realChanges.length === 0) return; // skip initial load snapshot
                  liveSetters[col](prev => {
                    let items = [...prev];
                    for (const change of realChanges) {
                      const item = { ...change.doc.data(), id: change.doc.id };
                      if (change.type === 'modified') {
                        const idx = items.findIndex(x => x.id === item.id);
                        if (idx >= 0) items[idx] = item; else items.push(item);
                      } else if (change.type === 'removed') {
                        items = items.filter(x => x.id !== item.id);
                      }
                    }
                    prevDataRef.current = { ...(prevDataRef.current || {}), [col]: items };
                    return items;
                  });
                })
              );
            }
          } catch (e) {
            // getData rejects everyone who isn't admin/staff — fall back to the
            // tenant portal path (covers tenants signing in with email/password).
            console.error('getData failed (not staff, or network error):', e);
            try {
              const fn = httpsCallable(functions, 'getTenantData');
              const res = await fn();
              setTenantPortalData(res.data);
            } catch {}
            setDataReady(true);
            setDbLoading(false);
          }
        }
      }
    });

    return () => {
      unsubAuth();
      liveUnsubs.forEach(fn => fn());
    };
  }, []);

  // Auto-generate monthly rent charges on the 25th for next month
  useEffect(() => {
    if (!dataReady) return;
    const now = new Date();
    const nyNow = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
    if (nyNow.getDate() < 25) return;

    // Target = next month
    const nextMonth = new Date(nyNow.getFullYear(), nyNow.getMonth() + 1, 1);
    const targetKey = `${nextMonth.getFullYear()}-${String(nextMonth.getMonth() + 1).padStart(2, '0')}`; // e.g. "2026-05"
    const chargeDate = `${targetKey}-01`;

    const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    const monthLabel = `${MONTH_NAMES[nextMonth.getMonth()]} ${nextMonth.getFullYear()}`;

    // Check if already generated this month
    const logEntry = appSettings.find(s => s.id === 'autoChargeLog');
    const generated = logEntry?.months || [];
    if (generated.includes(targetKey)) return;

    // Build new ledger entries for all active tenants
    const newEntries = [];
    for (const t of tenants) {
      if (t.status === 'Previous') continue;
      const ps = t.programShare || 0;
      const ts = t.tenantShare || 0;
      const pk = t.hasParking ? (t.parkingFee || 0) : 0;
      const wd = t.hasWasherDryer ? (t.washerDryerFee || 0) : 0;
      const tenantTotal = ts + pk + wd;
      if (ps > 0) {
        newEntries.push({ id: `al${Date.now()}${Math.random().toString(36).slice(2)}`, tenantId: t.id, type: 'charge', date: chargeDate, description: `Rent - ${monthLabel} (Program)`, amount: ps, payer: 'Program', paidAmount: 0, status: 'Unpaid', autoGenerated: true });
      }
      if (tenantTotal > 0) {
        newEntries.push({ id: `al${Date.now()}${Math.random().toString(36).slice(2)}`, tenantId: t.id, type: 'charge', date: chargeDate, description: `Rent - ${monthLabel} (Tenant)`, amount: tenantTotal, payer: 'Tenant', paidAmount: 0, status: 'Unpaid', autoGenerated: true });
      }
    }
    if (newEntries.length === 0) return;

    // Mark as generated and save entries
    setLedger(prev => [...prev, ...newEntries]);
    setAppSettings(prev => [...prev.filter(s => s.id !== 'autoChargeLog'), { id: 'autoChargeLog', months: [...generated, targetKey] }]);
  }, [dataReady, tenants.length, appSettings]); // eslint-disable-line react-hooks/exhaustive-deps

  // Save to Firestore — only write docs that actually changed (item-level diff)
  useEffect(() => {
    if (!user || !dataReady) return;
    const currentData = {
      properties, tenants, violations, maintenance, tasks, ledger, vendors, users, logins, appSettings,
      financials_invoices: invoices, financials_income: income, financials_vendors: fVendors,
    };
    const timer = setTimeout(async () => {
      const isFirstRun = prevDataRef.current === null;
      const prev = prevDataRef.current || {};
      prevDataRef.current = currentData;
      try {
        const ops = [];
        const auditEntries = [];
        const now = new Date().toISOString();
        const today = now.slice(0, 10);
        let auditSeq = 0;

        for (const key of DB_KEYS) {
          const prevArr = prev[key] || [];
          const currArr = currentData[key] || [];
          const prevMap = new Map(prevArr.map(x => [x.id, JSON.stringify(x)]));
          const currIds = new Set(currArr.map(x => x.id));
          for (const item of currArr) {
            const prevStr = prevMap.get(item.id);
            if (JSON.stringify(item) !== prevStr) {
              ops.push({ type: 'set', ref: doc(db, key, item.id), data: item });
              if (!isFirstRun && AUDIT_COLLECTIONS.has(key)) {
                const isNew = prevStr === undefined;
                const changes = isNew ? [] : auditDiff(JSON.parse(prevStr), item);
                if (isNew || changes.length > 0) {
                  const src = item.autoSynced ? 'sync' : item.autoGenerated ? 'system' : 'app';
                  const propId = item.propertyId || (item.tenantId ? (currentData.tenants.find(t => t.id === item.tenantId) || {}).propertyId || '' : '');
                  auditEntries.push({
                    id: `audit_${Date.now()}_${String(++auditSeq).padStart(3, '0')}`,
                    timestamp: now, date: today,
                    action: isNew ? 'CREATE' : 'UPDATE',
                    collection: key, docId: item.id,
                    label: auditLabel(key, item, currentData),
                    summary: auditSummary(isNew ? 'CREATE' : 'UPDATE', key, item, changes),
                    changes,
                    changedBy: user.displayName || user.email,
                    changedById: user.uid,
                    source: src, propertyId: propId,
                  });
                }
              }
            }
          }
          for (const id of prevMap.keys()) {
            if (!currIds.has(id)) {
              ops.push({ type: 'delete', ref: doc(db, key, id) });
              if (!isFirstRun && AUDIT_COLLECTIONS.has(key)) {
                const prevObj = JSON.parse(prevMap.get(id));
                auditEntries.push({
                  id: `audit_${Date.now()}_${String(++auditSeq).padStart(3, '0')}`,
                  timestamp: now, date: today,
                  action: 'DELETE',
                  collection: key, docId: id,
                  label: auditLabel(key, prevObj, currentData),
                  summary: auditSummary('DELETE', key, prevObj, []),
                  changes: [],
                  changedBy: user.displayName || user.email,
                  changedById: user.uid,
                  source: 'app', propertyId: prevObj.propertyId || '',
                });
              }
            }
          }
        }

        for (let i = 0; i < ops.length; i += 500) {
          const batch = writeBatch(db);
          ops.slice(i, i + 500).forEach(op => {
            if (op.type === 'set') batch.set(op.ref, op.data);
            else batch.delete(op.ref);
          });
          await batch.commit();
        }

        // Write audit entries fire-and-forget — never block main save
        if (auditEntries.length > 0) {
          Promise.resolve().then(async () => {
            for (let i = 0; i < auditEntries.length; i += 500) {
              const batch = writeBatch(db);
              auditEntries.slice(i, i + 500).forEach(e => batch.set(doc(db, 'auditLog', e.id), e));
              await batch.commit();
            }
          }).catch(err => console.error('Audit log write failed:', err));
        }
      } catch (e) { console.error(e); }
    }, 1500);
    return () => clearTimeout(timer);
  }, [properties, tenants, violations, maintenance, tasks, ledger, vendors, users, logins, appSettings, invoices, income, fVendors, user, dataReady]); // eslint-disable-line react-hooks/exhaustive-deps

  if (authLoading) return <div className="night" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#0f1117', color: '#8b90a0', fontSize: 14 }}>Loading…</div>;
  if (!user) return <LoginScreen />;
  if (dbLoading || !dataReady) return <div className="night" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#0f1117', color: '#8b90a0', fontSize: 14 }}>Loading your data...</div>;
  if (tenantPortalData) return <TenantPortal data={tenantPortalData} />;

  const isAdmin = user.email === ADMIN_EMAIL;
  const managedUser = users.find(u => userMatchesEmail(u, user.email));

  if (!isAdmin && !managedUser) return <NoAccessScreen user={user} />;

  const visibleTabs = isAdmin
    ? ALL_TABS
    : ALL_TABS.filter(t => (managedUser.allowedTabs || []).includes(t.id));

  const nk = t => navKeys[t] || 0;
  const ntId = t => navTarget?.tab === t ? navTarget.id : null;
  const clearNT = () => setNavTarget(null);
  const renderTab = () => {
    if (tab === 'dashboard')  return <Dashboard key={nk('dashboard')} properties={properties} tenants={tenants} tasks={tasks} violations={violations} maintenance={maintenance} ledger={ledger} setTab={setTab} />;
    if (tab === 'properties') return <Properties key={nk('properties')} properties={properties} setProperties={setProperties} tenants={tenants} violations={violations} user={user} initialId={ntId('properties')} clearNav={clearNT} />;
    if (tab === 'tenants')    return <Tenants key={nk('tenants')} tenants={tenants} setTenants={setTenants} users={users} properties={properties} ledger={ledger} setLedger={setLedger} maintenance={maintenance} setMaintenance={setMaintenance} tasks={tasks} setTasks={setTasks} user={user} initialTenantId={navTarget?.tab === 'tenants' ? navTarget.id : navTenantId} clearNav={() => { setNavTenantId(null); setNavTenantFromTab(null); clearNT(); }} setTab={setTab} setNavTarget={setNavTarget} returnTab={navTenantFromTab} />;
    if (tab === 'permits')    return <WorkPermits properties={properties} typeFilter={permitTypeFilter} />;
    if (tab === 'violations') return <Violations key={nk('violations')} violations={violations} setViolations={setViolations} properties={properties} tenants={tenants} appSettings={appSettings} setAppSettings={setAppSettings} initialId={ntId('violations')} clearNav={clearNT} typeFilter={violationTypeFilter} setTypeFilter={setViolationTypeFilter} />;
    if (tab === 'tasks')      return <Tasks key={nk('tasks')} tasks={tasks} setTasks={setTasks} maintenance={maintenance} setMaintenance={setMaintenance} properties={properties} tenants={tenants} users={users} user={user} initialId={ntId('tasks')} clearNav={clearNT} categoryFilter={taskCatFilter} setCategoryFilter={setTaskCatFilter} assigneeFilter={taskAssigneeFilter} setAssigneeFilter={setTaskAssigneeFilter} />;
    if (tab === 'calendar')   return <Calendar key={nk('calendar')} tasks={tasks} tenants={tenants} violations={violations} properties={properties} maintenance={maintenance} setTab={setTab} setNavTarget={setNavTarget} />;
    if (tab === 'vendors')    return <Vendors key={nk('vendors')} vendors={vendors} setVendors={setVendors} properties={properties} invoices={invoices} initialId={ntId('vendors')} clearNav={clearNT} />;
    if (tab === 'financials') return <Financials key={nk('financials')} invoices={invoices} setInvoices={setInvoices} income={income} setIncome={setIncome} fVendors={fVendors} setFVendors={setFVendors} properties={properties} vendors={vendors} setVendors={setVendors} />;
    if (tab === 'rentroll')   return <RentRoll key={nk('rentroll')} tenants={tenants} setTenants={setTenants} properties={properties} ledger={ledger} onTenantClick={id => { setNavTenantId(id); setNavTenantFromTab('rentroll'); setTab('tenants'); }} initialPropId={rentRollPropId} initialMonth={rentRollMonth} onPropIdChange={setRentRollPropId} onMonthChange={setRentRollMonth} />;
    if (tab === 'logins')     return <Logins key={nk('logins')} logins={logins} setLogins={setLogins} properties={properties} appSettings={appSettings} />;
    if (tab === 'admin')      return <Admin key={nk('admin')} users={users} setUsers={setUsers} tasks={tasks} setTasks={setTasks} logins={logins} setLogins={setLogins} vendors={vendors} setVendors={setVendors} appSettings={appSettings} setAppSettings={setAppSettings} properties={properties} violations={violations} setViolations={setViolations} user={user} />;
    if (tab === 'audit')      return <AuditLog key={nk('audit')} />;
  };

  // If current tab isn't visible to this user, snap to their first allowed tab
  const activeTab = visibleTabs.find(t => t.id === tab) ? tab : visibleTabs[0]?.id;

  const mobilePrimary = visibleTabs.filter(t => MOBILE_PRIMARY_IDS.includes(t.id));
  const mobileOverflow = visibleTabs.filter(t => !MOBILE_PRIMARY_IDS.includes(t.id));
  const overflowHasActive = mobileOverflow.some(t => t.id === activeTab);

  return (
    <ToastProvider>
    <div className="app-shell night">
      <aside className="sidebar">
        <div className="logo"><div className="logo-icon">{ic(I.building, 14, '#fff')}</div><span className="logo-txt">Amar Mgmt</span></div>
        {visibleTabs.map(t => (
          <button key={t.id} className={`nav-btn${activeTab === t.id ? ' active' : ''}`} onClick={() => handleTabClick(t.id)}>
            {ic(t.icon, 15, activeTab === t.id ? 'var(--accent)' : 'var(--text2)')} {t.label}
          </button>
        ))}
        <div className="theme-toggle">
          <div className="user-info">
            {user.photoURL && <img className="user-avatar" src={user.photoURL} alt="" />}
            <span className="user-name">{user.displayName || user.email}</span>
          </div>
          {window.__pwaInstallPrompt && <button className="nav-btn" style={{ color: 'var(--accent)' }} onClick={() => { window.__pwaInstallPrompt.prompt(); window.__pwaInstallPrompt.userChoice.then(() => { window.__pwaInstallPrompt = null; }); }}>{ic('M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4', 15, 'var(--accent)')} Install App</button>}
          <button className="nav-btn" onClick={handleSignOut}>{ic(I.logout, 15, 'var(--text2)')} Sign out</button>
          <div style={{ display: 'flex', gap: 12, padding: '6px 10px 2px', flexWrap: 'wrap' }}>
            <a href="https://amarmgmt.com/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: 'var(--text3)', textDecoration: 'none' }} onMouseEnter={e => e.target.style.color = 'var(--accent)'} onMouseLeave={e => e.target.style.color = 'var(--text3)'}>Privacy Policy</a>
            <a href="https://amarmgmt.com/terms" target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: 'var(--text3)', textDecoration: 'none' }} onMouseEnter={e => e.target.style.color = 'var(--accent)'} onMouseLeave={e => e.target.style.color = 'var(--text3)'}>Terms &amp; Conditions</a>
          </div>
        </div>
      </aside>
      {/* ── Global app bar ──
          A sibling ABOVE .app, so it spans the full window across the sidebar and the
          category rail. It never scrolls or unmounts on a tab change — only <main> does.
          .app keeps the theme class too: several exports read it off .app to pick
          light/dark colours. */}
      <div className="app-right">
      <div className="app-bar">
        {/* Global search — dropdowns position off the input's live rect, so they
            follow it here without any change to the search logic itself. */}
        <div className="app-bar-slot">
          <div ref={gsRef} style={{ position: 'relative', width: '100%', maxWidth: 420 }}>
            <div style={{ position: 'relative' }}>
              <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="var(--text3)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}><circle cx={11} cy={11} r={8}/><path d="M21 21l-4.35-4.35"/></svg>
              <input
                value={gsQuery}
                onChange={e => { setGsQuery(e.target.value); setGsOpen(true); }}
                onFocus={() => setGsOpen(true)}
                placeholder="Search tenants, properties, tasks, violations…"
                style={{ width: '100%', padding: '6px 8px 6px 28px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 0, color: 'var(--text1)', fontSize: 12, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }}
                onKeyDown={e => { if (e.key === 'Escape') { setGsOpen(false); setGsQuery(''); } }}
              />
            </div>
            {gsOpen && gsResults.length > 0 && (() => {
              const rect = gsRef.current?.getBoundingClientRect();
              return (
                <div style={{ position: 'fixed', top: rect ? rect.bottom + 4 : 80, left: rect ? rect.left : 12, width: 300, background: 'var(--bg1)', border: '1px solid var(--border)', borderRadius: 10, zIndex: 99999, overflow: 'hidden', boxShadow: '0 8px 24px rgba(0,0,0,0.35)' }}>
                  {gsResults.map((r, i) => (
                    <button key={i} onClick={() => { setTab(r.tab); setNavTarget({ tab: r.tab, id: r.id }); setGsOpen(false); setGsQuery(''); }} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '9px 12px', background: 'none', border: 'none', borderBottom: '1px solid var(--border)', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg2)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                      <div style={{ flexShrink: 0, width: 28, height: 28, borderRadius: 7, background: 'var(--bg3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {ic(r.icon, 13, 'var(--accent)')}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.label}</div>
                        <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 1 }}><span style={{ color: 'var(--accent)', fontWeight: 500 }}>{r.type}</span>{r.sub ? ` · ${r.sub}` : ''}</div>
                      </div>
                    </button>
                  ))}
                </div>
              );
            })()}
            {gsOpen && gsQuery.trim() && gsResults.length === 0 && (() => {
              const rect = gsRef.current?.getBoundingClientRect();
              return (
                <div style={{ position: 'fixed', top: rect ? rect.bottom + 4 : 80, left: rect ? rect.left : 12, width: 280, background: 'var(--bg1)', border: '1px solid var(--border)', borderRadius: 10, zIndex: 99999, padding: '16px 12px', fontSize: 12, color: 'var(--text3)', textAlign: 'center', boxShadow: '0 8px 24px rgba(0,0,0,0.35)' }}>
                  No results for "{gsQuery}"
                </div>
              );
            })()}
          </div>
        </div>

        {/* Bell — tasks the bot created, newest first */}
        <div ref={notifRef} style={{ position: 'relative', flexShrink: 0 }}>
          <button className="app-bar-bell" onClick={() => { setNotifOpen(o => !o); if (!notifOpen) markNotifsSeen(); }}
            title={unseenNotifs.length ? `${unseenNotifs.length} new task${unseenNotifs.length === 1 ? '' : 's'} from the bot` : 'Tasks created by the bot'}>
            <svg width={17} height={17} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
            {unseenNotifs.length > 0 && <span className="app-bar-bell-dot">{unseenNotifs.length > 9 ? '9+' : unseenNotifs.length}</span>}
          </button>
          {notifOpen && (
            <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 6, width: 330, maxHeight: 380, overflowY: 'auto', background: 'var(--bg1)', border: '1px solid var(--border)', borderRadius: 10, zIndex: 99999, boxShadow: '0 8px 24px rgba(0,0,0,0.35)' }}>
              <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)', fontSize: 12, fontWeight: 600, color: 'var(--text2)' }}>
                Tasks created by the bot
              </div>
              {botNotifs.length === 0 ? (
                <div style={{ padding: '18px 12px', fontSize: 12, color: 'var(--text3)', textAlign: 'center' }}>
                  Nothing from the bot in the last 7 days.
                </div>
              ) : botNotifs.map((n, i) => (
                <button key={n.id || i} onClick={() => { setTab('tasks'); setNavTarget({ tab: 'tasks', id: n.docId }); setNotifOpen(false); }}
                  style={{ display: 'block', width: '100%', padding: '9px 12px', background: 'none', border: 'none', borderBottom: '1px solid var(--border)', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--bg2)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                  <div style={{ fontSize: 12, color: 'var(--text1)', lineHeight: 1.4 }}>{n.label || n.summary || n.docId}</div>
                  <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 3 }}>
                    {auditWhenLabel(n)} · {n.changedBy || 'Bot'}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="app night">
      {/* ── Desktop sidebar ── */}

      {/* ── Task category secondary nav ── */}
      {(() => {
        const TASK_NAV_CATS = [
          { value: 'all',             label: 'All tasks',        dot: 'var(--accent)' },
          { value: 'Inspection',      label: 'Inspection',       dot: 'var(--blue-txt)' },
          { value: 'Maintenance',     label: 'Maintenance',      dot: '#fb923c' },
          { value: 'General',         label: 'General',          dot: 'var(--text2)' },
          { value: 'Invoice', label: 'Invoice', dot: 'var(--amber-txt)' },
          { value: 'Renewal',         label: 'Renewal',          dot: 'var(--green-txt)' },
          { value: 'Collection',      label: 'Collection',       dot: 'var(--accent)' },
          { value: 'Complaint',       label: 'Complaint',        dot: '#e11d48' },
        ];
        const activeTasks = (tasks || [])
          .filter(taskActive)
          .filter(t => isAdmin || isAssignedToUser(t, managedUser))
          .filter(t => matchesTaskAssignee(t, taskAssigneeFilter, managedUser?.name, users));
        // The badges count what needs attention NOW — active tasks whose follow-up date is
        // today or already past. A task dated in the future, or carrying no follow-up date
        // at all, isn't due and doesn't count. So a badge is deliberately not the number of
        // rows the list shows: the list still shows every active task in the category.
        const dueTasks = activeTasks.filter(t => isTaskDueToday(t.followUp) || isTaskOverdue(t.followUp));
        const countFor = cat => {
          if (cat === 'all') return dueTasks.length;
          return dueTasks.filter(t => taskCategory(t) === cat).length;
        };
        return (
          <aside className={`task-subnav${activeTab === 'tasks' ? ' open' : ''}`}>
            <div style={{ width: 160, padding: '20px 8px 16px', display: 'flex', flexDirection: 'column', gap: 1 }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em', padding: '0 10px', marginBottom: 2 }}>Categories</div>
              {/* The badges count due/overdue only, not every row — say so, or the numbers
                  read as wrong next to a longer list. */}
              <div style={{ fontSize: 9, color: 'var(--text3)', padding: '0 10px', marginBottom: 8, lineHeight: 1.3 }}>Counts due today or overdue</div>
              {TASK_NAV_CATS.map(cat => {
                const count = countFor(cat.value);
                const isActive = taskCatFilter === cat.value;
                return (
                  <button
                    key={cat.value}
                    onClick={() => setTaskCatFilter(cat.value)}
                    className={`nav-btn${isActive ? ' active' : ''}`}
                    style={{ fontSize: 12, padding: '7px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
                      <span style={{ width: 7, height: 7, borderRadius: '50%', background: cat.dot, flexShrink: 0 }} />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cat.label}</span>
                    </span>
                    {count > 0 && (
                      <span title={`${count} due today or overdue`}
                        style={{ fontSize: 10, fontWeight: 600, background: isActive ? 'var(--accent)' : 'var(--bg3)', color: isActive ? '#fff' : 'var(--text2)', borderRadius: 10, padding: '1px 6px', flexShrink: 0 }}>
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </aside>
        );
      })()}

      {/* ── Violation type secondary nav ── */}
      {(() => {
        const VIOLATION_NAV_TYPES = [
          { value: 'all',   label: 'All types',  dot: 'var(--accent)' },
          { value: 'HPD',   label: 'HPD',        dot: 'var(--blue-txt)' },
          { value: 'DOB',   label: 'DOB',        dot: 'var(--amber-txt)' },
          { value: 'FDNY',  label: 'FDNY',       dot: '#e11d48' },
          { value: 'DSNY',  label: 'DSNY',       dot: 'var(--green-txt)' },
          { value: 'DEP',   label: 'DEP',        dot: '#8b5cf6' },
          { value: 'Insurance Claim', label: 'Insurance Claim', dot: '#0ea5e9' },
          { value: 'Other', label: 'Other',      dot: 'var(--text3)' },
        ];
        const KNOWN = new Set(['HPD','DOB','FDNY','DSNY','DEP','Insurance Claim']);
        // Count only violations that are due today or overdue (and not resolved)
        const navToday = nyTodayISO();
        const activeViolations = (violations || []).filter(v => {
          const sts = v.statuses || (v.status ? [v.status] : ['Open']);
          if (sts.some(s => ['Resolved','Completed','Dismissed','Not Owned'].includes(s))) return false;
          return v.followUp && v.followUp <= navToday;
        });
        const countFor = t => {
          if (t === 'all') return activeViolations.length;
          if (t === 'Other') return activeViolations.filter(v => !KNOWN.has(v.type)).length;
          return activeViolations.filter(v => v.type === t).length;
        };
        return (
          <aside className={`task-subnav${activeTab === 'violations' ? ' open' : ''}`}>
            <div style={{ width: 160, padding: '20px 8px 16px', display: 'flex', flexDirection: 'column', gap: 1 }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em', padding: '0 10px', marginBottom: 8 }}>Type</div>
              {VIOLATION_NAV_TYPES.map(t => {
                const count = countFor(t.value);
                const isActive = violationTypeFilter === t.value;
                return (
                  <button
                    key={t.value}
                    onClick={() => setViolationTypeFilter(t.value)}
                    className={`nav-btn${isActive ? ' active' : ''}`}
                    style={{ fontSize: 12, padding: '7px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
                      <span style={{ width: 7, height: 7, borderRadius: '50%', background: t.dot, flexShrink: 0 }} />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.label}</span>
                    </span>
                    {count > 0 && (
                      <span style={{ fontSize: 10, fontWeight: 600, background: isActive ? 'var(--accent)' : 'var(--bg3)', color: isActive ? '#fff' : 'var(--text2)', borderRadius: 10, padding: '1px 6px', flexShrink: 0 }}>
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </aside>
        );
      })()}

      {/* ── Work permit type secondary nav ── */}
      {(() => {
        const PERMIT_NAV_TYPES = [
          { value: 'all',   label: 'All types', dot: 'var(--accent)' },
          { value: 'SH',    label: 'SH',        dot: 'var(--amber-txt)' },
          { value: 'GC',    label: 'GC',        dot: 'var(--blue-txt)' },
          { value: 'EL',    label: 'EL',        dot: '#facc15' },
          { value: 'PL',    label: 'PL',        dot: '#38bdf8' },
          { value: 'FO/EA', label: 'FO/EA',     dot: '#f97316' },
          { value: 'ST',    label: 'ST',        dot: '#a78bfa' },
          { value: 'FN',    label: 'FN',        dot: 'var(--green-txt)' },
        ];
        const activePermits = allPermits.filter(p => !p.signedOff);
        const countFor = t => t === 'all' ? activePermits.length : activePermits.filter(p => p.workType === t).length;
        return (
          <aside className={`task-subnav${activeTab === 'permits' ? ' open' : ''}`}>
            <div style={{ width: 160, padding: '20px 8px 16px', display: 'flex', flexDirection: 'column', gap: 1 }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em', padding: '0 10px', marginBottom: 8 }}>Permit Type</div>
              {PERMIT_NAV_TYPES.map(t => {
                const count = countFor(t.value);
                const isActive = permitTypeFilter === t.value;
                return (
                  <button
                    key={t.value}
                    onClick={() => setPermitTypeFilter(t.value)}
                    className={`nav-btn${isActive ? ' active' : ''}`}
                    style={{ fontSize: 12, padding: '7px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
                      <span style={{ width: 7, height: 7, borderRadius: '50%', background: t.dot, flexShrink: 0 }} />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.label}</span>
                    </span>
                    {count > 0 && (
                      <span style={{ fontSize: 10, fontWeight: 600, background: isActive ? 'var(--accent)' : 'var(--bg3)', color: isActive ? '#fff' : 'var(--text2)', borderRadius: 10, padding: '1px 6px', flexShrink: 0 }}>
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </aside>
        );
      })()}

      {/* ── Mobile top bar ── */}
      <div className="mobile-header">
        <div className="logo" style={{ margin: 0 }}>
          <div className="logo-icon">{ic(I.building, 13, '#fff')}</div>
          <span className="logo-txt">Amar Mgmt</span>
        </div>
        <div className="mobile-header-actions">
          {user.photoURL && <img className="user-avatar" src={user.photoURL} alt="" style={{ width: 26, height: 26 }} />}
          <button className="mobile-icon-btn" onClick={handleSignOut} title="Sign out">{ic(I.logout, 17)}</button>
        </div>
      </div>
      <main className="main">{renderTab()}</main>
      {/* ── Mobile bottom nav ── */}
      <nav className="bottom-nav">
        {mobilePrimary.map(t => (
          <button key={t.id} className={`bottom-nav-btn${activeTab === t.id ? ' active' : ''}`} onClick={() => handleTabClick(t.id)}>
            {ic(t.icon, 21, activeTab === t.id ? 'var(--accent)' : 'var(--text3)')}
            <span>{t.label}</span>
          </button>
        ))}
        {mobileOverflow.length > 0 && (
          <button className={`bottom-nav-btn${overflowHasActive ? ' active' : ''}`} onClick={() => setMoreOpen(o => !o)}>
            {ic(I.grid, 21, overflowHasActive ? 'var(--accent)' : 'var(--text3)')}
            <span>More</span>
          </button>
        )}
      </nav>
      {/* ── More sheet ── */}
      {moreOpen && (
        <>
          <div onClick={() => setMoreOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 150 }} />
          <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: 'var(--bg1)', borderRadius: '18px 18px 0 0', border: '1px solid var(--border)', borderBottom: 'none', padding: '12px 16px', paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 28px)', zIndex: 151 }}>
            <div style={{ width: 36, height: 4, background: 'var(--border)', borderRadius: 2, margin: '0 auto 20px' }} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
              {mobileOverflow.map(t => (
                <button key={t.id} onClick={() => { handleTabClick(t.id); setMoreOpen(false); }} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 7, padding: '14px 8px', borderRadius: 14, border: 'none', cursor: 'pointer', background: activeTab === t.id ? 'var(--accent-bg)' : 'var(--bg2)', color: activeTab === t.id ? 'var(--accent)' : 'var(--text2)', fontSize: 11, fontFamily: 'inherit', fontWeight: activeTab === t.id ? 600 : 400, transition: 'background 0.12s' }}>
                  {ic(t.icon, 22, activeTab === t.id ? 'var(--accent)' : 'var(--text2)')}
                  {t.label}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 20, marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
              <a href="https://amarmgmt.com/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: 'var(--text3)', textDecoration: 'none' }}>Privacy Policy</a>
              <a href="https://amarmgmt.com/terms" target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: 'var(--text3)', textDecoration: 'none' }}>Terms &amp; Conditions</a>
            </div>
          </div>
        </>
      )}
      {/* AI chat temporarily removed */}
      {false && (() => {
        const sendAiMsg = async (overrideText, fileText, fileName) => {
          const text = overrideText ?? aiInput.trim();
          if (!text && !fileText) return;
          const userMsg = { role: 'user', text: fileText ? `${text || ''}${text ? '\n' : ''}📎 ${fileName}` : text };
          setAiMessages(m => [...m, userMsg]);
          setAiInput('');
          setAiLoading(true);
          setTimeout(() => aiEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
          try {
            const fn = httpsCallable(functions, 'askAI');
            const res = await fn({ message: text || `Import this file into maintenance records.`, fileText, fileName, history: aiHistory });
            setAiMessages(m => [...m, { role: 'assistant', text: res.data.reply }]);
            setAiHistory(res.data.history || []);
          } catch (e) {
            setAiMessages(m => [...m, { role: 'assistant', text: `Error: ${e.message}` }]);
          } finally {
            setAiLoading(false);
            setTimeout(() => aiEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
          }
        };

        const excelDateToISO = serial => {
          if (!serial || typeof serial !== 'number') return String(serial || '');
          const d = new Date((serial - 25569) * 86400 * 1000);
          return d.toISOString().slice(0, 10);
        };

        const handleFile = async e => {
          const file = e.target.files?.[0];
          if (!file) return;
          e.target.value = '';
          if (file.name.match(/\.xlsx?$/i)) {
            const buf = await file.arrayBuffer();
            const wb = XLSX.read(buf, { type: 'array' });
            const allRecords = [];
            let sheetSource = '';
            wb.SheetNames.forEach(sn => {
              sheetSource = sn;
              const ws = wb.Sheets[sn];
              const data = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
              if (data.length < 2) return;
              const headers = data[0].map(h => String(h).trim().toLowerCase());
              data.slice(1).forEach(row => {
                if (row.every(c => c === '' || c === null || c === undefined)) return;
                const rec = {};
                headers.forEach((h, idx) => { rec[h] = row[idx]; });
                Object.keys(rec).forEach(k => {
                  if (k.includes('date') && typeof rec[k] === 'number') rec[k] = excelDateToISO(rec[k]);
                });
                allRecords.push(rec);
              });
            });
            // Show loading message then call direct import function (no AI, no timeout risk)
            setAiMessages(m => [...m, { role: 'user', text: `📎 ${file.name} — importing ${allRecords.length} records…` }]);
            setAiLoading(true);
            setTimeout(() => aiEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
            try {
              const fn = httpsCallable(functions, 'importMaintenanceFile');
              const res = await fn({ sheetName: sheetSource, records: allRecords });
              setAiMessages(m => [...m, { role: 'assistant', text: `Done! Imported ${res.data.count} maintenance records from "${sheetSource}" into the system. Refresh the Maintenance tab to see them.` }]);
            } catch (err) {
              setAiMessages(m => [...m, { role: 'assistant', text: `Import failed: ${err.message}` }]);
            } finally {
              setAiLoading(false);
              setTimeout(() => aiEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
            }
          } else {
            const text = await file.text();
            sendAiMsg(`Import ALL maintenance records from this file using add_maintenance. Call the tool now.`, text.slice(0, 40000), file.name);
          }
        };

        return (
          <>
            <button
              onClick={() => setAiOpen(o => !o)}
              title="AI Assistant"
              style={{ position: 'fixed', bottom: 80, right: 18, width: 48, height: 48, borderRadius: '50%', background: 'var(--accent)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 16px rgba(0,0,0,0.35)', zIndex: 200, transition: 'transform 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.08)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
            >
              <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
                <path d="M8 10h8M8 14h5" strokeWidth={1.5}/>
              </svg>
            </button>

            {aiOpen && (
              <div style={{ position: 'fixed', bottom: 136, right: 18, width: 360, maxWidth: 'calc(100vw - 36px)', height: 520, background: 'var(--bg1)', border: '1px solid var(--border)', borderRadius: 16, boxShadow: '0 8px 32px rgba(0,0,0,0.4)', zIndex: 200, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                {/* Header */}
                <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10, background: 'var(--bg2)', borderRadius: '16px 16px 0 0' }}>
                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"><circle cx={12} cy={12} r={10}/><path d="M12 8v4M12 16h.01"/></svg>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text1)' }}>AmarJarvis</div>
                    <div style={{ fontSize: 10, color: 'var(--text3)' }}>AI Assistant — upload files or ask anything</div>
                  </div>
                  {aiMessages.length > 0 && (
                    <button onClick={() => { setAiMessages([]); setAiHistory([]); }} title="Clear chat" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', fontSize: 11, padding: '2px 6px', borderRadius: 6 }}>Clear</button>
                  )}
                  <button onClick={() => setAiOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', display: 'flex', padding: 4 }}>
                    {ic('M18 6L6 18M6 6l12 12', 16)}
                  </button>
                </div>

                {/* Messages */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {aiMessages.length === 0 && (
                    <div style={{ textAlign: 'center', color: 'var(--text3)', fontSize: 12, marginTop: 40 }}>
                      <div style={{ fontSize: 28, marginBottom: 10 }}>✨</div>
                      <div style={{ fontWeight: 500, color: 'var(--text2)', marginBottom: 6 }}>Ask me anything or upload a file</div>
                      <div>I can import maintenance records, add tasks, record payments, and more.</div>
                    </div>
                  )}
                  {aiMessages.map((m, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
                      <div style={{ maxWidth: '82%', padding: '8px 12px', borderRadius: m.role === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px', background: m.role === 'user' ? 'var(--accent)' : 'var(--bg2)', color: m.role === 'user' ? '#fff' : 'var(--text1)', fontSize: 12, lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word', border: m.role === 'assistant' ? '1px solid var(--border)' : 'none' }}>
                        <Linkify text={m.text} />
                      </div>
                    </div>
                  ))}
                  {aiLoading && (
                    <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                      <div style={{ padding: '8px 14px', borderRadius: '14px 14px 14px 4px', background: 'var(--bg2)', border: '1px solid var(--border)', display: 'flex', gap: 5, alignItems: 'center' }}>
                        {[0,1,2].map(i => <div key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent)', animation: `pulse 1.2s ease-in-out ${i*0.2}s infinite` }} />)}
                      </div>
                    </div>
                  )}
                  <div ref={aiEndRef} />
                </div>

                {/* Input */}
                <div style={{ padding: '10px 12px', borderTop: '1px solid var(--border)', display: 'flex', gap: 8, alignItems: 'flex-end', background: 'var(--bg1)' }}>
                  <input ref={aiFileRef} type="file" accept=".xlsx,.xls,.csv,.pdf,.txt" style={{ display: 'none' }} onChange={handleFile} />
                  <button onClick={() => aiFileRef.current?.click()} title="Upload file" style={{ flexShrink: 0, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--text2)' }}>
                    {ic('M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48', 15)}
                  </button>
                  <textarea
                    value={aiInput}
                    onChange={e => setAiInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendAiMsg(); } }}
                    placeholder="Ask anything or describe what to import…"
                    rows={1}
                    style={{ flex: 1, resize: 'none', border: '1px solid var(--border)', borderRadius: 10, padding: '8px 10px', fontSize: 12, fontFamily: 'inherit', background: 'var(--bg2)', color: 'var(--text1)', outline: 'none', lineHeight: 1.4, maxHeight: 80, overflowY: 'auto' }}
                  />
                  <button onClick={() => sendAiMsg()} disabled={!aiInput.trim() || aiLoading} style={{ flexShrink: 0, background: aiInput.trim() && !aiLoading ? 'var(--accent)' : 'var(--bg3)', border: 'none', borderRadius: 10, width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: aiInput.trim() && !aiLoading ? 'pointer' : 'default', transition: 'background 0.15s' }}>
                    {ic('M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z', 15, aiInput.trim() && !aiLoading ? '#fff' : 'var(--text3)')}
                  </button>
                </div>
              </div>
            )}
          </>
        );
      })()}
    </div>
    </div>
    </div>
    </ToastProvider>
  );
}
